import { useState, useEffect, useMemo, useRef, startTransition, Component } from "react";
import { createPortal } from "react-dom";
import { supabase } from "./supabase";
import MeldingenKnop from "./MeldingenKnop";
import GespreksVoorbereiding from "./GespreksVoorbereiding";
import IntakeGesprek from "./IntakeGesprek";
import WachtwoordBeheer from "./WachtwoordBeheer";
import Vandaag from "./os/Vandaag";
import Klanten from "./os/Klanten";
import Facturatie from "./os/Facturatie";
import Nieuwsbrief from "./os/Nieuwsbrief";
import Systeemstatus from "./os/Systeemstatus";
import Control from "./os/Control";
import SeoPerDienst from "./os/SeoPerDienst";
import AiRadar from "./os/AiRadar";
import PersonaWachtrij from "./os/Persona";
import ShopGuard from "./os/ShopGuard";
import { laadTellers } from "./os/actieTellers";
import { LeadKiezer, Kop, Tellers, Groep, Rij, Leeg, KOP_STIJL } from "./os/gedeeld";
import OperatieKnop, { useOperatieStatus } from "./os/OperatieKnop";
import LeesModusLaag from "./os/LeesModusLaag";
import { logLezer, leegLogboek, nieuweSessieNaUitloggen } from "./os/lezerAudit";
import { LEZERS, isLezer, zetLeesModus } from "./os/leesModus";
import TaakInfo from "./os/TaakInfo";
import NotitiesImport from "./os/NotitiesImport";
import { naarServiceRij, naarBrainstormRij } from "./os/notitiesLezen";
import { verduidelijkTaken, mistContext } from "./os/taakVerduidelijking";
import { proposeChange, listProposals, approveProposal, rejectProposal, applyProposal, proposalHeadline } from "./proposalQueue";
import mammoth from "mammoth";
import { useOndertekendeUrl, ondertekendeUrl } from "./os/opslag";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Stuurt het inlogtoken van de ingelogde gebruiker mee. De /api-routes en de
// Supabase-functies weigeren aanvragen zonder geldig token van een teamlid.
async function metSessie(headers = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data && data.session && data.session.access_token;
  return token ? { ...headers, Authorization: `Bearer ${token}` } : { ...headers };
}
function apiFetch(url, opts = {}) {
  return metSessie(opts.headers || {}).then((headers) => fetch(url, { ...opts, headers }));
}

// Vangnet: als een module crasht (bv. een ontbrekende browser-API op mobiel of
// onverwachte datavorm), toont dit een nette foutmelding met 'opnieuw proberen'
// in plaats van een zwart scherm - de rest van de OS (navigatie) blijft staan.
// React error boundaries MOETEN class-componenten zijn; vandaar deze uitzondering
// op de verder function-based codebase.
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; this._gemeld = false; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    try { console.error("Module-crash opgevangen:", err, info); } catch {}
    // Meld de crash automatisch als bug, één keer per crash (niet bij elke re-render).
    if (this._gemeld) return;
    this._gemeld = true;
    try {
      const module = this.props.resetKey || "onbekend";
      const titel = "Automatisch gemeld: crash in " + module;
      const bericht = String(err?.message || err || "Onbekende fout");
      const stack = String(info?.componentStack || "").trim().slice(0, 1500);
      // Geen dubbele meldingen: als er al een open/in_progress bug met deze titel
      // staat, laat die staan i.p.v. de Bug Tracker vol te zetten met herhalingen.
      supabase.from("bugs").select("id").eq("title", titel).in("status", ["open", "in_progress"]).limit(1)
        .then(({ data }) => {
          if (data && data.length) return;
          return supabase.from("bugs").insert([{
            title: titel,
            summary: bericht + (stack ? "\n\nComponent-stack:\n" + stack : ""),
            status: "open", priority: "HIGH", reported_by: "automatisering", module,
          }]);
        }, () => {});
    } catch {}
  }
  // Reset de foutstatus wanneer de gebruiker naar een andere module navigeert
  // (resetKey verandert), ZONDER de children te remounten - zo blijven o.a. een
  // lopende meeting/brainstorm die met display:none verborgen worden gewoon leven.
  componentDidUpdate(prevProps) { if (prevProps.resetKey !== this.props.resetKey && this.state.err) { this._gemeld = false; this.setState({ err: null }); } }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div style={{ padding: 24, maxWidth: 560, margin: "40px auto", background: "#17181d", border: "1px solid rgba(229,86,78,.35)", borderRadius: 12, color: "#e8e8ee", fontFamily: "system-ui, sans-serif" }}>
        <p style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: "#e5564e" }}>Er ging iets mis in deze module</p>
        <p style={{ fontSize: 13, lineHeight: 1.6, color: "#9a9aae", marginBottom: 14 }}>De rest van de OS werkt nog. Probeer de module opnieuw te openen of herlaad de app.</p>
        <pre style={{ fontSize: 11, lineHeight: 1.5, background: "#0e0f13", border: "1px solid #2a2b33", borderRadius: 6, padding: 10, overflowX: "auto", whiteSpace: "pre-wrap", color: "#c7c7d2", marginBottom: 14 }}>{String(this.state.err?.message || this.state.err || "Onbekende fout")}</pre>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { this._gemeld = false; this.setState({ err: null }); }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#5b8def", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Probeer opnieuw</button>
          <button onClick={() => window.location.reload()} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #2a2b33", background: "transparent", color: "#c7c7d2", cursor: "pointer", fontSize: 13 }}>Herlaad app</button>
        </div>
      </div>
    );
  }
}

const LOGO = "data:image/png;base64,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"; // D3-logo (zelfde als de website)

// Zakelijk monochroom palet: grijstinten als basis, één blauw accent, en
// gedempte semantische status-kleuren (succes/fout/waarschuwing). De voormalige
// decoratieve hues (purple/cyan) zijn naar neutrale grijstinten gebracht zodat
// ze niet langer speels opvallen. Keys ongewijzigd → alle referenties intact.
const C = {
  bg: "#0a0a0c", surface: "#131418", surfaceHigh: "#1c1d23",
  border: "#2a2c34", borderHi: "#3b3e48",
  green: "#34bd82", greenDim: "rgba(52,189,130,0.14)", greenMid: "rgba(52,189,130,0.26)",
  red: "#e5564e", redDim: "rgba(229,86,78,0.14)",
  yellow: "#e0a82e", yellowDim: "rgba(224,168,46,0.14)",
  blue: "#5b8eff", blueDim: "rgba(91,142,255,0.14)",
  purple: "#9aa0b0", purpleDim: "rgba(154,160,176,0.12)",
  cyan: "#7f9bb5", cyanDim: "rgba(127,155,181,0.12)",
  text: "#f3f4f6", muted: "#a0a4af", dim: "#4a4d57",
};

// Leesweergave voor lange AI-tekst: koppen, lijstjes, vet en witruimte i.p.v. een muur van tekst.
// Gewone functie (geen component): geen remount bij hertekenen.
function vet(t, sleutel) {
  return String(t).split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((d, i) => d.startsWith("**") && d.endsWith("**") && d.length > 4
    ? <strong key={sleutel + "-" + i} style={{ color: C.text, fontWeight: 650 }}>{d.slice(2, -2)}</strong>
    : d.startsWith("`") && d.endsWith("`") && d.length > 2
      ? <code key={sleutel + "-" + i} style={{ fontSize: 11, background: C.bg, padding: "1px 5px", borderRadius: 4 }}>{d.slice(1, -1)}</code>
      : d);
}
export function LeesTekst(tekst, { maxHoogte } = {}) {
  let t = String(tekst == null ? "" : tekst).replace(/\r\n/g, "\n").trim();
  // Platgeslagen tekst zonder regeleinden: zet de structuur terug op basis van de markdown-tekens
  if (!/\n/.test(t) && t.length > 160) {
    t = t.replace(/\s+(#{1,4}\s)/g, "\n\n$1").replace(/\s+(\d{1,2}[.)]\s+(?=[A-Z*]))/g, "\n$1").replace(/\s+-\s+(?=[A-Z*\[\u201C"(`])/g, "\n- ");
  }
  const blokken = []; let lijst = null;
  const sluit = () => { if (lijst) { blokken.push(lijst); lijst = null; } };
  t.split("\n").forEach((regel, i) => {
    const r = regel.trim();
    if (!r) { sluit(); return; }
    const kop = r.match(/^(#{1,4})\s+(.*)$/);
    const punt = r.match(/^(?:[-*•]|\d{1,2}[.)])\s+(.*)$/);
    if (kop) {
      sluit();
      const n = kop[1].length;
      blokken.push({ soort: "kop", n, tekst: kop[2], i });
    } else if (punt) {
      if (!lijst) lijst = { soort: "lijst", items: [], i };
      lijst.items.push(punt[1]);
    } else { sluit(); blokken.push({ soort: "alinea", tekst: r, i }); }
  });
  sluit();
  return (
    <div style={{ fontSize: 13, lineHeight: 1.7, color: C.text, maxHeight: maxHoogte, overflowY: maxHoogte ? "auto" : undefined, wordBreak: "break-word" }}>
      {blokken.map((b, idx) => b.soort === "kop"
        ? <p key={idx} style={{ fontSize: b.n === 1 ? 15 : b.n === 2 ? 13.5 : 12, fontWeight: 700, color: b.n >= 3 ? C.muted : C.text, textTransform: b.n >= 3 ? "uppercase" : "none", letterSpacing: b.n >= 3 ? ".04em" : 0, margin: (idx === 0 ? 0 : b.n === 1 ? 16 : 18) + "px 0 6px", paddingTop: b.n === 2 && idx > 0 ? 12 : 0, borderTop: b.n === 2 && idx > 0 ? "1px solid " + C.border : "none" }}>{vet(b.tekst, "k" + idx)}</p>
        : b.soort === "lijst"
          ? <ul key={idx} style={{ margin: "0 0 10px", paddingLeft: 20 }}>{b.items.map((x, j) => <li key={j} style={{ margin: "3px 0" }}>{vet(x, "l" + idx + "-" + j)}</li>)}</ul>
          : <p key={idx} style={{ margin: "0 0 10px" }}>{vet(b.tekst, "a" + idx)}</p>)}
    </div>
  );
}

// Bekende bronnen voor bugs die via bug-ingest binnenkomen. Nieuwe diensten
// hoeven hier niet per se in te staan: sourceAppLabel() valt terug op een
// nette naam op basis van de slug, dus een dienst die zijn eigen bugtracker
// aansluit werkt meteen, ook zonder deze lijst bij te werken.
const SOURCE_APP_LABELS = {
  store: "Dif3r3nt Store", casaceleste: "Casa Celeste", limitedfloor: "Limited Floor",
};

/** Leesbare naam voor een source_app-slug: bekende naam, anders de slug netjes gemaakt. */
function sourceAppLabel(slug) {
  if (!slug) return "Dif3r3nt OS";
  if (SOURCE_APP_LABELS[slug]) return SOURCE_APP_LABELS[slug];
  return slug.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

const INIT_SERVICES = [];
const INIT_PIPELINE = [];
// Legacy: demo-financials voor de KPI/rapport-exports. Leeg → exports tonen geen
// live-diensten-regels (echte financiële data komt later uit admin_transactions).
const LIVE = [];
const INIT_LEADS = [];
const DEALS = [];
const AGENT_TOOLS = `
BESCHIKBARE TOOLS — voer altijd de juiste tool uit, beschrijf nooit alleen:

1. create_task — Taak aanmaken
   Velden: title, assignee("furgel"), priority("HIGH"|"MED"|"LOW"), due("YYYY-MM-DD"), actie, waarom, notes, dienst
   - title: korte titel met een werkwoord ("Bel Jan over de offerte"). Nooit vage titels zoals "Document behandelen" of "actie vereist".
   - actie: VERPLICHT. Wat moet er precies gebeuren, in een of twee zinnen, zodat Furgel direct weet wat hij moet doen en waar (welke module, welk document, welke klant).
   - waarom: VERPLICHT. Waarom is dit nodig en wat gaat er mis als het blijft liggen. Noem de aanleiding uit de gegevens.

2. create_event — Agenda-item aanmaken
   Velden: title, date("YYYY-MM-DD"), time("HH:MM"), duration(minuten), type("meeting"|"call"|"demo"|"build"|"deadline"), attendees(["furgel"]), notes

3. create_lead — Lead toevoegen aan CRM
   Velden: name, company, interest, status("new"|"contacted"|"qualified"|"proposal"|"won"|"lost"), value(€), notes

4. create_document — Document opslaan
   Velden: title, type("general"|"notitie"|"rapport"|"validation"|"financial"|"crm"), content, aangemaakt_door("furgel"|"tristan"), dienst

5. add_transaction — Transactie toevoegen aan administratie
   Velden: trans_date("YYYY-MM-DD"), type("income"|"expense"), category("Software/Hosting"|"Marketing"|"Kantoor"|"Reiskosten"|"Personeel"|"Apparatuur"|"Advies"|"Overig"), description, amount_excl(number), vat_rate(0|9|21)

6. add_hours — Zakelijke uren registreren (voor zelfstandigenaftrek)
   Velden: log_date("YYYY-MM-DD"), partner("furgel"|"tristan"), hours(number), category("Development"|"Klantcontact"|"Administratie"|"Sales"|"Research"|"Overig"), description

7. add_km — Zakelijke rit registreren (€0,23/km aftrek)
   Velden: log_date("YYYY-MM-DD"), partner("furgel"|"tristan"), from_loc, to_loc, purpose, km(number)

8. add_mutation — Privé storting of onttrekking registreren
   Velden: mut_date("YYYY-MM-DD"), partner("furgel"|"tristan"), type("storting"|"onttrekking"), amount(number), description

9. research_query — Zoek actuele markt- en bedrijfsinformatie op het internet
   Velden: query(string) — specifieke zoekopdracht voor marktdata, concurrentie, prijzen, wet- of regelgeving

10. log_ev_trip — EV-rit direct registreren in ritregistratie
    Velden: trip_date("YYYY-MM-DD"), driver("furgel"|"tristan"), start_location, end_location, distance_km(number), purpose("zakelijk"|"prive"|"woon_werk"|"gemengd"), description, client_project(optioneel)
`;

const BUSINESS_AGENT_SYS = `Je bent de "Dif3r3nt Agent" — geen assistent, maar de proactieve bedrijfspartner van Dif3r3nt VOF.
Eigenaar: Furgel (CTO). Furgel beoordeelt alles samen met Claude AI.

KARAKTER:
- Direct en scherp. Geen wollige taal, geen onnodige beleefdheden.
- Proactief: signaleer problemen en kansen VOORDAT ze gevraagd worden.
- Groei-gedreven: elke analyse eindigt met concrete acties gericht op meer omzet of minder kosten.
- Wettelijk correct: 100% conform Nederlandse BTW-wet, IB-wet en VOF-regelgeving.

ROLVERDELING — assignee voor create_task is STRIKT op rol:
- FURGEL: alle taken, technisch én commercieel — development, bouw, infra, MVP-implementatie, bugs, deploys, integraties, security, AI/data, maar ook sales, cashflow, omzet, pricing, CRM, leads, outreach, marketing, klantcontact, partnerships en business development
- Administratie/BTW/uren-acties zijn van degene die ze logt (default: degene die de bon/uren noemt)

PROACTIEVE SIGNALEN — Analyseer de context en meld ALTIJD als:
⚠ BTW-deadline binnen 30 dagen (Q1: 30 apr | Q2: 31 jul | Q3: 31 okt | Q4: 31 jan)
⚠ Uren onder tempo voor 1225h-drempel zelfstandigenaftrek (check: uren_tot_nu / maanden_verstreken * 12 < 1225)
⚠ Gemarkeerde documenten die behandeling vereisen
⚠ HIGH-prioriteit taken ouder dan 7 dagen zonder update

PROACTIEVE SIGNALEN — alleen wanneer er ≥ 1 LIVE DIENST is:
⚠ Nettowinst negatief of kosten stijgen sneller dan omzet
⚠ Cashflow-/omzet-/marge-waarschuwingen
⚠ Leads langer dan 14 dagen in dezelfde fase zonder actie

BELANGRIJK — als "Live diensten: 0" in de context staat:
- GEEN cashflow/omzet/sales/marge/pricing-suggesties of -taken aanmaken
- GEEN "verhoog omzet" / "verbeter cashflow" / "scale revenue" advies
- Focus uitsluitend op: validatie-pipeline versnellen, MVP's afronden, eerste dienst lanceren, kostenbeheersing, BTW/uren/administratie compliance
- Een onderneming zonder live diensten heeft eerst een PRODUCT nodig — geen omzet-strategie

ADMINISTRATIE:
- Registreer transacties direct via add_transaction als iemand een bon of factuur noemt
- Registreer uren via add_hours als iemand werkzaamheden noemt
- Bereken automatisch BTW-bedrag (amount_excl * vat_rate / 100)
- Gebruik altijd de juiste BTW-tarief: 21% voor diensten, 9% voor bepaalde goederen, 0% voor export

GROEI-ANALYSE (alleen relevant bij ≥ 1 live dienst):
- Identificeer welke diensten/leads het meest rendabel zijn
- Signaleer wanneer de pipeline leegloopt (te weinig nieuwe leads)
- Stel pricing-verbeteringen voor als marges te laag zijn
- Bereken break-even en geef cashflow-prognose op aanvraag

RESEARCH:
- Gebruik research_query voor actuele marktdata, concurrentieanalyse, prijsbenchmarks of wettelijke updates
- Combineer research-resultaten altijd met de interne context voor een volledig advies
- Denk mee als een strategisch partner: gebruik je kennis proactief bij elke analyse

GEHEUGEN — word slimmer over sessies heen:
- Onder "GELEERDE INZICHTEN" in de context staan eerdere conclusies die JIJ in vorige analyses trok. Bouw hierop voort, herhaal jezelf niet en spreek ze niet tegen zonder reden.
- Voeg het optionele veld "learnings" toe ALLEEN wanneer je een duurzaam, herbruikbaar inzicht over dit bedrijf ontdekt (bv. een patroon, voorkeur, structureel risico of strategische keuze) — niet voor losse feiten of dagkoersen. Houd elk inzicht kort (max 1 zin).

${AGENT_TOOLS}

Reageer UITSLUITEND als JSON — geen tekst buiten JSON:
{
  "result": "direct antwoord + eventuele proactieve waarschuwingen",
  "actions": [
    {"tool": "create_task", "title": "...", "assignee": "furgel", "priority": "HIGH", "due": "YYYY-MM-DD", "actie": "...", "waarom": "...", "notes": "..."},
    {"tool": "add_transaction", "trans_date": "YYYY-MM-DD", "type": "expense", "category": "Software/Hosting", "description": "...", "amount_excl": 0.00, "vat_rate": 21},
    {"tool": "add_hours", "log_date": "YYYY-MM-DD", "partner": "furgel", "hours": 8, "category": "Development", "description": "..."},
    {"tool": "add_km", "log_date": "YYYY-MM-DD", "partner": "furgel", "from_loc": "...", "to_loc": "...", "purpose": "...", "km": 0},
    {"tool": "add_mutation", "mut_date": "YYYY-MM-DD", "partner": "furgel", "type": "onttrekking", "amount": 0, "description": "..."}
  ],
  "suggestions": [{"type": "financieel|groei|btw|uren|crm|risico|optimalisatie", "icon": "emoji", "title": "...", "body": "concrete actie-opdracht"}],
  "learnings": [{"topic": "korte categorie", "insight": "1 duurzaam inzicht over Dif3r3nt"}]
}`;

const AGENT_INIT = [
  { id: "a1", type: "financieel", icon: "◈", title: "Financieel rapport", body: "Analyseer de financiële stand van zaken: kosten per categorie, BTW-saldo per kwartaal en naderende BTW-deadlines. Bij ≥1 live dienst: voeg ook omzet vs kosten, nettowinst en cashflow-risico's toe. Bij 0 live diensten: focus uitsluitend op kostenbeheersing en BTW-compliance — GEEN omzet/cashflow-suggesties." },
  { id: "a2", type: "groei", icon: "⬡", title: "Groei-analyse & pipeline", body: "Analyseer de validatie-pipeline en CRM. Welke validaties staan vast? Welke leads staan al te lang stil? Bij 0 live diensten: prioriteer afronden van eerste MVP en lancering boven alles — wijs alle taken toe aan Furgel." },
  { id: "a3", type: "uren", icon: "◷", title: "Zelfstandigenaftrek check", body: "Controleer de urenregistratie van Furgel. Ligt hij op koers voor de 1225-urendrempel? Bereken hoeveel uur per week nog nodig is. Loopt hij achter, plan dan direct wekelijkse uurblokken in de agenda." },
  { id: "a4", type: "btw", icon: "◫", title: "BTW-aangifte voorbereiding", body: "Controleer welke kwartalen volledig gedocumenteerd zijn. Bereken het BTW-saldo (verschuldigd vs voorbelasting) voor het huidige kwartaal. Signaleer ontbrekende documenten en stel voor wat er nog geüpload moet worden." },
  { id: "a5", type: "optimalisatie", icon: "◑", title: "Kostenoptimalisatie", body: "Analyseer alle kosten van dit jaar per categorie. Identificeer welke kosten te hoog zijn of onvoldoende waarde opleveren. Bereken het potentiële winsteffect bij optimalisatie en stel concrete reductiemaatregelen voor." },
  { id: "a6", type: "risico", icon: "⚠", title: "Risico & compliance scan", body: "Voer een volledige risicoscan uit: financieel (cashflow, debiteurenrisico), juridisch (contracten, BTW-verplichtingen, KvK), en operationeel (single-point-of-failure, kennis-concentratie). Prioriteer risico's en maak direct mitigatie-taken aan." },
];

const SCOL = {
  submitted: { bg: C.blueDim, fg: C.blue }, expanded: { bg: C.blueDim, fg: C.blue },
  pain_scored: { bg: C.yellowDim, fg: C.yellow }, market_scored: { bg: C.yellowDim, fg: C.yellow },
  decision_ready: { bg: C.yellowDim, fg: C.yellow }, review_pending: { bg: C.purpleDim, fg: C.purple },
  approved: { bg: C.greenDim, fg: C.green }, rejected: { bg: C.redDim, fg: C.red },
  mvp_ready: { bg: C.greenMid, fg: C.green }, ready_to_build: { bg: C.blueDim, fg: C.blue },
  in_progress: { bg: C.yellowDim, fg: C.yellow }, blocked: { bg: C.redDim, fg: C.red },
  on_hold: { bg: C.yellowDim, fg: C.yellow },
  mvp_complete: { bg: C.greenDim, fg: C.green },
  test_fase: { bg: C.yellowDim, fg: C.yellow },
  acceptatie: { bg: C.greenDim, fg: C.green },
  ready_for_launch: { bg: C.cyanDim, fg: C.cyan },
  live: { bg: C.greenDim, fg: C.green },
};
const VCOL = { "GO": C.green, "NO-GO": C.red, "WAIT": C.yellow };
const LCOL = { hot: C.red, warm: C.yellow, cold: C.blue };
const PCOL = { HIGH: C.red, MED: C.yellow, LOW: C.blue };
// Prioriteit zoals het takenscherm die kent (HIGH, MED, LOW); agenten schrijven soms MEDIUM of Nederlands.
const taakPrio = (p) => ({ HIGH: "HIGH", HOOG: "HIGH", MED: "MED", MEDIUM: "MED", MIDDEN: "MED", LOW: "LOW", LAAG: "LOW" }[String(p || "").toUpperCase()] || "MED");
const TCOL = { kans: C.green, risico: C.red, prioriteit: C.yellow, crm: C.blue, optimalisatie: C.cyan, bouw: C.purple, taken: C.yellow, agenda: C.purple, admin: C.cyan, email: C.blue, risico: C.red };

function Bdg({ label, state, bg, fg }) {
  const sc = SCOL[state] || { bg: C.border, fg: C.muted };
  return <span style={{ padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700, fontFamily: "monospace", letterSpacing: ".05em", textTransform: "uppercase", background: bg || sc.bg, color: fg || sc.fg }}>{label || (state || "").replace(/_/g, " ")}</span>;
}

function Portal({ children }) { return createPortal(children, document.body); }

function Bar({ val, max = 100, color }) {
  const pct = Math.min((val / max) * 100, 100);
  const col = color || (pct > 75 ? C.green : pct > 50 ? C.yellow : C.red);
  return <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 50, height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}><div style={{ width: pct + "%", height: "100%", background: col, borderRadius: 2 }} /></div><span style={{ fontSize: 11, color: col, fontFamily: "monospace" }}>{val}</span></div>;
}

function Dot() { return <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: C.green, animation: "pulse 1.5s infinite" }} />; }

// Extracts the first valid JSON object from a string, however messy
function extractJSON(raw) {
  if (!raw) return null;
  // Strip markdown fences
  let s = raw.replace(/```json\s*/gi, "").replace(/```\s*/g, "").trim();
  // Try direct parse first
  try { return JSON.parse(s); } catch { }
  // Find first { ... } block. String-bewust, zodat letterlijke { } binnen een
  // waarde (bv. schema_markup) de haakjes-telling niet om zeep helpt.
  const start = s.indexOf("{");
  if (start === -1) return null;
  let depth = 0, end = -1, inStr = false, esc = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inStr) { if (esc) esc = false; else if (ch === "\\") esc = true; else if (ch === '"') inStr = false; continue; }
    if (ch === '"') inStr = true;
    else if (ch === "{") depth++;
    else if (ch === "}") { depth--; if (depth === 0) { end = i; break; } }
  }
  if (end !== -1) { try { return JSON.parse(s.slice(start, end + 1)); } catch { } }
  // Best-effort herstel van AFGEKAPTE JSON (bv. door de token-limiet): sluit open
  // strings/arrays/objecten en verwijder een halve sleutel of komma aan het eind.
  try {
    let t = s.slice(start);
    const st = []; let iS = false, e = false;
    for (let i = 0; i < t.length; i++) {
      const c = t[i];
      if (iS) { if (e) e = false; else if (c === "\\") e = true; else if (c === '"') iS = false; continue; }
      if (c === '"') iS = true;
      else if (c === "{") st.push("}");
      else if (c === "[") st.push("]");
      else if (c === "}" || c === "]") st.pop();
    }
    if (iS) t += '"';
    t = t.replace(/,\s*$/, "").replace(/,?\s*"[^"]*"\s*:\s*$/, "");
    while (st.length) t += st.pop();
    return JSON.parse(t);
  } catch { }
  return null;
}

// Normalise any response into {result, suggestions}
function normalise(parsed, raw) {
  if (!parsed) {
    return { result: raw ? raw.slice(0, 400) : "Geen antwoord ontvangen.", suggestions: [] };
  }
  // Agent drawer format
  if (parsed.result !== undefined) {
    return {
      result: String(parsed.result || ""),
      actions: Array.isArray(parsed.actions) ? parsed.actions : [],
      suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions.map((s, i) => ({
        id: "ai" + Date.now() + i,
        type: s.type || "kans",
        icon: s.icon || "◆",
        title: String(s.title || "Suggestie"),
        body: String(s.body || ""),
      })) : [],
    };
  }
  // Reject improvement format
  if (parsed.verbeterd_concept !== undefined || parsed.verbetering !== undefined) {
    return parsed;
  }
  // Fallback: treat entire object as result
  return { result: JSON.stringify(parsed, null, 2).slice(0, 400), suggestions: [] };
}

// Primair: Claude (via Supabase Edge Function `claude-chat`) voor consistente output.
// Fallback: de oude Groq-proxy (/api/groq) zodat de app blijft werken bij een storing.
async function callGroq(system, user, opts = {}) {
  const fullSystem = system + "\n\nCRITICAL: Respond ONLY with a single valid JSON object. No text before or after. No markdown. No explanation. Just the JSON object starting with { and ending with }.";
  const payload = {
    messages: [{ role: "system", content: fullSystem }, { role: "user", content: user }],
    temperature: opts.temperature ?? 0.3,
    max_completion_tokens: opts.maxTokens || 1000,
  };

  // Gemini-fallback (Google) - zelfde payload, via de provider:"gemini"-tak in /api/groq.
  // Valt buiten Groq's rate-limit, dus redt zware generaties als Groq 429't.
  async function viaGemini() {
    const r = await apiFetch("/api/groq", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, provider: "gemini", model: opts.geminiModel || "gemini-2.5-flash", response_format: { type: "json_object" } }),
    });
    if (!r.ok) { const t = await r.text().catch(() => ""); throw new Error("Gemini HTTP " + r.status + ": " + t.slice(0, 150)); }
    const d = await r.json();
    if (d.error) throw new Error(d.error.message || "gemini error");
    const raw = d.choices?.[0]?.message?.content || "";
    return { raw, parsed: extractJSON(raw) };
  }

  async function viaGroq() {
    try {
      const headers = { "Content-Type": "application/json" };
      const r = await apiFetch("/api/groq", {
        method: "POST",
        headers,
        body: JSON.stringify({ ...payload, model: opts.groqModel || "openai/gpt-oss-120b" }),
      });
      if (!r.ok) { const t = await r.text().catch(() => ""); throw new Error("HTTP " + r.status + ": " + t.slice(0, 150)); }
      const d = await r.json();
      if (d.error) throw new Error(d.error.type + ": " + d.error.message);
      const raw = d.choices?.[0]?.message?.content || "";
      return { raw, parsed: extractJSON(raw) };
    } catch (groqErr) {
      // Groq onbereikbaar of rate-limited (429) -> val terug op Gemini.
      return viaGemini();
    }
  }

  // Zware/lange generaties (bv. het volledige launch-pakket) gaan rechtstreeks naar
  // Groq: Claude (claude-chat) is trager en kapt af op de 60s edge-limiet bij grote
  // JSON-output, wat een lege/afgekapte respons gaf.
  if (opts.groqOnly) return viaGroq();

  // --- Claude (primair) ------------------------------------------------------
  try {
    const base = process.env.REACT_APP_SUPABASE_URL;
    const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;
    if (!base || !anon) throw new Error("no supabase config");
    // claude-chat accepteert alleen het inlogtoken van een teamlid.
    const r = await fetch(`${base}/functions/v1/claude-chat`, {
      method: "POST",
      headers: await metSessie({ "Content-Type": "application/json", apikey: anon }),
      body: JSON.stringify({ ...payload, model: opts.model || "claude-sonnet-4-6" }),
    });
    if (!r.ok) throw new Error("claude HTTP " + r.status);
    const d = await r.json();
    if (d.error) throw new Error(d.error.message || "claude error");
    const raw = d.choices?.[0]?.message?.content || "";
    return { raw, parsed: extractJSON(raw) };
  } catch (claudeErr) {
    return viaGroq();
  }
}

async function claude(system, user) {
  const { raw, parsed } = await callGroq(system, user, { temperature: 0.5, maxTokens: 1500 });
  saveAgentLearnings(parsed); // fire-and-forget: leg duurzame inzichten vast
  return normalise(parsed, raw);
}

// Persisteer optionele "learnings" uit een Claude-antwoord naar agent_memory,
// zodat buildContext() ze in volgende sessies terugvoert (Grow wordt slimmer).
async function saveAgentLearnings(parsed) {
  try {
    const arr = parsed && Array.isArray(parsed.learnings) ? parsed.learnings : [];
    if (!arr.length) return;
    const rows = arr.slice(0, 5).map(l => ({
      topic: l && l.topic ? String(l.topic).slice(0, 120) : null,
      insight: String((l && (l.insight || l.text)) || (typeof l === "string" ? l : "")).slice(0, 500),
      source: "agent",
    })).filter(r => r.insight);
    if (rows.length) await supabase.from("agent_memory").insert(rows);
  } catch { /* geheugen is best-effort, nooit de UX blokkeren */ }
}

async function research(query) {
  const r = await apiFetch("/api/groq", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "compound-beta",
      messages: [
        { role: "system", content: "Je bent een business research assistent voor Dif3r3nt VOF, een Nederlands IT-bedrijf. Geef een gestructureerd, concreet antwoord in het Nederlands met feiten, cijfers en bronnen waar beschikbaar." },
        { role: "user", content: query }
      ],
      temperature: 0.3,
      max_completion_tokens: 1500,
    })
  });
  if (!r.ok) throw new Error("HTTP " + r.status);
  const d = await r.json();
  if (d.error) throw new Error(d.error.message);
  const content = d.choices?.[0]?.message?.content || "";
  const toolsUsed = d.choices?.[0]?.message?.executed_tools || [];
  return { content, sourcesCount: toolsUsed.length };
}

// ─── RANK LOOP ENGINE (ContentOS - Growth-subtab) ─────────────────────────────
// SEO content-levenscyclus: WORK (gechunkte generatie) -> DOC (meta + JSON-LD) ->
// SHIP -> INDEX -> LISTEN, met human-approval-gate. Fase 1: WORK + DOC draaien echt
// (gechunkt om de 60s-edgelimiet te omzeilen); SHIP/INDEX/LISTEN voorlopig handmatig.
// Publiceren = HTML-export naar de statische site (vervolgstap).
const RANK_STAGES = [["work", "WORK"], ["doc", "DOC"], ["ship", "SHIP"], ["index", "INDEX"], ["listen", "LISTEN"]];
const RANK_VOICE_DEFAULT = "Commercieel en overtuigend (Furgel + Claude): klantgericht, helder en concreet, vertaalt techniek naar klantwaarde, eindigt met een duidelijke call-to-action. (Placeholder - later aanscherpen met een echte alinea van Tristan.)";
const rankSlug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

function RankLoop({ user, services = [] }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ keyword: "", content_type: "cluster", dienst: "", voice: RANK_VOICE_DEFAULT });
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");
  const [err, setErr] = useState("");
  const [toast, setToast] = useState(null);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await supabase.from("seo_loop_items").select("*").order("created_at", { ascending: false }).limit(40);
    setItems(data || []);
  })(); }, []);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3000); }

  function stageDone(it) {
    return {
      work: !!it.content,
      doc: !!(it.title && it.description && it.schema_markup),
      ship: it.ship_status === "live",
      index: it.index_status === "indexed",
      listen: (it.metrics && it.metrics.impressions > 0),
    };
  }

  async function generate() {
    const keyword = form.keyword.trim();
    if (!keyword || busy) return;
    setBusy(true); setErr(""); setPhase("Onderzoek (SERP)...");
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 240000);
    let itemId = null;
    try {
      const dienstId = form.dienst ? Number(form.dienst) : null;
      const { data: it, error: ie } = await supabase.from("seo_loop_items").insert([{ keyword, content_type: form.content_type, dienst_id: dienstId, status: "research", created_by: meKey, canonical_url: "https://dif3r3nt.nl/blog/" + rankSlug(keyword) }]).select().single();
      if (ie) throw new Error(ie.message);
      itemId = it.id; setSel(it); setItems(p => [it, ...p]); setShowNew(false); logActivity("content-item", keyword, "growth");
      // Research: SERP via web-search
      const dienstTitle = services.find(s => String(s.id) === form.dienst)?.title || "";
      const sr = await apiFetch("/api/groq", { method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal, body: JSON.stringify({ search: keyword + " " + dienstTitle + " Nederland", max_results: 8 }) });
      const sd = sr.ok ? await sr.json() : { results: [] };
      const serp = (Array.isArray(sd.results) ? sd.results : []).slice(0, 8);
      const research = serp.map((h, i) => "[" + (i + 1) + "] " + (h.title || "") + ": " + String(h.content || "").slice(0, 200)).join("\n");
      // WORK 1: outline
      setPhase("Outline genereren...");
      await supabase.from("seo_loop_items").update({ status: "writing" }).eq("id", it.id);
      const nSec = form.content_type === "pillar" ? 5 : 3;
      const { parsed: outline } = await callGroq("Je bent een Nederlandse SEO-content-strateeg. Antwoord UITSLUITEND met JSON.", `Zoekwoord: "${keyword}". Type: ${form.content_type}. Maak een pakkende H1-titel en ${nSec} secties die de zoekintentie volledig dekken (zodat AI-modellen citeren). Gebruik de SERP-analyse.\nSERP:\n${research}\n\nJSON: {"title":"","sections":[{"heading":"","points":["",""]}]}`, { maxTokens: 900, temperature: 0.5 });
      const title = (outline && outline.title) || keyword;
      const sections = (outline && Array.isArray(outline.sections)) ? outline.sections.slice(0, nSec) : [];
      if (!sections.length) throw new Error("Geen bruikbare outline - probeer opnieuw.");
      // WORK 2: secties gechunkt (elke call ruim binnen 60s)
      const wSys = "Je bent een Nederlandse content-writer voor Dif3r3nt VOF. Stijl: " + form.voice + " Schrijf de sectie als vloeiende markdown-alinea's (herhaal de kop niet), concreet en met diepgang, 150-250 woorden. Geen JSON.";
      const parts = [];
      for (let i = 0; i < sections.length; i++) {
        const sec = sections[i];
        setPhase("Schrijven " + (i + 1) + "/" + sections.length + ": " + (sec.heading || ""));
        const body = await callChatText(wSys, "Artikeltitel: " + title + "\nSectie-kop: " + (sec.heading || "") + "\nTe behandelen: " + ((sec.points || []).join("; ")) + "\n\nSchrijf alleen de sectie-tekst.", { maxTokens: 800, temperature: 0.6 });
        parts.push("## " + (sec.heading || "Sectie " + (i + 1)) + "\n\n" + body);
      }
      const content = "# " + title + "\n\n" + parts.join("\n\n");
      // DOC: meta + FAQ + schema
      setPhase("Metadata + schema (DOC)...");
      const { parsed: doc } = await callGroq("Je bent een SEO/GEO-specialist. Antwoord UITSLUITEND met JSON.", `Voor het artikel "${title}" (zoekwoord "${keyword}"): geef meta_title (max 60 tekens), meta_description (max 155 tekens), 4 FAQ-items, en JSON-LD schema (Article + FAQPage, organisatie "Dif3r3nt", url ${"https://dif3r3nt.nl/blog/" + rankSlug(keyword)}). JSON: {"meta_title":"","meta_description":"","faq":[{"q":"","a":""}],"schema":{}}`, { maxTokens: 1500, temperature: 0.3 });
      const patch = {
        title: (doc && doc.meta_title) || title,
        description: (doc && doc.meta_description) || "",
        content,
        faq: (doc && Array.isArray(doc.faq)) ? doc.faq : [],
        schema_markup: (doc && doc.schema) || null,
        status: "review",
      };
      await supabase.from("seo_loop_items").update(patch).eq("id", it.id);
      const merged = { ...it, ...patch };
      setItems(p => p.map(x => x.id === it.id ? merged : x));
      setSel(merged);
      showToast("✓ Artikel + metadata gegenereerd - klaar voor review");
    } catch (e) {
      const msg = e.name === "AbortError" ? "Generatie duurde te lang - kies 'cluster' i.p.v. 'pillar' of probeer opnieuw." : (e.message || "Generatie mislukt");
      setErr(msg);
      if (itemId) await supabase.from("seo_loop_items").update({ status: "draft" }).eq("id", itemId);
    }
    clearTimeout(to); setBusy(false); setPhase("");
  }

  async function patchItem(id, patch) {
    setItems(p => p.map(x => x.id === id ? { ...x, ...patch } : x));
    setSel(s => (s && s.id === id) ? { ...s, ...patch } : s);
    await supabase.from("seo_loop_items").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
  }
  async function deleteItem(id) {
    if (!window.confirm("Loop-item definitief verwijderen?")) return;
    const { error } = await supabase.from("seo_loop_items").delete().eq("id", id);
    if (error) { showToast("✗ " + error.message); return; }
    setItems(p => p.filter(x => x.id !== id)); if (sel?.id === id) setSel(null);
  }

  const StageBar = ({ it }) => {
    const d = stageDone(it);
    return <span style={{ display: "flex", gap: 4, alignItems: "center" }}>{RANK_STAGES.map(([k, l]) => (
      <span key={k} title={l} style={{ fontSize: 8, padding: "2px 5px", borderRadius: 4, fontFamily: "monospace", letterSpacing: ".04em", background: d[k] ? C.greenDim : C.bg, color: d[k] ? C.green : C.dim, border: "1px solid " + (d[k] ? "rgba(52,189,130,.3)" : C.border) }}>{l}</span>
    ))}</span>;
  };

  const m = sel?.metrics || {};
  const feedbackHint = sel && (m.impressions > 50 && (m.ctr || 0) < 2);

  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace" }}>{toast}</div>}

      {!sel && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <p style={{ fontSize: 12, color: C.muted }}>De SEO-loop: WORK → DOC → SHIP → INDEX → LISTEN, met jouw goedkeuring vóór publicatie.</p>
            <button className="btn bp" style={{ fontSize: 11 }} onClick={() => setShowNew(v => !v)}>+ Nieuw onderwerp</button>
          </div>
          {showNew && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="frm-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Zoekwoord / onderwerp *</p><input value={form.keyword} onChange={e => setForm(p => ({ ...p, keyword: e.target.value }))} placeholder="bv. AVG compliant AI agent Nederland" /></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Type</p><select value={form.content_type} onChange={e => setForm(p => ({ ...p, content_type: e.target.value }))}><option value="pillar">Pillar (lang)</option><option value="cluster">Cluster</option><option value="proof">Bewijs</option></select></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Dienst</p><select value={form.dienst} onChange={e => setForm(p => ({ ...p, dienst: e.target.value }))}><option value="">- geen -</option>{services.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}</select></div>
              </div>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Stijl / toon (later aanscherpen)</p>
              <textarea rows={2} value={form.voice} onChange={e => setForm(p => ({ ...p, voice: e.target.value }))} style={{ fontSize: 12, marginBottom: 10 }} />
              <button className="btn bp" disabled={busy || !form.keyword.trim()} onClick={generate}>{busy ? "⟳ " + (phase || "Bezig...") : "▶ Genereer (WORK + DOC)"}</button>
              <p style={{ fontSize: 10, color: C.dim, marginTop: 8 }}>Research (SERP) → outline → secties gechunkt schrijven → metadata + schema. Een pillar duurt langer (meer secties). Houd het tabblad open.</p>
              {err && <p style={{ fontSize: 12, color: C.red, marginTop: 8 }}>Fout: {err}</p>}
            </div>
          )}
          {items.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen loop-items.</p></div>}
          {items.map(it => (
            <div key={it.id} className="card" style={{ marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }} onClick={() => setSel(it)}>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{it.title || it.keyword}</span>
                <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{it.content_type} · {it.keyword}</span>
              </div>
              <span style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                <Bdg label={(it.status || "draft").toUpperCase()} bg={it.status === "approved" ? C.greenDim : it.status === "review" ? C.yellowDim : C.bg} fg={it.status === "approved" ? C.green : it.status === "review" ? C.yellow : C.muted} />
                <StageBar it={it} />
              </span>
            </div>
          ))}
        </>
      )}

      {sel && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setSel(null)}>← Loop-items</button>
            <button onClick={() => deleteItem(sel.id)} style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "3px 9px" }}>✕ Verwijderen</button>
          </div>
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <p style={{ fontSize: 14, fontWeight: 700 }}>{sel.title || sel.keyword}</p><StageBar it={sel} />
            </div>
            <p style={{ fontSize: 11, color: C.muted }}>{sel.content_type} · zoekwoord: {sel.keyword} · {sel.canonical_url}</p>
            {busy && <p style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginTop: 6 }}>{phase}</p>}
            {err && <p style={{ fontSize: 12, color: C.red, marginTop: 6 }}>Fout: {err}</p>}
          </div>

          {/* DOC */}
          <div className="card" style={{ marginBottom: 12 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>DOC · metadata &amp; schema</p>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Meta title</p><input value={sel.title || ""} onChange={e => patchItem(sel.id, { title: e.target.value })} style={{ marginBottom: 8 }} />
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Meta description</p><textarea rows={2} value={sel.description || ""} onChange={e => patchItem(sel.id, { description: e.target.value })} style={{ fontSize: 12, marginBottom: 8 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Canonical URL</p><input value={sel.canonical_url || ""} onChange={e => patchItem(sel.id, { canonical_url: e.target.value })} /></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>OG image</p><input value={sel.og_image || ""} onChange={e => patchItem(sel.id, { og_image: e.target.value })} placeholder="optioneel" /></div>
            </div>
            {Array.isArray(sel.faq) && sel.faq.length > 0 && <details style={{ marginBottom: 8 }}><summary style={{ fontSize: 11, color: C.muted, cursor: "pointer" }}>FAQ ({sel.faq.length})</summary>{sel.faq.map((f, i) => <div key={i} style={{ marginTop: 6 }}><p style={{ fontSize: 12, fontWeight: 600 }}>{f.q || f.vraag}</p><p style={{ fontSize: 12, color: C.muted }}>{f.a || f.antwoord}</p></div>)}</details>}
            {sel.schema_markup && <details><summary style={{ fontSize: 11, color: C.muted, cursor: "pointer" }}>JSON-LD schema</summary><pre style={{ fontSize: 10, lineHeight: 1.4, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 8, overflowX: "auto", marginTop: 6 }}>{JSON.stringify(sel.schema_markup, null, 2)}</pre></details>}
          </div>

          {/* WORK content */}
          <div className="card" style={{ marginBottom: 12 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>WORK · artikel (markdown)</p>
            <textarea rows={14} value={sel.content || ""} onChange={e => patchItem(sel.id, { content: e.target.value })} style={{ fontSize: 12, fontFamily: "monospace", lineHeight: 1.5 }} placeholder="Nog geen content gegenereerd." />
          </div>

          {/* Approval + SHIP/INDEX/LISTEN */}
          <div className="card">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
              {sel.status !== "approved"
                ? <button className="btn bp" onClick={() => patchItem(sel.id, { status: "approved" })}>✓ Goedkeuren (approval-gate)</button>
                : <Bdg label="GOEDGEKEURD" bg={C.greenDim} fg={C.green} />}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>SHIP-status <span style={{ color: C.dim }}>(publiceren = HTML-export, handmatig)</span></p>
                <select value={sel.ship_status} onChange={e => patchItem(sel.id, { ship_status: e.target.value })}>{["draft", "building", "live"].map(s => <option key={s}>{s}</option>)}</select>
                {sel.ship_status === "live" && <input value={sel.url || ""} onChange={e => patchItem(sel.id, { url: e.target.value })} placeholder="gepubliceerde URL" style={{ marginTop: 6, fontSize: 12 }} />}
              </div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>INDEX-status <span style={{ color: C.dim }}>(sitemap/GSC/IndexNow)</span></p>
                <select value={sel.index_status} onChange={e => patchItem(sel.id, { index_status: e.target.value })}>{["pending", "submitted", "indexed"].map(s => <option key={s}>{s}</option>)}</select>
              </div>
            </div>
            <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>LISTEN · prestaties (GSC, handmatig tot koppeling)</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 8 }}>
              {[["clicks", "Clicks"], ["impressions", "Impressies"], ["ctr", "CTR %"], ["position", "Positie"]].map(([k, l]) => (
                <div key={k}><p style={{ fontSize: 10, color: C.dim, marginBottom: 3 }}>{l}</p><input type="number" step="0.1" value={m[k] ?? 0} onChange={e => patchItem(sel.id, { metrics: { ...m, [k]: Number(e.target.value) } })} style={{ fontSize: 12 }} /></div>
              ))}
            </div>
            {feedbackHint && <p style={{ fontSize: 12, color: C.yellow }}>↺ Feedback-loop: hoge impressies, lage CTR → kansrijk. Overweeg een scherpere title/meta of nieuwe cluster-content rond dit zoekwoord.</p>}
          </div>
        </>
      )}
    </div>
  );
}

// ─── DEV CONSOLE (CTO-only) ───────────────────────────────────────────────────
// Cockpit om gerapporteerde bugs vanuit de OS aan te pakken: AI doet root-cause +
// fix-voorstel, de CTO keurt goed en maakt er een fix-taak van die de dev-pipeline
// oppakt (nu deze Claude-sessie, later evt. een cloud-agent). De OS is de regie-laag;
// het echte code-edit + deploy gebeurt buiten de browser. Alleen zichtbaar voor de CTO.
// Lichte activiteiten-broadcast: als iemand handmatig iets toevoegt in een module,
// krijgen de andere teamleden een popup (ephemeral, net als de nieuw-bericht-melding).
// Het kanaal wordt door App gezet; de actor komt uit de ingelogde gebruiker (sessionStorage).
let _activityChannel = null;
function logActivity(label, title, view) {
  try {
    const u = JSON.parse(sessionStorage.getItem("dif3r3nt_user") || "null");
    const email = u && u.email;
    if (!email || !_activityChannel) return;
    const name = email === "tristan@dif3r3nt.nl" ? "Tristan" : email === "furgel@dif3r3nt.nl" ? "Furgel" : (u.name || email.split("@")[0]);
    _activityChannel.send({ type: "broadcast", event: "activity", payload: { actor_email: email, actor_name: name, label, title: String(title || "").slice(0, 140), view } });
  } catch (e) { /* best-effort, nooit de UX blokkeren */ }
}

const OS_ARCH_CONTEXT = "De Dif3r3nt OS is een single-file React-app (src/App.jsx, ~17.000 regels, Create React App). Modules (nav-id): dashboard, validatie, pipeline, launch, growth, sda, crm, leadfinder, gap, administratie (incl. tab 'boekhouder'), documenten, agenda, taken, meeting, brainstorm, boardroom, bugs, dev. Backend: Supabase Postgres (RLS meestal UIT, custom-auth via sessionStorage; allowlist furgel@dif3r3nt.nl), Edge Functions (claude-chat met dag-spend-cap, agent-invoke, scraping-jobs, legal-intents) en Vercel serverless (/api/groq met Groq llama + Tavily-search-tak, /api/video, /api/notify). AI-calls: callGroq() roept claude-chat (Claude sonnet-4-6) aan met Groq-fallback en forceert JSON; zware/lange JSON-output gaat direct naar /api/groq (Groq, response_format json_object) vanwege een ~60s edge-limiet op claude-chat. Veelvoorkomende bug-bronnen in deze codebase: controlled inputs die terugspringen (optimistic setState nodig vóór async save), realtime-syncs die lokale state overschrijven, de 60s-limiet bij grote LLM-output, RLS, en mobiel (brede .row-grids -> horizontale scroll, service-worker-cache via public/sw.js).";

function DevConsole({ user }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [bugs, setBugs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [sel, setSel] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [tab, setTab] = useState("bugs");
  const [toast, setToast] = useState(null);
  const [openFix, setOpenFix] = useState(null);
  // AutoFix-cockpit: ticket -> triage -> voorstel -> approval-gate -> audit.
  const [tickets, setTickets] = useState([]);
  const [afSel, setAfSel] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [audit, setAudit] = useState([]);
  const [afBusy, setAfBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [ackRisk, setAckRisk] = useState(false);

  useEffect(() => { (async () => {
    const [{ data: b }, { data: t }, { data: tk }] = await Promise.all([
      supabase.from("bugs").select("*").order("created_at", { ascending: false }).limit(60),
      supabase.from("dev_tasks").select("*").order("created_at", { ascending: false }).limit(60),
      supabase.from("autofix_tickets").select("*").order("created_at", { ascending: false }).limit(60),
    ]);
    setBugs(b || []); setTasks(t || []); setTickets(tk || []);
  })(); }, []);

  // Agent op de Mac: hartslag + taakstatus elke 8 s verversen, zodat de console nooit iets toont wat niet klopt.
  const [agentSeen, setAgentSeen] = useState(null);
  const [agentNote, setAgentNote] = useState("");
  const [, setTick] = useState(0);
  useEffect(() => {
    let stop = false;
    async function poll() {
      const [{ data: t }, { data: ag }] = await Promise.all([
        supabase.from("dev_tasks").select("*").order("created_at", { ascending: false }).limit(60),
        supabase.from("dev_agent_status").select("*").eq("id", "mac").maybeSingle(),
      ]);
      if (stop) return;
      if (t) setTasks(t);
      setAgentSeen(ag?.last_seen || null); setAgentNote(ag?.note || "");
      setTick(x => x + 1);
    }
    poll();
    const iv = setInterval(poll, 8000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  const agentOnline = agentSeen && (Date.now() - new Date(agentSeen).getTime()) < 45000;
  const TASK_LABEL = { open: "OPEN", dispatched: "IN WACHTRIJ (NOG NIET OPGEPAKT)", in_progress: "AGENT BEZIG", review: "REVIEW: NIET LIVE", approved: "UITROL AANGEVRAAGD", deploying: "WORDT UITGEROLD", done: "OPGELOST EN LIVE", failed: "MISLUKT", revert_requested: "TERUGDRAAIEN AANGEVRAAGD", reverted: "TERUGGEDRAAID", wont_fix: "WONT FIX" };
  const [openResult, setOpenResult] = useState(null);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3000); }
  const normConf = (v) => { let c = Number(v) || 0; if (c > 0 && c <= 1) c *= 100; return Math.max(0, Math.min(100, Math.round(c))); };

  async function analyzeBug(bug) {
    setSel(bug); setAnalysis(null); setAnalyzing(true);
    try {
      const sys = "Je bent een senior software-engineer die bugs in de Dif3r3nt OS diagnosticeert. Gebruik de architectuur-context. Geef een scherpe root-cause-hypothese, het meest waarschijnlijke bestand/gebied/component, een concrete aanpak in stappen, en een korte voorgestelde patch (diff of pseudocode mag). Antwoord UITSLUITEND met JSON.";
      const usr = "ARCHITECTUUR:\n" + OS_ARCH_CONTEXT + "\n\nBUG:\nTitel: " + (bug.title || "") + "\nModule: " + (bug.module || "?") + "\nSamenvatting: " + (bug.summary || "") + "\nVermoedelijke oorzaak (melder): " + (bug.likely_cause || "-") + "\nImpact: " + (bug.impact || "-") + "\nBestanden (melder): " + (bug.affected_files || "-") + "\n\nJSON: {\"root_cause\":\"\",\"affected_area\":\"bestand/component/module\",\"approach\":\"genummerde stappen\",\"proposed_patch\":\"korte code/diff of pseudocode\",\"confidence\":75}";
      const { parsed } = await callGroq(sys, usr, { maxTokens: 1600, temperature: 0.3 });
      if (!parsed || !(parsed.root_cause || parsed.approach)) throw new Error("Geen bruikbare analyse - probeer opnieuw.");
      setAnalysis(parsed);
    } catch (e) { showToast("✗ " + (e.message || "Analyse mislukt")); }
    setAnalyzing(false);
  }

  async function createTask(bug, a) {
    const row = {
      bug_id: bug.id, title: bug.title || "Fix",
      analysis: a?.root_cause || "",
      affected_area: a?.affected_area || bug.module || "",
      proposed_fix: [a?.approach, a?.proposed_patch].filter(Boolean).join("\n\n"),
      confidence: normConf(a?.confidence), status: "dispatched", created_by: meKey,
    };
    const { data, error } = await supabase.from("dev_tasks").insert([row]).select();
    if (error) { showToast("✗ " + error.message); return; }
    setTasks(p => [data[0], ...p]);
    await supabase.from("bugs").update({ status: "in_progress", updated_at: new Date().toISOString() }).eq("id", bug.id);
    setBugs(p => p.map(x => x.id === bug.id ? { ...x, status: "in_progress" } : x));
    setSel(null); setAnalysis(null); setTab("taken");
    showToast(agentOnline ? "✓ In de wachtrij gezet. De agent pakt hem op; er is nog NIETS opgelost. Volg de status bij Fix-taken." : "⚠ In de wachtrij gezet, maar de agent op je Mac is OFFLINE. De taak blijft wachten tot hij aanstaat. Er is nog NIETS opgelost.");
  }

  async function updateTask(id, patch) {
    setTasks(p => p.map(t => t.id === id ? { ...t, ...patch } : t));
    await supabase.from("dev_tasks").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
  }
  async function deleteTask(id) {
    if (!window.confirm("Fix-taak verwijderen?")) return;
    const { error } = await supabase.from("dev_tasks").delete().eq("id", id);
    if (error) { showToast("✗ " + error.message); return; }
    setTasks(p => p.filter(t => t.id !== id));
  }

  // ── AutoFix ────────────────────────────────────────────────────────────────
  async function sha256hex(s) {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
  }
  // Append-only audit met SHA-256-keten: hash = sha256(prev_hash + entry). De app
  // schrijft alleen INSERTs; de keten maakt manipulatie zichtbaar (validatie bij lezen).
  async function auditLog(entity_type, entity_id, action, metadata) {
    try {
      const { data: last } = await supabase.from("autofix_audit_log").select("hash").order("created_at", { ascending: false }).limit(1).maybeSingle();
      const prev = last?.hash || "GENESIS";
      const entry = JSON.stringify({ entity_type, entity_id, action, actor: meKey, metadata: metadata || {}, at: new Date().toISOString() });
      const hash = await sha256hex(prev + entry);
      await supabase.from("autofix_audit_log").insert([{ entity_type, entity_id, action, actor: meKey, metadata: metadata || {}, prev_hash: prev, hash }]);
    } catch (e) { /* audit is best-effort */ }
  }

  async function openTicket(t) {
    setAfSel(t); setProposal(null); setFeedback(""); setAckRisk(false);
    const [{ data: pr }, { data: au }] = await Promise.all([
      supabase.from("autofix_proposals").select("*").eq("ticket_id", t.id).order("generated_at", { ascending: false }).limit(1),
      supabase.from("autofix_audit_log").select("*").eq("entity_id", t.id).order("created_at", { ascending: true }),
    ]);
    if (pr && pr[0]) setProposal(pr[0]);
    setAudit(au || []);
  }

  // Triage: classificeer een bug tot een AutoFix-ticket (severity + component).
  async function createTicket(bug) {
    setAfBusy("Triage...");
    try {
      const sys = "Je bent een triage-engine voor de Dif3r3nt OS. Classificeer de bug. Antwoord UITSLUITEND met JSON: {\"severity\":\"critical|high|medium|low\",\"component\":\"Auth|API|DB|UI|Other\",\"summary\":\"1 zin\"}.";
      const usr = "ARCHITECTUUR:\n" + OS_ARCH_CONTEXT + "\n\nBUG:\nTitel: " + (bug.title || "") + "\nModule: " + (bug.module || "?") + "\nSamenvatting: " + (bug.summary || "") + "\nImpact: " + (bug.impact || "-");
      const { parsed } = await callGroq(sys, usr, { maxTokens: 300, temperature: 0.2 });
      const sev = ["critical", "high", "medium", "low"].includes(parsed?.severity) ? parsed.severity : "medium";
      const row = { bug_id: bug.id, title: bug.title || "Ticket", summary: stripDash(parsed?.summary || bug.summary || ""), severity: sev, component: String(parsed?.component || bug.module || "Other").slice(0, 40), status: "triaged", created_by: meKey };
      const { data, error } = await supabase.from("autofix_tickets").insert([row]).select();
      if (error) throw new Error(error.message);
      const t = data[0];
      setTickets(p => [t, ...p]);
      await auditLog("ticket", t.id, "create", { severity: sev, component: row.component });
      setTab("autofix"); await openTicket(t);
      showToast("✓ Ticket getriageerd (" + sev + ")");
    } catch (e) { showToast("✗ " + (e.message || "Triage mislukt")); }
    setAfBusy("");
  }

  // Genereer een fix-voorstel (patch + reasoning + confidence) voor het ticket.
  async function genProposal(t) {
    setAfBusy("Fix-voorstel genereren...");
    try {
      const sys = "Je bent een senior software-engineer die fixes voorstelt voor de Dif3r3nt OS. Gebruik de architectuur-context. Geef een unified-diff-achtige patch (of concrete pseudocode als je de exacte bestandsinhoud niet kent), een heldere reasoning, het geraakte gebied en een EERLIJKE confidence 0-100. Antwoord UITSLUITEND met JSON: {\"patch_diff\":\"\",\"reasoning\":\"\",\"affected_area\":\"\",\"confidence\":70}.";
      const usr = "ARCHITECTUUR:\n" + OS_ARCH_CONTEXT + "\n\nTICKET:\nTitel: " + (t.title || "") + "\nSeverity: " + (t.severity || "") + "\nComponent: " + (t.component || "") + "\nSamenvatting: " + (t.summary || "");
      const { parsed } = await callGroq(sys, usr, { groqOnly: true, maxTokens: 2000, temperature: 0.3 });
      if (!parsed || !(parsed.reasoning || parsed.patch_diff)) throw new Error("Geen bruikbaar voorstel.");
      const row = { ticket_id: t.id, patch_diff: stripDash(parsed.patch_diff || ""), reasoning: stripDash(parsed.reasoning || ""), affected_area: parsed.affected_area || t.component || "", confidence: normConf(parsed.confidence) };
      const { data, error } = await supabase.from("autofix_proposals").insert([row]).select();
      if (error) throw new Error(error.message);
      setProposal(data[0]);
      await supabase.from("autofix_tickets").update({ status: "proposed", updated_at: new Date().toISOString() }).eq("id", t.id);
      setTickets(p => p.map(x => x.id === t.id ? { ...x, status: "proposed" } : x));
      setAfSel(x => x && x.id === t.id ? { ...x, status: "proposed" } : x);
      await auditLog("proposal", t.id, "create", { confidence: row.confidence });
      const { data: au } = await supabase.from("autofix_audit_log").select("*").eq("entity_id", t.id).order("created_at", { ascending: true });
      setAudit(au || []);
      showToast("✓ Fix-voorstel gegenereerd");
    } catch (e) { showToast("✗ " + (e.message || "Genereren mislukt")); }
    setAfBusy("");
  }

  // Approval-gate: goedkeuren of afkeuren (met feedback). Afkeuren telt als herziening (cap 2).
  async function decide(t, prop, action) {
    if (action === "approve" && prop && normConf(prop.confidence) < 70 && !ackRisk) { showToast("⚠ Bevestig eerst de high-review-check."); return; }
    setAfBusy(action === "approve" ? "Goedkeuren..." : "Afkeuren...");
    try {
      await supabase.from("autofix_approvals").insert([{ proposal_id: prop?.id || null, ticket_id: t.id, approved_by: meKey, action, feedback: feedback || null }]);
      const newStatus = action === "approve" ? "approved" : "rejected";
      const retries = action === "reject" ? (t.retries || 0) + 1 : (t.retries || 0);
      await supabase.from("autofix_tickets").update({ status: newStatus, retries, updated_at: new Date().toISOString() }).eq("id", t.id);
      setTickets(p => p.map(x => x.id === t.id ? { ...x, status: newStatus, retries } : x));
      setAfSel(x => x && x.id === t.id ? { ...x, status: newStatus, retries } : x);
      await auditLog("approval", t.id, action, { feedback: feedback || "", by: meKey });
      showToast(action === "approve" ? "✓ Door jou goedgekeurd. Er is nog niets aangepast of uitgerold; dat gebeurt pas in een Claude Code-sessie." : (retries <= 2 ? "Afgekeurd - opnieuw voorstellen mag (poging " + retries + "/2)." : "Afgekeurd (max. herzieningen)."));
      setFeedback(""); setAckRisk(false);
      const { data: au } = await supabase.from("autofix_audit_log").select("*").eq("entity_id", t.id).order("created_at", { ascending: true });
      setAudit(au || []);
    } catch (e) { showToast("✗ " + (e.message || "Mislukt")); }
    setAfBusy("");
  }

  const openBugs = bugs.filter(b => b.status !== "resolved" && b.status !== "closed" && b.status !== "done");
  const prioCol = (p) => p === "HIGH" || (p || "").startsWith("HIGH") ? C.red : (p || "").startsWith("MED") || p === "MEDIUM" ? C.yellow : C.muted;
  const stCol = (s) => s === "done" ? C.green : s === "failed" ? C.red : s === "review" ? C.purple : (s === "in_progress" || s === "deploying" || s === "approved" || s === "revert_requested") ? C.yellow : s === "wont_fix" || s === "reverted" ? C.dim : s === "dispatched" ? C.blue : C.muted;
  const afSevCol = (s) => s === "critical" || s === "high" ? C.red : s === "medium" ? C.yellow : C.muted;
  const afStatusCol = (s) => s === "approved" || s === "deployed" ? C.green : s === "rejected" || s === "failed" ? C.red : s === "proposed" ? C.blue : C.muted;

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace" }}>{toast}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <h2 style={{ ...KOP_STIJL }}>Dev Console <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>CTO</span></h2>
          <p style={{ fontSize: 11, color: C.muted }}>Bugs diagnosticeren, fix-voorstel + AutoFix-cockpit met approval-gate en audit-log</p>
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          {[["bugs", "Bugs · " + openBugs.length], ["taken", "Fix-taken · " + tasks.filter(t => t.status !== "done" && t.status !== "wont_fix").length], ["autofix", "AutoFix · " + tickets.filter(t => t.status !== "approved" && t.status !== "deployed" && t.status !== "rejected").length]].map(([k, l]) => (
            <span key={k} className={"tab" + (tab === k ? " on" : "")} onClick={() => { setTab(k); setSel(null); setAnalysis(null); setAfSel(null); }}>{l}</span>
          ))}
        </div>
      </div>

      {tab === "bugs" && !sel && (
        <>
          {openBugs.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Geen openstaande bugs. 🎉</p></div>}
          {openBugs.map(b => (
            <div key={b.id} className="card" style={{ marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }} onClick={() => analyzeBug(b)}>
              <div style={{ minWidth: 0 }}><span style={{ fontSize: 13, fontWeight: 600 }}>{b.title}</span><span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{b.module || "?"} · {(b.created_at || "").slice(0, 10)}</span></div>
              <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                {b.priority && <Bdg label={b.priority} bg={prioCol(b.priority) + "20"} fg={prioCol(b.priority)} />}
                {b.status === "in_progress" && <Bdg label="TAAK AANGEMAAKT (NOG NIET OPGELOST)" bg={C.yellowDim} fg={C.yellow} />}
                <span style={{ fontSize: 11, color: C.blue }}>Analyseer →</span>
              </span>
            </div>
          ))}
        </>
      )}

      {tab === "bugs" && sel && (
        <>
          <button className="btn bg" style={{ marginBottom: 12 }} onClick={() => { setSel(null); setAnalysis(null); }}>← Bugs</button>
          <div className="card" style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{sel.title}</p>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{sel.module || "?"} · {sel.priority || "-"}</p>
            {sel.summary && <p style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 6 }}>{sel.summary}</p>}
            {sel.likely_cause && <p style={{ fontSize: 12, color: C.muted }}>Vermoeden melder: {sel.likely_cause}</p>}
            {sel.screenshot_url && <a href={sel.screenshot_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: C.blue }}>Screenshot bekijken →</a>}
          </div>
          {analyzing && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "6px 2px" }}>AI analyseert de bug...</div>}
          {analysis && (
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <p className="lbl" style={{ margin: 0 }}>AI-hypothese (niet gecontroleerd tegen de echte code)</p>
                <span style={{ fontSize: 11, color: C.muted }}>zekerheid volgens AI (schatting) {normConf(analysis.confidence)}</span>
              </div>
              {analysis.root_cause && <><p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 3 }}>Root-cause</p><p style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 10 }}>{analysis.root_cause}</p></>}
              {analysis.affected_area && <p style={{ fontSize: 12, marginBottom: 10 }}><strong>Gebied:</strong> {analysis.affected_area}</p>}
              {analysis.approach && <><p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 3 }}>Aanpak</p><p style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 10 }}>{analysis.approach}</p></>}
              {analysis.proposed_patch && <><p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 3 }}>Voorgestelde patch</p><pre style={{ fontSize: 11, lineHeight: 1.5, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 10, overflowX: "auto", whiteSpace: "pre-wrap", marginBottom: 12 }}>{analysis.proposed_patch}</pre></>}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn bp" onClick={() => createTask(sel, analysis)}>→ Laat de agent dit oplossen (echte code)</button>
                <button className="btn bg" onClick={() => createTicket(sel)} disabled={!!afBusy}>{afBusy ? "..." : "→ Naar AutoFix (approval-gate)"}</button>
                <button className="btn bg" onClick={() => analyzeBug(sel)} disabled={analyzing}>↻ Opnieuw analyseren</button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "taken" && (
        <>
          <div className="card" style={{ marginBottom: 10, padding: "8px 12px", background: agentOnline ? C.greenDim : C.redDim, border: "1px solid " + (agentOnline ? C.green : C.red) + "44" }}>
            <p style={{ fontSize: 12, color: agentOnline ? C.green : C.red }}>
              {agentOnline ? "● Agent online" + (agentNote ? ": " + agentNote : "") : "● Agent OFFLINE" + (agentSeen ? " (laatst gezien " + new Date(agentSeen).toLocaleString("nl-NL") + ")" : " (nog nooit gezien)") + ". Taken blijven in de wachtrij tot de agent op je Mac draait."}
            </p>
          </div>
          {tasks.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen fix-taken. Analyseer een bug en maak er een taak van.</p></div>}
          {tasks.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: 10, opacity: t.status === "done" || t.status === "wont_fix" ? 0.6 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{t.title}</span>
                <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  {t.confidence != null && <span style={{ fontSize: 10, color: C.dim }}>conf {t.confidence}</span>}
                  <Bdg label={TASK_LABEL[t.status] || t.status.toUpperCase()} bg={stCol(t.status) + "20"} fg={stCol(t.status)} />
                </span>
              </div>
              {t.affected_area && <p style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Gebied: {t.affected_area}</p>}
              {t.analysis && <p style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 6 }}>{t.analysis}</p>}
              {t.proposed_fix && <button className="btn bg" style={{ fontSize: 11, marginBottom: 8 }} onClick={() => setOpenFix(o => o === t.id ? null : t.id)}>{openFix === t.id ? "Verberg voorstel" : "Bekijk fix-voorstel"}</button>}
              {t.proposed_fix && openFix === t.id && <pre style={{ fontSize: 11, lineHeight: 1.5, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 10, overflowX: "auto", whiteSpace: "pre-wrap", marginBottom: 8 }}>{t.proposed_fix}</pre>}
              {t.result && (
                <>
                  <button className="btn bg" style={{ fontSize: 11, marginBottom: 8, marginRight: 8 }} onClick={() => setOpenResult(o => o === t.id ? null : t.id)}>{openResult === t.id || t.status === "review" ? "Verberg verslag en diff" : "Toon verslag van de agent"}</button>
                  {(openResult === t.id || t.status === "review") && <pre style={{ fontSize: 11, lineHeight: 1.5, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 10, overflow: "auto", maxHeight: 420, whiteSpace: "pre-wrap", marginBottom: 8 }}>{t.result}</pre>}
                </>
              )}
              {t.status === "review" && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <button className="btn bp" onClick={() => { if (window.confirm("Deze wijziging nu LIVE zetten (npm run deploy:prod)? Lees eerst de diff.")) updateTask(t.id, { status: "approved" }); }}>✓ Goedkeuren en live zetten</button>
                  <button className="btn bg" onClick={() => { if (window.confirm("Wijziging terugdraaien? De code gaat terug naar de staat van voor deze taak.")) updateTask(t.id, { status: "revert_requested" }); }}>↺ Terugdraaien</button>
                </div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {["open", "failed", "reverted", "wont_fix"].includes(t.status) && (
                  <select value={t.status} onChange={e => updateTask(t.id, { status: e.target.value })} style={{ width: "auto", fontSize: 11, padding: "3px 7px" }}>
                    {[["open", "open"], ["dispatched", "opnieuw door agent laten proberen"], ["wont_fix", "wont fix"], ["failed", "mislukt"], ["reverted", "teruggedraaid"]].filter(([s]) => s === t.status || s === "dispatched" || s === "open" || s === "wont_fix").map(([s, l]) => <option key={s} value={s}>{l}</option>)}
                  </select>
                )}
                <input value={t.preview_url || ""} onChange={e => updateTask(t.id, { preview_url: e.target.value })} placeholder="preview/PR-link" style={{ flex: 1, minWidth: 140, fontSize: 11, padding: "4px 8px" }} />
                {t.preview_url && <a href={t.preview_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: C.blue }}>open →</a>}
                <button onClick={() => deleteTask(t.id)} title="Verwijderen" style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✕</button>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "autofix" && !afSel && (
        <>
          <div className="card" style={{ marginBottom: 12, background: C.purple + "0c", border: "1px solid " + C.purple + "33" }}>
            <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>Ticket -&gt; triage -&gt; AI-fixvoorstel -&gt; <strong>jouw akkoord</strong> -&gt; audit. LET OP: de AI ziet de echte code niet, het voorstel is een onderbouwde gok. Goedkeuren past niets aan en rolt niets uit; de echte fix + test + uitrol gebeurt alleen in een Claude Code-sessie. Alles wordt append-only gelogd (SHA-256-keten).</p>
          </div>
          {tickets.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen tickets. Analyseer een bug en klik "Naar AutoFix".</p></div>}
          {tickets.map(t => (
            <div key={t.id} className="card" style={{ marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }} onClick={() => openTicket(t)}>
              <div style={{ minWidth: 0 }}><span style={{ fontSize: 13, fontWeight: 600 }}>{t.title}</span><span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{t.component || "?"} · {(t.created_at || "").slice(0, 10)}</span></div>
              <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                {t.severity && <Bdg label={t.severity.toUpperCase()} bg={afSevCol(t.severity) + "20"} fg={afSevCol(t.severity)} />}
                <Bdg label={t.status.toUpperCase()} bg={afStatusCol(t.status) + "20"} fg={afStatusCol(t.status)} />
              </span>
            </div>
          ))}
        </>
      )}

      {tab === "autofix" && afSel && (
        <>
          <button className="btn bg" style={{ marginBottom: 12 }} onClick={() => setAfSel(null)}>← Tickets</button>
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <p style={{ fontSize: 14, fontWeight: 700 }}>{afSel.title}</p>
              <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {afSel.severity && <Bdg label={afSel.severity.toUpperCase()} bg={afSevCol(afSel.severity) + "20"} fg={afSevCol(afSel.severity)} />}
                <Bdg label={afSel.status.toUpperCase()} bg={afStatusCol(afSel.status) + "20"} fg={afStatusCol(afSel.status)} />
              </span>
            </div>
            <p style={{ fontSize: 11, color: C.muted }}>Component: {afSel.component || "?"}{afSel.retries ? " · herzieningen: " + afSel.retries + "/2" : ""}</p>
            {afSel.summary && <p style={{ fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>{afSel.summary}</p>}
          </div>

          {afBusy && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "6px 2px" }}>{afBusy}</div>}

          {!proposal && !afBusy && <button className="btn bp" style={{ marginBottom: 12 }} onClick={() => genProposal(afSel)}>▶ Genereer fix-voorstel</button>}

          {proposal && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <p className="lbl" style={{ margin: 0 }}>AI fix-voorstel</p>
                <span style={{ fontSize: 11, fontWeight: 700, color: normConf(proposal.confidence) < 70 ? C.red : C.green }}>confidence {normConf(proposal.confidence)}</span>
              </div>
              {normConf(proposal.confidence) < 70 && <div style={{ fontSize: 11, color: C.red, background: C.redDim, border: "1px solid " + C.red + "44", borderRadius: 6, padding: "7px 10px", marginBottom: 10 }}>⚠ HIGH-REVIEW: lage confidence. Beoordeel extra kritisch voor je goedkeurt.</div>}
              {proposal.reasoning && <><p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 3 }}>Reasoning</p><p style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", marginBottom: 10 }}>{proposal.reasoning}</p></>}
              {proposal.affected_area && <p style={{ fontSize: 12, marginBottom: 10 }}><strong>Gebied:</strong> {proposal.affected_area}</p>}
              {proposal.patch_diff && <><p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 3 }}>Voorgestelde patch</p><pre style={{ fontSize: 11, lineHeight: 1.5, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 10, overflowX: "auto", whiteSpace: "pre-wrap", marginBottom: 12 }}>{proposal.patch_diff}</pre></>}

              {(afSel.status === "proposed" || afSel.status === "rejected") && (
                <>
                  <textarea rows={2} value={feedback} onChange={e => setFeedback(e.target.value)} placeholder="Feedback (optioneel, bv. bij afkeuren)" style={{ width: "100%", marginBottom: 8, fontSize: 12 }} />
                  {normConf(proposal.confidence) < 70 && (
                    <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: C.text, cursor: "pointer", marginBottom: 8 }}>
                      <input type="checkbox" checked={ackRisk} onChange={e => setAckRisk(e.target.checked)} style={{ width: 15, height: 15, accentColor: C.red }} />
                      Ik heb dit high-review-voorstel extra kritisch beoordeeld.
                    </label>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button className="btn bp" disabled={!!afBusy} onClick={() => decide(afSel, proposal, "approve")}>✓ Goedkeuren</button>
                    <button className="btn bg" disabled={!!afBusy} onClick={() => decide(afSel, proposal, "reject")}>✗ Afkeuren</button>
                    {afSel.retries < 2 && <button className="btn bg" disabled={!!afBusy} onClick={() => genProposal(afSel)}>↻ Nieuw voorstel</button>}
                  </div>
                </>
              )}
              {afSel.status === "approved" && <div style={{ fontSize: 12, color: C.green, background: C.greenDim, border: "1px solid " + C.green + "44", borderRadius: 6, padding: "8px 11px" }}>✓ Goedgekeurd. Klaar voor de repo-actie (branch + PR).</div>}
            </div>
          )}

          {audit.length > 0 && (
            <div className="card">
              <p className="lbl" style={{ marginBottom: 8 }}>Audit-log (append-only, SHA-256-keten)</p>
              {audit.map(a => (
                <div key={a.id} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 0", borderBottom: "1px solid " + C.border, fontSize: 11 }}>
                  <span style={{ color: C.muted, fontFamily: "monospace", flexShrink: 0 }}>{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                  <span style={{ fontWeight: 600 }}>{a.entity_type}/{a.action}</span>
                  <span style={{ color: C.muted }}>{a.actor}</span>
                  <span style={{ color: C.dim, fontFamily: "monospace", marginLeft: "auto", flexShrink: 0 }} title={a.hash}>{String(a.hash || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── GAP INTELLIGENCE (Intermezzo - interne MVP) ──────────────────────────────
// Strategic Gap Intelligence: scant een sector via web-search (signalen) en laat de
// Gap Detection Engine (Claude) strategische witte vlekken vinden met een timing-
// window + 3-stap playbook. Interne MVP: single-tenant, hergebruikt /api/groq + de
// agent-infra. Bewijst de engine voordat we het uitspinnen naar een los SaaS.
function GapIntelligence({ user }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [form, setForm] = useState({ sector: "", submarket: "", competitors: "", horizon: 6 });
  const [scans, setScans] = useState([]);
  const [sel, setSel] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");
  const [banner, setBanner] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { (async () => {
    const { data } = await supabase.from("gap_scans").select("*").order("created_at", { ascending: false }).limit(20);
    setScans(data || []);
  })(); }, []);

  async function openScan(s) {
    setSel(s); setErr(""); setBanner("");
    const { data } = await supabase.from("gap_alerts").select("*").eq("scan_id", s.id).order("created_at");
    setAlerts(data || []);
  }

  async function runScan() {
    const sector = form.sector.trim();
    if (!sector || busy) { if (!sector) setBanner("Vul minimaal een sector in."); return; }
    setBusy(true); setBanner(""); setErr(""); setAlerts([]); setPhase("Signalen verzamelen...");
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 90000);
    let scanId = null;
    try {
      const { data: scan, error: se } = await supabase.from("gap_scans").insert([{ sector, submarket: form.submarket || null, competitors: form.competitors || null, horizon_months: Number(form.horizon) || 6, status: "running", created_by: meKey }]).select().single();
      if (se) throw new Error(se.message);
      scanId = scan.id; setSel(scan); setScans(p => [scan, ...p]); logActivity("gap-scan", sector, "gap");
      // 1) Signalen via web-search (Tavily-tak van /api/groq)
      const year = new Date().getFullYear();
      const qbase = `${sector} ${form.submarket || ""}`.trim();
      // Multi-source signalen (nieuws + innovatie/patent + regelgeving) parallel: rijker
      // dan 1 generieke zoekopdracht en in lijn met de SDA-bronnenvisie (zonder de VPS).
      const srcDefs = [
        ["nieuws", `${qbase} nieuws ontwikkelingen marktverschuivingen ${year} Nederland`],
        ["innovatie", `${qbase} patent innovatie nieuwe technologie startup ${year}`],
        ["regelgeving", `${qbase} regelgeving wetgeving subsidie beleid EU ${year} Nederland`],
      ];
      const srRes = await Promise.all(srcDefs.map(([, q]) =>
        apiFetch("/api/groq", { method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal, body: JSON.stringify({ search: q, max_results: 6 }) })
          .then(r => r.ok ? r.json() : { results: [] }).catch(() => ({ results: [] }))
      ));
      const seen = new Set();
      let hits = [];
      srRes.forEach((s, si) => (Array.isArray(s.results) ? s.results : []).forEach(h => {
        const key = (h.url || h.title || "").trim();
        if (key && !seen.has(key)) { seen.add(key); hits.push({ ...h, source_type: srcDefs[si][0] }); }
      }));
      // SDA-resultaten meenemen als ze er al zijn voor deze sector (echte gescrapete bron).
      try {
        const { data: sda } = await supabase.from("scraping_results").select("source_url,data,created_at").ilike("source_url", "%" + (sector.split(" ")[0] || sector) + "%").order("created_at", { ascending: false }).limit(4);
        (sda || []).forEach(h => { const key = (h.source_url || "").trim(); if (key && !seen.has(key)) { seen.add(key); hits.push({ title: "SDA: " + key.replace(/^https?:\/\//, "").slice(0, 50), url: h.source_url, content: JSON.stringify(h.data || {}).slice(0, 360), source_type: "sda" }); } });
      } catch { /* SDA optioneel */ }
      hits = hits.slice(0, 16);
      if (!hits.length) throw new Error("Geen signalen gevonden - pas de sector of submarkt aan.");
      setPhase("Gaps analyseren (" + hits.length + " signalen)...");
      // 2) Gap Detection Engine (Groq JSON-modus - betrouwbaar binnen de 60s-edgelimiet)
      const ctx = hits.map((h, i) => `[S${i + 1}] (${h.source_type || "web"}) ${h.title || ""}\n${h.url || ""}\n${String(h.content || "").slice(0, 340)}`).join("\n\n");
      const sys = "Je bent de Gap Detection Engine van Intermezzo OS (Dif3r3nt VOF). Werkwijze per kans: (1) benoem in je hoofd de OVERDUIDELIJKE trend die iedereen al ziet, (2) bepaal dan de specifieke ONDERBENUTTE gap die er direct NAAST ligt en die de markt negeert - DAT is je alert (de titel beschrijft de gap, niet de trend). Voorbeeld puur ter illustratie van de DENKWIJZE (ander domein, NIET kopieren naar de sector): trend 'iedereen lanceert AI-assistenten' -> gap 'AVG-proof archivering van AI-gesprekslogs voor zorginstellingen'. Pas deze denkwijze toe op de gegeven sector. Combineer signalen uit verschillende bronnen (nieuws/innovatie/regelgeving) tot een cross-domain inzicht. Wees scherp, contrair en concreet; vermijd een algemene trend als titel. Antwoord UITSLUITEND met JSON.";
      const usr = `Sector: ${sector}\nSubmarkt: ${form.submarket || "-"}\nConcurrenten: ${form.competitors || "-"}\nHorizon: ${form.horizon} maanden.\n\nIdentificeer op basis van onderstaande recente signalen precies 3 strategische gaps. Per gap: een pakkende titel, gap_description (wat ontbreekt/onderbelicht is en waarom het structureel groeit), confidence_score (0-100), timing_window_days (geschat actiefvenster), 3 playbook_steps (elk step + rationale + resources), en de relevante bron-nummers (bijv. "S3").\n\nJSON: {"alerts":[{"title":"","gap_description":"","confidence_score":0,"timing_window_days":0,"playbook_steps":[{"step":"","rationale":"","resources":[""]}],"sources":["S1"]}]}\n\nSignalen:\n${ctx}`;
      // Directe Groq-call met JSON-modus (snel + betrouwbaar, en past binnen de 60s-
      // edgelimiet die claude-chat bij deze output overschrijdt). Zelfde patroon als de
      // Lead-finder. Claude/Opus is kwalitatief sterker maar haalt de 60s niet; dat is
      // een vervolgstap (async via een achtergrond-job, niet gebonden aan de edge-limiet).
      const gr = await apiFetch("/api/groq", {
        method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal,
        body: JSON.stringify({ model: "openai/gpt-oss-120b", messages: [{ role: "system", content: sys }, { role: "user", content: usr }], temperature: 0.4, max_completion_tokens: 2800, response_format: { type: "json_object" } }),
      });
      if (!gr.ok) { let det = ""; try { const e = await gr.json(); det = e?.error?.message || ""; } catch { /* geen body */ } throw new Error("Gap-analyse mislukt (HTTP " + gr.status + ")" + (det ? " - " + det : "")); }
      const gd = await gr.json();
      if (gd.error) throw new Error(gd.error.message || "Gap-analyse fout");
      let arr = [];
      try { const p = JSON.parse(gd.choices?.[0]?.message?.content || "{}"); arr = Array.isArray(p.alerts) ? p.alerts : (Array.isArray(p) ? p : []); } catch { arr = []; }
      if (!arr.length) throw new Error("De engine gaf geen bruikbare gaps terug - probeer opnieuw.");
      const rows = arr.slice(0, 4).map(a => ({
        scan_id: scan.id,
        title: String(a.title || "Naamloze gap").slice(0, 200),
        gap_description: String(a.gap_description || ""),
        confidence_score: (function (v) { let c = Number(v) || 0; if (c > 0 && c <= 1) c *= 100; return Math.max(0, Math.min(100, Math.round(c))); })(a.confidence_score),
        timing_window_days: Math.max(0, Math.round(Number(a.timing_window_days) || 0)),
        playbook_steps: Array.isArray(a.playbook_steps) ? a.playbook_steps.slice(0, 6) : [],
        sources: (Array.isArray(a.sources) ? a.sources : []).map(s => { const i = parseInt(String(s).replace(/\D/g, ""), 10) - 1; const h = hits[i]; return h ? { title: h.title, url: h.url } : { title: String(s) }; }),
        status: "new",
      }));
      const { data: ins, error: ie } = await supabase.from("gap_alerts").insert(rows).select();
      if (ie) throw new Error(ie.message);
      setAlerts(ins || []);
      await supabase.from("gap_scans").update({ status: "done", signals_count: hits.length, alerts_count: rows.length }).eq("id", scan.id);
      setScans(p => p.map(x => x.id === scan.id ? { ...x, status: "done", signals_count: hits.length, alerts_count: rows.length } : x));
      setBanner("✓ " + rows.length + " gaps gevonden uit " + hits.length + " signalen.");
    } catch (e) {
      const msg = e.name === "AbortError" ? "De scan duurde te lang (>90s) - probeer een smallere sector/submarkt." : (e.message || "Scan mislukt");
      setErr(msg);
      if (scanId) { await supabase.from("gap_scans").update({ status: "failed", error_message: msg.slice(0, 300) }).eq("id", scanId); setScans(p => p.map(x => x.id === scanId ? { ...x, status: "failed" } : x)); }
    }
    clearTimeout(to); setBusy(false); setPhase("");
  }

  async function setAlertStatus(id, status) {
    setAlerts(p => p.map(a => a.id === id ? { ...a, status } : a));
    await supabase.from("gap_alerts").update({ status }).eq("id", id);
  }
  async function rateAlert(id, rating) {
    setAlerts(p => p.map(a => a.id === id ? { ...a, user_rating: a.user_rating === rating ? 0 : rating } : a));
    const next = (alerts.find(a => a.id === id)?.user_rating === rating) ? 0 : rating;
    await supabase.from("gap_alerts").update({ user_rating: next }).eq("id", id);
  }

  const confColor = (c) => c >= 70 ? C.green : c >= 45 ? C.yellow : C.red;

  return (
    <div className="fu">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ ...KOP_STIJL }}>Gap Intelligence</h2>
          <p style={{ fontSize: 11, color: C.muted }}>Strategic gap detection (Intermezzo MVP) - vindt witte vlekken met een timing-window</p>
        </div>
        {sel && <button className="btn bg" onClick={() => { setSel(null); setAlerts([]); setErr(""); setBanner(""); }}>← Scans</button>}
      </div>

      {!sel && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <p className="lbl" style={{ marginBottom: 10 }}>Nieuwe gap-scan</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Sector *</p><input value={form.sector} onChange={e => setForm(p => ({ ...p, sector: e.target.value }))} placeholder="bijv. logistiek, zorgtech, bouw" /></div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Submarkt</p><input value={form.submarket} onChange={e => setForm(p => ({ ...p, submarket: e.target.value }))} placeholder="bijv. last-mile bezorging" /></div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Concurrenten</p><input value={form.competitors} onChange={e => setForm(p => ({ ...p, competitors: e.target.value }))} placeholder="komma-gescheiden" /></div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Horizon</p>
                <select value={form.horizon} onChange={e => setForm(p => ({ ...p, horizon: Number(e.target.value) }))}>
                  {[3, 6, 12].map(m => <option key={m} value={m}>{m} maanden</option>)}
                </select>
              </div>
            </div>
            <button className="btn bp" disabled={busy || !form.sector.trim()} onClick={runScan}>{busy ? "⟳ " + (phase || "Bezig...") : "▶ Start gap-scan"}</button>
            <p style={{ fontSize: 10, color: C.dim, marginTop: 8 }}>Verzamelt signalen via web-search en laat de engine 3 strategische gaps vinden (Claude, valt onder de dag-spend-cap). Houd het tabblad open.</p>
            {banner && <p style={{ fontSize: 12, color: banner.startsWith("✗") ? C.red : C.green, marginTop: 8 }}>{banner}</p>}
            {err && <p style={{ fontSize: 12, color: C.red, marginTop: 8 }}>Fout: {err}</p>}
          </div>
          <p className="lbl" style={{ marginBottom: 8 }}>Eerdere scans</p>
          {scans.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen scans.</p></div>}
          {scans.map(s => (
            <div key={s.id} className="card" style={{ marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => openScan(s)}>
              <div><span style={{ fontSize: 13, fontWeight: 600 }}>{s.sector}{s.submarket ? " · " + s.submarket : ""}</span><span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{(s.created_at || "").slice(0, 10)} · horizon {s.horizon_months}m</span></div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {s.alerts_count ? <span style={{ fontSize: 11, color: C.muted }}>{s.alerts_count} gaps</span> : null}
                <Bdg label={s.status === "done" ? "KLAAR" : s.status === "running" ? "BEZIG" : "MISLUKT"} bg={C.bg} fg={s.status === "done" ? C.green : s.status === "failed" ? C.red : C.muted} />
              </div>
            </div>
          ))}
        </>
      )}

      {sel && (
        <>
          <div className="card" style={{ marginBottom: 13 }}>
            <p style={{ fontSize: 13 }}><strong>{sel.sector}</strong>{sel.submarket ? " · " + sel.submarket : ""} <span style={{ color: C.muted, fontSize: 12 }}>· horizon {sel.horizon_months} maanden{sel.signals_count ? " · " + sel.signals_count + " signalen" : ""}</span></p>
            {sel.competitors && <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Concurrenten: {sel.competitors}</p>}
          </div>
          {busy && <div style={{ fontSize: 12, color: C.muted, padding: "6px 2px", fontStyle: "italic" }}>{phase || "Bezig..."}</div>}
          {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 12 }}><p style={{ fontSize: 12, color: C.red }}>Fout: {err}</p></div>}
          {alerts.length === 0 && !busy && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Geen gaps voor deze scan.</p></div>}
          {alerts.map(a => (
            <div key={a.id} className="card" style={{ marginBottom: 12, opacity: a.status === "archived" || a.status === "irrelevant" ? 0.55 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{a.title}</span>
                <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: confColor(a.confidence_score), fontWeight: 700 }}>{a.confidence_score}</span>
                  <span style={{ fontSize: 10, color: C.dim }}>conf</span>
                  {a.status !== "new" && <Bdg label={a.status === "saved" ? "BEWAARD" : a.status === "archived" ? "GEARCHIVEERD" : "IRRELEVANT"} bg={C.bg} fg={a.status === "saved" ? C.green : C.muted} />}
                </span>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 8 }}>{a.gap_description}</p>
              {a.timing_window_days > 0 && <p style={{ fontSize: 11, color: C.yellow, marginBottom: 8 }}>⧗ Actiewindow: ~{a.timing_window_days} dagen ({Math.round(a.timing_window_days / 30)} mnd)</p>}
              {Array.isArray(a.playbook_steps) && a.playbook_steps.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Playbook</p>
                  {a.playbook_steps.map((st, i) => (
                    <div key={i} style={{ padding: "5px 0", borderTop: i ? "1px solid " + C.border : "none" }}>
                      <p style={{ fontSize: 12.5, fontWeight: 600 }}>{i + 1}. {st.step}</p>
                      {st.rationale && <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{st.rationale}</p>}
                      {Array.isArray(st.resources) && st.resources.filter(Boolean).length > 0 && <p style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{st.resources.filter(Boolean).join(" · ")}</p>}
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(a.sources) && a.sources.length > 0 && (
                <p style={{ fontSize: 10, color: C.dim, marginBottom: 8 }}>Bronnen: {a.sources.map((s, i) => s.url ? <a key={i} href={s.url} target="_blank" rel="noreferrer" style={{ color: C.blue, marginRight: 8 }}>{(s.title || s.url).slice(0, 40)}</a> : <span key={i} style={{ marginRight: 8 }}>{(s.title || "").slice(0, 40)}</span>)}</p>
              )}
              <p style={{ fontSize: 9, color: C.dim, marginBottom: 8 }}>Gegenereerd door Intermezzo AI · controleer vóór je handelt.</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button className="btn bg" onClick={() => setAlertStatus(a.id, a.status === "saved" ? "new" : "saved")} style={{ fontSize: 11 }}>{a.status === "saved" ? "✓ Bewaard" : "Bewaren"}</button>
                <button className="btn bg" onClick={() => setAlertStatus(a.id, "archived")} style={{ fontSize: 11 }}>Archiveer</button>
                <button className="btn bg" onClick={() => setAlertStatus(a.id, "irrelevant")} style={{ fontSize: 11 }}>Irrelevant</button>
                <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button onClick={() => rateAlert(a.id, 1)} title="Relevant" style={{ background: "none", border: "1px solid " + (a.user_rating === 1 ? C.green : C.border), borderRadius: 5, color: a.user_rating === 1 ? C.green : C.muted, cursor: "pointer", padding: "3px 9px", fontSize: 12 }}>▲</button>
                  <button onClick={() => rateAlert(a.id, -1)} title="Niet relevant" style={{ background: "none", border: "1px solid " + (a.user_rating === -1 ? C.red : C.border), borderRadius: 5, color: a.user_rating === -1 ? C.red : C.muted, cursor: "pointer", padding: "3px 9px", fontSize: 12 }}>▼</button>
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

// ─── AI LEAD-FINDER ───────────────────────────────────────────────────────────
// Zoekt via web-search (Groq compound-beta, dezelfde infra als research_query)
// echte NL-bedrijven en zet geselecteerde resultaten als 'cold' B2B-leads in het
// CRM. Review-vóór-toevoegen: niets landt automatisch in de pijplijn.
// AVG bij koude acquisitie: een NL 06-nummer is een persoonlijk mobiel nummer
// (persoonsgegeven) en mag NOOIT worden opgeslagen/gebruikt voor ongevraagde verkoop.
// Koude acquisitie mag alleen naar rechtspersonen (BV/NV); ZZP/VOF/eenmanszaak gelden
// als natuurlijk persoon en worden niet met (persoonlijke) contactgegevens opgeslagen.
function isNlMobile(tel) {
  let d = String(tel || "").replace(/\D/g, "");
  if (d.startsWith("0031")) d = d.slice(2);
  if (d.startsWith("31")) d = "0" + d.slice(2);
  return /^06\d{8}$/.test(d);
}
function isRechtspersoon(x) {
  const rv = String((x && x.rechtsvorm) || "").toLowerCase().replace(/[^a-z]/g, "");
  if (rv === "bv" || rv === "nv") return true;
  return /\b(b\.?\s?v\.?|n\.?\s?v\.?)\b/i.test(String((x && x.naam) || ""));
}
// Geeft alleen een AVG-toegestaan telefoonnummer terug: geen 06 (persoonlijk), en een
// vast nummer uitsluitend bij een rechtspersoon (BV/NV).
function avgPhone(tel, rechtspersoon) {
  const t = String(tel || "").trim();
  if (!t || isNlMobile(t)) return "";
  return rechtspersoon ? t : "";
}

function LeadFinder() {
  const [q, setQ] = useState({ branche: "", regio: "", aantal: 10, zonderWebsite: true, alleenRechtspersoon: false });
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState({});
  const [rawFallback, setRawFallback] = useState("");
  const selCount = Object.values(sel).filter(Boolean).length;

  // Verifieert gericht of een bedrijf een EIGEN website heeft. Nodig omdat het LLM
  // "heeft_website" vaak fout gokt: het bedrijfsdomein staat meestal niet in de
  // algemene zoek-snippets (die bevatten maps/telefoongids/social). We zoeken per
  // bedrijf op naam + plaats en herkennen een plausibel eigen domein.
  async function verifyWebsite(naam, plaats, signal) {
    const nm = String(naam || "").trim();
    if (nm.length < 2) return "";
    try {
      const r = await apiFetch("/api/groq", {
        method: "POST", headers: { "Content-Type": "application/json" }, signal,
        body: JSON.stringify({ search: (`"${nm}" ${plaats || ""} officiele website`).trim(), max_results: 5 }),
      });
      if (!r.ok) return "";
      const d = await r.json();
      const items = Array.isArray(d.results) ? d.results : [];
      const bad = /(facebook|instagram|linkedin|twitter|x\.com|google\.|goo\.gl|maps\.|tripadvisor|thuisbezorgd|ubereats|deliveroo|telefoonboek|detelefoongids|kvk\.|funda|marktplaats|yelp|opentable|iens\.|resengo|wikipedia|youtube|pinterest|booking\.|trustpilot|horeca\.)/i;
      const toks = nm.toLowerCase().replace(/[^a-z0-9 ]/g, " ").split(/\s+/).filter(t => t.length > 2 && !["the", "van", "den", "het", "vof"].includes(t));
      for (const it of items) {
        let host = "";
        try { host = new URL(String(it.url || "")).hostname.replace(/^www\./, "").toLowerCase(); } catch { continue; }
        if (!host || bad.test(host)) continue;
        if (!/\.(nl|com|eu|net|be|org)$/.test(host)) continue;
        const flat = host.replace(/[^a-z0-9]/g, "");
        if (toks.some(t => flat.includes(t.replace(/[^a-z0-9]/g, "")))) return "https://" + host;
      }
      return "";
    } catch { return ""; }
  }

  async function search() {
    if (!q.branche.trim()) { setBanner("⚠ Vul minimaal een branche in (bijv. kappers, loodgieters)."); return; }
    setBusy(true); setBanner(""); setResults([]); setSel({}); setRawFallback("");
    const region = q.regio.trim() || "Nederland";
    const searchQ = `${q.branche} ${region} bedrijf adres telefoonnummer` + (q.zonderWebsite ? " telefoongids" : "");
    const ctrl = new AbortController();
    const to = setTimeout(() => ctrl.abort(), 58000);
    try {
      // 1) Web-search via Tavily (search-tak van /api/groq — geen extra functie)
      const sr = await apiFetch("/api/groq", {
        method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal,
        body: JSON.stringify({ search: searchQ, max_results: Math.min(20, Math.max(8, q.aantal * 2)) }),
      });
      if (!sr.ok) {
        let detail = ""; try { const eb = await sr.json(); detail = eb?.error?.message || ""; } catch { /* geen body */ }
        if (detail.includes("TAVILY_API_KEY")) throw new Error("Zoek-API nog niet geconfigureerd — de Tavily-sleutel ontbreekt op de server.");
        throw new Error("Zoeken mislukt (HTTP " + sr.status + ")" + (detail ? " — " + detail : ""));
      }
      const sd = await sr.json();
      const hits = Array.isArray(sd.results) ? sd.results : [];
      if (!hits.length) { setBanner("Geen zoekresultaten — pas branche of regio aan."); setBusy(false); clearTimeout(to); return; }

      // 2) Structureren tot leads via llama-3.3-70b (JSON-modus)
      const ctx = hits.slice(0, 15).map((h, i) => `[${i + 1}] ${h.title || ""}\nURL: ${h.url || ""}\n${String(h.content || "").slice(0, 400)}`).join("\n\n");
      const sys = "Je bent een B2B lead-extractor voor Dif3r3nt VOF (NL IT-bedrijf dat websites/software bouwt). Haal uit de gegeven zoekresultaten ECHTE Nederlandse bedrijven. Verzin niets; gebruik alleen wat in de tekst staat. Neem GEEN privegegevens van personen op, alleen zakelijke bedrijfsgegevens. Geef UITSLUITEND geldige JSON: {\"leads\":[{\"naam\":\"\",\"plaats\":\"\",\"branche\":\"\",\"telefoon\":\"\",\"website\":\"\",\"heeft_website\":true,\"rechtsvorm\":\"\",\"bron\":\"\"}]}. Zet heeft_website=true alleen bij een eigen bedrijfsdomein (geen telefoongids/social/maps); anders false en website leeg. Bepaal rechtsvorm uit de naam/tekst: een van \"BV\", \"NV\", \"VOF\", \"eenmanszaak\", \"zzp\" of \"onbekend\"; verzin niets.";
      const usr = `Branche: ${q.branche}. Regio: ${region}. ${q.zonderWebsite ? "Geef BIJ VOORKEUR bedrijven zonder eigen website." : ""} Haal maximaal ${q.aantal} bedrijven uit deze zoekresultaten en geef alleen de JSON:\n\n${ctx}`;
      const gr = await apiFetch("/api/groq", {
        method: "POST", headers: { "Content-Type": "application/json" }, signal: ctrl.signal,
        body: JSON.stringify({ model: "openai/gpt-oss-120b", messages: [{ role: "system", content: sys }, { role: "user", content: usr }], temperature: 0.2, max_completion_tokens: 1800, response_format: { type: "json_object" } }),
      });
      if (!gr.ok) {
        let detail = ""; try { const eb = await gr.json(); detail = eb?.error?.message || ""; } catch { /* geen body */ }
        throw new Error("Verwerken mislukt (HTTP " + gr.status + ")" + (detail ? " — " + detail : ""));
      }
      const gd = await gr.json();
      if (gd.error) throw new Error(gd.error.message);
      const content = gd.choices?.[0]?.message?.content || "";
      let arr = [];
      try { const p = JSON.parse(content); arr = Array.isArray(p.leads) ? p.leads : (Array.isArray(p) ? p : []); }
      catch { const m = content.match(/\{[\s\S]*\}/); if (m) { try { arr = JSON.parse(m[0]).leads || []; } catch { /* niets */ } } }
      if (!arr.length) { setRawFallback(content.slice(0, 1500)); setBanner("Geen bedrijven uit de resultaten kunnen halen — verfijn je zoekopdracht."); setBusy(false); clearTimeout(to); return; }
      arr = arr
        .filter(x => x && typeof x === "object" && String(x.naam || "").trim().length > 1)
        .slice(0, Math.min(20, Math.max(q.aantal, q.aantal * 2)));
      // Website-verificatie: de LLM-gok ("heeft_website") is onbetrouwbaar, dus we
      // checken per bedrijf gericht of er echt een eigen site is en overschrijven de gok.
      setBanner("Websites verifieren...");
      arr = await Promise.all(arr.map(async x => {
        const found = await verifyWebsite(x.naam, x.plaats, ctrl.signal);
        const website = found || String(x.website || "").trim();
        return { ...x, website, heeft_website: !!website };
      }));
      arr = arr
        .filter(x => !q.zonderWebsite || !x.heeft_website)
        .filter(x => !q.alleenRechtspersoon || isRechtspersoon(x))
        .slice(0, q.aantal)
        .map((x, i) => ({ ...x, _id: i, _rp: isRechtspersoon(x) }));
      setResults(arr);
      setSel(Object.fromEntries(arr.map(x => [x._id, true])));
      setBanner(arr.length ? "✓ " + arr.length + " bedrijven gevonden — controleer en kies wat naar het CRM mag." : "Geen geschikte bedrijven gevonden — pas branche/regio aan of zet 'alleen zonder website' uit.");
    } catch (e) {
      const msg = e.name === "AbortError"
        ? "Zoeken duurde te lang (>58s) — kies een lager aantal of een specifiekere regio."
        : (e.message === "Load failed" || e.message === "Failed to fetch")
          ? "Verbinding of timeout — probeer het opnieuw."
          : (e.message || "Zoeken mislukt");
      setBanner("✗ " + msg);
    } finally { clearTimeout(to); }
    setBusy(false);
  }

  async function addSelected() {
    const chosen = results.filter(x => sel[x._id]);
    if (!chosen.length) { setBanner("⚠ Selecteer minstens één bedrijf."); return; }
    setBusy(true); setBanner("");
    const rows = chosen.map(x => {
      const rp = isRechtspersoon(x);
      const tel = avgPhone(x.telefoon, rp);
      const avgNote = rp
        ? "AVG: rechtspersoon (BV/NV) - koude acquisitie via een algemeen bedrijfsnummer toegestaan."
        : "AVG: natuurlijk persoon (ZZP/VOF/eenmanszaak of onbekend) - GEEN koude acquisitie naar persoonlijke gegevens; benader alleen via een algemeen bedrijfskanaal.";
      return {
        name: String(x.naam || "").slice(0, 120), company: String(x.naam || "").slice(0, 120),
        interest: (x.heeft_website ? (String(x.branche || "") || "Via AI Lead-finder") : "Web/OS - geen eigen website").slice(0, 160),
        status: "cold", value: 0, segment: "b2b",
        phone: tel || null,
        industry: (String(x.branche || "") || q.branche).slice(0, 80), company_size: "Micro (MKB)",
        source: "AI Lead-finder (compound-beta)",
        notes: [x.plaats && ("Plaats: " + x.plaats), x.website && ("Site: " + x.website), x.rechtsvorm && ("Rechtsvorm: " + x.rechtsvorm), x.bron && ("Bron: " + x.bron), avgNote, "AI-voorgesteld via web-search - verifieer voor benaderen."].filter(Boolean).join("\n").slice(0, 600),
      };
    });
    const { error } = await supabase.from("leads").insert(rows);
    if (error) setBanner("✗ Toevoegen mislukt: " + error.message);
    else { setBanner("✓ " + rows.length + " leads toegevoegd aan CRM (B2B · cold). Open CRM → B2B om ze te zien."); setResults([]); setSel({}); }
    setBusy(false);
  }

  return (
    <div className="fu">
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 6 }}>
        <h2 style={{ ...KOP_STIJL }}>Lead-finder</h2>
      </div>
      <p style={{ fontSize: 12, color: C.muted, marginBottom: 8, maxWidth: 720 }}>
        Zoekt via web-search echte Nederlandse bedrijven en zet je selectie als <strong>cold</strong> B2B-leads in het CRM. Tip: filter op bedrijven zonder eigen website - ideale prospects voor web/OS.
      </p>
      <p style={{ fontSize: 11, color: C.yellow, marginBottom: 16, maxWidth: 720, lineHeight: 1.5 }}>
        <strong>AVG:</strong> 06-nummers (persoonlijk mobiel) worden nooit opgeslagen. Koude acquisitie mag alleen naar <strong>rechtspersonen (BV/NV)</strong>; bij ZZP/VOF/eenmanszaak bewaren we geen telefoonnummer - benader die uitsluitend via een algemeen bedrijfskanaal. Verifieer altijd voor je benadert.
      </p>

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 0.8fr", gap: 10 }}>
          <div><p className="lbl" style={{ marginBottom: 5 }}>Branche *</p><input value={q.branche} onChange={e => setQ(p => ({ ...p, branche: e.target.value }))} placeholder="bijv. kappers, loodgieters, snackbars" /></div>
          <div><p className="lbl" style={{ marginBottom: 5 }}>Regio / plaats</p><input value={q.regio} onChange={e => setQ(p => ({ ...p, regio: e.target.value }))} placeholder="bijv. Tilburg, Brabant (leeg = heel NL)" /></div>
          <div><p className="lbl" style={{ marginBottom: 5 }}>Aantal</p><input type="number" min={1} max={25} value={q.aantal} onChange={e => setQ(p => ({ ...p, aantal: Math.max(1, Math.min(25, parseInt(e.target.value) || 1)) }))} /></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: C.text, cursor: "pointer" }}>
            <input type="checkbox" checked={q.zonderWebsite} onChange={e => setQ(p => ({ ...p, zonderWebsite: e.target.checked }))} style={{ width: 15, height: 15, accentColor: C.blue }} />
            Alleen bedrijven zonder eigen website
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: C.text, cursor: "pointer" }} title="Koude acquisitie mag volgens de AVG alleen naar rechtspersonen (BV/NV).">
            <input type="checkbox" checked={q.alleenRechtspersoon} onChange={e => setQ(p => ({ ...p, alleenRechtspersoon: e.target.checked }))} style={{ width: 15, height: 15, accentColor: C.blue }} />
            Alleen rechtspersonen (BV/NV) - AVG-veilig
          </label>
          <button className="btn bp" disabled={busy} onClick={search} style={{ marginLeft: "auto" }}>{busy && !results.length ? "◌ Zoeken…" : "Zoek bedrijven"}</button>
        </div>
      </div>

      {banner && <div style={{ marginBottom: 12, padding: "9px 13px", borderRadius: 7, fontSize: 12, background: banner.startsWith("✗") ? C.redDim : banner.startsWith("⚠") ? C.yellowDim : C.greenDim, border: "1px solid " + (banner.startsWith("✗") ? C.red : banner.startsWith("⚠") ? C.yellow : C.green) + "55", color: banner.startsWith("✗") ? C.red : banner.startsWith("⚠") ? C.yellow : C.green }}>{banner}</div>}

      {rawFallback && <div className="card" style={{ marginBottom: 14 }}><p className="lbl" style={{ marginBottom: 7 }}>Ruwe uitvoer</p><pre style={{ whiteSpace: "pre-wrap", fontSize: 11, color: C.muted, fontFamily: "monospace", margin: 0 }}>{rawFallback}</pre></div>}

      {results.length > 0 && (
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 10 }}>
            <p className="lbl" style={{ margin: 0 }}>{results.length} resultaten · {selCount} geselecteerd</p>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setSel(Object.fromEntries(results.map(x => [x._id, true])))}>Alles</button>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setSel({})}>Niets</button>
            <button className="btn bp" style={{ marginLeft: "auto" }} disabled={busy || !selCount} onClick={addSelected}>{busy ? "◌ Bezig…" : "→ Toevoegen aan CRM (" + selCount + ")"}</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "28px 1.6fr 1fr 1.3fr 1fr 90px", gap: 8, padding: "6px 4px", borderBottom: "1px solid " + C.borderHi }}>
            {["", "Bedrijf", "Plaats", "Branche", "Telefoon", "Website"].map((h, i) => <span key={i} className="lbl" style={{ fontSize: 9 }}>{h}</span>)}
          </div>
          {results.map(x => {
            const heeftSite = !!String(x.website || "").trim() || x.heeft_website === true;
            return (
              <div key={x._id} style={{ display: "grid", gridTemplateColumns: "28px 1.6fr 1fr 1.3fr 1fr 90px", gap: 8, padding: "9px 4px", borderBottom: "1px solid " + C.border, alignItems: "center", fontSize: 12 }}>
                <input type="checkbox" checked={!!sel[x._id]} onChange={e => setSel(p => ({ ...p, [x._id]: e.target.checked }))} style={{ width: 15, height: 15, accentColor: C.blue }} />
                <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.naam} <span style={{ fontSize: 9, fontWeight: 700, color: x._rp ? C.green : C.yellow }}>{x._rp ? "BV/NV" : "ZZP/VOF"}</span></span>
                <span style={{ color: C.muted }}>{x.plaats || "-"}</span>
                <span style={{ color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.branche || "-"}</span>
                <span style={{ color: C.muted, fontFamily: "monospace", fontSize: 11 }} title={avgPhone(x.telefoon, x._rp) ? "" : "AVG: 06 of geen rechtspersoon, niet opgeslagen"}>{avgPhone(x.telefoon, x._rp) || (x.telefoon ? "AVG" : "-")}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: heeftSite ? C.yellow : C.green }}>{heeftSite ? "heeft site" : "geen site"}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{background:#0a0a0c;color:#f3f4f6;font-family:'DM Sans',sans-serif;overflow-x:hidden}
::-webkit-scrollbar{width:3px;height:3px}
::-webkit-scrollbar-track{background:#0a0a0c}
::-webkit-scrollbar-thumb{background:#3b3e48;border-radius:2px}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes slideIn{from{opacity:0;transform:translateX(100%)}to{opacity:1;transform:translateX(0)}}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes spin{to{transform:rotate(360deg)}}
.fu{animation:fadeUp .25s ease both}
.chat-reply-btn{opacity:0;transition:opacity .12s}
.chat-msg:hover .chat-reply-btn,.chat-reply-btn:focus{opacity:1}
@media(hover:none){.chat-reply-btn{opacity:.6}}
.card{background:#131418;border:1px solid #2a2c34;border-radius:8px;padding:14px 16px;position:relative;overflow:hidden}
.card::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(91,142,255,.25),transparent)}
.btn{padding:7px 13px;border-radius:6px;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:500;transition:all .15s}
.bp{background:#5b8eff;color:#fff}.bp:hover{background:#4476e6;transform:translateY(-1px)}
.bg{background:transparent;color:#a0a4af;border:1px solid #2a2c34}.bg:hover{border-color:#5b8eff;color:#5b8eff}
.bd{background:rgba(229,86,78,.14);color:#e5564e;border:1px solid rgba(229,86,78,.3)}.bd:hover{background:rgba(229,86,78,.24)}
.tab{padding:6px 11px;border-radius:5px;cursor:pointer;font-size:11px;font-weight:700;font-family:monospace;text-transform:uppercase;letter-spacing:.05em;transition:all .15s;color:#a0a4af;border:1px solid transparent;display:inline-block}
.tab.on{color:#5b8eff;background:rgba(91,142,255,.12);border-color:rgba(91,142,255,.22)}
.tab:hover:not(.on){color:#f3f4f6}
.nav{padding:9px 11px;border-radius:6px;cursor:pointer;display:flex;align-items:center;gap:9px;font-size:13px;font-weight:500;color:#a0a4af;border:1px solid transparent;transition:all .15s;margin-bottom:2px}
.nav:hover{background:#1c1d23;color:#f3f4f6}
.nav.on{background:rgba(91,142,255,.12);color:#5b8eff;border-color:rgba(91,142,255,.18)}
.row{display:grid;padding:11px 14px;border-bottom:1px solid #2a2c34;align-items:center;transition:background .1s}
.row:hover{background:#1c1d23}
.lbl{font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#a0a4af;font-family:monospace}
input,select,textarea{background:#0a0a0c;border:1px solid #2a2c34;border-radius:6px;color:#f3f4f6;font-family:'DM Sans',sans-serif;font-size:13px;padding:8px 11px;outline:none;transition:border-color .15s;width:100%}
input:focus,select:focus,textarea:focus{border-color:rgba(91,142,255,.45)}
.ovl{position:fixed;inset:0;background:rgba(0,0,0,.78);z-index:9999;display:flex;align-items:flex-start;justify-content:center;backdrop-filter:blur(4px);padding:5vh 16px;overflow-y:auto}
.mdl{background:#131418;border:1px solid #2a2c34;border-radius:12px;padding:24px;width:100%;max-width:520px;margin:auto;animation:fadeUp .2s ease;max-height:82vh;overflow-y:auto}
.kc{background:#0a0a0c;border:1px solid #2a2c34;border-radius:6px;padding:12px;margin-bottom:8px;cursor:pointer;transition:border-color .15s}
.kc:hover{border-color:rgba(91,142,255,.35)}
.kl{background:#131418;border:1px solid #2a2c34;border-radius:8px;padding:11px;min-width:185px;flex:1}
.st{background:#131418;border:1px solid #2a2c34;border-radius:8px;padding:10px 12px;position:relative;overflow:hidden}
.st p[style*="font-size: 2"]{font-size:19px!important}
.st::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2px;background:var(--ac);opacity:.5}
.drw{position:fixed;top:0;right:0;bottom:0;width:305px;background:#131418;border-left:1px solid #2a2c34;z-index:1000;display:flex;flex-direction:column;animation:slideIn .25s ease}
.drw-backdrop{display:none;position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:999;animation:fadeIn .2s ease}

/* ── MOBILE & TABLET RESPONSIVE ───────────────────────────── */
.hamburger{display:none;background:transparent;border:1px solid #2a2c34;border-radius:6px;padding:6px 10px;color:#5b8eff;cursor:pointer;font-size:18px;line-height:1;align-items:center;justify-content:center}
.sb-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:199;opacity:0;pointer-events:none;transition:opacity .2s}
.sb-backdrop.open{opacity:1;pointer-events:auto}
.scroll-x{overflow-x:auto;-webkit-overflow-scrolling:touch}
.scroll-x::-webkit-scrollbar{height:4px}

/* Tabbalk onderin + "Meer"-paneel (telefoon/tablet): alles binnen duimbereik, net als de Store */
.tabbar{display:none}
.sheet-handle{display:none}
.nav-pin{display:none}

@media (max-width:900px){
  .hamburger{display:none!important}
  /* Het zijpaneel wordt een paneel van onderen: opent met "Meer" rechtsonder */
  .sb{position:fixed!important;top:auto!important;left:0!important;right:0!important;bottom:0!important;height:auto!important;max-height:82vh!important;width:100%!important;z-index:200!important;transform:translateY(105%);transition:transform .25s ease;box-shadow:0 -8px 32px rgba(0,0,0,.6);border-radius:16px 16px 0 0!important;border-right:none!important;border-top:1px solid #2a2c34;padding-bottom:env(safe-area-inset-bottom,0px)}
  .sb.open{transform:translateY(0)}
  .sheet-handle{display:block;width:42px;height:5px;border-radius:3px;background:#3a3c46;margin:9px auto 2px;flex-shrink:0;cursor:pointer}
  .sb .nav{position:relative;padding-right:46px!important;min-height:44px}
  .nav-pin{display:flex;position:absolute;right:4px;top:50%;transform:translateY(-50%);width:40px;height:40px;align-items:center;justify-content:center;font-size:17px;color:#5b8eff;opacity:.8;cursor:pointer}
  .tabbar{display:grid;grid-template-columns:repeat(5,1fr);position:fixed;left:0;right:0;bottom:0;z-index:190;background:#131418;border-top:1px solid #2a2c34;padding-bottom:env(safe-area-inset-bottom,0px)}
  .tab-item{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;height:62px;min-width:0;background:transparent;border:none;color:#8b8d98;font-family:inherit;cursor:pointer;-webkit-tap-highlight-color:transparent;padding:0 2px}
  .tab-item:active{transform:scale(.94)}
  .tab-item.on{color:#5b8eff}
  .tab-ico{position:relative;font-size:21px;line-height:1}
  .tab-lbl{font-size:10.5px;line-height:1.1;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .tab-badge{position:absolute;top:-6px;right:-12px;min-width:16px;height:16px;padding:0 4px;border-radius:8px;background:#e5564e;color:#fff;font-size:9.5px;font-weight:700;line-height:16px;text-align:center}
  .bug-fab{bottom:calc(76px + env(safe-area-inset-bottom,0px))!important;right:14px!important;width:46px!important;height:46px!important;font-size:20px!important}
  /* Losse zwevende meldingen en de omhoog-knop moeten boven de tabbalk blijven */
  [style*="bottom: 24px"][style*="position: fixed"],[style*="bottom: 22px"][style*="position: fixed"]{bottom:calc(78px + env(safe-area-inset-bottom,0px))!important}
  .topbar-hide{display:none!important}
  .user-badge-role{display:none!important}
  .user-badge-name{font-size:11px!important}
  .drw{width:100%!important;max-width:380px}
  .drw-backdrop{display:block}
  /* Forceer 4-kol grids naar 2-kol op tablet/mobiel */
  [style*="grid-template-columns:repeat(4,1fr)"],[style*="grid-template-columns: repeat(4,1fr)"],[style*="grid-template-columns: repeat(4, 1fr)"]{grid-template-columns:repeat(2,1fr)!important}
  [style*="grid-template-columns:repeat(3,1fr)"],[style*="grid-template-columns: repeat(3,1fr)"],[style*="grid-template-columns: repeat(3, 1fr)"]{grid-template-columns:repeat(2,1fr)!important}
  /* Topbar: fixed op mobiel zodat sticky niet breekt door overflow:hidden parent */
  .topbar{position:fixed!important;top:0;left:0;right:0;width:100%!important;z-index:150!important}
  /* overflow-x hier expliciet dichtzetten: zonder dit berekent de browser overflow-x
     automatisch als "auto" (omdat overflowY:auto staat) zodra iets binnenin te breed
     wordt (bv. een tab-rij), en schuift dan de HELE kolom zijwaarts mee i.p.v. alleen
     die ene rij */
  .main-content{padding-top:70px!important;padding-bottom:calc(80px + env(safe-area-inset-bottom,0px))!important;overflow-x:hidden!important}
  /* Meldingenpaneel van de bel: de bel staat op mobiel niet meer rechts in de balk (er staan knoppen naast), dus een
     paneel dat aan de bel hangt valt links buiten beeld. Vastzetten aan de schermranden onder de topbalk. */
  .notif-pop{position:fixed!important;left:8px!important;right:8px!important;top:56px!important;min-width:0!important;max-width:none!important}
  /* Scroll-naar-boven FAB */
  .scroll-fab{display:flex!important}
}
.scroll-fab{display:none;position:fixed;bottom:calc(132px + env(safe-area-inset-bottom,0px));right:14px;z-index:140;width:44px;height:44px;border-radius:50%;border:1px solid rgba(255,255,255,.15);background:rgba(15,17,30,.85);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);color:#fff;font-size:18px;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,.5);transition:opacity .2s,transform .2s;-webkit-tap-highlight-color:transparent}
.scroll-fab:active{transform:scale(.9)}

@media (max-width:768px){
  body{font-size:14px}
  .fu{padding:12px 14px!important}
  /* 2-kol grids → 1-kol op mobiel */
  [style*="grid-template-columns:1fr 1fr"],[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}
  [style*="grid-template-columns:repeat(4,1fr)"],[style*="grid-template-columns: repeat(4,1fr)"],[style*="grid-template-columns: repeat(4, 1fr)"]{grid-template-columns:repeat(2,1fr)!important}
  /* Dashboard-funnel (vaste kolombreedtes 180px 1fr 80px) - te breed op een telefoon,
     laat de labelkolom krimpen zodat de balk zelf leesbaar blijft */
  [style*="grid-template-columns:180px 1fr 80px"],[style*="grid-template-columns: 180px 1fr 80px"]{grid-template-columns:100px 1fr 44px!important;gap:7px!important}
  /* Formulier-grids met vaste fr-kolommen → 1 kolom op mobiel */
  .frm-grid{grid-template-columns:1fr!important;gap:8px!important}
  /* Modal vrijwel volledig scherm */
  .ovl{padding:0!important;align-items:flex-start!important}
  .mdl{max-width:100%!important;max-height:100vh!important;border-radius:0!important;min-height:100vh;padding:18px 16px!important}
  /* Touch-friendly buttons */
  .btn{min-height:38px;padding:9px 14px;font-size:13px}
  .nav{padding:12px 12px;font-size:14px;margin-bottom:3px}
  /* iOS zoom voorkomen: input font ≥16px */
  input,select,textarea{font-size:16px!important;padding:10px 12px!important}
  /* Tabs: lange rij blijft horizontaal scrollbaar */
  .tab{flex-shrink:0}
  /* Tables: forceer horizontale scroll */
  table{display:block;overflow-x:auto;-webkit-overflow-scrolling:touch;white-space:nowrap}
  /* .row-tabellen (eigen grid-rijen met vaste kolommen): laat de kaart horizontaal
     scrollen en plet de rijen niet, zodat alle kolommen bereikbaar blijven op mobiel */
  .card:has(.row){overflow-x:auto!important;-webkit-overflow-scrolling:touch}
  .row{min-width:600px}
  /* Cards: minder padding */
  .card{padding:14px 13px}
  /* Kanban-kolommen niet wrappen */
  .kl{min-width:240px}
  /* Sub-tab rijen (Administratie, Validatie, Pipeline, ...): horizontaal scrollbaar
     i.p.v. buiten beeld vallen, en blijven zichtbaar tijdens scrollen zodat je niet
     terug naar boven moet om van submodule te wisselen (geldt voor alle tab-rijen in de app) */
  div:has(>.tab){overflow-x:auto!important;-webkit-overflow-scrolling:touch;flex-wrap:nowrap!important;position:sticky;top:0;z-index:45;background:#0a0a0c;padding-top:4px;padding-bottom:4px}
}

@media (max-width:480px){
  .fu{padding:10px 12px!important}
  [style*="grid-template-columns:repeat(4,1fr)"],[style*="grid-template-columns: repeat(4,1fr)"],[style*="grid-template-columns: repeat(4, 1fr)"]{grid-template-columns:1fr 1fr!important}
  .lbl{font-size:9px}
  h1,h2{font-size:15px!important}
  /* Compactere user-pills */
  .user-badge{padding:3px 6px!important}
  .user-badge-name{display:none!important}
}
`;

// ─── ECOSYSTEEM DASHBOARD ─────────────────────────────────────────────────────
function Ecosystem({ services, pipeline, launches, bugs, tasks, setView }) {
  // ─ Sectie A: Waardeketen funnel ─
  const stages = [
    { id: "idee", l: "Idee", icon: "○", count: services.filter(s => s.state !== "rejected").length, color: C.muted, view: "validatie" },
    { id: "gevalideerd", l: "Gevalideerd", icon: "✓", count: services.filter(s => s.verdict === "GO").length, color: C.blue, view: "validatie" },
    { id: "in_bouw", l: "In Bouw", icon: "◰", count: pipeline.filter(p => p.col === "in_progress").length, color: C.yellow, view: "pipeline" },
    { id: "mvp", l: "MVP Klaar", icon: "◳", count: pipeline.filter(p => p.col === "mvp_complete").length, color: C.cyan, view: "pipeline" },
    { id: "test", l: "In Test", icon: "◔", count: pipeline.filter(p => p.col === "test_fase").length, color: C.yellow, view: "launch" },
    { id: "acceptatie", l: "Acceptatie", icon: "◑", count: pipeline.filter(p => p.col === "acceptatie").length, color: C.green, view: "launch" },
    { id: "klaar", l: "Klaar v. Launch", icon: "△", count: pipeline.filter(p => p.col === "ready_for_launch").length, color: C.cyan, view: "launch" },
    { id: "gelanceerd", l: "Gelanceerd", icon: "◆", count: (launches || []).filter(l => l.status === "Gelanceerd").length, color: C.green, view: "live" },
  ];
  // Conversie % en bottleneck flag
  let prevCount = stages[0].count;
  stages.forEach((st, i) => {
    if (i === 0) { st.conv = null; st.bottleneck = false; return; }
    st.conv = prevCount > 0 ? Math.round((st.count / prevCount) * 100) : null;
    st.bottleneck = prevCount >= 3 && st.count === 0;
    prevCount = Math.max(prevCount, st.count);
  });
  const maxCount = Math.max(1, ...stages.map(s => s.count));
  const bottleneckStages = stages.filter(s => s.bottleneck);
  const onHoldCount = pipeline.filter(p => p.col === "on_hold").length;

  // ─ Sectie B: Service Health Matrix ─
  const liveServices = (launches || []).filter(l => l.status === "Gelanceerd" || l.status === "Klaar voor Launch");
  const healthRows = liveServices.map(l => {
    const titleLow = (l.title || "").toLowerCase();
    const openBugs = (bugs || []).filter(b => b.status === "open" && ((b.title || "").toLowerCase().includes(titleLow) || (b.summary || "").toLowerCase().includes(titleLow))).length;
    const inProgressBugs = (bugs || []).filter(b => b.status === "in_progress" && ((b.title || "").toLowerCase().includes(titleLow))).length;
    const openTasks = (tasks || []).filter(t => t.status !== "done" && ((t.dienst || "").toLowerCase() === titleLow || ((t.title || "").toLowerCase().includes(titleLow)))).length;
    const launchDate = l.launch_datum || l.updated_at || l.created_at;
    const ageDays = launchDate ? Math.max(0, Math.floor((Date.now() - new Date(launchDate)) / 86400000)) : null;
    // Health score: start 100, trek af voor problemen
    let score = 100;
    score -= openBugs * 12;
    score -= inProgressBugs * 5;
    score -= openTasks * 3;
    if (ageDays !== null && ageDays < 7) score -= 10; // recent gelanceerd = nog onbewezen
    if (l.status === "Klaar voor Launch") score -= 15; // wacht op publicatie
    score = Math.max(0, Math.min(100, score));
    const health = score >= 75 ? "healthy" : score >= 50 ? "warning" : "critical";
    return { launch: l, openBugs, inProgressBugs, openTasks, ageDays, score, health, launchDate };
  }).sort((a, b) => a.score - b.score);

  const healthBuckets = {
    healthy: healthRows.filter(r => r.health === "healthy").length,
    warning: healthRows.filter(r => r.health === "warning").length,
    critical: healthRows.filter(r => r.health === "critical").length,
  };

  function HealthDot({ health }) {
    const col = health === "healthy" ? C.green : health === "warning" ? C.yellow : C.red;
    return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: col, boxShadow: "0 0 8px " + col + "66" }} />;
  }

  return (
    <div className="fu">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <Dot />
        <h1 style={{ fontFamily: "monospace", fontSize: 17, fontWeight: 700, letterSpacing: ".05em" }}>DIF3R3NT / ECOSYSTEEM</h1>
        <span style={{ color: C.muted, fontSize: 12 }}>- De volledige waardeketen in één blik</span>
      </div>

      {/* ── A. Waardeketen funnel ── */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 7 }}>
          <p className="lbl">Waardeketen - idee tot live</p>
          <div style={{ display: "flex", gap: 7, fontSize: 10, color: C.muted, fontFamily: "monospace" }}>
            <span>● Counts per fase</span>
            <span>● Conversie %</span>
            <span style={{ color: C.red }}>● Bottleneck</span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {stages.map((s, i) => {
            const w = s.count > 0 ? Math.max(8, (s.count / maxCount) * 100) : 4;
            const dropping = s.conv !== null && s.conv < 30 && prevCount >= 2;
            return (
              <div key={s.id} style={{ display: "grid", gridTemplateColumns: "180px 1fr 80px", gap: 11, alignItems: "center", cursor: s.view ? "pointer" : "default" }} onClick={() => s.view && setView(s.view)}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  <span style={{ fontSize: 15 }}>{s.icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.l}</span>
                  {s.bottleneck && <Bdg label="VAST" bg={C.redDim} fg={C.red} />}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <div style={{ flex: 1, height: 18, background: C.bg, borderRadius: 4, overflow: "hidden", border: "1px solid " + C.border, position: "relative" }}>
                    <div style={{ width: w + "%", height: "100%", background: s.color, opacity: .8, transition: "width .3s", display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 7 }}>
                      <span style={{ fontSize: 10, fontFamily: "monospace", fontWeight: 700, color: "#000" }}>{s.count}</span>
                    </div>
                  </div>
                  {i > 0 && s.conv !== null && (
                    <span style={{ fontSize: 10, fontFamily: "monospace", color: dropping ? C.red : C.muted, minWidth: 34, textAlign: "right" }}>{s.conv}%</span>
                  )}
                </div>
                <span style={{ fontSize: 11, color: s.view ? C.green : C.dim, textAlign: "right" }}>{s.view ? "open →" : ""}</span>
              </div>
            );
          })}
        </div>
        {(bottleneckStages.length > 0 || onHoldCount > 0) && (
          <div style={{ marginTop: 14, padding: "10px 12px", background: C.redDim, border: "1px solid " + C.red + "55", borderRadius: 6 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: C.red, marginBottom: 5 }}>⚠ Aandachtspunten</p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.text, lineHeight: 1.7 }}>
              {bottleneckStages.map(b => <li key={b.id}>Niets in <strong>{b.l}</strong> terwijl voorgaande fase wel pipeline heeft - flow staat hier vast.</li>)}
              {onHoldCount > 0 && <li><strong>{onHoldCount}</strong> dienst{onHoldCount === 1 ? "" : "en"} op On Hold - controleer of er nog blockers zijn die opgelost moeten worden.</li>}
            </ul>
          </div>
        )}
      </div>

      {/* ── B. Service Health Matrix ── */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14, flexWrap: "wrap", gap: 7 }}>
          <p className="lbl">Service health - per live dienst</p>
          <div style={{ display: "flex", gap: 11, fontSize: 11, fontFamily: "monospace" }}>
            <span style={{ color: C.green }}>● {healthBuckets.healthy} gezond</span>
            <span style={{ color: C.yellow }}>● {healthBuckets.warning} aandacht</span>
            <span style={{ color: C.red }}>● {healthBuckets.critical} kritiek</span>
          </div>
        </div>
        {healthRows.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: C.muted }}>
            <p style={{ fontSize: 28, marginBottom: 9, color: C.dim }}>◇</p>
            <p style={{ fontSize: 13 }}>Nog geen gelanceerde diensten. De health matrix vult zich zodra je iets via de Launch Module live zet.</p>
            <button className="btn bp" style={{ marginTop: 11 }} onClick={() => setView("launch")}>Naar Launch Module →</button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 11 }}>
            {healthRows.map(r => {
              const col = r.health === "healthy" ? C.green : r.health === "warning" ? C.yellow : C.red;
              return (
                <div key={r.launch.id} style={{ padding: "13px 14px", border: "1px solid " + col + "55", borderRadius: 8, background: col + "08", cursor: "pointer", transition: "all .15s" }} onClick={() => setView("launch")} onMouseOver={e => e.currentTarget.style.borderColor = col} onMouseOut={e => e.currentTarget.style.borderColor = col + "55"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 9, gap: 9 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        <HealthDot health={r.health} />
                        <span style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{r.launch.title}</span>
                      </div>
                      <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{r.launch.status}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 700, color: col, lineHeight: 1 }}>{r.score}</p>
                      <p style={{ fontSize: 9, color: C.muted, fontFamily: "monospace" }}>health</p>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11 }}>
                    <div><span style={{ color: C.dim }}>🐛 Bugs:</span> <span style={{ color: r.openBugs > 0 ? C.red : C.muted, fontWeight: 600 }}>{r.openBugs}{r.inProgressBugs > 0 ? " (+" + r.inProgressBugs + " bezig)" : ""}</span></div>
                    <div><span style={{ color: C.dim }}>Taken:</span> <span style={{ color: r.openTasks > 0 ? C.yellow : C.muted, fontWeight: 600 }}>{r.openTasks} open</span></div>
                    <div><span style={{ color: C.dim }}>Leeftijd:</span> <span style={{ color: C.muted }}>{r.ageDays !== null ? r.ageDays + "d" : "-"}</span></div>
                    <div><span style={{ color: C.dim }}>Status:</span> <span style={{ color: r.launch.status === "Gelanceerd" ? C.green : C.cyan, fontSize: 10 }}>{r.launch.status === "Gelanceerd" ? "Live" : "Pre-launch"}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 12, padding: "8px 11px", background: C.bg, borderRadius: 5, fontSize: 10, color: C.dim, fontFamily: "monospace", lineHeight: 1.6 }}>
          Score-formule: 100 startpunt · -12 per open bug · -5 per bug in progress · -3 per open taak · -10 voor &lt;7d oude launch · -15 voor 'Klaar v. Launch' (wacht op publicatie)
        </div>
      </div>

      {/* ── C. Open bugs per dienst ── */}
      {/* Gegroepeerd op source_app (de dienst die de bug via bug-ingest doorstuurde),
          niet op titel-matching zoals de Service Health Matrix hierboven - dat is
          onbetrouwbaar zodra een dienst zijn eigen bugtracker krijgt. */}
      <BugsPerDienst bugs={bugs} setView={setView} />
    </div>
  );
}

function BugsPerDienst({ bugs, setView }) {
  const rows = useMemo(() => {
    const bij = new Map();
    (bugs || []).forEach(b => {
      if (b.status !== "open" && b.status !== "in_progress") return;
      const key = b.source_app || "os";
      const r = bij.get(key) || { key, label: sourceAppLabel(b.source_app), open: 0, inProgress: 0 };
      if (b.status === "open") r.open++; else r.inProgress++;
      bij.set(key, r);
    });
    return Array.from(bij.values()).sort((a, b) => (b.open + b.inProgress) - (a.open + a.inProgress));
  }, [bugs]);

  return (
    <div className="card">
      <p className="lbl" style={{ marginBottom: 14 }}>Open bugs per dienst</p>
      {rows.length === 0 ? (
        <div style={{ padding: 20, textAlign: "center", color: C.muted }}>
          <p style={{ fontSize: 13 }}>Geen enkele dienst heeft nu open bugs.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 11 }}>
          {rows.map(r => {
            const col = r.open > 0 ? C.red : C.yellow;
            return (
              <div key={r.key} style={{ padding: "11px 13px", border: "1px solid " + col + "40", borderRadius: 8, background: col + "08", cursor: "pointer" }} onClick={() => setView("bugs")}>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{r.label}</p>
                <div style={{ display: "flex", gap: 11, fontSize: 11 }}>
                  <span style={{ color: r.open > 0 ? C.red : C.muted, fontWeight: 600 }}>{r.open} open</span>
                  {r.inProgress > 0 && <span style={{ color: C.yellow, fontWeight: 600 }}>{r.inProgress} bezig</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Validatie({ services, setServices, pipeline, setPipeline }) {
  const [sel, setSel] = useState(null);
  const [newMod, setNewMod] = useState(false);
  const [idea, setIdea] = useState({ title: "", problem: "", target: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importMelding, setImportMelding] = useState("");
  const [rejMod, setRejMod] = useState(false);
  const [rejReason, setRejReason] = useState("");
  const [rejTarget, setRejTarget] = useState(null);
  const [valFilter, setValFilter] = useState("actief"); // actief = afgewezen diensten verborgen uit de pipeline
  const [aiState, setAiState] = useState(null);
  const [mvpTab, setMvpTab] = useState("MVP Document");
  const [isAnalyzing, setIsAnalyzing] = useState(null);
  const [mvpContent, setMvpContent] = useState({});
  const [mvpLoading, setMvpLoading] = useState(false);

  async function runAnalysis(id, svcOverride = null) {
    setIsAnalyzing(id);
    const svc = svcOverride || services.find(x => x.id === id);
    if (!svc) { setIsAnalyzing(null); return; }

    try {
      // Stap 1: Actuele marktonderzoek via compound-beta (web search)
      let researchCtx = "";
      try {
        const { content } = await research(
          `"${svc.title}" ${svc.problem || ""} pain points complaints problems competitors pricing Netherlands Europe market size 2024 2025 Reddit forum`
        );
        researchCtx = `\n\n=== ACTUELE MARKTDATA (web research) ===\n${content}\n=== EINDE MARKTDATA ===`;
      } catch (e) { console.warn("Research stap overgeslagen:", e.message); }

      // Stap 2: Gestructureerde analyse op basis van research
      const sys = `Je bent een brutaal eerlijke Business Analyst voor Dif3r3nt VOF. Je taak is een SPECIFIEK, ONDERBOUWD en CONSISTENT validatie-oordeel.

CONSISTENTIE-REGELS (kritisch):
- Pas ALTIJD dezelfde scoring-rubriek toe (zie hieronder).
- Pain en market scores MOETEN gewogen rekenkundig gemiddelde zijn van hun sub-scores. Geen onderbuik-scores.
- Geen algemeenheden — alles SPECIFIEK voor dit idee.
- Noem echte concurrenten, echte prijzen, echte cijfers uit de research data. Verzin geen nummers; bij gebrek aan data zeg je "onvoldoende data".
- Tone: zakelijk-direct. Geen marketingtaal.

PAIN SCORE RUBRIEK (gewogen gemiddelde 3 sub-scores):
- pain_frequency (0-100): hoe vaak ervaren mensen dit probleem? 0=nooit, 30=incidenteel, 60=wekelijks, 100=meerdere keren per dag
- pain_intensity (0-100): hoe storend is het? 0=ergernisje, 30=hinderlijk, 60=blokkeert werk, 100=existentieel/business-killer
- pain_willingness_to_pay (0-100): zou de doelgroep er voor betalen? 0=nooit, 30=alleen onder druk, 60=ja maar zoekt gratis alternatief, 100=betaalt nu al voor matige oplossingen
- pain_score = round((frequency + intensity + willingness) / 3)

MARKET SCORE RUBRIEK (gewogen gemiddelde 3 sub-scores):
- market_size (0-100): grootte van de bereikbare markt in NL/EU. 0=<100 klanten, 30=<1000, 60=<10k, 100=>50k
- market_growth (0-100): groeit de markt? 0=krimpt, 30=stagneert, 60=licht groeiend, 100=hard groeiend (>20%/jr)
- market_competition (0-100): is er ruimte? 0=monopoly door 1 dominante speler, 30=verzadigd, 60=enkele aanbieders met gaten, 100=open markt
- market_score = round((size + growth + competition) / 3)

ANALYSEER:
1. NICHE: Specifieke sub-doelgroep (niet "MKB" maar bv. "boekhouders bij accountantskantoren 5-20 man in NL"). Baseer die op de ingevulde doelgroep en het probleem van de eigenaar en maak hem daarbinnen specifieker. Verzin geen kenmerken die niet gegeven zijn (leeftijd, inkomen, aantallen, sectoren) en wijk niet af van wie de eigenaar noemt; staat iets niet in de invoer, noem het dan niet.
2. PAIN ANALYSE — diepgaand:
   - reddit_pain: 4-7 zinnen verhalend. Beschrijf het GEVOEL/de frustratie van de doelgroep in hun eigen woorden. Wat doet hen 's nachts wakker liggen?
   - pain_evidence: array van 3-5 concrete bewijspunten uit de research (Reddit citaten, klachten op forums, review-uitspraken, statistieken). Elk punt 1-2 zinnen, met bron-hint waar mogelijk.
   - pain_sentiment: één van: frustratie / woede / wanhoop / ergernis / berusting / urgentie
   - pain_explanation: 3-4 zinnen WAAROM de pain_score deze waarde heeft. Verwijs naar de 3 sub-scores. Voorbeeld: "Frequentie: hoog (dagelijks). Intensiteit: matig (hinderlijk maar werkbaar met workarounds). Betalingsbereidheid: laag (gewend aan gratis Excel-oplossing). Dat geeft 60+50+30=140/3=47."
3. MARKET INTENT: Marktgrootte in EUR (TAM/SAM), aantal potentiële klanten NL+EU, 2-3 echte concurrenten met hun prijzen, market_explanation (3-4 zinnen WAAROM market_score deze waarde heeft, refereer aan sub-scores).
4. AI VERDICT: Concreet eerlijk oordeel (5-8 zinnen). Wat werkt ECHT niet? Wie wint dit al? Wat moet anders? Welke 2-3 condities maken dit GO?
5. VERDIENMODEL: Meest logische model (SaaS / eenmalig / commissie / freemium) MET concreet prijspunt en onderbouwing.
6. MRR POTENTIEEL: Realistisch MRR-doel na 12 maanden, met aanname-rij ("X% conversie van Y leads × €Z prijs = €N MRR").

VERDICT-LOGICA (verplicht):
- pain_score >= 60 EN market_score >= 60 EN minimaal 1 GO-conditie haalbaar = GO
- pain_score < 40 OF market_score < 40 = NO_GO
- pain_score 40-59 OF market_score 40-59 = PIVOT (met concreet pivot-voorstel in ai_verdict_text)

Reageer UITSLUITEND als JSON:
{"nieuwe_doelgroep":"string","reddit_pain":"string","pain_evidence":["bewijs 1","bewijs 2","bewijs 3"],"pain_sentiment":"string","pain_explanation":"string","pain_frequency":number,"pain_intensity":number,"pain_willingness_to_pay":number,"pain_score":number,"market_intent":"string","market_explanation":"string","market_size":number,"market_growth":number,"market_competition":number,"market_score":number,"ai_verdict_text":"string","verdict_go_nogo":"GO of NO_GO of PIVOT","verdict_conditions":["voorwaarde 1","voorwaarde 2"],"verdienmodel":"string","mrr_potentieel":"string"}`;

      const usr = `Dienst: ${svc.title}
Probleem: ${svc.problem || "Niet opgegeven"}
Doelgroep: ${svc.target || "Niet opgegeven"}${researchCtx}`;

      const { parsed, raw } = await callGroq(sys, usr, { temperature: 0.25, maxTokens: 3500 });
      if (!parsed) throw new Error("Geen geldige JSON ontvangen. Raw: " + raw.slice(0, 200));

      const cl = (v) => typeof v === "number" ? Math.min(100, Math.max(0, Math.round(v))) : null;
      const updateData = {
        pain: typeof parsed.pain_score === "number" ? Math.min(100, Math.max(0, Math.round(parsed.pain_score))) : 0,
        market: typeof parsed.market_score === "number" ? Math.min(100, Math.max(0, Math.round(parsed.market_score))) : 0,
        verdict: ["GO", "NO_GO", "PIVOT"].includes(parsed.verdict_go_nogo) ? parsed.verdict_go_nogo : "—",
        improvement: {
          ...(svc.improvement || {}),
          nieuwe_doelgroep: parsed.nieuwe_doelgroep || "Niet bepaald",
          reddit_pain: parsed.reddit_pain || "Geen pijn geïdentificeerd",
          pain_evidence: Array.isArray(parsed.pain_evidence) ? parsed.pain_evidence : [],
          pain_sentiment: parsed.pain_sentiment || "—",
          pain_explanation: parsed.pain_explanation || "",
          pain_frequency: cl(parsed.pain_frequency),
          pain_intensity: cl(parsed.pain_intensity),
          pain_willingness_to_pay: cl(parsed.pain_willingness_to_pay),
          market_intent: parsed.market_intent || "Niet bepaald",
          market_explanation: parsed.market_explanation || "",
          market_size: cl(parsed.market_size),
          market_growth: cl(parsed.market_growth),
          market_competition: cl(parsed.market_competition),
          ai_verdict_text: parsed.ai_verdict_text || "Geen conclusie",
          verdict_conditions: Array.isArray(parsed.verdict_conditions) ? parsed.verdict_conditions : [],
          verdienmodel: parsed.verdienmodel || "Niet bepaald",
          mrr_potentieel: parsed.mrr_potentieel || "Niet bepaald",
        }
      };

      const { data, error } = await supabase.from("services").update(updateData).eq("id", id).select();
      if (error) throw new Error("Supabase update mislukt: " + error.message);
      if (data) setServices(p => p.map(s => s.id === id ? { ...data[0], improvement: updateData.improvement } : s));

    } catch (e) {
      console.error("Analyse fout:", e);
      alert("Fout bij analyse: " + e.message);
    }
    setIsAnalyzing(null);
  }

  async function generateMvp(svc) {
    if (mvpContent[svc.id] || mvpLoading) return;
    setMvpLoading(true);
    const sys = `Je bent een technisch product architect voor Dif3r3nt VOF. Genereer een volledig, SPECIFIEK MVP plan voor de opgegeven dienst. Geen generieke templates — alles moet op maat zijn voor dit product.
Reageer UITSLUITEND als JSON:
{"mvp_doc":"markdown tekst met ## Probleem, ## Oplossing, ## MVP Scope (max 5 features), ## Tech Stack, ## Go-to-Market strategie","user_stories":"6-8 specifieke user stories in format: Als [rol] wil ik [actie] zodat [doel]. Elke story op een nieuwe regel.","db_schema":"PostgreSQL tabelnamen en kolommen specifiek voor dit product (geen generieke users/tasks tabel)","api_flows":"8-10 key API endpoints specifiek voor dit product met HTTP methode en korte omschrijving","roadmap":"5-week sprint plan naar launch, specifiek voor dit product. Elke week: Week N: [focus] — [deliverables]"}`;
    const usr = `Dienst: ${svc.title}
Probleem: ${svc.problem || "—"}
Doelgroep: ${svc.improvement?.nieuwe_doelgroep || svc.target || "—"}
Pain score: ${svc.pain}/100 | Market score: ${svc.market}/100 | Verdict: ${svc.verdict}
Verdienmodel: ${svc.improvement?.verdienmodel || "—"}
MRR potentieel: ${svc.improvement?.mrr_potentieel || "—"}
Reddit pain: ${svc.improvement?.reddit_pain || "—"}`;
    try {
      const { parsed } = await callGroq(sys, usr, { temperature: 0.7, maxTokens: 2500 });
      setMvpContent(p => ({ ...p, [svc.id]: parsed || {} }));
    } catch (e) { alert("MVP genereren mislukt: " + e.message); }
    setMvpLoading(false);
  }

  async function submitIdea() {
    if (!idea.title.trim()) return;
    const newSvc = { title: idea.title, state: "submitted", pain: 0, market: 0, verdict: "—", review: "pending", problem: idea.problem, target: idea.target, reject_reason: null, resubmit: false };
    const { data, error } = await supabase.from("services").insert([newSvc]).select();
    if (!error && data) {
      setServices(p => [data[0], ...p]);
      logActivity("dienst-idee", idea.title, "validatie");
      runAnalysis(data[0].id, data[0]);
    }
    setNewMod(false); setIdea({ title: "", problem: "", target: "" });
  }

  // Notities uit bijvoorbeeld de iPhone: gecontroleerde cases worden ideeen, de eerste k starten meteen de validatie
  async function importNotities(items, k) {
    const rijen = items.map(naarServiceRij);
    const { data, error } = await supabase.from("services").insert(rijen).select();
    if (error || !data) throw new Error("Opslaan mislukt: " + (error && error.message));
    setServices(p => [...data, ...p]);
    logActivity("dienst-ideeen", data.length + " uit notities", "validatie");
    setImportMelding(data.length + " ideeen toegevoegd" + (k ? ". De eerste " + k + " worden nu gevalideerd, dat duurt even." : "."));
    // Een voor een, zodat de analyses elkaar niet verdringen en het AI-budget onder controle blijft
    (async () => {
      for (const rij of data.slice(0, k)) { try { await runAnalysis(rij.id, rij); } catch (e) { /* de fout staat bij het idee zelf */ } }
      setImportMelding(data.length + " ideeen toegevoegd" + (k ? ", " + k + " gevalideerd." : "."));
    })();
  }

  async function approve(id, extra = {}) {
    const { data, error } = await supabase.from("services").update({ state: "mvp_ready", review: "approved", ...extra }).eq("id", id).select();
    if (error) { alert("Goedkeuren mislukt: " + error.message); return; }
    if (data) {
      setServices(p => p.map(s => s.id === id ? { ...data[0], improvement: extra.improvement || data[0].improvement } : s));
      const svc = data[0];
      const pipeItem = { title: svc.title, col: "ready_to_build", priority: "MED", days: 0, updated: "Zojuist" };
      const { data: pData, error: pErr } = await supabase.from("pipeline").insert([pipeItem]).select();
      if (!pErr && pData) { setPipeline(p => [...p, pData[0]]); logActivity("bouwpipeline-kaart", svc.title, "pipeline"); }
    }
    setSel(null);
  }

  function openRej(id) { setRejTarget(id); setRejReason(""); setRejMod(true); }

  async function confirmRej(targetOverride, reasonOverride) {
    // Let op: roep dit aan als confirmRej() of confirmRej(id, reason) — NOOIT direct
    // als onClick-handler, want dan komt het click-event als targetOverride binnen.
    const target = (typeof targetOverride === "string" || typeof targetOverride === "number") ? targetOverride : rejTarget;
    const reason = typeof reasonOverride === "string" ? reasonOverride : rejReason;
    if (!target) { alert("Geen dienst geselecteerd om af te wijzen."); return; }
    if (!reason.trim()) return;
    const svc = services.find(x => x.id === target);
    const { data, error } = await supabase.from("services").update({ state: "rejected", review: "rejected", reject_reason: reason }).eq("id", target).select();
    if (error) { alert("Afwijzen mislukt: " + error.message); return; }
    if (data) setServices(p => p.map(s => s.id === target ? data[0] : s));

    setRejMod(false); setSel(target);
    setAiState({ id: target, loading: true, result: null });
    try {
      const sys = "Je bent AI-agent van Dif3R3Nt. Analyseer een afgewezen dienst-idee en geef een concrete winstgevende verbetering. Reageer ALLEEN als JSON zonder markdown or uitleg: verbeterd_concept, kernprobleem, verbetering, nieuwe_doelgroep, verdienmodel, mrr_potentieel, tristan_pitch";
      const usr = "Dienst: " + (svc?.title || "") + ". Afwijzingsreden: " + reason + ". Pain: " + (svc?.pain || 0) + "/100. Market: " + (svc?.market || 0) + "/100.";
      const res = await claude(sys, usr);
      setAiState({ id: target, loading: false, result: res });
    } catch (err) {
      const msg = err?.message || "Onbekende fout";
      setAiState({ id: target, loading: false, result: null, error: true, errMsg: msg });
    }
  }

  // Velden die een AI-verbetering aan een afgewezen dienst toevoegt (kolomnamen uit de services-tabel)
  function improvedFields(id) {
    const imp = aiState?.id === id ? aiState.result : null;
    if (!imp) return null;
    const s = services.find(x => x.id === id);
    return { title: imp.verbeterd_concept || s.title, reject_reason: null, resubmit: true, improvement: { ...(s.improvement || {}), ...imp } };
  }

  async function resubmit(id) {
    const f = improvedFields(id);
    if (!f) return;
    const { data, error } = await supabase.from("services").update({ state: "review_pending", review: "pending", ...f }).eq("id", id).select();
    if (error) { alert("Opnieuw insturen mislukt: " + error.message); return; }
    if (data) setServices(p => p.map(s => s.id === id ? { ...data[0], improvement: f.improvement } : s));
    setAiState(null); setSel(null);
  }

  async function approveImproved(id) {
    const f = improvedFields(id);
    if (!f) return;
    await approve(id, f);
    setAiState(null);
  }

  async function deleteIdea(id) {
    if (!window.confirm("Weet je zeker dat je dit idee definitief wilt verwijderen?")) return;
    const { error } = await supabase.from("services").delete().eq("id", id);
    if (!error) {
      setServices(p => p.filter(s => s.id !== id));
      setSel(null);
    } else {
      alert("Fout bij verwijderen: " + error.message);
    }
  }

  if (sel) {
    const s = services.find(x => x.id === sel);
    if (!s) { setSel(null); return null; }
    const ai = aiState?.id === s.id ? aiState : null;
    return (
      <div className="fu">
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
          <button className="btn bg" onClick={() => startTransition(() => { setSel(null); setAiState(null); })}>← Terug</button>
          <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>{s.title}</h2>
          <Bdg state={s.state} />
          {s.resubmit && <Bdg label="♻ Hersubmissie" bg={C.purpleDim} fg={C.purple} />}
        </div>

        {s.state === "rejected" && (s.reject_reason || s.rejectReason) && (
          <div className="card" style={{ marginBottom: 13, borderColor: "rgba(229,86,78,.4)", background: C.redDim }}>
            <p className="lbl" style={{ marginBottom: 7, color: C.red }}>✗ Afgewezen</p>
            <p style={{ fontSize: 13, lineHeight: 1.6 }}><strong>Reden:</strong> {s.reject_reason || s.rejectReason}</p>
          </div>
        )}

        {s.state === "rejected" && (
          <div className="card" style={{ marginBottom: 13, borderColor: "rgba(52,189,130,.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 13 }}>
              <Dot /><p className="lbl">Agent / Winstgevende Verbetering</p>
            </div>
            {ai?.loading && (
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: 13, background: C.greenDim, borderRadius: 6 }}>
                <Dot /><p style={{ fontSize: 12, color: C.green, fontFamily: "monospace" }}>Agent analyseert afwijzingsreden...</p>
              </div>
            )}
            {ai?.error && <div style={{ background: C.redDim, border: '1px solid rgba(229,86,78,.3)', borderRadius: 6, padding: 12 }}><p style={{ fontSize: 12, color: C.red, marginBottom: 6 }}>✗ Fout bij verbinding</p>{ai.errMsg && <p style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace' }}>{ai.errMsg}</p>}<button className='btn bd' style={{ fontSize: 11, marginTop: 9 }} onClick={() => confirmRej(s.id, s.reject_reason || s.rejectReason || "Geen reden opgegeven")}>↻ Opnieuw proberen</button></div>}
            {ai?.result && (() => {
              const r = ai.result;
              return (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 11 }}>
                    {[["Verbeterd concept", r.verbeterd_concept], ["Kernprobleem", r.kernprobleem], ["Nieuwe doelgroep", r.nieuwe_doelgroep], ["Verdienmodel", r.verdienmodel], ["MRR Potentieel", r.mrr_potentieel]].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} style={{ background: C.bg, borderRadius: 6, padding: 11, border: "1px solid " + C.border }}>
                        <p className="lbl" style={{ marginBottom: 5 }}>{k}</p>
                        <p style={{ fontSize: 13 }}>{v}</p>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: C.greenDim, border: "1px solid rgba(52,189,130,.18)", borderRadius: 7, padding: 13, marginBottom: 10 }}>
                    <p className="lbl" style={{ color: C.green, marginBottom: 5 }}>Verbetering</p>
                    <p style={{ fontSize: 13, lineHeight: 1.7 }}>{r.verbetering}</p>
                  </div>
                  <div style={{ background: C.purpleDim, border: "1px solid rgba(154,160,176,.22)", borderRadius: 7, padding: 13, marginBottom: 12 }}>
                    <p className="lbl" style={{ color: C.purple, marginBottom: 5 }}>Pitch</p>
                    <p style={{ fontSize: 13, lineHeight: 1.7, fontStyle: "italic" }}>"{r.tristan_pitch}"</p>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn bp" onClick={() => approveImproved(s.id)}>✓ Goedkeuren met verbetering</button>
                    <button className="btn bg" onClick={() => resubmit(s.id)}>↺ Opnieuw insturen voor review</button>
                    <button className="btn bg" onClick={() => confirmRej(s.id, s.reject_reason || s.rejectReason || "Geen reden opgegeven")}>↻ Nieuwe verbetering</button>
                    <button className="btn bg" style={{ color: C.red, borderColor: C.redDim, marginLeft: "auto" }} onClick={() => deleteIdea(s.id)}>Verwijderen</button>
                  </div>
                </div>
              );
            })()}
            {!ai && <button className="btn bp" onClick={() => confirmRej(s.id, s.reject_reason || s.rejectReason || "Geen reden opgegeven")}>Genereer verbetering</button>}
          </div>
        )}

        <div className="card" style={{ marginBottom: 13 }}>
          <p className="lbl" style={{ marginBottom: 11 }}>01 / Dienst Invoer</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
            {[["Probleem", s.problem || "—"], ["Doelgroep", s.target || "—"], ["Verdienmodel", s.improvement?.verdienmodel || "—"], ["MRR Potentieel", s.improvement?.mrr_potentieel || "—"]].map(([k, v]) => (
              <div key={k}><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{k}</p><p style={{ fontSize: 13 }}>{v}</p></div>
            ))}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 11 }}>
            <p className="lbl">02 / Agent Analyse</p>
            {isAnalyzing === s.id && <span style={{ fontSize: 11, color: C.green, fontFamily: "monospace" }}>Agent zoekt en analyseert...</span>}
          </div>
          <div style={{ opacity: isAnalyzing === s.id ? 0.5 : 1, transition: "opacity .2s" }}>
            {/* Niche */}
            <div style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
              <span style={{ fontFamily: "monospace", fontSize: 11, color: C.green }}>Niche Expander</span>
              <p style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{s.improvement?.nieuwe_doelgroep || "Nog niet geanalyseerd"}</p>
            </div>

            {/* Reddit Pain — uitgebreid */}
            <div style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: C.green }}>Reddit Pain Engine</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {s.improvement?.pain_sentiment && <Bdg label={s.improvement.pain_sentiment.toUpperCase()} bg={C.redDim} fg={C.red} />}
                  <Bar val={s.pain || 0} />
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: s.pain >= 60 ? C.green : s.pain >= 40 ? C.yellow : C.red, fontWeight: 700, minWidth: 32, textAlign: "right" }}>{s.pain || 0}</span>
                </div>
              </div>
              <p style={{ fontSize: 13, color: C.text, marginBottom: 7, lineHeight: 1.6 }}>{s.improvement?.reddit_pain || "Nog niet geanalyseerd"}</p>
              {(s.improvement?.pain_frequency != null || s.improvement?.pain_intensity != null || s.improvement?.pain_willingness_to_pay != null) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, padding: "7px 0", borderTop: "1px dashed " + C.border, marginBottom: 6 }}>
                  {[["Frequentie", s.improvement?.pain_frequency], ["Intensiteit", s.improvement?.pain_intensity], ["Betalingsbereidheid", s.improvement?.pain_willingness_to_pay]].map(([l, v]) => (
                    <div key={l}>
                      <p style={{ fontSize: 9, color: C.dim, fontFamily: "monospace", marginBottom: 3, textTransform: "uppercase", letterSpacing: ".05em" }}>{l}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <Bar val={v || 0} />
                        <span style={{ fontSize: 10, fontFamily: "monospace", color: C.muted, minWidth: 24, textAlign: "right" }}>{v != null ? v : "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(s.improvement?.pain_evidence) && s.improvement.pain_evidence.length > 0 && (
                <div style={{ padding: "7px 9px", background: C.bg, borderRadius: 5, marginBottom: 6, borderLeft: "2px solid " + C.red + "66" }}>
                  <p style={{ fontSize: 9, color: C.red, fontFamily: "monospace", marginBottom: 5, letterSpacing: ".05em" }}>BEWIJSPUNTEN UIT RESEARCH</p>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                    {s.improvement.pain_evidence.map((ev, i) => <li key={i} style={{ marginBottom: 3 }}>{ev}</li>)}
                  </ul>
                </div>
              )}
              {s.improvement?.pain_explanation && (
                <div style={{ padding: "7px 9px", background: C.surface, borderRadius: 5, fontSize: 11, color: C.muted, lineHeight: 1.6, fontStyle: "italic" }}>
                  <span style={{ color: C.green, fontFamily: "monospace", fontStyle: "normal", marginRight: 5, fontSize: 9 }}>WAAROM DEZE SCORE:</span>
                  {s.improvement.pain_explanation}
                </div>
              )}
            </div>

            {/* Market Intent — uitgebreid */}
            <div style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: C.green }}>Market Intent Engine</span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Bar val={s.market || 0} />
                  <span style={{ fontFamily: "monospace", fontSize: 11, color: s.market >= 60 ? C.green : s.market >= 40 ? C.yellow : C.red, fontWeight: 700, minWidth: 32, textAlign: "right" }}>{s.market || 0}</span>
                </div>
              </div>
              <p style={{ fontSize: 13, color: C.text, marginBottom: 7, lineHeight: 1.6 }}>{s.improvement?.market_intent || "Nog niet geanalyseerd"}</p>
              {(s.improvement?.market_size != null || s.improvement?.market_growth != null || s.improvement?.market_competition != null) && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, padding: "7px 0", borderTop: "1px dashed " + C.border, marginBottom: 6 }}>
                  {[["Marktgrootte", s.improvement?.market_size], ["Groei", s.improvement?.market_growth], ["Concurrentie-ruimte", s.improvement?.market_competition]].map(([l, v]) => (
                    <div key={l}>
                      <p style={{ fontSize: 9, color: C.dim, fontFamily: "monospace", marginBottom: 3, textTransform: "uppercase", letterSpacing: ".05em" }}>{l}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <Bar val={v || 0} />
                        <span style={{ fontSize: 10, fontFamily: "monospace", color: C.muted, minWidth: 24, textAlign: "right" }}>{v != null ? v : "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {s.improvement?.market_explanation && (
                <div style={{ padding: "7px 9px", background: C.surface, borderRadius: 5, fontSize: 11, color: C.muted, lineHeight: 1.6, fontStyle: "italic" }}>
                  <span style={{ color: C.green, fontFamily: "monospace", fontStyle: "normal", marginRight: 5, fontSize: 9 }}>WAAROM DEZE SCORE:</span>
                  {s.improvement.market_explanation}
                </div>
              )}
            </div>

            {/* AI Verdict + condities */}
            <div style={{ padding: "10px 0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: C.green }}>AI Verdict</span>
                {s.verdict && <Bdg label={s.verdict} bg={(VCOL[s.verdict] || C.muted) + "20"} fg={VCOL[s.verdict] || C.muted} />}
              </div>
              <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{s.improvement?.ai_verdict_text || "Nog niet geanalyseerd"}</p>
              {Array.isArray(s.improvement?.verdict_conditions) && s.improvement.verdict_conditions.length > 0 && (
                <div style={{ marginTop: 8, padding: "7px 9px", background: C.greenDim, borderRadius: 5, borderLeft: "2px solid " + C.green }}>
                  <p style={{ fontSize: 9, color: C.green, fontFamily: "monospace", marginBottom: 5, letterSpacing: ".05em" }}>VOORWAARDEN VOOR GO</p>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                    {s.improvement.verdict_conditions.map((c, i) => <li key={i} style={{ marginBottom: 3 }}>{c}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 13 }}>
          <p className="lbl" style={{ marginBottom: 11 }}>03 / Review (Furgel + Claude)</p>
          {s.state === "rejected" ? (
            <p style={{ fontSize: 13, color: C.red, padding: "7px 0" }}>✗ Afgewezen — zie agent verbetering hierboven</p>
          ) : s.state === "approved" || s.state === "mvp_ready" ? (
            <p style={{ fontSize: 13, color: C.green, padding: "7px 0" }}>✓ Goedgekeurd — MVP generator actief</p>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", flexWrap: "wrap" }}>
              <span style={{ flex: 1, fontSize: 13 }}>Status: <strong style={{ color: s.review === "approved" ? C.green : s.review === "rejected" ? C.red : C.yellow }}>{s.review.toUpperCase()}</strong></span>
              <button className="btn bp" onClick={() => approve(s.id)}>✓ Goedkeuren</button>
              <button className="btn bd" onClick={() => openRej(s.id)}>✗ Afwijzen</button>
              <button className="btn bg" onClick={() => runAnalysis(s.id)}>↺ Heranalyse</button>
              <button className="btn bg" style={{ color: C.red, borderColor: C.redDim }} onClick={() => deleteIdea(s.id)}>Verwijderen</button>
            </div>
          )}
        </div>

        {(s.state === "approved" || s.state === "mvp_ready") && (
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 11 }}>
              <p className="lbl">04 / MVP Generator</p>
              {mvpLoading && <span style={{ fontSize: 11, color: C.green, fontFamily: "monospace" }}>Agent genereert MVP plan...</span>}
            </div>
            {!mvpContent[s.id] && !mvpLoading && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <p style={{ fontSize: 13, color: C.muted, marginBottom: 14 }}>AI genereert een volledig op maat MVP plan voor <strong style={{ color: C.text }}>{s.title}</strong>.</p>
                <button className="btn bp" onClick={() => generateMvp(s)}>Genereer MVP Plan</button>
              </div>
            )}
            {mvpLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 9, padding: 16, background: C.greenDim, borderRadius: 6 }}>
                <Dot /><p style={{ fontSize: 12, color: C.green, fontFamily: "monospace" }}>Agent analyseert en schrijft MVP plan...</p>
              </div>
            )}
            {mvpContent[s.id] && (() => {
              const mc = mvpContent[s.id];
              const TAB_KEY = { "MVP Document": "mvp_doc", "User Stories": "user_stories", "DB Schema": "db_schema", "API Flows": "api_flows", "Roadmap": "roadmap" };
              return (
                <>
                  <div style={{ display: "flex", gap: 7, marginBottom: 13, flexWrap: "wrap" }}>
                    {Object.keys(TAB_KEY).map(t => (
                      <span key={t} className={"tab" + (mvpTab === t ? " on" : "")} onClick={() => setMvpTab(t)}>{t}</span>
                    ))}
                  </div>
                  <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 13, fontSize: 12, color: C.muted, fontFamily: "monospace", lineHeight: 1.9, whiteSpace: "pre-wrap", maxHeight: 320, overflowY: "auto" }}>
                    <p style={{ color: C.green, marginBottom: 8, fontSize: 11, letterSpacing: ".07em" }}># {s.title} — {mvpTab.toUpperCase()}</p>
                    <p>{mc[TAB_KEY[mvpTab]] || "Geen inhoud gegenereerd voor dit tabblad."}</p>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 11 }}>
                    <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { setMvpContent(p => ({ ...p, [s.id]: undefined })); setTimeout(() => generateMvp(s), 100); }}>↺ Regenereer</button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {rejMod && (
          <Portal><div className="ovl" onClick={() => setRejMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 8, fontSize: 14, color: C.red }}>✗ AFWIJZEN — REDEN VERPLICHT</h3>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 17, lineHeight: 1.6 }}>Geef een concrete reden. Claude gebruikt dit om een winstgevende verbetering te genereren die je samen kunt beoordelen.</p>
              <textarea rows={4} value={rejReason} onChange={e => setRejReason(e.target.value)} placeholder="bijv. Doelgroep te breed, geen duidelijk verdienmodel..." style={{ marginBottom: 11, lineHeight: 1.6, fontSize: 13 }} autoFocus />
              {!rejReason.trim() && <p style={{ fontSize: 11, color: C.red, marginBottom: 10 }}>⚠ Reden is verplicht</p>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => setRejMod(false)}>Annuleer</button>
                <button className="btn bd" onClick={() => confirmRej()} style={{ opacity: rejReason.trim() ? 1 : .4 }} disabled={!rejReason.trim()}>✗ Afwijzen & Agent Activeren</button>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  return (
    <div className="fu">
      <Kop titel="Validatie" rechts={<><button className="btn bg">Export</button><button className="btn bg" onClick={() => setImportOpen(true)}>Notities importeren</button><button className="btn bp" onClick={() => setNewMod(true)}>+ Nieuw idee</button></>} />
      {importMelding && <div style={{ background: C.greenDim, border: "1px solid " + C.green + "55", borderRadius: 8, padding: "10px 14px", fontSize: 12, marginBottom: 12, display: "flex", justifyContent: "space-between", gap: 10 }}><span>{importMelding}</span><span style={{ cursor: "pointer", color: C.muted }} onClick={() => setImportMelding("")}>sluiten</span></div>}
      {importOpen && <NotitiesImport bestaandeTitels={services.map(x => x.title)} onImporteer={importNotities} onSluit={() => setImportOpen(false)} />}
      <div className="card" style={{ padding: 0, overflow: "hidden", minHeight: services.length === 0 ? 400 : 0, display: "flex", flexDirection: "column" }}>
        {services.length === 0 ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40, position: "relative" }}>
            <h2 style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", fontSize: 80, fontWeight: 900, color: C.border, opacity: .3, pointerEvents: "none", letterSpacing: ".1em", whiteSpace: "nowrap", fontFamily: "monospace" }}>VALIDATIE</h2>
            <div className="fu" onClick={() => setNewMod(true)} style={{ background: C.surfaceHigh, border: "1px solid " + C.border, borderRadius: 12, padding: "30px 50px", cursor: "pointer", transition: "all .2s", textAlign: "center", zIndex: 1, position: "relative", boxShadow: "0 20px 40px rgba(0,0,0,.4)" }} onMouseOver={e => e.currentTarget.style.borderColor = C.green} onMouseOut={e => e.currentTarget.style.borderColor = C.border}>
              <p style={{ fontSize: 18, fontWeight: 700, color: C.text, fontFamily: "monospace", letterSpacing: ".05em" }}>+ NIEUW DIENST IDEE</p>
              <p style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Klik om je eerste commerciële validatie te starten</p>
            </div>
          </div>
        ) : (
          <>
            {(() => {
              const counts = { actief: services.filter(s => s.state !== "rejected").length, afgewezen: services.filter(s => s.state === "rejected").length, alle: services.length };
              return (
                <div style={{ display: "flex", gap: 6, padding: "10px 12px", borderBottom: "1px solid " + C.border }}>
                  {[["actief", "Actief"], ["afgewezen", "Afgewezen"], ["alle", "Alle"]].map(([k, l]) => (
                    <button key={k} className={"tab" + (valFilter === k ? " on" : "")} onClick={() => setValFilter(k)}>{l} ({counts[k]})</button>
                  ))}
                </div>
              );
            })()}
            {(() => {
              const visible = services.filter(s => valFilter === "actief" ? s.state !== "rejected" : valFilter === "afgewezen" ? s.state === "rejected" : true);
              if (visible.length === 0) return <Leeg tekst={valFilter === "actief" ? "Geen actieve diensten. Afgewezen diensten staan onder 'Afgewezen'." : "Geen diensten in deze weergave."} />;
              return visible.map(s => (
                <Rij key={s.id} titel={<>{s.title}{s.resubmit && <span style={{ fontSize: 10, color: C.purple, marginLeft: 6 }}>♻</span>}</>} onKlik={() => startTransition(() => setSel(s.id))}
                  meta={<>
                    <Bdg state={s.state} />
                    <Bdg label={s.verdict} bg={(VCOL[s.verdict] || C.muted) + "20"} fg={VCOL[s.verdict] || C.muted} />
                    <span>Pain {s.pain} · Market {s.market}</span>
                    <span style={{ color: s.review === "approved" ? C.green : s.review === "rejected" ? C.red : C.yellow }}>{s.review}</span>
                  </>} />
              ));
            })()}
          </>
        )}
      </div>
      {newMod && (
        <Portal><div className="ovl" onClick={() => setNewMod(false)}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", marginBottom: 17, fontSize: 14 }}>+ NIEUW DIENST IDEE</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Naam *</p><input value={idea.title} onChange={e => setIdea(p => ({ ...p, title: e.target.value }))} placeholder="bijv. ComplianceBot NL" /></div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Probleem</p><textarea rows={3} value={idea.problem} onChange={e => setIdea(p => ({ ...p, problem: e.target.value }))} placeholder="Welk probleem lost dit op?" /></div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Doelgroep</p><input value={idea.target} onChange={e => setIdea(p => ({ ...p, target: e.target.value }))} placeholder="bijv. Nederlandse ZZP-ers" /></div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 5 }}>
                <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={submitIdea}>→ Start Validatie</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
    </div>
  );
}

function Pipeline({ pipeline, setPipeline, launches, setLaunches, setView, setLaunchSel, services = [] }) {
  const cols = [
    { id: "ready_to_build", l: "Ready to Build" },
    { id: "in_progress", l: "In Progress" },
    { id: "on_hold", l: "On Hold" },
    { id: "mvp_complete", l: "MVP Complete" },
    { id: "test_fase", l: "Test Fase" },
    { id: "acceptatie", l: "Acceptatie" },
    { id: "ready_for_launch", l: "Klaar voor Launch" },
    { id: "live", l: "Live" },
  ];

  async function move(id, to) {
    const { data, error } = await supabase.from("pipeline").update({ col: to, updated: "Zojuist" }).eq("id", id).select();
    if (!error && data) setPipeline(p => p.map(x => x.id === id ? data[0] : x));
  }

  async function setPrio(id, priority) {
    const { data, error } = await supabase.from("pipeline").update({ priority }).eq("id", id).select();
    if (!error && data) setPipeline(p => p.map(x => x.id === id ? data[0] : x));
  }

  async function startLaunchModule(card) {
    const existing = (launches || []).find(l => l.pipeline_item_id === String(card.id));
    if (existing) {
      setLaunchSel(existing.id);
      setView("launch");
      return;
    }
    const row = {
      pipeline_item_id: String(card.id),
      title: card.title,
      status: "In Testfase",
      test_status: "Nog niet getest",
      verantwoordelijke: "furgel",
    };
    // Voorvullen met wat de eigenaar bij de validatie zelf invulde (probleem en doelgroep). Dat zijn echte feiten en geen AI-gok.
    // Het publiek kiest de eigenaar daarna zelf; de businesscase en MVP-specificatie vul je aan bij "Over deze dienst".
    const norm = t => String(t || "").trim().toLowerCase();
    const idee = (services || []).find(sv => norm(sv.title) === norm(card.title));
    if (idee && (idee.problem || idee.target)) {
      row.dienst_omschrijving = [
        "Uit de validatie (door de eigenaar ingevuld, controleer en vul aan met feiten uit de businesscase en MVP-specificatie):",
        idee.problem ? "Probleem: " + idee.problem : "",
        idee.target ? "Voor wie: " + idee.target : "",
      ].filter(Boolean).join("\n");
    }
    const { data, error } = await supabase.from("launch_modules").insert([row]).select();
    if (!error && data) {
      setLaunches(p => [data[0], ...(p || [])]);
      logActivity("launch", row.title, "launch");
      setLaunchSel(data[0].id);
      // Auto-verplaats kaart naar test_fase indien nog niet daar
      if (card.col !== "test_fase") {
        await supabase.from("pipeline").update({ col: "test_fase", updated: "Launch Module gestart" }).eq("id", card.id);
        setPipeline(p => p.map(x => x.id === card.id ? { ...x, col: "test_fase", updated: "Launch Module gestart" } : x));
      }
      setView("launch");
    }
  }

  return (
    <div className="fu">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ ...KOP_STIJL }}>Bouwpipeline</h2>
        <button className="btn bg">Export</button>
      </div>
      <div style={{ display: "flex", gap: 11, overflowX: "auto", paddingBottom: 8 }}>
        {cols.map((col, idx) => {
          const cards = pipeline.filter(p => p.col === col.id);
          const colColors = {
            test_fase: { accent: C.yellow, dim: C.yellowDim, emptyMsg: "Verplaats MVP's hierheen om te testen" },
            acceptatie: { accent: C.green, dim: C.greenDim, emptyMsg: "Wacht op AI-goedkeuring" },
            ready_for_launch: { accent: C.cyan, dim: C.cyanDim, emptyMsg: "Wacht op acceptatie door Furgel" },
            live: { accent: C.green, dim: C.greenDim, emptyMsg: "Nog geen gelanceerde diensten" },
          };
          const accent = colColors[col.id];
          const isLaunchableCol = col.id === "test_fase" || col.id === "acceptatie" || col.id === "ready_for_launch" || col.id === "live";
          const isStartCol = col.id === "test_fase";
          return (
            <div key={col.id} className="kl" style={accent ? { border: "1px solid " + accent.accent + "40", background: accent.accent === "#34bd82" ? "rgba(52,189,130,0.03)" : accent.accent === "#e0a82e" ? "rgba(224,168,46,0.03)" : "rgba(127,155,181,0.03)" } : {}}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span className="lbl" style={accent ? { color: accent.accent } : {}}>{col.l}</span>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: C.muted }}>{cards.length}</span>
              </div>
              {cards.map(c => {
                const linkedLaunch = (launches || []).find(l => l.pipeline_item_id === String(c.id));
                const hasLaunch = !!linkedLaunch;
                return (
                  <div key={c.id} className="kc" style={accent ? { borderColor: accent.accent + "30" } : {}}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{c.title}</span>
                      {col.id !== "live" && (
                        <select value={c.priority || "MED"} onChange={e => setPrio(c.id, e.target.value)} style={{ fontSize: 10, padding: "1px 4px", width: "auto", color: PCOL[c.priority] || C.muted }}>
                          <option value="HIGH">HIGH</option><option value="MED">MED</option><option value="LOW">LOW</option>
                        </select>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: C.muted }}>{c.days}d</p>
                    <p style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>⟳ {c.updated}</p>
                    <div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }}>
                      {idx > 0 && <button className="btn bg" style={{ fontSize: 10, padding: "3px 7px", flex: 1 }} onClick={() => move(c.id, cols[idx - 1].id)}>←</button>}
                      {idx < cols.length - 1 && <button className="btn bg" style={{ fontSize: 10, padding: "3px 7px", flex: 1 }} onClick={() => move(c.id, cols[idx + 1].id)}>→</button>}
                    </div>
                    {isLaunchableCol && (hasLaunch || isStartCol) && (
                      <button
                        className="btn bp"
                        style={{ width: "100%", marginTop: 7, fontSize: 10, padding: "5px 7px", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: hasLaunch ? accent.accent : "#34bd82", color: "#000" }}
                        onClick={() => {
                          if (hasLaunch) { setLaunchSel(linkedLaunch.id); setView("launch"); }
                          else startLaunchModule(c);
                        }}
                      >
                        {hasLaunch ? "Launch Module openen" : "Start Launch Module"}
                      </button>
                    )}
                  </div>
                );
              })}
              {!cards.length && (
                <div style={{ padding: 13, textAlign: "center", color: accent ? accent.accent + "60" : C.dim, fontSize: 12, border: "1px dashed " + (accent ? accent.accent + "30" : C.border), borderRadius: 6 }}>
                  {accent ? accent.emptyMsg : "Leeg"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Winkels met een eigen server waar de OS de cijfers van ophaalt (Edge Function winkel-statistieken).
// Sleutel: de titel van de dienst in de Launch Module; waarde: de dienstnaam in winkel_koppelingen.
const WINKEL_DIENSTEN = { "Casa Celeste Store": "CasaCeleste", "Dif3r3nt Store": "Dif3r3ntStore", "Limited Floor": "LimitedFloor" };

function WinkelCijfers({ dienst, titel }) {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState("");
  useEffect(() => {
    let weg = false;
    supabase.functions.invoke("winkel-statistieken", { body: { dienst } }).then(({ data, error }) => {
      if (weg) return;
      if (error || !data || data.fout) setFout((data && data.fout) || "Cijfers niet beschikbaar");
      else setD(data);
    });
    return () => { weg = true; };
  }, [dienst]);
  const eur = n => "€" + Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <p className="lbl" style={{ marginBottom: 10 }}>{titel.toUpperCase()} · CIJFERS</p>
      {fout && <p style={{ fontSize: 12, color: C.dim }}>{fout}</p>}
      {!fout && !d && <p style={{ fontSize: 12, color: C.dim }}>Laden…</p>}
      {d && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 11 }}>
          {[
            ["Bezoekers vandaag", d.bezoekers?.vandaag ?? 0, C.blue],
            ["Bestellingen vandaag", d.bestellingen?.vandaag ?? 0, C.cyan],
            ["Omzet 30 dagen", eur(d.omzet?.dertigDagenEur), C.green],
            ["Producten live", d.producten?.inDeWinkel ?? 0, C.green],
            ["Wacht op akkoord", d.producten?.wachtOpAkkoord ?? 0, C.yellow],
            ["Fouten laatste dag", d.keten?.foutenLaatsteDag ?? 0, (d.keten?.foutenLaatsteDag ?? 0) > 0 ? C.red : C.dim],
          ].map(([l, v, ac]) => (
            <div className="st" key={l} style={{ "--ac": ac }}>
              <p className="lbl" style={{ marginBottom: 7 }}>{l}</p>
              <p style={{ fontFamily: "monospace", fontSize: 20, color: ac }}>{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Live winkelcijfers (Zichtbaarheid) voor een winkel met een eigen server, als de dagelijkse meting
// geen 30-dagencijfer heeft: de koppeling geeft bezoekers en herkomst over zeven dagen.
// Eén verzoek per winkel per minuut, gedeeld door de cellen in de tabel.
const ZOEKMACHINE = /^(www\.|m\.)?google\.[a-z.]+$|googlequicksearchbox|^(www\.)?bing\.com$|duckduckgo\.com$|ecosia\.org$|search\.yahoo\.com$|startpage\.com$/i;
// Waar de Zichtbaarheid de bezoekcijfers van een site vandaan haalt (seo_sites.meetbron).
const MEETBRONNEN = {
  "": "Niet gemeten",
  website: "Bezoekstatistiek van dif3r3nt.nl",
  winkel_db: "Winkeldatabase van de Dif3r3nt Store",
  koppeling: "Winkel met eigen server (koppeling)",
};
const winkelLiveCache = {};
function haalWinkelLive(dienst) {
  const c = winkelLiveCache[dienst];
  if (c && Date.now() - c.t < 60000) return c.p;
  const p = supabase.functions.invoke("winkel-statistieken", { body: { dienst } })
    .then(({ data, error }) => (error || !data || data.fout) ? null : data);
  winkelLiveCache[dienst] = { t: Date.now(), p };
  return p;
}
function WinkelLive({ dienst, veld }) {
  const [n, setN] = useState(undefined);
  useEffect(() => {
    if (!dienst) return;
    let weg = false;
    haalWinkelLive(dienst).then(data => {
      if (weg) return;
      if (!data) { setN(null); return; }
      if (veld === "bezoekers") setN(data.bezoekers?.zevenDagen ?? null);
      else setN((data.bezoekers?.herkomst || []).filter(h => ZOEKMACHINE.test(h.bron || "")).reduce((t, h) => t + (h.bezoekers || 0), 0));
    });
    return () => { weg = true; };
  }, [dienst, veld]);
  if (!dienst) return <span style={{ color: C.dim }}>niet gemeten</span>;
  if (n === undefined) return <span style={{ color: C.dim }}>laden…</span>;
  if (n === null) return <span style={{ color: C.dim }}>cijfers niet beschikbaar</span>;
  return <span>{n} <span style={{ color: C.dim }}>(live, 7 d)</span></span>;
}

// Diva & Gifty (het spel) heeft een eigen server; de cijfers komen via dezelfde Edge Function als de winkels.
// Koppeling: rij "DivaGifty" in winkel_koppelingen (url + sleutel van het spel).
function SpelCijfers() {
  const [d, setD] = useState(null);
  const [fout, setFout] = useState("");
  useEffect(() => {
    let weg = false;
    supabase.functions.invoke("winkel-statistieken", { body: { dienst: "DivaGifty" } }).then(({ data, error }) => {
      if (weg) return;
      if (error || !data || data.fout) setFout((data && data.fout) || "Cijfers niet beschikbaar");
      else setD(data);
    });
    return () => { weg = true; };
  }, []);
  const eur = n => "€" + Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sp = d?.spelers, ab = d?.abonnementen, sc = d?.scholen, dl = d?.delen;
  const tegels = d ? [
    ["Gebruikers in totaal", sp?.totaal ?? "–", C.blue],
    ["Daarvan thuis", sp?.thuisTotaal ?? "–", C.blue],
    ["Daarvan via school", sp?.schoolTotaal ?? "–", C.cyan],
    ["Nog actief (laatste 30 dagen)", sp?.dertigDagen ?? "–", C.green],
    ["Abonnementen actief", ab ? ab.actief : "–", C.green],
    ["In proefweek", ab ? ab.proef : "–", C.yellow],
    ["Opgezegd", ab ? ab.opgezegd : "–", ab && ab.opgezegd > 0 ? C.red : C.dim],
    ["Terugkerende omzet / maand", ab ? eur(ab.mrrEur) : "–", C.green],
    ["Scholen actief", sc?.actief ?? "–", C.green],
    ["Scholen in proef", sc?.proef ?? "–", C.yellow],
    ["Aanvragen wachten op akkoord", sc?.aanvragen ?? "–", (sc?.aanvragen ?? 0) > 0 ? C.red : C.dim],
    ["Kinderen in klassen", sc?.leerlingen ?? "–", C.cyan],
  ] : [];
  // Groeiactie "deel de link": elke rij is een unieke, nieuwe speler die via iemands persoonlijke link
  // binnenkwam en echt is gaan spelen (bezoekenTotaal telt ook de bezoeken die niet meetelden voor de
  // deler zelf, bv. door de dagcap of hetzelfde netwerk - zo blijft zichtbaar of het spel echt verspreidt,
  // ook als de beloning daar niet (meer) voor meetelt).
  const delenTegels = dl ? [
    ["Ouders die een deel-link hebben", dl.delers ?? "–", C.blue],
    ["Daarvan al minstens 1 nieuwe speler", dl.delersMetHit ?? "–", C.cyan],
    ["Nu tijdelijk ontgrendeld door delen", dl.nuOntgrendeld ?? "–", C.green],
    ["Nieuwe spelers via een deel-link (7 d)", dl.bezoeken7 ?? "–", C.green],
    ["Nieuwe spelers via een deel-link (30 d)", dl.bezoeken30 ?? "–", C.green],
    ["Nieuwe spelers via een deel-link (totaal)", dl.bezoekenTellend ?? "–", C.blue],
    ["Haalden 5 (1 week)", dl.bereikt5 ?? "–", C.yellow],
    ["Haalden 10 (2 weken)", dl.bereikt10 ?? "–", C.yellow],
    ["Haalden 20 (1 maand)", dl.bereikt20 ?? "–", C.yellow],
  ] : [];
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <p className="lbl" style={{ marginBottom: 10 }}>DIVA & GIFTY · CIJFERS</p>
      {fout && <p style={{ fontSize: 12, color: C.dim }}>{fout}</p>}
      {!fout && !d && <p style={{ fontSize: 12, color: C.dim }}>Laden…</p>}
      {d && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 11 }}>
          {tegels.map(([l, v, ac]) => (
            <div className="st" key={l} style={{ "--ac": ac }}>
              <p className="lbl" style={{ marginBottom: 7 }}>{l}</p>
              <p style={{ fontFamily: "monospace", fontSize: 20, color: ac }}>{v}</p>
            </div>
          ))}
        </div>
      )}
      {d && <p style={{ fontSize: 11, color: C.dim, marginTop: 10 }}>Gebruikers zijn unieke toestellen, geen personen (een gezin met twee tablets telt dubbel; ouders die de telling uitzetten tellen niet mee). Via school = toestel dat ooit bij een klas is aangesloten. Abonnementen komen rechtstreeks uit Mollie.</p>}
      {dl && (
        <>
          <p className="lbl" style={{ marginTop: 16, marginBottom: 10 }}>WORDT DE LINK ECHT GEDEELD?</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 11 }}>
            {delenTegels.map(([l, v, ac]) => (
              <div className="st" key={l} style={{ "--ac": ac }}>
                <p className="lbl" style={{ marginBottom: 7 }}>{l}</p>
                <p style={{ fontFamily: "monospace", fontSize: 20, color: ac }}>{v}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11, color: C.dim, marginTop: 10 }}>Bij 5/10/20 nieuwe spelers via een eigen link ontgrendelt 1 week/2 weken/1 maand volledige toegang. "Nieuwe spelers" zijn unieke bezoekers die de link ook echt openden en gingen spelen, niet alleen een klik op WhatsApp - dat laatste kan geen server zien.</p>
        </>
      )}
    </div>
  );
}

// Klantenbestand: gevuld door spel-melding bij elk nieuw of verlengd abonnement (tabel "klanten",
// dienst-tag zodat andere diensten dezelfde tabel later kunnen hergebruiken). Rechtstreeks uit de
// database, geen Edge Function nodig: alleen OS-leden kunnen hier inloggen (RLS "Allow all for authenticated").
function SpelKlanten() {
  const [rijen, setRijen] = useState(null);
  const [fout, setFout] = useState("");
  useEffect(() => {
    let weg = false;
    supabase.from("klanten").select("*").eq("dienst", "DivaGifty").order("bijgewerkt", { ascending: false }).then(({ data, error }) => {
      if (weg) return;
      if (error) setFout(error.message);
      else setRijen(data || []);
    });
    return () => { weg = true; };
  }, []);
  const datum = (d) => new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
  const eur = (n) => "€" + Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <p className="lbl" style={{ marginBottom: 10 }}>DIVA & GIFTY · KLANTEN ({rijen ? rijen.length : "…"})</p>
      {fout && <p style={{ fontSize: 12, color: C.dim }}>{fout}</p>}
      {!fout && !rijen && <p style={{ fontSize: 12, color: C.dim }}>Laden…</p>}
      {rijen && rijen.length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Nog geen abonnementen.</p>}
      {rijen && rijen.length > 0 && (
        <div className="scroll">
          <table>
            <thead><tr><th>E-mail</th><th>Plan</th><th>Bedrag</th><th>Sinds / laatst bijgewerkt</th></tr></thead>
            <tbody>
              {rijen.map((r) => (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td>{r.plan === "jaar" ? "Jaar" : "Maand"}</td>
                  <td>{eur(r.bedrag_eur)}</td>
                  <td>{datum(r.bijgewerkt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Scholen van Diva & Gifty: aanvragen goedkeuren, licenties activeren, leerkrachtlinks. Loopt via de Edge Function spel-beheer
// (alleen OS-leden); bij goedkeuren en activeren mailt die de leerkrachtlink naar de school.
const SCHOOL_PAKKETTEN = { klein: "klein (4 klassen, 100 kinderen, € 249)", standaard: "standaard (12 klassen, 300 kinderen, € 449)", groot: "groot (24 klassen, 600 kinderen, € 799)" };
function SpelScholen() {
  const [scholen, setScholen] = useState(null);
  const [fout, setFout] = useState("");
  const [bericht, setBericht] = useState(null);
  const [bezig, setBezig] = useState("");
  const laad = () => supabase.functions.invoke("spel-beheer", { body: { actie: "lijst" } }).then(({ data, error }) => {
    if (error || !data || data.fout) setFout((data && data.fout) || "Scholen niet beschikbaar");
    else { setFout(""); setScholen(data.scholen || []); }
  });
  useEffect(() => { laad(); }, []);
  const doe = async (actie, extra, tekst) => {
    setBezig(actie + (extra.id || extra.teacher || "")); setBericht(null);
    const { data, error } = await supabase.functions.invoke("spel-beheer", { body: { actie, ...extra } });
    setBezig("");
    if (error || !data || data.fout) { setBericht({ tekst: "Mislukt: " + ((data && data.fout) || "probeer het opnieuw"), fout: true }); return null; }
    setBericht({ tekst: typeof tekst === "function" ? tekst(data) : tekst, link: data.link });
    laad();
    return data;
  };
  const kopieer = async (link) => { try { await navigator.clipboard.writeText(link); setBericht({ tekst: "Link gekopieerd.", link }); } catch { setBericht({ tekst: "Kopiëren lukte niet, kopieer de link hieronder zelf.", link }); } };
  const datum = d => d ? new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "";
  const stat = { aanvraag: [C.yellowDim || C.blueDim, C.yellow, "Aanvraag"], proef: [C.cyanDim, C.cyan, "Proef"], actief: [C.greenDim, C.green, "Actief"], verlopen: [C.redDim, C.red, "Verlopen"], afgewezen: ["rgba(96,96,128,.12)", C.muted, "Afgewezen"] };
  const wacht = (scholen || []).filter(s => s.status === "aanvraag").length;
  const mailTekst = d => d.gemaild ? "Gedaan. De leerkrachtlink is gemaild naar " + d.gemaildNaar + "." : "Gedaan, maar de mail is niet verstuurd. Kopieer de link hieronder en stuur hem zelf.";
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <p className="lbl" style={{ marginBottom: 10 }}>DIVA & GIFTY · SCHOLEN {wacht > 0 && <span style={{ color: C.red }}>· {wacht} WACHT{wacht === 1 ? "" : "EN"} OP AKKOORD</span>}</p>
      {fout && <p style={{ fontSize: 12, color: C.dim }}>{fout}</p>}
      {!fout && !scholen && <p style={{ fontSize: 12, color: C.dim }}>Laden…</p>}
      {bericht && (
        <div style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8, background: bericht.fout ? C.redDim : C.greenDim, fontSize: 13 }}>
          <div>{bericht.tekst}</div>
          {bericht.link && <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input readOnly value={bericht.link} style={{ flex: 1, minWidth: 220, fontSize: 11 }} onFocus={e => e.target.select()} />
            <button className="btn" onClick={() => kopieer(bericht.link)}>Kopieer</button></div>}
        </div>
      )}
      {scholen && scholen.length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Nog geen scholen. Aanvragen verschijnen hier en je krijgt een melding en een mail.</p>}
      {(scholen || []).map(s => {
        const [bg, fg, label] = stat[s.status] || stat.afgewezen;
        const actief = s.leerkrachten_lijst ? s.leerkrachten_lijst.filter(t => t.actief) : [];
        return (
          <div key={s.id} style={{ borderTop: "1px solid " + C.border, padding: "12px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div><span style={{ fontWeight: 600 }}>{s.naam}</span> <span style={{ color: C.dim, fontSize: 12 }}>BRIN {s.brin}</span></div>
              <Bdg label={label} bg={bg} fg={fg} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
              {s.contact_naam} · <a href={"mailto:" + s.contact_email} style={{ color: C.blue }}>{s.contact_email}</a> · aangevraagd {datum(s.aangevraagd_op)}
              {s.status === "proef" && " · proef tot " + datum(s.proef_tot)}
              {s.status === "actief" && " · pakket " + s.pakket + " tot " + datum(s.geldig_tot)}
              {s.status === "verlopen" && " · afgelopen " + datum(s.geldig_tot || s.proef_tot)}
              {(s.status === "proef" || s.status === "actief" || s.status === "verlopen") && " · " + s.klassen + " klassen, " + s.leerlingen + " kinderen"}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              {s.status === "aanvraag" && <>
                <button className="btn bg" disabled={!!bezig} onClick={() => { if (window.confirm("Goedkeuren? " + s.naam + " (BRIN " + s.brin + ", " + s.contact_email + ") krijgt een proefperiode van 30 dagen. Controleer eerst de BRIN en het e-mailadres.")) doe("goedkeuren", { id: s.id }, mailTekst); }}>Goedkeuren</button>
                <button className="btn" disabled={!!bezig} onClick={() => { const o = window.prompt("Reden voor afwijzen (voor jezelf):", ""); if (o !== null) doe("afwijzen", { id: s.id, opmerking: o }, "Afgewezen."); }}>Afwijzen</button></>}
              {(s.status === "proef" || s.status === "actief" || s.status === "verlopen") &&
                <button className="btn bg" disabled={!!bezig} onClick={() => {
                  const p = window.prompt("Pakket kiezen na betaling:\n" + Object.entries(SCHOOL_PAKKETTEN).map(([k, v]) => k + " = " + v).join("\n"), s.pakket || "klein");
                  if (!p || !SCHOOL_PAKKETTEN[p.trim()]) return;
                  const j = window.prompt("Voor hoeveel jaar (1 tot 3)?", "1");
                  if (j) doe("activeren", { id: s.id, pakket: p.trim(), jaren: Number(j) || 1 }, mailTekst);
                }}>{s.status === "actief" ? "Verlengen of ander pakket" : "Activeren (betaald)"}</button>}
              {(s.status === "proef" || s.status === "actief" || s.status === "verlopen") &&
                <button className="btn" disabled={!!bezig} onClick={() => { const n = window.prompt("Naam van de leerkracht:"); if (!n) return; const e = window.prompt("E-mailadres van de leerkracht:"); if (e) doe("leerkracht_toevoegen", { id: s.id, naam: n, email: e }, "Leerkracht toegevoegd. Stuur de link door."); }}>Leerkracht toevoegen</button>}
              {s.status !== "afgewezen" && <button className="btn" disabled={!!bezig} onClick={() => { if (window.confirm("ALLES van " + s.naam + " verwijderen (klassen, kinderen, voortgang, leerkrachten)? Dit kan niet ongedaan worden.")) doe("verwijderen", { id: s.id }, "School en alle gegevens verwijderd."); }} style={{ color: C.red }}>Verwijderen</button>}
              {s.status === "afgewezen" && <button className="btn" disabled={!!bezig} onClick={() => doe("verwijderen", { id: s.id }, "Verwijderd.")}>Opruimen</button>}
            </div>
            {actief.length > 0 && <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
              Leerkrachten: {actief.map(t => (
                <span key={t.id} style={{ marginRight: 12 }}>{t.naam} <button className="btn" style={{ padding: "1px 8px", fontSize: 11 }} onClick={async () => { const d = await doe("link", { teacher: t.id }, "Nieuwe link voor " + t.naam + "."); if (d && d.link) kopieer(d.link); }}>Link</button>{" "}
                  <button className="btn" style={{ padding: "1px 8px", fontSize: 11, color: C.red }} onClick={() => { if (window.confirm(t.naam + " de toegang intrekken?")) doe("leerkracht_intrekken", { teacher: t.id }, "Toegang van " + t.naam + " ingetrokken."); }}>Intrekken</button></span>))}
            </div>}
          </div>
        );
      })}
      <p style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Goedkeuren en activeren mailen de persoonlijke leerkrachtlink naar de school. Betalen gaat per factuur: activeer pas als de school betaald heeft.</p>
    </div>
  );
}

// Steunerslinks van Diva & Gifty (partnerprogramma): jij maakt partners aan, stelt per partner het percentage in (20 tot 50)
// en betaalt uit. Een partner verdient dat percentage van elke betaling van een klant die via zijn of haar link kwam, 12 maanden lang.
// Loopt via de Edge Function spel-beheer (alleen OS-leden), met dezelfde beheersleutel als het schoolbeheer.
const PARTNER_STAND = { wacht: ["In afwachting", "yellow"], goedgekeurd: ["Goedgekeurd", "blue"], uitbetaald: ["Uitbetaald", "green"], teruggedraaid: ["Teruggedraaid", "muted"] };
function SpelPartners() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState("");
  const [bericht, setBericht] = useState(null);
  const [bezig, setBezig] = useState("");
  const [nieuw, setNieuw] = useState({ naam: "", email: "", percentage: 20 });
  const [open, setOpen] = useState(null);
  const [regels, setRegels] = useState([]);
  const laad = () => supabase.functions.invoke("spel-beheer", { body: { actie: "partner_lijst" } }).then(({ data: d, error }) => {
    if (error || !d || d.fout) setFout((d && d.fout) || "Partners niet beschikbaar");
    else { setFout(""); setData(d); }
  });
  useEffect(() => { laad(); }, []);
  const regel = data?.regels || { minPct: 20, maxPct: 50, maanden: 12, drempel: 25 };
  const eur = n => "€" + Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const datum = d => d ? new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "";
  const doe = async (actie, extra, tekst) => {
    setBezig(actie + (extra.id || "")); setBericht(null);
    const { data: d, error } = await supabase.functions.invoke("spel-beheer", { body: { actie, ...extra } });
    setBezig("");
    if (error || !d || d.fout) { setBericht({ tekst: "Mislukt: " + ((d && d.fout) || "probeer het opnieuw"), fout: true }); return null; }
    setBericht({ tekst: typeof tekst === "function" ? tekst(d) : tekst, link: d.deelLink || d.standLink });
    laad();
    return d;
  };
  const kopieer = async (link, wat) => { try { await navigator.clipboard.writeText(link); setBericht({ tekst: wat + " gekopieerd.", link }); } catch { setBericht({ tekst: "Kopiëren lukte niet, kopieer de link hieronder zelf.", link }); } };
  const pctOk = n => Number.isInteger(n) && n >= regel.minPct && n <= regel.maxPct;
  const maak = async () => {
    const pct = Number(nieuw.percentage);
    if (nieuw.naam.trim().length < 2) return setBericht({ tekst: "Vul een naam in.", fout: true });
    if (!pctOk(pct)) return setBericht({ tekst: "Het percentage moet een geheel getal zijn van " + regel.minPct + " tot " + regel.maxPct + ".", fout: true });
    const d = await doe("partner_maak", { naam: nieuw.naam.trim(), email: nieuw.email.trim(), percentage: pct }, x => "Partner aangemaakt. Dit is de deellink van " + nieuw.naam.trim() + ".");
    if (d) setNieuw({ naam: "", email: "", percentage: 20 });
  };
  const toon = async (p) => {
    if (open === p.id) return setOpen(null);
    const { data: d } = await supabase.functions.invoke("spel-beheer", { body: { actie: "partner_verdiensten", id: p.id } });
    setRegels((d && d.verdiensten) || []); setOpen(p.id);
  };
  const partners = data?.partners || [];
  const tebetalen = partners.reduce((a, p) => a + (p.totalen?.goedgekeurd || 0), 0);
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <p className="lbl" style={{ marginBottom: 10 }}>DIVA & GIFTY · STEUNERSLINKS {tebetalen >= regel.drempel && <span style={{ color: C.green }}>· {eur(tebetalen)} KLAAR OM UIT TE BETALEN</span>}</p>
      {fout && <p style={{ fontSize: 12, color: C.dim }}>{fout}</p>}
      {!fout && !data && <p style={{ fontSize: 12, color: C.dim }}>Laden…</p>}
      {bericht && (
        <div style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8, background: bericht.fout ? C.redDim : C.greenDim, fontSize: 13 }}>
          <div>{bericht.tekst}</div>
          {bericht.link && <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input readOnly value={bericht.link} style={{ flex: 1, minWidth: 220, fontSize: 11 }} onFocus={e => e.target.select()} />
            <button className="btn" onClick={() => kopieer(bericht.link, "Link")}>Kopieer</button></div>}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
        <input placeholder="Naam" value={nieuw.naam} onChange={e => setNieuw({ ...nieuw, naam: e.target.value })} style={{ width: 150 }} />
        <input placeholder="E-mail (optioneel)" value={nieuw.email} onChange={e => setNieuw({ ...nieuw, email: e.target.value })} style={{ width: 190 }} />
        <label style={{ fontSize: 12, color: C.muted }}>Percentage <input type="number" min={regel.minPct} max={regel.maxPct} step="1" value={nieuw.percentage} onChange={e => setNieuw({ ...nieuw, percentage: e.target.value })} style={{ width: 64 }} /> %</label>
        <button className="btn bg" disabled={!!bezig} onClick={maak}>Partner aanmaken</button>
      </div>
      <p style={{ fontSize: 11, color: C.dim, margin: "0 0 4px" }}>Het percentage ligt tussen {regel.minPct} en {regel.maxPct}. Het e-mailadres is alleen om te voorkomen dat iemand aan zichzelf verdient.</p>
      {data && partners.length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Nog geen partners.</p>}
      {partners.map(p => {
        const t = p.totalen || {};
        return (
          <div key={p.id} style={{ borderTop: "1px solid " + C.border, padding: "12px 0", opacity: p.actief ? 1 : 0.6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div><span style={{ fontWeight: 600 }}>{p.naam}</span> <span style={{ color: C.dim, fontSize: 12 }}>{p.code}{p.email ? " · " + p.email : ""}</span></div>
              <Bdg label={p.actief ? p.percentage + "%" : "Uit · " + p.percentage + "%"} bg={p.actief ? C.greenDim : "rgba(96,96,128,.12)"} fg={p.actief ? C.green : C.muted} />
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
              {t.bezoekers || 0} bezoekers · {t.klanten || 0} abonnees · in afwachting {eur(t.wacht)} · <b style={{ color: t.goedgekeurd > 0 ? C.green : C.muted }}>goedgekeurd {eur(t.goedgekeurd)}</b> · uitbetaald {eur(t.uitbetaald)}
              {t.te_verrekenen > 0 && <span style={{ color: C.red }}> · te verrekenen {eur(t.te_verrekenen)}</span>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              <button className="btn bg" onClick={() => kopieer(p.deelLink, "Deellink")}>Kopieer deellink</button>
              <button className="btn" onClick={() => kopieer(p.standLink, "Link naar het eigen scherm van " + p.naam)}>Link voor eigen scherm</button>
              <button className="btn" disabled={!!bezig} onClick={() => {
                const v = window.prompt("Nieuw percentage voor " + p.naam + " (" + regel.minPct + " tot " + regel.maxPct + "). Geldt voor nieuwe betalingen; wat al verdiend is, blijft zoals het was.", String(p.percentage));
                if (v === null) return;
                const n = Number(v);
                if (!pctOk(n)) return setBericht({ tekst: "Het percentage moet een geheel getal zijn van " + regel.minPct + " tot " + regel.maxPct + ".", fout: true });
                doe("partner_wijzig", { id: p.id, percentage: n }, "Percentage van " + p.naam + " staat nu op " + n + "%.");
              }}>Percentage wijzigen</button>
              <button className="btn" disabled={!!bezig || t.goedgekeurd <= 0} onClick={() => { if (window.confirm("Heb je " + eur(t.goedgekeurd) + " overgemaakt aan " + p.naam + "? Dan zet ik alles wat goedgekeurd is op uitbetaald.")) doe("partner_uitbetaald", { id: p.id }, d => eur(d.totaal) + " staat nu als uitbetaald aan " + p.naam + " (" + d.aantal + " betalingen)."); }}>Uitbetaald markeren</button>
              <button className="btn" onClick={() => toon(p)}>{open === p.id ? "Verberg betalingen" : "Betalingen"}</button>
              <button className="btn" disabled={!!bezig} onClick={() => doe("partner_wijzig", { id: p.id, actief: !p.actief }, p.actief ? "Link van " + p.naam + " staat uit. Nieuwe betalingen leveren niets meer op." : "Link van " + p.naam + " staat weer aan.")}>{p.actief ? "Uitzetten" : "Aanzetten"}</button>
              <button className="btn" disabled={!!bezig} style={{ color: C.red }} onClick={() => { if (window.confirm("Een nieuwe link voor het eigen scherm van " + p.naam + " maken? De oude link werkt dan niet meer. De deellink blijft hetzelfde.")) doe("partner_nieuwe_link", { id: p.id }, "Nieuwe link voor het eigen scherm. De oude werkt niet meer."); }}>Nieuwe schermlink</button>
            </div>
            {open === p.id && <div style={{ marginTop: 8, fontSize: 12, color: C.muted }}>
              {regels.length === 0 && "Nog geen betalingen."}
              {regels.map(r => { const [lbl, kl] = PARTNER_STAND[r.status] || [r.status, "muted"]; return (
                <div key={r.betaling} style={{ display: "flex", gap: 12, padding: "2px 0", flexWrap: "wrap" }}>
                  <span style={{ minWidth: 90 }}>{datum(r.betaald_op)}</span>
                  <span>{eur(r.bruto)} × {r.percentage}% = <b style={{ color: C.text }}>{eur(r.bedrag)}</b></span>
                  <span style={{ color: C[kl] }}>{lbl}{r.status === "wacht" ? " tot " + datum(r.goedkeur_vanaf) : ""}{r.verrekenen ? " (al uitbetaald: verrekenen)" : ""}</span>
                </div>); })}
            </div>}
          </div>
        );
      })}
      <p style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Een partner verdient {regel.maanden} maanden lang het afgesproken percentage van elke betaling van een klant die via de link kwam. Na de bedenktijd van 14 dagen wordt een verdienste goedgekeurd; terugbetalingen vervallen. Uitbetalen doe je zelf (vanaf {eur(regel.drempel)} is handig), daarna markeer je het hier. Laat voorwaarden en belastingafhandeling controleren door je accountant.</p>
    </div>
  );
}

function LiveServices({ liveServices = [], user }) {
  const gelanceerd = liveServices.filter(l => l.status === "Gelanceerd");
  const klaar = liveServices.filter(l => l.status === "Klaar voor Launch");
  const fmtDate = d => d ? new Date(d).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  return (
    <div className="fu">
      <Kop titel="Live Diensten" />
      <Tellers items={[{ label: "Live", waarde: gelanceerd.length, kleur: C.green }, { label: "Klaar voor launch", waarde: klaar.length, kleur: C.cyan }, { label: "Totaal", waarde: liveServices.length, kleur: C.blue }]} />
      {liveServices.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "32px 16px", color: C.dim, fontSize: 13 }}>
          Nog geen live diensten. Zet een dienst in de Launch Module op status <strong>"Gelanceerd"</strong> of <strong>"Klaar voor Launch"</strong> — dan verschijnt 'ie hier.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {liveServices.map(s => {
            const live = s.status === "Gelanceerd";
            // Alleen tonen wat de eigenaar heeft vastgelegd. De doelgroep telt pas als er feiten (omschrijving) en een
            // publiek bij staan; anders is het een AI-gok. Een lanceerdatum is de echte datum, nooit de aanmaakdatum.
            const feitenOk = !!((s.dienst_omschrijving || "").trim() && s.publiek && s.doelgroep);
            const PUBLIEK_KORT = { zakelijk: "Zakelijk", consumenten: "Consumenten", ouders_kinderen: "Ouders en kinderen" };
            return (
              <Rij key={s.id} titel={s.title || "—"}
                rechts={<Bdg label={live ? "● LIVE" : "Klaar"} bg={live ? C.greenDim : C.cyanDim} fg={live ? C.green : C.cyan} />}
                meta={<>
                  {feitenOk
                    ? <span title={s.doelgroep}><strong style={{ color: C.text, fontWeight: 600 }}>{PUBLIEK_KORT[s.publiek] || s.publiek}</strong>{" · " + s.doelgroep}</span>
                    : <span title="Vul in de Launch Module bij 'Over deze dienst' het publiek en de feiten in" style={{ color: C.yellow }}>⚠ Feiten ontbreken</span>}
                  {s.launch_datum
                    ? <span>{fmtDate(s.launch_datum)}</span>
                    : live
                      ? <span title="Zet de lanceerdatum in de Launch Module, tab Launch Status" style={{ color: C.yellow }}>⚠ Datum ontbreekt</span>
                      : <span style={{ color: C.dim }}>Nog niet gelanceerd</span>}
                  {s.verantwoordelijke && <span style={{ textTransform: "capitalize" }}>{s.verantwoordelijke}</span>}
                </>} />
            );
          })}
        </div>
      )}

      {liveServices.filter(s => WINKEL_DIENSTEN[s.title]).map(s => (
        <WinkelCijfers key={s.id} dienst={WINKEL_DIENSTEN[s.title]} titel={s.title} />
      ))}

      <SpelCijfers />
      <SpelKlanten />
      <SpelScholen />
      <SpelPartners />

      <div style={{ marginTop: 30 }}>
        <h2 style={{ ...KOP_STIJL, marginBottom: 4 }}>Diensten & interesse</h2>
        <DienstInteresse user={user} />
      </div>
    </div>
  );
}

const B2B_STATUSES = ["cold", "prospect", "qualified", "proposal", "won", "lost"];
const B2C_STATUSES = ["lead", "interesse", "demo", "offerte", "gewonnen", "verloren"];
const B2B_INDUSTRIES = ["Tech", "Retail", "Finance", "Zorg", "Horeca", "Logistiek", "Overig"];
const B2C_SOURCES = ["LinkedIn", "Referral", "Website", "Social Media", "Event", "Anders"];
const B2B_SIZES = ["1–10", "11–50", "51–200", "200+"];
const SEG_STATUS_COL = {
  cold: C.muted, prospect: C.blue, qualified: C.yellow, proposal: C.purple, won: C.green, lost: C.red,
  lead: C.muted, interesse: C.blue, demo: C.yellow, offerte: C.purple, gewonnen: C.green, verloren: C.red,
};

// ─── DIENSTEN-CATALOGUS + INTERESSE-WHITELIST (in Live Diensten) ───────────────
// Registreer diensten (eenmalig zoals 'website bouwen + hosting regelen, geen
// onderhoud' of doorlopend). Per dienst een publieke interesse-link die je op de
// site van de dienst (bv. Reason3n) zet; inzendingen vullen automatisch de
// whitelist. Geinteresseerden zet je met 1 klik om naar een CRM-lead.
const slugify = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-+|-+$)/g, "");
// Apex dif3r3nt.nl staat nog op mijn.host; de interesse-pagina draait op de
// Vercel-website. Zodra dif3r3nt.nl naar Vercel wijst kan dit terug naar dif3r3nt.nl.
const INTEREST_BASE = "https://dif3r3nt-website.vercel.app/interesse";

function DienstInteresse({ user }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [diensten, setDiensten] = useState([]);
  const [counts, setCounts] = useState({});
  const [sel, setSel] = useState(null);
  const [interest, setInterest] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nd, setNd] = useState({ name: "", dienst_type: "eenmalig", prijs: "", scope: "", description: "" });
  const [ni, setNi] = useState({ name: "", email: "", company: "", note: "" });
  const [toast, setToast] = useState(null);

  useEffect(() => { (async () => {
    const { data } = await supabase.from("crm_services").select("*").order("created_at", { ascending: false });
    setDiensten(data || []);
    const { data: si } = await supabase.from("service_interest").select("crm_service_id");
    const c = {}; (si || []).forEach(r => { c[r.crm_service_id] = (c[r.crm_service_id] || 0) + 1; });
    setCounts(c);
  })(); }, []);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 2500); }
  const prijsLabel = (d) => d.prijs != null ? ("€ " + Number(d.prijs).toLocaleString("nl-NL") + (d.dienst_type === "doorlopend" ? " /mnd" : " eenmalig")) : "prijs n.t.b.";

  async function addDienst() {
    if (!nd.name.trim()) return;
    const base = slugify(nd.name) || "dienst";
    const taken = new Set(diensten.map(d => d.slug).filter(Boolean));
    let slug = base, n = 2; while (taken.has(slug)) { slug = base + "-" + n; n++; }
    const row = { name: nd.name.trim(), slug, dienst_type: nd.dienst_type, prijs: nd.prijs ? Number(nd.prijs) : null, scope: nd.scope || null, description: nd.description || null, status: "actief", created_by: meKey };
    const { data, error } = await supabase.from("crm_services").insert([row]).select();
    if (error) { showToast("✗ " + error.message); return; }
    logActivity("dienst", nd.name, "live");
    setDiensten(p => [data[0], ...p]); setShowNew(false);
    setNd({ name: "", dienst_type: "eenmalig", prijs: "", scope: "", description: "" });
    showToast("✓ Dienst toegevoegd");
  }
  async function openDienst(d) {
    setSel(d);
    const { data } = await supabase.from("service_interest").select("*").eq("crm_service_id", d.id).order("created_at", { ascending: false });
    setInterest(data || []);
  }
  async function patchDienst(id, patch) {
    setDiensten(p => p.map(d => d.id === id ? { ...d, ...patch } : d));
    setSel(s => (s && s.id === id) ? { ...s, ...patch } : s);
    await supabase.from("crm_services").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
  }
  async function deleteDienst(id) {
    if (!window.confirm("Dienst + interesselijst definitief verwijderen?")) return;
    const { error } = await supabase.from("crm_services").delete().eq("id", id);
    if (error) { showToast("✗ " + error.message); return; }
    setDiensten(p => p.filter(d => d.id !== id)); if (sel?.id === id) setSel(null);
  }
  async function addInterest() {
    if (!ni.name.trim() || !sel) return;
    const row = { crm_service_id: sel.id, name: ni.name.trim(), email: ni.email || null, company: ni.company || null, note: ni.note || null, created_by: meKey };
    const { data, error } = await supabase.from("service_interest").insert([row]).select();
    if (error) { showToast("✗ " + error.message); return; }
    setInterest(p => [data[0], ...p]); setCounts(c => ({ ...c, [sel.id]: (c[sel.id] || 0) + 1 }));
    setNi({ name: "", email: "", company: "", note: "" });
  }
  async function delInterest(id) {
    await supabase.from("service_interest").delete().eq("id", id);
    setInterest(p => p.filter(x => x.id !== id));
    setCounts(c => ({ ...c, [sel.id]: Math.max(0, (c[sel.id] || 1) - 1) }));
  }
  async function toLead(it) {
    const segment = it.company ? "b2b" : "b2c";
    const row = { name: it.name, company: it.company || null, interest: "Interesse: " + (sel?.name || ""), status: segment === "b2b" ? "cold" : "lead", segment, email: it.email || null, source: "interesse", notes: it.note || ("Geinteresseerd in dienst: " + (sel?.name || "")) };
    const { data, error } = await supabase.from("leads").insert([row]).select();
    if (error) { showToast("✗ " + error.message); return; }
    await supabase.from("service_interest").update({ lead_id: data[0].id }).eq("id", it.id);
    setInterest(p => p.map(x => x.id === it.id ? { ...x, lead_id: data[0].id } : x));
    showToast("✓ Lead aangemaakt in CRM");
  }

  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, fontFamily: "monospace", zIndex: 999 }}>{toast}</div>}

      {!sel && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <p style={{ fontSize: 12, color: C.muted }}>Eenmalige en doorlopende diensten, met per dienst een interesse-/wachtlijst om vraag te peilen.</p>
            <button className="btn bp" style={{ fontSize: 12 }} onClick={() => setShowNew(v => !v)}>+ Nieuwe dienst</button>
          </div>
          {showNew && (
            <div className="card" style={{ marginBottom: 14 }}>
              <div className="frm-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Naam *</p><input value={nd.name} onChange={e => setNd(p => ({ ...p, name: e.target.value }))} placeholder="bv. Website bouwen" /></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Type</p><select value={nd.dienst_type} onChange={e => setNd(p => ({ ...p, dienst_type: e.target.value }))}><option value="eenmalig">Eenmalig</option><option value="doorlopend">Doorlopend</option></select></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Prijs (€)</p><input type="number" value={nd.prijs} onChange={e => setNd(p => ({ ...p, prijs: e.target.value }))} placeholder={nd.dienst_type === "doorlopend" ? "per maand" : "eenmalig"} /></div>
              </div>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Scope (wat wel/niet inbegrepen)</p>
              <input value={nd.scope} onChange={e => setNd(p => ({ ...p, scope: e.target.value }))} placeholder="bv. incl. hosting opzetten; excl. onderhoud" style={{ marginBottom: 10 }} />
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Omschrijving</p>
              <textarea rows={2} value={nd.description} onChange={e => setNd(p => ({ ...p, description: e.target.value }))} style={{ fontSize: 12, marginBottom: 10 }} />
              <button className="btn bp" disabled={!nd.name.trim()} onClick={addDienst}>Dienst toevoegen</button>
            </div>
          )}
          {diensten.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen diensten geregistreerd.</p></div>}
          {diensten.map(d => (
            <div key={d.id} className="card" style={{ marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }} onClick={() => openDienst(d)}>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{d.name}</span>
                <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{prijsLabel(d)}{d.scope ? " · " + d.scope : ""}</span>
              </div>
              <span style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                <Bdg label={d.dienst_type === "eenmalig" ? "EENMALIG" : "DOORLOPEND"} bg={d.dienst_type === "eenmalig" ? C.blueDim : C.purpleDim} fg={d.dienst_type === "eenmalig" ? C.blue : C.purple} />
                <span style={{ fontSize: 11, color: (counts[d.id] || 0) > 0 ? C.green : C.muted, fontWeight: 600 }}>{counts[d.id] || 0} interesse</span>
              </span>
            </div>
          ))}
        </>
      )}

      {sel && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setSel(null)}>← Diensten</button>
            <button onClick={() => deleteDienst(sel.id)} style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "3px 9px" }}>✕ Verwijderen</button>
          </div>
          <div className="card" style={{ marginBottom: 12 }}>
            <p className="lbl" style={{ marginBottom: 10 }}>Dienst</p>
            <div className="frm-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Naam</p><input value={sel.name} onChange={e => patchDienst(sel.id, { name: e.target.value })} /></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Type</p><select value={sel.dienst_type} onChange={e => patchDienst(sel.id, { dienst_type: e.target.value })}><option value="eenmalig">Eenmalig</option><option value="doorlopend">Doorlopend</option></select></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Prijs (€)</p><input type="number" value={sel.prijs ?? ""} onChange={e => patchDienst(sel.id, { prijs: e.target.value ? Number(e.target.value) : null })} /></div>
            </div>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Scope</p>
            <input value={sel.scope || ""} onChange={e => patchDienst(sel.id, { scope: e.target.value })} placeholder="bv. incl. hosting opzetten; excl. onderhoud" style={{ marginBottom: 10 }} />
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Omschrijving</p>
            <textarea rows={2} value={sel.description || ""} onChange={e => patchDienst(sel.id, { description: e.target.value })} style={{ fontSize: 12 }} />
          </div>
          <div className="card" style={{ marginBottom: 12 }}>
            <p className="lbl" style={{ marginBottom: 6 }}>Interesse-link (op de site van de dienst)</p>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Zet deze link op de website van de dienst (bv. Reason3n). Wie interesse toont, verschijnt automatisch in de whitelist hieronder.</p>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <code style={{ flex: 1, fontSize: 12, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: "8px 10px", overflowX: "auto", whiteSpace: "nowrap" }}>{INTEREST_BASE + "?dienst=" + (sel.slug || "")}</code>
              <button className="btn bg" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => { try { navigator.clipboard && navigator.clipboard.writeText(INTEREST_BASE + "?dienst=" + (sel.slug || "")); } catch (e) { /* stil */ } showToast("✓ Link gekopieerd"); }}>Kopieer</button>
            </div>
          </div>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 8 }}>Interesse-/wachtlijst ({interest.length})</p>
            <div className="frm-grid" style={{ display: "grid", gridTemplateColumns: "1.4fr 1.6fr 1.4fr 2fr auto", gap: 8, marginBottom: 10, alignItems: "end" }}>
              <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 3 }}>Naam *</p><input value={ni.name} onChange={e => setNi(p => ({ ...p, name: e.target.value }))} /></div>
              <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 3 }}>E-mail</p><input value={ni.email} onChange={e => setNi(p => ({ ...p, email: e.target.value }))} /></div>
              <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 3 }}>Bedrijf</p><input value={ni.company} onChange={e => setNi(p => ({ ...p, company: e.target.value }))} /></div>
              <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 3 }}>Notitie</p><input value={ni.note} onChange={e => setNi(p => ({ ...p, note: e.target.value }))} /></div>
              <button className="btn bp" disabled={!ni.name.trim()} onClick={addInterest} style={{ fontSize: 11 }}>+ Toevoegen</button>
            </div>
            {interest.length === 0 && <p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 8 }}>Nog geen interesse vastgelegd.</p>}
            {interest.map(it => (
              <div key={it.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: "1px solid " + C.border, gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{it.name}</span>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{[it.company, it.email].filter(Boolean).join(" · ")}{it.note ? " - " + it.note : ""}</span>
                </div>
                <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  {it.lead_id ? <Bdg label="LEAD" bg={C.greenDim} fg={C.green} /> : <button className="btn bg" style={{ fontSize: 11 }} onClick={() => toLead(it)}>→ Maak lead</button>}
                  <button onClick={() => delInterest(it.id)} style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 11, padding: "2px 7px" }}>✕</button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CRM({ openKlant, openFacturatie } = {}) {
  const [segment, setSegment] = useState("b2b");
  const [onlyWebsite, setOnlyWebsite] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState(INIT_LEADS);
  const [selLead, setSelLead] = useState(null);
  const [newMod, setNewMod] = useState(false);
  const [nl, setNl] = useState({ name: "", company: "", interest: "", status: "", value: "", email: "", phone: "", industry: "", company_size: "", source: "" });
  const [activities, setActivities] = useState([]);
  const [actMod, setActMod] = useState(false);
  const [na, setNa] = useState({ icon: "✓", text: "", type: "Taak", time: "Vandaag of later" });

  // Laad leads uit Supabase
  useEffect(() => {
    async function load() {
      const { data, error } = await supabase.from("leads").select("*").order("created_at", { ascending: false });
      if (!error && data && data.length > 0) setLeads(data);
    }
    load();
    const ch = supabase.channel("leads_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "leads" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, []);

  // Laad activiteiten voor de geselecteerde lead
  useEffect(() => {
    if (!selLead) { setActivities([]); return; }
    async function load() {
      const { data, error } = await supabase.from("activities").select("*").eq("lead_id", selLead).order("created_at", { ascending: false });
      if (!error && data) setActivities(data);
    }
    load();
    const ch = supabase.channel("activities_sync_" + selLead)
      .on("postgres_changes", { event: "*", schema: "public", table: "activities", filter: "lead_id=eq." + selLead }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [selLead]);

  const segLeadsAll = leads.filter(l => (l.segment || "b2b") === segment);
  const isWebsiteLead = (l) => (l.source || "").toLowerCase().includes("website");
  const websiteCount = segLeadsAll.filter(isWebsiteLead).length;
  const segLeads = onlyWebsite ? segLeadsAll.filter(isWebsiteLead) : segLeadsAll;
  const activeLeads = segLeads.filter(l => l.status !== "won" && l.status !== "lost" && l.status !== "gewonnen" && l.status !== "verloren");
  const wonLeads = segLeads.filter(l => l.status === "won" || l.status === "gewonnen");
  const pipelineVal = activeLeads.reduce((s, l) => s + (l.value || 0), 0);
  const avgDeal = activeLeads.length ? Math.round(pipelineVal / activeLeads.length) : 0;
  const qualifiedCount = segLeads.filter(l => ["qualified", "proposal", "demo", "offerte"].includes(l.status)).length;

  const defaultStatus = segment === "b2b" ? "cold" : "lead";
  const statuses = segment === "b2b" ? B2B_STATUSES : B2C_STATUSES;

  async function submitLead() {
    if (!nl.name.trim()) return;
    const row = {
      name: nl.name, company: nl.company || "—", interest: nl.interest || "—",
      status: nl.status || defaultStatus, value: parseInt(nl.value) || 0, notes: "",
      segment, email: nl.email || null, phone: nl.phone || null,
      industry: nl.industry || null, company_size: nl.company_size || null, source: nl.source || null,
    };
    const { data, error } = await supabase.from("leads").insert([row]).select();
    if (!error && data) { setLeads(p => [data[0], ...p]); logActivity("lead", row.name, "crm"); }
    setNewMod(false);
    setNl({ name: "", company: "", interest: "", status: "", value: "", email: "", phone: "", industry: "", company_size: "", source: "" });
  }

  if (selLead) {
    const l = leads.find(x => x.id === selLead);
    if (!l) return null;
    const sc = SEG_STATUS_COL[l.status] || C.muted;
    const isB2B = (l.segment || "b2b") === "b2b";
    // Locatie afleiden voor de kaart. We tonen ALLEEN een kaart bij een echt geografisch
    // veld (adres of plaats uit de notities). Een kale bedrijfsnaam is geen locatie:
    // Nominatim matcht die fuzzy op een willekeurige plek met die naam (bv. "Test,
    // Nederland"), wat een misleidende pin geeft. De bedrijfsnaam mag een bestaand
    // adres/plaats wel verfijnen, maar niet zelf de zoekopdracht zijn.
    const _adres = ((l.notes || "").match(/Adres:\s*([^\n]+)/i) || [])[1];
    const _plaats = ((l.notes || "").match(/Plaats:\s*([^\n]+)/i) || [])[1];
    const locAdres = (_adres || "").replace(/\.\s*$/, "").trim();
    const locPlaats = (_plaats || "").trim();
    const locBedrijf = (l.company && l.company !== "—") ? l.company : "";
    const hasLoc = !!(locAdres || locPlaats);
    const mapQuery = locAdres ? locAdres + ", Nederland" : [locBedrijf, locPlaats, "Nederland"].filter(Boolean).join(", ");
    // Fase-progressie: schuif één stap op in de status-volgorde. De laatste twee
    // (won/lost resp. gewonnen/verloren) zijn eindstatussen — daar is geen "volgende".
    const stArr = isB2B ? B2B_STATUSES : B2C_STATUSES;
    const stIdx = stArr.indexOf(l.status);
    const nextStatus = stIdx >= 0 && stIdx < stArr.length - 2 ? stArr[stIdx + 1] : null;
    async function advancePhase() {
      if (!nextStatus) return;
      const { error } = await supabase.from("leads").update({ status: nextStatus }).eq("id", l.id);
      if (!error) setLeads(p => p.map(x => x.id === l.id ? { ...x, status: nextStatus } : x));
    }
    async function saveLeadNotes() {
      const val = document.getElementById("leadNotes")?.value ?? "";
      const { error } = await supabase.from("leads").update({ notes: val }).eq("id", l.id);
      if (!error) setLeads(p => p.map(x => x.id === l.id ? { ...x, notes: val } : x));
    }
    // Weigeren: lead blijft bewaard (voor historie/rapportage) maar gaat naar de
    // eindstatus 'verloren'. Verwijderen: definitief uit de database.
    const lostStatus = isB2B ? "lost" : "verloren";
    const isRejected = l.status === lostStatus;
    async function rejectLead() {
      if (isRejected) return;
      if (!window.confirm("Lead \"" + l.name + "\" weigeren?\n\nDe lead blijft bewaard maar wordt op '" + lostStatus + "' gezet.")) return;
      const { error } = await supabase.from("leads").update({ status: lostStatus }).eq("id", l.id);
      if (error) { alert("Weigeren mislukt: " + error.message); return; }
      setLeads(p => p.map(x => x.id === l.id ? { ...x, status: lostStatus } : x));
    }
    async function deleteLead() {
      if (!window.confirm("Lead \"" + l.name + "\" definitief verwijderen?\n\nDit verwijdert de lead en kan niet ongedaan worden gemaakt.")) return;
      const { error } = await supabase.from("leads").delete().eq("id", l.id);
      if (error) { alert("Verwijderen mislukt: " + error.message); return; }
      setLeads(p => p.filter(x => x.id !== l.id));
      setSelLead(null);
    }
    const infoRows = isB2B
      ? [["Naam", l.name], ["Bedrijf", l.company], ["E-mail", l.email || "-"], ["Telefoon", l.phone || "-"], ["Industrie", l.industry || "—"], ["Bedrijfsgrootte", l.company_size || "—"], ["Interesse", l.interest], ["Waarde", "€" + (l.value || 0).toLocaleString()], ["Status", l.status]]
      : [["Naam", l.name], ["E-mail", l.email || "—"], ["Telefoon", l.phone || "—"], ["Interesse", l.interest], ["Budget", "€" + (l.value || 0).toLocaleString()], ["Bron", l.source || "—"], ["Status", l.status]];
    return (
      <div className="fu">
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
          <button className="btn bg" onClick={() => setSelLead(null)}>← Terug</button>
          <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>{l.name}</h2>
          <Bdg label={l.segment?.toUpperCase() || "B2B"} bg={isB2B ? C.blueDim : C.purpleDim} fg={isB2B ? C.blue : C.purple} />
          <Bdg label={l.status} bg={sc + "20"} fg={sc} />
          {openKlant && <button className="btn bg" style={{ marginLeft: "auto" }} onClick={() => openKlant(l.id)}>Klantdossier</button>}
          {openFacturatie && <button className="btn bp" onClick={() => openFacturatie(l.id)}>+ Offerte</button>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 13 }}>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 11 }}>Lead Info</p>
            {infoRows.map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.border }}>
                <span style={{ fontSize: 12, color: C.muted }}>{k}</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 11 }}>Activiteiten</p>
            {activities.length === 0 && <p style={{ fontSize: 12, color: C.dim, padding: "8px 0" }}>Nog geen activiteiten.</p>}
            {activities.map(a => (
              <div key={a.id} style={{ display: "flex", gap: 9, padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{a.icon}</span>
                <div><p style={{ fontSize: 12 }}>{a.text}</p><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{a.type} · {a.time}</p></div>
              </div>
            ))}
            <button className="btn bg" style={{ width: "100%", marginTop: 10, fontSize: 12 }} onClick={() => setActMod(true)}>+ Activiteit / Afspraak</button>
          </div>
        </div>
        {hasLoc && (
          <div className="card" style={{ marginBottom: 13 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>Locatie</p>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{locAdres || [locBedrijf, locPlaats].filter(Boolean).join(" · ") || "—"}</p>
            <AddressMap query={mapQuery} height={220} />
          </div>
        )}
        <GespreksVoorbereiding leadId={l.id} leadNaam={l.name + (l.company ? " (" + l.company + ")" : "")} />
        <IntakeGesprek lead={l} />
        <div className="card">
          <p className="lbl" style={{ marginBottom: 10 }}>Notities</p>
          <textarea id="leadNotes" rows={4} defaultValue={l.notes || ""} style={{ fontSize: 13, lineHeight: 1.6, resize: "vertical" }} />
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button className="btn bp" onClick={saveLeadNotes}>Opslaan</button>
            <button className="btn bg" disabled={!nextStatus} title={nextStatus ? "Verplaats naar: " + nextStatus : "Eindfase bereikt"} onClick={advancePhase}>
              {nextStatus ? "→ Volgende fase: " + nextStatus : "→ Eindfase (" + l.status + ")"}
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={rejectLead} disabled={isRejected} title={isRejected ? "Lead is al geweigerd" : "Markeer als geweigerd (" + lostStatus + ")"} style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid " + C.border, background: "transparent", color: isRejected ? C.dim : C.muted, cursor: isRejected ? "default" : "pointer", fontSize: 12, fontWeight: 600, opacity: isRejected ? 0.5 : 1 }}>⊘ Weigeren</button>
            <button onClick={deleteLead} title="Lead definitief verwijderen" style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid " + C.red + "55", background: "transparent", color: C.red, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>✕ Verwijderen</button>
          </div>
        </div>
        {actMod && (
          <Portal><div className="ovl" onClick={() => setActMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 17, fontSize: 14 }}>+ ACTIVITEIT / AFSPRAAK</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Soort</p>
                  <select value={na.type} onChange={e => { const t = e.target.value; setNa(p => ({ ...p, type: t, icon: t === "Afspraak" ? "◷" : t === "Gesprek" ? "◎" : t === "E-mail" ? "◫" : "✓" })); }}>
                    <option>Taak</option><option>Afspraak</option><option>Gesprek</option><option>E-mail</option>
                  </select>
                </div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Beschrijving *</p><input value={na.text} onChange={e => setNa(p => ({ ...p, text: e.target.value }))} placeholder="bijv. Demo meeting plannen" /></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Wanneer</p><input value={na.time} onChange={e => setNa(p => ({ ...p, time: e.target.value }))} placeholder="Morgen 14:00" /></div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                  <button className="btn bg" onClick={() => setActMod(false)}>Annuleer</button>
                  <button className="btn bp" onClick={async () => {
                    if (!na.text.trim()) return;
                    const { data, error } = await supabase.from("activities").insert([{ lead_id: selLead, ...na }]).select();
                    if (!error && data) setActivities(p => [data[0], ...p]);
                    setActMod(false); setNa({ icon: "✓", text: "", type: "Taak", time: "Vandaag of later" });
                  }}>→ Toevoegen</button>
                </div>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  const funnelStages = segment === "b2b"
    ? ["cold", "prospect", "qualified", "proposal"]
    : ["lead", "interesse", "demo", "offerte"];

  return (
    <div className="fu">
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2 style={{ ...KOP_STIJL }}>CRM</h2>
          <div style={{ display: "flex", background: C.bg, border: "1px solid " + C.border, borderRadius: 7, padding: 2, gap: 2 }}>
            {["b2b", "b2c"].map(s => (
              <button key={s} onClick={() => { setSegment(s); setTab("leads"); }} style={{ padding: "5px 16px", borderRadius: 5, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", transition: "all .15s", background: segment === s ? (s === "b2b" ? C.blueDim : C.purpleDim) : "transparent", color: segment === s ? (s === "b2b" ? C.blue : C.purple) : C.muted }}>
                {s.toUpperCase()}
              </button>
            ))}
          </div>
          {websiteCount > 0 && (
            <button onClick={() => setOnlyWebsite(v => !v)} title="Toon alleen leads die via de website binnenkwamen" style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid " + (onlyWebsite ? C.green : C.border), background: onlyWebsite ? C.greenDim : "transparent", color: onlyWebsite ? C.green : C.muted, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Website · {websiteCount}</button>
          )}
        </div>
        <button className="btn bp" onClick={() => setNewMod(true)}>+ Nieuwe {segment === "b2b" ? "B2B" : "B2C"} Lead</button>
      </div>

      {/* KPI balk */}
      <Tellers items={(segment === "b2b" ? [
        ["Pipeline waarde", "€" + pipelineVal.toLocaleString(), C.green],
        ["Gem. dealwaarde", "€" + avgDeal.toLocaleString(), C.blue],
        ["Qualified leads", qualifiedCount + " leads", C.yellow],
      ] : [
        ["Totaal leads", segLeads.length + " leads", C.purple],
        ["Actief", activeLeads.length + " actief", C.blue],
        ["Totaal budget", "€" + pipelineVal.toLocaleString(), C.green],
      ]).map(([label, waarde, kleur]) => ({ label, waarde, kleur }))} />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
        {["leads", "funnel", "activiteiten"].map(t => (
          <span key={t} className={"tab" + (tab === t ? " on" : "")} onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
        ))}
      </div>

      {/* Leads: per status een groep, een regel per lead */}
      {tab === "leads" && (segLeads.length === 0 ? (
        <div className="card"><Leeg tekst={segment === "b2b" ? "Nog geen B2B leads." : "Nog geen B2C leads."} /></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[...funnelStages.map(st => [st, false]), ...(segment === "b2b" ? [["won", true], ["lost", true]] : [["gewonnen", true], ["verloren", true]]), ["overig", false]].map(([st, dicht]) => {
            const bekend = [...funnelStages, ...(segment === "b2b" ? ["won", "lost"] : ["gewonnen", "verloren"])];
            const items = st === "overig" ? segLeads.filter(l => !bekend.includes(l.status)) : segLeads.filter(l => l.status === st);
            if (!items.length) return null;
            const sc = SEG_STATUS_COL[st] || C.muted;
            const som = items.reduce((t, l) => t + (l.value || 0), 0);
            return (
              <Groep key={segment + st} label={st} kleur={sc} aantal={items.length + " · €" + som.toLocaleString()} standaardDicht={dicht}>
                {items.map(l => (
                  <Rij key={l.id} titel={l.name} onKlik={() => setSelLead(l.id)}
                    meta={<>
                      <span>{segment === "b2b" ? l.company : l.interest}</span>
                      {(segment === "b2b" ? l.industry : l.source) && <span>{segment === "b2b" ? l.industry : l.source}</span>}
                    </>}
                    rechts={<span style={{ fontFamily: "monospace", fontSize: 13, color: segment === "b2b" ? C.green : C.purple, whiteSpace: "nowrap", marginTop: 1 }}>€{(l.value || 0).toLocaleString()}</span>} />
                ))}
              </Groep>
            );
          })}
        </div>
      ))}

      {/* Funnel kanban */}
      {tab === "funnel" && (
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
          {funnelStages.map(stage => {
            const cards = segLeads.filter(l => l.status === stage);
            const sc = SEG_STATUS_COL[stage] || C.muted;
            const stageVal = cards.reduce((s, l) => s + (l.value || 0), 0);
            return (
              <div key={stage} style={{ minWidth: 200, flex: "0 0 200px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: sc, letterSpacing: ".06em" }}>{stage.toUpperCase()}</span>
                  <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{cards.length} · €{stageVal.toLocaleString()}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {cards.map(l => (
                    <div key={l.id} className="kc" onClick={() => setSelLead(l.id)} style={{ borderLeft: "3px solid " + sc }}>
                      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{l.name}</p>
                      <p style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{segment === "b2b" ? l.company : l.interest}</p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontFamily: "monospace", fontSize: 12, color: segment === "b2b" ? C.green : C.purple }}>€{(l.value || 0).toLocaleString()}</span>
                        <span style={{ fontSize: 10, color: C.dim }}>Details →</span>
                      </div>
                    </div>
                  ))}
                  {!cards.length && <div style={{ padding: 12, textAlign: "center", color: C.dim, fontSize: 11, border: "1px dashed " + C.border, borderRadius: 6 }}>Leeg</div>}
                </div>
              </div>
            );
          })}
          {/* Won/Verloren kolom */}
          {(() => {
            const wlStatus = segment === "b2b" ? ["won", "lost"] : ["gewonnen", "verloren"];
            const wlCards = segLeads.filter(l => wlStatus.includes(l.status));
            return (
              <div style={{ minWidth: 200, flex: "0 0 200px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, color: C.muted, letterSpacing: ".06em" }}>GESLOTEN</span>
                  <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{wonLeads.length} gewonnen</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {wlCards.map(l => {
                    const sc = SEG_STATUS_COL[l.status] || C.muted; return (
                      <div key={l.id} className="kc" onClick={() => setSelLead(l.id)} style={{ borderLeft: "3px solid " + sc, opacity: .75 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{l.name}</p>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <Bdg label={l.status} bg={sc + "20"} fg={sc} />
                          <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted }}>€{(l.value || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  })}
                  {!wlCards.length && <div style={{ padding: 12, textAlign: "center", color: C.dim, fontSize: 11, border: "1px dashed " + C.border, borderRadius: 6 }}>Leeg</div>}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Activiteiten */}
      {tab === "activiteiten" && (
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span className="lbl">Activiteiten & Afspraken</span>
            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setActMod(true)}>+ Nieuw</button>
          </div>
          {activities.length === 0 && <p style={{ fontSize: 12, color: C.dim, padding: "8px 0" }}>Selecteer een lead om activiteiten te zien.</p>}
          {activities.map(a => (
            <div key={a.id} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "1px solid " + C.border }}>
              <span style={{ fontSize: 17, flexShrink: 0 }}>{a.icon}</span>
              <div style={{ flex: 1 }}><p style={{ fontSize: 13 }}>{a.text}</p><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{a.type} · {a.time}</p></div>
            </div>
          ))}
        </div>
      )}

      {/* Nieuwe lead modal */}
      {newMod && (
        <Portal><div className="ovl" onClick={() => setNewMod(false)}>
          <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 17 }}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14 }}>+ NIEUWE LEAD</h3>
              <Bdg label={segment.toUpperCase()} bg={segment === "b2b" ? C.blueDim : C.purpleDim} fg={segment === "b2b" ? C.blue : C.purple} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Naam *</p>
                <input value={nl.name} onChange={e => setNl(p => ({ ...p, name: e.target.value }))} placeholder={segment === "b2b" ? "Naam contactpersoon" : "Volledige naam"} />
              </div>
              {segment === "b2b" ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Bedrijfsnaam</p><input value={nl.company} onChange={e => setNl(p => ({ ...p, company: e.target.value }))} placeholder="Bedrijf BV" /></div>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Industrie</p>
                      <select value={nl.industry} onChange={e => setNl(p => ({ ...p, industry: e.target.value }))}>
                        <option value="">— Kies —</option>
                        {B2B_INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Bedrijfsgrootte</p>
                      <select value={nl.company_size} onChange={e => setNl(p => ({ ...p, company_size: e.target.value }))}>
                        <option value="">— Kies —</option>
                        {B2B_SIZES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Contractwaarde (€)</p><input type="number" value={nl.value} onChange={e => setNl(p => ({ ...p, value: e.target.value }))} placeholder="5000" /></div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>E-mail</p><input type="email" value={nl.email} onChange={e => setNl(p => ({ ...p, email: e.target.value }))} placeholder="naam@email.nl" /></div>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Telefoon</p><input value={nl.phone} onChange={e => setNl(p => ({ ...p, phone: e.target.value }))} placeholder="+31 6 ..." /></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Bron</p>
                      <select value={nl.source} onChange={e => setNl(p => ({ ...p, source: e.target.value }))}>
                        <option value="">— Kies —</option>
                        {B2C_SOURCES.map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Budget (€)</p><input type="number" value={nl.value} onChange={e => setNl(p => ({ ...p, value: e.target.value }))} placeholder="500" /></div>
                  </div>
                </>
              )}
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Interesse / Dienst</p>
                <input value={nl.interest} onChange={e => setNl(p => ({ ...p, interest: e.target.value }))} placeholder={segment === "b2b" ? "bijv. AutoCompli, TaxLeverage" : "bijv. abonnement, eenmalig project"} />
              </div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Status</p>
                <select value={nl.status || defaultStatus} onChange={e => setNl(p => ({ ...p, status: e.target.value }))}>
                  {statuses.filter(s => !["won", "lost", "gewonnen", "verloren"].includes(s)).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={submitLead}>→ Toevoegen</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
    </div>
  );
}

// ─── ADMINISTRATIE & TAX MODULE ──────────────────────────────────────────────

const EXPENSE_CATS = ["Software/Hosting", "Marketing", "Kantoor", "Reiskosten", "Personeel", "Apparatuur", "Advies", "Overig"];
const DOC_TYPE_LABELS = { factuur_inkoop: "Factuur Inkoop", factuur_verkoop: "Factuur Verkoop", bon: "Bon/Receipt", bankafschrift: "Bankafschrift", contract: "Contract", investering: "Investering", km_log: "KM Log", uren_log: "Uren Log" };
const HOUR_CATS = ["Development", "Klantcontact", "Administratie", "Sales", "Research", "Overig"];
const KM_RATE = 0.23;
const ZELFST_MIN = 1225;

// ─── REEKSEN (agenda en uren) ────────────────────────────────────────────────
// Een reeks krijgt een gedeelde recur_id, zodat je hem later in een keer kunt verwijderen.
const REEKS_MAX = 120;
const REEKS_NIEUW = { patroon: "none", aantal: 4, eindSoort: "aantal", eind: "", dagen: [1, 2, 3, 4, 5], skipFeest: true };
const REEKS_DAGEN = [["Ma", 1], ["Di", 2], ["Wo", 3], ["Do", 4], ["Vr", 5], ["Za", 6], ["Zo", 0]];

function isoLokaal(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }

// Nationale feestdagen in Nederland (zonder Goede Vrijdag; Bevrijdingsdag alleen in lustrumjaren).
function nlFeestdagen(jaar) {
  const a = jaar % 19, b = Math.floor(jaar / 100), c = jaar % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const maand = Math.floor((h + l - 7 * m + 114) / 31), dag = ((h + l - 7 * m + 114) % 31) + 1;
  const pasen = new Date(jaar, maand - 1, dag);
  const plus = (n) => { const x = new Date(pasen); x.setDate(x.getDate() + n); return isoLokaal(x); };
  let koning = new Date(jaar, 3, 27); if (koning.getDay() === 0) koning = new Date(jaar, 3, 26);
  const lijst = [isoLokaal(new Date(jaar, 0, 1)), plus(0), plus(1), isoLokaal(koning), plus(39), plus(49), plus(50), isoLokaal(new Date(jaar, 11, 25)), isoLokaal(new Date(jaar, 11, 26))];
  if (jaar % 5 === 0) lijst.push(isoLokaal(new Date(jaar, 4, 5)));
  return new Set(lijst);
}

// Berekent de data van een reeks. maxIso (optioneel) is de laatste toegestane datum; latere data worden weggelaten en geteld.
function reeksDatums(startIso, r, opties = {}) {
  if (!startIso) return { datums: [], overgeslagen: 0, feestOvergeslagen: 0 };
  if (!r || r.patroon === "none") return { datums: [startIso], overgeslagen: 0, feestOvergeslagen: 0 };
  const start = new Date(startIso + "T00:00:00");
  const grens = Math.min(opties.max || REEKS_MAX, REEKS_MAX);
  const doel = r.eindSoort === "aantal" ? Math.max(1, Math.min(parseInt(r.aantal) || 1, grens)) : grens;
  const einde = r.eindSoort === "datum" && r.eind ? new Date(r.eind + "T00:00:00") : null;
  if (r.eindSoort === "datum" && !einde) return { datums: [startIso], overgeslagen: 0, feestOvergeslagen: 0 };
  const feest = {};
  const isFeest = (d) => { const j = d.getFullYear(); if (!feest[j]) feest[j] = nlFeestdagen(j); return feest[j].has(isoLokaal(d)); };
  const dagenSet = new Set(r.dagen || []);
  const uit = []; let feestOver = 0;
  for (let stap = 0; stap < 4000 && uit.length < doel; stap++) {
    let d;
    if (r.patroon === "day" || r.patroon === "dagen") { d = new Date(start); d.setDate(d.getDate() + stap); }
    else if (r.patroon === "week") { d = new Date(start); d.setDate(d.getDate() + stap * 7); }
    else if (r.patroon === "2weeks") { d = new Date(start); d.setDate(d.getDate() + stap * 14); }
    else { d = new Date(start.getFullYear(), start.getMonth() + stap, 1); const laatste = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); d.setDate(Math.min(start.getDate(), laatste)); }
    if (einde && d > einde) break;
    if (r.patroon === "dagen" && !dagenSet.has(d.getDay())) continue;
    if (r.skipFeest && isFeest(d)) { feestOver++; continue; }
    uit.push(isoLokaal(d));
  }
  const toegestaan = opties.maxIso ? uit.filter(x => x <= opties.maxIso) : uit;
  return { datums: toegestaan, overgeslagen: uit.length - toegestaan.length, feestOvergeslagen: feestOver };
}

function reeksOmschrijving(r) {
  if (!r || r.patroon === "none") return "";
  if (r.patroon === "day") return "dagelijks";
  if (r.patroon === "week") return "wekelijks";
  if (r.patroon === "2weeks") return "elke 2 weken";
  if (r.patroon === "month") return "maandelijks";
  return "op " + REEKS_DAGEN.filter(([, n]) => (r.dagen || []).includes(n)).map(([l]) => l.toLowerCase()).join(", ");
}

function ReeksKiezer({ waarde, onChange, startIso, maxIso, max }) {
  const r = waarde;
  const zet = (p) => onChange({ ...r, ...p });
  const { datums, overgeslagen, feestOvergeslagen } = reeksDatums(startIso, r, { maxIso, max });
  const veld = { fontSize: 11, color: C.dim, marginBottom: 4 };
  return (
    <div style={{ background: C.bg, borderRadius: 8, padding: "10px 12px", border: "1px solid " + C.border }}>
      <p style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Herhaling (reeks)</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <p style={veld}>Patroon</p>
          <select value={r.patroon} onChange={e => zet({ patroon: e.target.value })}>
            <option value="none">Geen herhaling</option>
            <option value="day">Elke dag</option>
            <option value="dagen">Op bepaalde weekdagen</option>
            <option value="week">Wekelijks (zelfde dag)</option>
            <option value="2weeks">Tweewekelijks</option>
            <option value="month">Maandelijks</option>
          </select>
        </div>
        {r.patroon !== "none" && (
          <div>
            <p style={veld}>Eindigt</p>
            <select value={r.eindSoort} onChange={e => zet({ eindSoort: e.target.value })}>
              <option value="aantal">Na een aantal keer</option>
              <option value="datum">Op een einddatum</option>
            </select>
          </div>
        )}
      </div>
      {r.patroon === "dagen" && (
        <div style={{ marginTop: 10 }}>
          <p style={veld}>Weekdagen</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {REEKS_DAGEN.map(([l, n]) => {
              const aan = (r.dagen || []).includes(n);
              return <button key={l} type="button" onClick={() => zet({ dagen: aan ? r.dagen.filter(x => x !== n) : [...(r.dagen || []), n] })} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid " + (aan ? C.blue : C.border), background: aan ? C.blueDim : "transparent", color: aan ? C.blue : C.muted, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{l}</button>;
            })}
            <button type="button" onClick={() => zet({ dagen: [1, 2, 3, 4, 5] })} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid " + C.border, background: "transparent", color: C.muted, cursor: "pointer", fontSize: 11 }}>Ma t/m Vr</button>
          </div>
        </div>
      )}
      {r.patroon !== "none" && (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, alignItems: "end" }}>
          {r.eindSoort === "aantal"
            ? <div><p style={veld}>Aantal keer (max {Math.min(max || REEKS_MAX, REEKS_MAX)})</p><input type="number" min={1} max={Math.min(max || REEKS_MAX, REEKS_MAX)} value={r.aantal} onChange={e => zet({ aantal: parseInt(e.target.value) || 1 })} /></div>
            : <div><p style={veld}>Laatste datum</p><input type="date" min={startIso} max={maxIso || undefined} value={r.eind} onChange={e => zet({ eind: e.target.value })} /></div>}
          <label style={{ fontSize: 12, color: C.muted, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={!!r.skipFeest} onChange={e => zet({ skipFeest: e.target.checked })} style={{ width: "auto" }} /> Feestdagen overslaan
          </label>
        </div>
      )}
      {r.patroon !== "none" && (
        <p style={{ fontSize: 11, color: datums.length ? C.purple : C.red, marginTop: 8 }}>
          {datums.length
            ? "→ " + datums.length + (datums.length === 1 ? " keer" : " keer") + " (" + reeksOmschrijving(r) + "), van " + datums[0] + " tot " + datums[datums.length - 1]
            : "→ Geen enkele datum valt binnen deze instelling."}
          {feestOvergeslagen > 0 ? " · " + feestOvergeslagen + " feestdag(en) overgeslagen" : ""}
          {overgeslagen > 0 ? " · " + overgeslagen + " datum(s) in de toekomst niet meegenomen" : ""}
        </p>
      )}
    </div>
  );
}

function qToDateRange(q, year) {
  const n = parseInt(q[1]);
  const start = new Date(year, (n - 1) * 3, 1);
  const end = new Date(year, n * 3, 0);
  return [start.toISOString().split("T")[0], end.toISOString().split("T")[0]];
}
function docQuarter(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return "Q" + (Math.ceil((d.getMonth() + 1) / 3)) + "-" + d.getFullYear();
}

// Vult ontbrekende bedragvelden aan vanuit de wél-aanwezige velden.
// Veel bonnen/facturen vermelden alleen het totaal (incl. BTW) + het BTW-bedrag,
// niet het bedrag excl. BTW. Omdat ALLE boekhoud-logica (transactie aanmaken,
// omzet/kosten, BTW-aangifte) op amount_excl draait, blijft alles leeg én wordt
// het document gemarkeerd zodra amount_excl ontbreekt. Hier leiden we 'm af.
function deriveAmounts(d) {
  if (!d) return d;
  const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
  const r2 = n => n == null ? null : Math.round(n * 100) / 100;
  let excl = num(d.amount_excl);
  let incl = num(d.amount_incl);
  let vat = num(d.vat_amount);
  let rate = num(d.vat_rate); if (rate == null) rate = 21;

  // 1) bedrag excl. BTW reconstrueren uit incl/vat/rate
  if (excl == null) {
    if (incl != null && vat != null) excl = incl - vat;        // totaal − BTW
    else if (incl != null) excl = incl / (1 + rate / 100); // terugrekenen via tarief
    else if (vat != null && rate > 0) excl = vat / (rate / 100);    // alleen BTW-bedrag bekend
  }
  // 2) BTW-bedrag afleiden
  if (vat == null && excl != null) vat = excl * rate / 100;
  // 3) totaal incl. BTW afleiden
  if (incl == null && excl != null) incl = excl + (vat ?? 0);

  return { ...d, amount_excl: r2(excl), vat_amount: r2(vat), amount_incl: r2(incl), vat_rate: rate };
}

function Fld({ label, children }) {
  return <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{label}</p>{children}</div>;
}

function EvRouteMap({ startLat, startLng, endLat, endLng, routeGeometry, height = 220 }) {
  const ref = useRef(null);
  const instance = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (instance.current) { instance.current.remove(); instance.current = null; }
    const map = L.map(ref.current, { zoomControl: true, attributionControl: false });
    instance.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    // Route polyline
    if (routeGeometry?.coordinates?.length) {
      const lls = routeGeometry.coordinates.map(([lng, lat]) => [lat, lng]);
      const poly = L.polyline(lls, { color: "#4285f4", weight: 5, opacity: .9 }).addTo(map);
      map.fitBounds(poly.getBounds(), { padding: [28, 28] });
    } else {
      map.setView([(startLat + endLat) / 2, (startLng + endLng) / 2], 9);
    }
    // Start marker (groen)
    const mkStart = L.divIcon({ html: `<div style="width:14px;height:14px;border-radius:50%;background:#34bd82;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`, className: "", iconSize: [14, 14], iconAnchor: [7, 7] });
    const mkEnd = L.divIcon({ html: `<div style="width:14px;height:14px;border-radius:50%;background:#e5564e;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)"></div>`, className: "", iconSize: [14, 14], iconAnchor: [7, 7] });
    L.marker([startLat, startLng], { icon: mkStart }).addTo(map);
    L.marker([endLat, endLng], { icon: mkEnd }).addTo(map);
    return () => { if (instance.current) { instance.current.remove(); instance.current = null; } };
  }, [startLat, startLng, endLat, endLng, routeGeometry]);
  return <div ref={ref} style={{ width: "100%", height, borderRadius: 6, overflow: "hidden", border: "1px solid " + C.border }} />;
}

// Herbruikbare open-source mapviewer: geocodeert een tekstueel adres via
// Nominatim (OSM) en toont een pin op een Leaflet-kaart — als alternatief voor
// een Google-Maps-embed. Werkt overal waar een adres/locatie als tekst staat.
function AddressMap({ query, height = 200 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ok | none | error
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const q = (query || "").trim();
    if (!q) { setStatus("none"); return; }
    setStatus("loading"); setCoords(null);
    // Debounce: geocode pas ~700ms nadat het typen stopt — zo kan de kaart live
    // meebewegen terwijl je een adres intypt, zonder Nominatim te bestoken.
    const timer = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q)}&format=json&limit=1&countrycodes=nl,be&addressdetails=1`;
        const r = await fetch(url, { headers: { Accept: "application/json" } });
        const d = await r.json();
        if (cancelled) return;
        if (Array.isArray(d) && d[0]) { setCoords({ lat: +d[0].lat, lng: +d[0].lon, label: d[0].display_name }); setStatus("ok"); }
        else setStatus("none");
      } catch (e) { if (!cancelled) setStatus("error"); }
    }, 700);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query]);

  useEffect(() => {
    if (status !== "ok" || !coords || !ref.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = L.map(ref.current, { zoomControl: true, attributionControl: false, scrollWheelZoom: false }).setView([coords.lat, coords.lng], 15);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    const pin = L.divIcon({ html: `<div style="width:16px;height:16px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#e5564e;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.5)"></div>`, className: "", iconSize: [16, 16], iconAnchor: [8, 16] });
    L.marker([coords.lat, coords.lng], { icon: pin }).addTo(map).bindPopup(coords.label);
    mapRef.current = map;
    setTimeout(() => { try { map.invalidateSize(); } catch (e) {} }, 120);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [status, coords]);

  if (status === "none") return null;
  return (
    <div>
      {status === "loading" && <p style={{ fontSize: 11, color: C.muted }}>Kaart laden…</p>}
      {status === "error" && <p style={{ fontSize: 11, color: C.yellow }}>Locatie kon niet op de kaart worden gevonden.</p>}
      {status === "ok" && coords && (
        <>
          <div ref={ref} style={{ width: "100%", height, borderRadius: 6, overflow: "hidden", border: "1px solid " + C.border }} />
          <a href={`https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lng}#map=16/${coords.lat}/${coords.lng}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.blue }}>↗ Open in OpenStreetMap</a>
        </>
      )}
    </div>
  );
}

// ─── Gedeelde bestand-detectie + viewer (Administratie + Documenten) ──────────
const IMG_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "heic", "heif", "avif", "jfif", "tif", "tiff", "ico"];
const IMG_NO_INLINE = ["heic", "heif", "tif", "tiff"]; // browsers kunnen deze niet inline renderen
function fileKind(file_type, file_name, mime_type) {
  const ext = (file_name || "").toLowerCase().split(".").pop();
  if (file_type === "image" || mime_type?.startsWith("image/") || IMG_EXTS.includes(ext)) return "image";
  if (file_type === "pdf" || mime_type === "application/pdf" || ext === "pdf") return "pdf";
  if (file_type === "docx" || ["docx", "doc"].includes(ext)) return "docx";
  return "text";
}
// Bestanden uit de bucket "documents" krijgen bij het tonen een tijdelijke ondertekende link (zie os/opslag.js).
function DocFileViewer(props) {
  const url = useOndertekendeUrl(props.file_url);
  if (props.file_url && !url) return <div style={{ padding: 14, textAlign: "center", color: C.muted, fontSize: 12 }}>Bestand laden...</div>;
  return <DocFileViewerInhoud {...props} file_url={url} />;
}
// Upload origineel naar de 'documents' storage-bucket en geef publieke URL terug
async function uploadToDocuments(file, prefix) {
  const ext = (file.name || "bin").split(".").pop() || "bin";
  const path = `${prefix || "docs"}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from("documents").upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type || undefined });
  if (error) throw error;
  const { data } = supabase.storage.from("documents").getPublicUrl(path);
  return { url: data?.publicUrl || "", path };
}
// Toont het originele bestand (image/pdf) — of een download-link wanneer inline niet kan
function DocFileViewerInhoud({ file_url, file_type, file_name, mime_type, maxH }) {
  if (!file_url) return <div style={{ padding: 14, textAlign: "center", color: C.muted, fontSize: 12, border: "1px dashed " + C.border, borderRadius: 6 }}>Geen origineel bestand opgeslagen bij dit document (oude upload).</div>;
  const kind = fileKind(file_type, file_name, mime_type);
  const ext = (file_name || "").toLowerCase().split(".").pop();
  const mh = maxH || "60vh";
  if (kind === "pdf") return (
    <div>
      <iframe src={file_url + "#toolbar=1&navpanes=1"} title={file_name || "document"} style={{ width: "100%", height: mh, minHeight: 380, border: "1px solid " + C.border, borderRadius: 6, background: "#fff" }} />
      <p style={{ fontSize: 11, color: C.dim, marginTop: 6, fontFamily: "monospace" }}>Native PDF · <a href={file_url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>open in nieuw tabblad ↗</a></p>
    </div>
  );
  if (kind === "image") {
    if (IMG_NO_INLINE.includes(ext) || /heic|heif|tiff?/.test(mime_type || "")) return (
      <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 13, border: "1px solid " + C.border, borderRadius: 6, background: C.bg }}>
        <p style={{ fontSize: 26, marginBottom: 6, color: C.dim }}>◇</p>
        <p>Dit formaat (<strong>.{ext}</strong>) kan de browser niet inline tonen.</p>
        <p style={{ marginTop: 6 }}><a href={file_url} target="_blank" rel="noopener noreferrer" style={{ color: C.green, fontWeight: 600 }}>Open / download de originele afbeelding ↗</a></p>
      </div>
    );
    return (
      <div>
        <div style={{ padding: 10, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, textAlign: "center" }}>
          <img src={file_url} alt={file_name || "document"} style={{ maxWidth: "100%", maxHeight: mh, borderRadius: 4, boxShadow: "0 2px 12px rgba(0,0,0,.4)" }} />
        </div>
        <p style={{ fontSize: 11, color: C.dim, marginTop: 6, fontFamily: "monospace" }}>Origineel · <a href={file_url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>open vol formaat ↗</a></p>
      </div>
    );
  }
  return <p style={{ fontSize: 12, color: C.muted }}><a href={file_url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>download origineel ↗</a></p>;
}

// ─── AUTONOME BOEKHOUDER ──────────────────────────────────────────────────────
// Dubbel boekhouden op het RGS-light schema (ledger_accounts) via journaalposten
// (journal_entries). De autoboeker stelt per transactie een journaalpost voor met
// debet = credit; de gebruiker keurt goed of past de rekening aan. Goedgekeurde
// posten voeden grootboek, balans en winst-en-verliesrekening.
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const eur = (n) => "€ " + round2(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TYPE_LABEL = { activa: "Activa", passiva: "Passiva (schulden)", eigen_vermogen: "Eigen vermogen", omzet: "Omzet", kosten: "Kosten" };
const bkRow = { display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid " + C.border, fontSize: 12 };
const bkMono = { fontFamily: "monospace" };

// Categorie -> kostenrekening (RGS-light). Bewust simpel en uitlegbaar.
function expenseAccountCode(category) {
  const c = (category || "").toLowerCase();
  if (/software|tool|hosting|saas|server|cloud|\bapi\b|abonnement|licentie|domein/.test(c)) return "4000";
  if (/auto|vervoer|elektr|\bev\b|brandstof|reis|\bkm\b|parkeren/.test(c)) return "4100";
  if (/kantoor|office|huur|inventaris/.test(c)) return "4200";
  if (/marketing|reclame|\bads\b|advertentie|campagne|promotie/.test(c)) return "4300";
  if (/bankkosten|transactiekosten/.test(c)) return "4600";
  if (/afschrijv/.test(c)) return "4500";
  if (/algemeen/.test(c)) return "4400";
  return "4900";
}

function Boekhouder({ selYear, trans, meKey, showToast }) {
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [sub, setSub] = useState("teboeken");
  const [busyId, setBusyId] = useState(null);
  const [edit, setEdit] = useState(null); // { id, lines:[{code,debit,credit}] }
  const emptyLine = () => ({ code: "", debit: "", credit: "" });
  const [newEntry, setNewEntry] = useState(false);
  const [ne, setNe] = useState({ entry_date: new Date().toISOString().slice(0, 10), description: "", lines: [emptyLine(), emptyLine()] });
  const [csvRaw, setCsvRaw] = useState("");
  const [csvRows, setCsvRows] = useState([]);
  const [importBusy, setImportBusy] = useState(false);
  const [aiBusyId, setAiBusyId] = useState(null);
  const [aiNote, setAiNote] = useState({}); // transactie-id -> AI-uitleg

  useEffect(() => {
    let alive = true;
    (async () => {
      const [{ data: acc }, { data: ent }] = await Promise.all([
        supabase.from("ledger_accounts").select("*").eq("active", true).order("sort"),
        supabase.from("journal_entries").select("*").eq("year", selYear).order("entry_date", { ascending: false }),
      ]);
      if (!alive) return;
      setAccounts(acc || []);
      setEntries(ent || []);
    })();
    return () => { alive = false; };
  }, [selYear]);

  const acByCode = {};
  accounts.forEach(a => { acByCode[a.code] = a; });
  const nameOf = (code) => acByCode[code]?.name || code;

  const bookedIds = new Set(entries.filter(e => e.source_type === "admin_transaction").map(e => e.source_id));
  const yearTrans = (trans || []).filter(t => (t.year || new Date(t.trans_date).getFullYear()) === selYear);
  const teBoeken = yearTrans.filter(t => !bookedIds.has(t.id));

  function proposalFor(t) {
    const excl = round2(t.amount_excl), vat = round2(t.vat_amount), incl = round2(t.amount_incl || (excl + vat));
    let lines = [];
    if (t.type === "income") {
      lines = [{ code: "1300", debit: incl, credit: 0 }, { code: Number(t.vat_rate) === 0 ? "8010" : "8000", debit: 0, credit: excl }];
      if (vat > 0) lines.push({ code: "1510", debit: 0, credit: vat });
    } else {
      lines = [{ code: expenseAccountCode(t.category), debit: excl, credit: 0 }];
      if (vat > 0) lines.push({ code: "1500", debit: vat, credit: 0 });
      lines.push({ code: "1100", debit: 0, credit: incl });
    }
    return lines;
  }

  async function postEntry(t, lines) {
    setBusyId(t.id);
    const norm = lines.map(l => ({ account_code: l.code, account_name: nameOf(l.code), debit: round2(l.debit), credit: round2(l.credit) }));
    const totalD = round2(norm.reduce((s, l) => s + l.debit, 0));
    const totalC = round2(norm.reduce((s, l) => s + l.credit, 0));
    if (Math.abs(totalD - totalC) > 0.005) { showToast("✗ Niet in balans: debet " + eur(totalD) + " is niet gelijk aan credit " + eur(totalC)); setBusyId(null); return; }
    const row = {
      entry_date: t.trans_date,
      description: (t.type === "income" ? "Omzet: " : "Kosten: ") + (t.description || t.category || ""),
      source_type: "admin_transaction", source_id: t.id, status: "approved",
      lines: norm, total: totalD, confidence: 1, ai_rationale: "Voorgesteld via RGS-light mapping, goedgekeurd door " + meKey,
      year: selYear, quarter: t.quarter || null, created_by: meKey, approved_by: meKey, approved_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from("journal_entries").insert([row]).select();
    if (error) { showToast("✗ " + error.message); setBusyId(null); return; }
    setEntries(p => [data[0], ...p]); setEdit(null); setBusyId(null); showToast("✓ Geboekt");
  }

  async function skip(t) {
    setBusyId(t.id);
    const row = { entry_date: t.trans_date, description: "Overgeslagen: " + (t.description || t.category || ""), source_type: "admin_transaction", source_id: t.id, status: "rejected", lines: [], total: 0, year: selYear, quarter: t.quarter || null, created_by: meKey };
    const { data, error } = await supabase.from("journal_entries").insert([row]).select();
    if (error) { showToast("✗ " + error.message); setBusyId(null); return; }
    setEntries(p => [data[0], ...p]); setBusyId(null); showToast("✓ Overgeslagen");
  }

  // AI-verfijning: laat Claude de beste journaalpost bepalen voor een twijfelgeval.
  // Het resultaat wordt als bewerkbaar voorstel gezet (de gebruiker keurt nog steeds
  // zelf goed) plus een korte uitleg. Codes worden gevalideerd en moeten in balans zijn.
  async function aiRefine(t) {
    setAiBusyId(t.id);
    try {
      const schema = accounts.map(a => a.code + " " + a.name + " (" + a.type + ")").join("\n");
      const sys = "Je bent een nauwkeurige Nederlandse boekhouder die dubbel boekhoudt volgens RGS. Debet moet altijd gelijk zijn aan credit. Gebruik UITSLUITEND rekeningcodes uit het gegeven schema. Antwoord UITSLUITEND met JSON.";
      const usr = "Grootboekschema (RGS-light):\n" + schema +
        "\n\nTransactie:\n- soort: " + (t.type === "income" ? "omzet/verkoop" : "kosten/inkoop") +
        "\n- categorie: " + (t.category || "-") +
        "\n- omschrijving: " + (t.description || "-") +
        "\n- bedrag excl. BTW: " + round2(t.amount_excl) +
        "\n- BTW-bedrag: " + round2(t.vat_amount) + " (tarief " + (t.vat_rate || 0) + "%)" +
        "\n- bedrag incl. BTW: " + round2(t.amount_incl) +
        '\n\nBepaal de juiste journaalpost. JSON: {"lines":[{"code":"4000","debit":100,"credit":0}],"rationale":"korte uitleg in 1 zin"}';
      const { parsed } = await callGroq(sys, usr, { temperature: 0.1, maxTokens: 600 });
      const ok = parsed && Array.isArray(parsed.lines) && parsed.lines.length >= 2 && parsed.lines.every(l => acByCode[l.code]);
      if (!ok) { showToast("✗ AI gaf geen geldige journaalpost"); setAiBusyId(null); return; }
      const lines = parsed.lines.map(l => ({ code: l.code, debit: round2(l.debit), credit: round2(l.credit) }));
      const d = round2(lines.reduce((s, l) => s + l.debit, 0)), c = round2(lines.reduce((s, l) => s + l.credit, 0));
      if (Math.abs(d - c) > 0.005) { showToast("✗ AI-voorstel niet in balans (" + eur(d) + " vs " + eur(c) + ")"); setAiBusyId(null); return; }
      setEdit({ id: t.id, lines });
      setAiNote(p => ({ ...p, [t.id]: parsed.rationale || "" }));
      showToast("✓ AI-verfijning toegepast");
    } catch (e) {
      showToast("✗ AI-fout: " + (e.message || "onbekend"));
    }
    setAiBusyId(null);
  }

  // Handmatige journaalpost (ook voor de beginbalans of correcties).
  async function saveManual() {
    const norm = ne.lines
      .filter(l => l.code && (Number(l.debit) || Number(l.credit)))
      .map(l => ({ account_code: l.code, account_name: nameOf(l.code), debit: round2(l.debit), credit: round2(l.credit) }));
    if (norm.length < 2) { showToast("✗ Minimaal 2 regels met een rekening en bedrag"); return; }
    const totalD = round2(norm.reduce((s, l) => s + l.debit, 0));
    const totalC = round2(norm.reduce((s, l) => s + l.credit, 0));
    if (Math.abs(totalD - totalC) > 0.005) { showToast("✗ Niet in balans: debet " + eur(totalD) + " is niet gelijk aan credit " + eur(totalC)); return; }
    const yr = new Date(ne.entry_date).getFullYear();
    const q = "Q" + Math.ceil((new Date(ne.entry_date).getMonth() + 1) / 3) + "-" + yr;
    const row = {
      entry_date: ne.entry_date, description: ne.description || "Handmatige boeking",
      source_type: "manual", source_id: null, status: "approved",
      lines: norm, total: totalD, confidence: 1, ai_rationale: "Handmatige boeking door " + meKey,
      year: yr, quarter: q, created_by: meKey, approved_by: meKey, approved_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from("journal_entries").insert([row]).select();
    if (error) { showToast("✗ " + error.message); return; }
    if (yr === selYear) setEntries(p => [data[0], ...p]);
    setNewEntry(false);
    setNe({ entry_date: new Date().toISOString().slice(0, 10), description: "", lines: [emptyLine(), emptyLine()] });
    showToast("✓ Boeking opgeslagen");
  }

  async function deleteEntry(id) {
    if (!window.confirm("Deze journaalpost definitief verwijderen?")) return;
    const { error } = await supabase.from("journal_entries").delete().eq("id", id);
    if (error) { showToast("✗ " + error.message); return; }
    setEntries(p => p.filter(e => e.id !== id));
    showToast("✓ Verwijderd");
  }

  // Beginbalans: een handmatige openingsboeking per 1 januari, voorgevuld met Bank
  // (debet) tegen Kapitaal (credit) zodat de gebruiker alleen de bedragen invult.
  function beginbalansPreset() {
    setNe({
      entry_date: selYear + "-01-01",
      description: "Beginbalans " + selYear,
      lines: [{ code: "1100", debit: "", credit: "" }, { code: "0500", debit: "", credit: "" }],
    });
    setNewEntry(true);
  }

  // Betaling afletteren: boekt een ontvangst (Bank <- Debiteuren) of een betaling
  // (Crediteuren <- Bank) voor het openstaande bedrag, gekoppeld via source_id zodat
  // de oorspronkelijke post als 'betaald' geldt.
  async function settle(entry, kind) {
    setBusyId(entry.id);
    const amt = kind === "debiteur"
      ? round2((entry.lines || []).filter(l => l.account_code === "1300").reduce((s, l) => s + (Number(l.debit) || 0), 0))
      : round2((entry.lines || []).filter(l => l.account_code === "1600").reduce((s, l) => s + (Number(l.credit) || 0), 0));
    if (amt <= 0) { setBusyId(null); return; }
    const today = new Date().toISOString().slice(0, 10);
    const yr = new Date(today).getFullYear();
    const q = "Q" + Math.ceil((new Date(today).getMonth() + 1) / 3) + "-" + yr;
    const lines = kind === "debiteur"
      ? [{ account_code: "1100", account_name: nameOf("1100"), debit: amt, credit: 0 }, { account_code: "1300", account_name: nameOf("1300"), debit: 0, credit: amt }]
      : [{ account_code: "1600", account_name: nameOf("1600"), debit: amt, credit: 0 }, { account_code: "1100", account_name: nameOf("1100"), debit: 0, credit: amt }];
    const row = {
      entry_date: today, description: (kind === "debiteur" ? "Betaling ontvangen: " : "Betaling gedaan: ") + (entry.description || ""),
      source_type: "payment", source_id: entry.id, status: "approved", lines, total: amt, confidence: 1,
      ai_rationale: "Afletteren van " + (entry.description || entry.id), year: yr, quarter: q,
      created_by: meKey, approved_by: meKey, approved_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from("journal_entries").insert([row]).select();
    if (error) { showToast("✗ " + error.message); setBusyId(null); return; }
    if (yr === selYear) setEntries(p => [data[0], ...p]);
    setBusyId(null); showToast("✓ Afgeletterd");
  }

  // Bankafschrift-CSV ontleden. Ondersteunt komma/puntkomma-scheiding, Nederlandse
  // bedragnotatie (1.234,56) en gangbare datumvormen. Per regel een geschatte
  // tegenrekening; de gebruiker controleert vóór import.
  function parseCsv(text) {
    const rows0 = (text || "").trim().split(/\r?\n/).filter(Boolean);
    if (!rows0.length) return [];
    const delim = (rows0[0].match(/;/g) || []).length >= (rows0[0].match(/,/g) || []).length ? ";" : ",";
    const split = (line) => line.split(delim).map(c => c.replace(/^"|"$/g, "").trim());
    const header = split(rows0[0]).map(h => h.toLowerCase());
    const hasHeader = header.some(h => /datum|date|bedrag|amount|omschrijving|description|naam|mededeling|tegenpartij/.test(h));
    const idx = (names) => header.findIndex(h => names.some(n => h.includes(n)));
    const di = hasHeader ? idx(["datum", "date"]) : 0;
    const ai = hasHeader ? idx(["bedrag", "amount"]) : 1;
    const oi = hasHeader ? idx(["omschrijving", "description", "mededeling", "tegenpartij", "naam"]) : 2;
    const out = [];
    (hasHeader ? rows0.slice(1) : rows0).forEach(line => {
      const c = split(line);
      let d = c[di >= 0 ? di : 0] || "";
      const amtRaw = (c[ai >= 0 ? ai : 1] || "").replace(/\s/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
      const amount = Number(amtRaw);
      const desc = c[oi >= 0 ? oi : 2] || "";
      if (!d || Number.isNaN(amount) || amount === 0) return;
      if (/^\d{8}$/.test(d)) d = d.slice(0, 4) + "-" + d.slice(4, 6) + "-" + d.slice(6, 8);
      else if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(d)) { const m = d.split(/[-/]/); d = m[2] + "-" + m[1] + "-" + m[0]; }
      out.push({ date: d, amount: round2(amount), desc, code: amount < 0 ? expenseAccountCode(desc) : "1300" });
    });
    return out;
  }

  async function importRows() {
    setImportBusy(true);
    const payload = csvRows.map(r => {
      const amt = Math.abs(r.amount);
      const yr = new Date(r.date).getFullYear();
      const q = "Q" + Math.ceil((new Date(r.date).getMonth() + 1) / 3) + "-" + yr;
      const lines = r.amount < 0
        ? [{ account_code: r.code, account_name: nameOf(r.code), debit: amt, credit: 0 }, { account_code: "1100", account_name: nameOf("1100"), debit: 0, credit: amt }]
        : [{ account_code: "1100", account_name: nameOf("1100"), debit: amt, credit: 0 }, { account_code: r.code, account_name: nameOf(r.code), debit: 0, credit: amt }];
      return { entry_date: r.date, description: "Bank: " + (r.desc || "").slice(0, 140), source_type: "bank_import", source_id: null, status: "approved", lines, total: amt, confidence: 0.7, ai_rationale: "Bankafschrift-import", year: yr, quarter: q, created_by: meKey, approved_by: meKey, approved_at: new Date().toISOString() };
    });
    if (!payload.length) { setImportBusy(false); return; }
    const { data, error } = await supabase.from("journal_entries").insert(payload).select();
    if (error) { showToast("✗ " + error.message); setImportBusy(false); return; }
    const sameYear = (data || []).filter(e => e.year === selYear);
    setEntries(p => [...sameYear, ...p]);
    setCsvRows([]); setCsvRaw(""); setImportBusy(false);
    showToast("✓ " + (data?.length || 0) + " boekingen geimporteerd");
  }

  // Grootboek-saldi uit goedgekeurde journaalposten
  const approved = entries.filter(e => e.status === "approved");
  const saldi = {};
  approved.forEach(e => (e.lines || []).forEach(l => {
    const s = saldi[l.account_code] || (saldi[l.account_code] = { debit: 0, credit: 0 });
    s.debit += Number(l.debit) || 0; s.credit += Number(l.credit) || 0;
  }));
  const netDebit = (code) => round2((saldi[code]?.debit || 0) - (saldi[code]?.credit || 0));
  const netCredit = (code) => round2((saldi[code]?.credit || 0) - (saldi[code]?.debit || 0));
  const omzetTot = accounts.filter(a => a.type === "omzet").reduce((s, a) => s + netCredit(a.code), 0);
  const kostenTot = accounts.filter(a => a.type === "kosten").reduce((s, a) => s + netDebit(a.code), 0);
  const resultaat = round2(omzetTot - kostenTot);
  const activa = accounts.filter(a => a.type === "activa");
  const passiva = accounts.filter(a => a.type === "passiva");
  const evAcc = accounts.filter(a => a.type === "eigen_vermogen");
  const activaTot = round2(activa.reduce((s, a) => s + netDebit(a.code), 0));
  const passivaTot = round2(passiva.reduce((s, a) => s + netCredit(a.code), 0));
  const evTot = round2(evAcc.reduce((s, a) => s + netCredit(a.code), 0));
  const balansPassivaTot = round2(passivaTot + evTot + resultaat);

  // Openstaande posten: facturen die op Debiteuren (1300) of Crediteuren (1600)
  // staan en nog geen gekoppelde betaling (source_type 'payment') hebben.
  const paidIds = new Set(approved.filter(e => e.source_type === "payment").map(e => e.source_id));
  const lineSum = (e, code, side) => (e.lines || []).filter(l => l.account_code === code).reduce((s, l) => s + (Number(l[side]) || 0), 0);
  const openDebiteuren = approved.filter(e => e.source_type !== "payment" && lineSum(e, "1300", "debit") > 0 && !paidIds.has(e.id));
  const openCrediteuren = approved.filter(e => e.source_type !== "payment" && lineSum(e, "1600", "credit") > 0 && !paidIds.has(e.id));

  // BTW per kwartaal uit het grootboek (1510 verschuldigd, 1500 voorbelasting).
  const btwByQ = {};
  approved.forEach(e => {
    const q = e.quarter || ("Q?-" + selYear);
    const b = btwByQ[q] || (btwByQ[q] = { verschuldigd: 0, voorbelasting: 0 });
    (e.lines || []).forEach(l => {
      if (l.account_code === "1510") b.verschuldigd += (Number(l.credit) || 0) - (Number(l.debit) || 0);
      if (l.account_code === "1500") b.voorbelasting += (Number(l.debit) || 0) - (Number(l.credit) || 0);
    });
  });
  const btwQuarters = Object.keys(btwByQ).sort();

  const SUBS = [["teboeken", "Te boeken" + (teBoeken.length ? " · " + teBoeken.length : "")], ["journaal", "Journaal"], ["openstaand", "Openstaand" + (openDebiteuren.length + openCrediteuren.length ? " · " + (openDebiteuren.length + openCrediteuren.length) : "")], ["btw", "BTW"], ["grootboek", "Grootboek"], ["balans", "Balans"], ["wv", "Winst & Verlies"], ["import", "Importeren"]];

  return (
    <div>
      <div style={{ display: "flex", gap: 7, marginBottom: 14 }}>
        {SUBS.map(([k, label]) => (
          <span key={k} className={"tab" + (sub === k ? " on" : "")} onClick={() => setSub(k)}>{label}</span>
        ))}
      </div>

      {sub === "teboeken" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <p style={{ fontSize: 11, color: C.muted }}>De boekhouder stelt per transactie een journaalpost voor (debet = credit). Controleer de rekening en keur goed; bij twijfel kun je de rekening aanpassen of de post overslaan.</p>
          {teBoeken.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Alles is geboekt. Geen openstaande transacties in {selYear}.</p></div>}
          {teBoeken.map(t => {
            const lines = (edit && edit.id === t.id) ? edit.lines : proposalFor(t);
            const totD = round2(lines.reduce((s, l) => s + Number(l.debit || 0), 0));
            const totC = round2(lines.reduce((s, l) => s + Number(l.credit || 0), 0));
            const balanced = Math.abs(totD - totC) < 0.005;
            const editing = !!(edit && edit.id === t.id);
            return (
              <div key={t.id} className="card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{t.description || t.category || "(geen omschrijving)"}</span>
                    <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{t.trans_date} · {t.type === "income" ? "Omzet" : "Kosten"} · {eur(t.amount_incl)}</span>
                  </div>
                  <Bdg label={t.type === "income" ? "VERKOOP" : "INKOOP"} bg={t.type === "income" ? C.greenDim : C.blueDim} fg={t.type === "income" ? C.green : C.blue} />
                </div>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginBottom: 8 }}>
                  <thead><tr style={{ color: C.muted, textAlign: "left" }}>
                    <th style={{ padding: "4px 0", fontWeight: 500 }}>Rekening</th>
                    <th style={{ padding: "4px 0", fontWeight: 500, textAlign: "right", width: 110 }}>Debet</th>
                    <th style={{ padding: "4px 0", fontWeight: 500, textAlign: "right", width: 110 }}>Credit</th>
                  </tr></thead>
                  <tbody>
                    {lines.map((l, i) => (
                      <tr key={i} style={{ borderTop: "1px solid " + C.border }}>
                        <td style={{ padding: "5px 0" }}>
                          <select value={l.code} disabled={!editing} onChange={e => {
                            const nl = lines.map((x, j) => j === i ? { ...x, code: e.target.value } : x);
                            setEdit({ id: t.id, lines: nl });
                          }} style={{ fontSize: 12, padding: "3px 6px", maxWidth: 290 }}>
                            {accounts.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "5px 0", textAlign: "right", color: l.debit > 0 ? C.text : C.dim }}>{l.debit > 0 ? eur(l.debit) : "-"}</td>
                        <td style={{ padding: "5px 0", textAlign: "right", color: l.credit > 0 ? C.text : C.dim }}>{l.credit > 0 ? eur(l.credit) : "-"}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: "2px solid " + C.border, fontWeight: 600 }}>
                      <td style={{ padding: "5px 0", color: balanced ? C.green : C.red }}>{balanced ? "In balans" : "Niet in balans"}</td>
                      <td style={{ padding: "5px 0", textAlign: "right" }}>{eur(totD)}</td>
                      <td style={{ padding: "5px 0", textAlign: "right" }}>{eur(totC)}</td>
                    </tr>
                  </tbody>
                </table>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn bp" disabled={busyId === t.id || !balanced} onClick={() => postEntry(t, lines)}>✓ Goedkeuren &amp; boeken</button>
                  {!editing
                    ? <button className="btn bg" onClick={() => setEdit({ id: t.id, lines: proposalFor(t) })}>Aanpassen</button>
                    : <button className="btn bg" onClick={() => setEdit(null)}>Herstel voorstel</button>}
                  <button className="btn bg" disabled={aiBusyId === t.id} onClick={() => aiRefine(t)} title="Laat Claude de juiste rekeningen bepalen">{aiBusyId === t.id ? "AI denkt..." : "✦ AI-verfijn"}</button>
                  <button className="btn bg" disabled={busyId === t.id} onClick={() => skip(t)} style={{ marginLeft: "auto" }}>Overslaan</button>
                </div>
                {aiNote[t.id] && <p style={{ fontSize: 11, color: C.muted, marginTop: 8, fontStyle: "italic" }}>✦ {aiNote[t.id]}</p>}
              </div>
            );
          })}
        </div>
      )}

      {sub === "journaal" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn bp" onClick={() => { setNe({ entry_date: new Date().toISOString().slice(0, 10), description: "", lines: [emptyLine(), emptyLine()] }); setNewEntry(true); }}>+ Nieuwe boeking</button>
            <button className="btn bg" onClick={beginbalansPreset}>Beginbalans {selYear}</button>
          </div>
          {approved.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen geboekte journaalposten in {selYear}.</p></div>}
          {approved.map(e => (
            <div key={e.id} className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{e.description}</span>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{e.entry_date} · {eur(e.total)}</span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Bdg label={e.source_type === "manual" ? "HANDMATIG" : e.source_type === "admin_transaction" ? "AUTO" : (e.source_type || "?").toUpperCase()} bg={C.bg} fg={C.muted} />
                  <button onClick={() => deleteEntry(e.id)} title="Verwijderen" style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✕</button>
                </div>
              </div>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                <tbody>
                  {(e.lines || []).map((l, i) => (
                    <tr key={i} style={{ borderTop: "1px solid " + C.border }}>
                      <td style={{ padding: "4px 0" }}>{l.account_code} · {l.account_name}</td>
                      <td style={{ padding: "4px 0", textAlign: "right", width: 110, color: Number(l.debit) > 0 ? C.text : C.dim }}>{Number(l.debit) > 0 ? eur(l.debit) : "-"}</td>
                      <td style={{ padding: "4px 0", textAlign: "right", width: 110, color: Number(l.credit) > 0 ? C.text : C.dim }}>{Number(l.credit) > 0 ? eur(l.credit) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {sub === "openstaand" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <p className="lbl" style={{ marginBottom: 8 }}>Openstaande debiteuren (nog te ontvangen)</p>
            {openDebiteuren.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 10 }}>Geen openstaande verkoopfacturen.</p></div>}
            {openDebiteuren.map(e => {
              const amt = round2(lineSum(e, "1300", "debit"));
              return (
                <div key={e.id} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div><span style={{ fontSize: 13, fontWeight: 600 }}>{e.description}</span><span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{e.entry_date} · {eur(amt)}</span></div>
                  <button className="btn bp" disabled={busyId === e.id} onClick={() => settle(e, "debiteur")}>Betaling ontvangen</button>
                </div>
              );
            })}
          </div>
          <div>
            <p className="lbl" style={{ marginBottom: 8 }}>Openstaande crediteuren (nog te betalen)</p>
            {openCrediteuren.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 10 }}>Geen openstaande inkoopfacturen op crediteuren.</p></div>}
            {openCrediteuren.map(e => {
              const amt = round2(lineSum(e, "1600", "credit"));
              return (
                <div key={e.id} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div><span style={{ fontSize: 13, fontWeight: 600 }}>{e.description}</span><span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{e.entry_date} · {eur(amt)}</span></div>
                  <button className="btn bp" disabled={busyId === e.id} onClick={() => settle(e, "crediteur")}>Betaling gedaan</button>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: C.muted }}>Tip: boek een inkoopfactuur op "1600 Crediteuren" (via Aanpassen onder Te boeken) als je 'm nog niet betaald hebt; dan verschijnt 'ie hier om af te letteren.</p>
        </div>
      )}

      {sub === "btw" && (
        <div className="card">
          <p className="lbl" style={{ marginBottom: 10 }}>BTW-aangifte {selYear} (uit het grootboek)</p>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead><tr style={{ color: C.muted, textAlign: "left" }}>
              <th style={{ padding: "5px 0", fontWeight: 500 }}>Kwartaal</th>
              <th style={{ padding: "5px 0", fontWeight: 500, textAlign: "right" }}>Verschuldigd (1510)</th>
              <th style={{ padding: "5px 0", fontWeight: 500, textAlign: "right" }}>Voorbelasting (1500)</th>
              <th style={{ padding: "5px 0", fontWeight: 500, textAlign: "right" }}>Saldo</th>
            </tr></thead>
            <tbody>
              {btwQuarters.length === 0 && <tr><td colSpan={4} style={{ padding: 10, color: C.dim, textAlign: "center" }}>Nog geen BTW geboekt.</td></tr>}
              {btwQuarters.map(q => {
                const b = btwByQ[q]; const saldo = round2(b.verschuldigd - b.voorbelasting);
                return (
                  <tr key={q} style={{ borderTop: "1px solid " + C.border }}>
                    <td style={{ padding: "6px 0" }}>{q}</td>
                    <td style={{ padding: "6px 0", textAlign: "right", ...bkMono }}>{eur(b.verschuldigd)}</td>
                    <td style={{ padding: "6px 0", textAlign: "right", ...bkMono }}>{eur(b.voorbelasting)}</td>
                    <td style={{ padding: "6px 0", textAlign: "right", ...bkMono, color: saldo >= 0 ? C.text : C.green }}>{eur(saldo)} <span style={{ fontSize: 10, color: C.dim }}>{saldo >= 0 ? "betalen" : "terug"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>Saldo = verschuldigde BTW minus voorbelasting. Positief = af te dragen aan de Belastingdienst; negatief = terug te ontvangen. Controleer altijd vóór indienen.</p>
        </div>
      )}

      {sub === "grootboek" && (
        <div className="card">
          <p className="lbl" style={{ marginBottom: 10 }}>Grootboek {selYear} · saldo per rekening</p>
          {["activa", "passiva", "eigen_vermogen", "omzet", "kosten"].map(type => {
            const rows = accounts.filter(a => a.type === type && (saldi[a.code]?.debit || saldi[a.code]?.credit));
            if (!rows.length) return null;
            return (
              <div key={type} style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{TYPE_LABEL[type]}</p>
                {rows.map(a => {
                  const d = saldi[a.code]?.debit || 0, c = saldi[a.code]?.credit || 0;
                  return (
                    <div key={a.code} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                      <span>{a.code} · {a.name}</span>
                      <span style={bkMono}>{eur(Math.abs(d - c))} <span style={{ color: C.dim, fontSize: 10 }}>{d - c >= 0 ? "D" : "C"}</span></span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {approved.length === 0 && <p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 10 }}>Nog niets geboekt. Keur eerst posten goed onder "Te boeken".</p>}
        </div>
      )}

      {sub === "balans" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 10 }}>Activa (bezittingen)</p>
            {activa.map(a => <div key={a.code} style={bkRow}><span>{a.name}</span><span style={bkMono}>{eur(netDebit(a.code))}</span></div>)}
            <div style={{ ...bkRow, fontWeight: 700, borderTop: "2px solid " + C.border, marginTop: 4 }}><span>Totaal activa</span><span style={bkMono}>{eur(activaTot)}</span></div>
          </div>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 10 }}>Passiva (eigen vermogen + schulden)</p>
            {evAcc.map(a => <div key={a.code} style={bkRow}><span>{a.name}</span><span style={bkMono}>{eur(netCredit(a.code))}</span></div>)}
            <div style={bkRow}><span>Resultaat boekjaar</span><span style={{ ...bkMono, color: resultaat >= 0 ? C.green : C.red }}>{eur(resultaat)}</span></div>
            {passiva.map(a => <div key={a.code} style={bkRow}><span>{a.name}</span><span style={bkMono}>{eur(netCredit(a.code))}</span></div>)}
            <div style={{ ...bkRow, fontWeight: 700, borderTop: "2px solid " + C.border, marginTop: 4 }}><span>Totaal passiva</span><span style={bkMono}>{eur(balansPassivaTot)}</span></div>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <p style={{ fontSize: 11, color: Math.abs(activaTot - balansPassivaTot) < 0.01 ? C.green : C.red, textAlign: "center" }}>
              {Math.abs(activaTot - balansPassivaTot) < 0.01 ? "✓ Balans in evenwicht" : "⚠ Verschil activa/passiva: " + eur(activaTot - balansPassivaTot) + ". Meestal een ontbrekende beginbalans of nog niet geboekte betalingen."}
            </p>
          </div>
        </div>
      )}

      {sub === "wv" && (
        <div className="card">
          <p className="lbl" style={{ marginBottom: 10 }}>Winst- en verliesrekening {selYear}</p>
          <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Omzet</p>
          {accounts.filter(a => a.type === "omzet" && netCredit(a.code)).map(a => <div key={a.code} style={bkRow}><span>{a.name}</span><span style={bkMono}>{eur(netCredit(a.code))}</span></div>)}
          <div style={{ ...bkRow, fontWeight: 600 }}><span>Totaal omzet</span><span style={bkMono}>{eur(omzetTot)}</span></div>
          <p style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", margin: "12px 0 4px" }}>Kosten</p>
          {accounts.filter(a => a.type === "kosten" && netDebit(a.code)).map(a => <div key={a.code} style={bkRow}><span>{a.name}</span><span style={bkMono}>{eur(netDebit(a.code))}</span></div>)}
          <div style={{ ...bkRow, fontWeight: 600 }}><span>Totaal kosten</span><span style={bkMono}>{eur(kostenTot)}</span></div>
          <div style={{ ...bkRow, fontWeight: 700, borderTop: "2px solid " + C.border, marginTop: 6, fontSize: 14 }}>
            <span>{resultaat >= 0 ? "Nettowinst" : "Nettoverlies"}</span>
            <span style={{ ...bkMono, color: resultaat >= 0 ? C.green : C.red }}>{eur(resultaat)}</span>
          </div>
        </div>
      )}

      {sub === "import" && (
        <div className="card">
          <p className="lbl" style={{ marginBottom: 8 }}>Bankafschrift importeren (CSV)</p>
          <p style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Plak de CSV van je bank (kolommen datum, bedrag, omschrijving). Negatieve bedragen worden kosten, positieve worden ontvangsten. Controleer de tegenrekening per regel vóór je importeert.</p>
          <textarea value={csvRaw} onChange={e => setCsvRaw(e.target.value)} rows={5} placeholder={"datum;bedrag;omschrijving\n2026-06-01;-49,00;Vercel hosting\n2026-06-03;1210,00;Betaling klant X"} style={{ fontSize: 12, fontFamily: "monospace", width: "100%", marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <label className="btn bg" style={{ cursor: "pointer" }}>
              Bestand kiezen
              <input type="file" accept=".csv,text/csv,text/plain" style={{ display: "none" }} onChange={async e => {
                const f = e.target.files?.[0]; if (!f) return;
                const text = await f.text();
                setCsvRaw(text); setCsvRows(parseCsv(text));
                e.target.value = "";
              }} />
            </label>
            <button className="btn bg" onClick={() => setCsvRows(parseCsv(csvRaw))}>Inlezen</button>
            {csvRows.length > 0 && <button className="btn bg" onClick={() => { setCsvRows([]); setCsvRaw(""); }}>Wissen</button>}
          </div>
          {csvRows.length > 0 && (
            <>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginBottom: 10 }}>
                <thead><tr style={{ color: C.muted, textAlign: "left" }}>
                  <th style={{ padding: "5px 0", fontWeight: 500 }}>Datum</th>
                  <th style={{ padding: "5px 0", fontWeight: 500 }}>Omschrijving</th>
                  <th style={{ padding: "5px 0", fontWeight: 500 }}>Tegenrekening</th>
                  <th style={{ padding: "5px 0", fontWeight: 500, textAlign: "right" }}>Bedrag</th>
                </tr></thead>
                <tbody>
                  {csvRows.map((r, i) => (
                    <tr key={i} style={{ borderTop: "1px solid " + C.border }}>
                      <td style={{ padding: "5px 0", whiteSpace: "nowrap" }}>{r.date}</td>
                      <td style={{ padding: "5px 0", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.desc}</td>
                      <td style={{ padding: "5px 0" }}>
                        <select value={r.code} onChange={e => setCsvRows(p => p.map((x, j) => j === i ? { ...x, code: e.target.value } : x))} style={{ fontSize: 12, padding: "3px 6px", maxWidth: 230 }}>
                          {accounts.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: "5px 0", textAlign: "right", ...bkMono, color: r.amount < 0 ? C.red : C.green }}>{eur(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn bp" disabled={importBusy} onClick={importRows}>Importeer {csvRows.length} boeking{csvRows.length !== 1 ? "en" : ""}</button>
            </>
          )}
        </div>
      )}

      {newEntry && (() => {
        const totD = round2(ne.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0));
        const totC = round2(ne.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0));
        const balanced = Math.abs(totD - totC) < 0.005 && totD > 0;
        const setLine = (i, patch) => setNe(p => ({ ...p, lines: p.lines.map((x, j) => j === i ? { ...x, ...patch } : x) }));
        return (
          <Portal><div className="ovl" onClick={() => setNewEntry(false)}>
            <div className="mdl" onClick={ev => ev.stopPropagation()} style={{ maxWidth: 640 }}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 14, fontSize: 14 }}>NIEUWE JOURNAALPOST</h3>
              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 160 }}><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Datum</p><input type="date" value={ne.entry_date} onChange={e => setNe(p => ({ ...p, entry_date: e.target.value }))} style={{ fontSize: 12 }} /></div>
                <div style={{ flex: 1 }}><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Omschrijving</p><input value={ne.description} onChange={e => setNe(p => ({ ...p, description: e.target.value }))} placeholder="bv. Beginbalans, correctie, privestorting" /></div>
              </div>
              <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginBottom: 8 }}>
                <thead><tr style={{ color: C.muted, textAlign: "left" }}>
                  <th style={{ fontWeight: 500, padding: "4px 0" }}>Rekening</th>
                  <th style={{ fontWeight: 500, padding: "4px 0", textAlign: "right", width: 110 }}>Debet</th>
                  <th style={{ fontWeight: 500, padding: "4px 0", textAlign: "right", width: 110 }}>Credit</th>
                  <th style={{ width: 28 }}></th>
                </tr></thead>
                <tbody>
                  {ne.lines.map((l, i) => (
                    <tr key={i} style={{ borderTop: "1px solid " + C.border }}>
                      <td style={{ padding: "4px 0" }}>
                        <select value={l.code} onChange={e => setLine(i, { code: e.target.value })} style={{ fontSize: 12, padding: "3px 6px", maxWidth: 260 }}>
                          <option value="">- kies rekening -</option>
                          {accounts.map(a => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: "4px 0", textAlign: "right" }}><input type="number" step="0.01" value={l.debit} onChange={e => setLine(i, { debit: e.target.value, credit: "" })} style={{ width: 95, textAlign: "right", fontSize: 12 }} /></td>
                      <td style={{ padding: "4px 0", textAlign: "right" }}><input type="number" step="0.01" value={l.credit} onChange={e => setLine(i, { credit: e.target.value, debit: "" })} style={{ width: 95, textAlign: "right", fontSize: 12 }} /></td>
                      <td style={{ textAlign: "center" }}>{ne.lines.length > 2 && <button onClick={() => setNe(p => ({ ...p, lines: p.lines.filter((_, j) => j !== i) }))} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 13 }}>✕</button>}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: "2px solid " + C.border, fontWeight: 600 }}>
                    <td style={{ padding: "5px 0", color: balanced ? C.green : C.red }}>{balanced ? "In balans" : "Niet in balans"}</td>
                    <td style={{ padding: "5px 0", textAlign: "right" }}>{eur(totD)}</td>
                    <td style={{ padding: "5px 0", textAlign: "right" }}>{eur(totC)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <button className="btn bg" onClick={() => setNe(p => ({ ...p, lines: [...p.lines, emptyLine()] }))} style={{ fontSize: 12, marginBottom: 12 }}>+ Regel</button>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => setNewEntry(false)}>Annuleer</button>
                <button className="btn bp" disabled={!balanced} onClick={saveManual}>Boeken</button>
              </div>
            </div>
          </div></Portal>
        );
      })()}
    </div>
  );
}

function Administratie({ user }) {
  const [admLoc, setAdmLoc] = useState("");      // adres-invoer voor de kaart-lookup
  const [admLocShow, setAdmLocShow] = useState(""); // bevestigde zoekopdracht
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const meName = meKey === "tristan" ? "Tristan" : "Furgel";
  const curYear = new Date().getFullYear();
  const curQ = "Q" + Math.ceil((new Date().getMonth() + 1) / 3);
  const [tab, setTab] = useState("inname");
  const [selYear, setSelYear] = useState(curYear);
  const [selQ, setSelQ] = useState(curQ);
  const [selPartner, setSelPartner] = useState(meKey); // start op je eigen tab (incl. snel-loggen)
  const [partnerTab, setPartnerTab] = useState("uren");

  const [docs, setDocs] = useState([]);
  const [trans, setTrans] = useState([]);
  const [mileage, setMileage] = useState([]);
  const [hours, setHours] = useState([]);
  const [mutations, setMutations] = useState([]);

  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [editDoc, setEditDoc] = useState(null); // bestaande admin_doc bewerken
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);

  const [newTrans, setNewTrans] = useState(false);
  const [nt, setNt] = useState({ trans_date: "", type: "expense", category: "Overig", description: "", amount_excl: "", vat_rate: 21, amount_incl: "", partner: "" });
  const [newHour, setNewHour] = useState(false);
  const [nh, setNh] = useState({ log_date: new Date().toISOString().split("T")[0], partner: meKey, hours: "", category: "Development", description: "" });
  const [nhReeks, setNhReeks] = useState(REEKS_NIEUW);
  const [editHourId, setEditHourId] = useState(null); // null = nieuw, id = bestaande uren-registratie bewerken
  const [quickBusy, setQuickBusy] = useState(false);
  const [quickCat, setQuickCat] = useState("Development");

  // Snelregistratie: één klik logt het gekozen aantal uren voor vandaag op je
  // eigen naam — geen typen nodig. Aanpassen kan altijd via "+ Uren toevoegen".
  async function quickLogHours(amount) {
    if (quickBusy) return;
    setQuickBusy(true);
    const today = new Date().toISOString().split("T")[0];
    const row = { log_date: today, partner: meKey, hours: amount, category: quickCat, description: "Snelregistratie", year: new Date(today).getFullYear() };
    const { data, error } = await supabase.from("hour_logs").insert([row]).select();
    if (error) { showToast("✗ " + error.message); }
    else if (data) { setHours(p => [...p, data[0]].sort((a, b) => a.log_date.localeCompare(b.log_date))); showToast(`✓ ${amount}u geregistreerd voor vandaag`); }
    setQuickBusy(false);
  }
  const [newKm, setNewKm] = useState(false);
  const [nk, setNk] = useState({ log_date: new Date().toISOString().split("T")[0], partner: meKey, from_loc: "", to_loc: "", purpose: "", km: "" });
  const [newMut, setNewMut] = useState(false);
  const [nm, setNm] = useState({ mut_date: new Date().toISOString().split("T")[0], partner: meKey, type: "storting", amount: "", description: "" });
  const [editKmId, setEditKmId] = useState(null); // null = nieuw, id = rit bewerken
  const [editMutId, setEditMutId] = useState(null); // null = nieuw, id = mutatie bewerken

  // EV Ritregistratie state
  const TODAY = new Date().toISOString().split("T")[0];
  const [evTrips, setEvTrips] = useState([]);
  const [evVehicles, setEvVehicles] = useState([]);
  const [newTrip, setNewTrip] = useState(false);
  const [tripStep, setTripStep] = useState(1);
  const [routeCalc, setRouteCalc] = useState({ loading: false, done: false, error: null });
  const [tripGeo, setTripGeo] = useState(null);
  const [savingTrip, setSavingTrip] = useState(false);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [tripRouteCache, setTripRouteCache] = useState({});
  const [newVehicleModal, setNewVehicleModal] = useState(false);
  const [nvForm, setNvForm] = useState({ name: "", owner: "furgel", brand: "", model: "", license_plate: "", kwh_per_100km: "18" });
  const EV_ELEC_PRICE = 0.32;
  const [ntEv, setNtEv] = useState({ trip_date: TODAY, driver: meKey, vehicle_id: "", start_location: "", end_location: "", roundtrip: false, purpose: "zakelijk", description: "", client_project: "", kwh_per_100km: "18", electricity_price_kwh: EV_ELEC_PRICE.toString(), notes: "" });

  const PURPOSE_LABELS = { zakelijk: "Zakelijk", prive: "Privé", woon_werk: "Woon-werk", gemengd: "Gemengd" };
  const PURPOSE_COL = { zakelijk: C.green, prive: C.muted, woon_werk: C.blue, gemengd: C.yellow };

  async function geocodeAndRoute() {
    if (!ntEv.start_location.trim() || !ntEv.end_location.trim()) return;
    setRouteCalc({ loading: true, done: false, error: null });
    setTripGeo(null);
    try {
      async function geocode(q) {
        const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + ", Nederland")}&format=json&limit=3&countrycodes=nl,be&addressdetails=1`;
        const d = await fetch(url).then(r => r.json());
        if (!d.length) throw new Error("Adres niet gevonden: " + q);
        return { lat: parseFloat(d[0].lat), lng: parseFloat(d[0].lon) };
      }
      const [s, e] = await Promise.all([geocode(ntEv.start_location), geocode(ntEv.end_location)]);
      const rrRaw = await fetch("https://valhalla1.openstreetmap.de/route", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locations: [{ lon: s.lng, lat: s.lat }, { lon: e.lng, lat: e.lat }], costing: "auto" })
      }).then(r => r.json());
      if (!rrRaw.trip) throw new Error("Route niet berekend");
      const shape = rrRaw.trip.legs[0].shape;
      function decPoly6(str) { let i = 0, lat = 0, lng = 0, c = []; while (i < str.length) { let b, sh = 0, r = 0; do { b = str.charCodeAt(i++) - 63; r |= (b & 0x1f) << sh; sh += 5; } while (b >= 0x20); lat += (r & 1) ? ~(r >> 1) : (r >> 1); sh = r = 0; do { b = str.charCodeAt(i++) - 63; r |= (b & 0x1f) << sh; sh += 5; } while (b >= 0x20); lng += (r & 1) ? ~(r >> 1) : (r >> 1); c.push([lng / 1e6, lat / 1e6]); } return c; }
      const geometry = { type: "LineString", coordinates: decPoly6(shape) };
      const rr = { distance_km: Math.round(rrRaw.trip.summary.length * 10) / 10, duration_min: Math.round(rrRaw.trip.summary.time / 60), geometry };
      if (rr.error) throw new Error(rr.error);
      const dist = ntEv.roundtrip ? rr.distance_km * 2 : rr.distance_km;
      const dur = ntEv.roundtrip ? rr.duration_min * 2 : rr.duration_min;
      setTripGeo({ startLat: s.lat, startLng: s.lng, endLat: e.lat, endLng: e.lng, startDisplay: s.display, endDisplay: e.display, distance_km: dist, duration_min: dur, routeGeometry: rr.geometry || null });
      setRouteCalc({ loading: false, done: true, error: null });
      setTripStep(2);
    } catch (e) {
      setRouteCalc({ loading: false, done: false, error: e.message });
    }
  }

  async function saveEvTrip() {
    if (!tripGeo || !ntEv.trip_date || !ntEv.description.trim() || savingTrip) return;
    setSavingTrip(true);
    const kwh100 = parseFloat(ntEv.kwh_per_100km) || 18;
    const ePrijs = parseFloat(ntEv.electricity_price_kwh) || EV_ELEC_PRICE;
    const km = tripGeo.distance_km;
    const totalKwh = Math.round(km * (kwh100 / 100) * 100) / 100;
    const costTrip = Math.round(totalKwh * ePrijs * 100) / 100;
    const costKm = km > 0 ? Math.round(costTrip / km * 1000) / 1000 : 0;
    const yr = new Date(ntEv.trip_date).getFullYear();
    const q = "Q" + Math.ceil((new Date(ntEv.trip_date).getMonth() + 1) / 3);
    const veh = evVehicles.find(v => v.id === ntEv.vehicle_id);
    const row = {
      driver: ntEv.driver, vehicle_id: ntEv.vehicle_id || null, vehicle_name: veh?.name || "",
      trip_date: ntEv.trip_date, start_location: ntEv.start_location, end_location: ntEv.end_location,
      start_lat: tripGeo.startLat, start_lng: tripGeo.startLng, end_lat: tripGeo.endLat, end_lng: tripGeo.endLng,
      distance_km: km, roundtrip: ntEv.roundtrip, purpose: ntEv.purpose,
      description: ntEv.description, client_project: ntEv.client_project,
      kwh_per_100km: kwh100, electricity_price_kwh: ePrijs,
      total_kwh: totalKwh, cost_per_trip: costTrip, cost_per_km: costKm,
      duration_min: tripGeo.duration_min, year: yr, quarter: q + "-" + yr, notes: ntEv.notes,
      flagged: !ntEv.description.trim(),
    };
    const { data, error } = await supabase.from("ev_trips").insert([row]).select();
    if (error) { setSavingTrip(false); showToast("✗ Fout: " + error.message); return; }
    if (data) { setEvTrips(p => [data[0], ...p]); logActivity("rit", (ntEv && ntEv.description) || "", "administratie"); }
    // Zakelijk rit → ook als transactie boeken
    if (ntEv.purpose === "zakelijk" || ntEv.purpose === "gemengd") {
      const frac = ntEv.purpose === "gemengd" ? 0.5 : 1;
      await supabase.from("admin_transactions").insert([{
        trans_date: ntEv.trip_date, type: "expense", category: "Vervoer – Elektrisch",
        description: `EV rit: ${ntEv.start_location} → ${ntEv.end_location} (${km}km)`,
        amount_excl: Math.round(costTrip * frac * 100) / 100, vat_rate: 0, vat_amount: 0,
        amount_incl: Math.round(costTrip * frac * 100) / 100, year: yr, quarter: q + "-" + yr,
      }]);
    }
    setSavingTrip(false);
    setNewTrip(false); setTripStep(1); setTripGeo(null); setRouteCalc({ loading: false, done: false, error: null });
    setNtEv({ trip_date: TODAY, driver: meKey, vehicle_id: "", start_location: "", end_location: "", roundtrip: false, purpose: "zakelijk", description: "", client_project: "", kwh_per_100km: "18", electricity_price_kwh: EV_ELEC_PRICE.toString(), notes: "" });
    showToast("✓ Rit geregistreerd" + (ntEv.purpose === "zakelijk" ? " — als transactie geboekt" : ""));
  }

  async function saveEvVehicle() {
    if (!nvForm.name.trim()) return;
    const row = { name: nvForm.name, owner: nvForm.owner, brand: nvForm.brand, model: nvForm.model, license_plate: nvForm.license_plate, kwh_per_100km: parseFloat(nvForm.kwh_per_100km) || 18 };
    const { data, error } = await supabase.from("ev_vehicles").insert([row]).select();
    if (error) { showToast("✗ " + error.message); return; }
    if (data) { setEvVehicles(p => [...p, data[0]]); setNtEv(p => ({ ...p, vehicle_id: data[0].id, kwh_per_100km: data[0].kwh_per_100km.toString() })); }
    setNewVehicleModal(false); setNvForm({ name: "", owner: "furgel", brand: "", model: "", license_plate: "", kwh_per_100km: "18" });
    showToast("✓ Voertuig opgeslagen");
  }

  async function fetchTripRoute(trip) {
    if (tripRouteCache[trip.id]) return;
    if (!trip.start_lat || !trip.start_lng || !trip.end_lat || !trip.end_lng) return;
    try {
      const rrRaw = await fetch("https://valhalla1.openstreetmap.de/route", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locations: [{ lon: trip.start_lng, lat: trip.start_lat }, { lon: trip.end_lng, lat: trip.end_lat }], costing: "auto" })
      }).then(r => r.json());
      if (!rrRaw.trip) return;
      const shape = rrRaw.trip.legs[0].shape;
      function decPoly6(str) { let i = 0, lat = 0, lng = 0, c = []; while (i < str.length) { let b, sh = 0, r = 0; do { b = str.charCodeAt(i++) - 63; r |= (b & 0x1f) << sh; sh += 5; } while (b >= 0x20); lat += (r & 1) ? ~(r >> 1) : (r >> 1); sh = r = 0; do { b = str.charCodeAt(i++) - 63; r |= (b & 0x1f) << sh; sh += 5; } while (b >= 0x20); lng += (r & 1) ? ~(r >> 1) : (r >> 1); c.push([lng / 1e6, lat / 1e6]); } return c; }
      setTripRouteCache(p => ({ ...p, [trip.id]: { type: "LineString", coordinates: decPoly6(shape) } }));
    } catch (e) { }
  }

  async function deleteEvTrip(id) {
    if (!window.confirm("Rit verwijderen?")) return;
    await supabase.from("ev_trips").delete().eq("id", id);
    setEvTrips(p => p.filter(t => t.id !== id));
    showToast("✓ Rit verwijderd");
  }

  function exportEvTrips() {
    const yr = selYear;
    const trips = evTrips.filter(t => t.year === yr);
    const zakelijk = trips.filter(t => t.purpose === "zakelijk" || t.purpose === "gemengd");
    const totalKm = trips.reduce((s, t) => s + (t.distance_km || 0), 0);
    const zakKm = zakelijk.reduce((s, t) => s + (t.distance_km || 0), 0);
    const totalCost = trips.reduce((s, t) => s + (t.cost_per_trip || 0), 0);
    const zakCost = zakelijk.reduce((s, t) => s + (t.cost_per_trip || 0), 0);
    const lines = [
      `EV RITREGISTRATIE EXPORT — ${yr}`,
      `Bedrijf: Dif3r3nt VOF`,
      `Gegenereerd: ${new Date().toLocaleDateString("nl-NL")}`, ``,
      `SAMENVATTING`,
      `Totaal ritten: ${trips.length}`,
      `Totaal km: ${totalKm.toFixed(1)} km`,
      `Zakelijk km: ${zakKm.toFixed(1)} km`,
      `Totaal EV kosten: €${totalCost.toFixed(2)}`,
      `Zakelijk aftrekbaar: €${zakCost.toFixed(2)}`, ``,
      `FURGEL`,
      ...["zakelijk", "prive", "woon_werk", "gemengd"].map(p => {
        const t = trips.filter(x => x.driver === "furgel" && x.purpose === p);
        return `  ${PURPOSE_LABELS[p]}: ${t.length} ritten, ${t.reduce((s, x) => s + (x.distance_km || 0), 0).toFixed(1)} km`;
      }), ``,
      `TRISTAN`,
      ...["zakelijk", "prive", "woon_werk", "gemengd"].map(p => {
        const t = trips.filter(x => x.driver === "tristan" && x.purpose === p);
        return `  ${PURPOSE_LABELS[p]}: ${t.length} ritten, ${t.reduce((s, x) => s + (x.distance_km || 0), 0).toFixed(1)} km`;
      }), ``, ``,
      `ALLE RITTEN`,
      `Datum       | Bestuurder | Van                     | Naar                    | km     | Categorie  | kWh   | Kosten   | Doel`,
      `${"─".repeat(120)}`,
      ...trips.map(t => `${t.trip_date} | ${t.driver.padEnd(10)} | ${(t.start_location || "").slice(0, 23).padEnd(23)} | ${(t.end_location || "").slice(0, 23).padEnd(23)} | ${(t.distance_km || 0).toFixed(1).padStart(6)} | ${(PURPOSE_LABELS[t.purpose] || "").padEnd(10)} | ${(t.total_kwh || 0).toFixed(1).padStart(5)} | €${(t.cost_per_trip || 0).toFixed(2).padStart(7)} | ${t.description || ""}`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `EV_Ritregistratie_${yr}.txt`; a.click();
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(null), 3000); }

  useEffect(() => {
    async function load() {
      const [d, t, m, h, p, ev, veh] = await Promise.all([
        supabase.from("admin_docs").select("*").eq("year", selYear).order("doc_date", { ascending: false }),
        supabase.from("admin_transactions").select("*").eq("year", selYear).order("trans_date", { ascending: false }),
        supabase.from("mileage_logs").select("*").eq("year", selYear).order("log_date"),
        supabase.from("hour_logs").select("*").eq("year", selYear).order("log_date"),
        supabase.from("partner_mutations").select("*").eq("year", selYear).order("mut_date", { ascending: false }),
        supabase.from("ev_trips").select("*").eq("year", selYear).order("trip_date", { ascending: false }),
        supabase.from("ev_vehicles").select("*").order("name"),
      ]);
      if (!d.error) setDocs(d.data || []);
      if (!t.error) setTrans(t.data || []);
      if (!m.error) setMileage(m.data || []);
      if (!h.error) setHours(h.data || []);
      if (!p.error) setMutations(p.data || []);
      if (!ev.error) setEvTrips(ev.data || []);
      if (!veh.error) setEvVehicles(veh.data || []);
    }
    load();
    // Realtime: betaalstatus die door de Mollie-webhook wordt bijgewerkt, live in de UI tonen
    const ch = supabase.channel("admin_docs_sync_" + selYear)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "admin_docs" }, (payload) => {
        const u = payload.new; if (!u) return;
        setDocs(p => p.map(x => x.id === u.id ? { ...x, ...u } : x));
        setEditDoc(p => p && p.id === u.id ? { ...p, payment_status: u.payment_status, paid_at: u.paid_at, payment_url: u.payment_url, mollie_payment_id: u.mollie_payment_id } : p);
      })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [selYear]);

  async function imageToBase64(file) {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1200, canvas = document.createElement("canvas");
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) { if (w > h) { h = Math.round(h * MAX / w); w = MAX; } else { w = Math.round(w * MAX / h); h = MAX; } }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  async function pdfToBase64(file) {
    return new Promise(async (resolve, reject) => {
      try {
        if (!window.pdfjsLib) {
          throw new Error("PDF.js library is nog niet geladen.");
        }
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);

        let viewport = page.getViewport({ scale: 2.0 });
        const MAX = 1600;
        let width = viewport.width;
        let height = viewport.height;
        if (width > MAX || height > MAX) {
          const scale = width > height ? MAX / width : MAX / height;
          viewport = page.getViewport({ scale: 2.0 * scale });
        }

        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext("2d");

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch (err) {
        reject(err);
      }
    });
  }

  async function processFile(file) {
    setExtracting(true); setExtracted(null);
    // Bewaar ALTIJD het originele bestand in storage (foto/PDF), zodat het document
    // later getoond kan worden — niet alleen de geëxtraheerde tekst.
    let fileUrl = "";
    try { const up = await uploadToDocuments(file, "admin"); fileUrl = up.url; }
    catch (e) { console.error("Admin upload origineel:", e); }
    const base = { file_name: file.name, file_type: file.type, mime_type: file.type || "", file_url: fileUrl, uploaded_by: "furgel" };
    try {
      if (file.type.startsWith("image/")) {
        const base64 = await imageToBase64(file);
        const r = await apiFetch("/api/ocr", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ image: base64, filename: file.name }) });
        const json = await r.json();
        setExtracted(deriveAmounts({ ...json.data, ...base }));
      } else if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
        const base64 = await pdfToBase64(file);
        const r = await apiFetch("/api/ocr", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ image: base64, filename: file.name }) });
        const json = await r.json();
        setExtracted(deriveAmounts({ ...json.data, ...base }));
      } else {
        setExtracted({ doc_type: "bon", doc_date: new Date().toISOString().split("T")[0], description: file.name.replace(/\.[^.]+$/, ""), ...base });
      }
    } catch (e) {
      console.error("Fout bij verwerken OCR:", e);
      setExtracted({ doc_type: "bon", doc_date: new Date().toISOString().split("T")[0], ...base });
    }
    setExtracting(false);
  }

  async function saveDoc() {
    if (!extracted) return;
    setSaving(true);
    const d = deriveAmounts(extracted);
    const dateStr = d.doc_date || new Date().toISOString().split("T")[0];
    const yr = new Date(dateStr).getFullYear();
    const q = docQuarter(dateStr);
    const row = { uploaded_by: d.uploaded_by || "furgel", doc_type: d.doc_type, status: "processed", doc_date: dateStr, supplier: d.supplier || null, customer: d.customer || null, description: d.description || null, amount_excl: parseFloat(d.amount_excl) || null, vat_rate: parseFloat(d.vat_rate) || 21, vat_amount: parseFloat(d.vat_amount) || null, amount_incl: parseFloat(d.amount_incl) || null, payment_method: d.payment_method || null, category: d.category || "Overig", quarter: q, year: yr, file_name: d.file_name, file_type: d.file_type, file_url: d.file_url || null, mime_type: d.mime_type || d.file_type || null, flagged: !d.amount_excl || !d.doc_date, flag_reason: (!d.amount_excl || !d.doc_date) ? "Controleer bedrag en datum" : null };
    const { data: docData, error } = await supabase.from("admin_docs").insert([row]).select();
    if (!error && docData) {
      setDocs(p => [docData[0], ...p]);
      if (d.amount_excl) {
        const isIncome = d.doc_type === "factuur_verkoop";
        const excl = parseFloat(d.amount_excl) || 0;
        const vatAmt = parseFloat(d.vat_amount) || Math.round(excl * (parseFloat(d.vat_rate) || 21) / 100 * 100) / 100;
        const trow = { trans_date: dateStr, type: isIncome ? "income" : "expense", category: d.category || "Overig", description: d.description || (d.supplier || d.customer || "—"), amount_excl: excl, vat_rate: parseFloat(d.vat_rate) || 21, vat_amount: vatAmt, amount_incl: parseFloat(d.amount_incl) || excl + vatAmt, doc_id: docData[0].id, year: yr, quarter: q };
        const { data: td } = await supabase.from("admin_transactions").insert([trow]).select();
        if (td) setTrans(p => [td[0], ...p]);
      }
      showToast("✓ Document opgeslagen" + (row.flagged ? " — controleer gemarkeerde velden in 'Recente documenten'" : ""));
    } else showToast("✗ Fout: " + error?.message);
    setExtracted(null); setSaving(false);
  }

  async function updateDoc() {
    if (!editDoc) return;
    setSaving(true);
    const d = deriveAmounts(editDoc);
    const dateStr = d.doc_date || new Date().toISOString().split("T")[0];
    const yr = new Date(dateStr).getFullYear();
    const q = docQuarter(dateStr);
    const excl = parseFloat(d.amount_excl) || null;
    const vatRate = parseFloat(d.vat_rate) || 21;
    const vatAmt = parseFloat(d.vat_amount) || (excl ? Math.round(excl * vatRate / 100 * 100) / 100 : null);
    const flagged = !excl || !d.doc_date;
    const patch = {
      doc_type: d.doc_type, doc_date: dateStr, supplier: d.supplier || null, customer: d.customer || null,
      description: d.description || null, amount_excl: excl, vat_rate: vatRate, vat_amount: vatAmt,
      amount_incl: parseFloat(d.amount_incl) || (excl && vatAmt ? excl + vatAmt : null),
      payment_method: d.payment_method || null, category: d.category || "Overig",
      quarter: q, year: yr,
      flagged, flag_reason: flagged ? "Controleer bedrag en datum" : null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from("admin_docs").update(patch).eq("id", d.id).select();
    if (error) { showToast("✗ Bijwerken mislukt: " + error.message); setSaving(false); return; }
    if (data) setDocs(p => p.map(x => x.id === d.id ? data[0] : x));
    // Sync de gekoppelde transactie als die er is, of maak er een als bedrag nu wel ingevuld is
    if (excl) {
      const isIncome = d.doc_type === "factuur_verkoop";
      const trow = { trans_date: dateStr, type: isIncome ? "income" : "expense", category: d.category || "Overig", description: d.description || (d.supplier || d.customer || "—"), amount_excl: excl, vat_rate: vatRate, vat_amount: vatAmt || 0, amount_incl: patch.amount_incl, doc_id: d.id, year: yr, quarter: q };
      const { data: exTrans } = await supabase.from("admin_transactions").select("id").eq("doc_id", d.id).maybeSingle();
      if (exTrans) {
        await supabase.from("admin_transactions").update(trow).eq("id", exTrans.id);
        setTrans(p => p.map(t => t.id === exTrans.id ? { ...t, ...trow } : t));
      } else {
        const { data: td } = await supabase.from("admin_transactions").insert([trow]).select();
        if (td) setTrans(p => [td[0], ...p]);
      }
    }
    setEditDoc(null); setSaving(false);
    showToast(flagged ? "⚑ Opgeslagen — er ontbreken nog velden" : "✓ Bijgewerkt en goedgekeurd");
  }

  async function deleteAdminDoc(id) {
    if (!window.confirm("Verwijder dit document en bijbehorende transactie permanent?")) return;
    await supabase.from("admin_transactions").delete().eq("doc_id", id);
    const { error } = await supabase.from("admin_docs").delete().eq("id", id);
    if (error) { showToast("✗ Verwijderen mislukt: " + error.message); return; }
    setDocs(p => p.filter(x => x.id !== id));
    setTrans(p => p.filter(t => t.doc_id !== id));
    setEditDoc(null);
    showToast("Document verwijderd");
  }

  // ── Betalingen (Mollie iDEAL/creditcard) ──
  const [payBusy, setPayBusy] = useState(false);
  function patchDocLocal(id, patch) {
    setDocs(p => p.map(x => x.id === id ? { ...x, ...patch } : x));
    setEditDoc(p => p && p.id === id ? { ...p, ...patch } : p);
  }
  async function createPaymentLink(doc) {
    const amount = parseFloat(doc.amount_incl) || parseFloat(doc.amount_excl) || 0;
    if (amount <= 0) { showToast("✗ Geen bedrag op dit document — vul eerst het bedrag in."); return; }
    setPayBusy(true);
    try {
      const desc = [doc.supplier || doc.customer, doc.description].filter(Boolean).join(" — ") || "Factuur Dif3r3nt";
      const r = await apiFetch("/api/mollie-create-payment", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, description: desc, docId: doc.id, redirectBase: window.location.origin })
      });
      const d = await r.json();
      if (!r.ok || !d.checkoutUrl) { showToast("✗ Betaallink mislukt: " + (d.error || "onbekend")); setPayBusy(false); return; }
      const patch = { payment_status: "pending", mollie_payment_id: d.id, payment_url: d.checkoutUrl };
      await supabase.from("admin_docs").update(patch).eq("id", doc.id);
      patchDocLocal(doc.id, patch);
      showToast("✓ Betaallink aangemaakt — deel 'm met je klant.");
    } catch (e) { showToast("✗ Fout: " + (e.message || "?")); }
    setPayBusy(false);
  }
  async function checkPaymentStatus(doc) {
    if (!doc.mollie_payment_id) return;
    setPayBusy(true);
    try {
      const r = await apiFetch("/api/mollie-payment-status?id=" + encodeURIComponent(doc.mollie_payment_id));
      const d = await r.json();
      if (!r.ok) { showToast("✗ Status ophalen mislukt: " + (d.error || "onbekend")); setPayBusy(false); return; }
      const map = { paid: "betaald", failed: "mislukt", canceled: "mislukt", expired: "verlopen", pending: "pending", open: "pending" };
      const ps = map[d.status] || "pending";
      const patch = { payment_status: ps, paid_at: d.paidAt || (ps === "betaald" ? new Date().toISOString() : null) };
      await supabase.from("admin_docs").update(patch).eq("id", doc.id);
      patchDocLocal(doc.id, patch);
      showToast(ps === "betaald" ? "✓ Betaald!" : ps === "pending" ? "Nog niet betaald" : "Status: " + ps);
    } catch (e) { showToast("✗ Fout: " + (e.message || "?")); }
    setPayBusy(false);
  }
  async function markPaid(doc) {
    setPayBusy(true);
    const patch = { payment_status: "betaald", paid_at: new Date().toISOString() };
    const { error } = await supabase.from("admin_docs").update(patch).eq("id", doc.id);
    if (error) { showToast("✗ Mislukt: " + error.message); setPayBusy(false); return; }
    patchDocLocal(doc.id, patch);
    showToast("✓ Gemarkeerd als betaald");
    setPayBusy(false);
  }

  const qTrans = trans.filter(t => t.quarter === selQ + "-" + selYear);
  const qIncome = qTrans.filter(t => t.type === "income").reduce((s, t) => s + (t.amount_excl || 0), 0);
  const qExpense = qTrans.filter(t => t.type === "expense").reduce((s, t) => s + (t.amount_excl || 0), 0);
  const qVatOut = qTrans.filter(t => t.type === "income").reduce((s, t) => s + (t.vat_amount || 0), 0);
  const qVatIn = qTrans.filter(t => t.type === "expense").reduce((s, t) => s + (t.vat_amount || 0), 0);
  const qVatSaldo = qVatOut - qVatIn;
  const yearIncome = trans.filter(t => t.type === "income").reduce((s, t) => s + (t.amount_excl || 0), 0);
  const yearExpense = trans.filter(t => t.type === "expense").reduce((s, t) => s + (t.amount_excl || 0), 0);
  const yearProfit = yearIncome - yearExpense;
  const partnerHours = hours.filter(h => h.partner === selPartner).reduce((s, h) => s + (h.hours || 0), 0);
  const partnerKm = mileage.filter(m => m.partner === selPartner).reduce((s, m) => s + (m.km || 0), 0);
  const partnerKmDeduction = Math.round(partnerKm * KM_RATE * 100) / 100;
  const stortingen = mutations.filter(m => m.partner === selPartner && m.type === "storting").reduce((s, m) => s + (m.amount || 0), 0);
  const onttrekkingen = mutations.filter(m => m.partner === selPartner && m.type === "onttrekking").reduce((s, m) => s + (m.amount || 0), 0);
  const flaggedDocs = docs.filter(d => d.flagged);

  // Bouwt de aangifte-tekst voor een SPECIFIEK kwartaal (quarter-expliciet, niet via de
  // async selQ-state; dat gaf een stale-quarter bug bij de kwartaalknoppen).
  function btwLines(q) {
    const [qs, qe] = qToDateRange(q, selYear);
    const qt = trans.filter(t => t.quarter === q + "-" + selYear);
    const inc = (rate) => qt.filter(t => t.type === "income" && t.vat_rate === rate).reduce((s, t) => s + (t.amount_excl || 0), 0);
    const vatOut = qt.filter(t => t.type === "income").reduce((s, t) => s + (t.vat_amount || 0), 0);
    const vatIn = qt.filter(t => t.type === "expense").reduce((s, t) => s + (t.vat_amount || 0), 0);
    const saldo = vatOut - vatIn;
    return [
      `BTW-AANGIFTE ${q} ${selYear}`, `Periode: ${qs} t/m ${qe}`, `Bedrijf: Dif3r3nt VOF`, `Gegenereerd: ${new Date().toLocaleDateString("nl-NL")}`, ``,
      `=== RUBRIEK 1 - BINNENLANDSE OMZET ===`,
      `1a. Leveringen/diensten 21%: €${inc(21).toFixed(2)}`,
      `1b. Leveringen/diensten 9%:  €${inc(9).toFixed(2)}`,
      `1c. Leveringen/diensten 0%:  €${inc(0).toFixed(2)}`, ``,
      `=== RUBRIEK 5 - SALDO BTW ===`,
      `5a. Verschuldigde BTW (omzet): €${vatOut.toFixed(2)}`,
      `5b. Voorbelasting (inkoop):    €${vatIn.toFixed(2)}`,
      `Saldo te betalen / terug:      €${saldo.toFixed(2)} (${saldo > 0 ? "te betalen" : saldo < 0 ? "terug te vorderen" : "nihil"})`, ``,
      `=== TRANSACTIES KWARTAAL (${qt.length}) ===`,
      ...(qt.length ? qt.map(t => `${t.trans_date} | ${t.type === "income" ? "Verkoop" : "Inkoop"} | ${t.description || "-"} | Excl: €${(t.amount_excl || 0).toFixed(2)} | BTW ${t.vat_rate}%: €${(t.vat_amount || 0).toFixed(2)}`) : ["(geen transacties in dit kwartaal - de aangifte is nog leeg)"]),
    ];
  }
  function exportBTW(q = selQ) {
    const blob = new Blob([btwLines(q).join("\n")], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `BTW_Aangifte_${q}_${selYear}.txt`; a.click();
  }
  // Slaat de aangifte MET echte cijfers op als document in de Documenten-module.
  // Bestaat er al een aangifte met dezelfde titel, dan wordt die bijgewerkt (geen duplicaat).
  async function saveBTWtoDoc(q) {
    const title = `BTW-aangifte ${q} ${selYear}`;
    const content = btwLines(q).join("\n");
    const by = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
    try {
      const { data: ex } = await supabase.from("documents").select("id").eq("title", title).order("version", { ascending: false }).limit(1).maybeSingle();
      if (ex) await supabase.from("documents").update({ content, type: "financial", updated_at: new Date().toISOString() }).eq("id", ex.id);
      else await supabase.from("documents").insert([{ title, type: "financial", content, aangemaakt_door: by, is_custom: true, archived: false, version: 1 }]);
      showToast("✓ BTW-aangifte " + q + " " + selYear + " opgeslagen in Documenten");
    } catch (e) { showToast("✗ Opslaan mislukt: " + (e.message || "?")); }
  }

  function exportIB(partner) {
    const pHours = hours.filter(h => h.partner === partner).reduce((s, h) => s + (h.hours || 0), 0);
    const pKm = mileage.filter(m => m.partner === partner).reduce((s, m) => s + (m.km || 0), 0);
    const pStort = mutations.filter(m => m.partner === partner && m.type === "storting").reduce((s, m) => s + (m.amount || 0), 0);
    const pOntt = mutations.filter(m => m.partner === partner && m.type === "onttrekking").reduce((s, m) => s + (m.amount || 0), 0);
    const winst = yearProfit / 2;
    const lines = [
      `IB EXPORT — ${partner.toUpperCase()} — ${selYear}`, `Bedrijf: Dif3r3nt VOF`, `Gegenereerd: ${new Date().toLocaleDateString("nl-NL")}`, ``,
      `=== WINSTAANDEEL ===`, `VOF nettowinst: €${yearProfit.toFixed(2)}`, `Jouw aandeel (50%): €${winst.toFixed(2)}`, ``,
      `=== ZELFSTANDIGENAFTREK ===`, `Geregistreerde uren: ${pHours}h`, `Drempel: ${ZELFST_MIN}h`, `Status: ${pHours >= ZELFST_MIN ? "✓ KWALIFICATIE BEHAALD" : "✗ " + Math.max(0, ZELFST_MIN - pHours) + "h tekort"}`, ``,
      `=== KM AFTREK ===`, `Zakelijke kilometers: ${pKm} km`, `Aftrekbedrag (€${KM_RATE}/km): €${(pKm * KM_RATE).toFixed(2)}`, ``,
      `=== PRIVÉ MUTATIES ===`, `Stortingen: €${pStort.toFixed(2)}`, `Onttrekkingen: €${pOntt.toFixed(2)}`, `Netto saldo: €${(pStort - pOntt).toFixed(2)}`, ``,
      `=== UREN DETAIL ===`,
      ...hours.filter(h => h.partner === partner).map(h => `${h.log_date} | ${h.hours}u | ${h.category} | ${h.description || "—"}`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `IB_Export_${partner}_${selYear}.txt`; a.click();
  }

  const infoRow = (k, v, accent) => (
    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid " + C.border }}>
      <span style={{ fontSize: 12, color: C.muted }}>{k}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: accent || C.text }}>{v}</span>
    </div>
  );
  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace" }}>{toast}</div>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ ...KOP_STIJL, marginBottom: 2 }}>Administratie & Belasting</h2>
          <p style={{ fontSize: 11, color: C.muted }}>Dif3r3nt VOF · KvK · BTW-plichtig</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={selYear} onChange={e => setSelYear(parseInt(e.target.value))} style={{ fontSize: 12, padding: "5px 8px" }}>
            {[curYear, curYear - 1, curYear - 2].map(y => <option key={y}>{y}</option>)}
          </select>
          {flaggedDocs.length > 0 && <Bdg label={flaggedDocs.length + " ⚑ te controleren"} bg={C.yellowDim} fg={C.yellow} />}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
        {["inname", "transacties", "boekhouder", "btw", "partners", "ev", "export"].map(t => (
          <span key={t} className={"tab" + (tab === t ? " on" : "")} onClick={() => setTab(t)}
            style={t === "export" ? { marginLeft: "auto" } : {}}>
            {t === "inname" ? "Inname" : t === "transacties" ? "Transacties" : t === "boekhouder" ? "Boekhouder" : t === "btw" ? "BTW" : t === "partners" ? "Partners" : t === "ev" ? "EV Ritten" : "Export"}
          </span>
        ))}
      </div>

      {/* ── INNAME ── */}
      {tab === "inname" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {/* Upload */}
          <div>
            {!extracted && !extracting && (
              <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
                onClick={() => fileRef.current?.click()}
                style={{ border: "2px dashed " + (dragOver ? C.green : C.border), borderRadius: 10, padding: "32px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? C.greenDim : "transparent", transition: "all .15s", marginBottom: 14 }}>
                <p style={{ fontSize: 28, marginBottom: 8, color: C.dim }}>◇</p>
                <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Sleep een document hierheen</p>
                <p style={{ fontSize: 12, color: C.muted }}>JPG, PNG, PDF — facturen, bonnen, bankafschriften</p>
                <input ref={fileRef} type="file" accept="image/*,.pdf,.json" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) processFile(f); e.target.value = ""; }} />
              </div>
            )}
            {extracting && (
              <div style={{ border: "1px solid " + C.green + "44", borderRadius: 10, padding: 24, textAlign: "center", background: C.greenDim, marginBottom: 14 }}>
                <Dot /><p style={{ fontSize: 13, color: C.green, marginTop: 8, fontFamily: "monospace" }}>AI analyseert document...</p>
                <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Gegevens worden geëxtraheerd</p>
              </div>
            )}
            {extracted && (
              <div className="card" style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <p className="lbl">Geëxtraheerde gegevens — controleer</p>
                  <button className="btn bg" style={{ fontSize: 10 }} onClick={() => setExtracted(null)}>✕</button>
                </div>
                {extracted.file_url && (
                  <div style={{ marginBottom: 12 }}>
                    <DocFileViewer file_url={extracted.file_url} file_type={extracted.file_type} file_name={extracted.file_name} mime_type={extracted.mime_type} maxH="38vh" />
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                  <Fld label="Document type">
                    <select value={extracted.doc_type || ""} onChange={e => setExtracted(p => ({ ...p, doc_type: e.target.value }))}>
                      {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Fld>
                  <Fld label="Datum">
                    <input type="date" value={extracted.doc_date || ""} onChange={e => setExtracted(p => ({ ...p, doc_date: e.target.value }))} />
                  </Fld>
                  <Fld label="Leverancier / Klant">
                    <input value={extracted.supplier || extracted.customer || ""} onChange={e => setExtracted(p => ({ ...p, supplier: e.target.value }))} placeholder="Naam" />
                  </Fld>
                  <Fld label="Omschrijving">
                    <input value={extracted.description || ""} onChange={e => setExtracted(p => ({ ...p, description: e.target.value }))} placeholder="Wat is het?" />
                  </Fld>
                  <Fld label="Bedrag excl. BTW (€)">
                    <input type="number" step="0.01" value={extracted.amount_excl || ""} onChange={e => setExtracted(p => ({ ...p, amount_excl: e.target.value }))} />
                  </Fld>
                  <Fld label="BTW %">
                    <select value={extracted.vat_rate ?? 21} onChange={e => setExtracted(p => ({ ...p, vat_rate: parseFloat(e.target.value) }))}>
                      {[0, 9, 21].map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </Fld>
                  <Fld label="BTW bedrag (€)">
                    <input type="number" step="0.01" value={extracted.vat_amount || ""} onChange={e => setExtracted(p => ({ ...p, vat_amount: e.target.value }))} />
                  </Fld>
                  <Fld label="Categorie">
                    <select value={extracted.category || "Overig"} onChange={e => setExtracted(p => ({ ...p, category: e.target.value }))}>
                      {EXPENSE_CATS.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </Fld>
                </div>
                <Fld label="Betalingsmethode">
                  <div style={{ display: "flex", gap: 6 }}>
                    {["bank", "iDEAL", "cash", "creditcard"].map(m => (
                      <button key={m} onClick={() => setExtracted(p => ({ ...p, payment_method: m }))} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid " + (extracted.payment_method === m ? C.green : C.border), background: extracted.payment_method === m ? C.greenDim : "transparent", color: extracted.payment_method === m ? C.green : C.muted, fontSize: 11, cursor: "pointer" }}>{m}</button>
                    ))}
                  </div>
                </Fld>
                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <button className="btn bp" style={{ flex: 1 }} disabled={saving} onClick={saveDoc}>{saving ? "Opslaan..." : "→ Opslaan & Verwerken"}</button>
                  <button className="btn bg" onClick={() => setExtracted(null)}>Annuleer</button>
                </div>
              </div>
            )}
            {!extracted && !extracting && <button className="btn bg" style={{ width: "100%", fontSize: 12 }} onClick={() => fileRef.current?.click()}>+ Document uploaden</button>}
          </div>

          {/* Recent docs */}
          <div>
            <p className="lbl" style={{ marginBottom: 10 }}>Recente documenten {selYear}</p>
            {docs.length === 0 && <p style={{ fontSize: 12, color: C.dim, padding: "16px 0", textAlign: "center" }}>Nog geen documenten dit jaar.</p>}
            {docs.slice(0, 12).map(d => {
              const sc = d.doc_type === "factuur_verkoop" ? C.green : d.doc_type === "factuur_inkoop" ? C.blue : C.muted;
              return (
                <div key={d.id} onClick={() => setEditDoc({ ...d })} style={{ display: "flex", gap: 10, padding: "9px 7px", borderBottom: "1px solid " + C.border, alignItems: "flex-start", cursor: "pointer", borderRadius: 5, transition: "background .12s" }} onMouseOver={e => e.currentTarget.style.background = C.surfaceHigh} onMouseOut={e => e.currentTarget.style.background = "transparent"} title={d.flagged ? "Klik om te controleren en bij te werken" : "Klik om te bewerken"}>
                  <div style={{ width: 3, minHeight: 36, borderRadius: 2, background: d.flagged ? C.yellow : sc, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.description || d.supplier || d.customer || d.file_name || "—"}</span>
                      {d.amount_excl && <span style={{ fontFamily: "monospace", fontSize: 12, color: sc, flexShrink: 0, marginLeft: 8 }}>€{d.amount_excl.toFixed(2)}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 3, alignItems: "center" }}>
                      <Bdg label={DOC_TYPE_LABELS[d.doc_type] || d.doc_type || "?"} bg={sc + "20"} fg={sc} />
                      {d.flagged && <Bdg label="⚑ controleer" bg={C.yellowDim} fg={C.yellow} />}
                      {d.payment_status === "betaald" && <Bdg label="✓ betaald" bg={C.greenDim} fg={C.green} />}
                      {d.payment_status === "pending" && <Bdg label="betaallink" bg={C.cyanDim} fg={C.cyan} />}
                      <span style={{ fontSize: 10, color: C.dim }}>{d.doc_date || "?"}</span>
                      <span style={{ fontSize: 10, color: C.dim, marginLeft: "auto" }}>klik →</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TRANSACTIES ── */}
      {tab === "boekhouder" && <Boekhouder selYear={selYear} trans={trans} meKey={meKey} showToast={showToast} />}

      {tab === "transacties" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
            {[["Omzet " + selYear, "€" + yearIncome.toFixed(2), C.green], ["Kosten " + selYear, "€" + yearExpense.toFixed(2), C.red], ["Nettowinst", "€" + yearProfit.toFixed(2), yearProfit >= 0 ? C.green : C.red], ["Documenten", docs.length + " stuks", C.blue]].map(([l, v, c]) => (
              <div key={l} className="card" style={{ padding: "12px 14px" }}>
                <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 5 }}>{l.toUpperCase()}</p>
                <p style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: c }}>{v}</p>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <p className="lbl">Alle transacties {selYear}</p>
            <button className="btn bp" style={{ fontSize: 11 }} onClick={() => setNewTrans(true)}>+ Handmatig toevoegen</button>
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div className="row" style={{ gridTemplateColumns: "100px 1fr 1fr 80px 90px 90px", background: C.bg }}>
              {["Datum", "Omschrijving", "Categorie", "Type", "Excl. BTW", "BTW"].map(h => <span key={h} className="lbl">{h}</span>)}
            </div>
            {trans.length === 0 && <p style={{ padding: 16, fontSize: 12, color: C.dim, textAlign: "center" }}>Geen transacties — upload een document.</p>}
            {trans.map(t => {
              const isIn = t.type === "income";
              return (
                <div key={t.id} className="row" style={{ gridTemplateColumns: "100px 1fr 1fr 80px 90px 90px" }}>
                  <span style={{ fontSize: 12, color: C.muted }}>{t.trans_date}</span>
                  <span style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.description || "—"}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{t.category || "—"}</span>
                  <Bdg label={isIn ? "Inkomen" : "Kosten"} bg={isIn ? C.greenDim : C.redDim} fg={isIn ? C.green : C.red} />
                  <span style={{ fontFamily: "monospace", fontSize: 13, color: isIn ? C.green : C.red }}>{isIn ? "+" : "−"}€{(t.amount_excl || 0).toFixed(2)}</span>
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted }}>€{(t.vat_amount || 0).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── BTW ── */}
      {tab === "btw" && (
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
            <p className="lbl">Kwartaal:</p>
            {["Q1", "Q2", "Q3", "Q4"].map(q => (
              <button key={q} onClick={() => setSelQ(q)} style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid " + (selQ === q ? C.green : C.border), background: selQ === q ? C.greenDim : "transparent", color: selQ === q ? C.green : C.muted, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{q} {selYear}</button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div className="card">
              <p className="lbl" style={{ marginBottom: 12, color: C.green }}>BTW Verschuldigd (omzet)</p>
              {[["Omzet excl. BTW", "€" + qIncome.toFixed(2), C.green], ["BTW over omzet", "€" + qVatOut.toFixed(2), C.green], ["Tarieven", qTrans.filter(t => t.type === "income").map(t => t.vat_rate + "%").filter((v, i, a) => a.indexOf(v) === i).join(", ") || "—", C.muted]].map(([k, v, c]) => infoRow(k, v, c))}
            </div>
            <div className="card">
              <p className="lbl" style={{ marginBottom: 12, color: C.blue }}>Voorbelasting (inkoop)</p>
              {[["Kosten excl. BTW", "€" + qExpense.toFixed(2), C.red], ["BTW voorbelasting", "€" + qVatIn.toFixed(2), C.blue], ["Documenten", qTrans.filter(t => t.type === "expense").length + " facturen/bonnen", C.muted]].map(([k, v, c]) => infoRow(k, v, c))}
            </div>
          </div>
          <div className="card" style={{ background: qVatSaldo > 0 ? C.redDim : C.greenDim, border: "1px solid " + (qVatSaldo > 0 ? "rgba(229,86,78,.25)" : "rgba(52,189,130,.2)") }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontSize: 11, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 4 }}>{selQ} {selYear} — BTW SALDO</p>
                <p style={{ fontSize: 26, fontWeight: 700, fontFamily: "monospace", color: qVatSaldo > 0 ? C.red : C.green }}>€{Math.abs(qVatSaldo).toFixed(2)}</p>
                <p style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{qVatSaldo > 0 ? "Te betalen aan Belastingdienst" : "Terug te vorderen van Belastingdienst"}</p>
              </div>
              <button className="btn bp" onClick={exportBTW} style={{ fontSize: 12 }}>↓ BTW Export {selQ}</button>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <p className="lbl" style={{ marginBottom: 10 }}>Transacties {selQ} {selYear}</p>
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div className="row" style={{ gridTemplateColumns: "100px 1fr 80px 90px 90px", background: C.bg }}>
                {["Datum", "Omschrijving", "Type", "Excl. BTW", "BTW"].map(h => <span key={h} className="lbl">{h}</span>)}
              </div>
              {qTrans.length === 0 && <p style={{ padding: 14, fontSize: 12, color: C.dim, textAlign: "center" }}>Geen transacties in dit kwartaal.</p>}
              {qTrans.map(t => {
                const isIn = t.type === "income";
                return (
                  <div key={t.id} className="row" style={{ gridTemplateColumns: "100px 1fr 80px 90px 90px" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{t.trans_date}</span>
                    <span style={{ fontSize: 13 }}>{t.description || "—"}</span>
                    <Bdg label={isIn ? "Omzet" : "Inkoop"} bg={isIn ? C.greenDim : C.blueDim} fg={isIn ? C.green : C.blue} />
                    <span style={{ fontFamily: "monospace", fontSize: 13, color: isIn ? C.green : C.red }}>{isIn ? "+" : "−"}€{(t.amount_excl || 0).toFixed(2)}</span>
                    <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted }}>€{(t.vat_amount || 0).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── PARTNERS ── */}
      {tab === "partners" && (
        <div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
            {["furgel", "tristan"].map(p => (
              <button key={p} onClick={() => setSelPartner(p)} style={{ padding: "6px 18px", borderRadius: 6, border: "1px solid " + (selPartner === p ? C.green : C.border), background: selPartner === p ? C.greenDim : "transparent", color: selPartner === p ? C.green : C.muted, cursor: "pointer", fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>{p}</button>
            ))}
            <div style={{ display: "flex", gap: 6, marginLeft: 16 }}>
              {["uren", "km", "mutaties"].map(t => (
                <span key={t} className={"tab" + (partnerTab === t ? " on" : "")} onClick={() => setPartnerTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
              ))}
            </div>
          </div>

          {partnerTab === "uren" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                {[["Uren " + selYear, partnerHours.toFixed(1) + "h", C.blue], ["Drempel zelfst.", ZELFST_MIN + "h", C.muted], ["Status", partnerHours >= ZELFST_MIN ? "✓ Behaald" : (ZELFST_MIN - partnerHours).toFixed(0) + "h tekort", partnerHours >= ZELFST_MIN ? C.green : C.yellow]].map(([l, v, c]) => (
                  <div key={l} className="card" style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 5 }}>{l.toUpperCase()}</p>
                    <p style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: c }}>{v}</p>
                  </div>
                ))}
              </div>
              <Bar val={Math.min(100, Math.round(partnerHours / ZELFST_MIN * 100))} color={partnerHours >= ZELFST_MIN ? C.green : C.yellow} />
              <p style={{ fontSize: 11, color: C.muted, marginTop: 4, marginBottom: 12 }}>{Math.min(100, Math.round(partnerHours / ZELFST_MIN * 100))}% naar zelfstandigenaftrek</p>
              {selPartner === meKey && (
                <div className="card" style={{ padding: "12px 14px", marginBottom: 12, background: C.blueDim, border: "1px solid " + C.blue + "33" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 9 }}>
                    <p className="lbl" style={{ margin: 0, color: C.blue }}>Snel loggen — vandaag</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: C.muted }}>Categorie</span>
                      <select value={quickCat} onChange={e => setQuickCat(e.target.value)} style={{ fontSize: 11, padding: "3px 8px", width: "auto" }}>{HOUR_CATS.map(c => <option key={c}>{c}</option>)}</select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                    {[["½ dag", 4], ["Hele dag", 8], ["+1u", 1], ["+2u", 2], ["+4u", 4]].map(([lbl, n]) => (
                      <button key={lbl} className="btn bp" disabled={quickBusy} style={{ fontSize: 12, opacity: quickBusy ? .5 : 1 }} onClick={() => quickLogHours(n)}>{quickBusy ? "…" : lbl}</button>
                    ))}
                  </div>
                  <p style={{ fontSize: 10, color: C.dim, marginTop: 8 }}>Eén klik = {new Date().toLocaleDateString("nl-NL", { day: "numeric", month: "long" })} geregistreerd op jouw naam ({meName}). Andere datum of omschrijving? Gebruik "+ Uren toevoegen".</p>
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <p className="lbl">Urenregistratie</p>
                <button className="btn bp" style={{ fontSize: 11 }} onClick={() => { setEditHourId(null); setNhReeks(REEKS_NIEUW); setNh({ log_date: new Date().toISOString().split("T")[0], partner: meKey, hours: "", category: "Development", description: "" }); setNewHour(true); }}>+ Uren toevoegen</button>
              </div>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="row" style={{ gridTemplateColumns: "100px 60px 1fr 1fr 76px", background: C.bg }}>
                  {["Datum", "Uren", "Categorie", "Omschrijving", ""].map((h, i) => <span key={i} className="lbl">{h}</span>)}
                </div>
                {hours.filter(h => h.partner === selPartner).length === 0 && <p style={{ padding: 14, fontSize: 12, color: C.dim, textAlign: "center" }}>Nog geen uren geregistreerd.</p>}
                {hours.filter(h => h.partner === selPartner).map(h => (
                  <div key={h.id} className="row" style={{ gridTemplateColumns: "100px 60px 1fr 1fr 76px" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{h.log_date}</span>
                    <span style={{ fontFamily: "monospace", color: C.blue, fontSize: 13 }}>{h.hours}u</span>
                    <Bdg label={h.category || "—"} bg={C.blueDim} fg={C.blue} />
                    <span style={{ fontSize: 12, color: C.muted }}>{h.description || "—"}</span>
                    {h.partner === meKey ? (
                      <span style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Wijzigen" onClick={() => { setNh({ log_date: h.log_date, partner: h.partner, hours: String(h.hours), category: h.category || "Development", description: h.description || "" }); setEditHourId(h.id); setNewHour(true); }} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 4, color: C.muted, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✎</button>
                        <button title="Verwijderen" onClick={async () => { if (!window.confirm("Deze uren-registratie (" + h.log_date + ", " + h.hours + "u) verwijderen?")) return; const { error } = await supabase.from("hour_logs").delete().eq("id", h.id); if (error) { showToast("✗ Verwijderen mislukt"); return; } let weg = new Set([h.id]); if (h.recur_id) { const latere = hours.filter(x => x.recur_id === h.recur_id && x.id !== h.id && x.log_date > h.log_date); if (latere.length > 0 && window.confirm("Deze uren horen bij een reeks. Ook de " + latere.length + " latere registraties uit deze reeks verwijderen?")) { const { error: e2 } = await supabase.from("hour_logs").delete().eq("recur_id", h.recur_id).gt("log_date", h.log_date); if (!e2) latere.forEach(x => weg.add(x.id)); else showToast("✗ Latere registraties verwijderen mislukt"); } } setHours(p => p.filter(x => !weg.has(x.id))); showToast("✓ Uren verwijderd"); }} style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✕</button>
                      </span>
                    ) : <span />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {partnerTab === "km" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                {[["Km " + selYear, partnerKm.toFixed(0) + " km", C.cyan], ["Aftrekbedrag", "€" + partnerKmDeduction.toFixed(2), C.green], ["Tarief", "€" + KM_RATE + "/km", C.muted]].map(([l, v, c]) => (
                  <div key={l} className="card" style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 5 }}>{l.toUpperCase()}</p>
                    <p style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: c }}>{v}</p>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <p className="lbl">KM Registratie</p>
                <button className="btn bp" style={{ fontSize: 11 }} onClick={() => { setEditKmId(null); setNk({ log_date: new Date().toISOString().split("T")[0], partner: meKey, from_loc: "", to_loc: "", purpose: "", km: "" }); setNewKm(true); }}>+ Rit toevoegen</button>
              </div>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="row" style={{ gridTemplateColumns: "100px 1fr 1fr 1fr 60px 76px", background: C.bg }}>
                  {["Datum", "Van", "Naar", "Doel", "KM", ""].map((h, i) => <span key={i} className="lbl">{h}</span>)}
                </div>
                {mileage.filter(m => m.partner === selPartner).length === 0 && <p style={{ padding: 14, fontSize: 12, color: C.dim, textAlign: "center" }}>Nog geen ritten geregistreerd.</p>}
                {mileage.filter(m => m.partner === selPartner).map(m => (
                  <div key={m.id} className="row" style={{ gridTemplateColumns: "100px 1fr 1fr 1fr 60px 76px" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{m.log_date}</span>
                    <span style={{ fontSize: 12 }}>{m.from_loc || "—"}</span>
                    <span style={{ fontSize: 12 }}>{m.to_loc || "—"}</span>
                    <span style={{ fontSize: 12, color: C.muted }}>{m.purpose || "—"}</span>
                    <span style={{ fontFamily: "monospace", color: C.cyan, fontSize: 13 }}>{m.km}</span>
                    {m.partner === meKey ? (
                      <span style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Wijzigen" onClick={() => { setNk({ log_date: m.log_date, partner: m.partner, from_loc: m.from_loc || "", to_loc: m.to_loc || "", purpose: m.purpose || "", km: String(m.km) }); setEditKmId(m.id); setNewKm(true); }} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 4, color: C.muted, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✎</button>
                        <button title="Verwijderen" onClick={async () => { if (!window.confirm("Deze rit (" + m.log_date + ", " + m.km + " km) verwijderen?")) return; const { error } = await supabase.from("mileage_logs").delete().eq("id", m.id); if (!error) { setMileage(p => p.filter(x => x.id !== m.id)); showToast("✓ Rit verwijderd"); } else showToast("✗ Verwijderen mislukt"); }} style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✕</button>
                      </span>
                    ) : <span />}
                  </div>
                ))}
              </div>
            </div>
          )}

          {partnerTab === "mutaties" && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
                {[["Stortingen", "€" + stortingen.toFixed(2), C.green], ["Onttrekkingen", "€" + onttrekkingen.toFixed(2), C.red], ["Netto saldo", "€" + (stortingen - onttrekkingen).toFixed(2), (stortingen - onttrekkingen) >= 0 ? C.green : C.red]].map(([l, v, c]) => (
                  <div key={l} className="card" style={{ padding: "12px 14px" }}>
                    <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 5 }}>{l.toUpperCase()}</p>
                    <p style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: c }}>{v}</p>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <p className="lbl">Privé mutaties</p>
                <button className="btn bp" style={{ fontSize: 11 }} onClick={() => { setEditMutId(null); setNm({ mut_date: new Date().toISOString().split("T")[0], partner: meKey, type: "storting", amount: "", description: "" }); setNewMut(true); }}>+ Mutatie toevoegen</button>
              </div>
              <div className="card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="row" style={{ gridTemplateColumns: "100px 1fr 100px 100px 76px", background: C.bg }}>
                  {["Datum", "Omschrijving", "Type", "Bedrag", ""].map((h, i) => <span key={i} className="lbl">{h}</span>)}
                </div>
                {mutations.filter(m => m.partner === selPartner).length === 0 && <p style={{ padding: 14, fontSize: 12, color: C.dim, textAlign: "center" }}>Geen mutaties.</p>}
                {mutations.filter(m => m.partner === selPartner).map(m => (
                  <div key={m.id} className="row" style={{ gridTemplateColumns: "100px 1fr 100px 100px 76px" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{m.mut_date}</span>
                    <span style={{ fontSize: 13 }}>{m.description || "—"}</span>
                    <Bdg label={m.type} bg={m.type === "storting" ? C.greenDim : C.redDim} fg={m.type === "storting" ? C.green : C.red} />
                    <span style={{ fontFamily: "monospace", fontSize: 13, color: m.type === "storting" ? C.green : C.red }}>{m.type === "storting" ? "+" : "−"}€{(m.amount || 0).toFixed(2)}</span>
                    {m.partner === meKey ? (
                      <span style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                        <button title="Wijzigen" onClick={() => { setNm({ mut_date: m.mut_date, partner: m.partner, type: m.type, amount: String(m.amount), description: m.description || "" }); setEditMutId(m.id); setNewMut(true); }} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 4, color: C.muted, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✎</button>
                        <button title="Verwijderen" onClick={async () => { if (!window.confirm("Deze mutatie (" + m.mut_date + ", €" + (m.amount || 0).toFixed(2) + ") verwijderen?")) return; const { error } = await supabase.from("partner_mutations").delete().eq("id", m.id); if (!error) { setMutations(p => p.filter(x => x.id !== m.id)); showToast("✓ Mutatie verwijderd"); } else showToast("✗ Verwijderen mislukt"); }} style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✕</button>
                      </span>
                    ) : <span />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── EV RITTEN ── */}
      {tab === "ev" && (() => {
        const evYear = evTrips.filter(t => t.year === selYear);
        const totalKm = evYear.reduce((s, t) => s + (t.distance_km || 0), 0);
        const totalCost = evYear.reduce((s, t) => s + (t.cost_per_trip || 0), 0);
        const zakKm = evYear.filter(t => t.purpose === "zakelijk" || t.purpose === "gemengd").reduce((s, t) => s + (t.distance_km || 0), 0);
        const avgCostKm = totalKm > 0 ? totalCost / totalKm : 0;
        const selVeh = evVehicles.find(v => v.id === ntEv.vehicle_id);
        return (
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <p className="lbl" style={{ marginBottom: 8 }}>Locatie op kaart</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input value={admLoc} onChange={e => setAdmLoc(e.target.value)} onKeyDown={e => { if (e.key === "Enter") setAdmLocShow(admLoc.trim()); }} placeholder="Typ een adres — bijv. Keizersgracht 123, Amsterdam" style={{ flex: 1, minWidth: 220 }} />
                <button className="btn bp" style={{ fontSize: 12 }} onClick={() => setAdmLocShow(admLoc.trim())}>Toon op kaart</button>
              </div>
              {admLocShow && <div style={{ marginTop: 10 }}><AddressMap query={admLocShow + ", Nederland"} height={200} /></div>}
            </div>
            {/* KPI strip */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
              {[["Totaal km " + selYear, totalKm.toFixed(1) + " km", C.blue], ["Zakelijk km", zakKm.toFixed(1) + " km", C.green], ["EV Kosten", "€" + totalCost.toFixed(2), C.yellow], ["Gem. €/km", "€" + avgCostKm.toFixed(3), C.muted]].map(([l, v, c]) => (
                <div key={l} className="card" style={{ padding: "12px 14px" }}>
                  <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 5 }}>{l.toUpperCase()}</p>
                  <p style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: c }}>{v}</p>
                </div>
              ))}
            </div>

            {/* Header + actions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <p className="lbl">EV Ritregistratie {selYear}</p>
                {evVehicles.length === 0 && <Bdg label="Voeg eerst een voertuig toe" bg={C.yellowDim} fg={C.yellow} />}
              </div>
              <div style={{ display: "flex", gap: 7 }}>
                <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setNewVehicleModal(true)}>+ Voertuig</button>
                <button className="btn bg" style={{ fontSize: 11 }} onClick={exportEvTrips}>↓ Export</button>
                <button className="btn bp" style={{ fontSize: 11 }} onClick={() => { setNewTrip(true); setTripStep(1); setTripGeo(null); setRouteCalc({ loading: false, done: false, error: null }); }}>+ Rit registreren</button>
              </div>
            </div>

            {/* Vehicles strip */}
            {evVehicles.length > 0 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                {evVehicles.map(v => (
                  <div key={v.id} style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: "7px 12px", fontSize: 12 }}>
                    <span style={{ color: C.green, marginRight: 6 }}></span>
                    <strong>{v.name}</strong>
                    <span style={{ color: C.muted, marginLeft: 8 }}>{v.kwh_per_100km} kWh/100km</span>
                    <span style={{ color: C.dim, marginLeft: 8, fontSize: 11 }}>{v.owner}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Trips table */}
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div className="row" style={{ gridTemplateColumns: "90px 80px 1fr 1fr 60px 90px 65px 70px 32px", background: C.bg }}>
                {["Datum", "Bestuurder", "Van", "Naar", "km", "Categorie", "kWh", "Kosten", ""].map(h => <span key={h} className="lbl">{h}</span>)}
              </div>
              {evYear.length === 0 && <p style={{ padding: 16, fontSize: 12, color: C.dim, textAlign: "center" }}>Nog geen ritten geregistreerd voor {selYear}.</p>}
              {evYear.map(trip => {
                const isExp = expandedTrip === trip.id;
                return (
                  <div key={trip.id}>
                    <div className="row" style={{ gridTemplateColumns: "90px 80px 1fr 1fr 80px 90px 65px 70px 32px", cursor: "pointer" }}
                      onClick={() => {
                        if (isExp) { setExpandedTrip(null); }
                        else { setExpandedTrip(trip.id); fetchTripRoute(trip); }
                      }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{trip.trip_date}</span>
                      <span style={{ fontSize: 12, textTransform: "capitalize" }}>{trip.driver}</span>
                      <span style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={trip.start_location}>{trip.start_location}</span>
                      <span style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={trip.roundtrip ? `↩ retour: ${trip.end_location}` : trip.end_location}>
                        {trip.roundtrip && <span style={{ color: C.blue, marginRight: 4, fontSize: 11 }}>↩</span>}
                        {trip.end_location}
                      </span>
                      <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {(trip.distance_km || 0).toFixed(1)}
                        {trip.roundtrip && <span style={{ fontSize: 10, color: C.blue, marginLeft: 3 }}>v/t</span>}
                      </span>
                      <Bdg label={PURPOSE_LABELS[trip.purpose] || trip.purpose} bg={(PURPOSE_COL[trip.purpose] || C.muted) + "22"} fg={PURPOSE_COL[trip.purpose] || C.muted} />
                      <span style={{ fontFamily: "monospace", fontSize: 12, color: C.muted }}>{(trip.total_kwh || 0).toFixed(1)}</span>
                      <span style={{ fontFamily: "monospace", fontSize: 12, color: C.yellow }}>€{(trip.cost_per_trip || 0).toFixed(2)}</span>
                      <button onClick={e => { e.stopPropagation(); deleteEvTrip(trip.id); }} style={{ background: "none", border: "1px solid " + C.red, borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 6px" }}></button>
                    </div>
                    {isExp && (
                      <div style={{ padding: "12px 16px", background: C.bg, borderTop: "1px solid " + C.border }}>
                        {trip.start_lat && trip.end_lat ? (
                          <EvRouteMap startLat={trip.start_lat} startLng={trip.start_lng} endLat={trip.end_lat} endLng={trip.end_lng} routeGeometry={tripRouteCache[trip.id] || null} height={200} />
                        ) : <p style={{ fontSize: 12, color: C.dim }}>Geen coördinaten beschikbaar voor deze rit.</p>}
                        <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 12, color: C.muted }}>
                          <span>{trip.start_location} → {trip.end_location}</span>
                          {trip.description && <span>{trip.description}</span>}
                          {trip.client_project && <span>{trip.client_project}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Per driver summary */}
            {evYear.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
                {["furgel", "tristan"].map(driver => {
                  const dTrips = evYear.filter(t => t.driver === driver);
                  const dKm = dTrips.reduce((s, t) => s + (t.distance_km || 0), 0);
                  const dCost = dTrips.reduce((s, t) => s + (t.cost_per_trip || 0), 0);
                  return (
                    <div key={driver} className="card" style={{ padding: "12px 14px" }}>
                      <p className="lbl" style={{ marginBottom: 10, textTransform: "capitalize" }}>{driver}</p>
                      {["zakelijk", "prive", "woon_werk", "gemengd"].map(pur => {
                        const pt = dTrips.filter(t => t.purpose === pur);
                        const pkm = pt.reduce((s, t) => s + (t.distance_km || 0), 0);
                        if (!pt.length) return null;
                        return <div key={pur} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: PURPOSE_COL[pur] }}>{PURPOSE_LABELS[pur]}</span>
                          <span style={{ fontFamily: "monospace", color: C.muted }}>{pkm.toFixed(1)} km · {pt.length}×</span>
                        </div>;
                      })}
                      <div style={{ borderTop: "1px solid " + C.border, marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12, color: C.muted }}>Totaal</span>
                        <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 700 }}>{dKm.toFixed(1)} km · €{dCost.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── EV ADD TRIP MODAL ── */}
      {newTrip && (
        <Portal><div className="ovl" onClick={() => setNewTrip(false)}>
          <div className="mdl" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14 }}>+ EV RIT REGISTREREN</h3>
              <div style={{ display: "flex", gap: 6 }}>
                <span style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, fontFamily: "monospace", background: tripStep === 1 ? C.greenDim : "transparent", color: tripStep === 1 ? C.green : C.dim, border: "1px solid " + (tripStep === 1 ? C.green : C.border) }}>1 Route</span>
                <span style={{ padding: "3px 10px", borderRadius: 4, fontSize: 11, fontFamily: "monospace", background: tripStep === 2 ? C.greenDim : "transparent", color: tripStep === 2 ? C.green : C.dim, border: "1px solid " + (tripStep === 2 ? C.green : C.border) }}>2 Details</span>
              </div>
            </div>

            {tripStep === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Fld label="Datum *"><input type="date" value={ntEv.trip_date} onChange={e => setNtEv(p => ({ ...p, trip_date: e.target.value }))} /></Fld>
                  <Fld label="Bestuurder (vergrendeld)">
                    <input value={meName} disabled style={{ opacity: .7, cursor: "not-allowed" }} title="Je kunt alleen je eigen ritten registreren" />
                  </Fld>
                </div>
                <Fld label="Voertuig">
                  <div style={{ display: "flex", gap: 6 }}>
                    <select value={ntEv.vehicle_id} style={{ flex: 1 }} onChange={e => {
                      const v = evVehicles.find(x => x.id === e.target.value);
                      setNtEv(p => ({ ...p, vehicle_id: e.target.value, kwh_per_100km: v ? v.kwh_per_100km.toString() : p.kwh_per_100km }));
                    }}>
                      <option value="">— Kies voertuig —</option>
                      {evVehicles.map(v => <option key={v.id} value={v.id}>{v.name} ({v.kwh_per_100km} kWh/100km)</option>)}
                    </select>
                    <button className="btn bg" style={{ fontSize: 11, padding: "6px 10px", flexShrink: 0 }} onClick={() => setNewVehicleModal(true)}>+ Nieuw</button>
                  </div>
                </Fld>
                <Fld label="Startlocatie *"><input value={ntEv.start_location} onChange={e => setNtEv(p => ({ ...p, start_location: e.target.value }))} placeholder="bijv. Keizersgracht 123, Amsterdam" /></Fld>
                <Fld label="Eindlocatie *"><input value={ntEv.end_location} onChange={e => setNtEv(p => ({ ...p, end_location: e.target.value }))} placeholder="bijv. Beatrixlaan 5, Den Haag" /></Fld>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={ntEv.roundtrip} onChange={e => setNtEv(p => ({ ...p, roundtrip: e.target.checked }))} style={{ width: "auto" }} />
                  Retourrit (km × 2)
                </label>
                {routeCalc.error && <p style={{ fontSize: 12, color: C.red, background: C.redDim, padding: "8px 11px", borderRadius: 5 }}>⚠ {routeCalc.error}</p>}
                {routeCalc.done && tripGeo && (
                  <div style={{ background: C.greenDim, border: "1px solid rgba(52,189,130,.2)", borderRadius: 6, padding: "10px 13px" }}>
                    <p style={{ fontSize: 12, color: C.green, fontFamily: "monospace", marginBottom: 4 }}>✓ Route berekend</p>
                    <p style={{ fontSize: 13 }}><strong>{tripGeo.distance_km} km</strong> · {tripGeo.duration_min} min{ntEv.roundtrip ? " (retour)" : ""}</p>
                    <div style={{ marginTop: 10 }}>
                      <EvRouteMap startLat={tripGeo.startLat} startLng={tripGeo.startLng} endLat={tripGeo.endLat} endLng={tripGeo.endLng} routeGeometry={tripGeo.routeGeometry} height={220} />
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button className="btn bg" onClick={() => setNewTrip(false)}>Annuleer</button>
                  {routeCalc.done && tripGeo
                    ? <button className="btn bp" onClick={() => setTripStep(2)}>Volgende → Details</button>
                    : <button className="btn bp" disabled={routeCalc.loading || !ntEv.start_location || !ntEv.end_location} onClick={geocodeAndRoute}>
                      {routeCalc.loading ? "Berekenen..." : "↗ Bereken Route"}
                    </button>
                  }
                </div>
              </div>
            )}

            {tripStep === 2 && tripGeo && (() => {
              const kwh100 = parseFloat(ntEv.kwh_per_100km) || 18;
              const ePrijs = parseFloat(ntEv.electricity_price_kwh) || EV_ELEC_PRICE;
              const km = tripGeo.distance_km;
              const totalKwh = Math.round(km * (kwh100 / 100) * 100) / 100;
              const costTrip = Math.round(totalKwh * ePrijs * 100) / 100;
              const costKm = km > 0 ? Math.round(costTrip / km * 1000) / 1000 : 0;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                  <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: "10px 13px", fontSize: 12 }}>
                    <span style={{ color: C.green }}>↗ </span>
                    <strong>{ntEv.start_location}</strong>
                    <span style={{ color: C.muted }}> → </span>
                    <strong>{ntEv.end_location}</strong>
                    <span style={{ color: C.muted, marginLeft: 10 }}>{tripGeo.distance_km} km · {tripGeo.duration_min} min</span>
                    <button onClick={() => setTripStep(1)} style={{ marginLeft: 10, background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 11 }}>✎ Aanpassen</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <Fld label="Categorie *">
                      <select value={ntEv.purpose} onChange={e => setNtEv(p => ({ ...p, purpose: e.target.value }))}>
                        {Object.entries(PURPOSE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </Fld>
                    <Fld label="Klant / Project"><input value={ntEv.client_project} onChange={e => setNtEv(p => ({ ...p, client_project: e.target.value }))} placeholder="optioneel" /></Fld>
                    <Fld label="kWh / 100 km"><input type="number" step="0.5" value={ntEv.kwh_per_100km} onChange={e => setNtEv(p => ({ ...p, kwh_per_100km: e.target.value }))} placeholder="18" /></Fld>
                    <Fld label="Stroomprijs (€/kWh)"><input type="number" step="0.01" value={ntEv.electricity_price_kwh} onChange={e => setNtEv(p => ({ ...p, electricity_price_kwh: e.target.value }))} placeholder="0.32" /></Fld>
                  </div>
                  <Fld label="Reisdoel / omschrijving *"><input value={ntEv.description} onChange={e => setNtEv(p => ({ ...p, description: e.target.value }))} placeholder="bijv. Klantbezoek ACME BV — offerte bespreking" /></Fld>
                  <Fld label="Notities"><input value={ntEv.notes} onChange={e => setNtEv(p => ({ ...p, notes: e.target.value }))} placeholder="optioneel" /></Fld>
                  <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: "11px 13px" }}>
                    <p className="lbl" style={{ marginBottom: 9 }}>EV Kostenberekening</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      {[["Verbruik", totalKwh + " kWh", C.blue], ["Kosten rit", "€" + costTrip.toFixed(2), C.yellow], ["Per km", "€" + costKm.toFixed(3), C.muted]].map(([l, v, c]) => (
                        <div key={l} style={{ textAlign: "center" }}>
                          <p style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{l}</p>
                          <p style={{ fontFamily: "monospace", fontSize: 15, fontWeight: 700, color: c }}>{v}</p>
                        </div>
                      ))}
                    </div>
                    {(ntEv.purpose === "zakelijk" || ntEv.purpose === "gemengd") && (
                      <p style={{ fontSize: 11, color: C.green, marginTop: 8, textAlign: "center" }}>
                        ✓ {ntEv.purpose === "gemengd" ? "50% van €" + costTrip.toFixed(2) + " = €" + (costTrip * 0.5).toFixed(2) : "€" + costTrip.toFixed(2)} wordt als zakelijke kosten geboekt
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button className="btn bg" onClick={() => setTripStep(1)}>← Terug</button>
                    <button className="btn bg" onClick={() => setNewTrip(false)}>Annuleer</button>
                    <button className="btn bp" disabled={!ntEv.description.trim() || savingTrip} onClick={saveEvTrip}>{savingTrip ? "Opslaan..." : "→ Opslaan"}</button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div></Portal>
      )}

      {/* ── EV ADD VEHICLE MODAL ── */}
      {newVehicleModal && (
        <Portal><div className="ovl" onClick={() => setNewVehicleModal(false)}>
          <div className="mdl" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 16 }}>+ VOERTUIG TOEVOEGEN</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Fld label="Naam *"><input value={nvForm.name} onChange={e => setNvForm(p => ({ ...p, name: e.target.value }))} placeholder="bijv. Tesla Model 3 — Furgel" /></Fld>
                <Fld label="Eigenaar">
                  <select value={nvForm.owner} onChange={e => setNvForm(p => ({ ...p, owner: e.target.value }))}>
                    <option value="furgel">Furgel</option>
                  </select>
                </Fld>
                <Fld label="Merk"><input value={nvForm.brand} onChange={e => setNvForm(p => ({ ...p, brand: e.target.value }))} placeholder="Tesla" /></Fld>
                <Fld label="Model"><input value={nvForm.model} onChange={e => setNvForm(p => ({ ...p, model: e.target.value }))} placeholder="Model 3 Long Range" /></Fld>
                <Fld label="Kenteken"><input value={nvForm.license_plate} onChange={e => setNvForm(p => ({ ...p, license_plate: e.target.value }))} placeholder="AB-123-C" /></Fld>
                <Fld label="kWh / 100 km *"><input type="number" step="0.5" value={nvForm.kwh_per_100km} onChange={e => setNvForm(p => ({ ...p, kwh_per_100km: e.target.value }))} placeholder="18" /></Fld>
              </div>
              <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>Typisch verbruik: Tesla Model 3 ≈ 15–17 kWh, VW ID.4 ≈ 18–21 kWh, Hyundai Ioniq 6 ≈ 14–16 kWh</p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => setNewVehicleModal(false)}>Annuleer</button>
                <button className="btn bp" disabled={!nvForm.name.trim()} onClick={saveEvVehicle}>→ Opslaan</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}

      {/* ── EXPORT ── */}
      {tab === "export" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20 }}>
            {[["Omzet " + selYear, "€" + yearIncome.toFixed(2), C.green], ["Kosten " + selYear, "€" + yearExpense.toFixed(2), C.red], ["Nettowinst " + selYear, "€" + yearProfit.toFixed(2), yearProfit >= 0 ? C.green : C.red], ["Documenten", docs.length + " stuks", C.blue], ["Uren Furgel", hours.filter(h => h.partner === "furgel").reduce((s, h) => s + (h.hours || 0), 0).toFixed(1) + "h", C.blue], ["Uren Tristan", hours.filter(h => h.partner === "tristan").reduce((s, h) => s + (h.hours || 0), 0).toFixed(1) + "h", C.cyan]].map(([l, v, c]) => (
              <div key={l} className="card" style={{ padding: "12px 14px" }}>
                <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".07em", marginBottom: 5 }}>{l.toUpperCase()}</p>
                <p style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: c }}>{v}</p>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 3 }}>BTW Aangifte Export</p>
                <p style={{ fontSize: 12, color: C.muted }}>Kwartaaloverzicht met alle facturen, BTW-bedragen en saldo per kwartaal.</p>
              </div>
              <div style={{ display: "flex", gap: 7 }}>
                {["Q1", "Q2", "Q3", "Q4"].map(q => (
                  <button key={q} className="btn bp" style={{ fontSize: 11 }} onClick={() => { setSelQ(q); exportBTW(q); }}>{q}</button>
                ))}
              </div>
            </div>
            <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 3 }}>BTW-aangifte in Documenten opslaan</p>
                <p style={{ fontSize: 12, color: C.muted }}>Bewaart de aangifte mét de echte cijfers als document in de Documenten-module (werkt een bestaande aangifte van dat kwartaal bij).</p>
              </div>
              <div style={{ display: "flex", gap: 7 }}>
                {["Q1", "Q2", "Q3", "Q4"].map(q => (
                  <button key={q} className="btn bg" style={{ fontSize: 11 }} onClick={() => saveBTWtoDoc(q)}>{q}</button>
                ))}
              </div>
            </div>
            <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 3 }}>IB Export — Furgel</p>
                <p style={{ fontSize: 12, color: C.muted }}>Winstaandeel, uren, KM-aftrek en privémutaties voor inkomstenbelasting {selYear}.</p>
              </div>
              <button className="btn bp" style={{ fontSize: 12 }} onClick={() => exportIB("furgel")}>↓ Download</button>
            </div>
            <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 3 }}>IB Export — Tristan</p>
                <p style={{ fontSize: 12, color: C.muted }}>Winstaandeel, uren, KM-aftrek en privémutaties voor inkomstenbelasting {selYear}.</p>
              </div>
              <button className="btn bp" style={{ fontSize: 12 }} onClick={() => exportIB("tristan")}>↓ Download</button>
            </div>
            {/* EV Export */}
            <div className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontWeight: 600, marginBottom: 3 }}>EV Ritregistratie Export</p>
                <p style={{ fontSize: 12, color: C.muted }}>Alle ritten {selYear}: km per categorie, EV kosten, belastingdienst-audit log.</p>
                {evTrips.filter(t => t.year === selYear).length > 0 && (
                  <p style={{ fontSize: 11, color: C.green, marginTop: 3 }}>
                    {evTrips.filter(t => t.year === selYear).length} ritten · {evTrips.filter(t => t.year === selYear).reduce((s, t) => s + (t.distance_km || 0), 0).toFixed(0)} km · €{evTrips.filter(t => t.year === selYear).reduce((s, t) => s + (t.cost_per_trip || 0), 0).toFixed(2)} kosten
                  </p>
                )}
              </div>
              <button className="btn bp" style={{ fontSize: 12 }} onClick={exportEvTrips}>↓ Download</button>
            </div>
            {flaggedDocs.length > 0 && (
              <div className="card" style={{ background: C.yellowDim, border: "1px solid rgba(224,168,46,.25)" }}>
                <p style={{ color: C.yellow, fontWeight: 600, marginBottom: 6 }}>⚑ {flaggedDocs.length} document{flaggedDocs.length !== 1 ? "en" : ""} vereist aandacht</p>
                {flaggedDocs.map(d => (
                  <p key={d.id} style={{ fontSize: 12, color: C.muted, marginBottom: 3 }}>— {d.description || d.file_name || "Onbekend"}: {d.flag_reason}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODALS ── */}
      {editDoc && (
        <Portal><div className="ovl" onClick={() => !saving && setEditDoc(null)}>
          <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
              <span style={{ fontSize: 22 }}>{editDoc.flagged ? "⚑" : ""}</span>
              <div style={{ flex: 1 }}>
                <h3 style={{ fontFamily: "monospace", fontSize: 14 }}>{editDoc.flagged ? "CONTROLEER DOCUMENT" : "DOCUMENT BEWERKEN"}</h3>
                <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{editDoc.file_name || "Geen bestandsnaam"} · ID {editDoc.id}{editDoc.flagged ? <span style={{ color: C.yellow, marginLeft: 8 }}>· {editDoc.flag_reason || "Velden ontbreken"}</span> : ""}</p>
              </div>
            </div>
            {(editDoc.file_url || editDoc.file_name) && (
              <div style={{ marginBottom: 13 }}>
                <p className="lbl" style={{ marginBottom: 7 }}>Origineel document</p>
                <DocFileViewer file_url={editDoc.file_url} file_type={editDoc.file_type} file_name={editDoc.file_name} mime_type={editDoc.mime_type} maxH="44vh" />
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 11 }}>
              <Fld label="Document type">
                <select value={editDoc.doc_type || "bon"} onChange={e => setEditDoc(p => ({ ...p, doc_type: e.target.value }))}>
                  {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Fld>
              <Fld label={editDoc.flagged && !editDoc.doc_date ? "Datum * ⚑ verplicht" : "Datum *"}>
                <input type="date" value={editDoc.doc_date || ""} onChange={e => setEditDoc(p => ({ ...p, doc_date: e.target.value }))} style={{ borderColor: editDoc.flagged && !editDoc.doc_date ? C.yellow : undefined }} />
              </Fld>
              <Fld label="Leverancier / Klant">
                <input value={editDoc.supplier || editDoc.customer || ""} onChange={e => setEditDoc(p => ({ ...p, supplier: e.target.value }))} placeholder="Naam" />
              </Fld>
              <Fld label="Omschrijving">
                <input value={editDoc.description || ""} onChange={e => setEditDoc(p => ({ ...p, description: e.target.value }))} placeholder="Wat is het?" />
              </Fld>
              <Fld label={editDoc.flagged && !editDoc.amount_excl ? "Bedrag excl. BTW (€) * ⚑ verplicht" : "Bedrag excl. BTW (€) *"}>
                <input type="number" step="0.01" value={editDoc.amount_excl || ""} onChange={e => setEditDoc(p => ({ ...p, amount_excl: e.target.value }))} style={{ borderColor: editDoc.flagged && !editDoc.amount_excl ? C.yellow : undefined }} />
              </Fld>
              <Fld label="BTW %">
                <select value={editDoc.vat_rate ?? 21} onChange={e => setEditDoc(p => ({ ...p, vat_rate: parseFloat(e.target.value) }))}>
                  {[0, 9, 21].map(r => <option key={r} value={r}>{r}%</option>)}
                </select>
              </Fld>
              <Fld label="BTW bedrag (€)">
                <input type="number" step="0.01" value={editDoc.vat_amount || ""} onChange={e => setEditDoc(p => ({ ...p, vat_amount: e.target.value }))} />
              </Fld>
              <Fld label="Categorie">
                <select value={editDoc.category || "Overig"} onChange={e => setEditDoc(p => ({ ...p, category: e.target.value }))}>
                  {EXPENSE_CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </Fld>
            </div>
            <Fld label="Betalingsmethode">
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["bank", "iDEAL", "cash", "creditcard"].map(m => (
                  <button key={m} type="button" onClick={() => setEditDoc(p => ({ ...p, payment_method: m }))} style={{ padding: "4px 10px", borderRadius: 5, border: "1px solid " + (editDoc.payment_method === m ? C.green : C.border), background: editDoc.payment_method === m ? C.greenDim : "transparent", color: editDoc.payment_method === m ? C.green : C.muted, fontSize: 11, cursor: "pointer" }}>{m}</button>
                ))}
              </div>
            </Fld>
            {/* ── Betalen via Mollie ── */}
            {(() => {
              const ps = editDoc.payment_status || "open";
              const isSale = editDoc.doc_type === "factuur_verkoop";
              const amt = parseFloat(editDoc.amount_incl) || parseFloat(editDoc.amount_excl) || 0;
              const paidBadge = <span style={{ padding: "3px 10px", borderRadius: 12, background: C.greenDim, color: C.green, fontSize: 11, fontWeight: 700 }}>✓ Betaald{editDoc.paid_at ? " · " + (editDoc.paid_at || "").slice(0, 10) : ""}</span>;
              return (
                <div style={{ marginTop: 13, padding: "11px 13px", border: "1px solid " + C.border, borderRadius: 8, background: C.bg }}>
                  <p className="lbl" style={{ marginBottom: 8 }}>Betaling</p>
                  {ps === "betaald" ? paidBadge : isSale ? (<>
                    {ps === "pending" && editDoc.payment_url ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <p style={{ fontSize: 12, color: C.muted }}>Betaallink aangemaakt — stuur 'm naar je klant:</p>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <a href={editDoc.payment_url} target="_blank" rel="noopener noreferrer" className="btn bg" style={{ fontSize: 11, textDecoration: "none" }}>↗ Open checkout</a>
                          <button type="button" className="btn bg" style={{ fontSize: 11 }} onClick={() => { navigator.clipboard?.writeText(editDoc.payment_url); showToast("Link gekopieerd"); }}>Kopieer link</button>
                          <button type="button" className="btn bp" style={{ fontSize: 11 }} disabled={payBusy} onClick={() => checkPaymentStatus(editDoc)}>{payBusy ? "⟳" : "Controleer betaalstatus"}</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="btn bp" style={{ fontSize: 12 }} disabled={payBusy || amt <= 0} onClick={() => createPaymentLink(editDoc)}>{payBusy ? "⟳ Bezig..." : `Betaallink maken (iDEAL/creditcard · €${amt.toFixed(2)})`}</button>
                    )}
                    {ps === "mislukt" && <p style={{ fontSize: 11, color: C.red, marginTop: 7 }}>Vorige poging mislukt/geannuleerd — maak een nieuwe link.</p>}
                    {ps === "verlopen" && <p style={{ fontSize: 11, color: C.yellow, marginTop: 7 }}>Vorige link verlopen — maak een nieuwe.</p>}
                  </>) : (<>
                    <p style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Inkoop/bon: iDEAL kan losse leveranciers niet betalen (dat is een bankoverschrijving). Betaal via je bank en vink hier af.</p>
                    <button type="button" className="btn bp" style={{ fontSize: 12 }} disabled={payBusy} onClick={() => markPaid(editDoc)}>{payBusy ? "⟳" : "✓ Markeer als betaald"}</button>
                  </>)}
                </div>
              );
            })()}
            <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
              <button className="btn bd" onClick={() => deleteAdminDoc(editDoc.id)} disabled={saving} style={{ fontSize: 11 }}>Verwijder</button>
              <span style={{ flex: 1 }} />
              <button className="btn bg" onClick={() => setEditDoc(null)} disabled={saving}>Annuleer</button>
              <button className="btn bp" onClick={updateDoc} disabled={saving}>{saving ? "Opslaan..." : "✓ Opslaan & Goedkeuren"}</button>
            </div>
          </div>
        </div></Portal>
      )}
      {newTrans && (
        <Portal><div className="ovl" onClick={() => setNewTrans(false)}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 16 }}>+ TRANSACTIE TOEVOEGEN</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Fld label="Datum *"><input type="date" value={nt.trans_date} onChange={e => setNt(p => ({ ...p, trans_date: e.target.value }))} /></Fld>
                <Fld label="Type"><select value={nt.type} onChange={e => setNt(p => ({ ...p, type: e.target.value }))}><option value="income">Inkomen</option><option value="expense">Kosten</option></select></Fld>
                <Fld label="Bedrag excl. BTW (€)"><input type="number" step="0.01" value={nt.amount_excl} onChange={e => setNt(p => ({ ...p, amount_excl: e.target.value }))} /></Fld>
                <Fld label="BTW %"><select value={nt.vat_rate} onChange={e => setNt(p => ({ ...p, vat_rate: e.target.value }))}>{[0, 9, 21].map(r => <option key={r} value={r}>{r}%</option>)}</select></Fld>
              </div>
              <Fld label="Omschrijving"><input value={nt.description} onChange={e => setNt(p => ({ ...p, description: e.target.value }))} placeholder="Factuur omschrijving" /></Fld>
              <Fld label="Categorie"><select value={nt.category} onChange={e => setNt(p => ({ ...p, category: e.target.value }))}>{EXPENSE_CATS.map(c => <option key={c}>{c}</option>)}</select></Fld>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => setNewTrans(false)}>Annuleer</button>
                <button className="btn bp" onClick={async () => {
                  if (!nt.trans_date || !nt.amount_excl) return;
                  const yr = new Date(nt.trans_date).getFullYear();
                  const excl = parseFloat(nt.amount_excl);
                  const vatAmt = Math.round(excl * (parseFloat(nt.vat_rate) || 0) / 100 * 100) / 100;
                  const row = { trans_date: nt.trans_date, type: nt.type, category: nt.category, description: nt.description, amount_excl: excl, vat_rate: parseFloat(nt.vat_rate), vat_amount: vatAmt, amount_incl: excl + vatAmt, year: yr, quarter: docQuarter(nt.trans_date) };
                  const { data } = await supabase.from("admin_transactions").insert([row]).select();
                  if (data) { setTrans(p => [data[0], ...p]); logActivity("transactie", nt.description, "administratie"); }
                  setNewTrans(false); setNt({ trans_date: "", type: "expense", category: "Overig", description: "", amount_excl: "", vat_rate: 21, amount_incl: "", partner: "" });
                  showToast("✓ Transactie toegevoegd");
                }}>→ Opslaan</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
      {newHour && (
        <Portal><div className="ovl" onClick={() => { setNewHour(false); setEditHourId(null); }}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 16 }}>{editHourId ? "UREN WIJZIGEN" : "+ UREN REGISTREREN"}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Fld label="Datum *"><input type="date" value={nh.log_date} onChange={e => setNh(p => ({ ...p, log_date: e.target.value }))} /></Fld>
                <Fld label="Partner (vergrendeld)"><input value={meName} disabled style={{ opacity: .7, cursor: "not-allowed" }} title="Je kunt alleen je eigen uren registreren" /></Fld>
                <Fld label="Uren *"><input type="number" step="0.25" min="0.25" max="24" value={nh.hours} onChange={e => setNh(p => ({ ...p, hours: e.target.value }))} placeholder="7.5" /></Fld>
                <Fld label="Categorie"><select value={nh.category} onChange={e => setNh(p => ({ ...p, category: e.target.value }))}>{HOUR_CATS.map(c => <option key={c}>{c}</option>)}</select></Fld>
              </div>
              <Fld label="Omschrijving"><input value={nh.description} onChange={e => setNh(p => ({ ...p, description: e.target.value }))} placeholder="Wat heb je gedaan?" /></Fld>
              {!editHourId && (
                <>
                  <ReeksKiezer waarde={nhReeks} onChange={setNhReeks} startIso={nh.log_date} maxIso={new Date().toISOString().split("T")[0]} />
                  {nhReeks.patroon !== "none" && <p style={{ fontSize: 10, color: C.dim }}>Alleen datums tot en met vandaag worden geregistreerd: geregistreerde uren moeten ook echt gewerkt zijn. Wil je toekomstige blokken plannen? Gebruik de agenda.</p>}
                </>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => { setNewHour(false); setEditHourId(null); }}>Annuleer</button>
                <button className="btn bp" onClick={async () => {
                  if (!nh.log_date || !nh.hours) return;
                  const row = { log_date: nh.log_date, partner: nh.partner, hours: parseFloat(nh.hours), category: nh.category, description: nh.description, year: new Date(nh.log_date).getFullYear() };
                  if (editHourId) {
                    const { data, error } = await supabase.from("hour_logs").update(row).eq("id", editHourId).select();
                    if (!error && data) { setHours(p => p.map(x => x.id === editHourId ? data[0] : x).sort((a, b) => a.log_date.localeCompare(b.log_date))); showToast("✓ Uren bijgewerkt"); }
                    else showToast("✗ Bijwerken mislukt");
                  } else {
                    const vandaag = new Date().toISOString().split("T")[0];
                    const { datums, overgeslagen } = reeksDatums(nh.log_date, nhReeks, { maxIso: vandaag });
                    if (datums.length === 0) { showToast("✗ Geen enkele datum valt in de reeks (toekomst wordt niet geregistreerd)"); return; }
                    const recurId = nhReeks.patroon !== "none" && datums.length > 1 ? "recur_" + Date.now() : null;
                    const rijen = datums.map(d => ({ ...row, log_date: d, year: parseInt(d.slice(0, 4)), recur_id: recurId }));
                    const { data, error } = await supabase.from("hour_logs").insert(rijen).select();
                    if (error || !data) { showToast("✗ Registreren mislukt"); return; }
                    setHours(p => [...p, ...data].sort((a, b) => a.log_date.localeCompare(b.log_date))); logActivity("uren-registratie", "", "administratie");
                    showToast("✓ " + (data.length === 1 ? "Uren geregistreerd" : data.length + " registraties aangemaakt") + (overgeslagen ? " (" + overgeslagen + " in de toekomst overgeslagen)" : ""));
                  }
                  setNewHour(false); setEditHourId(null); setNhReeks(REEKS_NIEUW); setNh({ log_date: new Date().toISOString().split("T")[0], partner: meKey, hours: "", category: "Development", description: "" });
                }}>→ Opslaan</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
      {newKm && (
        <Portal><div className="ovl" onClick={() => { setNewKm(false); setEditKmId(null); }}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 16 }}>{editKmId ? "RIT WIJZIGEN" : "+ RIT REGISTREREN"}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Fld label="Datum *"><input type="date" value={nk.log_date} onChange={e => setNk(p => ({ ...p, log_date: e.target.value }))} /></Fld>
                <Fld label="Partner (vergrendeld)"><input value={meName} disabled style={{ opacity: .7, cursor: "not-allowed" }} title="Je kunt alleen je eigen ritten registreren" /></Fld>
                <Fld label="Van"><input value={nk.from_loc} onChange={e => setNk(p => ({ ...p, from_loc: e.target.value }))} placeholder="Amsterdam" /></Fld>
                <Fld label="Naar"><input value={nk.to_loc} onChange={e => setNk(p => ({ ...p, to_loc: e.target.value }))} placeholder="Rotterdam" /></Fld>
                <Fld label="KM *"><input type="number" step="0.1" value={nk.km} onChange={e => setNk(p => ({ ...p, km: e.target.value }))} placeholder="45" /></Fld>
                <Fld label="Doel"><input value={nk.purpose} onChange={e => setNk(p => ({ ...p, purpose: e.target.value }))} placeholder="Klantbezoek" /></Fld>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => { setNewKm(false); setEditKmId(null); }}>Annuleer</button>
                <button className="btn bp" onClick={async () => {
                  if (!nk.log_date || !nk.km) return;
                  const row = { log_date: nk.log_date, partner: nk.partner, from_loc: nk.from_loc, to_loc: nk.to_loc, purpose: nk.purpose, km: parseFloat(nk.km), year: new Date(nk.log_date).getFullYear() };
                  if (editKmId) {
                    const { data, error } = await supabase.from("mileage_logs").update(row).eq("id", editKmId).select();
                    if (!error && data) { setMileage(p => p.map(x => x.id === editKmId ? data[0] : x).sort((a, b) => a.log_date.localeCompare(b.log_date))); showToast("✓ Rit bijgewerkt"); }
                    else showToast("✗ Bijwerken mislukt");
                  } else {
                    const { data } = await supabase.from("mileage_logs").insert([row]).select();
                    if (data) { setMileage(p => [...p, data[0]].sort((a, b) => a.log_date.localeCompare(b.log_date))); logActivity("kilometers", "", "administratie"); }
                    showToast("✓ Rit geregistreerd");
                  }
                  setNewKm(false); setEditKmId(null); setNk({ log_date: new Date().toISOString().split("T")[0], partner: meKey, from_loc: "", to_loc: "", purpose: "", km: "" });
                }}>→ Opslaan</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
      {newMut && (
        <Portal><div className="ovl" onClick={() => { setNewMut(false); setEditMutId(null); }}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 16 }}>{editMutId ? "MUTATIE WIJZIGEN" : "+ PRIVÉ MUTATIE"}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Fld label="Datum *"><input type="date" value={nm.mut_date} onChange={e => setNm(p => ({ ...p, mut_date: e.target.value }))} /></Fld>
                <Fld label="Partner (vergrendeld)"><input value={meName} disabled style={{ opacity: .7, cursor: "not-allowed" }} title="Je kunt alleen je eigen mutaties registreren" /></Fld>
                <Fld label="Type"><select value={nm.type} onChange={e => setNm(p => ({ ...p, type: e.target.value }))}><option value="storting">Privé storting → VOF</option><option value="onttrekking">Onttrekking ← VOF</option></select></Fld>
                <Fld label="Bedrag (€) *"><input type="number" step="0.01" value={nm.amount} onChange={e => setNm(p => ({ ...p, amount: e.target.value }))} placeholder="500" /></Fld>
              </div>
              <Fld label="Omschrijving"><input value={nm.description} onChange={e => setNm(p => ({ ...p, description: e.target.value }))} placeholder="Salaris, investering, etc." /></Fld>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => { setNewMut(false); setEditMutId(null); }}>Annuleer</button>
                <button className="btn bp" onClick={async () => {
                  if (!nm.mut_date || !nm.amount) return;
                  const row = { mut_date: nm.mut_date, partner: nm.partner, type: nm.type, amount: parseFloat(nm.amount), description: nm.description, year: new Date(nm.mut_date).getFullYear() };
                  if (editMutId) {
                    const { data, error } = await supabase.from("partner_mutations").update(row).eq("id", editMutId).select();
                    if (!error && data) { setMutations(p => p.map(x => x.id === editMutId ? data[0] : x)); showToast("✓ Mutatie bijgewerkt"); }
                    else showToast("✗ Bijwerken mislukt");
                  } else {
                    const { data } = await supabase.from("partner_mutations").insert([row]).select();
                    if (data) { setMutations(p => [data[0], ...p]); logActivity("privé-mutatie", "", "administratie"); }
                    showToast("✓ Mutatie geregistreerd");
                  }
                  setNewMut(false); setEditMutId(null); setNm({ mut_date: new Date().toISOString().split("T")[0], partner: meKey, type: "storting", amount: "", description: "" });
                }}>→ Opslaan</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
    </div>
  );
}

function Documenten({ services }) {
  const [selDoc, setSelDoc] = useState(null);
  const selDocUrl = useOndertekendeUrl(selDoc ? selDoc.file_url : "");
  const [docBewerk, setDocBewerk] = useState(false);
  useEffect(() => { setDocBewerk(false); }, [selDoc?.id]);
  const [toast, setToast] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [filterDienst, setFilterDienst] = useState("Alle diensten");
  const [filterType, setFilterType] = useState("Alle types");
  const [newMod, setNewMod] = useState(false);
  const [editMod, setEditMod] = useState(false);
  const [customDocs, setCustomDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [nd, setNd] = useState({ title: "", type: "general", dienst: "", content: "", aangemaakt_door: "furgel" });

  // fileKind / IMG_EXTS / IMG_NO_INLINE staan nu module-level (gedeeld met Administratie)
  const uploadFile = (file) => uploadToDocuments(file, "docs");

  // Lees content uit (voor zoekbaarheid + DOCX preview); upload origineel naar storage
  async function processFile(file) {
    const ext = (file.name || "").toLowerCase().split(".").pop();
    const isDocx = file.name.toLowerCase().endsWith(".docx") || file.name.toLowerCase().endsWith(".doc");
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    // Detecteer beeld op MIME OF extensie — sommige browsers geven geen MIME (bv. iPhone HEIC),
    // waardoor de foto anders als binaire tekst werd ingelezen i.p.v. als afbeelding getoond.
    const isImage = file.type.startsWith("image/") || IMG_EXTS.includes(ext);
    setUploading(true);
    try {
      const { url } = await uploadFile(file);
      let content = ""; let fileType = "text";
      if (isPdf) { fileType = "pdf"; content = "[PDF: " + file.name + "]"; }
      else if (isImage) { fileType = "image"; content = "[Afbeelding: " + file.name + "]"; }
      else if (isDocx) {
        fileType = "docx";
        const buf = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer: buf });
        content = result.value;
      } else {
        fileType = "text";
        content = await file.text();
      }
      setNd(p => ({ ...p, content, title: p.title || file.name.replace(/\.[^.]+$/, ""), fileName: file.name, fileType, fileSize: file.size, fileUrl: url, mimeType: file.type || "" }));
    } catch (e) {
      console.error("Upload:", e);
      showToast("✗ Upload mislukt: " + (e.message || "onbekend"));
    }
    setUploading(false);
  }

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase.from("documents").select("*").order("created_at", { ascending: false });
      if (error) console.error("Fout bij laden documenten:", error);
      if (data) setCustomDocs(data.map(d => ({ ...d, date: new Date(d.created_at).toLocaleDateString("nl-NL"), isCustom: true, icon: d.file_type === "pdf" ? "PDF" : d.file_name ? "DOC" : "TXT" })));
    }
    load();
    const ch = supabase.channel("documents_comp_sync_v2")
      .on("postgres_changes", { event: "*", schema: "public", table: "documents" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, []);

  const mvpDocs = services.filter(s => s.state === "mvp_ready" || s.state === "approved").map(s => ({ title: s.title + " — MVP As-Built v1.0", type: "mvp", date: "Recent", created_at: new Date().toISOString(), dienst: s.title, content: "# " + s.title + " — As-Built MVP v1.0\n\nProbleem: " + (s.problem || "—") + "\nDoelgroep: " + (s.target || "—") + "\n\n## MVP Scope\n→ AI task generator\n→ Health dashboard\n→ Automatische rapportages" }));

  const allDocs = [...mvpDocs, ...customDocs].sort((a, b) => {
    const da = a.date === "Recent" ? new Date() : new Date(a.created_at || 0);
    const db = b.date === "Recent" ? new Date() : new Date(b.created_at || 0);
    return db - da;
  });

  const TC = { validation: { c: C.blue, bg: C.blueDim }, mvp: { c: C.green, bg: C.greenDim }, build: { c: C.yellow, bg: C.yellowDim }, financial: { c: C.purple, bg: C.purpleDim }, crm: { c: C.cyan, bg: C.cyanDim }, general: { c: C.muted, bg: C.borderHi }, notitie: { c: C.text, bg: C.borderHi }, rapport: { c: C.purple, bg: C.purpleDim } };

  const docs = allDocs.filter(d => {
    if (!showArchived && d.archived) return false;
    if (showArchived && !d.archived) return false;
    if (filterDienst !== "Alle diensten" && d.dienst !== filterDienst) return false;
    if (filterType !== "Alle types" && d.type !== filterType) return false;
    return true;
  });

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }

  async function download(d) {
    // Als er een originele upload-URL is, download die direct (PDF/image/docx blijven intact)
    if (d.file_url) {
      const a = document.createElement("a");
      a.href = await ondertekendeUrl(d.file_url);
      a.download = d.file_name || d.title.replace(/[^a-z0-9.]/gi, "_");
      a.target = "_blank";
      a.click();
      showToast("↓ Origineel gedownload: " + d.file_name);
      return;
    }
    const blob = new Blob([d.content || d.title], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = d.title.replace(/[^a-z0-9]/gi, "_") + ".txt";
    a.click(); URL.revokeObjectURL(url);
    showToast("↓ Gedownload: " + d.title);
  }

  function share(d) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(d.title + " — Dif3r3nt OS");
      showToast("⎘ Link gekopieerd naar klembord");
    } else {
      showToast("⎘ Delen niet beschikbaar in dit scherm");
    }
  }

  async function archive(d) {
    if (!d.id) return;
    const { error } = await supabase.from("documents").update({ archived: true }).eq("id", d.id);
    if (!error) {
      setCustomDocs(p => p.map(x => x.id === d.id ? { ...x, archived: true } : x));
      if (selDoc?.id === d.id) setSelDoc(null);
      showToast("⊘ Gearchiveerd: " + d.title);
    } else {
      showToast("✗ Fout bij archiveren");
    }
  }

  async function deleteDoc(d) {
    if (!window.confirm(`Weet je zeker dat je "${d.title}" definitief wilt verwijderen?`)) return;
    const { error } = await supabase.from("documents").delete().eq("id", d.id);
    if (!error) {
      setCustomDocs(p => p.filter(x => x.id !== d.id));
      if (selDoc?.id === d.id) setSelDoc(null);
      showToast("Verwijderd: " + d.title);
    } else {
      showToast("✗ Fout bij verwijderen");
    }
  }

  async function submitDoc() {
    if (!nd.title.trim()) return;
    // Archiveer oudere versies van hetzelfde project+type
    let newVersion = 1;
    if (nd.dienst) {
      const older = customDocs.filter(x => x.dienst === nd.dienst && x.type === nd.type && !x.archived);
      if (older.length > 0) {
        newVersion = Math.max(...older.map(x => x.version || 1)) + 1;
        const ids = older.map(x => x.id);
        await supabase.from("documents").update({ archived: true }).in("id", ids);
        setCustomDocs(p => p.map(x => ids.includes(x.id) ? { ...x, archived: true } : x));
      }
    }
    const row = { title: nd.title, type: nd.type, dienst: nd.dienst || null, content: nd.content || null, file_name: nd.fileName || null, file_type: nd.fileType || null, file_url: nd.fileUrl || null, file_size: nd.fileSize || null, mime_type: nd.mimeType || null, aangemaakt_door: nd.aangemaakt_door, is_custom: true, archived: false, version: newVersion, ...(nd.lead_id ? { lead_id: nd.lead_id } : {}) };
    const { data, error } = await supabase.from("documents").insert([row]).select();
    if (!error && data) {
      const d = data[0];
      logActivity("document", row.title, "documenten");
      setCustomDocs(p => [{ ...d, date: new Date(d.created_at).toLocaleDateString("nl-NL"), isCustom: true, icon: d.file_type === "pdf" ? "PDF" : d.file_name ? "DOC" : "TXT" }, ...p.filter(x => !x.archived || x.id !== d.id)]);
      setFilterDienst("Alle diensten");
      setFilterType("Alle types");
      setShowArchived(false);
      showToast("✓ Document opgeslagen" + (newVersion > 1 ? " (v" + newVersion + ", vorige gearchiveerd)" : ""));
    } else {
      console.error("Fout bij opslaan document:", error);
      showToast("✗ Fout bij opslaan document");
    }
    setNd({ title: "", type: "general", dienst: "", content: "", aangemaakt_door: "furgel", fileName: null, fileType: null, fileUrl: null, fileSize: null, mimeType: null });
    setNewMod(false);
  }

  async function saveEdit(updatedContent) {
    if (selDoc?.id) {
      await supabase.from("documents").update({ content: updatedContent, updated_at: new Date().toISOString() }).eq("id", selDoc.id);
      setCustomDocs(p => p.map(d => d.id === selDoc.id ? { ...d, content: updatedContent } : d));
    }
    showToast("Opgeslagen");
  }

  if (selDoc) {
    const tc = TC[selDoc.type] || { c: C.muted, bg: C.border };
    return (
      <div className="fu">
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
          <button className="btn bg" onClick={() => setSelDoc(null)}>← Terug</button>
          <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>{selDoc.title}</h2>
          <Bdg label={selDoc.type} bg={tc.bg} fg={tc.c} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 13, marginBottom: 13 }}>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 11 }}>Document Inhoud</p>
            {(() => {
              const kind = fileKind(selDoc.file_type, selDoc.file_name, selDoc.mime_type);
              const url = selDocUrl;
              // PDF: native iframe = exacte browser-render
              if (kind === "pdf" && url) {
                return (
                  <div>
                    <iframe src={url + "#toolbar=1&navpanes=1"} title={selDoc.title} style={{ width: "100%", height: "70vh", minHeight: 540, border: "1px solid " + C.border, borderRadius: 6, background: "#fff" }} />
                    <p style={{ fontSize: 11, color: C.dim, marginTop: 6, fontFamily: "monospace" }}>Native PDF preview · <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>open in nieuw tabblad ↗</a></p>
                  </div>
                );
              }
              // Image: img tag op zwart canvas
              if (kind === "image" && url) {
                const iext = (selDoc.file_name || "").toLowerCase().split(".").pop();
                // HEIC/TIFF e.d. kunnen browsers niet inline renderen → toon download i.p.v. kapotte <img>
                if (IMG_NO_INLINE.includes(iext) || /heic|heif|tiff?/.test(selDoc.mime_type || "")) {
                  return (
                    <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13, border: "1px solid " + C.border, borderRadius: 6, background: C.bg }}>
                      <p style={{ fontSize: 30, marginBottom: 8, color: C.dim }}>◇</p>
                      <p>Dit formaat (<strong>.{iext}</strong>) kan de browser niet inline tonen.</p>
                      <p style={{ marginTop: 8 }}><a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.green, fontWeight: 600 }}>Open / download de originele afbeelding ↗</a></p>
                    </div>
                  );
                }
                return (
                  <div>
                    <div style={{ padding: 12, background: C.bg, border: "1px solid " + C.border, borderRadius: 6, textAlign: "center" }}>
                      <img src={url} alt={selDoc.title} style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 4, boxShadow: "0 2px 12px rgba(0,0,0,.4)" }} />
                    </div>
                    <p style={{ fontSize: 11, color: C.dim, marginTop: 6, fontFamily: "monospace" }}>Originele afbeelding · <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>open vol formaat ↗</a></p>
                  </div>
                );
              }
              // DOCX: HTML preview (mammoth) + download knop voor originele .docx
              if (kind === "docx") {
                if (selDoc.content?.startsWith("PK")) {
                  return (
                    <div style={{ padding: 20, textAlign: "center", color: C.muted, fontSize: 13, border: "1px solid " + C.border, borderRadius: 6 }}>
                      <p style={{ fontSize: 22, marginBottom: 8 }}>⚠</p>
                      <p>Dit document is opgeslagen als binaire data (oude upload).</p>
                      <p style={{ marginTop: 6 }}>Verwijder het document en upload het opnieuw.</p>
                    </div>
                  );
                }
                return (
                  <div>
                    <div style={{ fontSize: 13, lineHeight: 1.6, color: C.text, maxHeight: 500, overflowY: "auto", padding: 14, background: "#fff", color: "#222", borderRadius: 6, border: "1px solid " + C.border }}
                      dangerouslySetInnerHTML={{ __html: selDoc.content || "" }} />
                    {url && <p style={{ fontSize: 11, color: C.dim, marginTop: 6, fontFamily: "monospace" }}>Tekst-preview (Word kan geen native render) · <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>download originele .docx ↗</a></p>}
                  </div>
                );
              }
              // Editable text (custom doc met content, geen file)
              if (selDoc.isCustom && !url) {
                if (!docBewerk) return (
                  <div>
                    <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "16px 20px", maxWidth: 760 }}>{LeesTekst(selDoc.content || selDoc.title, { maxHoogte: 600 })}</div>
                    <button className="btn bg" style={{ fontSize: 11, marginTop: 8 }} onClick={() => setDocBewerk(true)}>✎ Bewerken</button>
                  </div>
                );
                return <textarea defaultValue={selDoc.content || ""} rows={14} id="docContent" style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.8, resize: "vertical", borderColor: C.green + "40" }} onBlur={e => { saveEdit(e.target.value); setDocBewerk(false); }} autoFocus />;
              }
              // Plain text/markdown
              return (
                <div>
                  <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "16px 20px", maxWidth: 760 }}>{LeesTekst(selDoc.content || selDoc.title, { maxHoogte: 600 })}</div>
                  {url && <p style={{ fontSize: 11, color: C.dim, marginTop: 6, fontFamily: "monospace" }}><a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.green }}>download origineel ↗</a></p>}
                </div>
              );
            })()}
          </div>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 11 }}>Details</p>
            {[["Dienst", selDoc.dienst || "—"], ["Type", selDoc.type], ["Datum", selDoc.date], ["Versie", "v" + (selDoc.version || 1)], ["Aangemaakt door", selDoc.aangemaakt_door ? ((selDoc.aangemaakt_door === "furgel" ? "Furgel" : "Tristan")) : "Dif3R3Nt OS"]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.border }}>
                <span style={{ fontSize: 12, color: C.muted }}>{k}</span>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>
              </div>
            ))}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              <button className="btn bp" style={{ width: "100%" }} onClick={() => download(selDoc)}>↓ Download (.txt)</button>
              <button className="btn bg" style={{ width: "100%" }} onClick={() => share(selDoc)}>⎘ Link kopiëren</button>
              <div style={{ display: "flex", gap: 8 }}>
                {selDoc.isCustom && !selDoc.archived && <button className="btn bg" style={{ flex: 1 }} onClick={() => archive(selDoc)}>⊘ Archiveren</button>}
                {selDoc.isCustom && <button className="btn bd" style={{ flex: 1 }} onClick={() => deleteDoc(selDoc)}>Verwijderen</button>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fu">
      {toast && (
        <div style={{ position: "fixed", bottom: 22, right: 22, background: C.surface, border: "1px solid " + C.green, borderRadius: 8, padding: "10px 16px", fontSize: 13, color: C.green, zIndex: 300, fontFamily: "monospace", animation: "fadeUp .2s ease" }}>
          {toast}
        </div>
      )}
      <Kop titel="Documenten" rechts={<button className="btn bp" onClick={() => setNewMod(true)}>+ Nieuw document</button>} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <select style={{ width: "auto", fontSize: 12 }} value={filterDienst} onChange={e => setFilterDienst(e.target.value)}>
            <option>Alle diensten</option>{services.map(s => <option key={s.id}>{s.title}</option>)}
          </select>
          <select style={{ width: "auto", fontSize: 12 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="Alle types">Alle types</option>
            <option value="general">Algemeen</option>
            <option value="notitie">Notitie</option>
            <option value="rapport">Rapport</option>
            <option value="validation">Validation</option>
            <option value="mvp">MVP</option>
            <option value="build">Bouw</option>
            <option value="financial">Financieel</option>
            <option value="crm">CRM</option>
          </select>
          <button className="btn bg" style={{ fontSize: 12 }} onClick={() => setShowArchived(p => !p)}>
            {showArchived ? "← Actief" : "⊘ Archief"}
          </button>
      </div>
      {showArchived && (
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: C.muted }}>Gearchiveerde documenten</span>
        </div>
      )}

      {newMod && (
        <Portal><div className="ovl" onClick={() => setNewMod(false)}>
          <div className="mdl" style={{ width: 560 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", marginBottom: 17, fontSize: 14 }}>+ NIEUW DOCUMENT</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Titel *</p>
                <input value={nd.title} onChange={e => setNd(p => ({ ...p, title: e.target.value }))} placeholder="bijv. AutoCompli — Marktanalyse Q2" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Type</p>
                  <select value={nd.type} onChange={e => setNd(p => ({ ...p, type: e.target.value }))}>
                    <option value="general">Algemeen</option>
                    <option value="notitie">Notitie</option>
                    <option value="rapport">Rapport</option>
                    <option value="validation">Validatie</option>
                    <option value="mvp">MVP</option>
                    <option value="build">Bouw</option>
                    <option value="financial">Financieel</option>
                    <option value="crm">CRM</option>
                  </select>
                </div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Gekoppelde dienst</p>
                  <select value={nd.dienst} onChange={e => setNd(p => ({ ...p, dienst: e.target.value }))}>
                    <option value="">Geen</option>
                    {services.map(s => <option key={s.id} value={s.title}>{s.title}</option>)}
                  </select>
                </div>
              </div>
              <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Aangemaakt door</p>
                <div style={{ display: "flex", gap: 10 }}>
                  {["furgel"].map(u => {
                    const on = nd.aangemaakt_door === u;
                    const col = u === "furgel" ? C.green : C.blue;
                    const name = u === "furgel" ? "Furgel" : "Tristan";
                    return <div key={u} onClick={() => setNd(p => ({ ...p, aangemaakt_door: u }))} style={{ padding: "7px 16px", borderRadius: 20, cursor: "pointer", border: "1px solid " + (on ? col : C.border), background: on ? col + "20" : "transparent", color: on ? col : C.muted, fontSize: 13, fontWeight: 600, transition: "all .15s" }}>{name}</div>;
                  })}
                </div>
              </div>
              <div>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Inhoud</p>
                <div style={{ border: "2px dashed " + (uploading ? C.green : C.border), borderRadius: 8, padding: 18, textAlign: "center", marginBottom: 10, cursor: uploading ? "wait" : "pointer", transition: "border-color .15s", background: C.bg }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.green; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = C.border; }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = C.border;
                    const file = e.dataTransfer.files[0];
                    if (file && !uploading) processFile(file);
                  }}
                  onClick={() => !uploading && document.getElementById("docUpload").click()}>
                  <p style={{ fontSize: 22, marginBottom: 6 }}>{uploading ? "⟳" : ""}</p>
                  <p style={{ fontSize: 13, color: C.muted }}>{uploading ? "Bezig met uploaden naar storage..." : <>Sleep een bestand hierheen of <span style={{ color: C.green, fontWeight: 600 }}>klik om te uploaden</span></>}</p>
                  <p style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>PDF, Word (.docx), afbeeldingen (PNG/JPG/GIF), .txt, .md — max 25MB</p>
                  {nd.fileName && !uploading && <p style={{ fontSize: 12, color: C.green, marginTop: 8, fontWeight: 600 }}>✓ Bestand geladen: {nd.fileName} ({Math.round((nd.fileSize || 0) / 1024)}KB)</p>}
                </div>
                <input id="docUpload" type="file" accept=".pdf,.doc,.docx,.txt,.md,.rtf,image/*" style={{ display: "none" }}
                  onChange={e => { const file = e.target.files[0]; if (file) processFile(file); }} />
                <div style={{ marginBottom: 10 }}><LeadKiezer waarde={nd.lead_id} onChange={v => setNd(p => ({ ...p, lead_id: v }))} label="Hoort bij klant (optioneel)" /></div>
                <p style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Of schrijf/plak tekst:</p>
                <textarea rows={5} value={nd.fileType ? "" : nd.content}
                  onChange={e => setNd(p => ({ ...p, content: e.target.value, fileName: null, fileType: null }))}
                  placeholder="Schrijf of plak inhoud hier..."
                  style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.7, resize: "vertical", opacity: nd.fileType ? .5 : 1 }} />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={submitDoc}>+ Document Opslaan</button>
              </div>
            </div>
          </div>
        </div></Portal>
      )}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {docs.length === 0 && (
          <Leeg tekst={
            <>
              {showArchived ? "Geen gearchiveerde documenten." : "Geen documenten gevonden."}
              {(filterDienst !== "Alle diensten" || filterType !== "Alle types") && (
                <button className="btn bg" style={{ marginLeft: 10, fontSize: 11, padding: "4px 10px" }} onClick={() => { setFilterDienst("Alle diensten"); setFilterType("Alle types"); }}>Filters wissen</button>
              )}
            </>
          } />
        )}
        {docs.map((d, i) => {
          const tc = TC[d.type] || { c: C.muted, bg: C.border };
          return (
            <Rij key={i} onKlik={() => setSelDoc(d)}
              titel={<>{d.title}{(d.version > 1) && <span style={{ fontSize: 10, color: C.blue, marginLeft: 6, fontFamily: "monospace" }}>v{d.version}</span>}</>}
              meta={<>
                <Bdg label={d.type} bg={tc.bg} fg={tc.c} />
                {d.dienst && <span>{d.dienst}</span>}
                <span>{d.date}</span>
                {d.file_name && <span style={{ fontFamily: "monospace" }}>{d.file_name}</span>}
              </>}
              rechts={
                <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                  <button className="btn bg" style={{ fontSize: 12, padding: "3px 8px" }} title="Download" onClick={() => download(d)}>↓</button>
                  <button className="btn bg" style={{ fontSize: 12, padding: "3px 8px" }} title="Delen" onClick={() => share(d)}>⎘</button>
                  {!showArchived && d.isCustom && <button className="btn bg" style={{ fontSize: 12, padding: "3px 8px" }} title="Archiveren" onClick={() => archive(d)}>⊘</button>}
                  {d.isCustom && <button className="btn bd" style={{ fontSize: 12, padding: "3px 8px" }} title="Verwijderen" onClick={() => deleteDoc(d)}>×</button>}
                </div>
              } />
          );
        })}
      </div>
    </div>
  );
}

// ─── AI BOARDROOM (multi-agent vergadering) ───────────────────────────────────
// Een team van AI-persona's (Voorzitter, Furgel/CTO) vergadert
// client-side naar een doel en levert besluiten + concrete actiepunten. Bijna
// autonoom: veilige actiepunten maak je in 1 klik als taken aan; risicovolle acties
// (naar buiten / onomkeerbaar) zijn gemarkeerd als 'akkoord nodig'.
// Gedeelde OS-kennis + rol van het AI-team. Gemini en Groq zijn ZELFSTANDIGE
// AI-collega's - geen AI die Furgel speelt. Ze kennen de hele OS, sparren
// met de directie (mensen) en voeren taken echt uit om hen te ontlasten.
const OS_TEAM_CONTEXT = `Je bent onderdeel van het AI-team van Dif3r3nt VOF (Nederlands software-/AI-/security-maatwerkbedrijf). Furgel (CTO) is de MENSELIJKE oprichter/directie - jij bent NIET een van hen en spreekt NOOIT namens hen. Jij bent een zelfstandige AI-collega die met hen spart en werk uit handen neemt.

Je kent de complete Dif3r3nt OS en mag over ALLE modules adviseren en handelen:
- Dashboard (ecosysteem-funnel), Validatie (dienst-ideeen scoren), Bouwpipeline (kanban idee->live), Launch Module (positionering, SEO, landingspagina, social, ads, Studio-video), Live Diensten (+ Diensten & interesse-whitelist), Growth Operator (content/SEO/social/outreach/ads/automation, Rank Loop, Reclame-studio), Scraping/SDA (bedrijven scrapen -> CRM-leads), CRM (B2B/B2C + funnel), Lead-finder, Gap Intelligence, Administratie/Boekhouder (transacties, BTW, uren, IB), Documenten, Agenda, Taken, Meetings, Bug Tracker.
Backend: Supabase (o.a. services, pipeline, leads, tasks, documents, events, admin_transactions, launch_modules, bugs).

Belangrijk: je bent er om taken van Furgel OVER TE NEMEN en echt UIT TE VOEREN, niet om alleen oplossingen te benoemen. Je werkt BINNEN de OS en hebt via tools echte toegang: documenten, agenda, CRM-leads, taken, uren registreren (registreer_uren), web-research en data uitlezen (lees_data). De bedrijfsdata hieronder is live. Alleen voor een stap buiten jouw tools lever je een volledig uitgewerkt document of maak je een concrete taak aan.

SCHRIJFREGEL (verplicht): gebruik in AL je output NOOIT een lang of middellang gedachtestreepje (em-dash / en-dash). Gebruik altijd een gewoon koppelteken (-), of herschrijf de zin.`;

// Vervangt em-dash (charCode 8212) en en-dash (8211) door een koppelteken; harde garantie naast de prompt-regel.
const stripDash = (s) => String(s == null ? "" : s).split(String.fromCharCode(8212)).join("-").split(String.fromCharCode(8211)).join("-");

// Tabellen die het AI-team mag LEZEN (geen auth-, wachtwoord- of sleuteltabellen).
const BOARDROOM_LEESBAAR = ["tasks", "events", "leads", "documents", "pipeline", "bugs", "services", "launch_modules", "admin_transactions", "hour_logs", "mileage_logs"];

const TEAM_PERSONAS = {
  claude: { name: "Max", role: "Manager · Claude", icon: "◆", color: "purple", provider: "claude",
    sys: "Je heet Max, de manager/coordinator van het Dif3r3nt AI-team (jij draait op het Claude-model). Je denkt scherp STRATEGISCH en WINSTGEVEND: elke keuze weeg je op ROI, kosten, marge en groei. Je bent KRITISCH waar nodig - je challenget zwakke of dure ideeën van Sofie, Daan of de directie in plaats van mee te bewegen, benoemt risico's en zet er een beter/goedkoper/winstgevender alternatief tegenover. Je accepteert alleen KWALITEIT: geen halfbakken, middelmatig of slordig werk - je bewaakt een hoge lat en stuurt Sofie en Daan terug tot het echt goed genoeg is. Je stuurt Sofie (strateeg) en Daan (operator) aan: weegt hun input, lost tegenstellingen op, bepaalt de lijn en de concrete volgende stap, en bewaakt dat het team Furgel echt ontlast. Beslissend en kort. Je bent NIET Furgel. Word je bij naam (Max) aangesproken, dan antwoord jij." },
  gemini: { name: "Sofie", role: "Strateeg · Gemini", icon: "◇", color: "green", provider: "gemini",
    sys: "Je heet Sofie, de strateeg/analist van het Dif3r3nt AI-team (jij draait op het Gemini-model). Je denkt breed en diep over alle OS-modules: strategie, analyse, structuur, risico's, kansen. Je onderbouwt met de echte bedrijfsdata en werkt naar concrete, uitvoerbare stappen. Word je bij naam (Sofie) aangesproken, dan antwoord jij." },
  groq: { name: "Daan", role: "Operator · Groq", icon: "⬡", color: "blue", provider: "groq",
    sys: "Je heet Daan, de operator/uitvoerder van het Dif3r3nt AI-team (jij draait op het Groq-model). Je bent snel en actiegericht en LEVERT het operationele werk direct: concrete stappen, teksten, checklists, scripts - de echte inhoud, niet een aankondiging. Je doet NOOIT alsof je al een taak of record hebt aangemaakt ('Taak aangemaakt' is verboden); echte records ontstaan pas als de directie op 'Voer uit' klikt. BELANGRIJK: jij bent de manager-in-opleiding - je leert actief van Max (strategie, winstgevendheid, kwaliteitslat) en van Sofie (diepe analyse), want jij groeit toe naar de manager die straks de hele Dif3r3nt OS gaat aansturen. Neem hun denkwijze bewust over, stel scherpe vragen en toon dat je meegroeit in strategisch + winstgevend denken - maar blijf nu vooral de snelle, betrouwbare uitvoerder. Word je bij naam (Daan) aangesproken, dan antwoord jij." },
};

// Plat-tekst LLM-call (claude-chat) zonder de JSON-dwang van callGroq, voor de
// gespreksbeurten. Synthese gebruikt wel callGroq (JSON).
async function callChatText(system, user, opts = {}) {
  const base = process.env.REACT_APP_SUPABASE_URL;
  const anon = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const payload = {
    messages: [{ role: "system", content: system }, { role: "user", content: user }],
    temperature: opts.temperature ?? 0.6,
    max_completion_tokens: opts.maxTokens || 450,
    ...(opts.module ? { module: opts.module } : {}),   // noodknop per module (Control)
  };
  // Primair: Claude (claude-chat). Fallback: Groq (/api/groq), zodat een hapering bij
  // claude-chat (netwerk, timeout, mobiele tab-throttling) een vergadering niet laat
  // klappen. Beide leveren platte tekst (geen JSON-dwang).
  try {
    const r = await fetch(`${base}/functions/v1/claude-chat`, {
      method: "POST",
      headers: await metSessie({ "Content-Type": "application/json", apikey: anon }),
      body: JSON.stringify({ ...payload, model: "claude-sonnet-4-6" }),
    });
    if (!r.ok) throw new Error("claude HTTP " + r.status);
    const d = await r.json();
    if (d.error) throw new Error(d.error.message || "claude error");
    const c = (d.choices?.[0]?.message?.content || "").trim();
    if (!c) throw new Error("claude lege respons");
    return c;
  } catch (claudeErr) {
    const headers = { "Content-Type": "application/json" };
    const r = await apiFetch("/api/groq", {
      method: "POST",
      headers,
      body: JSON.stringify({ ...payload, model: "openai/gpt-oss-120b" }),
    });
    if (!r.ok) { const t = await r.text().catch(() => ""); throw new Error("HTTP " + r.status + ": " + t.slice(0, 150)); }
    const d = await r.json();
    if (d.error) throw new Error(d.error.type + ": " + d.error.message);
    return (d.choices?.[0]?.message?.content || "").trim();
  }
}

// Roept een specifieke provider aan als platte tekst (voor de Boardroom-zetels, die
// elk op een ander model draaien). claude -> claude-chat; groq/gemini -> /api/groq.
async function callProviderText(provider, system, user, opts = {}) {
  if (provider === "claude") return callChatText(system, user, opts);
  const payload = {
    messages: [{ role: "system", content: system }, { role: "user", content: user }],
    temperature: opts.temperature ?? 0.6,
    max_completion_tokens: opts.maxTokens || 400,
    ...(opts.module ? { module: opts.module } : {}),   // noodknop per module (Control)
  };
  const doCall = async (prov) => {
    const body = prov === "gemini"
      ? { ...payload, provider: "gemini", model: "gemini-2.5-flash" }
      : { ...payload, model: "openai/gpt-oss-120b" };
    const headers = { "Content-Type": "application/json" };
    const r = await apiFetch("/api/groq", { method: "POST", headers, body: JSON.stringify(body) });
    if (!r.ok) { const t = await r.text().catch(() => ""); const e = new Error("HTTP " + r.status + ": " + t.slice(0, 120)); e.status = r.status; throw e; }
    const d = await r.json();
    if (d.error) throw new Error(d.error.message || d.error.type || "provider error");
    return (d.choices?.[0]?.message?.content || "").trim();
  };
  // Retry op transiente fouten (429/5xx, "high demand"); daarna fallback naar de andere
  // provider zodat een agent altijd antwoordt i.p.v. een 503 te tonen.
  for (let attempt = 0; attempt < 3; attempt++) {
    try { return await doCall(provider); }
    catch (e) {
      const transient = e.status === 429 || (e.status >= 500 && e.status < 600) || /high demand|overload|unavailable|503|429/i.test(e.message || "");
      if (attempt < 2 && transient) { await new Promise(res => setTimeout(res, 700 * (attempt + 1))); continue; }
      if (provider === "gemini") { try { return await doCall("groq"); } catch (e2) { /* val door naar throw */ } }
      throw e;
    }
  }
}

// Echte web-research via de Tavily-tak van /api/groq (voor de Onderzoeker-zetel).
async function researchWeb(query, module) {
  try {
    const r = await apiFetch("/api/groq", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ search: query, max_results: 6, ...(module ? { module } : {}) }) });
    if (!r.ok) return "";
    const d = await r.json();
    const items = Array.isArray(d.results) ? d.results : [];
    return items.slice(0, 6).map((x) => "- " + (x.title || "") + ": " + String(x.content || "").slice(0, 220)).join("\n");
  } catch { return ""; }
}

// ============================================================
// Operatie-team: overzicht van ketens, wachtrij, fouten en kosten.
// De agenten leveren alleen concepten op; niets wordt gepubliceerd of verstuurd
// zonder jouw akkoord. Ontwerp: docs/Ontwerp_Operatie_Team.md
// ============================================================
const OPERATIE_MODELLEN = [
  { id: "claude-opus-5", label: "Claude Opus 5 (beste kwaliteit, ca. €0,40 per keten)" },
  { id: "claude-sonnet-5", label: "Claude Sonnet 5 (sneller en goedkoper, ca. €0,17 per keten)" },
  { id: "claude-haiku-4-5", label: "Claude Haiku 4.5 (goedkoopst, eenvoudiger teksten)" },
];
const RISK_LABEL = ["Lezen", "Concept, alleen intern", "Buitenwereld", "Geld of onomkeerbaar"];

// Niveau van een voorstel: 1 = alleen intern concept, 2 = raakt de buitenwereld, 3 = kost geld.
function voorstelNiveau(p) {
  if (p && p.task_type === "kennis_inzicht") return 2; // gaat na akkoord direct live op de website
  const t = p && p.target_system;
  if (t === "video") return 3;
  if (["documents", "launch_modules", "kennis_artikelen", "tasks", "events", "leads", "internal"].includes(t)) return 1;
  return 2;
}
function NiveauBadge({ niveau }) {
  const kleur = niveau >= 3 ? C.red : niveau === 2 ? C.yellow : C.cyan;
  return <span title={"Niveau " + niveau + ": " + RISK_LABEL[niveau]}><Bdg label={"N" + niveau} bg={C.bg} fg={kleur} /></span>;
}

async function operatieCall(action, payload = {}) {
  const { data, error } = await supabase.functions.invoke("operatie-keten", { body: { action, ...payload } });
  if (error) {
    let msg = error.message || "Aanroep mislukt";
    try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* laat het standaardbericht staan */ }
    throw new Error(msg);
  }
  if (data && data.error) throw new Error(data.error);
  return data;
}

// ============================================================
// VERKOOP (AI Boardroom): lead, voorbereiding, wachtrij, verzending, opvolging, afspraak.
// De vangrails (noodstop, dagbudget, verzendlimieten, bounce en klachten) staan in de database.
// Verzending staat standaard uit. Edge function: operatie-verkoop.
// ============================================================
async function verkoopCall(action, payload = {}) {
  const { data, error } = await supabase.functions.invoke("operatie-verkoop", { body: { action, ...payload } });
  if (error) {
    let msg = error.message || "Aanroep mislukt";
    try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* standaardbericht */ }
    throw new Error(msg);
  }
  if (data && data.error) throw new Error(data.error);
  return data;
}

const VERKOOP_STATUS = {
  nieuw: "Nieuw", concept: "Wacht op akkoord", goedgekeurd: "Klaar om te versturen", verzonden: "Verzonden",
  gereageerd: "Gereageerd", afspraak: "Afspraak", afgemeld: "Afgemeld", bounce: "Bounce", gestopt: "Gestopt", mislukt: "Mislukt",
};
const VERKOOP_STAP_LABEL = { outreach_eerste_mail: "Eerste mail", outreach_opvolging: "Opvolging" };
const VERKOOP_INSTELLINGEN = ["operatie_outreach_aan", "operatie_outreach_pauze", "operatie_max_per_dag", "operatie_max_per_ontvangdomein", "operatie_opvolg_dagen", "operatie_afzender", "operatie_voettekst", "operatie_verkoop_pitch"];

function OperatieVerkoop() {
  const MEEMAIL = "furgel@dif3r3nt.nl";
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [auto, setAuto] = useState([]);
  const [inst, setInst] = useState({});
  const [stat, setStat] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);
  const [addMod, setAddMod] = useState(false);
  const [invoer, setInvoer] = useState("");
  const [zakelijk, setZakelijk] = useState(false);
  const [bewerkt, setBewerkt] = useState({});
  const [reden, setReden] = useState({});
  const [maxDag, setMaxDag] = useState("");
  const [maxDom, setMaxDom] = useState("");

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 4000); }

  async function load() {
    try {
      const [l, r, a, s] = await Promise.all([
        supabase.from("operatie_leads").select("*").order("created_at", { ascending: false }).limit(150),
        supabase.from("growth_reviews").select("*").eq("engine", "outreach").eq("status", "pending").order("created_at"),
        supabase.rpc("operatie_autonomie_status"),
        supabase.from("app_settings").select("key,value").in("key", VERKOOP_INSTELLINGEN),
      ]);
      const eerste = [l, r, a, s].find(x => x.error);
      if (eerste) throw new Error(eerste.error.message);
      setLeads(l.data || []); setReviews(r.data || []); setAuto(a.data || []);
      const o = {}; (s.data || []).forEach(x => { o[x.key] = x.value; }); setInst(o);
      setErr(null);
    } catch (e) { setErr(e.message || "Kon verkoop niet laden"); }
  }
  async function laadStatus() { try { setStat(await verkoopCall("status")); } catch (e) { setErr(e.message); } }

  useEffect(() => { load(); laadStatus(); }, []);
  useEffect(() => {
    const ch = supabase.channel("operatie_verkoop_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "operatie_leads" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "growth_reviews" }, () => load())
      .subscribe();
    const iv = setInterval(load, 30000);
    return () => { supabase.removeChannel(ch); clearInterval(iv); };
  }, []);

  async function actie(id, fn, okMsg) {
    setBusy(id);
    try { const r = await fn(); if (okMsg) showToast(typeof okMsg === "function" ? okMsg(r) : okMsg); await load(); }
    catch (e) { showToast("✗ " + (e.message || "Mislukt")); }
    setBusy(null);
  }
  async function bewaar(key, value) {
    const { error } = await supabase.from("app_settings").upsert([{ key, value: String(value), updated_by: "furgel", updated_at: new Date().toISOString() }], { onConflict: "key" });
    if (error) throw new Error(error.message);
  }

  const aan = inst.operatie_outreach_aan === "aan";
  const pauze = (inst.operatie_outreach_pauze || "").trim();
  const maxPerDag = Number(inst.operatie_max_per_dag || 10);
  const maxPerDom = Number(inst.operatie_max_per_ontvangdomein || 2);
  const webhookOk = !!(stat && stat.webhook_ingesteld && stat.resend_ingesteld);
  const tijd = (iso) => iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  const stKleur = (s) => s === "gereageerd" || s === "afspraak" ? C.green : s === "concept" ? C.yellow : s === "goedgekeurd" ? C.blue : s === "verzonden" ? C.cyan : (s === "afgemeld" || s === "bounce" || s === "mislukt") ? C.red : s === "gestopt" ? C.dim : C.muted;
  const aantal = (s) => leads.filter(l => l.status === s).length;

  async function beslis(rv, soort) {
    const nu = new Date().toISOString();
    const p = rv.preview || {};
    let patch = { decided_by: MEEMAIL, decided_at: nu, updated_at: nu };
    if (soort === "reject") {
      patch = { ...patch, status: "rejected", reject_reason: (reden[rv.id] || "").trim() || "geen reden opgegeven" };
    } else {
      const b = bewerkt[rv.id] || {};
      const body = b.body != null ? b.body : p.body;
      const onderwerp = b.onderwerp != null ? b.onderwerp : p.onderwerp;
      const gewijzigd = body !== p.body || onderwerp !== p.onderwerp;
      patch = { ...patch, status: gewijzigd ? "edited" : "approved", ...(gewijzigd ? { preview: { ...p, onderwerp, body }, edits_summary: "Tekst aangepast door reviewer" } : {}) };
    }
    const { data, error } = await supabase.from("growth_reviews").update(patch).eq("id", rv.id).eq("status", "pending").select("id");
    if (error) throw new Error(error.message);
    if (!data || !data.length) throw new Error("Dit voorstel is al beoordeeld.");
    verkoopCall("tick").catch(() => {});   // meteen doorzetten, anders pakt de planning het binnen 5 minuten op
  }

  async function voegToe() {
    const regels = invoer.split("\n").map(r => r.trim()).filter(Boolean);
    const rijen = regels.map(r => {
      const d = r.includes(";") || r.includes("\t") ? r.split(/[;\t]/) : r.split(",");
      const [bedrijf, contact_naam, email, website, branche] = d.map(x => (x || "").trim());
      return { bedrijf, contact_naam, email, website, branche };
    });
    const r = await verkoopCall("lead_toevoegen", { leads: rijen, zakelijk_bevestigd: zakelijk });
    if (r.overgeslagen && r.overgeslagen.length) showToast("Toegevoegd: " + r.toegevoegd + ". Overgeslagen: " + r.overgeslagen.map(o => o.email + " (" + o.reden + ")").join(", "));
    else showToast("✓ " + r.toegevoegd + " lead" + (r.toegevoegd === 1 ? "" : "s") + " toegevoegd, de voorbereiding start vanzelf");
    setAddMod(false); setInvoer(""); setZakelijk(false);
    verkoopCall("tick").catch(() => {});
  }

  function Poortregel({ ok, tekst }) {
    return <p style={{ fontSize: 12, padding: "2px 0", color: ok ? C.green : C.yellow }}>{ok ? "✓" : "○"} <span style={{ color: C.muted }}>{tekst}</span></p>;
  }

  const bew = stat && stat.bewaking;
  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace", maxWidth: "92vw" }}>{toast}</div>}
      {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 10 }}><p style={{ fontSize: 12, color: C.red }}>{err}</p></div>}

      {pauze && (
        <div className="card" style={{ borderColor: C.red + "66", background: C.redDim, marginBottom: 10 }}>
          <p style={{ fontSize: 13, color: C.red, fontWeight: 600 }}>Verzending gepauzeerd: {pauze}</p>
          <p style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Onderzoek eerst de oorzaak (adressenlijst, inhoud, reputatie van het domein). Hervatten kan pas daarna.</p>
          <button className="btn bg" style={{ fontSize: 11, marginTop: 8 }} disabled={busy === "hervat"} onClick={() => actie("hervat", () => bewaar("operatie_outreach_pauze", ""), "✓ Verzending hervat")}>Ik heb het onderzocht, hervat</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600 }}>Verzending</p>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Alles wat je goedkeurt gaat vanzelf de deur uit, binnen de limieten hieronder. Alleen op werkdagen van 8:30 tot 17:00.</p>
          </div>
          <select style={{ maxWidth: 190 }} value={aan ? "aan" : "uit"} onChange={e => {
            if (e.target.value === "aan" && !webhookOk) { showToast("✗ Eerst de bounce- en klachtmeldingen koppelen (zie hieronder)"); return; }
            actie("aan", () => bewaar("operatie_outreach_aan", e.target.value), e.target.value === "aan" ? "✓ Verzending staat aan" : "✓ Verzending staat uit");
          }}>
            <option value="uit">Uit</option>
            <option value="aan">Aan</option>
          </select>
        </div>
        <div style={{ marginTop: 10 }}>
          <Poortregel ok={!!(stat && stat.resend_ingesteld)} tekst="Verzendkoppeling (Resend) ingesteld" />
          <Poortregel ok={!!(stat && stat.webhook_ingesteld)} tekst="Bounce- en klachtmeldingen gekoppeld. Zonder deze koppeling verstuurt het systeem niets, want de automatische pauze is dan blind." />
          {stat && !stat.webhook_ingesteld && (
            <p style={{ fontSize: 11, color: C.dim, margin: "4px 0 6px 16px", lineHeight: 1.6, wordBreak: "break-all" }}>
              Zet in Resend (Webhooks) een webhook op {stat.webhook_url} voor de gebeurtenissen email.bounced en email.complained. Zet daarna het getoonde signing secret in Supabase als RESEND_WEBHOOK_SECRET.
            </p>
          )}
          <Poortregel ok={!pauze} tekst={pauze ? "Automatisch gepauzeerd" : "Geen automatische pauze actief"} />
          <p style={{ fontSize: 12, color: C.muted, padding: "2px 0 0 16px" }}>
            Vandaag verzonden: {stat ? stat.verzonden_vandaag : "..."} van {maxPerDag}
            {bew ? " · laatste " + bew.verzonden + " mails: " + bew.bounces + " bounces, " + bew.klachten + " klachten" : ""}
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginTop: 12 }}>
          <div>
            <p className="lbl" style={{ marginBottom: 5 }}>Maximaal per dag</p>
            <div style={{ display: "flex", gap: 6 }}>
              <input type="number" min="0" max="200" defaultValue={maxPerDag} key={"md" + maxPerDag} onChange={e => setMaxDag(e.target.value)} style={{ maxWidth: 90 }} />
              <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { const v = Number(maxDag === "" ? maxPerDag : maxDag); if (!(v >= 0 && v <= 200)) { showToast("✗ Kies 0 tot 200"); return; } actie("md", () => bewaar("operatie_max_per_dag", v), "✓ Opgeslagen"); }}>Opslaan</button>
            </div>
          </div>
          <div>
            <p className="lbl" style={{ marginBottom: 5 }}>Maximaal per ontvangend bedrijf per dag</p>
            <div style={{ display: "flex", gap: 6 }}>
              <input type="number" min="1" max="20" defaultValue={maxPerDom} key={"mm" + maxPerDom} onChange={e => setMaxDom(e.target.value)} style={{ maxWidth: 90 }} />
              <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { const v = Number(maxDom === "" ? maxPerDom : maxDom); if (!(v >= 1 && v <= 20)) { showToast("✗ Kies 1 tot 20"); return; } actie("mm", () => bewaar("operatie_max_per_ontvangdomein", v), "✓ Opgeslagen"); }}>Opslaan</button>
            </div>
          </div>
        </div>
        <p style={{ fontSize: 11, color: C.dim, marginTop: 10, lineHeight: 1.6 }}>Begin laag en bouw op: een nieuw verzenddomein heeft opwarmen nodig. Bij een klacht of te veel bounces pauzeert het systeem zelf. Bij eenmanszaken en VOF's is vooraf toestemming nodig, alleen rechtspersonen (BV, NV, stichting) en algemene zakelijke adressen mogen zonder.</p>
      </div>

      <p className="lbl" style={{ margin: "16px 0 8px" }}>Mails die op jouw akkoord wachten ({reviews.length})</p>
      {reviews.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 8 }}>Geen mails in de wachtrij.</p></div>}
      {reviews.map(rv => {
        const p = rv.preview || {};
        const b = bewerkt[rv.id] || {};
        return (
          <div key={rv.id} className="card" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <p style={{ fontSize: 13, fontWeight: 600 }}>{rv.title}</p>
              <p style={{ fontSize: 11, color: C.dim }}>Aan: {p.ontvanger || "?"} · {tijd(rv.created_at)}</p>
            </div>
            <input value={b.onderwerp != null ? b.onderwerp : (p.onderwerp || "")} onChange={e => setBewerkt(x => ({ ...x, [rv.id]: { ...(x[rv.id] || {}), onderwerp: e.target.value } }))} maxLength={80} style={{ marginTop: 8, fontWeight: 600 }} />
            <textarea rows={9} value={b.body != null ? b.body : (p.body || "")} onChange={e => setBewerkt(x => ({ ...x, [rv.id]: { ...(x[rv.id] || {}), body: e.target.value } }))} style={{ marginTop: 6, lineHeight: 1.6, fontSize: 13 }} />
            {rv.waarom && <p style={{ fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.55, whiteSpace: "pre-wrap" }}><span style={{ color: C.dim }}>Waarom: </span>{rv.waarom}</p>}
            <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>Controleer of alles over het bedrijf klopt. Afmeldlink en voettekst worden automatisch toegevoegd.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn bp" style={{ fontSize: 12 }} disabled={busy === rv.id} onClick={() => actie(rv.id, () => beslis(rv, "ok"), "✓ Akkoord, de mail gaat binnen de limieten de deur uit")}>{(b.body != null && b.body !== p.body) || (b.onderwerp != null && b.onderwerp !== p.onderwerp) ? "Akkoord met mijn aanpassing" : "Akkoord"}</button>
              <input placeholder="Reden bij afwijzen" value={reden[rv.id] || ""} onChange={e => setReden(x => ({ ...x, [rv.id]: e.target.value }))} style={{ maxWidth: 240, fontSize: 12 }} />
              <button className="btn bg" style={{ fontSize: 12 }} disabled={busy === rv.id} onClick={() => actie(rv.id, () => beslis(rv, "reject"), "✓ Afgewezen, de lead is gestopt")}>Afwijzen</button>
            </div>
          </div>
        );
      })}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, margin: "18px 0 8px", flexWrap: "wrap" }}>
        <p className="lbl">Leads ({leads.length})</p>
        <button className="btn bp" style={{ fontSize: 11 }} onClick={() => setAddMod(true)}>+ Leads toevoegen</button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
        {Object.keys(VERKOOP_STATUS).filter(s => aantal(s) > 0).map(s => <Bdg key={s} label={VERKOOP_STATUS[s] + " " + aantal(s)} bg={C.bg} fg={stKleur(s)} />)}
      </div>
      {leads.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen leads. Voeg een paar rechtspersonen toe: de voorbereiding (audit en concept) draait daarna vanzelf.</p></div>}
      {leads.map(l => (
        <div key={l.id} className="card" style={{ marginBottom: 6, padding: "10px 14px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{l.bedrijf}</span>
              <span style={{ fontSize: 11, color: C.dim, marginLeft: 8 }}>{l.email}</span>
              {l.audit && l.audit.relevantie != null && <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>relevantie {l.audit.relevantie}/10</span>}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <Bdg label={VERKOOP_STATUS[l.status] || l.status} bg={C.bg} fg={stKleur(l.status)} />
              {l.status === "verzonden" && l.volgende_actie_op && <span style={{ fontSize: 11, color: C.dim }}>{l.opvolg_nr >= 2 ? "sluit af" : "opvolging"} {tijd(l.volgende_actie_op)}</span>}
              {["verzonden", "concept", "goedgekeurd"].includes(l.status) && <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === l.id} onClick={() => actie(l.id, () => verkoopCall("lead_actie", { lead_id: l.id, actie: "gereageerd" }), "✓ Opvolging gestopt")}>Reactie</button>}
              {["verzonden", "concept", "goedgekeurd", "gereageerd"].includes(l.status) && <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === l.id} onClick={() => actie(l.id, () => verkoopCall("lead_actie", { lead_id: l.id, actie: "afspraak" }), "✓ Afspraak vastgelegd")}>Afspraak</button>}
              {["nieuw", "verzonden", "concept", "goedgekeurd"].includes(l.status) && <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === l.id} onClick={() => actie(l.id, () => verkoopCall("lead_actie", { lead_id: l.id, actie: "stop" }), "✓ Lead gestopt")}>Stop</button>}
              {l.status === "mislukt" && <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === l.id} onClick={() => actie(l.id, () => verkoopCall("lead_actie", { lead_id: l.id, actie: "opnieuw" }), "✓ Staat weer klaar")}>↻ Opnieuw</button>}
            </div>
          </div>
          {(l.stop_reden || l.fout) && <p style={{ fontSize: 11, color: l.fout ? C.red : C.muted, marginTop: 4, wordBreak: "break-word" }}>{l.fout || l.stop_reden}</p>}
        </div>
      ))}

      <p className="lbl" style={{ margin: "18px 0 8px" }}>Vertrouwen opbouwen met data</p>
      <div className="card" style={{ marginBottom: 10 }}>
        <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 8 }}>Een stap mag pas zelfstandig lopen als je hem lang genoeg vrijwel altijd goedkeurde. Aanpassen telt niet als goedkeuren. Ook in automatische modus zie je nog 1 op de 5 mails, en zakt de kwaliteit dan valt de stap vanzelf terug naar handmatig.</p>
        {auto.map(a => (
          <div key={a.stap} style={{ padding: "8px 0", borderTop: "1px solid " + C.border }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{VERKOOP_STAP_LABEL[a.stap] || a.stap}</span>
                <span style={{ fontSize: 11, marginLeft: 8, color: a.modus === "automatisch" ? C.green : C.muted }}>{a.modus}</span>
              </div>
              {a.modus === "automatisch"
                ? <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === a.stap} onClick={() => actie(a.stap, async () => { const { error } = await supabase.from("operatie_autonomie").update({ modus: "handmatig", sinds: new Date().toISOString() }).eq("stap", a.stap); if (error) throw new Error(error.message); }, "✓ Weer handmatig")}>Terug naar handmatig</button>
                : <button className="btn bg" style={{ fontSize: 11 }} disabled={!a.in_aanmerking || busy === a.stap} title={a.reden} onClick={() => actie(a.stap, async () => { const { error } = await supabase.from("operatie_autonomie").update({ modus: "automatisch" }).eq("stap", a.stap); if (error) throw new Error(error.message); }, "✓ Automatisch (met steekproef)")}>Zet op automatisch</button>}
            </div>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              {a.beoordeeld} van {a.min_beoordelingen} beoordelingen · {a.dagen} van {a.min_dagen} dagen · goedkeuring {a.goedkeuring_pct == null ? "n.v.t." : a.goedkeuring_pct + "%"} (nodig: {a.min_pct}%) · laatste 20: {a.laatste20_pct == null ? "n.v.t." : a.laatste20_pct + "%"}
            </p>
            <p style={{ fontSize: 11, color: a.in_aanmerking ? C.green : C.dim, marginTop: 2 }}>{a.reden}</p>
          </div>
        ))}
      </div>

      {addMod && (
        <Portal><div className="ovl" onClick={() => setAddMod(false)}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", marginBottom: 8, fontSize: 14, color: C.green }}>LEADS TOEVOEGEN</h3>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>Een lead per regel: bedrijf; contactpersoon; e-mail; website; branche. Alleen bedrijf en e-mail zijn verplicht. Maximaal 25 per keer. Het team leest de website, maakt een korte audit en schrijft een concept. Is er geen echte aanleiding, dan stopt de lead vanzelf.</p>
            <textarea rows={7} value={invoer} onChange={e => setInvoer(e.target.value)} placeholder={"Voorbeeld Installatie BV; Jan de Vries; info@voorbeeld.nl; voorbeeld.nl; installatietechniek"} style={{ fontSize: 12, lineHeight: 1.6, fontFamily: "monospace" }} />
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              <input type="checkbox" checked={zakelijk} onChange={e => setZakelijk(e.target.checked)} style={{ marginTop: 3 }} />
              <span>Ik heb gecontroleerd dat dit rechtspersonen (BV, NV, stichting) of algemene zakelijke adressen zijn. Bij een eenmanszaak of VOF heb ik vooraf toestemming.</span>
            </label>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="btn bp" disabled={!zakelijk || !invoer.trim() || busy === "add"} onClick={() => actie("add", voegToe)}>Toevoegen</button>
              <button className="btn bg" onClick={() => setAddMod(false)}>Annuleer</button>
            </div>
          </div>
        </div></Portal>
      )}
    </div>
  );
}

// ============================================================
// GOEDKEURINGEN (Poort): alles wat op jouw akkoord wacht, op één plek.
// De AI stelt voor en bereidt voor, jij beslist over alles wat naar buiten gaat.
// Data: RPC poort_overzicht en view poort_wachtrij_detail (migratie poort_goedkeuringen).
// Voorstellen (proposed_changes) worden uitgebreid beoordeeld en toegepast in ApprovalQueue.
// ============================================================
function Goedkeuringen({ user, setView }) {
  const MEEMAIL = "furgel@dif3r3nt.nl";
  const [ov, setOv] = useState(null);
  const [rijen, setRijen] = useState([]);
  const [growth, setGrowth] = useState({});
  const [reden, setReden] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  // Concept-artikelen voor de website (kennis_artikelen): hier lezen, publiceren of afwijzen, er is geen apart beheer
  const [artikelen, setArtikelen] = useState([]);
  const [artikelTekst, setArtikelTekst] = useState({});
  const [artikelOpen, setArtikelOpen] = useState(null);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3500); }

  async function load() {
    try {
      const [o, w, g, k] = await Promise.all([
        supabase.rpc("poort_overzicht"),
        supabase.from("poort_wachtrij_detail").select("*").order("created_at").limit(100),
        supabase.from("growth_reviews").select("id,engine,type,title,doel,waarom,preview,risico,impact").eq("status", "pending"),
        supabase.from("kennis_artikelen").select("id,slug,titel,beschrijving,onderwerp,created_at").eq("status", "concept").order("created_at", { ascending: false }).limit(60),
      ]);
      if (!k.error) setArtikelen(k.data || []);
      const eerste = [o, w, g].find(x => x.error);
      if (eerste) throw new Error(eerste.error.message);
      setOv(o.data); setRijen(w.data || []);
      const m = {}; (g.data || []).forEach(x => { m[x.id] = x; }); setGrowth(m);
      setErr(null);
    } catch (e) { setErr(e.message || "Kon de goedkeuringen niet laden"); }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const ch = supabase.channel("goedkeuringen_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "growth_reviews" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "proposed_changes" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "sandbox_control" }, () => load())
      .subscribe();
    const iv = setInterval(load, 30000);
    return () => { supabase.removeChannel(ch); clearInterval(iv); };
  }, []);

  async function actie(id, fn, okMsg) {
    setBusy(id);
    try { await fn(); if (okMsg) showToast(okMsg); await load(); }
    catch (e) { showToast("✗ " + (e.message || "Mislukt")); }
    setBusy(null);
  }

  async function beslis(rij, soort) {
    const nu = new Date().toISOString();
    const patch = soort === "reject"
      ? { status: "rejected", reject_reason: (reden[rij.id] || "").trim() || "geen reden opgegeven", decided_by: MEEMAIL, decided_at: nu, updated_at: nu }
      : { status: "approved", decided_by: MEEMAIL, decided_at: nu, updated_at: nu };
    const { data, error } = await supabase.from("growth_reviews").update(patch).eq("id", rij.id).eq("status", "pending").select("id");
    if (error) throw new Error(error.message);
    if (!data || !data.length) throw new Error("Dit voorstel is al beoordeeld.");
    if (rij.agent === "outreach") verkoopCall("tick").catch(() => {});   // meteen doorzetten
  }

  async function bekijkArtikel(a) {
    if (artikelOpen === a.id) { setArtikelOpen(null); return; }
    setArtikelOpen(a.id);
    if (artikelTekst[a.id] != null) return;
    const { data, error } = await supabase.from("kennis_artikelen").select("body_html").eq("id", a.id).maybeSingle();
    if (error) { showToast("✗ " + error.message); return; }
    const plat = String((data && data.body_html) || "").replace(/<\/(p|h2|h3|h4|li|blockquote)>/gi, "\n").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/\n{3,}/g, "\n\n").trim();
    setArtikelTekst(t => ({ ...t, [a.id]: plat || "(geen tekst)" }));
  }
  async function beslisArtikel(a, publiceer) {
    if (publiceer && !window.confirm('"' + a.titel + '" nu publiceren? Het staat direct live op dif3r3nt.nl/kennis/' + a.slug)) return;
    const patch = publiceer ? { status: "gepubliceerd", published_at: new Date().toISOString() } : { status: "afgewezen" };
    const { data, error } = await supabase.from("kennis_artikelen").update(patch).eq("id", a.id).eq("status", "concept").select("id");
    if (error) throw new Error(error.message);
    if (!data || !data.length) throw new Error("Dit artikel is al beoordeeld.");
  }

  async function noodstop(nu) {
    const { error } = await supabase.from("sandbox_control").update({ paused: !nu, updated_at: new Date().toISOString() }).eq("id", true);
    if (error) throw new Error(error.message);
  }

  const eur = (n) => "€" + Number(n || 0).toFixed(2).replace(".", ",");
  const uren = (u) => u == null ? "" : Number(u) >= 48 ? Math.round(Number(u) / 24) + " dagen" : Math.round(Number(u)) + " uur";
  const w = ov && ov.wacht;
  const kosten = ov && ov.kosten;
  const alarmen = (ov && ov.alarmen) || [];
  const pct = kosten && kosten.budget > 0 ? Math.min(100, Math.round((kosten.vandaag / kosten.budget) * 100)) : 0;
  const leads = (ov && ov.pijplijn && ov.pijplijn.leads) || {};
  const mails = (ov && ov.pijplijn && ov.pijplijn.mails_7d) || {};
  const gesorteerd = [...rijen].sort((a, b) => (a.risico === "extern" ? 0 : 1) - (b.risico === "extern" ? 0 : 1) || new Date(a.created_at) - new Date(b.created_at));

  function Stat({ label, waarde, kleur, sub }) {
    return (
      <div className="card" style={{ padding: "10px 12px" }}>
        <p className="lbl" style={{ marginBottom: 4 }}>{label}</p>
        <p style={{ fontSize: 19, fontWeight: 700, color: kleur || C.text, fontFamily: "monospace" }}>{waarde}</p>
        {sub && <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</p>}
      </div>
    );
  }

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace", maxWidth: "92vw" }}>{toast}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <h2 style={{ ...KOP_STIJL }}>Goedkeuringen</h2>
          <p style={{ fontSize: 12, color: C.muted, margin: "3px 0 0", lineHeight: 1.45 }}>Het OS werkt door, jij beslist. Niets gaat naar buiten of kost geld zonder jouw akkoord. Wat langer dan 48 uur wacht, wordt opnieuw aangeboden en nooit vanzelf verstuurd.</p>
        </div>
        <button className="btn bg" style={ov && ov.noodstop ? { color: C.red, borderColor: C.red } : {}} disabled={busy === "noodstop"} onClick={() => actie("noodstop", () => noodstop(ov && ov.noodstop), ov && ov.noodstop ? "✓ Agenten hervat" : "✓ Alle agenten gepauzeerd")}>
          {ov && ov.noodstop ? "▶ Hervat agenten" : "⏸ Noodstop"}
        </button>
      </div>

      {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 12 }}><p style={{ fontSize: 12, color: C.red }}>{err}</p></div>}

      <div className="card" style={{ margin: "12px 0", borderColor: alarmen.length ? C.yellow + "66" : C.border }}>
        <p className="lbl" style={{ marginBottom: 6 }}>Alarmen</p>
        {alarmen.length === 0
          ? <p style={{ fontSize: 12, color: C.green }}>✓ Geen alarmen</p>
          : alarmen.map((a, i) => <p key={i} style={{ fontSize: 12, padding: "2px 0", color: /noodstop|gepauzeerd|hashketen/i.test(a) ? C.red : C.yellow }}>● {a}</p>)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginBottom: 14 }}>
        <Stat label="Wacht op jou" waarde={w ? w.totaal : "…"} kleur={w && w.totaal ? C.yellow : C.green}
          sub={w ? w.extern + " naar buiten, " + w.intern + " intern (grens " + w.grens + ")" : ""} />
        <Stat label="Oudste wacht" waarde={w && w.totaal ? uren(w.oudste_uren) : "-"} kleur={w && w.oudste_uren > 72 ? C.red : C.text}
          sub={w && w.opnieuw_aangeboden ? w.opnieuw_aangeboden + " opnieuw aangeboden" : ""} />
        <div className="card" style={{ padding: "10px 12px" }}>
          <p className="lbl" style={{ marginBottom: 4 }}>Kosten vandaag</p>
          <p style={{ fontSize: 19, fontWeight: 700, fontFamily: "monospace", color: pct >= 90 ? C.red : C.text }}>{kosten ? eur(kosten.vandaag) : "…"}</p>
          <div style={{ height: 5, background: C.border, borderRadius: 3, marginTop: 8 }}><div style={{ width: pct + "%", height: "100%", background: pct >= 90 ? C.red : pct >= 70 ? C.yellow : C.green, borderRadius: 3 }} /></div>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>van {kosten ? eur(kosten.budget) : "…"} per dag</p>
        </div>
        <Stat label="Verzending" waarde={ov ? (ov.verzending.pauze ? "Gepauzeerd" : ov.verzending.aan ? "Aan" : "Uit") : "…"}
          kleur={ov && ov.verzending.pauze ? C.red : ov && ov.verzending.aan ? C.green : C.muted}
          sub={ov && ov.verzending.pauze ? ov.verzending.pauze : "alleen goedgekeurde mails"} />
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <p className="lbl" style={{ marginBottom: 6 }}>Pijplijn</p>
        <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
          Leads: {Object.keys(leads).length ? Object.entries(leads).map(([s, n]) => (VERKOOP_STATUS[s] || s) + " " + n).join(" · ") : "nog geen"}
          <br />Mails afgelopen 7 dagen: {Object.keys(mails).length ? Object.entries(mails).map(([s, n]) => s + " " + n).join(" · ") : "nog geen"}
        </p>
        <button className="btn bg" style={{ fontSize: 11, marginTop: 8 }} onClick={() => setView && setView("operatie")}>Naar Operatie</button>
      </div>

      {artikelen.length > 0 && (
        <div className="card" style={{ marginBottom: 14, borderColor: C.yellow + "55" }}>
          <p className="lbl" style={{ marginBottom: 4 }}>Artikelen voor de website · {artikelen.length}</p>
          <p style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>Concepten voor dif3r3nt.nl/kennis. Na "Publiceren" staan ze direct live. Afgewezen artikelen komen niet op de website.</p>
          {artikelen.map(a => (
            <div key={a.id} style={{ padding: "10px 0", borderTop: "1px solid " + C.border }}>
              <p style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{a.titel}</p>
              <p style={{ fontSize: 11, color: C.muted, margin: "2px 0 8px" }}>{String(a.created_at).slice(0, 10).split("-").reverse().join("-")}{a.onderwerp ? " · " + a.onderwerp : ""}</p>
              {artikelOpen === a.id && (
                <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 12, maxHeight: 320, overflowY: "auto", marginBottom: 8 }}>{artikelTekst[a.id] == null ? "Laden..." : artikelTekst[a.id]}</pre>
              )}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button className="btn bg" style={{ fontSize: 11 }} onClick={() => bekijkArtikel(a)}>{artikelOpen === a.id ? "Sluit" : "Lees"}</button>
                <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === a.id} onClick={() => actie(a.id, () => beslisArtikel(a, true), "✓ Gepubliceerd op de website")}>Publiceren</button>
                <button className="btn bg" style={{ fontSize: 11, color: C.red }} disabled={busy === a.id} onClick={() => actie(a.id, () => beslisArtikel(a, false), "Afgewezen")}>Afwijzen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        {gesorteerd.length === 0 ? <Leeg tekst="✓ Niets dat op je wacht" /> : (
          <Groep label="Wachtrij" aantal={gesorteerd.length} kleur={C.yellow}>
            {gesorteerd.map(r => {
              const g = growth[r.id];
              const p = (g && g.preview) || {};
              return (
                <Rij key={r.bron + r.id}
                  titel={<>
                    <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, marginRight: 6, background: r.risico === "extern" ? C.yellow + "22" : C.border, color: r.risico === "extern" ? C.yellow : C.muted }}>{r.risico === "extern" ? "NAAR BUITEN" : "INTERN"}</span>{" "}
                    {r.titel}
                  </>}
                  meta={<span>{r.agent} · {r.doel} · wacht {uren(r.leeftijd_uren)}{r.aangeboden_nr > 0 ? " · opnieuw aangeboden ×" + r.aangeboden_nr : ""}</span>}>
                  <div>
                  {r.waarom && <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{r.waarom}</p>}
                  {r.bron === "growth" && (p.onderwerp || p.body) && (
                    <div style={{ background: C.border + "55", borderRadius: 6, padding: 10, marginBottom: 8 }}>
                      {p.onderwerp && <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{p.onderwerp}</p>}
                      {p.body && <p style={{ fontSize: 12, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{p.body}</p>}
                    </div>
                  )}
                  {r.bron === "growth" ? (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <button className="btn bp" style={{ fontSize: 12 }} disabled={busy === r.id} onClick={() => actie(r.id, () => beslis(r, "approve"), "✓ Goedgekeurd")}>Goedkeuren</button>
                      <input style={{ maxWidth: 240 }} placeholder="Reden van afkeuren" value={reden[r.id] || ""} onChange={e => setReden(x => ({ ...x, [r.id]: e.target.value }))} />
                      <button className="btn bg" style={{ fontSize: 12 }} disabled={busy === r.id} onClick={() => actie(r.id, () => beslis(r, "reject"), "Afgekeurd")}>Afkeuren</button>
                      {r.agent === "outreach" && <span style={{ fontSize: 11, color: C.dim }}>Tekst aanpassen kan in Operatie.</span>}
                    </div>
                  ) : (
                    <p style={{ fontSize: 12, color: C.muted }}>Dit voorstel beoordeel en pas je toe in de lijst hieronder.</p>
                  )}
                  </div>
                </Rij>
              );
            })}
          </Groep>
        )}
      </div>

      <p className="lbl" style={{ margin: "18px 0 8px" }}>Voorstellen beoordelen en toepassen</p>
      <ApprovalQueue user={user} />

      <p style={{ fontSize: 11, color: C.dim, marginTop: 16 }}>
        Auditlog: {ov && ov.audit ? (ov.audit.ok ? "✓ " + ov.audit.regels + " regels, de keten klopt" : "✗ hashketen defect bij regel " + ov.audit.eerste_fout_seq) : "…"} · elk besluit wordt onveranderbaar vastgelegd.
      </p>
    </div>
  );
}

function Operatie({ user, services = [], pipeline = [], setView }) {
  const meKey = "furgel";
  const [ketens, setKetens] = useState([]);
  const [stappen, setStappen] = useState([]);
  const [settings, setSettings] = useState({});
  const [paused, setPaused] = useState(false);
  const [uitgegeven, setUitgegeven] = useState(0);
  const [sbFout, setSbFout] = useState([]);
  const [growthN, setGrowthN] = useState(0);
  const [wachtN, setWachtN] = useState(0);
  const [wachtLijst, setWachtLijst] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const [startMod, setStartMod] = useState(false);
  const [form, setForm] = useState({ titel: "", beschrijving: "", pipeline_item_id: "" });
  const [budgetInvoer, setBudgetInvoer] = useState("");
  const tickBezig = useRef(false);
  const laatsteTick = useRef(0);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3500); }

  async function load() {
    try {
      const dagStart = new Date(); dagStart.setUTCHours(0, 0, 0, 0);
      const week = new Date(Date.now() - 7 * 86400000).toISOString();
      const [k, s, a, c, sb, g, w, kos] = await Promise.all([
        supabase.from("operatie_ketens").select("*").order("created_at", { ascending: false }).limit(30),
        supabase.from("operatie_stappen").select("*").order("volgorde").limit(300),
        supabase.from("app_settings").select("key,value").in("key", ["operatie_dagbudget_eur", "operatie_model", "operatie_brief_email"]),
        supabase.from("sandbox_control").select("paused").eq("id", true).maybeSingle(),
        supabase.from("sandbox_runs").select("id,task_title,error,started_at").eq("status", "failed").gte("started_at", week).order("started_at", { ascending: false }).limit(8),
        supabase.from("growth_reviews").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("proposed_changes").select("id,task_type,source_agent,payload,created_at", { count: "exact" }).eq("status", "pending").order("created_at").limit(5),
        supabase.rpc("operatie_uitgegeven_vandaag"),
      ]);
      const eerste = [k, s, a, c].find(x => x.error);
      if (eerste) throw new Error(eerste.error.message);
      setKetens(k.data || []);
      setStappen(s.data || []);
      const st = {}; (a.data || []).forEach(r => { st[r.key] = r.value; });
      setSettings(st);
      setPaused(!!(c.data && c.data.paused));
      setSbFout(sb.data || []);
      setGrowthN(g.count || 0);
      setWachtN(w.count || 0);
      setWachtLijst(w.data || []);
      setUitgegeven(Number(kos.data || 0));
      setErr(null);
    } catch (e) { setErr(e.message || "Kon het overzicht niet laden"); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const ch = supabase.channel("operatie_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "operatie_ketens" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "operatie_stappen" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "proposed_changes" }, () => load())
      .subscribe();
    const iv = setInterval(load, 30000);
    return () => { supabase.removeChannel(ch); clearInterval(iv); };
  }, []);

  const keten = (id) => ketens.find(k => k.id === id);
  const actief = ketens.filter(k => k.status === "wacht" || k.status === "loopt");
  const stappenVan = (id) => stappen.filter(s => s.keten_id === id).sort((x, y) => x.volgorde - y.volgorde);
  const mislukt = stappen.filter(s => s.status === "mislukt" && keten(s.keten_id) && keten(s.keten_id).status === "mislukt");
  const vastgelopen = stappen.filter(s => s.status === "loopt" && s.started_at && (Date.now() - new Date(s.started_at).getTime()) > 10 * 60000);
  const cap = Number(settings.operatie_dagbudget_eur || 5);
  const pct = cap > 0 ? Math.min(100, Math.round((uitgegeven / cap) * 100)) : 0;
  const model = settings.operatie_model || "claude-opus-5";
  const briefMail = settings.operatie_brief_email === "aan";

  // Zolang dit scherm openstaat gaat de keten meteen door met de volgende stap.
  // De geplande taak op de achtergrond (elke 5 minuten) doet hetzelfde als het scherm dicht is.
  useEffect(() => {
    if (paused || tickBezig.current) return;
    const loopt = stappen.some(s => s.status === "loopt");
    const wacht = stappen.some(s => s.status === "wacht" && actief.some(k => k.id === s.keten_id));
    if (loopt || !wacht) return;
    if (cap > 0 && uitgegeven >= cap) return;   // dagbudget op, dan wacht het team tot morgen
    if (Date.now() < laatsteTick.current) return;
    tickBezig.current = true; laatsteTick.current = Date.now() + 8000;
    operatieCall("tick")
      .catch(e => { setErr(e.message); laatsteTick.current = Date.now() + 60000; })   // bij een fout een minuut wachten
      .finally(() => { tickBezig.current = false; load(); });
  }, [ketens, stappen, paused]);

  async function actie(id, fn, okMsg) {
    setBusy(id);
    try { const r = await fn(); if (okMsg) showToast(typeof okMsg === "function" ? okMsg(r) : okMsg); await load(); }
    catch (e) { showToast("✗ " + (e.message || "Mislukt")); }
    setBusy(null);
  }

  function kiesPipeline(id) {
    const p = pipeline.find(x => String(x.id) === String(id));
    const svc = p && services.find(s => (s.title || "").trim().toLowerCase() === (p.title || "").trim().toLowerCase());
    setForm({
      titel: p ? p.title : "",
      beschrijving: svc ? [svc.problem && ("Probleem: " + svc.problem), svc.target && ("Doelgroep: " + svc.target)].filter(Boolean).join("\n") : "",
      pipeline_item_id: p ? String(p.id) : "",
    });
  }

  async function startKeten() {
    if (form.titel.trim().length < 3) { showToast("✗ Geef het idee een titel van minimaal 3 tekens"); return; }
    await actie("start", () => operatieCall("start", { titel: form.titel.trim(), beschrijving: form.beschrijving.trim(), pipeline_item_id: form.pipeline_item_id || null }), "✓ Keten gestart, de eerste stap loopt zo");
    setStartMod(false); setForm({ titel: "", beschrijving: "", pipeline_item_id: "" });
  }

  async function bewaarInstelling(key, value) {
    const { error } = await supabase.from("app_settings").upsert([{ key, value: String(value), updated_by: meKey, updated_at: new Date().toISOString() }], { onConflict: "key" });
    if (error) throw new Error(error.message);
  }

  const stCol = (s) => s === "klaar" ? C.green : s === "loopt" ? C.blue : s === "mislukt" ? C.red : s === "overgeslagen" ? C.dim : C.yellow;
  const kStCol = (s) => s === "klaar" ? C.green : s === "loopt" ? C.blue : s === "mislukt" ? C.red : s === "geannuleerd" ? C.dim : C.yellow;
  const eur = (n) => "€" + Number(n || 0).toFixed(2).replace(".", ",");
  const tijd = (iso) => iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  function Stat({ label, waarde, kleur, sub, onClick }) {
    return (
      <div className="card" onClick={onClick} style={{ cursor: onClick ? "pointer" : "default", padding: 14 }}>
        <p className="lbl" style={{ marginBottom: 6 }}>{label}</p>
        <p style={{ fontSize: 24, fontWeight: 700, color: kleur || C.text, fontFamily: "monospace" }}>{waarde}</p>
        {sub && <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</p>}
      </div>
    );
  }

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace", maxWidth: "92vw" }}>{toast}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Operatie</h2>
          <p style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Het team werkt, jij geeft akkoord. Niets wordt gepubliceerd of verstuurd zonder jouw goedkeuring, of een stap die je zelf op automatisch zette.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn bp" onClick={() => setStartMod(true)} disabled={paused}>+ Start keten</button>
          <button className="btn bg" style={paused ? { color: C.red, borderColor: C.red } : {}} onClick={() => actie("pauze", async () => {
            const { error } = await supabase.from("sandbox_control").update({ paused: !paused, updated_at: new Date().toISOString() }).eq("id", true);
            if (error) throw new Error(error.message);
          }, paused ? "✓ Agenten hervat" : "✓ Alle agenten gepauzeerd")}>{paused ? "▶ Hervat agenten" : "⏸ Noodstop"}</button>
        </div>
      </div>
      {paused && <div className="card" style={{ borderColor: C.red + "66", background: C.redDim, marginBottom: 12 }}><p style={{ fontSize: 13, color: C.red, fontWeight: 600 }}>Alle agenten staan gepauzeerd. Er start niets nieuws tot je ze hervat.</p></div>}
      {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 12 }}><p style={{ fontSize: 12, color: C.red }}>{err}</p></div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, margin: "14px 0" }}>
        <Stat label="Wacht op jou" waarde={wachtN + growthN} kleur={(wachtN + growthN) ? C.yellow : C.green} sub={growthN ? wachtN + " voorstellen en " + growthN + " Growth" : "voorstellen"} />
        <Stat label="Loopt nu" waarde={actief.length} kleur={actief.length ? C.blue : C.muted} sub={actief.length ? "ketens actief" : "niets actief"} />
        <Stat label="Mislukt" waarde={mislukt.length + vastgelopen.length + sbFout.length} kleur={(mislukt.length + vastgelopen.length) ? C.red : C.muted} sub="laatste 7 dagen" />
        <div className="card" style={{ padding: 14 }}>
          <p className="lbl" style={{ marginBottom: 6 }}>Kosten vandaag</p>
          <p style={{ fontSize: 24, fontWeight: 700, fontFamily: "monospace", color: pct >= 90 ? C.red : C.text }}>{eur(uitgegeven)}</p>
          <div style={{ height: 5, background: C.border, borderRadius: 3, marginTop: 8 }}><div style={{ width: pct + "%", height: "100%", background: pct >= 90 ? C.red : pct >= 70 ? C.yellow : C.green, borderRadius: 3 }} /></div>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>van {eur(cap)} per dag</p>
        </div>
      </div>

      <p className="lbl" style={{ margin: "18px 0 8px" }}>01 / Wacht op jou</p>
      {growthN > 0 && (
        <div className="card" style={{ marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <p style={{ fontSize: 13 }}>{growthN} Growth-review{growthN === 1 ? "" : "s"} wacht{growthN === 1 ? "" : "en"} op jouw beoordeling.</p>
          <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setView && setView("growth")}>Naar Growth</button>
        </div>
      )}
      <ApprovalQueue user={user} />

      <p className="lbl" style={{ margin: "22px 0 8px" }}>02 / Verkoop</p>
      <OperatieVerkoop />

      <p className="lbl" style={{ margin: "22px 0 8px" }}>03 / Mislukt of vastgelopen</p>
      {mislukt.length === 0 && vastgelopen.length === 0 && sbFout.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 8 }}>Niets mislukt. 🎉</p></div>}
      {mislukt.map(s => (
        <div key={s.id} className="card" style={{ marginBottom: 8, borderColor: C.red + "44" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600 }}>{(keten(s.keten_id) || {}).titel}: {s.stap}</p>
              <p style={{ fontSize: 12, color: C.red, marginTop: 3, wordBreak: "break-word" }}>{s.fout || "Onbekende fout"}</p>
              <p style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>Poging {s.pogingen} van 3</p>
            </div>
            <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === s.id || s.pogingen >= 3} onClick={() => actie(s.id, () => operatieCall("retry", { stap_id: s.id }), "✓ Stap staat weer klaar")}>↻ Opnieuw</button>
          </div>
        </div>
      ))}
      {vastgelopen.map(s => (
        <div key={s.id} className="card" style={{ marginBottom: 8, borderColor: C.yellow + "55" }}>
          <p style={{ fontSize: 13 }}>{(keten(s.keten_id) || {}).titel}: {s.stap} loopt al meer dan 10 minuten.</p>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>De volgende controle markeert deze stap als mislukt, dan kun je hem opnieuw proberen.</p>
        </div>
      ))}
      {sbFout.length > 0 && (
        <div className="card" style={{ marginBottom: 8 }}>
          <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Mislukte sandbox-taken (7 dagen)</p>
          {sbFout.map(r => <p key={r.id} style={{ fontSize: 12, padding: "3px 0", borderBottom: "1px solid " + C.border, wordBreak: "break-word" }}><strong>{r.task_title || "taak"}</strong> <span style={{ color: C.red }}>{String(r.error || "").slice(0, 140)}</span> <span style={{ color: C.dim }}>{tijd(r.started_at)}</span></p>)}
        </div>
      )}

      <p className="lbl" style={{ margin: "22px 0 8px" }}>04 / Ketens</p>
      {loading && <div style={{ fontSize: 12, color: C.muted, padding: 8 }}>Laden...</div>}
      {!loading && ketens.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen ketens. Klik op "Start keten" om een idee door het team te laten uitwerken tot een analyse, launch-pakket en content.</p></div>}
      {ketens.map(k => {
        const lijst = stappenVan(k.id);
        const kost = lijst.reduce((t, s) => t + Number(s.kost_eur || 0), 0);
        return (
          <div key={k.id} className="card" style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, cursor: "pointer", flexWrap: "wrap" }} onClick={() => setOpen(open === k.id ? null : k.id)}>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{k.titel}</span>
                <span style={{ fontSize: 11, color: C.dim, marginLeft: 8 }}>{tijd(k.created_at)} · {eur(kost)}</span>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {lijst.map(s => <span key={s.id} title={s.stap + ": " + s.status}><Bdg label={s.agent} bg={C.bg} fg={stCol(s.status)} /></span>)}
                <Bdg label={k.status} bg={C.bg} fg={kStCol(k.status)} />
              </div>
            </div>
            {open === k.id && (
              <div style={{ marginTop: 12 }}>
                {k.beschrijving && <p style={{ fontSize: 12, color: C.muted, whiteSpace: "pre-wrap", marginBottom: 10 }}>{k.beschrijving}</p>}
                {lijst.map(s => (
                  <div key={s.id} style={{ padding: "8px 0", borderTop: "1px solid " + C.border }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{s.volgorde}. {s.agent}: {s.stap}</span>
                      <span style={{ fontSize: 11, color: stCol(s.status) }}>{s.status}{s.status === "klaar" ? " · " + eur(s.kost_eur) + " · " + s.model : ""}</span>
                    </div>
                    {s.decision_log && <p style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.55 }}><span style={{ color: C.dim }}>Waarom: </span>{s.decision_log}</p>}
                    {s.fout && <p style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{s.fout}</p>}
                  </div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  {(k.status === "wacht" || k.status === "loopt") && (
                    <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === k.id} onClick={() => actie(k.id, () => operatieCall("cancel", { keten_id: k.id }), "✓ Keten geannuleerd")}>Annuleer keten</button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <p className="lbl" style={{ margin: "22px 0 8px" }}>05 / Dagelijkse brief en instellingen</p>
      <div className="card" style={{ marginBottom: 10 }}>
        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Brief van vandaag</p>
        <p style={{ fontSize: 13, lineHeight: 1.7 }}>
          {wachtN + growthN === 0 ? "Er wacht niets op jou." : (wachtN + growthN) + " onderdeel" + (wachtN + growthN === 1 ? "" : "en") + " wacht" + (wachtN + growthN === 1 ? "" : "en") + " op jouw akkoord."}
          {" "}{actief.length ? actief.length + " keten" + (actief.length === 1 ? " loopt" : "s lopen") + " nu." : "Er loopt geen keten."}
          {" "}{(mislukt.length + vastgelopen.length) ? (mislukt.length + vastgelopen.length) + " stap" + ((mislukt.length + vastgelopen.length) === 1 ? " is" : "pen zijn") + " mislukt of vastgelopen." : "Niets mislukt."}
          {" "}Verbruik vandaag {eur(uitgegeven)} van {eur(cap)}.
        </p>
        {wachtLijst.length > 0 && <div style={{ marginTop: 8 }}>{wachtLijst.map(v => <p key={v.id} style={{ fontSize: 12, color: C.muted, padding: "2px 0" }}>• {(v.payload && v.payload.title) || v.task_type} <span style={{ color: C.dim }}>({v.source_agent || "?"})</span></p>)}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === "brief"} onClick={() => actie("brief", () => operatieCall("brief", { force: true }), r => r && r.verzonden ? "✓ Brief verstuurd naar je e-mail" : "✗ Niet verstuurd: " + ((r && r.reden) || "onbekend"))}>Verstuur de brief nu per e-mail</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 10 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <div>
            <p className="lbl" style={{ marginBottom: 5 }}>Dagbudget (euro)</p>
            <div style={{ display: "flex", gap: 6 }}>
              <input type="number" min="0" max="100" step="0.5" defaultValue={cap} key={"cap" + cap} onChange={e => setBudgetInvoer(e.target.value)} style={{ maxWidth: 110 }} />
              <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { const v = Number(budgetInvoer === "" ? cap : budgetInvoer); if (!(v >= 0 && v <= 100)) { showToast("✗ Kies een bedrag tussen 0 en 100"); return; } actie("budget", () => bewaarInstelling("operatie_dagbudget_eur", v), "✓ Dagbudget opgeslagen"); }}>Opslaan</button>
            </div>
            <p style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Bij het plafond stopt het team tot morgen. Het bedrag is een schatting.</p>
          </div>
          <div>
            <p className="lbl" style={{ marginBottom: 5 }}>Model</p>
            <select value={model} onChange={e => actie("model", () => bewaarInstelling("operatie_model", e.target.value), "✓ Model opgeslagen")}>
              {OPERATIE_MODELLEN.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <p className="lbl" style={{ marginBottom: 5 }}>Dagelijkse brief per e-mail (elke ochtend)</p>
            <select value={briefMail ? "aan" : "uit"} onChange={e => actie("mail", () => bewaarInstelling("operatie_brief_email", e.target.value), "✓ Instelling opgeslagen")}>
              <option value="uit">Uit</option>
              <option value="aan">Aan (alleen als er iets te melden is)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 10 }}>
        <p className="lbl" style={{ marginBottom: 6 }}>Wat het team wel en niet mag</p>
        {[0, 1, 2, 3].map(n => (
          <p key={n} style={{ fontSize: 12, color: C.muted, padding: "2px 0" }}><NiveauBadge niveau={n} /> <strong style={{ color: C.text }}>{RISK_LABEL[n]}</strong>: {[
            "loopt vanzelf (analyses, samenvattingen).",
            "loopt vanzelf, resultaat komt als voorstel in de wachtrij.",
            "wacht altijd op jouw akkoord (publiceren, mailen, uitrollen).",
            "voert een agent nooit zelf uit, alleen jij (geld, verwijderen, contracten).",
          ][n]}</p>
        ))}
      </div>

      {startMod && (
        <Portal><div className="ovl" onClick={() => setStartMod(false)}>
          <div className="mdl" onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: "monospace", marginBottom: 8, fontSize: 14, color: C.green }}>START KETEN</h3>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>Het team maakt drie dingen: een analyse, een launch-pakket en content. Alles komt als concept in "Wacht op jou". Kosten: ongeveer {model === "claude-opus-5" ? "€0,40" : model === "claude-sonnet-5" ? "€0,17" : "minder dan €0,10"}. Het team heeft geen toegang tot internet, dus de analyse is gebaseerd op jouw beschrijving.</p>
            {pipeline.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Kies een idee uit de bouwpipeline (optioneel)</p>
                <select value={form.pipeline_item_id} onChange={e => kiesPipeline(e.target.value)}>
                  <option value="">Eigen idee</option>
                  {pipeline.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Titel</p>
              <input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))} maxLength={120} placeholder="bijv. Offerte-assistent voor installateurs" autoFocus />
            </div>
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Beschrijving (voor wie, welk probleem, hoe verdien je eraan)</p>
              <textarea rows={6} value={form.beschrijving} onChange={e => setForm(p => ({ ...p, beschrijving: e.target.value }))} maxLength={4000} style={{ lineHeight: 1.6, fontSize: 13 }} placeholder="Hoe meer je hier opschrijft, hoe beter de uitkomst. Het team verzint geen feiten die jij niet noemt." />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn bp" disabled={busy === "start"} onClick={startKeten}>Start</button>
              <button className="btn bg" onClick={() => setStartMod(false)}>Annuleer</button>
            </div>
          </div>
        </div></Portal>
      )}
    </div>
  );
}

// ============================================================
// SEO en Zichtbaarheid voor de eigen sites: technische controle, AI-zichtbaarheid en concepten.
// Alles wat het team maakt komt als concept in de wachtrij; niets gaat live zonder jouw akkoord.
// Ontwerp: docs/Ontwerp_SEO_Zichtbaarheid.md
// ============================================================
const ISSUE_INFO = {
  ALLEEN_JAVASCRIPT: ["Pagina is leeg zonder JavaScript", "Zoekmachines en AI-crawlers die geen JavaScript uitvoeren zien niets. Dit is de belangrijkste oorzaak van slechte AI-zichtbaarheid van een webshop.", "Laat de server de echte inhoud (kop, tekst, producten) alvast in de HTML zetten, zoals de winkel al voor /winkel doet."],
  NIET_BEREIKBAAR: ["Pagina niet bereikbaar", "De pagina gaf geen antwoord of verwees naar een ander domein.", "Controleer de link en de server."],
  STATUS_4XX: ["Pagina geeft een foutmelding (4xx)", "Bezoekers en zoekmachines komen op een foutpagina.", "Herstel de pagina of leid door naar de juiste pagina en pas de links aan."],
  STATUS_5XX: ["Serverfout (5xx)", "De server gaf een fout terug.", "Bekijk de serverlogs."],
  REDIRECT_KETEN: ["Meerdere doorverwijzingen achter elkaar", "Elke extra stap kost laadtijd en verdunt de waarde van links.", "Verwijs in een keer naar de eindpagina."],
  TITEL_ONTBREEKT: ["Geen paginatitel", "De titel is het belangrijkste tekstelement in zoekresultaten.", "Voeg een unieke, beschrijvende title toe."],
  TITEL_TE_LANG: ["Titel te lang", "Google kapt titels af rond 60 tekens.", "Kort de titel in en zet het belangrijkste vooraan."],
  TITEL_TE_KORT: ["Titel te kort", "Een te korte titel gebruikt de ruimte niet.", "Maak de titel beschrijvender."],
  TITEL_DUBBEL: ["Dezelfde titel op meerdere pagina's", "Zoekmachines kunnen de pagina's niet uit elkaar houden.", "Geef elke pagina een eigen titel."],
  META_ONTBREEKT: ["Geen meta description", "Zonder beschrijving kiest Google zelf een fragment.", "Schrijf een beschrijving van 120 tot 155 tekens."],
  META_TE_LANG: ["Meta description te lang", "Wordt afgekapt in zoekresultaten.", "Kort in tot maximaal 155 tekens."],
  META_DUBBEL: ["Dezelfde meta description op meerdere pagina's", "Minder onderscheidend in zoekresultaten.", "Schrijf per pagina een eigen beschrijving."],
  H1_ONTBREEKT: ["Geen h1-kop", "De hoofdkop helpt zoekmachines het onderwerp te begrijpen.", "Voeg een h1 toe in de HTML."],
  H1_MEERDERE: ["Meerdere h1-koppen", "Verwarrend voor de paginastructuur.", "Gebruik een h1 en h2 voor de rest."],
  CANONICAL_ONTBREEKT: ["Geen canonical-link", "Zonder canonical kan dezelfde pagina onder meerdere adressen worden gezien.", "Voeg een canonical toe die naar de pagina zelf verwijst."],
  CANONICAL_ANDERS: ["Canonical wijst naar een andere pagina", "Google kan deze pagina negeren en alleen de andere tonen.", "Laat de canonical naar de pagina zelf verwijzen, tenzij je bewust wilt consolideren."],
  OG_ONTBREEKT: ["Geen Open Graph-gegevens", "Links naar deze pagina zien er slecht uit op LinkedIn, WhatsApp en dergelijke.", "Voeg og:title, og:description en og:image toe."],
  LANG_ONTBREEKT: ["Geen taalattribuut", "Helpt zoekmachines en schermlezers de taal te bepalen.", "Zet lang=\"nl\" op het html-element."],
  JSONLD_ONTBREEKT: ["Geen structured data", "Structured data (JSON-LD) helpt zoekmachines en AI producten, organisatie en veelgestelde vragen te begrijpen.", "Voeg passende schema.org-gegevens toe, bijvoorbeeld Product, Organization en FAQPage."],
  JSONLD_ONGELDIG: ["Ongeldige structured data", "Foute structured data is erger dan geen.", "Corrigeer de JSON-LD."],
  DUN_CONTENT: ["Weinig tekst", "Pagina's met weinig tekst scoren en worden zelden geciteerd.", "Voeg nuttige, concrete informatie toe."],
  AFBEELDING_ZONDER_ALT: ["Afbeeldingen zonder alt-tekst", "Slecht voor toegankelijkheid en beeldzoekopdrachten.", "Voeg een korte beschrijving toe aan elke inhoudelijke afbeelding."],
  NIET_IN_SITEMAP: ["Niet in de sitemap", "Zoekmachines vinden de pagina minder snel.", "Neem de pagina op in de sitemap."],
  NOINDEX_IN_SITEMAP: ["Noindex-pagina in de sitemap", "Tegenstrijdig signaal.", "Haal de pagina uit de sitemap of verwijder noindex."],
  WEES_PAGINA: ["Geen interne links naar deze pagina", "Zoekmachines vinden hem alleen via de sitemap en geven hem weinig gewicht.", "Link vanaf relevante pagina's naar deze pagina."],
  SITEMAP_ONTBREEKT: ["Geen sitemap", "Een sitemap helpt zoekmachines alle pagina's te vinden.", "Publiceer een sitemap.xml en verwijs ernaar in robots.txt."],
  ROBOTS_ONTBREEKT: ["Geen robots.txt", "Niet fataal, maar het is de plek om de sitemap te melden.", "Publiceer een robots.txt."],
  ROBOTS_BLOKKEERT_ALLES: ["robots.txt blokkeert de hele site", "Zoekmachines mogen niets crawlen.", "Verwijder Disallow: / tenzij dit bewust is."],
  HTTP_NAAR_HTTPS: ["http verwijst niet naar https", "Bezoekers kunnen op een onveilige versie belanden.", "Stel een permanente doorverwijzing naar https in."],
  WWW_DUBBEL: ["www en zonder www tonen allebei de site", "Dubbele inhoud onder twee adressen.", "Verwijs een van de twee permanent door naar de andere."],
  CRAWL_AFGEKAPT: ["Niet alle pagina's gecontroleerd", "De controle stopte bij de limiet van 60 pagina's.", "Geen actie nodig; de belangrijkste pagina's zijn gecontroleerd."],
};

const FIXBARE_CODES = new Set([
  "TITEL_ONTBREEKT", "TITEL_TE_LANG", "TITEL_TE_KORT", "TITEL_DUBBEL",
  "META_ONTBREEKT", "META_TE_LANG", "META_DUBBEL",
  "H1_ONTBREEKT", "H1_MEERDERE",
  "CANONICAL_ONTBREEKT", "CANONICAL_ANDERS",
  "OG_ONTBREEKT", "JSONLD_ONTBREEKT", "JSONLD_ONGELDIG",
  "DUN_CONTENT",
]);

async function seoCall(action, payload = {}) {
  const { data, error } = await supabase.functions.invoke("seo-zichtbaarheid", { body: { action, ...payload } });
  if (error) {
    let msg = error.message || "Aanroep mislukt";
    try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* standaardbericht */ }
    throw new Error(msg);
  }
  if (data && data.error) throw new Error(data.error);
  return data;
}

function Zichtbaarheid({ user, setView }) {
  const meKey = "furgel";
  const [sites, setSites] = useState([]);
  const [audits, setAudits] = useState([]);
  const [snaps, setSnaps] = useState([]);
  const [settings, setSettings] = useState({});
  const [prompts, setPrompts] = useState([]);
  const [runs, setRuns] = useState([]);
  const [concepten, setConcepten] = useState([]);
  const [toegepast, setToegepast] = useState(0);
  const [uitgegeven, setUitgegeven] = useState(0);
  const [paused, setPaused] = useState(false);
  const [issues, setIssues] = useState([]);
  const [paginas, setPaginas] = useState([]);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("problemen");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nieuweVraag, setNieuweVraag] = useState("");
  const [nieuwOnderwerp, setNieuwOnderwerp] = useState("");
  const [form, setForm] = useState(null);
  const [budgetInvoer, setBudgetInvoer] = useState("");
  const [weergave, setWeergave] = useState("site");
  const tickBezig = useRef(false);
  const laatsteTick = useRef(0);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3500); }
  const eur = (n) => "€" + Number(n || 0).toFixed(2).replace(".", ",");
  const tijd = (iso) => iso ? new Date(iso).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  async function load() {
    try {
      const dagStart = new Date(); dagStart.setUTCHours(0, 0, 0, 0);
      const [a, b, c, d, e, f, g, h, i, j, k] = await Promise.all([
        supabase.from("seo_sites").select("*").order("created_at"),
        supabase.from("seo_audits").select("*").order("gestart_op", { ascending: false }).limit(60),
        supabase.from("seo_snapshots").select("*").order("datum"),
        supabase.from("app_settings").select("key,value").in("key", ["seo_dagbudget_eur", "seo_model", "seo_minuten_per_voorstel"]),
        supabase.from("geo_prompts").select("*").order("created_at"),
        supabase.from("geo_runs").select("*").order("created_at", { ascending: false }).limit(150),
        supabase.from("seo_concepten").select("*").order("created_at", { ascending: false }).limit(40),
        supabase.from("proposed_changes").select("id", { count: "exact", head: true }).eq("created_by", "seo").eq("status", "applied"),
        supabase.from("geo_runs").select("kosten_eur").gte("created_at", dagStart.toISOString()),
        supabase.from("seo_concepten").select("kosten_eur").gte("created_at", dagStart.toISOString()),
        supabase.from("sandbox_control").select("paused").eq("id", true).maybeSingle(),
      ]);
      const eerste = [a, b, c, d, e, f, g].find(x => x.error);
      if (eerste) throw new Error(eerste.error.message);
      setSites(a.data || []); setAudits(b.data || []); setSnaps(c.data || []);
      const st = {}; (d.data || []).forEach(r => { st[r.key] = r.value; }); setSettings(st);
      setPrompts(e.data || []); setRuns(f.data || []); setConcepten(g.data || []);
      setToegepast(h.count || 0);
      setUitgegeven([...(i.data || []), ...(j.data || [])].reduce((t, r) => t + Number(r.kosten_eur || 0), 0));
      setPaused(!!(k.data && k.data.paused));
      setErr(null);
      setSel(cur => cur || ((a.data || [])[0] ? a.data[0].id : null));
    } catch (ex) { setErr(ex.message || "Kon het overzicht niet laden"); }
    setLoading(false);
  }

  async function laadDetail(siteId) {
    if (!siteId) return;
    const [x, y] = await Promise.all([
      supabase.from("seo_issues").select("*").eq("site_id", siteId).in("status", ["open", "genegeerd"]).limit(1500),
      supabase.from("seo_pages").select("*").eq("site_id", siteId).order("pad").limit(300),
    ]);
    setIssues(x.data || []); setPaginas(y.data || []);
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { laadDetail(sel); }, [sel, audits.length && audits[0] && audits[0].status]);
  useEffect(() => {
    const ch = supabase.channel("seo_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_audits" }, () => { load(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "geo_runs" }, () => { load(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "geo_prompts" }, () => { load(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "seo_concepten" }, () => { load(); })
      .subscribe();
    const iv = setInterval(load, 45000);
    return () => { supabase.removeChannel(ch); clearInterval(iv); };
  }, []);

  const site = sites.find(s => s.id === sel) || null;
  const cap = Number(settings.seo_dagbudget_eur || 2);
  const pct = cap > 0 ? Math.min(100, Math.round((uitgegeven / cap) * 100)) : 0;
  const model = settings.seo_model || "claude-opus-5";
  const minuten = Number(settings.seo_minuten_per_voorstel || 30);

  const laatsteAudit = (id) => audits.find(a => a.site_id === id);
  const promptsVan = (id) => prompts.filter(p => p.site_id === id);
  const laatsteRun = (pid) => runs.find(r => r.prompt_id === pid);
  const geoStand = (id) => {
    const l = promptsVan(id).filter(p => p.actief).map(p => laatsteRun(p.id)).filter(r => r && !r.fout && !r.waarschuwing);
    return { totaal: l.length, geciteerd: l.filter(r => r.geciteerd).length, genoemd: l.filter(r => r.genoemd || r.geciteerd).length };
  };
  // Score en trend voor de AI-zichtbaarheid tegel: citaties tellen vol mee, vermeldingen half.
  const geoScore = (id) => {
    const g = geoStand(id);
    if (!g.totaal) return null;
    return Math.round(((g.geciteerd + (g.genoemd - g.geciteerd) * 0.5) / g.totaal) * 100);
  };
  const geoTrend = (id) => {
    const pids = new Set(promptsVan(id).map(p => p.id));
    const relevant = runs.filter(r => pids.has(r.prompt_id) && !r.fout && !r.waarschuwing);
    const byWeek = {};
    relevant.forEach(r => {
      const d = new Date(r.created_at);
      const onejan = new Date(d.getFullYear(), 0, 1);
      const week = Math.ceil((((d - onejan) / 86400000) + onejan.getDay() + 1) / 7);
      const key = d.getFullYear() + "-W" + week;
      (byWeek[key] = byWeek[key] || []).push(r);
    });
    return Object.keys(byWeek).sort().map(k => {
      const list = byWeek[k];
      return { week: k, pct: Math.round((list.filter(r => r.geciteerd).length / list.length) * 100) };
    });
  };

  // Zolang dit scherm openstaat gaan de metingen door. De planning op de achtergrond doet dat ook.
  useEffect(() => {
    if (paused || tickBezig.current) return;
    const nu = Date.now();
    const bezig = prompts.some(p => p.bezig_sinds);
    const klaar = prompts.some(p => p.actief && !p.bezig_sinds && p.volgende_run && new Date(p.volgende_run).getTime() <= nu && sites.some(s => s.id === p.site_id && s.actief && s.bevestigd_eigen));
    if (bezig || !klaar) return;
    if (cap > 0 && uitgegeven >= cap) return;
    if (nu < laatsteTick.current) return;
    tickBezig.current = true; laatsteTick.current = nu + 8000;
    seoCall("tick")
      .catch(ex => { setErr(ex.message); laatsteTick.current = Date.now() + 60000; })
      .finally(() => { tickBezig.current = false; load(); });
  }, [prompts, sites, paused, uitgegeven]);

  async function actie(id, fn, okMsg) {
    setBusy(id);
    try { const r = await fn(); if (okMsg) showToast(typeof okMsg === "function" ? okMsg(r) : okMsg); await load(); if (sel) await laadDetail(sel); }
    catch (ex) { showToast("✗ " + (ex.message || "Mislukt")); }
    setBusy(null);
  }
  async function bewaarInstelling(key, value) {
    const { error } = await supabase.from("app_settings").upsert([{ key, value: String(value), updated_by: meKey, updated_at: new Date().toISOString() }], { onConflict: "key" });
    if (error) throw new Error(error.message);
  }

  const ernstKleur = (e) => e === "hoog" ? C.red : e === "midden" ? C.yellow : C.cyan;
  const scoreKleur = (s) => s == null ? C.muted : s >= 80 ? C.green : s >= 60 ? C.yellow : C.red;
  const scoreLabel = (s) => s == null ? "nog niet gemeten" : s >= 80 ? "goed" : s >= 60 ? "aandacht nodig" : "kritiek";

  // Problemen per code groeperen
  const groepen = (() => {
    const m = {};
    issues.filter(i => i.status === "open").forEach(i => { (m[i.code] = m[i.code] || { code: i.code, ernst: i.ernst, items: [] }).items.push(i); });
    const w = { hoog: 0, midden: 1, laag: 2 };
    return Object.values(m).sort((a, b) => (w[a.ernst] - w[b.ernst]) || (b.items.length - a.items.length));
  })();
  const genegeerd = issues.filter(i => i.status === "genegeerd");

  function statusBadge(r) {
    if (!r) return <Bdg label="nog niet gemeten" bg={C.bg} fg={C.dim} />;
    if (r.fout) return <Bdg label="fout" bg={C.bg} fg={C.red} />;
    if (r.waarschuwing) return <Bdg label="onbetrouwbaar" bg={C.yellowDim} fg={C.yellow} />;
    if (r.geciteerd) return <Bdg label={"geciteerd" + (r.positie ? " #" + r.positie : "")} bg={C.greenDim} fg={C.green} />;
    if (r.in_bronnen) return <Bdg label="in bronnen" bg={C.blueDim} fg={C.blue} />;
    if (r.genoemd) return <Bdg label="genoemd" bg={C.blueDim} fg={C.blue} />;
    return <Bdg label="niet gevonden" bg={C.redDim} fg={C.red} />;
  }

  function slaSiteOp() {
    const f = form; if (!f) return;
    const domein = String(f.domein || "").trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (!domein || !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domein)) { showToast("✗ Vul een geldig domein in, bijvoorbeeld voorbeeld.nl"); return; }
    if (!f.bevestigd_eigen) { showToast("✗ Bevestig dat dit je eigen domein is"); return; }
    const dienst = String(f.winkel_dienst || "").trim();
    if (f.meetbron === "koppeling" && !dienst) { showToast("✗ Vul de dienstnaam van de winkelkoppeling in"); return; }
    // Startadres van de controle (bij winkels bijvoorbeeld /winkel) blijft staan zolang het domein gelijk blijft.
    const oud = f.id ? sites.find(x => x.id === f.id) : null;
    const basis_url = oud && oud.domein === domein && oud.basis_url ? oud.basis_url : "https://" + domein + "/";
    const rij = { naam: String(f.naam || domein).trim(), domein, basis_url, soort: f.soort || "website", beschrijving: String(f.beschrijving || "").trim() || null, merknamen: String(f.merknamen || "").split(",").map(x => x.trim()).filter(Boolean), actief: !!f.actief, bevestigd_eigen: true, meetbron: f.meetbron || null, winkel_dienst: dienst || null };
    actie("site", async () => {
      const q = f.id ? supabase.from("seo_sites").update(rij).eq("id", f.id) : supabase.from("seo_sites").insert([rij]);
      const { error } = await q; if (error) throw new Error(error.message);
      setForm(null);
    }, "✓ Site opgeslagen");
  }

  const weergaveTabs = (
    <div style={{ display: "flex", gap: 7, marginBottom: 12 }}>
      {[["site", "Per site"], ["dienst", "Per dienst"], ["radar", "AI-radar"]].map(([k, l]) => (
        <span key={k} className={"tab" + (weergave === k ? " on" : "")} onClick={() => setWeergave(k)}>{l}</span>
      ))}
    </div>
  );
  if (weergave === "dienst") return <div className="fu">{weergaveTabs}<SeoPerDienst /></div>;
  if (weergave === "radar") return <div className="fu">{weergaveTabs}<AiRadar /></div>;

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace", maxWidth: "92vw" }}>{toast}</div>}
      {weergaveTabs}
      <div style={{ marginBottom: 10 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Zichtbaarheid</h2>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Technische SEO en AI-zichtbaarheid van je eigen sites. Concepten komen in "Wacht op jou"; niets wordt gepubliceerd zonder jouw akkoord.</p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 8, fontSize: 11, color: C.muted }}>
          <span><span style={{ color: C.green, fontWeight: 700 }}>●</span> 80-100 goed</span>
          <span><span style={{ color: C.yellow, fontWeight: 700 }}>●</span> 60-79 aandacht nodig</span>
          <span><span style={{ color: C.red, fontWeight: 700 }}>●</span> 0-59 kritiek</span>
          <span>Score daalt met het aantal en de ernst van gevonden problemen (hoog telt het zwaarst). Klik op een probleem hieronder om te zien wat het is en hoe je het oplost.</span>
        </div>
      </div>
      {paused && <div className="card" style={{ borderColor: C.red + "66", background: C.redDim, marginBottom: 10 }}><p style={{ fontSize: 13, color: C.red, fontWeight: 600 }}>Alle agenten staan gepauzeerd (noodstop in Operatie). Metingen wachten.</p></div>}
      {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 10 }}><p style={{ fontSize: 12, color: C.red }}>{err}</p></div>}
      {loading && <div style={{ fontSize: 12, color: C.muted, padding: 8 }}>Laden...</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 10, marginBottom: 12 }}>
        {sites.map(s => {
          const au = laatsteAudit(s.id); const g = geoStand(s.id);
          return (
            <div key={s.id} className="card" onClick={() => setSel(s.id)} style={{ cursor: "pointer", padding: 14, borderColor: sel === s.id ? C.green + "88" : undefined, opacity: s.actief ? 1 : 0.55 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600 }}>{s.naam}</p>
                  <p style={{ fontSize: 11, color: C.dim, wordBreak: "break-all" }}>{s.domein}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontSize: 28, fontWeight: 700, fontFamily: "monospace", color: scoreKleur(au && au.score), lineHeight: 1 }}>{au && au.score != null ? au.score : "-"}</p>
                  <p style={{ fontSize: 10, color: scoreKleur(au && au.score), marginTop: 2 }}>{scoreLabel(au && au.score)}</p>
                </div>
              </div>
              {!s.bevestigd_eigen && <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>Nog niet bevestigd als eigen domein.</p>}
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {au && au.status === "loopt" ? <Bdg label="controle loopt" bg={C.bg} fg={C.blue} /> : au ? <>
                  <Bdg label={au.issues_hoog + " hoog"} bg={C.bg} fg={au.issues_hoog ? C.red : C.dim} />
                  <Bdg label={au.issues_midden + " midden"} bg={C.bg} fg={au.issues_midden ? C.yellow : C.dim} />
                  <Bdg label={au.issues_laag + " laag"} bg={C.bg} fg={C.dim} />
                </> : <Bdg label="nog niet gecontroleerd" bg={C.bg} fg={C.dim} />}
              </div>
              <p style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{au ? au.paginas + " pagina's, " + tijd(au.gestart_op) : "Geen controle gedaan"}</p>
              <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>AI-zichtbaarheid: {g.totaal ? g.geciteerd + " van " + g.totaal + " vragen geciteerd, " + g.genoemd + " genoemd" : "nog niet gemeten"}</p>
            </div>
          );
        })}
        <div className="card" onClick={() => { setSel(null); setForm({ naam: "", domein: "", soort: "website", beschrijving: "", merknamen: "", actief: true, bevestigd_eigen: false, meetbron: "", winkel_dienst: "" }); setTab("site"); }} style={{ cursor: "pointer", padding: 14, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 13, minHeight: 90 }}>+ Site toevoegen</div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <p className="lbl" style={{ marginBottom: 8 }}>Meetpunten (nulmeting tegenover nu)</p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "4px 8px 4px 0" }}>Site</th><th>Score</th><th>Bezoekers (30 d)</th><th>Via zoekmachines (30 d)</th><th>Leads of bestellingen (30 d)</th><th>AI-citatiegraad</th></tr></thead>
            <tbody>
              {sites.map(s => {
                const lijst = snaps.filter(x => x.site_id === s.id);
                const eerst = lijst[0], nu = lijst[lijst.length - 1]; const g = geoStand(s.id);
                const vergelijk = (a, b) => a == null || b == null ? "-" : (b === a ? String(b) : a + " → " + b);
                // Nulmeting per meetwaarde: het eerste meetpunt waarin die waarde echt gemeten is.
                const reeks = (veld) => {
                  const m = lijst.filter(x => x[veld] != null);
                  if (!m.length) return null;
                  const a = m[0][veld], b = m[m.length - 1][veld];
                  return { tekst: a === b ? String(b) : a + " → " + b, laatste: b };
                };
                const winkel = s.soort === "shop";
                const live = winkel && s.meetbron === "koppeling" ? s.winkel_dienst : null;
                const bezoek = reeks("bezoekers_30d"), zoek = reeks("organisch_30d");
                const conv = winkel ? reeks("bestellingen_30d") : reeks("leads_30d");
                const convWoord = !conv ? "" : winkel ? (conv.laatste === 1 ? " bestelling" : " bestellingen") : (conv.laatste === 1 ? " lead" : " leads");
                return (
                  <tr key={s.id} style={{ borderTop: "1px solid " + C.border }}>
                    <td style={{ padding: "6px 8px 6px 0", fontWeight: 600 }}>{s.naam}</td>
                    <td>{eerst ? vergelijk(eerst.score, nu.score) : "-"}</td>
                    <td>{bezoek ? bezoek.tekst : live ? <WinkelLive dienst={live} veld="bezoekers" /> : "-"}</td>
                    <td>{zoek ? zoek.tekst : live ? <WinkelLive dienst={live} veld="organisch" /> : "-"}</td>
                    <td>{conv ? <>{conv.tekst}<span style={{ color: C.dim }}>{convWoord}</span></> : "-"}</td>
                    <td>{g.totaal ? Math.round((g.geciteerd / g.totaal) * 100) + "% (" + g.geciteerd + "/" + g.totaal + ")" : "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Elke dag een meetpunt per site; de eerste gemeten waarde is de nulmeting. Zoekmachines: Google, Bing, DuckDuckGo, Ecosia, Yahoo en Startpage. De bron per site staat bij Site.</p>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Bespaarde tijd: {toegepast} toegepast{toegepast === 1 ? "" : "e"} voorstel{toegepast === 1 ? "" : "len"} x {minuten} minuten = <strong style={{ color: C.text }}>{Math.round((toegepast * minuten) / 6) / 10} uur</strong> (schatting, instelbaar hieronder).</p>
      </div>

      {site && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              {[["problemen", "Problemen" + (groepen.length ? " · " + issues.filter(i => i.status === "open").length : "")], ["paginas", "Pagina's"], ["geo", "AI-zichtbaarheid"], ["concepten", "Concepten"], ["site", "Site"]].map(([k, l]) => (
                <span key={k} className={"tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}>{l}</span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
              <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === "crawl" || !site.bevestigd_eigen || (laatsteAudit(site.id) && laatsteAudit(site.id).status === "loopt")} onClick={() => actie("crawl", () => seoCall("crawl", { site_id: site.id }), "✓ Controle gestart, resultaat over enkele seconden")}>Controleer nu</button>
              <button className="btn bg" style={{ fontSize: 11 }} disabled={busy === "geo" || !site.bevestigd_eigen || paused} onClick={() => actie("geo", () => seoCall("geo_nu", { site_id: site.id }), r => "✓ " + r.klaargezet + " vragen staan klaar voor meting")}>Meet AI-zichtbaarheid nu</button>
            </div>
          </div>

          {tab === "problemen" && (
            <div>
              {groepen.length > 0 && (
                <p style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
                  <span style={{ color: C.red, fontWeight: 600 }}>Hoog</span> = raakt Google of AI-crawlers direct, pak dit eerst aan · <span style={{ color: C.yellow, fontWeight: 600 }}>Midden</span> = verzwakt de vindbaarheid · <span style={{ color: C.cyan, fontWeight: 600 }}>Laag</span> = kleine verbetering. Klik op een probleem voor uitleg en oplossing.
                </p>
              )}
              {groepen.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>{laatsteAudit(site.id) ? "Geen open problemen. 🎉" : "Nog geen controle gedaan. Klik op Controleer nu."}</p></div>}
              {groepen.map(gr => {
                const info = ISSUE_INFO[gr.code] || [gr.code, "", ""];
                return (
                  <div key={gr.code} className="card" style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, cursor: "pointer", flexWrap: "wrap" }} onClick={() => setOpen(open === gr.code ? null : gr.code)}>
                      <div style={{ minWidth: 0 }}><Bdg label={gr.ernst} bg={C.bg} fg={ernstKleur(gr.ernst)} /> <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 6 }}>{info[0]}</span></div>
                      <span style={{ fontSize: 12, color: C.muted }}>{gr.items.length} pagina{gr.items.length === 1 ? "" : "'s"}</span>
                    </div>
                    {open === gr.code && (
                      <div style={{ marginTop: 10 }}>
                        {info[1] && <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{info[1]}</p>}
                        {info[2] && <p style={{ fontSize: 12, lineHeight: 1.6, marginTop: 6 }}><strong style={{ color: C.green }}>Oplossing: </strong>{info[2]}</p>}
                        <div style={{ marginTop: 8, maxHeight: 240, overflowY: "auto" }}>
                          {gr.items.slice(0, 60).map(i => <p key={i.id} style={{ fontSize: 11, padding: "3px 0", borderTop: "1px solid " + C.border, wordBreak: "break-all" }}><a href={i.url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>{i.url.replace(/^https:\/\//, "")}</a> <span style={{ color: C.dim }}>{i.detail}</span></p>)}
                          {gr.items.length > 60 && <p style={{ fontSize: 11, color: C.dim, paddingTop: 4 }}>en {gr.items.length - 60} meer</p>}
                        </div>
                        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                          {FIXBARE_CODES.has(gr.code) && (
                            <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === "fix" + gr.code || paused || !site.bevestigd_eigen} onClick={() => actie("fix" + gr.code, () => seoCall("issue_fix", { site_id: site.id, code: gr.code }), "✓ Fix-voorstel wordt gemaakt, over ongeveer een minuut in Wacht op jou")}>Maak fix-voorstel</button>
                          )}
                          <button className="btn bg" style={{ fontSize: 11 }} onClick={() => actie("neg" + gr.code, async () => { const { error } = await supabase.from("seo_issues").update({ status: "genegeerd" }).eq("site_id", site.id).eq("code", gr.code).eq("status", "open"); if (error) throw new Error(error.message); }, "✓ Genegeerd, het komt niet meer terug")}>Negeer dit probleem</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {genegeerd.length > 0 && (
                <div className="card" style={{ marginTop: 10 }}>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Genegeerd ({genegeerd.length})</p>
                  {[...new Set(genegeerd.map(i => i.code))].map(code => <div key={code} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ fontSize: 12 }}>{(ISSUE_INFO[code] || [code])[0]}</span><button className="btn bg" style={{ fontSize: 10 }} onClick={() => actie("her" + code, async () => { const { error } = await supabase.from("seo_issues").update({ status: "open" }).eq("site_id", site.id).eq("code", code).eq("status", "genegeerd"); if (error) throw new Error(error.message); }, "✓ Heropend")}>Heropen</button></div>)}
                </div>
              )}
            </div>
          )}

          {tab === "paginas" && (
            <div className="card" style={{ overflowX: "auto" }}>
              {paginas.length === 0 ? <p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen pagina's gecrawld.</p> : (
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <thead><tr style={{ color: C.muted, textAlign: "left" }}><th style={{ padding: "4px 8px 4px 0" }}>Pagina</th><th>Status</th><th>Titel</th><th>Woorden</th><th>Links in</th><th>Structured data</th></tr></thead>
                  <tbody>{paginas.map(p => (
                    <tr key={p.id} style={{ borderTop: "1px solid " + C.border }}>
                      <td style={{ padding: "5px 8px 5px 0", wordBreak: "break-all" }}><a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>{p.pad || p.url}</a></td>
                      <td style={{ color: p.statuscode >= 400 || !p.statuscode ? C.red : C.green }}>{p.statuscode || "-"}{p.redirect_naar ? " ↪" : ""}</td>
                      <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.titel || <span style={{ color: C.red }}>ontbreekt</span>}</td>
                      <td>{p.woorden == null ? "-" : p.woorden}</td>
                      <td>{p.links_in}</td>
                      <td style={{ color: p.jsonld_types && p.jsonld_types.length ? C.text : C.dim }}>{p.jsonld_types && p.jsonld_types.length ? p.jsonld_types.join(", ") : "geen"}</td>
                    </tr>))}</tbody>
                </table>
              )}
            </div>
          )}

          {tab === "geo" && (
            <div>
              {(() => {
                const g = geoStand(site.id); const score = geoScore(site.id); const trend = geoTrend(site.id).slice(-10);
                return (
                  <div className="card" style={{ marginBottom: 10 }}>
                    <p className="lbl" style={{ marginBottom: 10 }}>AI-zichtbaarheid overzicht</p>
                    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{ textAlign: "center" }}>
                        <p style={{ fontSize: 36, fontWeight: 700, fontFamily: "monospace", color: scoreKleur(score), lineHeight: 1 }}>{score == null ? "-" : score}</p>
                        <p style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>visibility score</p>
                      </div>
                      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                        <div><p style={{ fontSize: 20, fontWeight: 700 }}>{g.totaal}</p><p style={{ fontSize: 10, color: C.muted }}>vragen gevolgd</p></div>
                        <div><p style={{ fontSize: 20, fontWeight: 700, color: C.blue }}>{g.genoemd}</p><p style={{ fontSize: 10, color: C.muted }}>genoemd</p></div>
                        <div><p style={{ fontSize: 20, fontWeight: 700, color: C.green }}>{g.geciteerd}</p><p style={{ fontSize: 10, color: C.muted }}>geciteerd</p></div>
                      </div>
                      {trend.length > 1 && (
                        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 40 }}>
                          {trend.map((t, i) => (
                            <div key={t.week} title={t.week + ": " + t.pct + "%"} style={{ width: 8, height: Math.max(3, t.pct * 0.4), background: C.green, opacity: 0.4 + (i / trend.length) * 0.6, borderRadius: 2 }} />
                          ))}
                        </div>
                      )}
                    </div>
                    <p style={{ fontSize: 11, color: C.dim, marginTop: 10 }}>Score weegt citaties vol en vermeldingen half mee, tegenover het aantal gevolgde vragen. Gemeten via Claude met de webzoektool, wekelijks per vraag; geen metingen op andere AI-platformen.</p>
                  </div>
                );
              })()}
              <div className="card" style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>Claude beantwoordt de vragen hieronder met de webzoektool, zoals een AI-zoekassistent dat voor een klant doet, zonder te weten wie jij bent. We leggen vast of {site.naam} genoemd of geciteerd wordt. Het is een steekproef en dus een indicatie, geen meting. Elke vraag wordt wekelijks herhaald.</p>
                <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                  <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === "seed"} onClick={() => actie("seed", () => seoCall("seed", { site_id: site.id }), r => "✓ " + r.toegevoegd + " vragen voorgesteld")}>Laat Claude vragen voorstellen</button>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <input value={nieuweVraag} onChange={e => setNieuweVraag(e.target.value)} placeholder="Eigen vraag, zoals een klant die stelt" maxLength={300} />
                  <button className="btn bg" style={{ fontSize: 11 }} disabled={nieuweVraag.trim().length < 8} onClick={() => actie("vraag", async () => { const { error } = await supabase.from("geo_prompts").insert([{ site_id: site.id, vraag: nieuweVraag.trim(), categorie: "eigen" }]); if (error) throw new Error(error.message); setNieuweVraag(""); }, "✓ Vraag toegevoegd")}>Toevoegen</button>
                </div>
              </div>
              {promptsVan(site.id).length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen vragen. Controleer eerst de beschrijving op het tabblad Site en laat Claude vragen voorstellen.</p></div>}
              {promptsVan(site.id).map(p => {
                const r = laatsteRun(p.id);
                return (
                  <div key={p.id} className="card" style={{ marginBottom: 8, opacity: p.actief ? 1 : 0.5 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", cursor: "pointer", flexWrap: "wrap" }} onClick={() => setOpen(open === p.id ? null : p.id)}>
                      <div style={{ minWidth: 0, flex: 1 }}><p style={{ fontSize: 13 }}>{p.vraag}</p><p style={{ fontSize: 11, color: C.dim }}>{p.bezig_sinds ? "wordt nu gemeten..." : r ? "Gemeten " + tijd(r.created_at) : "wacht op eerste meting"}</p></div>
                      {statusBadge(r)}
                    </div>
                    {open === p.id && (
                      <div style={{ marginTop: 10 }}>
                        {r && r.fout && <p style={{ fontSize: 12, color: C.red }}>{r.fout}</p>}
                        {r && r.waarschuwing && <p style={{ fontSize: 12, color: C.yellow, marginBottom: 6 }}>{r.waarschuwing} De vraag wordt over enkele uren opnieuw gemeten.</p>}
                        {r && r.antwoord && <><p className="lbl" style={{ marginBottom: 4 }}>Antwoord van de AI (fragment)</p><p style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", color: C.muted }}>{r.antwoord.slice(0, 700)}{r.antwoord.length > 700 ? "..." : ""}</p></>}
                        {r && r.bronnen && r.bronnen.length > 0 && <div style={{ marginTop: 8 }}><p className="lbl" style={{ marginBottom: 4 }}>Bronnen</p>{r.bronnen.map((b, i) => <p key={i} style={{ fontSize: 11, padding: "2px 0", wordBreak: "break-all" }}>{b.geciteerd ? "✓ " : "· "}<a href={b.url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>{b.titel || b.url}</a></p>)}</div>}
                        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                          {(!r || !r.geciteerd) && <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === "cpt" + p.id} onClick={() => actie("cpt" + p.id, () => seoCall("concept", { site_id: site.id, onderwerp: p.vraag, geo_run_id: r ? r.id : null }), "✓ Concept wordt gemaakt, over ongeveer een minuut in Wacht op jou")}>Maak concept-artikel hiervoor</button>}
                          <button className="btn bg" style={{ fontSize: 11 }} onClick={() => actie("act" + p.id, async () => { const { error } = await supabase.from("geo_prompts").update({ actief: !p.actief }).eq("id", p.id); if (error) throw new Error(error.message); })}>{p.actief ? "Pauzeer" : "Hervat"}</button>
                          <button className="btn bg" style={{ fontSize: 11, color: C.red }} onClick={() => { if (window.confirm("Deze vraag en de metingen ervan verwijderen?")) actie("del" + p.id, async () => { const { error } = await supabase.from("geo_prompts").delete().eq("id", p.id); if (error) throw new Error(error.message); }, "✓ Verwijderd"); }}>Verwijder</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tab === "concepten" && (
            <div>
              <div className="card" style={{ marginBottom: 10 }}>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{site.soort === "website" ? "Een goedgekeurd concept komt als concept in de kennisartikelen van de website. Publiceren doe je daar zelf in het beheer." : "Een goedgekeurd concept komt als document in de OS. Plak het in de AI SEO Engine van de winkel."}</p>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={nieuwOnderwerp} onChange={e => setNieuwOnderwerp(e.target.value)} placeholder="Onderwerp of vraag, bijvoorbeeld: Wat kost maatwerksoftware voor een klein bedrijf?" maxLength={300} />
                  <button className="btn bp" style={{ fontSize: 11 }} disabled={busy === "nieuw" || nieuwOnderwerp.trim().length < 5} onClick={() => actie("nieuw", async () => { await seoCall("concept", { site_id: site.id, onderwerp: nieuwOnderwerp.trim() }); setNieuwOnderwerp(""); }, "✓ Concept wordt gemaakt, over ongeveer een minuut in Wacht op jou")}>Maak concept</button>
                </div>
              </div>
              {concepten.filter(c => c.site_id === site.id && c.onderwerp !== "Vragen voorstellen").map(c => (
                <div key={c.id} className="card" style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}><p style={{ fontSize: 13 }}>{c.onderwerp}</p><p style={{ fontSize: 11, color: C.dim }}>{tijd(c.created_at)} · {eur(c.kosten_eur)}</p>{c.fout && <p style={{ fontSize: 12, color: C.red, marginTop: 3 }}>{c.fout}</p>}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Bdg label={c.status} bg={C.bg} fg={c.status === "klaar" ? C.green : c.status === "mislukt" ? C.red : C.blue} />
                      {c.status === "klaar" && <button className="btn bg" style={{ fontSize: 10 }} onClick={() => setView && setView("operatie")}>Naar wachtrij</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "site" && form === null && (
            <div className="card">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 8, fontSize: 12 }}>
                <p><span style={{ color: C.muted }}>Domein: </span>{site.domein}</p>
                <p><span style={{ color: C.muted }}>Soort: </span>{site.soort === "shop" ? "webshop" : "website"}</p>
                <p><span style={{ color: C.muted }}>Merknamen: </span>{(site.merknamen || []).join(", ") || "-"}</p>
                <p><span style={{ color: C.muted }}>Eigen domein bevestigd: </span>{site.bevestigd_eigen ? "ja" : "nee"}</p>
                <p><span style={{ color: C.muted }}>Bezoekcijfers uit: </span>{MEETBRONNEN[site.meetbron || ""]}{site.winkel_dienst ? " (" + site.winkel_dienst + ")" : ""}</p>
              </div>
              <p style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.6 }}>{site.beschrijving || "Nog geen beschrijving."}</p>
              {/nog invullen/i.test(site.beschrijving || "") && <p style={{ fontSize: 12, color: C.yellow, marginTop: 6 }}>Vul de beschrijving in voordat Claude vragen voorstelt.</p>}
              <button className="btn bg" style={{ fontSize: 11, marginTop: 10 }} onClick={() => setForm({ id: site.id, naam: site.naam, domein: site.domein, soort: site.soort, beschrijving: site.beschrijving || "", merknamen: (site.merknamen || []).join(", "), actief: site.actief, bevestigd_eigen: site.bevestigd_eigen, meetbron: site.meetbron || "", winkel_dienst: site.winkel_dienst || "" })}>Bewerk site</button>
            </div>
          )}
        </div>
      )}

      {tab === "site" && form !== null && (
        <div className="card" style={{ marginTop: 10 }}>
          <p className="lbl" style={{ marginBottom: 8 }}>{form.id ? "Site bewerken" : "Site toevoegen"}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Naam</p><input value={form.naam} onChange={e => setForm(p => ({ ...p, naam: e.target.value }))} /></div>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Domein (zonder https://)</p><input value={form.domein} onChange={e => setForm(p => ({ ...p, domein: e.target.value }))} placeholder="voorbeeld.nl" /></div>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Soort</p><select value={form.soort} onChange={e => setForm(p => ({ ...p, soort: e.target.value }))}><option value="website">Website</option><option value="shop">Webshop</option></select></div>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Merknamen (komma gescheiden)</p><input value={form.merknamen} onChange={e => setForm(p => ({ ...p, merknamen: e.target.value }))} /></div>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Bezoekcijfers uit</p><select value={form.meetbron || ""} onChange={e => setForm(p => ({ ...p, meetbron: e.target.value }))}>{Object.entries(MEETBRONNEN).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            {(form.meetbron === "koppeling" || form.meetbron === "winkel_db") && <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Dienstnaam winkelkoppeling</p><input value={form.winkel_dienst || ""} onChange={e => setForm(p => ({ ...p, winkel_dienst: e.target.value }))} placeholder="bijv. CasaCeleste" /></div>}
          </div>
          <p style={{ fontSize: 11, color: C.muted, margin: "10px 0 4px" }}>Beschrijving: wat biedt de site en aan wie? Hierop baseren de agenten vragen en teksten.</p>
          <textarea rows={4} value={form.beschrijving} onChange={e => setForm(p => ({ ...p, beschrijving: e.target.value }))} style={{ lineHeight: 1.6, fontSize: 13 }} />
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, marginTop: 10 }}><input type="checkbox" checked={!!form.actief} onChange={e => setForm(p => ({ ...p, actief: e.target.checked }))} style={{ width: "auto" }} /> Actief (wekelijkse controle en metingen)</label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, marginTop: 6 }}><input type="checkbox" checked={!!form.bevestigd_eigen} onChange={e => setForm(p => ({ ...p, bevestigd_eigen: e.target.checked }))} style={{ width: "auto" }} /> Ik bevestig dat dit mijn eigen domein is. De controle haalt alleen bevestigde domeinen op.</label>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn bp" disabled={busy === "site"} onClick={slaSiteOp}>Opslaan</button>
            <button className="btn bg" onClick={() => setForm(null)}>Annuleer</button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 14 }}>
        <p className="lbl" style={{ marginBottom: 8 }}>Budget en instellingen</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <div>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Dagbudget AI-metingen en concepten (euro)</p>
            <div style={{ display: "flex", gap: 6 }}>
              <input type="number" min="0" max="50" step="0.5" defaultValue={cap} key={"cap" + cap} onChange={e => setBudgetInvoer(e.target.value)} style={{ maxWidth: 100 }} />
              <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { const v = Number(budgetInvoer === "" ? cap : budgetInvoer); if (!(v >= 0 && v <= 50)) { showToast("✗ Kies een bedrag tussen 0 en 50"); return; } actie("budget", () => bewaarInstelling("seo_dagbudget_eur", v), "✓ Dagbudget opgeslagen"); }}>Opslaan</button>
            </div>
            <div style={{ height: 5, background: C.border, borderRadius: 3, marginTop: 8 }}><div style={{ width: pct + "%", height: "100%", background: pct >= 90 ? C.red : pct >= 70 ? C.yellow : C.green, borderRadius: 3 }} /></div>
            <p style={{ fontSize: 11, color: C.dim, marginTop: 4 }}>Vandaag {eur(uitgegeven)} van {eur(cap)}. Een schatting. De technische controle kost niets.</p>
          </div>
          <div>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Model</p>
            <select value={model} onChange={e => actie("model", () => bewaarInstelling("seo_model", e.target.value), "✓ Model opgeslagen")}>
              <option value="claude-opus-5">Claude Opus 5 (beste kwaliteit)</option>
              <option value="claude-sonnet-5">Claude Sonnet 5 (goedkoper, ongeveer 40 procent van de prijs)</option>
            </select>
          </div>
          <div>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Minuten die een toegepast voorstel bespaart</p>
            <input type="number" min="0" max="600" defaultValue={minuten} key={"min" + minuten} onBlur={e => { const v = Number(e.target.value); if (v >= 0 && v <= 600 && v !== minuten) actie("min", () => bewaarInstelling("seo_minuten_per_voorstel", v), "✓ Opgeslagen"); }} style={{ maxWidth: 100 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// AI Boardroom - Fase 1: de centrale approval-wachtrij (task-type agnostisch).
// Toont voorgestelde wijzigingen met een preview/diff en de state machine
// pending -> approved -> applied. De 'apply' schrijft in Fase 1 nog naar de
// interne Supabase (documents/tasks/events/leads) via applyInternal; in Fase 2
// verhuist die executor naar een aparte service met write-credentials.
function ApprovalQueue({ user }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");   // pending | approved | historie
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [vid, setVid] = useState({});   // per voorstel: live video-generatie (status/url/error)
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3000); }

  // Goedgekeurd maar nog niet uitgevoerd (of mislukt): die vragen nog een klik op Toepassen.
  const [nogUitTeVoeren, setNogUitTeVoeren] = useState(0);
  async function load() {
    setLoading(true); setErr(null);
    try {
      const status = filter === "historie" ? ["applied", "rejected"] : filter === "approved" ? ["approved", "failed"] : filter;
      setItems(await listProposals({ status, limit: 200 }));
      const { count } = await supabase.from("proposed_changes").select("id", { count: "exact", head: true }).in("status", ["approved", "failed"]);
      setNogUitTeVoeren(count || 0);
    } catch (e) { setErr(e.message || "Kon de wachtrij niet laden"); setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    const ch = supabase.channel("pq_sync").on("postgres_changes", { event: "*", schema: "public", table: "proposed_changes" }, () => load()).subscribe();
    return () => supabase.removeChannel(ch);
  }, [filter]);

  // De echte uitvoering (Fase 1: interne Supabase-writes). Spiegelt runTool.
  async function applyInternal(payload, row) {
    const t = row.target_system; const p = payload || {};
    if (t === "documents") {
      const d = { title: stripDash(p.title || "AI-document").slice(0, 200), type: p.type || "notitie", content: stripDash(p.content || p.text || ""), dienst: p.dienst || null, aangemaakt_door: meKey, is_custom: true, archived: false, version: 1, ...(p.lead_id ? { lead_id: p.lead_id } : {}) };
      const { error } = await supabase.from("documents").insert([d]); if (error) throw new Error(error.message);
      // Resultaat van een taak die naar Operatie ging: na akkoord is de taak klaar en loskoppelen van de persoonlijke bak
      if (row.task_type === "taak_resultaat" && p.taak_id != null) await supabase.from("tasks").update({ status: "done", assignee: null, owner: null }).eq("id", p.taak_id);
      return "Document opgeslagen in Documenten: " + d.title + (row.task_type === "taak_resultaat" ? " (taak afgerond)" : "");
    }
    if (t === "tasks") {
      const owner = "furgel"; // enige teamlid sinds Tristan uit dienst is
      const due = p.due || new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);
      const d = { title: String(p.title || "Taak").slice(0, 200), owner, assignee: owner, priority: taakPrio(p.priority), status: "todo", due, dienst: p.dienst || "", notes: stripDash([p.wat && ("Wat: " + p.wat), p.hoe && ("Hoe: " + p.hoe), p.waarom && ("Waarom: " + p.waarom), p.notes].filter(Boolean).join("\n")) };
      const { error } = await supabase.from("tasks").insert([d]); if (error) throw new Error(error.message);
      return "Taak aangemaakt: " + d.title;
    }
    if (t === "events") {
      const att = (Array.isArray(p.attendees) ? p.attendees : []).filter(a => a === "furgel");
      const d = { title: String(p.title || "Afspraak").slice(0, 200), date: p.date || new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10), time: p.time || "10:00", duration: Number(p.duration) || 60, owner: att[0] || "furgel", attendees: att.length ? att : ["furgel"], type: p.type || "meeting", notes: p.notes || "", meeting_url: "" };
      const { error } = await supabase.from("events").insert([d]); if (error) throw new Error(error.message);
      return "Afspraak ingepland: " + d.title + " op " + d.date + " " + d.time;
    }
    if (t === "leads") {
      const seg = p.segment === "b2c" ? "b2c" : "b2b";
      const d = { name: String(p.name || p.company || "Lead").slice(0, 160), company: p.company ? String(p.company).slice(0, 160) : null, interest: String(p.interest || "").slice(0, 160), status: seg === "b2b" ? "cold" : "lead", segment: seg, email: p.email || null, source: "AI-team (queue)", notes: p.notes || "Toegevoegd via approval-queue" };
      const { error } = await supabase.from("leads").insert([d]); if (error) throw new Error(error.message);
      return "CRM-lead toegevoegd: " + d.name;
    }
    if (t === "video") {
      // De clip zelf staat al in Supabase-storage; we leggen de promo + link vast
      // in Documenten zodat het resultaat een vindbare plek in de OS krijgt.
      const d = { title: stripDash(p.title || "Promo-video").slice(0, 200), type: "notitie", content: stripDash(String(p.promo || p.content || "") + (p.video_url ? "\n\nVideo: " + p.video_url : "\n\n(nog geen clip gegenereerd)")), aangemaakt_door: meKey, is_custom: true, archived: false, version: 1 };
      const { error } = await supabase.from("documents").insert([d]); if (error) throw new Error(error.message);
      return "Promo-video vastgelegd in Documenten: " + d.title + (p.video_url ? " (met clip)" : "");
    }
    if (t === "kennis_artikelen") {
      // Zet het artikel als concept klaar in de kennisartikelen van de website. Publiceren gebeurt daar zelf (beheer).
      const basis = String(p.slug || "").toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "") || "artikel";
      let slug = basis;
      for (let i = 2; i < 30; i++) {
        const { data: bestaat } = await supabase.from("kennis_artikelen").select("id").eq("slug", slug).limit(1);
        if (!bestaat || !bestaat.length) break;
        slug = basis + "-" + i;
      }
      const faq = Array.isArray(p.faq) ? p.faq.filter(f => f && f.vraag && f.antwoord).map(f => ({ vraag: stripDash(String(f.vraag)), antwoord: stripDash(String(f.antwoord)) })) : [];
      const direct = p.publiceer === true; // alleen het wekelijkse inzicht: akkoord in de OS = publiceren
      const rij = { slug, titel: stripDash(String(p.artikel_titel || p.title || "Artikel")).slice(0, 200), beschrijving: stripDash(String(p.beschrijving || "")).slice(0, 300) || null, lead: stripDash(String(p.lead || "")) || null, body_html: String(p.body_html || ""), faq, onderwerp: p.onderwerp ? String(p.onderwerp).slice(0, 300) : null, status: direct ? "gepubliceerd" : "concept", ...(direct ? { published_at: new Date().toISOString() } : {}) };
      const { error } = await supabase.from("kennis_artikelen").insert([rij]); if (error) throw new Error(error.message);
      if (direct) return "Inzicht gepubliceerd op de website: " + rij.titel + " (dif3r3nt.nl/kennis/" + slug + ")";
      return "Artikel als concept klaargezet op de website (publiceren in het beheer): " + rij.titel;
    }
    if (t === "launch_modules") {
      // Vult alleen velden die in de launch-module nog leeg zijn. Bestaande tekst blijft staan.
      const txt = (v) => (typeof v === "string" && v.trim() ? stripDash(v.trim()) : undefined);
      const arr = (v) => (Array.isArray(v) && v.length ? v.map(x => stripDash(String(x))).filter(Boolean) : undefined);
      const faq = Array.isArray(p.faq) ? p.faq.filter(f => f && f.vraag && f.antwoord).map(f => ({ vraag: stripDash(String(f.vraag)), antwoord: stripDash(String(f.antwoord)) })) : [];
      const velden = {
        doelgroep: txt(p.doelgroep), pijnpunten: txt(p.pijnpunten), value_proposition: txt(p.value_proposition),
        elevator_pitch: txt(p.elevator_pitch), hero: txt(p.hero), hook: txt(p.hook), cta: txt(p.cta),
        pricing: txt(p.pricing_voorstel), meta_title: txt(p.meta_title), meta_description: txt(p.meta_description),
        usp_lijst: arr(p.usp_lijst), zoekwoorden: arr(p.zoekwoorden), faq: faq.length ? faq : undefined,
      };
      const naam = String(p.dienst || p.title || "").replace(/^Launch-pakket:\s*/i, "").trim();
      // Zoek de bestaande module op pipeline-id of exacte naam. Zonder beide maken we een nieuwe.
      let gevonden = [];
      if (p.pipeline_item_id || naam) {
        let q = supabase.from("launch_modules").select("*");
        q = p.pipeline_item_id ? q.eq("pipeline_item_id", String(p.pipeline_item_id)) : q.ilike("title", naam.replace(/[\\%_]/g, "\\$&"));
        const { data: zoek, error: zErr } = await q.limit(1);
        if (zErr) throw new Error(zErr.message);
        gevonden = zoek || [];
      }
      const leeg = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
      if (gevonden && gevonden[0]) {
        const bestaand = gevonden[0]; const patch = {}; const overgeslagen = [];
        Object.entries(velden).forEach(([k, v]) => { if (v === undefined) return; if (leeg(bestaand[k])) patch[k] = v; else overgeslagen.push(k); });
        if (Object.keys(patch).length) { const { error } = await supabase.from("launch_modules").update(patch).eq("id", bestaand.id); if (error) throw new Error(error.message); }
        return "Launch-module '" + bestaand.title + "': " + Object.keys(patch).length + " lege veld(en) gevuld" + (overgeslagen.length ? ", " + overgeslagen.length + " bestaande veld(en) ongemoeid gelaten" : "");
      }
      const nieuw = { title: naam || "Nieuwe dienst", pipeline_item_id: p.pipeline_item_id ? String(p.pipeline_item_id) : null, verantwoordelijke: "furgel" };
      Object.entries(velden).forEach(([k, v]) => { if (v !== undefined) nieuw[k] = v; });
      const { error } = await supabase.from("launch_modules").insert([nieuw]); if (error) throw new Error(error.message);
      return "Nieuwe launch-module aangemaakt: " + nieuw.title;
    }
    return "Geen interne write nodig voor target '" + t + "' (Fase 2: externe systemen).";
  }

  // Payload aanpassen en opslaan op het voorstel zelf, zodat de wijziging voor
  // Furgel EN Tristan zichtbaar is (niet alleen lokaal). De DB-trigger bewaakt
  // alleen status-overgangen, dus een payload-update is toegestaan.
  async function patchPayload(p, patch) {
    const next = { ...(p.payload || {}), ...patch };
    const { error } = await supabase.from("proposed_changes").update({ payload: next }).eq("id", p.id);
    if (error) { showToast("✗ Opslaan mislukt: " + error.message); return; }
    setItems(list => list.map(x => x.id === p.id ? { ...x, payload: next } : x));
  }

  // Genereert de ECHTE clip uit de promotekst en bewaart de URL op het voorstel,
  // zodat je hem live kunt bekijken en testen voordat je goedkeurt.
  async function genPreview(p) {
    const promo = String((p.payload && (p.payload.promo || p.payload.content || p.payload.title)) || "").trim();
    if (!promo) { showToast("✗ Geen promotekst om te genereren"); return; }
    setVid(v => ({ ...v, [p.id]: { status: "starting" } }));
    try {
      const idem = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
      const secs = Number(p.payload && p.payload.duration_s) || 5;   // 5 of 10 sec
      const r = await apiFetch("/api/video", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: promo.slice(0, 500), mode: "text_to_video", params: { durationSeconds: secs, aspectRatio: "9:16" }, idempotencyKey: idem, userEmail: (user && user.email) || "" }) });
      const d = await r.json();
      if (!r.ok) { setVid(v => ({ ...v, [p.id]: { error: d.error || "Aanmaken mislukt" } })); return; }
      const jid = d.id;
      setVid(v => ({ ...v, [p.id]: { status: d.status || "running" } }));
      const poll = async () => {
        try {
          const pr = await apiFetch("/api/video?id=" + encodeURIComponent(jid));
          const pd = await pr.json();
          if (pd.asset && pd.asset.url) {
            setVid(v => ({ ...v, [p.id]: { status: "succeeded", url: pd.asset.url } }));
            await patchPayload(p, { video_url: pd.asset.url });
            return;
          }
          if (pd.status === "failed" || pd.status === "cancelled") { setVid(v => ({ ...v, [p.id]: { error: pd.error?.message || "Video mislukt" } })); return; }
          setTimeout(poll, 4000);
        } catch (e) { setTimeout(poll, 4000); }
      };
      setTimeout(poll, 4000);
    } catch (e) { setVid(v => ({ ...v, [p.id]: { error: e.message || "Mislukt" } })); }
  }

  // Interne voorstellen (niveau 1) en het wekelijkse inzicht (akkoord = publiceren) voeren we direct uit bij goedkeuren.
  // Wat de buitenwereld raakt of geld kost blijft twee stappen: eerst goedkeuren, dan Toepassen.
  const directUitvoeren = (p) => voorstelNiveau(p) === 1 || p.task_type === "kennis_inzicht";
  async function onApprove(p) {
    setBusy(p.id);
    try {
      await approveProposal(p.id, meKey);
      if (directUitvoeren(p)) { await applyProposal(p.id, applyInternal); showToast("✓ Goedgekeurd en uitgevoerd"); }
      else showToast("✓ Goedgekeurd. Voer het uit via Goedgekeurd > Toepassen");
    } catch (e) { showToast("✗ " + e.message); }
    await load();
    setBusy(null);
  }
  async function onReject(p) {
    const reason = window.prompt("Reden van afkeuren (gaat als feedback terug naar de agent):") || null;
    setBusy(p.id);
    try {
      await rejectProposal(p.id, meKey, reason);
      if (p.task_type === "taak_resultaat" && p.payload && p.payload.taak_id != null) await supabase.from("tasks").update({ status: "todo" }).eq("id", p.payload.taak_id);
      // Feedback-lus: kwam dit voorstel van een sandbox-agent en is er feedback? Dan
      // zet ik een eenmalige herzieningstaak klaar. Mens-gated: elke herziening vraagt
      // opnieuw een menselijke afkeuring, dus geen autonome oneindige lus.
      if (p.source_run_id && reason) {
        const goal = "Herzie een afgekeurd voorstel. Oorspronkelijk (" + p.task_type + " -> " + p.target_system + "): " + (p.reasoning_log || "") + "\nInhoud: " + JSON.stringify(p.payload || {}).slice(0, 700) + "\n\nFeedback van de directie (verwerk dit volledig): " + reason + "\n\nLever een verbeterd voorstel.";
        try {
          await supabase.from("sandbox_agent_tasks").insert([{ title: "Herziening: " + proposalHeadline(p), goal, agent: p.source_agent || "Daan", target_system: p.target_system, task_type: p.task_type, interval_minutes: 0, enabled: true, created_by: meKey, next_run_at: new Date().toISOString() }]);
          apiFetch("/api/notify?job=agent-tick", { method: "POST" }).catch(() => {});
          showToast("Afgekeurd - de agent maakt een herziening");
        } catch (e) { showToast("Afgekeurd"); }
      } else { showToast("Afgekeurd"); }
      await load();
    } catch (e) { showToast("✗ " + e.message); }
    setBusy(null);
  }
  async function onApply(p) { setBusy(p.id); try { await applyProposal(p.id, applyInternal); showToast("✓ Toegepast op productie-data"); await load(); } catch (e) { showToast("✗ Toepassen mislukt: " + e.message); } setBusy(null); }

  const stCol = (s) => s === "applied" ? C.green : s === "approved" ? C.blue : s === "rejected" ? C.red : s === "failed" ? C.red : C.yellow;
  const stLabel = (s) => ({ pending: "WACHT OP REVIEW", approved: "GOEDGEKEURD", applied: "TOEGEPAST", rejected: "AFGEKEURD", failed: "MISLUKT" }[s] || s);

  const isLang = (v) => typeof v === "string" && (v.length > 140 || /\n/.test(v));
  const leesKader = (kop, v) => (
    <div key={kop} style={{ marginTop: 10 }}>
      <p style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>{kop}</p>
      <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "14px 18px", maxWidth: 760 }}>{LeesTekst(v, { maxHoogte: 520 })}</div>
    </div>
  );

  function KV({ k, v }) {
    if (isLang(v)) return leesKader(k, v);
    return <div style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: "1px solid " + C.border }}><span style={{ fontSize: 11, color: C.muted, minWidth: 90 }}>{k}</span><span style={{ fontSize: 12, wordBreak: "break-word" }}>{String(v)}</span></div>;
  }

  function Preview({ p }) {
    const pl = p.payload || {}; const t = p.task_type;
    // Echte before/after diff wanneer het voorstel die meelevert (bv. crm_update op bestaande lead)
    if (pl.before && pl.after) {
      const keys = Array.from(new Set([...Object.keys(pl.before), ...Object.keys(pl.after)]));
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <div><p style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>VOOR</p>{keys.map(k => <KV key={k} k={k} v={pl.before[k] ?? "-"} />)}</div>
          <div><p style={{ fontSize: 10, color: C.green, marginBottom: 4 }}>NA</p>{keys.map(k => <KV key={k} k={k} v={pl.after[k] ?? "-"} />)}</div>
        </div>
      );
    }
    // Promo-video: promotekst live aanpasbaar + de ECHTE clip genereren en bekijken.
    if (t === "video_promo" || p.target_system === "video") {
      const j = vid[p.id];
      const busyV = j && (j.status === "starting" || j.status === "running" || j.status === "queued");
      const url = pl.video_url || (j && j.url);
      const editable = p.status === "pending" || p.status === "approved";
      return (
        <div>
          <p style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Promotekst (wordt de prompt voor de clip)</p>
          {editable
            ? <textarea defaultValue={pl.promo || pl.content || ""} onBlur={e => patchPayload(p, { promo: e.target.value })} rows={4} style={{ width: "100%", fontSize: 12, lineHeight: 1.55, resize: "vertical" }} placeholder="Visueel beschrijvende promotekst…" />
            : <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 10 }}>{pl.promo || pl.content || "-"}</pre>}
          <div style={{ display: "flex", gap: 7, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
            {editable && (
              <select value={Number(pl.duration_s) || 5} onChange={e => patchPayload(p, { duration_s: Number(e.target.value) })} title="Cliplengte" style={{ fontSize: 11, width: "auto", padding: "4px 7px" }}>
                <option value={5}>5 sec &middot; €0,25</option>
                <option value={10}>10 sec &middot; €0,50</option>
              </select>
            )}
            {editable && <button className="btn bp" style={{ fontSize: 11 }} disabled={busyV} onClick={() => genPreview(p)}>{busyV ? "⟳ Video maken…" : (url ? "↻ Opnieuw genereren" : "▶ Genereer preview")}</button>}
            {url && <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.blue }}>↓ Download clip</a>}
          </div>
          {busyV && <p style={{ fontSize: 11, color: C.blue, marginTop: 6 }}>Bezig met genereren - dit duurt ±1 minuut…</p>}
          {j && j.error && <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>⚠ {j.error}</p>}
          {url && <video src={url} controls style={{ width: 210, maxWidth: "100%", borderRadius: 8, border: "1px solid " + C.border, marginTop: 10, display: "block", background: "#000" }} />}
          {!url && !busyV && <p style={{ fontSize: 11, color: C.dim, marginTop: 8 }}>Nog geen clip. Klik "Genereer preview" om hem echt te maken en te bekijken.</p>}
        </div>
      );
    }
    if (t === "kennis_inzicht") {
      const feiten = Array.isArray(pl.verificatie) ? pl.verificatie : [];
      const platteTekst = String(pl.body_html || "").replace(/<\/(p|h2|h3|h4|li|blockquote)>/gi, "\n").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/\n{3,}/g, "\n\n").trim();
      return (
        <div>
          <div style={{ background: C.yellowDim, border: "1px solid " + C.yellow + "55", borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: C.yellow, marginBottom: 4 }}>NA JE AKKOORD STAAT DIT DIRECT LIVE OP DE WEBSITE</p>
            <p style={{ fontSize: 12, lineHeight: 1.5 }}>Controleer vooral de feiten hieronder. De bronnen zijn automatisch gecontroleerd: is de pagina bereikbaar en staat het genoemde cijfer erin. Of het cijfer in de juiste context wordt gebruikt, beoordeel jij.</p>
          </div>
          {feiten.map((f, i) => (
            <div key={i} style={{ border: "1px solid " + C.border, borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <p style={{ fontSize: 13, fontWeight: 700 }}>{f.claim} {f.cijfer ? <span style={{ color: C.cyan }}>({f.cijfer})</span> : null}</p>
              <p style={{ fontSize: 11, color: f.gecontroleerd ? C.green : C.red, marginTop: 3 }}>{f.gecontroleerd ? "Gecontroleerd: cijfer staat in de bron" : "Niet gecontroleerd: " + (f.reden || "onbekend")}</p>
              <p style={{ fontSize: 11, color: C.dim, marginTop: 3 }}>Bron: {f.bron_naam || "-"} {f.datum ? "(" + f.datum + ")" : ""} {f.bron_url ? <a href={f.bron_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>open bron</a> : null}</p>
              {f.citaat && <p style={{ fontSize: 11, color: C.dim, marginTop: 3, fontStyle: "italic" }}>"{f.citaat}"</p>}
            </div>
          ))}
          <KV k="Titel" v={pl.artikel_titel || "-"} /><KV k="Slug" v={pl.slug || "-"} /><KV k="Meta" v={pl.beschrijving || "-"} />
          <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 12, maxHeight: 360, overflowY: "auto", marginTop: 8 }}>{platteTekst || "(geen tekst)"}</pre>
        </div>
      );
    }
    if (t === "seo_artikel") {
      const nb = Array.isArray(pl.nog_te_bevestigen) ? pl.nog_te_bevestigen : [];
      const platteTekst = String(pl.body_html || "").replace(/<\/(p|h2|h3|h4|li|blockquote)>/gi, "\n").replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/\n{3,}/g, "\n\n").trim();
      const br = pl.brief || {};
      return (
        <div>
          {nb.length > 0 && <div style={{ background: C.yellowDim, border: "1px solid " + C.yellow + "55", borderRadius: 8, padding: 10, marginBottom: 10 }}><p style={{ fontSize: 11, fontWeight: 700, color: C.yellow, marginBottom: 4 }}>NOG TE BEVESTIGEN VOOR GEBRUIK</p>{nb.map((x, i) => <p key={i} style={{ fontSize: 12, lineHeight: 1.5 }}>• {x}</p>)}</div>}
          <p style={{ fontSize: 10, color: C.dim, marginBottom: 8 }}>Bij toepassen komt dit als concept in de kennisartikelen van de website. Publiceren doe je daar zelf.</p>
          <KV k="Titel" v={pl.artikel_titel || "-"} /><KV k="Slug" v={pl.slug || "-"} /><KV k="Meta" v={pl.beschrijving || "-"} />
          {pl.aeo_snippet && <KV k="Kort antwoord" v={pl.aeo_snippet} />}
          {br.doel && <KV k="Doel" v={br.doel} />}{br.zoekintentie && <KV k="Zoekintentie" v={br.zoekintentie} />}
          {Array.isArray(br.interne_links) && br.interne_links.length > 0 && <KV k="Interne links" v={br.interne_links.join("; ")} />}
          <pre style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: 12, maxHeight: 360, overflowY: "auto", marginTop: 8 }}>{platteTekst || "(geen tekst)"}</pre>
        </div>
      );
    }
    if (t === "launch_pakket") {
      const tekst = (v) => Array.isArray(v) ? v.map(x => typeof x === "string" ? "• " + x : "• " + (x.vraag || "") + " " + (x.antwoord || "")).join("\n") : String(v);
      const veld = (k, v) => (v && (!Array.isArray(v) || v.length)) ? <div key={k} style={{ marginBottom: 9 }}><p style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", marginBottom: 2 }}>{k}</p><p style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{tekst(v)}</p></div> : null;
      const nb = Array.isArray(pl.nog_te_bevestigen) ? pl.nog_te_bevestigen : [];
      return (
        <div>
          {nb.length > 0 && <div style={{ background: C.yellowDim, border: "1px solid " + C.yellow + "55", borderRadius: 8, padding: 10, marginBottom: 10 }}><p style={{ fontSize: 11, fontWeight: 700, color: C.yellow, marginBottom: 4 }}>NOG TE BEVESTIGEN VOOR GEBRUIK</p>{nb.map((x, i) => <p key={i} style={{ fontSize: 12, lineHeight: 1.5 }}>• {x}</p>)}</div>}
          <p style={{ fontSize: 10, color: C.dim, marginBottom: 8 }}>Bij toepassen worden alleen lege velden van de launch-module gevuld. Bestaande tekst blijft staan.</p>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {veld("Doelgroep", pl.doelgroep)}{veld("Pijnpunten", pl.pijnpunten)}{veld("Waardepropositie", pl.value_proposition)}{veld("Elevator pitch", pl.elevator_pitch)}
            {veld("USP's", pl.usp_lijst)}{veld("Hero", pl.hero)}{veld("Hook", pl.hook)}{veld("Call to action", pl.cta)}{veld("Prijs (voorstel)", pl.pricing_voorstel)}
            {veld("Meta-titel", pl.meta_title)}{veld("Meta-beschrijving", pl.meta_description)}{veld("Zoekwoorden", pl.zoekwoorden)}{veld("FAQ", pl.faq)}
          </div>
        </div>
      );
    }
    if (t === "content_draft" || t === "outreach_email") {
      const body = pl.content || pl.text || pl.body || "(geen inhoud)";
      const editable = p.status === "pending";
      return editable
        ? <><textarea defaultValue={body} onBlur={e => patchPayload(p, { content: e.target.value })} rows={10} style={{ width: "100%", fontSize: 12, lineHeight: 1.6, resize: "vertical" }} /><p style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>Je kunt de tekst hier aanpassen; dat wordt bewaard bij het voorstel.</p></>
        : <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 8, padding: "14px 18px", maxWidth: 760 }}>{LeesTekst(body, { maxHoogte: 520 })}</div>;
    }
    // Generiek: toon de payload-velden als tabel
    // Korte velden als compacte rijen (lege weggelaten), lange tekst als leesbaar blok eronder
    const entries = Object.entries(pl).filter(([, v]) => typeof v !== "object" && v !== "" && v != null);
    const kort = entries.filter(([, v]) => !isLang(v)); const lang = entries.filter(([, v]) => isLang(v));
    return <div>{entries.length ? <>{kort.map(([k, v]) => <KV key={k} k={k} v={v} />)}{lang.map(([k, v]) => leesKader(k, v))}</> : <pre style={{ fontSize: 11, whiteSpace: "pre-wrap" }}>{JSON.stringify(pl, null, 2)}</pre>}</div>;
  }

  const counts = { pending: items.filter(i => i.status === "pending").length };

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace" }}>{toast}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 7 }}>
          {[["pending", "Wachtend" + (filter === "pending" && counts.pending ? " · " + counts.pending : "")], ["approved", "Goedgekeurd" + (nogUitTeVoeren ? " · " + nogUitTeVoeren : "")], ["historie", "Historie"]].map(([k, l]) => (
            <span key={k} className={"tab" + (filter === k ? " on" : "")} onClick={() => setFilter(k)} style={k === "approved" && nogUitTeVoeren && filter !== k ? { color: C.yellow } : undefined}>{l}</span>
          ))}
        </div>
        <button className="btn bg" style={{ fontSize: 11 }} onClick={load}>Vernieuwen</button>
      </div>
      <p style={{ fontSize: 10, color: C.dim, marginBottom: 12 }}>Agents stellen wijzigingen voor; niets gebeurt zonder jouw goedkeuring. Interne voorstellen (N1) en het wekelijkse inzicht worden bij goedkeuren meteen uitgevoerd. Wat de buitenwereld raakt of geld kost keur je eerst goed en voer je daarna uit met "Toepassen".</p>
      {filter !== "approved" && nogUitTeVoeren > 0 && (
        <div className="card" onClick={() => setFilter("approved")} style={{ cursor: "pointer", borderColor: C.yellow + "66", marginBottom: 10 }}>
          <p style={{ fontSize: 12, color: C.yellow }}>{nogUitTeVoeren === 1 ? "1 goedgekeurd voorstel is" : nogUitTeVoeren + " goedgekeurde voorstellen zijn"} nog niet uitgevoerd. Open Goedgekeurd en klik op Toepassen.</p>
        </div>
      )}

      {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 10 }}><p style={{ fontSize: 12, color: C.red }}>{err}</p><p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Tip: de tabel <code>proposed_changes</code> bestaat mogelijk nog niet - draai eerst de migratie <code>add_proposed_changes_queue.sql</code>.</p></div>}
      {loading && <div style={{ fontSize: 12, color: C.muted, padding: 8 }}>Laden...</div>}
      {!loading && !err && items.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>{filter === "pending" ? "Geen wijzigingen die wachten op review. 🎉" : "Niets hier."}</p></div>}

      {items.map(p => (
        <div key={p.id} className="card" style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setOpenId(openId === p.id ? null : p.id)}>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{proposalHeadline(p)}</span>
              <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{p.source_agent || "?"} → {p.target_system}</span>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              <NiveauBadge niveau={voorstelNiveau(p)} />
              <Bdg label={p.task_type} bg={C.bg} fg={C.muted} />
              <Bdg label={stLabel(p.status)} bg={C.bg} fg={stCol(p.status)} />
            </div>
          </div>
          {openId === p.id && (
            <div style={{ marginTop: 12 }}>
              {p.reasoning_log && <><p style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", marginBottom: 3 }}>Onderbouwing</p><p style={{ fontSize: 12, lineHeight: 1.55, marginBottom: 10 }}>{p.reasoning_log}</p></>}
              <p style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", marginBottom: 5 }}>Voorstel / preview</p>
              {/* Preview is een gewone functie, geen component: als <Preview/> zou React de hele preview (en de <video>) bij elke hertekening opnieuw opbouwen, wat het beeld liet knipperen */}
              {Preview({ p })}
              {p.status === "failed" && p.error_log && <p style={{ fontSize: 11, color: C.red, marginTop: 8 }}>Fout: {p.error_log}</p>}
              {p.status === "applied" && p.result?.message && <p style={{ fontSize: 11, color: C.green, marginTop: 8 }}>{p.result.message}</p>}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                {p.status === "pending" && <>
                  <button className="btn bp" disabled={busy === p.id} onClick={() => onApprove(p)}>{busy === p.id ? "..." : directUitvoeren(p) ? "✓ Goedkeuren en uitvoeren" : "✓ Goedkeuren"}</button>
                  <button className="btn bg" style={{ color: C.red, borderColor: C.red + "40" }} disabled={busy === p.id} onClick={() => onReject(p)}>✗ Afkeuren</button>
                </>}
                {p.status === "approved" && <button className="btn bp" disabled={busy === p.id} onClick={() => onApply(p)}>{busy === p.id ? "Toepassen..." : "▶ Toepassen op productie-data"}</button>}
                {p.status === "failed" && <button className="btn bg" disabled={busy === p.id} onClick={() => onApply(p)}>{busy === p.id ? "..." : "↻ Opnieuw toepassen"}</button>}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// AI Boardroom - Fase 2: de 24/7 Sandbox. Toont de agent-taken (recepten die de
// server-side runner /api/notify?job=agent-tick uitvoert), een live statusbord + run-logs,
// en een kill-switch. Voorstellen die de agents klaarleggen verschijnen in de
// tab "Wachtrij" (ApprovalQueue) waar je ze goedkeurt en toepast.
function SandboxPanel({ user }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [tasks, setTasks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [runProps, setRunProps] = useState({});  // run-id -> het opgeleverde voorstel
  const [sec, setSec] = useState([]);             // recente beveiligings-auditregels
  const [showForm, setShowForm] = useState(false);
  const NEW = { title: "", goal: "", agent: "Daan", target_system: "documents", interval_minutes: 1440 };
  const [form, setForm] = useState(NEW);
  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3000); }

  async function load() {
    try {
      const [{ data: t }, { data: r }, { data: c }] = await Promise.all([
        supabase.from("sandbox_agent_tasks").select("*").order("created_at", { ascending: false }),
        supabase.from("sandbox_runs").select("*").order("started_at", { ascending: false }).limit(25),
        supabase.from("sandbox_control").select("paused").eq("id", true).maybeSingle(),
      ]);
      setTasks(t || []); setRuns(r || []); setPaused(!!(c && c.paused));
      // Het opgeleverde werk per run erbij halen, zodat je hier live ziet WAT er uitkwam.
      const ids = (r || []).map(x => x.id);
      if (ids.length) {
        const { data: pr } = await supabase.from("proposed_changes").select("id,source_run_id,status,task_type,target_system,payload").in("source_run_id", ids);
        const map = {}; (pr || []).forEach(x => { if (x.source_run_id) map[x.source_run_id] = x; });
        setRunProps(map);
      } else setRunProps({});
      const { data: sa } = await supabase.from("security_audit").select("*").order("at", { ascending: false }).limit(8);
      setSec(sa || []);
    } catch (e) { /* tabel bestaat mogelijk nog niet */ }
    setLoading(false);
  }
  useEffect(() => {
    load();
    const ch = supabase.channel("sandbox_sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "sandbox_runs" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "sandbox_agent_tasks" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "proposed_changes" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "security_audit" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, []);

  async function togglePause() {
    const np = !paused; setPaused(np);
    await supabase.from("sandbox_control").update({ paused: np, updated_at: new Date().toISOString() }).eq("id", true);
    showToast(np ? "Alle agents gepauzeerd" : "Agents weer actief");
  }
  async function toggleTask(t) {
    await supabase.from("sandbox_agent_tasks").update({ enabled: !t.enabled, updated_at: new Date().toISOString() }).eq("id", t.id);
    load();
  }
  async function runNow(t) {
    setBusy(t.id);
    await supabase.from("sandbox_agent_tasks").update({ enabled: true, next_run_at: new Date(Date.now() - 1000).toISOString() }).eq("id", t.id);
    try { await apiFetch("/api/notify?job=agent-tick", { method: "POST" }); } catch (e) { /* stil */ }
    showToast("Tik verstuurd - de agent draait deze taak nu");
    setBusy(""); setTimeout(load, 2000);
  }
  async function delTask(t) {
    if (!window.confirm("Deze agent-taak verwijderen?")) return;
    await supabase.from("sandbox_agent_tasks").delete().eq("id", t.id); load();
  }
  async function addTask() {
    if (!form.title.trim() || !form.goal.trim()) { showToast("✗ Titel en opdracht zijn verplicht"); return; }
    const tt = { documents: "content_draft", tasks: "task", events: "agenda_event", leads: "crm_update", video: "video_promo" }[form.target_system] || "content_draft";
    const row = { title: form.title.slice(0, 200), goal: form.goal, agent: form.agent, target_system: form.target_system, task_type: tt, interval_minutes: Number(form.interval_minutes) || 0, enabled: true, created_by: meKey, next_run_at: new Date().toISOString() };
    const { error } = await supabase.from("sandbox_agent_tasks").insert([row]);
    if (error) { showToast("✗ " + error.message); return; }
    setForm(NEW); setShowForm(false); showToast("✓ Taak toegevoegd en actief"); load();
  }
  async function openRunLog(r) {
    if (openRun === r.id) { setOpenRun(null); return; }
    setOpenRun(r.id);
    const { data } = await supabase.from("sandbox_run_steps").select("*").eq("run_id", r.id).order("at");
    setSteps(data || []);
  }

  const running = tasks.filter(t => t.status === "running").length + runs.filter(r => r.status === "running").length;
  const waiting = tasks.filter(t => t.enabled && t.status !== "running").length;
  const today = new Date().toISOString().slice(0, 10);
  const doneToday = runs.filter(r => r.status === "succeeded" && (r.started_at || "").slice(0, 10) === today).length;
  const failed = runs.filter(r => r.status === "failed").length;
  const fmtTime = (s) => s ? new Date(s).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "-";
  const rStat = (s) => s === "succeeded" ? { fg: C.green, l: "klaar" } : s === "failed" ? { fg: C.red, l: "mislukt" } : { fg: C.blue, l: "bezig" };
  const Stat = ({ n, label, fg }) => <div className="card" style={{ textAlign: "center", padding: "13px 8px" }}><div style={{ fontFamily: "monospace", fontSize: 26, fontWeight: 700, color: fg }}>{n}</div><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{label}</p></div>;

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace" }}>{toast}</div>}

      <div className="card" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", borderColor: paused ? C.red + "55" : C.border }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: paused ? C.red : C.green, boxShadow: paused ? "none" : "0 0 8px " + C.green }} />
        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 700 }}>{paused ? "Agents gepauzeerd" : "Sandbox actief - agents draaien 24/7"}</p>
          <p style={{ fontSize: 11, color: C.muted }}>Agents leggen werk klaar als voorstel; niets gaat live zonder jouw goedkeuring in de Wachtrij.</p>
        </div>
        <button className={"btn " + (paused ? "bp" : "bd")} style={{ marginLeft: "auto", fontSize: 12 }} onClick={togglePause}>{paused ? "▶ Hervat agents" : "⏸ Pauzeer alles"}</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
        <Stat n={waiting} label="Wachtend" fg={C.yellow} />
        <Stat n={running} label="Bezig" fg={C.blue} />
        <Stat n={doneToday} label="Klaar vandaag" fg={C.green} />
        <Stat n={failed} label="Mislukt" fg={C.red} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <p className="lbl" style={{ margin: 0 }}>Agent-taken ({tasks.length})</p>
        <button className="btn bp" style={{ marginLeft: "auto", fontSize: 11 }} onClick={() => setShowForm(s => !s)}>{showForm ? "Sluiten" : "+ Nieuwe taak"}</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 9 }}>
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Titel, bijv. 'Wekelijkse contentideeen'" />
          <textarea rows={3} value={form.goal} onChange={e => setForm(p => ({ ...p, goal: e.target.value }))} placeholder="Opdracht voor de agent, bijv. 'Schrijf 1 concept-blogartikel over AI-automatisering voor MKB, klaar om te publiceren.'" style={{ fontSize: 12 }} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Agent</p><select value={form.agent} onChange={e => setForm(p => ({ ...p, agent: e.target.value }))}><option>Daan</option><option>Sofie</option><option>Max</option></select></div>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Levert op in</p><select value={form.target_system} onChange={e => setForm(p => ({ ...p, target_system: e.target.value }))}><option value="documents">Documenten</option><option value="tasks">Taken</option><option value="events">Agenda</option><option value="leads">CRM-lead</option><option value="video">Promo-video</option></select></div>
            <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Herhaalt elke</p><select value={form.interval_minutes} onChange={e => setForm(p => ({ ...p, interval_minutes: Number(e.target.value) }))}><option value={0}>eenmalig</option><option value={60}>uur</option><option value={1440}>dag</option><option value={10080}>week</option></select></div>
          </div>
          <button className="btn bp" style={{ alignSelf: "flex-start" }} onClick={addTask}>Taak aanmaken (actief)</button>
        </div>
      )}

      {loading && <div style={{ fontSize: 12, color: C.muted, padding: 8 }}>Laden...</div>}
      {!loading && tasks.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen agent-taken. Maak er een aan; hij draait dan 24/7 en legt werk klaar in de Wachtrij.</p></div>}

      {tasks.map(t => (
        <div key={t.id} className="card" style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.status === "running" ? C.blue : t.enabled ? C.green : C.dim, flexShrink: 0 }} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{t.title}</span>
              <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{t.agent} → {t.target_system} · {t.interval_minutes ? "elke " + (t.interval_minutes >= 1440 ? (t.interval_minutes / 1440) + "d" : (t.interval_minutes / 60) + "u") : "eenmalig"}</span>
            </div>
            <Bdg label={t.status === "running" ? "BEZIG" : t.enabled ? "ACTIEF" : "UIT"} bg={C.bg} fg={t.status === "running" ? C.blue : t.enabled ? C.green : C.dim} />
            <button className="btn bg" style={{ fontSize: 10 }} disabled={busy === t.id || t.status === "running"} onClick={() => runNow(t)}>{busy === t.id ? "…" : "▶ Run nu"}</button>
            <button className="btn bg" style={{ fontSize: 10 }} onClick={() => toggleTask(t)}>{t.enabled ? "Zet uit" : "Zet aan"}</button>
            <button className="btn bg" style={{ fontSize: 10, color: C.red, borderColor: C.red + "40" }} onClick={() => delTask(t)}>✕</button>
          </div>
          {t.last_error && <p style={{ fontSize: 11, color: C.red, marginTop: 6 }}>Laatste fout: {t.last_error}</p>}
          {t.run_count > 0 && <p style={{ fontSize: 10, color: C.dim, marginTop: 5 }}>{t.run_count}x gedraaid · laatst {fmtTime(t.last_run_at)}</p>}
        </div>
      ))}

      <p className="lbl" style={{ margin: "18px 0 8px" }}>Recente runs &amp; logs</p>
      {runs.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 10 }}>Nog geen runs.</p></div>}
      {runs.map(r => { const st = rStat(r.status); return (
        <div key={r.id} className="card" style={{ marginBottom: 6, padding: "10px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer" }} onClick={() => openRunLog(r)}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: st.fg, flexShrink: 0 }} />
            <span style={{ fontSize: 12.5, fontWeight: 600, minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.task_title || "Run"}</span>
            <span style={{ fontSize: 10, color: C.dim, fontFamily: "monospace" }}>{fmtTime(r.started_at)}</span>
            <Bdg label={st.l} bg={C.bg} fg={st.fg} />
          </div>
          {runProps[r.id] && (() => {
            const pr = runProps[r.id]; const pl = pr.payload || {};
            const title = pl.title || pl.name || proposalHeadline(pr);
            const sc = pr.status === "applied" ? C.green : pr.status === "rejected" ? C.red : pr.status === "approved" ? C.blue : C.yellow;
            return (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed " + C.border, display: "flex", gap: 10, alignItems: "flex-start" }}>
                {pl.video_url && <video src={pl.video_url} controls style={{ width: 92, borderRadius: 6, border: "1px solid " + C.border, background: "#000", flexShrink: 0 }} />}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>Opgeleverd:</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>
                    <Bdg label={pr.status} bg={C.bg} fg={sc} />
                  </div>
                  <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, maxHeight: 54, overflow: "hidden" }}>{String(pl.content || pl.promo || pl.notes || pl.interest || "").slice(0, 220)}</p>
                </div>
              </div>
            );
          })()}
          {openRun === r.id && (
            <div style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid " + C.border }}>
              {r.error && <p style={{ fontSize: 11, color: C.red, marginBottom: 6 }}>Fout: {r.error}</p>}
              {steps.length === 0 && <p style={{ fontSize: 11, color: C.dim }}>Geen stappen gelogd.</p>}
              {steps.map(s => (
                <div key={s.id} style={{ display: "flex", gap: 9, fontSize: 11, padding: "3px 0" }}>
                  <span style={{ color: C.dim, fontFamily: "monospace", flexShrink: 0 }}>{fmtTime(s.at)}</span>
                  <span style={{ color: C.text }}>{s.step}</span>
                  {s.outcome && <span style={{ color: C.muted, marginLeft: "auto", textAlign: "right" }}>{s.outcome}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      ); })}

      <p className="lbl" style={{ margin: "18px 0 8px" }}>Beveiligingslog <span style={{ fontSize: 10, color: C.dim, fontWeight: 400 }}>Field-Safe firewall - secrets naar agents worden gemaskeerd</span></p>
      {sec.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 10 }}>Geen beveiligingsgebeurtenissen. Zou een secret (API-key, token, wachtwoord) richting een agent lekken, dan wordt het hier gemaskeerd en gelogd.</p></div>}
      {sec.map(s => (
        <div key={s.id} className="card" style={{ marginBottom: 5, padding: "8px 13px", display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontFamily: "monospace", letterSpacing: 1, color: C.green, flexShrink: 0 }}>{"•••••"}</span>
          <span style={{ fontSize: 12, fontWeight: 600 }}>{s.action === "prompt_redaction" ? "Secret gemaskeerd vóór de agent" : s.action === "output_redaction" ? "Secret gemaskeerd in agent-output" : s.action}</span>
          <Bdg label={s.result} bg={C.bg} fg={s.result === "masked" ? C.green : s.result === "denied" ? C.red : C.muted} />
          <span style={{ fontSize: 11, color: C.muted }}>{s.hit_count}x · {s.actor}</span>
          <span style={{ fontSize: 10, color: C.dim, fontFamily: "monospace", marginLeft: "auto" }}>{fmtTime(s.at)}</span>
        </div>
      ))}
    </div>
  );
}

// Social-wachtrij (Diva & Gifty): clips en posts die jij hier goedkeurt voordat ze naar buiten mogen.
// De clip staat in opslag 'videos' (map diva-gifty/); de rest in de tabel social_posts (migratie add_social_posts.sql).
function SocialQueue({ user }) {
  const [merk, setMerk] = useState("diva-gifty");
  const tabs = (
    <div style={{ display: "flex", gap: 7, marginBottom: 12 }}>
      {[["diva-gifty", "Diva & Gifty"], ["persona", "Persona (Dif3r3nt + Store)"]].map(([k, l]) => (
        <span key={k} className={"tab" + (merk === k ? " on" : "")} onClick={() => setMerk(k)}>{l}</span>
      ))}
    </div>
  );
  if (merk === "persona") return <div>{tabs}<PersonaWachtrij /></div>;
  return <div>{tabs}<DivaGiftyQueue user={user} /></div>;
}

function DivaGiftyQueue({ user }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);
  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3000); }
  async function load() {
    setLoading(true); setErr(null);
    const { data, error } = await supabase.from("social_posts").select("*").eq("merk", "diva-gifty").order("id");
    if (error) { setErr(error.message); setPosts([]); } else setPosts(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);
  // De database kan de status zelf aanpassen (wijziging na goedkeuring gaat terug naar concept), dus de rij terug inlezen.
  async function save(p, patch) {
    setBusy(p.id);
    const { data, error } = await supabase.from("social_posts").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", p.id).select().single();
    if (error) showToast("✗ " + error.message);
    else setPosts(list => list.map(x => x.id === p.id ? data : x));
    setBusy(null);
    return !error;
  }
  async function upload(p, file) {
    if (!file) return;
    setBusy(p.id);
    const pad = "diva-gifty/" + p.id + ".mp4";
    const { error } = await supabase.storage.from("videos").upload(pad, file, { cacheControl: "3600", upsert: true, contentType: "video/mp4" });
    setBusy(null);
    if (error) { showToast("✗ Uploaden mislukt: " + error.message); return; }
    if (await save(p, { clip_pad: pad })) showToast("✓ Clip geüpload");
  }
  const clipUrl = (pad) => pad ? supabase.storage.from("videos").getPublicUrl(pad).data?.publicUrl + "?t=" + encodeURIComponent(pad) : null;
  const kleur = (st) => st === "goedgekeurd" ? C.green : st === "afgewezen" ? C.red : st === "concept" ? C.yellow : C.blue;
  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? C.red : C.green), padding: "8px 16px", borderRadius: 8, fontSize: 12, zIndex: 99 }}>{toast}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <p style={{ fontSize: 11, color: C.muted, maxWidth: 560 }}>Posts van Diva &amp; Gifty. Elke post heeft het AI-label en toont alleen de avatars. Er gaat niets naar buiten zonder jouw goedkeuring hier, en pas nadat de ouders akkoord hebben gegeven.</p>
        <button className="btn bg" style={{ fontSize: 11 }} onClick={load}>Vernieuwen</button>
      </div>
      {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 10 }}><p style={{ fontSize: 12, color: C.red }}>{err}</p><p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Staat de tabel er al? Draai migratie add_social_posts.sql in Supabase.</p></div>}
      {loading && <p style={{ fontSize: 12, color: C.dim }}>Laden...</p>}
      {!loading && !err && posts.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen posts.</p></div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {posts.map(p => {
          const url = clipUrl(p.clip_pad);
          const regels = Array.isArray(p.regels) ? p.regels : [];
          const tags = Array.isArray(p.hashtags) ? p.hashtags : [];
          return (
            <div key={p.id} className="card" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: kleur(p.status) }}>{p.status}</span>
                <span style={{ fontSize: 11, color: C.muted }}>{p.soort}{p.ai ? " · AI-label" : ""}</span>
              </div>
              {url
                ? <video src={url} controls playsInline style={{ width: "100%", maxHeight: 440, aspectRatio: "9 / 16", borderRadius: 8, border: "1px solid " + C.border, background: "#000" }} />
                : <div style={{ aspectRatio: "9 / 16", maxHeight: 440, borderRadius: 8, border: "1px dashed " + C.border, display: "grid", placeItems: "center", fontSize: 12, color: C.dim, padding: 12, textAlign: "center" }}>Nog geen clip. Upload hieronder het bestand uit Social/clips.</div>}
              <label className="btn bg" style={{ fontSize: 11, textAlign: "center", cursor: "pointer" }}>
                {busy === p.id ? "Bezig..." : url ? "↻ Andere clip uploaden" : "⬆ Clip uploaden (.mp4)"}
                <input type="file" accept="video/mp4" style={{ display: "none" }} disabled={busy === p.id} onChange={e => { upload(p, e.target.files?.[0]); e.target.value = ""; }} />
              </label>
              {regels.length > 0 && <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{regels.map((r, i) => <div key={i}><b style={{ textTransform: "capitalize" }}>{r.wie}:</b> {r.tekst}</div>)}</div>}
              <textarea defaultValue={p.tekst} rows={4} disabled={p.status === "geplaatst"} onBlur={e => { if (e.target.value !== p.tekst) save(p, { tekst: e.target.value.slice(0, 2000) }); }} style={{ width: "100%", fontSize: 12, lineHeight: 1.55, resize: "vertical" }} />
              {tags.length > 0 && <div style={{ fontSize: 11, color: C.muted }}>{tags.join(" ")}</div>}
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn bg" style={{ flex: 1, color: C.red, borderColor: C.red + "40" }} disabled={busy === p.id || p.status === "afgewezen"} onClick={() => save(p, { status: "afgewezen", goedgekeurd_door: null, goedgekeurd_op: null })}>✗ Afwijzen</button>
                <button className="btn bp" style={{ flex: 1 }} disabled={busy === p.id || p.status === "goedgekeurd" || !p.clip_pad} title={p.clip_pad ? "" : "Upload eerst een clip"} onClick={() => save(p, { status: "goedgekeurd", goedgekeurd_door: meKey, goedgekeurd_op: new Date().toISOString(), goedgekeurde_hash: p.content_hash })}>✓ Goedkeuren</button>
              </div>
              {p.status === "goedgekeurd" && <p style={{ fontSize: 10, color: C.muted }}>Goedgekeurd door {p.goedgekeurd_door || "?"}. Inplannen in Metricool kan zodra de accounts van Diva &amp; Gifty gekoppeld zijn.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Boardroom({ user, services = [], setTasks, setView }) {
  const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
  const [sessions, setSessions] = useState([]);
  const [sel, setSel] = useState(null);
  const [goal, setGoal] = useState("");
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("");
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [tasksMade, setTasksMade] = useState(false);
  const [actions, setActions] = useState([]);     // team_actions van de huidige sessie
  const [execBusy, setExecBusy] = useState(null);  // action-id die nu uitgevoerd wordt
  const [openOut, setOpenOut] = useState(null);    // action-id waarvan het resultaat open staat
  const [ctxInfo, setCtxInfo] = useState("");      // korte omschrijving van de geladen bedrijfsdata
  const [input, setInput] = useState("");          // chat-invoer (sparren met het AI-team)
  const [tab, setTab] = useState("gesprekken");    // gesprekken | uitgevoerd
  const [allActions, setAllActions] = useState([]); // acties over alle gesprekken
  const [ovLoading, setOvLoading] = useState(false);
  const sessRef = useRef(null), ctxRef = useRef("");

  useEffect(() => { (async () => {
    const { data } = await supabase.from("team_sessions").select("*").order("created_at", { ascending: false }).limit(20);
    setSessions(data || []);
  })(); }, []);

  // De echte tools van het AI-team. Alle acties zijn intern + omkeerbaar.
  const TOOL_SPECS = 'Beschikbare tools:\n' +
    '- maak_document: LEVER het uitgewerkte werk (plan, analyse, tekst, launch-/SEO-/marketing-uitwerking) VOLLEDIG op in Documenten. args {title, type, brief (wat het exact moet bevatten), content (de complete, direct bruikbare inhoud - GEEN samenvatting of "nog te doen"), dienst}. DIT IS JE STANDAARD voor alles wat het team zelf kan maken.\n' +
    '- maak_afspraak: echte agenda-afspraak. args {title, date("YYYY-MM-DD"), time("HH:MM"), duration(min), attendees(["furgel"]), type, notes}\n' +
    '- maak_crm_lead: prospect in het CRM. args {name, company, interest, segment("b2b"|"b2c"), email}\n' +
    '- zoek_web: actuele webinformatie ophalen. args {query}\n' +
    '- registreer_uren: schrijft uren ECHT in de urenregistratie (Administratie > Partners > Uren), ook met terugwerkende kracht. Gebruik dit zodra Furgel vraagt uren te registreren/boeken/invoeren; maak daarvoor NOOIT een taak. args {entries:[{date("YYYY-MM-DD"), hours(0.25-12), category("Development"|"Klantcontact"|"Administratie"|"Sales"|"Research"|"Overig"), description}]}. Verzin geen uren: gebruik alleen datums/uren die Furgel noemde of die uit echte agendagegevens volgen.\n' +
    '- lees_data: leest echte OS-data uit. args {tabel(' + BOARDROOM_LEESBAAR.join("|") + '), filter({kolom:waarde}), limit(max 50)}. Gebruik dit om te controleren i.p.v. te gokken.\n' +
    '- maak_taak: UITSLUITEND voor iets dat een MENS of externe stap vereist (iemand bellen, fysiek langsgaan, iets kopen, extern versturen, een beslissing die alleen Furgel kan nemen). NOOIT voor werk dat het team zelf kan doen - dat lever je met maak_document. Geef ALTIJD volledige context mee zodat de uitvoerder niets hoeft na te vragen. args {title, owner("furgel"), priority, due, wat (concreet WAT er moet gebeuren, 1-2 zinnen), hoe (HOE: concrete stappen/aanpak), waarom (WAAROM: de reden/aanleiding uit het gesprek)}';

  async function runTool(tool, args) {
    args = args || {};
    if (tool === "maak_taak" && args.title) {
      const owner = "furgel"; // enige teamlid sinds Tristan uit dienst is
      const due = args.due || new Date(Date.now() + 5 * 86400000).toISOString().slice(0, 10);
      const ctxNote = [args.wat && ("Wat: " + args.wat), args.hoe && ("Hoe: " + args.hoe), args.waarom && ("Waarom: " + args.waarom), args.notes].filter(Boolean).join("\n");
      const notes = stripDash((ctxNote ? ctxNote + "\n\n" : "") + "(via AI-Boardroom" + (goal ? ": " + goal : "") + ")").slice(0, 1000);
      const row = { title: String(args.title).slice(0, 200), owner, assignee: owner, priority: taakPrio(args.priority), status: "todo", due, dienst: args.dienst || "", notes };
      const { data, error } = await supabase.from("tasks").insert([row]).select();
      if (error) throw new Error(error.message);
      if (setTasks && data) setTasks(p => [...data, ...(p || [])]);
      return "Taak aangemaakt: " + row.title + " (eigenaar " + owner + ", deadline " + due + ")";
    }
    if ((tool === "registreer_uren" || tool === "add_hours") && (Array.isArray(args.entries) || args.date)) {
      const today = new Date().toISOString().slice(0, 10);
      const lijst = Array.isArray(args.entries) ? args.entries : [args];
      const rows = [], afgekeurd = [];
      for (const e of lijst) {
        const uren = Number(String(e.hours ?? e.uren ?? "").replace(",", "."));
        const date = String(e.date || e.datum || "").slice(0, 10);
        const cat = HOUR_CATS.find(c => c.toLowerCase() === String(e.category || e.categorie || "Overig").toLowerCase()) || "Overig";
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date > today || !(uren > 0 && uren <= 12)) { afgekeurd.push(date || "?"); continue; }
        rows.push({ log_date: date, partner: "furgel", hours: uren, category: cat, description: stripDash(String(e.description || e.omschrijving || "Via AI-Boardroom")).slice(0, 200), year: Number(date.slice(0, 4)) });
      }
      if (!rows.length) throw new Error("Geen geldige uren-regels (datum niet in de toekomst, 0-12 uur per regel)");
      const { error } = await supabase.from("hour_logs").insert(rows);
      if (error) throw new Error(error.message);
      const totaal = rows.reduce((s, r) => s + r.hours, 0);
      return "Uren geregistreerd in de administratie: " + totaal + "u in " + rows.length + " regel(s)" + (afgekeurd.length ? " (overgeslagen wegens ongeldige datum/uren: " + afgekeurd.join(", ") + ")" : "");
    }
    if (tool === "lees_data" && BOARDROOM_LEESBAAR.includes(args.tabel)) {
      let q = supabase.from(args.tabel).select("*");
      Object.entries(args.filter || {}).forEach(([k, v]) => { if (/^[a-z_]+$/.test(k)) q = q.eq(k, v); });
      const { data, error } = await q.limit(Math.min(Number(args.limit) || 20, 50));
      if (error) throw new Error(error.message);
      return args.tabel + " (" + (data || []).length + " rijen):\n" + JSON.stringify(data || []).slice(0, 6000);
    }
    if (tool === "maak_document" && (args.title || args.content)) {
      const row = { title: stripDash(args.title || "AI-document").slice(0, 200), type: args.type || "notitie", content: stripDash(args.content), dienst: args.dienst || null, aangemaakt_door: meKey, is_custom: true, archived: false, version: 1 };
      const { error } = await supabase.from("documents").insert([row]);
      if (error) throw new Error(error.message);
      return "Document opgeslagen in Documenten: " + row.title + " (" + String(args.content || "").length + " tekens)";
    }
    if (tool === "maak_afspraak" && args.title) {
      const att = (Array.isArray(args.attendees) ? args.attendees : []).filter(a => a === "furgel");
      const row = { title: String(args.title).slice(0, 200), date: args.date || new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10), time: args.time || "10:00", duration: Number(args.duration) || 60, owner: att[0] || "furgel", attendees: att.length ? att : ["furgel"], type: args.type || "meeting", notes: args.notes || "", meeting_url: "" };
      const { error } = await supabase.from("events").insert([row]);
      if (error) throw new Error(error.message);
      return "Afspraak ingepland: " + row.title + " op " + row.date + " " + row.time;
    }
    if (tool === "maak_crm_lead" && (args.name || args.company)) {
      const seg = args.segment === "b2c" ? "b2c" : "b2b";
      const row = { name: String(args.name || args.company).slice(0, 160), company: args.company ? String(args.company).slice(0, 160) : null, interest: String(args.interest || "").slice(0, 160), status: seg === "b2b" ? "cold" : "lead", segment: seg, email: args.email || null, source: "AI-team", notes: args.notes || "Toegevoegd door het AI-team" };
      const { error } = await supabase.from("leads").insert([row]);
      if (error) throw new Error(error.message);
      return "CRM-lead toegevoegd: " + row.name;
    }
    if (tool === "zoek_web" && args.query) {
      const web = await researchWeb(String(args.query), "boardroom");
      return "Web-research '" + String(args.query) + "':\n" + (web || "(geen resultaten)");
    }
    if (tool === "concept" && (args.text || args.content)) {
      const content = stripDash(args.text || args.content);
      const row = { title: stripDash(args.title || "AI-concept").slice(0, 200), type: "notitie", content, aangemaakt_door: meKey, is_custom: true, archived: false, version: 1 };
      const { error } = await supabase.from("documents").insert([row]);
      if (error) throw new Error(error.message);
      return "Concept opgeslagen in Documenten: " + row.title;
    }
    return String(args.text || args.content || "(geen uitvoerbare inhoud)");
  }

  // Voert een opgeslagen actie echt uit (knop 'Voer uit' / 'Akkoord & uitvoeren').
  async function executeAction(action) {
    setExecBusy(action.id);
    try {
      let output = await runTool(action.tool, action.args);
      if (!output) output = "(uitgevoerd)";
      await supabase.from("team_actions").update({ output, status: "done" }).eq("id", action.id);
      setActions(p => p.map(a => a.id === action.id ? { ...a, output, status: "done" } : a));
      setOpenOut(action.id);
    } catch (e) { showToast("✗ " + (e.message || "uitvoering mislukt")); }
    setExecBusy(null);
  }
  const approveAction = (action) => executeAction(action);

  function showToast(m) { setToast(m); setTimeout(() => setToast(null), 3000); }
  const pColor = (key) => { const c = TEAM_PERSONAS[key]?.color; return c === "blue" ? C.blue : c === "green" ? C.green : c === "cyan" ? C.cyan : C.purple; };

  // Centraal overzicht: alle acties van het team over alle gesprekken heen.
  async function loadOverview() {
    setOvLoading(true);
    const [{ data: acts }, { data: sess }] = await Promise.all([
      supabase.from("team_actions").select("*").order("created_at", { ascending: false }).limit(200),
      supabase.from("team_sessions").select("id,goal"),
    ]);
    const gmap = {}; (sess || []).forEach(s => { gmap[s.id] = s.goal; });
    setAllActions((acts || []).map(a => ({ ...a, _goal: gmap[a.session_id] || "" })));
    setOvLoading(false);
  }

  async function openSession(s) {
    setSel(s.id); setErr(null); setTasksMade(false); setOpenOut(null); setInput("");
    setGoal(s.goal); setSummary(s.summary || null);
    sessRef.current = s;                          // zonder dit stopt sendMessage/executeAgreed stil
    // Tijdens het laden 'running' houden: anders kan een net verstuurd bericht door
    // setMessages(msgs) hieronder weer overschreven worden (en mist de AI de context).
    setRunning(true); setPhase("Gesprek laden...");
    try {
      ctxRef.current = await buildContext(s.goal);  // datacontext opnieuw laden voor dit gesprek
      const { data: msgs } = await supabase.from("team_messages").select("*").eq("session_id", s.id).order("created_at");
      setMessages(msgs || []);
    } finally { setRunning(false); setPhase(""); }
    let { data: acts } = await supabase.from("team_actions").select("*").eq("session_id", s.id).order("created_at");
    // Oudere sessies hadden acties alleen in summary.acties: eenmalig migreren.
    if ((!acts || !acts.length) && Array.isArray(s.summary?.acties) && s.summary.acties.length) {
      const rows = s.summary.acties.map(a => ({ session_id: s.id, title: a.title, owner: a.owner, risk: a.risk, status: a.risk === "akkoord" ? "wacht" : "open" }));
      const { data: ins } = await supabase.from("team_actions").insert(rows).select();
      acts = ins || [];
    }
    setActions(acts || []);
  }

  // Verwijdert een vergadering. De FK's op team_messages/team_actions staan op
  // 'on delete cascade', dus transcript en actiepunten verdwijnen mee.
  async function deleteSession(id) {
    if (!window.confirm("Deze vergadering definitief verwijderen?\n\nHet transcript en de actiepunten worden ook verwijderd.")) return;
    const { error } = await supabase.from("team_sessions").delete().eq("id", id);
    if (error) { showToast("✗ " + error.message); return; }
    setSessions(p => p.filter(s => s.id !== id));
    if (sel === id) { setSel(null); setMessages([]); setSummary(null); setActions([]); setGoal(""); setOpenOut(null); }
    showToast("✓ Vergadering verwijderd");
  }

  // Compacte, ECHTE bedrijfsdata als context voor de agents.
  async function buildContext(goalText) {
    let dataBlock = "(bedrijfsdata kon niet geladen worden)";
    let extraBlock = "";
    try {
      const vandaag = new Date().toISOString().slice(0, 10), jaar = vandaag.slice(0, 4);
      const over14 = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
      const zacht = (q) => q.then(r => r.data || [], () => []);
      const [pipe, leads, bugs, taken, afspr, uren, trans, docs] = await Promise.all([
        supabase.from("pipeline").select("col").then(r => r.data || []),
        supabase.from("leads").select("segment,status").then(r => r.data || []),
        supabase.from("bugs").select("status").then(r => r.data || []),
        zacht(supabase.from("tasks").select("title,status,due,dienst").neq("status", "done").order("due").limit(15)),
        zacht(supabase.from("events").select("title,date,time").gte("date", vandaag).lte("date", over14).order("date").limit(15)),
        zacht(supabase.from("hour_logs").select("partner,hours").gte("log_date", jaar + "-01-01")),
        zacht(supabase.from("admin_transactions").select("type,amount_excl").gte("trans_date", jaar + "-01-01")),
        zacht(supabase.from("documents").select("title,type").eq("archived", false).order("created_at", { ascending: false }).limit(10)),
      ]);
      const uurSom = uren.filter(u => u.partner === "furgel").reduce((s, u) => s + Number(u.hours || 0), 0);
      const omzet = trans.filter(t => t.type === "income").reduce((s, t) => s + Number(t.amount_excl || 0), 0);
      const inkoop = trans.filter(t => t.type !== "income").reduce((s, t) => s + Number(t.amount_excl || 0), 0);
      extraBlock = "\nOpen taken (" + taken.length + "): " + (taken.map(t => t.title + (t.due ? " [" + t.due + "]" : "")).join("; ") || "geen") +
        "\nAgenda komende 14 dagen: " + (afspr.map(a => a.date + " " + (a.time || "") + " " + a.title).join("; ") || "leeg") +
        "\nUren Furgel " + jaar + ": " + uurSom.toFixed(1) + "h (drempel " + ZELFST_MIN + "h)" +
        "\nBoekhouding " + jaar + ": omzet excl. EUR " + omzet.toFixed(0) + ", inkoop excl. EUR " + inkoop.toFixed(0) +
        "\nRecente documenten: " + (docs.map(d => d.title).join("; ") || "geen");
      const svc = services || [];
      const svcLine = svc.length ? svc.slice(0, 12).map(s => (s.title || s.name || "?") + (s.state ? " (" + s.state + ")" : "")).join(", ") : "geen";
      const pc = {}; pipe.forEach(p => { pc[p.col] = (pc[p.col] || 0) + 1; });
      const pipeLine = Object.keys(pc).length ? Object.entries(pc).map(([k, v]) => k + ": " + v).join(", ") : "leeg";
      const b2b = leads.filter(l => (l.segment || "b2b") === "b2b").length, b2c = leads.length - b2b;
      const openBugs = bugs.filter(b => b.status === "open").length;
      setCtxInfo(svc.length + " diensten · " + leads.length + " leads · " + openBugs + " open bugs");
      dataBlock = "Diensten: " + svcLine + "\nBouwpipeline: " + pipeLine + "\nCRM-leads: " + leads.length + " (B2B " + b2b + ", B2C " + b2c + ")\nOpen bugs: " + openBugs + extraBlock;
    } catch (e) { setCtxInfo("data deels onbeschikbaar"); }
    return "Bedrijf: Dif3r3nt VOF (NL software-/AI-/security-maatwerk). Doel van deze vergadering: " + goalText +
      ".\n\n=== ECHTE BEDRIJFSDATA ===\n" + dataBlock + "\n=== EINDE DATA ===";
  }

  // Eén AI-beurt: Gemini (strateeg) en Groq (operator) reageren parallel, elk als
  // zichzelf, op de volledige gesprekgeschiedenis + OS-kennis + live data.
  async function aiTurn(sess, history) {
    setRunning(true); setPhase("Gemini en Groq denken mee...");
    try {
      const head = OS_TEAM_CONTEXT + "\n\n" + ctxRef.current + "\n\n=== GESPREK TOT NU TOE ===\n" + history.map(m => m.name + ": " + m.content).join("\n\n");
      const ask = (key) => {
        const p = TEAM_PERSONAS[key];
        return callProviderText(p.provider, p.sys + " Antwoord ALS JEZELF (" + p.name + "), in het Nederlands, concreet, geen JSON. Je bent NIET Furgel en spreekt nooit namens hen. CRUCIAAL: als de directie je vraagt iets te LEVEREN of uit te werken (copy, tekst, plan, analyse, checklist, script), lever dan het VOLLEDIGE resultaat DIRECT in dit antwoord - de echte, complete inhoud. Kondig NOOIT alleen aan ('ik ga...', 'hier is...') zonder de inhoud er meteen achteraan; dat is verboden. Beweer ook NOOIT dat je een taak of record hebt aangemaakt - echte uitvoering gebeurt pas als de directie op 'Voer uit' klikt. Puur overleg mag kort; een gevraagd deliverable lever je altijd volledig.", head + "\n\nReageer nu als " + p.name + " op de laatste inbreng van de directie. Wordt er om een deliverable gevraagd, lever het dan volledig in dit bericht.", { temperature: 0.6, maxTokens: 1800, module: "boardroom" })
          .then(content => ({ persona: key, name: p.name, content: stripDash(content) }))
          .catch(e => ({ persona: key, name: p.name, content: "(kon niet antwoorden: " + (e.message || "?") + ")" }));
      };
      const [gem, grq] = await Promise.all([ask("gemini"), ask("groq")]);
      setMessages(h => [...h, gem, grq]);
      await supabase.from("team_messages").insert([
        { session_id: sess.id, persona: "gemini", name: gem.name, content: gem.content, round: 0 },
        { session_id: sess.id, persona: "groq", name: grq.name, content: grq.content, round: 0 },
      ]);
      // Claude (manager) weegt Gemini en Groq af, bepaalt de lijn + de volgende stap.
      setPhase("Claude (manager) weegt af...");
      try {
        const cp = TEAM_PERSONAS.claude;
        const mgrContent = await callProviderText("claude", cp.sys + " Antwoord ALS JEZELF (Claude, manager), in het Nederlands, beslissend. Geen JSON. Houd je afweging kort, maar als je zelf een deliverable oplevert (omdat Sofie/Daan het niet deden), lever dat dan VOLLEDIG en niet afgekapt.", head + "\n\n" + gem.name + " zei: " + gem.content + "\n\n" + grq.name + " zei: " + grq.content + "\n\nGeef als manager je afweging van beide + de concrete volgende stap of het besluit. Als een gevraagd deliverable nog niet geleverd is, lever jij het nu zelf volledig.", { temperature: 0.4, maxTokens: 1600, module: "boardroom" });
        const mgr = { persona: "claude", name: cp.name, content: stripDash(mgrContent) };
        setMessages(h => [...h, mgr]);
        await supabase.from("team_messages").insert([{ session_id: sess.id, persona: "claude", name: mgr.name, content: mgr.content, round: 0 }]);
      } catch (e) { /* de manager-beurt is optioneel; nooit het gesprek blokkeren */ }
    } catch (e) { setErr(e.message || "Antwoord mislukt"); }
    setRunning(false); setPhase("");
  }

  // Start een nieuw gesprek: sessie + OS-datacontext, jouw doel als eerste bericht.
  async function startChat(g) {
    const goalText = (g || "").trim();
    if (!goalText || running) return;
    setErr(null); setActions([]); setOpenOut(null); setTasksMade(false); setSummary(null);
    setRunning(true); setPhase("Sessie en bedrijfsdata laden...");
    try {
      const { data: sess, error } = await supabase.from("team_sessions").insert([{ goal: goalText, status: "running", created_by: meKey }]).select().single();
      if (error) throw new Error(error.message);
      sessRef.current = sess; setSel(sess.id); setSessions(p => [sess, ...p]); setGoal(goalText);
      ctxRef.current = await buildContext(goalText);
      const userMsg = { persona: "user", name: meKey === "tristan" ? "Tristan" : "Furgel", content: goalText };
      setMessages([userMsg]);
      await supabase.from("team_messages").insert([{ session_id: sess.id, persona: "user", name: userMsg.name, content: goalText, round: 0 }]);
      await aiTurn(sess, [userMsg]);
    } catch (e) { setErr(e.message || "Kon niet starten"); showToast("✗ Gesprek starten mislukt: " + (e.message || "onbekend")); setRunning(false); setPhase(""); }
  }

  // Jij spart terug: je bericht gaat naar het AI-team, zij reageren.
  async function sendMessage() {
    const msg = input.trim(); const sess = sessRef.current;
    if (!msg || running) return;
    if (!sess) { showToast("Geen actief gesprek - open of start er eerst een."); return; }
    setInput("");
    const userMsg = { persona: "user", name: meKey === "tristan" ? "Tristan" : "Furgel", content: msg };
    const history = [...messages, userMsg];
    setMessages(history);
    await supabase.from("team_messages").insert([{ session_id: sess.id, persona: "user", name: userMsg.name, content: msg, round: 0 }]);
    await aiTurn(sess, history);
  }

  // "Voer uit": het team bepaalt de concrete acties uit het gesprek, voert de VEILIGE
  // direct uit en zet risico-acties (mail/publiceren/geld/verwijderen) op wacht.
  async function executeAgreed() {
    const sess = sessRef.current;
    if (!sess || running) return;
    if (!messages.length) { showToast("Begin eerst een gesprek."); return; }
    setRunning(true); setPhase("Het AI-team voert de afspraken uit...");
    try {
      const convo = messages.map(m => m.name + ": " + m.content).join("\n\n");
      const sys = OS_TEAM_CONTEXT + "\n\n" + TOOL_SPECS + "\n\nJe bent Max, de manager. Je zet het gesprek om in ECHT AFGELEVERD WERK dat Furgel ontlast - GEEN to-do-lijst. Alles wat het team zelf kan (schrijven, analyseren, plannen, content, een launch-/SEO-/marketing-uitwerking) LEVER je op met maak_document: geef de volledige 'content' OF minimaal een duidelijke 'brief' zodat het team het volledig uitwerkt. Gebruik maak_taak UITSLUITEND voor een echte menselijke/externe stap, NOOIT voor werk dat het team zelf kan doen. Zet risk=akkoord alleen voor naar-buiten/onomkeerbare acties (mail, publiceren, geld uitgeven, verwijderen); intern werk is risk=veilig. Antwoord UITSLUITEND met JSON: {\"samenvatting\":\"1-2 zinnen wat je oplevert\",\"acties\":[{\"title\":\"korte titel\",\"tool\":\"maak_document|maak_afspraak|maak_crm_lead|zoek_web|registreer_uren|lees_data|maak_taak\",\"args\":{\"brief\":\"...\",\"content\":\"...\"},\"risk\":\"veilig|akkoord\"}]}";
      const usr = ctxRef.current + "\n\n=== GESPREK ===\n" + convo + "\n\nGeef nu de acties.";
      const { parsed } = await callGroq(sys, usr, { groqOnly: true, temperature: 0.3, maxTokens: 2500 });
      const acts = (parsed && Array.isArray(parsed.acties)) ? parsed.acties : [];
      const clean = acts.map(a => ({ title: String(a.title || a.tool || "actie").slice(0, 200), tool: a.tool, args: a.args || {}, risk: a.risk === "akkoord" ? "akkoord" : "veilig", owner: meKey })).filter(a => a.tool);
      // Het team werkt de deliverables ECHT uit: voor elk document/concept zonder
      // volwaardige inhoud genereren we de volledige content (grote output via Gemini/Sofie).
      for (const a of clean) {
        if (a.tool === "maak_document" || a.tool === "concept") {
          const have = String((a.args && (a.args.content || a.args.text)) || "");
          if (have.trim().length < 250) {
            setPhase("Sofie werkt uit: " + a.title);
            try {
              const content = await callProviderText("gemini",
                "Je bent het Dif3r3nt AI-team (Sofie/strateeg en Daan/operator, onder leiding van Max). Lever het gevraagde deliverable VOLLEDIG en direct bruikbaar op in het Nederlands - complete, echte inhoud, geen samenvatting en geen 'nog te doen'. Gebruik markdown waar nuttig.",
                ctxRef.current + "\n\n=== GESPREK ===\n" + convo + "\n\nLever nu volledig op:\nTitel: " + a.title + "\nBrief: " + ((a.args && a.args.brief) || a.title),
                { temperature: 0.5, maxTokens: 3500, module: "boardroom" });
              if (content && content.trim()) { a.tool = "maak_document"; a.args = { ...a.args, title: (a.args && a.args.title) || a.title, content: stripDash(content.trim()) }; }
            } catch (e) { /* val terug op de bestaande inhoud */ }
          }
        }
      }
      // Fase 1 change-queue: risico-acties ('akkoord') gaan naar de centrale wachtrij
      // als 'pending' (goedkeuren + expliciet toepassen gebeurt daar). Veilige acties
      // houden de directe interne flow (team_actions + auto-uitvoeren). Faalt de
      // queue-insert (bv. tabel ontbreekt), dan valt die ene actie terug op de oude
      // 'wacht'-flow, zodat er nooit een actie verloren gaat en niets dubbel draait.
      const QMAP = { maak_document: ["content_draft", "documents"], concept: ["content_draft", "documents"], maak_taak: ["task", "tasks"], maak_afspraak: ["agenda_event", "events"], maak_crm_lead: ["crm_update", "leads"], zoek_web: ["research", "web"] };
      let inserted = [];
      const veiligActs = clean.filter(a => a.risk === "veilig");
      if (veiligActs.length) {
        const rows = veiligActs.map(a => ({ session_id: sess.id, title: a.title, owner: a.owner, risk: "veilig", tool: a.tool, args: a.args, status: "open" }));
        const { data: ins, error: insErr } = await supabase.from("team_actions").insert(rows).select();
        if (insErr) throw new Error("Acties opslaan mislukt: " + insErr.message);
        inserted = ins || [];
      }
      for (const a of clean.filter(x => x.risk === "akkoord")) {
        try {
          const [tt, ts] = QMAP[a.tool] || ["generic", "internal"];
          await proposeChange({ sessionId: sess.id, taskType: tt, sourceAgent: "Max", targetSystem: ts, payload: a.args || {}, reasoningLog: a.title, createdBy: meKey });
        } catch (e) {
          await supabase.from("team_actions").insert([{ session_id: sess.id, title: a.title, owner: a.owner, risk: "akkoord", tool: a.tool, args: a.args, status: "wacht" }]);
        }
      }
      setActions(p => [...inserted, ...p]);
      const geleverd = clean.filter(a => a.risk === "veilig").map(a => "- " + a.title + (a.tool === "maak_document" || a.tool === "concept" ? " (document opgeleverd)" : a.tool === "maak_afspraak" ? " (afspraak ingepland)" : a.tool === "maak_crm_lead" ? " (lead toegevoegd)" : a.tool === "zoek_web" ? " (research gedaan)" : a.tool === "registreer_uren" ? " (uren geregistreerd)" : a.tool === "lees_data" ? " (data gelezen)" : " (taak - vereist een mens)")).join("\n");
      const wachten = clean.filter(a => a.risk === "akkoord").map(a => "- " + a.title).join("\n");
      const mgrContent = stripDash((parsed?.samenvatting || "Uitgevoerd.") + (geleverd ? "\n\nOpgeleverd:\n" + geleverd : "") + (wachten ? "\n\nWacht op jouw akkoord (tab Wachtrij):\n" + wachten : "") + (clean.length ? "\n\n(Bekijk de resultaten hieronder of open ze in Documenten.)" : "\n(nog geen concrete acties - spar nog even door)"));
      const summaryMsg = { persona: "claude", name: "Max", content: mgrContent };
      setMessages(h => [...h, summaryMsg]);
      await supabase.from("team_messages").insert([{ session_id: sess.id, persona: "claude", name: "Max", content: mgrContent, round: 0 }]);
      for (const act of inserted.filter(a => a.risk === "veilig")) {
        setPhase("Uitvoeren: " + act.title);
        await executeAction(act);
      }
      await supabase.from("team_sessions").update({ status: "done" }).eq("id", sess.id);
      setSessions(p => p.map(x => x.id === sess.id ? { ...x, status: "done" } : x));
    } catch (e) { console.error("Boardroom uitvoeren:", e); setErr(e.message || "Uitvoeren mislukt"); showToast("✗ Uitvoeren mislukt: " + (e.message || "onbekend")); }
    setRunning(false); setPhase("");
  }

  async function makeTasks() {
    if (!actions.length) return;
    const due = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
    const rows = actions.map(a => ({
      title: a.title, owner: a.owner, assignee: a.owner, priority: a.risk === "akkoord" ? "HIGH" : "MEDIUM",
      status: "todo", due, dienst: "",
      notes: "Uit AI-Boardroom (" + goal + ")" + (a.risk === "akkoord" ? " - LET OP: vereist jouw akkoord voor uitvoering" : ""),
    }));
    const { data, error } = await supabase.from("tasks").insert(rows).select();
    if (error) { showToast("✗ " + error.message); return; }
    if (setTasks && data) setTasks(p => [...data, ...(p || [])]);
    setTasksMade(true);
    showToast("✓ " + rows.length + " taken aangemaakt");
  }

  const svcLabel = (s) => s?.name || s?.title || s?.dienst || "";
  const svcChips = (services || []).map(svcLabel).filter(Boolean).slice(0, 6);

  return (
    <div className="fu">
      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.startsWith("✗") ? C.redDim : C.greenDim, border: "1px solid " + (toast.startsWith("✗") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.25)"), color: toast.startsWith("✗") ? C.red : C.green, padding: "9px 18px", borderRadius: 8, fontSize: 13, zIndex: 999, fontFamily: "monospace" }}>{toast}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ ...KOP_STIJL }}>AI Boardroom</h2>
          <p style={{ fontSize: 11, color: C.muted }}>Max (manager · Claude) stuurt Sofie (strateeg · Gemini) &amp; Daan (operator · Groq) aan - je eigen AI-team dat de hele OS kent en taken echt uitvoert. Noem ze bij naam.</p>
        </div>
        {sel && !running && <button className="btn bg" onClick={() => { setSel(null); setMessages([]); setSummary(null); setGoal(""); setErr(null); setTasksMade(false); setActions([]); setOpenOut(null); setInput(""); }}>← Gesprekken</button>}
      </div>

      {!sel && (
        <>
          <div style={{ display: "flex", gap: 7, marginBottom: 14 }}>
            <span className={"tab" + (tab === "gesprekken" ? " on" : "")} onClick={() => setTab("gesprekken")}>Gesprekken</span>
            <span className={"tab" + (tab === "wachtrij" ? " on" : "")} onClick={() => setTab("wachtrij")}>Wachtrij</span>
            <span className={"tab" + (tab === "social" ? " on" : "")} onClick={() => setTab("social")}>Social</span>
            <span className={"tab" + (tab === "sandbox" ? " on" : "")} onClick={() => setTab("sandbox")}>Sandbox 24/7</span>
            <span className={"tab" + (tab === "uitgevoerd" ? " on" : "")} onClick={() => { setTab("uitgevoerd"); loadOverview(); }}>Uitgevoerd</span>
          </div>

          {tab === "wachtrij" && <ApprovalQueue user={user} />}
          {tab === "social" && <SocialQueue user={user} />}
          {tab === "sandbox" && <SandboxPanel user={user} />}

          {tab === "gesprekken" && (
            <>
              <div className="card" style={{ marginBottom: 16 }}>
                <p className="lbl" style={{ marginBottom: 8 }}>Nieuw gesprek met het AI-team</p>
                <input value={goal} onChange={e => setGoal(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && goal.trim()) startChat(goal); }} placeholder="Wat wil je bespreken of laten uitvoeren? Bijv: werk de launch van Reason3n uit / analyseer onze pipeline" style={{ width: "100%", marginBottom: 8 }} />
                {svcChips.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    {svcChips.map(n => <span key={n} onClick={() => setGoal("Werk de launch uit van de dienst: " + n)} style={{ cursor: "pointer", fontSize: 11, padding: "3px 9px", borderRadius: 6, border: "1px solid " + C.border, color: C.muted }}>Launch: {n}</span>)}
                  </div>
                )}
                <button className="btn bp" disabled={!goal.trim() || running} onClick={() => startChat(goal)}>▶ Start gesprek</button>
                <p style={{ fontSize: 10, color: C.dim, marginTop: 8 }}>Gemini en Groq reageren op je echte bedrijfsdata. Je spart heen en weer; klik op "Voer uit" wanneer ze het werk mogen doen (taken, documenten, agenda, CRM).</p>
              </div>
              <p className="lbl" style={{ marginBottom: 8 }}>Eerdere gesprekken</p>
              {sessions.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog geen gesprekken.</p></div>}
              {sessions.map(s => (
                <div key={s.id} className="card" style={{ marginBottom: 8, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }} onClick={() => openSession(s)}>
                  <div><span style={{ fontSize: 13, fontWeight: 600 }}>{s.goal}</span><span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{(s.created_at || "").slice(0, 10)}</span></div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {s.summary?.acties?.length ? <span style={{ fontSize: 11, color: C.muted }}>{s.summary.acties.length} acties</span> : null}
                    <Bdg label={s.status === "done" ? "AFGEROND" : s.status === "running" ? "BEZIG" : "GESTOPT"} bg={C.bg} fg={s.status === "done" ? C.green : C.muted} />
                    <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} title="Gesprek verwijderen" style={{ background: "none", border: "1px solid " + C.red + "55", borderRadius: 4, color: C.red, cursor: "pointer", fontSize: 12, padding: "2px 7px" }}>✕</button>
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === "uitgevoerd" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <p className="lbl" style={{ margin: 0 }}>Uitgevoerd door het AI-team ({allActions.length})</p>
                <button className="btn bg" style={{ fontSize: 11 }} onClick={loadOverview}>Vernieuwen</button>
              </div>
              <p style={{ fontSize: 10, color: C.dim, marginBottom: 10 }}>Alle acties over alle gesprekken. De echte records staan in de doelmodules (Taken, Documenten, Agenda, CRM).</p>
              {ovLoading && <div style={{ fontSize: 12, color: C.muted, padding: 8 }}>Laden...</div>}
              {!ovLoading && allActions.length === 0 && <div className="card"><p style={{ fontSize: 12, color: C.dim, textAlign: "center", padding: 12 }}>Nog niets uitgevoerd.</p></div>}
              {allActions.map(a => (
                <div key={a.id} className="card" style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{a.title}</span>
                      {a.tool && <span style={{ fontSize: 10, color: C.dim, marginLeft: 6, fontFamily: "monospace" }}>{a.tool}</span>}
                      <p style={{ fontSize: 11, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a._goal ? "uit: " + a._goal : ""}{a.created_at ? " · " + a.created_at.slice(0, 10) : ""}</p>
                    </div>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {a.status === "done" ? <Bdg label="UITGEVOERD" bg={C.greenDim} fg={C.green} /> : a.status === "wacht" ? <Bdg label="WACHT" bg={C.yellowDim} fg={C.yellow} /> : <Bdg label="OPEN" bg={C.bg} fg={C.muted} />}
                    </span>
                  </div>
                  {a.output && <><button className="btn bg" style={{ fontSize: 10, marginTop: 6 }} onClick={() => setOpenOut(o => o === a.id ? null : a.id)}>{openOut === a.id ? "Verberg resultaat" : "Bekijk resultaat"}</button>{openOut === a.id && <div style={{ marginTop: 8, padding: 10, background: C.bg, borderRadius: 8, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{a.output}</div>}</>}
                </div>
              ))}
            </>
          )}
        </>
      )}

      {sel && (
        <>
          <div className="card" style={{ marginBottom: 13 }}><p style={{ fontSize: 13 }}><strong>Onderwerp:</strong> {goal}</p>{ctxInfo && <p style={{ fontSize: 11, color: C.cyan, marginTop: 4 }}>◈ Het team kent je data: {ctxInfo}</p>}</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
            {messages.map((m, i) => {
              const isUser = m.persona === "user";
              const isExec = m.persona === "executor";
              const col = isUser ? C.text : isExec ? C.purple : pColor(m.persona);
              return (
                <div key={i} className="card" style={{ borderLeft: "3px solid " + col, marginLeft: isUser ? 36 : 0, marginRight: isUser ? 0 : 36 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                    <span style={{ color: col, fontSize: 14 }}>{isUser ? "●" : isExec ? "⚙" : (TEAM_PERSONAS[m.persona]?.icon || "•")}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: col }}>{m.name}</span>
                    <span style={{ fontSize: 10, color: C.dim }}>{isUser ? "directie" : isExec ? "uitvoering" : TEAM_PERSONAS[m.persona]?.role}</span>
                  </div>
                  {LeesTekst(m.content)}
                </div>
              );
            })}
            {running && <div style={{ fontSize: 12, color: C.muted, padding: "6px 2px", fontStyle: "italic" }}>{phase || "Bezig..."}</div>}
          </div>

          {err && <div className="card" style={{ borderColor: C.red + "55", marginBottom: 12 }}><p style={{ fontSize: 12, color: C.red }}>Fout: {err}</p></div>}

          <div className="card" style={{ marginBottom: 12 }}>
            <textarea rows={2} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Spar terug, stel een vraag of geef een opdracht... (Enter = versturen)" style={{ width: "100%", marginBottom: 8 }} disabled={running} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn bg" disabled={!input.trim() || running} onClick={sendMessage}>Verstuur</button>
              <button className="btn bp" disabled={running || !messages.length} onClick={executeAgreed} title="Het AI-team voert de afgesproken taken echt uit">▶ Voer uit</button>
            </div>
          </div>

          {actions.length > 0 && (
            <div className="card">
              <p className="lbl" style={{ marginBottom: 4 }}>Acties &amp; uitvoering</p>
              <p style={{ fontSize: 10, color: C.dim, marginBottom: 6 }}>Veilige acties zijn automatisch uitgevoerd; risico-acties (mail, publiceren, geld, verwijderen) wachten op jouw akkoord.</p>
              {actions.map(a => (
                <div key={a.id} style={{ padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13 }}>{a.title}{a.tool ? <span style={{ fontSize: 10, color: C.dim, marginLeft: 6, fontFamily: "monospace" }}>{a.tool}</span> : null}</span>
                    <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {a.status === "done" ? <Bdg label="UITGEVOERD" bg={C.greenDim} fg={C.green} /> : a.status === "wacht" ? <Bdg label="WACHT OP AKKOORD" bg={C.yellowDim} fg={C.yellow} /> : <Bdg label="OPEN" bg={C.bg} fg={C.muted} />}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    {a.status === "open" && <button className="btn bg" disabled={execBusy === a.id} onClick={() => executeAction(a)}>{execBusy === a.id ? "Bezig..." : "▶ Voer uit"}</button>}
                    {a.status === "wacht" && <button className="btn bp" disabled={execBusy === a.id} onClick={() => approveAction(a)}>{execBusy === a.id ? "Bezig..." : "✓ Akkoord & uitvoeren"}</button>}
                    {a.output && <button className="btn bg" onClick={() => setOpenOut(o => o === a.id ? null : a.id)}>{openOut === a.id ? "Verberg resultaat" : "Bekijk resultaat"}</button>}
                  </div>
                  {a.output && openOut === a.id && <div style={{ marginTop: 8, padding: 10, background: C.bg, borderRadius: 8, fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{a.output}</div>}
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button className="btn bg" disabled={tasksMade} onClick={makeTasks}>{tasksMade ? "✓ Taken aangemaakt" : "→ Openstaande als taken"}</button>
                {setView && <button className="btn bg" onClick={() => setView("taken")}>Naar Taken →</button>}
              </div>
            </div>
          )}

          {summary && (summary.samenvatting || summary.besluiten?.length) && (
            <div className="card" style={{ marginTop: 12 }}>
              <p className="lbl" style={{ marginBottom: 8 }}>Eerdere uitkomst</p>
              {summary.samenvatting && <p style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 8 }}>{summary.samenvatting}</p>}
              {summary.besluiten?.length > 0 && <ul style={{ margin: 0, paddingLeft: 18 }}>{summary.besluiten.map((b, i) => <li key={i} style={{ fontSize: 13, marginBottom: 3 }}>{b}</li>)}</ul>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AgentDrawer({ onClose, services = [], tasks = [], user, liveServices = [] }) {
    const userEmail = user?.email || "unknown";
    const [sugs, setSugs] = useState(AGENT_INIT);
    const [agentMemory, setAgentMemory] = useState([]);
    const [editId, setEditId] = useState(null);
    const [editTxt, setEditTxt] = useState("");
    const [loadId, setLoadId] = useState(null);
    const [results, setResults] = useState({});
    const [hist, setHist] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [chatLoading, setChatLoading] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [leads, setLeads] = useState([]);
    const [adminTrans, setAdminTrans] = useState([]);
    const [adminDocs, setAdminDocs] = useState([]);
    const [adminHours, setAdminHours] = useState([]);

    const agentChatEndRef = useRef(null);

    useEffect(() => {
      if (agentChatEndRef.current) {
        agentChatEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, [chatHistory, chatLoading]);

    // ── Claude Agent Integration (Sprint 1) ──
    const [claudeOpen, setClaudeOpen] = useState(false);
    const [claudeAgent, setClaudeAgent] = useState("service_scoring");
    const [claudeTarget, setClaudeTarget] = useState("");
    const [claudeRunning, setClaudeRunning] = useState(false);
    const [claudeResult, setClaudeResult] = useState(null);
    const [claudeError, setClaudeError] = useState(null);
    const [claudeWarning, setClaudeWarning] = useState(null);
    const [claudeRuns, setClaudeRuns] = useState([]);
    const [claudeSpent, setClaudeSpent] = useState(0);
    const [claudeCap, setClaudeCap] = useState(10);

    const CLAUDE_AGENTS = [
      { key: "service_scoring", label: "Service Scoring", icon: "◈", desc: "Multi-criteria scoring met reasoning", entity: "service" },
      { key: "service_classification", label: "Service Classificatie", icon: "◫", desc: "Categoriseren + tagging", entity: "service" },
      { key: "build_review", label: "Build Review", icon: "⬡", desc: "Code/architectuur review", entity: "build_artifact" },
      { key: "crm_assist", label: "CRM Assist", icon: "◑", desc: "Lead/deal reasoning", entity: "lead" },
      { key: "pipeline_orchestrator", label: "Pipeline Orchestrator", icon: "◷", desc: "Pipeline stage routing", entity: "pipeline_card" },
      { key: "lead_enrichment", label: "Lead Enrichment", icon: "◆", desc: "Gestructureerde lead-extractie", entity: "lead" },
      { key: "draft_generation", label: "Draft Generatie", icon: "✎", desc: "Concept e-mails/teksten", entity: "service" },
    ];

    async function invokeClaudeAgent() {
      if (!claudeTarget || claudeRunning) return;
      setClaudeRunning(true);
      setClaudeResult(null);
      setClaudeError(null);
      setClaudeWarning(null);
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session) {
          setClaudeError({ code: "UNAUTHORIZED", message: "Geen actieve sessie — log opnieuw in" });
          setClaudeRunning(false);
          return;
        }
        const agentDef = CLAUDE_AGENTS.find(a => a.key === claudeAgent);
        const resp = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/agent-invoke`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.session.access_token}`,
            },
            body: JSON.stringify({
              agent_type: claudeAgent,
              target_entity_type: agentDef?.entity || "service",
              target_entity_id: claudeTarget,
            }),
          }
        );
        const budgetWarn = resp.headers.get("X-Budget-Warning");
        if (budgetWarn) setClaudeWarning(budgetWarn);
        if (!resp.ok) {
          const errBody = await resp.json();
          setClaudeError(errBody.error || { code: "HTTP_" + resp.status, message: "Onbekende fout" });
          setClaudeRunning(false);
          return;
        }
        const result = await resp.json();
        setClaudeResult(result);
        setClaudeRuns(p => [{ ...result, agent_type: claudeAgent, timestamp: new Date().toISOString() }, ...p].slice(0, 10));
      } catch (err) {
        setClaudeError({ code: "NETWORK_ERROR", message: err?.message || "Netwerk fout" });
      }
      setClaudeRunning(false);
    }

    // Load recent Claude agent runs
    useEffect(() => {
      supabase.from("agent_runs").select("id,agent_type,status,model,created_at").order("created_at", { ascending: false }).limit(5)
        .then(({ data }) => { if (data) setClaudeRuns(data.map(r => ({ run_id: r.id, agent_type: r.agent_type, status: r.status, model_selected: r.model, timestamp: r.created_at }))); });
    }, []);

    // Live Claude-dagbudget (realtime: werkt bij elke spend-update bij)
    useEffect(() => {
      const day = new Date().toISOString().slice(0, 10);
      const load = () => supabase.from("claude_spend_ledger").select("spent_eur,cap_eur").eq("day", day).maybeSingle()
        .then(({ data }) => { if (data) { setClaudeSpent(Number(data.spent_eur)); setClaudeCap(Number(data.cap_eur)); } else { setClaudeSpent(0); } });
      load();
      const ch = supabase.channel("claude_spend_live")
        .on("postgres_changes", { event: "*", schema: "public", table: "claude_spend_ledger" }, load)
        .subscribe();
      return () => { supabase.removeChannel(ch); };
    }, []);

    useEffect(() => {
      const yr = new Date().getFullYear();
      supabase.from("leads").select("id,name,company,status,updated_at").order("created_at", { ascending: false }).limit(20)
        .then(({ data }) => { if (data) setLeads(data); });
      supabase.from("admin_transactions").select("*").gte("trans_date", yr + "-01-01").lte("trans_date", yr + "-12-31")
        .then(({ data }) => { if (data) setAdminTrans(data); });
      supabase.from("admin_docs").select("id,flagged,doc_date").order("created_at", { ascending: false }).limit(100)
        .then(({ data }) => { if (data) setAdminDocs(data); });
      supabase.from("hour_logs").select("*").gte("log_date", yr + "-01-01")
        .then(({ data }) => { if (data) setAdminHours(data); });
      supabase.from("agent_memory").select("topic,insight,created_at").order("created_at", { ascending: false }).limit(20)
        .then(({ data }) => { if (data) setAgentMemory(data); });
    }, []);

    // Load private chat history for current user
    useEffect(() => {
      if (!userEmail || userEmail === "unknown") { setHistoryLoaded(true); return; }
      async function loadChat() {
        const { data, error } = await supabase.from("agent_chats")
          .select("id,role,text,actions,created_at")
          .eq("user_email", userEmail)
          .order("created_at", { ascending: true })
          .limit(200);
        if (!error && data) setChatHistory(data.map(r => ({ id: r.id, role: r.role, text: r.text, actions: r.actions || [] })));
        setHistoryLoaded(true);
      }
      loadChat();
    }, [userEmail]);

    async function persistMessage(role, text, actions = []) {
      if (!userEmail || userEmail === "unknown") return null;
      const { data, error } = await supabase.from("agent_chats").insert([{ user_email: userEmail, role, text, actions }]).select();
      return error || !data ? null : data[0];
    }

    async function resetChat() {
      setHist([]);
      setChatHistory([]);
      setSugs(AGENT_INIT);
      if (userEmail && userEmail !== "unknown") {
        await supabase.from("agent_chats").delete().eq("user_email", userEmail);
      }
    }

    function buildContext() {
      const today = new Date().toISOString().split("T")[0];
      const yr = new Date().getFullYear();
      const mo = new Date().getMonth() + 1;
      const curQ = Math.ceil(mo / 3);
      const curQStr = "Q" + curQ;

      // Year financials
      const income = adminTrans.filter(t => t.type === "income").reduce((s, t) => s + (t.amount_excl || 0), 0);
      const expense = adminTrans.filter(t => t.type === "expense").reduce((s, t) => s + (t.amount_excl || 0), 0);
      const profit = income - expense;

      // Current quarter BTW
      const qStarts = { "Q1": `${yr}-01-01`, "Q2": `${yr}-04-01`, "Q3": `${yr}-07-01`, "Q4": `${yr}-10-01` };
      const qEnds = { "Q1": `${yr}-03-31`, "Q2": `${yr}-06-30`, "Q3": `${yr}-09-30`, "Q4": `${yr}-12-31` };
      const qTrans = adminTrans.filter(t => t.trans_date >= qStarts[curQStr] && t.trans_date <= qEnds[curQStr]);
      const vatOut = qTrans.filter(t => t.type === "income").reduce((s, t) => s + (t.vat_amount || 0), 0);
      const vatIn = qTrans.filter(t => t.type === "expense").reduce((s, t) => s + (t.vat_amount || 0), 0);
      const vatSaldo = vatOut - vatIn;
      const BTW_DL = { "Q1": "30 april", "Q2": "31 juli", "Q3": "31 oktober", "Q4": "31 januari" };
      const dlMap = { "Q1": new Date(yr, 3, 30), "Q2": new Date(yr, 6, 31), "Q3": new Date(yr, 9, 31), "Q4": new Date(yr + 1, 0, 31) };
      const daysUntilBTW = Math.ceil((dlMap[curQStr] - new Date()) / (1000 * 60 * 60 * 24));

      // Hours
      const furgH = adminHours.filter(h => h.partner === "furgel").reduce((s, h) => s + (h.hours || 0), 0);
      const tristH = adminHours.filter(h => h.partner === "tristan").reduce((s, h) => s + (h.hours || 0), 0);
      const ZMIN = 1225;

      // Flagged docs
      const flagged = adminDocs.filter(d => d.flagged).length;

      const svcList = services.map(s => s.title).join(", ") || "geen";
      const taskList = tasks.slice(0, 8).map(t => `${t.title} (${t.assignee}, ${t.priority}, ${t.status}, deadline:${t.due})`).join("; ") || "geen";
      const leadList = leads.slice(0, 6).map(l => `${l.name} @ ${l.company || "—"} [${l.status}]`).join("; ") || "geen";
      const liveCount = liveServices.length;
      const memBlock = (agentMemory && agentMemory.length)
        ? "\n\n=== GELEERDE INZICHTEN (uit eerdere Claude-analyses) ===\n" + agentMemory.slice(0, 15).map(m => "• " + (m.topic ? m.topic + ": " : "") + m.insight).join("\n")
        : "";

      return `\n\n=== CONTEXT ${today} ===
Live diensten: ${liveCount}${liveCount === 0 ? " — Geen omzetgenererende diensten online. STOP met cashflow/omzet/sales-suggesties; focus op validatie + MVP afronden + lancering." : ""}
Diensten in validatie/bouw: ${svcList}
Openstaande taken: ${taskList}
Actieve leads: ${leadList}

=== FINANCIËN ${yr} ===
Omzet: €${income.toFixed(2)} | Kosten: €${expense.toFixed(2)} | Nettowinst: €${profit.toFixed(2)}
Gemarkeerde docs (vereisen actie): ${flagged}

=== BTW ${curQStr} ${yr} ===
Verschuldigd: €${vatOut.toFixed(2)} | Voorbelasting: €${vatIn.toFixed(2)} | Saldo te betalen: €${vatSaldo.toFixed(2)}
Deadline: ${BTW_DL[curQStr]} (nog ${daysUntilBTW} dagen)

=== UREN ${yr} (drempel: ${ZMIN}h) ===
Furgel: ${furgH.toFixed(1)}h (${Math.round(furgH / ZMIN * 100)}%)
Tristan (niet meer actief, alleen historie; plan niets voor hem): ${tristH.toFixed(1)}h${memBlock}
=== EINDE CONTEXT ===`;
    }

    async function executeActions(actions) {
      if (!actions?.length) return [];
      const log = [];
      for (const a of actions) {
        try {
          if (a.tool === "create_task") {
            const row = { title: a.title, assignee: "furgel", owner: "furgel", priority: taakPrio(a.priority), status: "todo", due: a.due || new Date().toISOString().split("T")[0], notes: a.notes || "", dienst: a.dienst || "", bron: "AI-assistent" };
            if (String(a.actie || "").trim()) row.actie = stripDash(String(a.actie)).slice(0, 400);
            if (String(a.waarom || "").trim()) row.waarom = stripDash(String(a.waarom)).slice(0, 500);
            const { error } = await supabase.from("tasks").insert([row]);
            log.push(error ? `✗ Taak mislukt: ${error.message}` : `✓ Taak aangemaakt: "${a.title}"`);
          } else if (a.tool === "create_event") {
            const row = { title: a.title, date: a.date, time: a.time || "10:00", duration: a.duration || 60, owner: a.attendees?.[0] || "furgel", attendees: a.attendees || ["furgel"], type: a.type || "meeting", notes: a.notes || "", meeting_url: "" };
            const { error } = await supabase.from("events").insert([row]);
            log.push(error ? `✗ Agenda mislukt: ${error.message}` : `✓ Agenda-item aangemaakt: "${a.title}" op ${a.date}`);
          } else if (a.tool === "create_lead") {
            const row = { name: a.name, company: a.company || "—", interest: a.interest || "—", status: a.status || "new", value: parseInt(a.value) || 0, notes: a.notes || "" };
            const { error } = await supabase.from("leads").insert([row]);
            log.push(error ? `✗ Lead mislukt: ${error.message}` : `✓ Lead aangemaakt: "${a.name}" @ ${a.company}`);
          } else if (a.tool === "create_document") {
            const row = { title: a.title, type: a.type || "notitie", content: a.content || "", aangemaakt_door: a.aangemaakt_door || "furgel", dienst: a.dienst || null, is_custom: true, archived: false, version: 1 };
            const { error } = await supabase.from("documents").insert([row]);
            log.push(error ? `✗ Document mislukt: ${error.message}` : `✓ Document opgeslagen: "${a.title}"`);
          } else if (a.tool === "add_transaction") {
            const excl = parseFloat(a.amount_excl) || 0;
            const rate = parseInt(a.vat_rate) || 21;
            const vat = parseFloat(a.vat_amount) || (excl * rate / 100);
            const row = { trans_date: a.trans_date || new Date().toISOString().split("T")[0], type: a.type || "expense", category: a.category || "Overig", description: a.description || "", amount_excl: excl, vat_rate: rate, vat_amount: parseFloat(vat.toFixed(2)), amount_incl: parseFloat((excl + vat).toFixed(2)), payment_method: a.payment_method || "bank" };
            const { error } = await supabase.from("admin_transactions").insert([row]);
            log.push(error ? `✗ Transactie mislukt: ${error.message}` : `✓ ${a.type === "income" ? "Omzet" : "Kosten"} €${excl.toFixed(2)} geboekt — ${a.description}`);
          } else if (a.tool === "add_hours") {
            const row = { log_date: a.log_date || new Date().toISOString().split("T")[0], partner: a.partner || "furgel", hours: parseFloat(a.hours) || 0, category: a.category || "Projectwerk", description: a.description || "" };
            const { error } = await supabase.from("hour_logs").insert([row]);
            log.push(error ? `✗ Uren mislukt: ${error.message}` : `✓ ${row.hours}h geregistreerd voor ${row.partner} — ${row.description}`);
          } else if (a.tool === "add_km") {
            const km = parseFloat(a.km) || 0;
            const row = { log_date: a.log_date || new Date().toISOString().split("T")[0], partner: a.partner || "furgel", from_loc: a.from_loc || "", to_loc: a.to_loc || "", purpose: a.purpose || "", km, aftrek: parseFloat((km * 0.23).toFixed(2)) };
            const { error } = await supabase.from("mileage_logs").insert([row]);
            log.push(error ? `✗ KM mislukt: ${error.message}` : `✓ ${km}km geregistreerd (€${(km * 0.23).toFixed(2)} aftrek) — ${row.purpose}`);
          } else if (a.tool === "add_mutation") {
            const row = { mut_date: a.mut_date || new Date().toISOString().split("T")[0], partner: a.partner || "furgel", type: a.type || "onttrekking", amount: parseFloat(a.amount) || 0, description: a.description || "" };
            const { error } = await supabase.from("partner_mutations").insert([row]);
            log.push(error ? `✗ Mutatie mislukt: ${error.message}` : `✓ ${a.type === "storting" ? "Storting" : "Onttrekking"} €${row.amount.toFixed(2)} geregistreerd — ${row.description}`);
          } else if (a.tool === "log_ev_trip") {
            const km = parseFloat(a.distance_km) || 0;
            const kwh100 = 18, eprijs = 0.32;
            const totalKwh = Math.round(km * (kwh100 / 100) * 100) / 100;
            const costTrip = Math.round(totalKwh * eprijs * 100) / 100;
            const yr = new Date(a.trip_date || new Date()).getFullYear();
            const q = "Q" + Math.ceil((new Date(a.trip_date || new Date()).getMonth() + 1) / 3);
            const row = { driver: a.driver || "furgel", trip_date: a.trip_date || new Date().toISOString().split("T")[0], start_location: a.start_location || "", end_location: a.end_location || "", distance_km: km, purpose: a.purpose || "zakelijk", description: a.description || "", client_project: a.client_project || "", kwh_per_100km: kwh100, electricity_price_kwh: eprijs, total_kwh: totalKwh, cost_per_trip: costTrip, cost_per_km: km > 0 ? Math.round(costTrip / km * 1000) / 1000 : 0, year: yr, quarter: q + "-" + yr, flagged: !a.distance_km };
            const { error } = await supabase.from("ev_trips").insert([row]);
            log.push(error ? `✗ EV rit mislukt: ${error.message}` : `✓ EV rit geregistreerd: ${a.start_location} → ${a.end_location} (${km}km, €${costTrip.toFixed(2)})`);
          } else if (a.tool === "research_query") {
            const { content, sourcesCount } = await research(a.query);
            log.push(`Research: "${a.query}"${sourcesCount > 0 ? ` (${sourcesCount} bronnen)` : ""}\n${content}`);
          }
        } catch (e) {
          log.push(`✗ Fout: ${e.message}`);
        }
      }
      return log;
    }

    async function run(s, custom = null) {
      const sid = s.id;
      setLoadId(sid); setEditId(null);
      const task = (custom ? "Originele taak: " + s.title + " — " + s.body + ". Aanpassing: " + custom + ". Verwerk en voer uit." : "Voer uit: " + s.title + " — " + s.body) + buildContext();
      try {
        const res = await claude(BUSINESS_AGENT_SYS, task);
        setHist(p => [...p, { role: "user", content: task }, { role: "assistant", content: JSON.stringify(res) }]);
        const log = await executeActions(res.actions);
        const display = res.result + (log.length ? "\n\n" + log.join("\n") : "");
        setResults(p => ({ ...p, [sid]: display }));
        if (res.suggestions?.length) setTimeout(() => setSugs(res.suggestions.map((x, i) => ({ ...x, id: "ai" + Date.now() + i }))), 1800);
      } catch (err) {
        setResults(p => ({ ...p, [sid]: "Fout: " + (err?.message || "Onbekende fout") }));
      }
      setLoadId(null);
    }

    async function sendChat() {
      const msg = chatInput.trim();
      if (!msg || chatLoading) return;
      setChatInput("");
      setChatLoading(true);
      const userMsg = { id: "chat" + Date.now(), role: "user", text: msg };
      setChatHistory(p => [...p, userMsg]);
      const savedUser = await persistMessage("user", msg, []);
      if (savedUser) userMsg.id = savedUser.id;
      try {
        const res = await claude(BUSINESS_AGENT_SYS, msg + buildContext());
        const log = await executeActions(res.actions);
        const display = res.result + (log.length ? "\n\n" + log.join("\n") : "");
        const agentMsg = { id: "r" + Date.now(), role: "agent", text: display, actions: log };
        setChatHistory(p => [...p, agentMsg]);
        const savedAgent = await persistMessage("agent", display, log);
        if (savedAgent) agentMsg.id = savedAgent.id;
        if (res.suggestions?.length) setSugs(res.suggestions.map((x, i) => ({ ...x, id: "ai" + Date.now() + i })));
      } catch (err) {
        const errText = "Fout: " + (err?.message || "Onbekende fout");
        setChatHistory(p => [...p, { id: "e" + Date.now(), role: "agent", text: errText }]);
        await persistMessage("agent", errText, []);
      }
      setChatLoading(false);
    }

    return (
      <div className="drw" style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 13px 12px", borderBottom: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Dot /><span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, letterSpacing: ".08em" }}>DIF3R3NT AGENT</span>
            <Bdg label={sugs.length + " taken"} bg={C.greenDim} fg={C.green} />
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 18 }}>×</button>
        </div>
        {user && (
          <div style={{ padding: "5px 13px", borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", gap: 6, background: C.bg }}>
            <span style={{ fontSize: 9, color: C.dim }}></span>
            <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".05em" }}>PRIVÉ — alleen zichtbaar voor {user.name}</span>
          </div>
        )}

        {/* ── Claude Agent Panel ── */}
        <div style={{ borderBottom: "1px solid " + C.border }}>
          <div onClick={() => setClaudeOpen(p => !p)} style={{ padding: "8px 13px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: claudeOpen ? C.purpleDim : "transparent", transition: "background .15s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontSize: 12, color: C.purple }}>⬡</span>
              <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace", letterSpacing: ".06em", color: claudeOpen ? C.purple : C.muted }}>CLAUDE AGENT</span>
              <span style={{ fontSize: 9, color: C.dim, background: C.purpleDim, padding: "2px 6px", borderRadius: 10, fontFamily: "monospace" }}>Sprint 1</span>
            </div>
            <span style={{ fontSize: 10, color: C.muted, transition: "transform .2s", transform: claudeOpen ? "rotate(90deg)" : "none" }}>▶</span>
          </div>
          {claudeOpen && (
            <div style={{ padding: "10px 13px", display: "flex", flexDirection: "column", gap: 10, animation: "fadeUp .15s ease" }}>
              {/* Live dagbudget-meter */}
              {(() => {
                const pct = claudeCap > 0 ? Math.min(100, Math.round(claudeSpent / claudeCap * 100)) : 0;
                const reached = claudeSpent >= claudeCap;
                const over80 = pct >= 80 && !reached;
                const left = Math.max(0, claudeCap - claudeSpent);
                return (
                  <div style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 6, padding: "8px 10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 10, fontFamily: "monospace", letterSpacing: ".06em", color: C.muted }}>CLAUDE — DAGBUDGET</span>
                      <span style={{ fontSize: 11, fontFamily: "monospace", color: reached ? C.red : over80 ? C.yellow : C.green }}>€{left.toFixed(2)} over</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: reached ? C.red : over80 ? C.yellow : C.green, transition: "width .3s" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                      <span style={{ fontSize: 9, color: C.dim, fontFamily: "monospace" }}>€{claudeSpent.toFixed(2)} / €{claudeCap.toFixed(2)} gebruikt</span>
                      {reached && <span style={{ fontSize: 9, color: C.red, fontFamily: "monospace" }}>valt terug op Groq</span>}
                    </div>
                  </div>
                );
              })()}
              {/* Agent Type Selector */}
              <div>
                <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginBottom: 5, letterSpacing: ".06em" }}>AGENT TYPE</p>
                <select value={claudeAgent} onChange={e => setClaudeAgent(e.target.value)} style={{ fontSize: 12, background: C.bg, borderColor: C.purple + "40" }}>
                  {CLAUDE_AGENTS.map(a => (
                    <option key={a.key} value={a.key}>{a.icon} {a.label}</option>
                  ))}
                </select>
                <p style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>{CLAUDE_AGENTS.find(a => a.key === claudeAgent)?.desc}</p>
              </div>

              {/* Target Entity */}
              <div>
                <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginBottom: 5, letterSpacing: ".06em" }}>TARGET ENTITY</p>
                <select value={claudeTarget} onChange={e => setClaudeTarget(e.target.value)} style={{ fontSize: 12, background: C.bg, borderColor: C.purple + "40" }}>
                  <option value="">— Selecteer —</option>
                  {services.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              </div>

              {/* Invoke Button */}
              <button
                className="btn"
                disabled={!claudeTarget || claudeRunning}
                onClick={invokeClaudeAgent}
                style={{ background: claudeRunning ? C.purpleDim : C.purple, color: claudeRunning ? C.purple : "#000", fontWeight: 700, fontSize: 11, fontFamily: "monospace", letterSpacing: ".05em", border: "none", opacity: (!claudeTarget || claudeRunning) ? .5 : 1, cursor: (!claudeTarget || claudeRunning) ? "wait" : "pointer" }}
              >
                {claudeRunning ? "⟳ Agent verwerkt..." : "▶ INVOKE AGENT"}
              </button>

              {/* Budget Warning */}
              {claudeWarning && (
                <div style={{ background: C.yellowDim, border: "1px solid rgba(224,168,46,.3)", borderRadius: 5, padding: "6px 10px" }}>
                  <p style={{ fontSize: 10, color: C.yellow, fontFamily: "monospace" }}>⚠ {claudeWarning}</p>
                </div>
              )}

              {/* Error */}
              {claudeError && (
                <div style={{ background: C.redDim, border: "1px solid rgba(229,86,78,.3)", borderRadius: 5, padding: "8px 10px" }}>
                  <p style={{ fontSize: 10, color: C.red, fontFamily: "monospace", marginBottom: 3 }}>✗ {claudeError.code}</p>
                  <p style={{ fontSize: 11, color: C.text }}>{claudeError.message}</p>
                </div>
              )}

              {/* Success Result */}
              {claudeResult && !claudeError && (
                <div style={{ background: C.purpleDim, border: "1px solid rgba(154,160,176,.25)", borderRadius: 5, padding: "8px 10px" }}>
                  <p style={{ fontSize: 10, color: C.purple, fontFamily: "monospace", marginBottom: 4 }}>✓ AGENT KLAAR</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <p style={{ fontSize: 11, color: C.text }}>Run ID: <span style={{ fontFamily: "monospace", color: C.muted, fontSize: 10 }}>{claudeResult.run_id?.slice(0, 8)}...</span></p>
                    <p style={{ fontSize: 11, color: C.text }}>Status: <span style={{ color: C.green, fontWeight: 600 }}>{claudeResult.status}</span></p>
                    <p style={{ fontSize: 11, color: C.text }}>Model: <span style={{ fontFamily: "monospace", color: C.purple }}>{claudeResult.model_selected}</span></p>
                    {typeof claudeResult.cost_eur === "number" && (
                      <p style={{ fontSize: 11, color: C.text }}>Kosten: <span style={{ fontFamily: "monospace", color: C.muted }}>€{Number(claudeResult.cost_eur).toFixed(4)} · {(claudeResult.input_tokens || 0) + (claudeResult.output_tokens || 0)} tok</span></p>
                    )}
                  </div>
                  {claudeResult.output && (
                    <pre style={{ marginTop: 6, padding: "6px 8px", background: C.bg, borderRadius: 4, fontSize: 10, color: C.text, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 180, overflowY: "auto", fontFamily: "monospace" }}>
                      {typeof claudeResult.output === "string" ? claudeResult.output : (claudeResult.output.summary || JSON.stringify(claudeResult.output, null, 2))}
                    </pre>
                  )}
                </div>
              )}

              {/* Recent Runs */}
              {claudeRuns.length > 0 && (
                <div>
                  <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginBottom: 5, letterSpacing: ".06em" }}>RECENTE RUNS</p>
                  {claudeRuns.slice(0, 5).map((r, i) => {
                    const sc = { queued: C.yellow, pending: C.yellow, running: C.blue, completed: C.green, failed: C.red };
                    return (
                      <div key={r.run_id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: "1px solid " + C.border }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc[r.status] || C.muted, display: "inline-block" }}></span>
                          <span style={{ fontSize: 10, color: C.text, fontFamily: "monospace" }}>{r.agent_type?.replace("_", " ")}</span>
                        </div>
                        <span style={{ fontSize: 9, color: C.dim, fontFamily: "monospace" }}>{r.timestamp ? new Date(r.timestamp).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        {(hist.length > 0 || chatHistory.length > 0) && (
          <div style={{ padding: "6px 13px", borderBottom: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{Math.floor(hist.length / 2) + chatHistory.filter(x => x.role === "user").length} acties uitgevoerd</span>
            <button onClick={resetChat} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 10 }} title="Verwijder alle chat geschiedenis (alleen jouw eigen)">Reset</button>
          </div>
        )}
        <div style={{ padding: 9, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          {sugs.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {sugs.map((s, i) => {
                const sid = s.id, isL = loadId === sid, isE = editId === sid, res = results[sid];
                const tc = TCOL[s.type] || C.green;
                return (
                  <div key={sid} style={{ background: C.bg, border: "1px solid " + (isL ? C.green + "50" : C.border), borderRadius: 7, padding: 12, marginBottom: 8, opacity: isL ? .8 : 1, transition: "all .2s", animationDelay: (i * .04) + "s" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{s.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{s.title}</span>
                          <Bdg label={s.type} bg={tc + "20"} fg={tc} />
                        </div>
                      </div>
                    </div>
                    {isE ? (
                      <div style={{ marginBottom: 9 }}>
                        <textarea value={editTxt} onChange={e => setEditTxt(e.target.value)} rows={3} style={{ fontSize: 12, lineHeight: 1.5, resize: "vertical", borderColor: "rgba(52,189,130,.4)" }} autoFocus />
                        <p style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Pas aan — agent voert gewijzigde taak uit.</p>
                      </div>
                    ) : (
                      <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 9 }}>{s.body}</p>
                    )}
                    {res && !isL && (
                      <div style={{ background: res.startsWith("Fout:") ? C.redDim : C.greenDim, border: "1px solid " + (res.startsWith("Fout:") ? "rgba(229,86,78,.3)" : "rgba(52,189,130,.18)"), borderRadius: 5, padding: "8px 11px", marginBottom: 9 }}>
                        <p style={{ fontSize: 10, color: res.startsWith("Fout:") ? C.red : C.green, fontFamily: "monospace", marginBottom: 4, letterSpacing: ".06em" }}>{res.startsWith("Fout:") ? "✗ FOUT" : "✓ UITVOER"}</p>
                        <p style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{res}</p>
                        {res.startsWith("Fout:") && <button className="btn bd" style={{ fontSize: 10, padding: "4px 9px", marginTop: 7 }} onClick={() => { setResults(p => ({ ...p, [sid]: null })); run(s); }}>↻ Opnieuw proberen</button>}
                      </div>
                    )}
                    {isL && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.greenDim, borderRadius: 5, padding: "8px 11px", marginBottom: 9 }}>
                        <Dot /><p style={{ fontSize: 11, color: C.green, fontFamily: "monospace" }}>Agent verwerkt...</p>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                      {isE ? (
                        <>
                          <button className="btn bp" style={{ fontSize: 10, padding: "4px 9px" }} onClick={() => run(s, editTxt)}>▶ Uitvoeren</button>
                          <button className="btn bg" style={{ fontSize: 10, padding: "4px 9px" }} onClick={() => setEditId(null)}>Annuleer</button>
                        </>
                      ) : (
                        <>
                          <button className="btn bp" style={{ fontSize: 10, padding: "4px 9px" }} disabled={isL} onClick={() => run(s)}>▶ Accepteer & Uitvoer</button>
                          <button className="btn bg" style={{ fontSize: 10, padding: "4px 9px" }} disabled={isL} onClick={() => { setEditId(sid); setEditTxt(s.body); }}>✎ Aanpassen</button>
                          <button className="btn bd" style={{ fontSize: 10, padding: "4px 7px" }} disabled={isL} onClick={() => setSugs(p => p.filter(x => x.id !== sid))}>✕</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {!sugs.length && !chatHistory.length && (
            <div style={{ padding: 20, textAlign: "center", color: C.dim, fontSize: 12 }}>
              <p style={{ fontSize: 20, marginBottom: 8 }}>✓</p><p>Alle taken verwerkt.</p>
              <button className="btn bg" style={{ marginTop: 10, fontSize: 11 }} onClick={() => setSugs(AGENT_INIT)}>Herlaad</button>
            </div>
          )}
          {sugs.length > 0 && chatHistory.length > 0 && (
            <div style={{ borderBottom: "1px solid " + C.border, margin: "10px 0" }} />
          )}
          {chatHistory.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", letterSpacing: ".06em", marginBottom: 4 }}>GESPREK MET AGENT</p>
              {chatHistory.map(m => {
                const isMine = m.role === "user";
                return (
                  <div key={m.id} style={{ alignSelf: isMine ? "flex-end" : "flex-start", maxWidth: "85%", display: "flex", flexDirection: "column" }}>
                    <div style={{
                      background: isMine ? C.greenDim : C.surface,
                      border: "1px solid " + (isMine ? C.green + "40" : C.border),
                      padding: "8px 12px",
                      borderRadius: 14,
                      borderBottomRightRadius: isMine ? 4 : 14,
                      borderBottomLeftRadius: isMine ? 14 : 4,
                      fontSize: 12,
                      color: isMine ? C.green : C.text,
                      lineHeight: 1.45,
                      whiteSpace: "pre-wrap"
                    }}>
                      {m.text}
                    </div>
                  </div>
                );
              })}
              {chatLoading && <div style={{ display: "flex", alignItems: "center", gap: 7, alignSelf: "flex-start" }}><Dot /><p style={{ fontSize: 11, color: C.green, fontFamily: "monospace" }}>Agent denkt na...</p></div>}
              <div ref={agentChatEndRef} />
            </div>
          )}
        </div>
        <div style={{ padding: "10px 10px 12px", borderTop: "1px solid " + C.border, flexShrink: 0 }}>
          <p style={{ fontSize: 10, color: C.dim, fontFamily: "monospace", marginBottom: 6, letterSpacing: ".06em" }}>VRAAG AAN AGENT</p>
          <div style={{ display: "flex", gap: 6 }}>
            <textarea
              rows={2}
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="Stel een vraag of geef een opdracht... (Enter = verstuur)"
              style={{ flex: 1, fontSize: 12, lineHeight: 1.5, resize: "none", borderColor: C.border, fontFamily: "inherit" }}
            />
            <button
              className="btn bp"
              style={{ fontSize: 12, padding: "6px 11px", alignSelf: "stretch" }}
              disabled={chatLoading || !chatInput.trim()}
              onClick={sendChat}
            >▶</button>
          </div>
        </div>
      </div>
    );
  }


  // ─── AGENDA ──────────────────────────────────────────────────────────────────
  const USERS = {
    furgel: { name: "Furgel", role: "CTO", color: C.green, email: "furgel@dif3r3nt.nl" },
    tristan: { name: "Tristan", role: "CCO", color: C.blue, email: "tristan@dif3r3nt.nl" },
  };

  const TODAY = new Date();
  function dateStr(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }
  function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
  function addMonths(d, n) { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; }

  const INIT_EVENTS = [];

  const ETC = { meeting: { bg: C.purpleDim, fg: C.purple }, demo: { bg: C.greenDim, fg: C.green }, build: { bg: C.yellowDim, fg: C.yellow }, call: { bg: C.blueDim, fg: C.blue }, deadline: { bg: C.redDim, fg: C.red }, feestdag: { bg: "rgba(48,48,74,.5)", fg: C.muted } };

  function Agenda({ initialEventId, setView, user, setMeetingSessionId }) {
    const [events, setEvents] = useState(INIT_EVENTS);
    const [selEv, setSelEv] = useState(null);
    const [newMod, setNewMod] = useState(false);
    const [viewMode, setViewMode] = useState(() => (typeof window !== "undefined" && window.innerWidth <= 900 ? "lijst" : "maand"));
    const [wk, setWk] = useState(0);
    const [mo, setMo] = useState(0);
    const NEV_INIT = { title: "", date: dateStr(addDays(TODAY, 1)), time: "10:00", duration: 60, owner: "furgel", attendees: ["furgel"], type: "meeting", notes: "", meeting_url: "", location: "", reeks: REEKS_NIEUW };
    const [nev, setNev] = useState(NEV_INIT);
    const [editMod, setEditMod] = useState(false);
    const [evEdit, setEvEdit] = useState(null);
    const [evBereik, setEvBereik] = useState("deze");
    const notifiedRef = useRef(new Set());

    // Browser notificaties: 1u voor + bij start.
    // Guard: op mobiel (o.a. iOS Safari buiten een geïnstalleerde PWA) bestaat de
    // Notification-API niet. Zonder deze check gooit Notification.permission een
    // ReferenceError in de effect; omdat er geen error boundary is crasht daardoor
    // de hele app en blijft het scherm zwart bij het openen van de Agenda.
    useEffect(() => {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission === "default") { try { Notification.requestPermission(); } catch {} }
      const tick = setInterval(() => {
        if (Notification.permission !== "granted") return;
        const now = new Date();
        events.filter(e => !e.cancelled).forEach(ev => {
          const evDt = new Date(ev.date + "T" + ev.time);
          const diffMin = Math.round((evDt - now) / 60000);
          const keyHour = ev.id + "_1h";
          const keyStart = ev.id + "_start";
          if (diffMin >= 58 && diffMin <= 62 && !notifiedRef.current.has(keyHour)) {
            notifiedRef.current.add(keyHour);
            new Notification("Herinnering: " + ev.title, { body: "Over 1 uur — " + ev.time + " | " + ev.date, icon: "/favicon.ico" });
          }
          if (diffMin >= -2 && diffMin <= 2 && !notifiedRef.current.has(keyStart)) {
            notifiedRef.current.add(keyStart);
            new Notification("Vergadering gestart: " + ev.title, { body: ev.time + " — Succes!", icon: "/favicon.ico" });
          }
        });
      }, 30000);
      return () => clearInterval(tick);
    }, [events]);

    // Laad events uit Supabase
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("events").select("*").order("date", { ascending: true });
        if (!error && data && data.length > 0) {
          setEvents(data);
          if (initialEventId) {
            const found = data.find(e => String(e.id) === String(initialEventId));
            if (found) setSelEv(found.id);
          }
        }
      }
      load();
      const ch = supabase.channel("events_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "events" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    const wkStart = addDays(TODAY, wk * 7 - TODAY.getDay() + 1);
    const wkDays = Array.from({ length: 7 }, (_, i) => addDays(wkStart, i));

    const moStart = new Date(TODAY.getFullYear(), TODAY.getMonth() + mo, 1);
    const moDays = new Date(TODAY.getFullYear(), TODAY.getMonth() + mo + 1, 0).getDate();
    const moFirstDay = (moStart.getDay() + 6) % 7;
    const moCells = Array.from({ length: 42 }, (_, i) => { const d = i - moFirstDay + 1; return (d > 0 && d <= moDays) ? addDays(moStart, d - 1) : null; });

    async function submit() {
      if (!nev.title.trim()) { alert("Je bent vergeten de Titel in te vullen bovenin!"); return; }
      const { reeks, location, ...base } = nev;
      if (!base.lead_id) delete base.lead_id;
      if (location && location.trim()) base.notes = (base.notes ? base.notes + "\n" : "") + "Locatie: " + location.trim();
      const { datums } = reeksDatums(base.date, reeks, { max: 52 });
      if (datums.length === 0) { alert("Met deze herhaling valt er geen enkele datum binnen de reeks. Pas de instellingen aan."); return; }
      const count = datums.length;
      const recur = reeks.patroon;
      const recurId = recur !== "none" && count > 1 ? "recur_" + Date.now() : null;
      const eventsToAdd = datums.map(d => ({ ...base, date: d, recur_id: recurId }));
      const { data, error } = await supabase.from("events").insert(eventsToAdd).select();
      if (!error && data) { setEvents(p => [...p, ...data]); logActivity("afspraak", (eventsToAdd[0] && eventsToAdd[0].title) || "", "agenda"); }
      else setEvents(p => [...p, ...eventsToAdd.map((e, i) => ({ ...e, id: Date.now() + i }))]);
      setNewMod(false);
      setNev(NEV_INIT);
      // Automatische email uitnodiging (na save zodat we het id hebben voor de deep-link)
      const first = eventsToAdd[0];
      const savedFirst = (!error && data) ? data[0] : first;
      const appLink = window.location.origin + "/?event=" + savedFirst.id;
      const recurNote = recurId ? "\n\nHerhalend: " + count + "x (" + reeksOmschrijving(reeks) + ").\nEerste datum: " + first.date + "\nLaatste datum: " + eventsToAdd[eventsToAdd.length - 1].date : "";
      const subj = encodeURIComponent("Uitnodiging: " + base.title + " — " + first.date + " " + first.time);
      const body = encodeURIComponent(
        "Hallo,\n\nEr is een afspraak ingepland:\n\n" +
        "" + base.title + "\n" + first.date + " om " + first.time + "\n" + base.duration + " min | " + base.type +
        (base.meeting_url ? "\n\nDEELNEMEN AAN VERGADERING:\n" + base.meeting_url : "") +
        "\n\nDIRECT OPENEN IN DIF3R3NT OS:\n" + appLink +
        (base.notes ? "\n\nNotities: " + base.notes : "") + recurNote +
        "\n\nJe ontvangt automatisch een herinnering 1 uur van tevoren en bij de start.\n\nGroeten,\nDif3R3Nt OS"
      );
      window.open("mailto:furgel@dif3r3nt.nl?subject=" + subj + "&body=" + body);
    }
    async function delEv(id) {
      await supabase.from("events").delete().eq("id", id);
      setEvents(p => p.filter(e => e.id !== id)); setSelEv(null);
    }
    async function delReeks(ev, vanafDeze) {
      const lijst = events.filter(e => e.recur_id && e.recur_id === ev.recur_id && (!vanafDeze || e.date >= ev.date));
      const tekst = vanafDeze ? "Deze afspraak en alle volgende uit de reeks (" + lijst.length + " in totaal) verwijderen?" : "De hele reeks (" + lijst.length + " afspraken) verwijderen?";
      if (!window.confirm(tekst)) return;
      let q = supabase.from("events").delete().eq("recur_id", ev.recur_id);
      if (vanafDeze) q = q.gte("date", ev.date);
      const { error } = await q;
      if (error) { alert("Verwijderen van de reeks mislukt: " + error.message); return; }
      const weg = new Set(lijst.map(e => e.id));
      setEvents(p => p.filter(e => !weg.has(e.id))); setSelEv(null);
    }
    async function cancelEv(id) {
      const upd = { cancelled: true };
      const { data, error } = await supabase.from("events").update(upd).eq("id", id).select();
      if (!error && data) setEvents(p => p.map(e => e.id === id ? data[0] : e));
      else setEvents(p => p.map(e => e.id === id ? { ...e, cancelled: true } : e));
      setSelEv(null);
    }
    async function updateEv() {
      if (!evEdit || !evEdit.title.trim()) return;
      const { id, status, actual_start, actual_end, cancelled, meeting_url, ...fields } = evEdit;

      // Hele reeks of deze en volgende wijzigen: alleen gedeelde velden, en nooit afspraken die al klaar, bezig of geannuleerd zijn.
      const orig = events.find(e => e.id === id);
      const bereik = orig && orig.recur_id ? evBereik : "deze";
      if (bereik !== "deze") {
        const doelen = events.filter(e => e.recur_id === orig.recur_id && e.id !== id && (bereik === "alle" || e.date >= orig.date) && !e.cancelled && e.status !== "completed" && e.status !== "live");
        const delta = Math.round((new Date(fields.date + "T00:00:00") - new Date(orig.date + "T00:00:00")) / 86400000);
        const gedeeld = { title: fields.title, time: fields.time, duration: fields.duration, type: fields.type, owner: fields.owner, attendees: fields.attendees, notes: fields.notes };
        const eigen = await supabase.from("events").update(fields).eq("id", id).select();
        if (eigen.error) { alert("Wijzigen mislukt: " + eigen.error.message); return; }
        const nieuw = { [id]: { ...eigen.data[0], meeting_url, status, cancelled } };
        let mislukt = 0;
        for (const e of doelen) {
          const upd = delta === 0 ? gedeeld : { ...gedeeld, date: dateStr(addDays(new Date(e.date + "T00:00:00"), delta)) };
          const r = await supabase.from("events").update(upd).eq("id", e.id).select();
          if (r.error || !r.data) mislukt++; else nieuw[e.id] = { ...r.data[0], meeting_url: e.meeting_url, status: e.status, cancelled: e.cancelled };
        }
        setEvents(p => p.map(e => nieuw[e.id] ? nieuw[e.id] : e));
        setEditMod(false); setEvEdit(null); setSelEv(null);
        alert((doelen.length + 1 - mislukt) + " afspraken uit de reeks gewijzigd" + (mislukt ? ", " + mislukt + " mislukt" : "") + ".");
        return;
      }

      // Alleen de fields opslaan die echt in events tabel staan (tijdelijke oplossing voor ontbrekende kolommen)
      const { data, error } = await supabase.from("events").update(fields).eq("id", id).select();

      if (!error && data) setEvents(p => p.map(e => e.id === id ? { ...data[0], meeting_url, status, cancelled } : e));
      else setEvents(p => p.map(e => e.id === id ? evEdit : e));

      setEditMod(false); setEvEdit(null); setSelEv(null);

      // E-mail notificatie bij wijziging
      const subj = encodeURIComponent("✎ Afspraak gewijzigd: " + evEdit.title + " — " + evEdit.date + " " + evEdit.time);
      const body = encodeURIComponent(
        "Hallo,\n\nEen afspraak is zojuist gewijzigd in Dif3R3Nt OS:\n\n" +
        "" + evEdit.title + "\n" + evEdit.date + " om " + evEdit.time + "\n" + evEdit.duration + " min | " + evEdit.type +
        (evEdit.meeting_url ? "\n\nMeeting link: " + evEdit.meeting_url : "") +
        (evEdit.notes ? "\n\nNotities: " + evEdit.notes : "") +
        "\n\nBekijk in Dif3R3Nt OS:\n" + window.location.origin + "/?event=" + id +
        "\n\nGroeten,\nDif3R3Nt OS"
      );
      // Voorkom popup blocker
      window.location.href = "mailto:furgel@dif3r3nt.nl?subject=" + subj + "&body=" + body;
    }
    async function startMeeting(id) {
      const ev = events.find(e => e.id === id);
      const now = new Date().toISOString();
      const { data, error } = await supabase.from("events").update({ status: "live", actual_start: now }).eq("id", id).select();
      if (!error && data) setEvents(p => p.map(e => e.id === id ? data[0] : e));
      else setEvents(p => p.map(e => e.id === id ? { ...e, status: "live", actual_start: now } : e));
      if (ev?.meeting_url) window.open(ev.meeting_url, "_blank");
    }
    async function endMeeting(id) {
      const now = new Date().toISOString();
      const { data, error } = await supabase.from("events").update({ status: "completed", actual_end: now }).eq("id", id).select();
      if (!error && data) setEvents(p => p.map(e => e.id === id ? data[0] : e));
      else setEvents(p => p.map(e => e.id === id ? { ...e, status: "completed", actual_end: now } : e));
    }
    function togAtt(u) { setNev(p => ({ ...p, attendees: p.attendees.includes(u) ? p.attendees.filter(a => a !== u) : [...p.attendees, u] })); }
    function togAttEdit(u) { setEvEdit(p => ({ ...p, attendees: p.attendees.includes(u) ? p.attendees.filter(a => a !== u) : [...p.attendees, u] })); }

    const upcoming = events.filter(e => e.date >= dateStr(TODAY)).sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

    if (selEv) {
      const ev = events.find(e => e.id === selEv);
      if (!ev) { setSelEv(null); return null; }
      const tc = ETC[ev.type] || ETC.meeting;
      return (
        <div className="fu">
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20 }}>
            <button className="btn bg" onClick={() => setSelEv(null)}>Terug</button>
            <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>{ev.title}</h2>
            <Bdg label={ev.type} bg={tc.bg} fg={tc.fg} />
            {(() => { const s = ev.cancelled ? "cancelled" : (ev.status || "scheduled"); const sc = { scheduled: { bg: C.blueDim, fg: C.blue, l: "Gepland" }, live: { bg: C.greenDim, fg: C.green, l: "● LIVE" }, completed: { bg: "rgba(96,96,128,.12)", fg: C.muted, l: "Afgerond" }, cancelled: { bg: C.redDim, fg: C.red, l: "Geannuleerd" } }; const cfg = sc[s] || sc.scheduled; return <Bdg label={cfg.l} bg={cfg.bg} fg={cfg.fg} />; })()}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <div className="card">
              <p className="lbl" style={{ marginBottom: 12 }}>Details</p>
              {[["Datum", ev.date], ["Tijd", ev.time], ["Duur", ev.duration + " min"], ["Type", ev.type], ["Door", USERS[ev.owner]?.name || ev.owner]].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.border }}>
                  <span style={{ fontSize: 12, color: C.muted }}>{k}</span><span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              <div style={{ marginTop: 11 }}>
                <p className="lbl" style={{ marginBottom: 7 }}>Deelnemers</p>
                <div style={{ display: "flex", gap: 8 }}>
                  {ev.attendees.map(a => { const u = USERS[a]; return <span key={a} style={{ padding: "3px 10px", borderRadius: 12, fontSize: 12, background: u?.color + "20", color: u?.color, fontWeight: 600 }}>{u?.name || a}</span>; })}
                </div>
              </div>
            </div>
            <div className="card">
              <p className="lbl" style={{ marginBottom: 10 }}>Notities</p>
              <div style={{ background: C.bg, borderRadius: 6, padding: 12, border: "1px solid " + C.border, fontSize: 13, lineHeight: 1.7, marginBottom: 13, minHeight: 60 }}>{ev.notes || "Geen notities."}</div>
              {(() => { const loc = ((ev.notes || "").match(/Locatie:\s*([^\n]+)/i) || [])[1]; if (!loc) return null; return <div style={{ marginBottom: 13 }}><p className="lbl" style={{ marginBottom: 7 }}>Locatie op kaart</p><AddressMap query={loc.trim() + ", Nederland"} height={180} /></div>; })()}
              {ev.meeting_url && (
                <a href={ev.meeting_url} target="_blank" rel="noopener noreferrer" style={{ display: "block", background: C.purple + "20", border: "1px solid " + C.purple + "50", borderRadius: 8, padding: "10px 14px", marginBottom: 9, textDecoration: "none", textAlign: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.purple }}>Externe meeting-link</span>
                  <p style={{ fontSize: 10, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.meeting_url}</p>
                </a>
              )}
              {(ev.active_session_id || ev.type === "meeting") && (
                <div style={{ marginBottom: 11 }}>
                  <button className="btn bp" style={{ width: "100%", padding: "11px 14px", fontWeight: 700, background: ev.active_session_id ? C.red : C.green, color: "#000" }} onClick={async () => {
                    if (!setView || !setMeetingSessionId) return;
                    if (ev.active_session_id) {
                      setMeetingSessionId(ev.active_session_id);
                      setView("meeting");
                    } else {
                      // Maak nieuwe gedeelde meeting sessie
                      const row = {
                        event_id: ev.id, title: ev.title, host_email: user?.email || "",
                        participants: ev.attendees || ["furgel"],
                        agenda_text: ev.notes || "",
                      };
                      const { data, error } = await supabase.from("meeting_sessions").insert([row]).select();
                      if (error) { alert("Kon vergader sessie niet starten: " + error.message); return; }
                      if (data && data[0]) {
                        await supabase.from("events").update({ active_session_id: data[0].id }).eq("id", ev.id);
                        setEvents(p => p.map(e => e.id === ev.id ? { ...e, active_session_id: data[0].id } : e));
                        setMeetingSessionId(data[0].id);
                        setView("meeting");
                      }
                    }
                  }}>{ev.active_session_id ? "Join Vergader Modus (sessie actief)" : "Start Vergader Modus"}</button>
                  <p style={{ fontSize: 10, color: C.muted, textAlign: "center", marginTop: 5 }}>
                    {ev.active_session_id ? "Een sessie loopt al. Klik om in te haken; elk apparaat transcribeert lokaal en het transcript wordt samengevoegd." : "Start een vergadersessie. Deelnemers haken in vanaf hun eigen apparaat en het transcript wordt samengevoegd."}
                  </p>
                </div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!ev.cancelled && (!ev.status || ev.status === "scheduled") && <button className="btn bp" style={{ fontWeight: 700, width: "100%" }} onClick={() => startMeeting(ev.id)}>▶ Start Vergadering</button>}
                {!ev.cancelled && ev.status === "live" && <button style={{ fontWeight: 700, width: "100%", padding: "9px 16px", borderRadius: 8, border: "none", background: C.green, color: "#000", cursor: "pointer", fontSize: 13 }} onClick={() => endMeeting(ev.id)}>■ Beëindig Vergadering</button>}
                <button className="btn bg" style={{ flex: 1 }} onClick={() => {
                  const appLink = window.location.origin + "/?event=" + ev.id;
                  const s = encodeURIComponent("Uitnodiging: " + ev.title + " — " + ev.date + " " + ev.time);
                  const b = encodeURIComponent(
                    "Hallo,\n\nJe bent uitgenodigd voor:\n\n" + ev.title +
                    "\n" + ev.date + " om " + ev.time + "\n" + ev.duration + " min | " + ev.type +
                    (ev.meeting_url ? "\n\nDEELNEMEN AAN VERGADERING:\n" + ev.meeting_url : "") +
                    "\n\nDIRECT OPENEN IN DIF3R3NT OS:\n" + appLink +
                    (ev.notes ? "\n\nNotities: " + ev.notes : "") +
                    "\n\nJe ontvangt automatisch een herinnering 1 uur van tevoren.\n\nGroeten,\nDif3R3Nt OS"
                  );
                  window.open("mailto:furgel@dif3r3nt.nl?subject=" + s + "&body=" + b);
                }}>Stuur uitnodiging</button>
                <button className="btn bg" onClick={() => { setEvEdit({ ...ev }); setEvBereik("deze"); setEditMod(true); }}>✎ Wijzig</button>
                {!ev.cancelled
                  ? <button className="btn bg" style={{ color: C.yellow, borderColor: C.yellow + "40" }} onClick={() => cancelEv(ev.id)}>⊘ Annuleer</button>
                  : <span style={{ fontSize: 12, color: C.red, alignSelf: "center", fontWeight: 600 }}>Geannuleerd</span>}
                <button className="btn bd" onClick={() => delEv(ev.id)}>Verwijder</button>
                {ev.recur_id && events.filter(e => e.recur_id === ev.recur_id).length > 1 && (
                  <>
                    <button className="btn bd" onClick={() => delReeks(ev, true)}>Verwijder deze en volgende</button>
                    <button className="btn bd" onClick={() => delReeks(ev, false)}>Verwijder hele reeks</button>
                  </>
                )}
              </div>
            </div>
          </div>

          {editMod && evEdit && (
            <Portal><div className="ovl" onClick={() => { setEditMod(false); setEvEdit(null); }}>
              <div className="mdl" onClick={e => e.stopPropagation()}>
                <h3 style={{ fontFamily: "monospace", marginBottom: 17, fontSize: 14 }}>✎ AFSPRAAK WIJZIGEN</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Titel *</p><input value={evEdit.title} onChange={e => setEvEdit(p => ({ ...p, title: e.target.value }))} placeholder="bijv. Demo AutoCompli" /></div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Datum</p><input type="date" value={evEdit.date} onChange={e => setEvEdit(p => ({ ...p, date: e.target.value }))} /></div>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Tijd</p><input type="time" value={evEdit.time} onChange={e => setEvEdit(p => ({ ...p, time: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Duur (min)</p><input type="number" value={evEdit.duration} onChange={e => setEvEdit(p => ({ ...p, duration: parseInt(e.target.value) || 60 }))} /></div>
                    <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Type</p>
                      <select value={evEdit.type} onChange={e => setEvEdit(p => ({ ...p, type: e.target.value }))}>
                        <option value="meeting">Meeting</option><option value="demo">Demo</option><option value="build">Build</option><option value="call">Call</option><option value="deadline">Deadline</option><option value="feestdag">Feestdag</option>
                      </select>
                    </div>
                  </div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Aangemaakt door</p>
                    <select value={evEdit.owner} onChange={e => setEvEdit(p => ({ ...p, owner: e.target.value }))}>
                      <option value="furgel">Furgel</option>
                    </select>
                  </div>
                  <div>
                    <p style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Deelnemers</p>
                    <div style={{ display: "flex", gap: 10 }}>
                      {["furgel"].map(u => { const on = evEdit.attendees?.includes(u); const usr = USERS[u]; return <div key={u} onClick={() => togAttEdit(u)} style={{ padding: "7px 14px", borderRadius: 20, cursor: "pointer", border: "1px solid " + (on ? usr.color : C.border), background: on ? usr.color + "20" : "transparent", color: on ? usr.color : C.muted, fontSize: 13, fontWeight: 600, transition: "all .15s" }}>{usr.name}</div>; })}
                    </div>
                  </div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Notities</p><textarea rows={2} value={evEdit.notes || ""} onChange={e => setEvEdit(p => ({ ...p, notes: e.target.value }))} placeholder="Optionele notities..." /></div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Meeting link</p><input value={evEdit.meeting_url || ""} onChange={e => setEvEdit(p => ({ ...p, meeting_url: e.target.value }))} placeholder="https://meet.google.com/..." /></div>
                  {(() => {
                    const o = events.find(e => e.id === evEdit.id);
                    const n = o && o.recur_id ? events.filter(e => e.recur_id === o.recur_id).length : 0;
                    if (n < 2) return null;
                    const vanaf = events.filter(e => e.recur_id === o.recur_id && e.date >= o.date).length;
                    return (
                      <div style={{ background: C.bg, borderRadius: 8, padding: "10px 12px", border: "1px solid " + C.border }}>
                        <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Deze afspraak hoort bij een reeks van {n}. Wat wil je wijzigen?</p>
                        <select value={evBereik} onChange={e => setEvBereik(e.target.value)}>
                          <option value="deze">Alleen deze afspraak</option>
                          <option value="volgende">Deze en alle volgende ({vanaf})</option>
                          <option value="alle">Hele reeks ({n})</option>
                        </select>
                        {evBereik !== "deze" && <p style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>Titel, tijd, duur, type, deelnemers en notities gaan mee. Verschuif je de datum, dan schuiven alle gekozen afspraken evenveel dagen mee. Afspraken die al klaar, bezig of geannuleerd zijn, blijven zoals ze zijn. De meeting link geldt alleen voor deze afspraak.</p>}
                      </div>
                    );
                  })()}
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 5 }}>
                    <button className="btn bg" onClick={() => { setEditMod(false); setEvEdit(null); }}>Annuleer</button>
                    <button className="btn bp" onClick={updateEv}>Opslaan</button>
                  </div>
                </div>
              </div>
            </div></Portal>
          )}
        </div>
      );
    }

    return (
      <div className="fu">
        <Kop titel="Agenda" rechts={<button className="btn bp" onClick={() => setNewMod(true)}>+ Afspraak</button>} />
        <div style={{ display: "flex", gap: 7, marginBottom: 14 }}>{["maand", "week", "lijst"].map(m => <span key={m} className={"tab" + (viewMode === m ? " on" : "")} onClick={() => setViewMode(m)}>{m.charAt(0).toUpperCase() + m.slice(1)}</span>)}</div>

        {viewMode === "maand" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 13 }}>
              <button className="btn bg" style={{ fontSize: 12 }} onClick={() => setMo(p => p - 1)}>Vorige maand</button>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{moStart.toLocaleDateString("nl-NL", { month: "long", year: "numeric" })}</span>
              <button className="btn bg" style={{ fontSize: 12 }} onClick={() => setMo(p => p + 1)}>Volgende maand</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8, marginBottom: 8 }}>
              {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: C.muted }}>{d}</div>)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8, marginBottom: 15 }}>
              {moCells.map((day, i) => {
                if (!day) return <div key={i} style={{ background: C.surface, opacity: 0.3, borderRadius: 8, minHeight: 90 }} />;
                const ds = dateStr(day);
                const de = events.filter(e => e.date === ds).sort((a, b) => a.time.localeCompare(b.time));
                const isT = ds === dateStr(TODAY);
                return (
                  <div key={i} style={{ background: isT ? C.greenDim : C.surface, border: "1px solid " + (isT ? C.green + "40" : C.border), borderTopWidth: isT ? 3 : 1, borderRadius: 8, padding: 8, minHeight: 90, cursor: "pointer", transition: "background .1s" }} onClick={() => { setNev(p => ({ ...p, date: ds })); setNewMod(true); }} onMouseOver={e => e.currentTarget.style.background = isT ? C.greenDim : "#1c1d23"} onMouseOut={e => e.currentTarget.style.background = isT ? C.greenDim : C.surface}>
                    <p style={{ fontSize: 12, fontWeight: isT ? 700 : 500, color: isT ? C.green : C.muted, marginBottom: 5 }}>{day.getDate()}</p>
                    {de.map(ev => {
                      const tc = ETC[ev.type] || ETC.meeting; const isLive = !ev.cancelled && ev.status === "live"; const chip = isLive ? { bg: C.greenDim, fg: C.green } : ev.cancelled ? { bg: C.redDim, fg: C.red } : ev.status === "completed" ? { bg: "rgba(48,48,74,.5)", fg: C.muted } : tc; return (
                        <div key={ev.id} onClick={(e) => { e.stopPropagation(); setSelEv(ev.id) }} style={{ background: chip.bg, borderRadius: 4, padding: "2px 5px", marginBottom: 3, borderLeft: "2px solid " + chip.fg, opacity: ev.cancelled || ev.status === "completed" ? 0.6 : 1 }}>
                          <p style={{ fontSize: 10, fontWeight: 600, color: chip.fg, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: ev.cancelled ? "line-through" : "none" }}>{isLive ? "● " : ""}{ev.time} {ev.title}</p>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewMode === "week" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 13 }}>
              <button className="btn bg" style={{ fontSize: 12 }} onClick={() => setWk(p => p - 1)}>Vorige week</button>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Week van {wkStart.toLocaleDateString("nl-NL", { day: "numeric", month: "long" })}</span>
              <button className="btn bg" style={{ fontSize: 12 }} onClick={() => setWk(p => p + 1)}>Volgende week</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "46px repeat(7,1fr)", minWidth: 560, border: "1px solid " + C.border, borderRadius: 8, overflow: "hidden" }}>
                <div style={{ background: C.bg, borderBottom: "1px solid " + C.border, borderRight: "1px solid " + C.border }} />
                {wkDays.map(day => {
                  const ds = dateStr(day); const isT = ds === dateStr(TODAY); return (
                    <div key={ds} style={{ background: isT ? C.greenDim : C.bg, borderBottom: "1px solid " + C.border, borderRight: "1px solid " + C.border + "60", padding: "7px 4px", textAlign: "center" }}>
                      <p style={{ fontSize: 10, fontWeight: 700, color: isT ? C.green : C.muted, textTransform: "uppercase", marginBottom: 2 }}>{day.toLocaleDateString("nl-NL", { weekday: "short" })}</p>
                      <p style={{ fontSize: 15, fontWeight: 700, color: isT ? C.green : "inherit" }}>{day.getDate()}</p>
                    </div>
                  );
                })}
                {Array.from({ length: 15 }, (_, i) => i + 7).flatMap(hour => {
                  const timeStr = `${String(hour).padStart(2, "0")}:00`;
                  return [
                    <div key={"t" + hour} style={{ background: C.bg, borderBottom: "1px solid " + C.border + "40", borderRight: "1px solid " + C.border, padding: "5px 5px 0", display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
                      <span style={{ fontSize: 9, color: C.dim, fontFamily: "monospace" }}>{timeStr}</span>
                    </div>,
                    ...wkDays.map(day => {
                      const ds = dateStr(day);
                      const slotEvs = events.filter(e => e.date === ds && e.time === timeStr);
                      const isT = ds === dateStr(TODAY);
                      return (
                        <div key={ds + hour} style={{ borderBottom: "1px solid " + C.border + "20", borderRight: "1px solid " + C.border + "30", minHeight: 44, padding: 3, cursor: "pointer", background: isT ? "rgba(52,189,130,.03)" : "transparent", transition: "background .1s" }}
                          onClick={() => { setNev(p => ({ ...p, date: ds, time: timeStr })); setNewMod(true); }}
                          onMouseOver={e => e.currentTarget.style.background = "rgba(52,189,130,.06)"}
                          onMouseOut={e => e.currentTarget.style.background = isT ? "rgba(52,189,130,.03)" : "transparent"}>
                          {slotEvs.map(ev => {
                            const tc = ETC[ev.type] || ETC.meeting;
                            const sc = ev.cancelled ? { bg: C.redDim, fg: C.red } : ev.status === "live" ? { bg: C.greenDim, fg: C.green } : ev.status === "completed" ? { bg: "rgba(48,48,74,.6)", fg: C.muted } : tc;
                            return (
                              <div key={ev.id} onClick={e => { e.stopPropagation(); setSelEv(ev.id); }} style={{ background: sc.bg, borderLeft: "3px solid " + sc.fg, borderRadius: 3, padding: "2px 5px", marginBottom: 2, cursor: "pointer", opacity: ev.cancelled || ev.status === "completed" ? 0.6 : 1 }}>
                                <p style={{ fontSize: 10, fontWeight: 600, color: sc.fg, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: ev.cancelled ? "line-through" : "none" }}>{ev.status === "live" ? "● " : ""}{ev.title}</p>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  ];
                })}
              </div>
            </div>
          </div>
        )}

        {viewMode === "lijst" && (upcoming.length === 0 ? (
          <div className="card"><Leeg tekst="Geen aankomende afspraken." /></div>
        ) : (() => {
          const vandaagStr = dateStr(TODAY);
          const morgenStr = dateStr(addDays(TODAY, 1));
          const weekStr = dateStr(addDays(TODAY, 7));
          const dagNaam = d => { const x = new Date(d + "T00:00:00"); return isNaN(x) ? d : x.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" }); };
          const groepen = [
            { key: "vandaag", label: "Vandaag", kleur: C.green, items: upcoming.filter(e => e.date === vandaagStr) },
            { key: "morgen", label: "Morgen", kleur: C.blue, items: upcoming.filter(e => e.date === morgenStr) },
            { key: "week", label: "Deze week", kleur: C.yellow, items: upcoming.filter(e => e.date > morgenStr && e.date <= weekStr) },
            { key: "later", label: "Later", kleur: C.muted, items: upcoming.filter(e => e.date > weekStr), standaardDicht: true },
          ].filter(g => g.items.length);
          const STATUS = { scheduled: { bg: C.blueDim, fg: C.blue, l: "Gepland" }, live: { bg: C.greenDim, fg: C.green, l: "● LIVE" }, completed: { bg: "rgba(96,96,128,.12)", fg: C.muted, l: "Afgerond" }, cancelled: { bg: C.redDim, fg: C.red, l: "Geannuleerd" } };
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {groepen.map(g => (
                <Groep key={g.key} label={g.label} kleur={g.kleur} aantal={g.items.length} standaardDicht={g.standaardDicht}>
                  {g.items.map(ev => {
                    const tc = ETC[ev.type] || ETC.meeting;
                    const cfg = STATUS[ev.cancelled ? "cancelled" : (ev.status || "scheduled")] || STATUS.scheduled;
                    return (
                      <Rij key={ev.id} onKlik={() => setSelEv(ev.id)} gedimd={!!ev.cancelled}
                        titel={ev.title}
                        rechts={<Bdg label={cfg.l} bg={cfg.bg} fg={cfg.fg} />}
                        meta={<>
                          <span style={{ fontWeight: 600, color: C.text }}>{ev.time}</span>
                          {g.key !== "vandaag" && g.key !== "morgen" && <span>{dagNaam(ev.date)}</span>}
                          <Bdg label={ev.type} bg={tc.bg} fg={tc.fg} />
                          {ev.attendees.map(a => { const u = USERS[a]; return <span key={a} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 9, background: u?.color + "20", color: u?.color, fontWeight: 700 }}>{u?.name}</span>; })}
                        </>} />
                    );
                  })}
                </Groep>
              ))}
            </div>
          );
        })())}

        {newMod && (
          <Portal><div className="ovl" onClick={() => setNewMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 17, fontSize: 14 }}>+ NIEUWE AFSPRAAK</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Titel *</p><input value={nev.title} onChange={e => setNev(p => ({ ...p, title: e.target.value }))} placeholder="bijv. Demo AutoCompli" /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Datum</p><input type="date" value={nev.date} onChange={e => setNev(p => ({ ...p, date: e.target.value }))} /></div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Tijd</p><input type="time" value={nev.time} onChange={e => setNev(p => ({ ...p, time: e.target.value }))} /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Duur (min)</p><input type="number" value={nev.duration} onChange={e => setNev(p => ({ ...p, duration: parseInt(e.target.value) || 60 }))} /></div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Type</p>
                    <select value={nev.type} onChange={e => setNev(p => ({ ...p, type: e.target.value }))}>
                      <option value="meeting">Meeting</option><option value="demo">Demo</option><option value="build">Build</option><option value="call">Call</option><option value="deadline">Deadline</option>
                    </select>
                  </div>
                </div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Aangemaakt door</p>
                  <select value={nev.owner} onChange={e => setNev(p => ({ ...p, owner: e.target.value }))}>
                    <option value="furgel">Furgel</option>
                  </select>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Deelnemers</p>
                  <div style={{ display: "flex", gap: 10 }}>
                    {["furgel"].map(u => { const on = nev.attendees.includes(u); const usr = USERS[u]; return <div key={u} onClick={() => togAtt(u)} style={{ padding: "7px 14px", borderRadius: 20, cursor: "pointer", border: "1px solid " + (on ? usr.color : C.border), background: on ? usr.color + "20" : "transparent", color: on ? usr.color : C.muted, fontSize: 13, fontWeight: 600, transition: "all .15s" }}>{usr.name}</div>; })}
                  </div>
                </div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Notities</p><textarea rows={2} value={nev.notes} onChange={e => setNev(p => ({ ...p, notes: e.target.value }))} placeholder="Optionele notities..." /></div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Locatie (adres)</p>
                  <input value={nev.location} onChange={e => setNev(p => ({ ...p, location: e.target.value }))} placeholder="bijv. Keizersgracht 123, Amsterdam — verschijnt live op de kaart" />
                  {nev.location.trim().length > 4 && <div style={{ marginTop: 8 }}><AddressMap query={nev.location.trim() + ", Nederland"} height={170} /></div>}
                </div>
                <LeadKiezer waarde={nev.lead_id} onChange={v => setNev(p => ({ ...p, lead_id: v }))} />
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Meeting link (Zoom / Meet / Teams)</p><input value={nev.meeting_url} onChange={e => setNev(p => ({ ...p, meeting_url: e.target.value }))} placeholder="https://meet.google.com/..." /></div>
                <ReeksKiezer waarde={nev.reeks} onChange={r => setNev(p => ({ ...p, reeks: r }))} startIso={nev.date} max={52} />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 5 }}>
                  {!nev.title.trim() && <span style={{ color: C.red, fontSize: 12, marginRight: "auto", alignSelf: "center" }}>⚠ Vul bovenaan een Titel in</span>}
                  <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                  <button className="btn bp" opacity={!nev.title.trim() ? .5 : 1} onClick={submit}>+ Toevoegen</button>
                </div>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  // ─── TAKEN ────────────────────────────────────────────────────────────────────
  const INIT_TASKS = [];
  const TSC = { todo: { bg: C.borderHi, fg: C.muted, l: "To Do" }, in_progress: { bg: C.yellowDim, fg: C.yellow, l: "Bezig" }, done: { bg: C.greenDim, fg: C.green, l: "Klaar" }, blocked: { bg: C.redDim, fg: C.red, l: "Geblokkeerd" } };

  function Taken({ user, setView, openKlant }) {
    const meKey = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
    const [tasks, setTasks] = useState(INIT_TASKS);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("open");
    const [toonAlleKlaar, setToonAlleKlaar] = useState(false);
    const [newMod, setNewMod] = useState(false);
    const [nt, setNt] = useState({ title: "", owner: meKey, assignee: meKey, priority: "MED", status: "todo", due: dateStr(addDays(TODAY, 7)), dienst: "", notes: "" });

    // Laad taken uit Supabase
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("tasks").select("*").order("due", { ascending: true });
        if (!error && data && data.length > 0) setTasks(data);
        setLoading(false);
      }
      load();
      // Realtime subscribe: als Tristan iets wijzigt, update jij ook live
      const ch = supabase.channel("tasks_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "tasks" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    async function submit() {
      if (!nt.title.trim()) return;
      const newTask = { ...nt };
      delete newTask.id;
      // Alleen meesturen als ingevuld, zodat opslaan ook werkt voordat de database de nieuwe velden heeft
      if (!String(newTask.actie || "").trim()) delete newTask.actie;
      if (!String(newTask.waarom || "").trim()) delete newTask.waarom;
      const { data, error } = await supabase.from("tasks").insert([newTask]).select();
      if (!error && data) { setTasks(p => [...p, data[0]]); logActivity("taak", newTask.title, "taken"); }
      setNewMod(false); setNt({ title: "", owner: meKey, assignee: meKey, priority: "MED", status: "todo", due: dateStr(addDays(TODAY, 7)), dienst: "", notes: "" });
      // E-mail notificatie bij nieuwe taak
      const assigneeName = USERS[newTask.assignee]?.name || newTask.assignee;
      const subj = encodeURIComponent("Nieuwe taak: " + newTask.title + " → " + assigneeName);
      const body = encodeURIComponent(
        "Hallo,\n\nEr is een nieuwe taak aangemaakt in Dif3R3Nt OS:\n\n" +
        "" + newTask.title + "\nToegewezen aan: " + assigneeName + "\nPrioriteit: " + newTask.priority + "\nDeadline: " + newTask.due +
        (newTask.dienst ? "\nDienst: " + newTask.dienst : "") +
        (newTask.notes ? "\n\nNotities: " + newTask.notes : "") +
        "\n\nBekijk in Dif3R3Nt OS:\n" + window.location.origin +
        "\n\nGroeten,\nDif3R3Nt OS"
      );
      window.open("mailto:furgel@dif3r3nt.nl?subject=" + subj + "&body=" + body);
    }
    async function setStatus(id, s) {
      const cur = tasks.find(t => t.id === id);
      const patch = { status: s };
      if (s === "done") {
        // Afgehandelde taken loskoppelen van persoonlijke takenbak
        patch.assignee = null;
        patch.owner = null;
      } else if (cur && !cur.assignee) {
        // Heropenen → terug op naam van huidige gebruiker
        patch.assignee = meKey;
        patch.owner = meKey;
      }
      await supabase.from("tasks").update(patch).eq("id", id);
      setTasks(p => p.map(t => t.id === id ? { ...t, ...patch } : t));
    }
    async function del(id) {
      await supabase.from("tasks").delete().eq("id", id);
      setTasks(p => p.filter(t => t.id !== id));
    }

    const todayStr = dateStr(TODAY);
    const [opStatus, verversOp] = useOperatieStatus(tasks);
    const [aanvullen, setAanvullen] = useState({ bezig: false, tekst: "" });
    async function herlaadTaken() {
      const { data } = await supabase.from("tasks").select("*").order("due", { ascending: true });
      if (data) setTasks(data);
    }
    async function vulAllesAan() {
      setAanvullen({ bezig: true, tekst: "" });
      try {
        const r = await verduidelijkTaken(tasks, 12);
        await herlaadTaken();
        setAanvullen({ bezig: false, tekst: r.aangevuld + " taken aangevuld" + (r.rest ? ", nog " + r.rest + " te gaan (klik nog een keer)" : "") + ". Controleer even of het klopt." });
      } catch (e) { setAanvullen({ bezig: false, tekst: "Mislukt: " + (e.message || "onbekend") }); }
    }
    const teVerduidelijken = tasks.filter(mistContext).length;
    const filtered = tasks.filter(t => {
      if (filter === "furgel") return t.assignee === "furgel" && t.status !== "done";
      if (filter === "tristan") return t.assignee === "tristan" && t.status !== "done";
      if (filter === "blocked") return t.status === "blocked";
      if (filter === "done") return t.status === "done";
      if (filter === "open") return t.status !== "done";
      return true;
    }).sort((a, b) => ({ HIGH: 0, MED: 1, LOW: 2 }[taakPrio(a.priority)]) - ({ HIGH: 0, MED: 1, LOW: 2 }[taakPrio(b.priority)]) || String(a.due || "").localeCompare(String(b.due || "")));

    const cnt = { todo: tasks.filter(t => t.status === "todo").length, in_progress: tasks.filter(t => t.status === "in_progress").length, blocked: tasks.filter(t => t.status === "blocked").length, done: tasks.filter(t => t.status === "done").length };

    return (
      <div className="fu">
        <Kop titel="Taken" rechts={<button className="btn bp" onClick={() => setNewMod(true)}>+ Nieuwe taak</button>} />
        <Tellers items={[{ label: "To Do", waarde: cnt.todo, kleur: C.muted }, { label: "Bezig", waarde: cnt.in_progress, kleur: C.yellow }, { label: "Geblokkeerd", waarde: cnt.blocked, kleur: C.red }, { label: "Klaar", waarde: cnt.done, kleur: C.green }]} />
        <div style={{ display: "flex", gap: 7, marginBottom: 15, flexWrap: "wrap" }}>
          {[["open", "Open"], ["furgel", "Furgel"], ["blocked", "Geblokkeerd"], ["done", "Klaar"]].map(([v, l]) => (
            <span key={v} className={"tab" + (filter === v ? " on" : "")} onClick={() => setFilter(v)}>{l}</span>
          ))}
        </div>
        {(teVerduidelijken > 0 || aanvullen.tekst) && (
          <div className="card" style={{ marginBottom: 10, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: C.muted, flex: 1, minWidth: 200 }}>{aanvullen.tekst || (teVerduidelijken + " open taken hebben nog geen duidelijke actie en reden.")}</span>
            {teVerduidelijken > 0 && <button className="btn bg" disabled={aanvullen.bezig} onClick={vulAllesAan}>{aanvullen.bezig ? "Claude schrijft..." : "Vul ze aan met AI"}</button>}
          </div>
        )}
        {(() => {
          // Eén compacte regel per taak, gegroepeerd op urgentie (zelfde opbouw als de andere modules).
          const weekStr = dateStr(addDays(TODAY, 7));
          const prioVolgorde = t => ({ HIGH: 0, MED: 1, LOW: 2 }[taakPrio(t.priority)]);
          const opDatum = (x, y) => String(x.due || "9999").localeCompare(String(y.due || "9999")) || prioVolgorde(x) - prioVolgorde(y);
          const nieuwste = (x, y) => String(y.due || "").localeCompare(String(x.due || ""));
          const kort = d => { if (!d) return "geen deadline"; const x = new Date(d + "T00:00:00"); return isNaN(x) ? String(d) : x.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" }); };
          const groepen = filter === "done"
            ? [{ key: "klaar", label: "Klaar", kleur: C.green, items: [...filtered].sort(nieuwste), beperk: 10 }]
            : [
                { key: "telaat", label: "Te laat", kleur: C.red, items: filtered.filter(t => t.status !== "done" && t.due && t.due < todayStr).sort(opDatum) },
                { key: "week", label: "Deze week", kleur: C.yellow, items: filtered.filter(t => t.status !== "done" && (!t.due || t.due >= todayStr) && t.due <= weekStr).sort(opDatum) },
                { key: "later", label: "Later", kleur: C.muted, items: filtered.filter(t => t.status !== "done" && (!t.due || t.due > weekStr)).sort(opDatum), standaardDicht: true },
                { key: "klaar", label: "Klaar", kleur: C.green, items: filtered.filter(t => t.status === "done").sort(nieuwste), standaardDicht: true, beperk: 10 },
              ].filter(g => g.items.length);
          if (groepen.length === 0) return <Leeg tekst="Geen taken gevonden." />;
          const rij = (t) => {
            const sc = TSC[t.status] || TSC.todo;
            const pc = PCOL[t.priority] || C.muted;
            const ow = USERS[t.assignee];
            const overdue = t.status !== "done" && t.due && t.due < todayStr;
            const klaar = t.status === "done";
            return (
              <Rij key={t.id} kleur={pc} gedimd={klaar} titel={t.title} voorbeeld={t.actie}
                voor={<input type="checkbox" checked={klaar} onClick={e => e.stopPropagation()} onChange={e => setStatus(t.id, e.target.checked ? "done" : "todo")} aria-label="Taak afvinken" style={{ width: 18, height: 18, marginTop: 1, cursor: "pointer", accentColor: C.green, flexShrink: 0 }} />}
                meta={<>
                  <span style={{ color: overdue ? C.red : C.muted, fontWeight: overdue ? 600 : 400 }}>{kort(t.due)}</span>
                  {ow && <span style={{ color: ow.color, fontWeight: 600 }}>{ow.name}</span>}
                  {t.dienst && <span>{t.dienst}</span>}
                  {!klaar && t.status !== "todo" && <Bdg label={sc.l} bg={sc.bg} fg={sc.fg} />}
                </>}>
                <TaakInfo taak={t} naAanvullen={herlaadTaken} openKlant={openKlant} />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
                  <OperatieKnop taak={t} status={opStatus[String(t.id)]} user={user} setView={setView} compact naVerzenden={verversOp} />
                  <select value={t.status} onChange={e => setStatus(t.id, e.target.value)} style={{ width: "auto", fontSize: 12, padding: "4px 8px", background: sc.bg, color: sc.fg, border: "1px solid " + sc.fg + "40" }}>
                    <option value="todo">To Do</option>
                    <option value="in_progress">Bezig</option>
                    <option value="blocked">Geblokkeerd</option>
                    <option value="done">Klaar</option>
                  </select>
                  <button className="btn bd" style={{ fontSize: 12, padding: "4px 10px", marginLeft: "auto" }} onClick={() => { if (window.confirm("Deze taak verwijderen?")) del(t.id); }}>Verwijderen</button>
                </div>
              </Rij>
            );
          };
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {groepen.map(g => {
                const zichtbaar = g.beperk && !toonAlleKlaar ? g.items.slice(0, g.beperk) : g.items;
                return (
                  <Groep key={filter + g.key} label={g.label} kleur={g.kleur} aantal={g.items.length} standaardDicht={g.standaardDicht && filter !== "done"}
                    voet={g.beperk && g.items.length > g.beperk ? <div onClick={() => setToonAlleKlaar(p => !p)} style={{ borderTop: "1px solid " + C.border, padding: "10px 12px", fontSize: 12, color: C.blue, cursor: "pointer", textAlign: "center" }}>{toonAlleKlaar ? "Toon minder" : "Toon alle " + g.items.length}</div> : null}>
                    {zichtbaar.map(rij)}
                  </Groep>
                );
              })}
            </div>
          );
        })()}
        {newMod && (
          <Portal><div className="ovl" onClick={() => setNewMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 17, fontSize: 14 }}>+ NIEUWE TAAK</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Titel *</p><input value={nt.title} onChange={e => setNt(p => ({ ...p, title: e.target.value }))} placeholder="Wat moet er gedaan worden?" /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Voor wie</p>
                    <select value={nt.assignee} onChange={e => setNt(p => ({ ...p, assignee: e.target.value, owner: e.target.value }))}>
                      <option value="furgel">Furgel</option>
                    </select>
                  </div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Prioriteit</p>
                    <select value={nt.priority} onChange={e => setNt(p => ({ ...p, priority: e.target.value }))}>
                      <option value="HIGH">Hoog</option><option value="MED">Middel</option><option value="LOW">Laag</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Deadline</p><input type="date" value={nt.due} onChange={e => setNt(p => ({ ...p, due: e.target.value }))} /></div>
                  <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Dienst</p><input value={nt.dienst} onChange={e => setNt(p => ({ ...p, dienst: e.target.value }))} placeholder="optioneel" /></div>
                </div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Actie: wat moet er precies gebeuren?</p><textarea rows={2} value={nt.actie || ""} onChange={e => setNt(p => ({ ...p, actie: e.target.value }))} placeholder="Bijvoorbeeld: Bel Jan en vraag of de offerte akkoord is" /></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Waarom? (wat gebeurt er als het blijft liggen)</p><textarea rows={2} value={nt.waarom || ""} onChange={e => setNt(p => ({ ...p, waarom: e.target.value }))} placeholder="Bijvoorbeeld: Zonder akkoord kan het werk niet starten" /></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Notities</p><textarea rows={2} value={nt.notes} onChange={e => setNt(p => ({ ...p, notes: e.target.value }))} placeholder="Context..." /></div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 5 }}>
                  <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                  <button className="btn bp" onClick={submit}>+ Toevoegen</button>
                </div>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }


  // ─── MEETING ─────────────────────────────────────────────────────────────────
  const INIT_MEETINGS = [];

  // ── Meeting Mode AI prompts (defined before component) ──
  const MEETING_ANALYSIS_SYS = `Je bent Business Partner Agent voor Dif3r3nt VOF (Furgel=CTO, beoordeelt samen met Claude AI). Analyseer dit live meeting transcript en extraheer gestructureerde informatie. Reageer UITSLUITEND als JSON: {"summary":"beknopte samenvatting","actions":[{"task":"concrete actie","owner":"furgel of tristan","deadline":"YYYY-MM-DD of leeg"}],"decisions":["besluit"],"risks":["risico"],"followups":["follow-up"],"open_questions":["vraag"]}. owner moet altijd "furgel" of "tristan" zijn (lowercase).`;
  const MEETING_FINAL_SYS = `Je bent Business Partner Agent voor Dif3r3nt VOF (Furgel=CTO, beoordeelt samen met Claude AI). Maak een VOLLEDIGE eindanalyse van deze meeting. Wees grondig en compleet. Reageer UITSLUITEND als JSON: {"summary":"volledige samenvatting","actions":[{"task":"concrete actie","owner":"furgel of tristan","deadline":"YYYY-MM-DD of leeg"}],"decisions":["besluit"],"risks":["risico"],"followups":["follow-up"],"open_questions":["openstaande vraag"]}. owner moet altijd "furgel" of "tristan" zijn (lowercase).`;

  function Meeting({ setView, tasks, setTasks, user, sharedSessionId, clearSharedSession, onSoloActiveChange }) {
    // Wanneer er een gedeelde sessie actief is, gebruik SharedMeeting i.p.v. solo mode
    if (sharedSessionId) {
      return <SharedMeeting sessionId={sharedSessionId} user={user} setTasks={setTasks} setView={setView} onExit={() => { if (clearSharedSession) clearSharedSession(); }} />;
    }
    return <SoloMeeting setView={setView} tasks={tasks} setTasks={setTasks} onActiveChange={onSoloActiveChange} />;
  }

  function SoloMeeting({ setView, tasks, setTasks, onActiveChange }) {
    const [meetings, setMeetings] = useState(INIT_MEETINGS);
    const [sel, setSel] = useState(null);
    const [newMod, setNewMod] = useState(false);
    const [nm, setNm] = useState({ title: "", agenda: "" });

    // ── Meeting Mode state ──
    const [modeActive, setModeActive] = useState(false);
    const [activeMeeting, setActiveMeeting] = useState(null);
    const [transcript, setTranscript] = useState("");
    const [interimText, setInterimText] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [nextAnalysis, setNextAnalysis] = useState(30);
    const [analysis, setAnalysis] = useState({ summary: "", actions: [], decisions: [], risks: [], followups: [], open_questions: [] });
    const [analyzing, setAnalyzing] = useState(false);
    const [finalMode, setFinalMode] = useState(false);
    const [finalData, setFinalData] = useState(null);
    const [saving, setSaving] = useState(false);

    // Refs to avoid stale closures in setInterval
    const recogRef = useRef(null);
    const transcriptRef = useRef("");
    const elapsedRef = useRef(0);
    const modeActiveRef = useRef(false);
    const analyzingRef = useRef(false);
    const activeMeetingRef = useRef(null);

    // ── Load meetings from Supabase ──
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("meetings").select("*").order("created_at", { ascending: false });
        if (!error && data && data.length > 0) setMeetings(data);
      }
      load();
      const ch = supabase.channel("meetings_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "meetings" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    // ── Elapsed + 30s periodic analysis ticker ──
    useEffect(() => {
      if (!modeActive) { setElapsed(0); elapsedRef.current = 0; return; }
      let countdown = 30;
      const t = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(p => p + 1);
        countdown -= 1;
        setNextAnalysis(countdown);
        if (countdown <= 0) {
          countdown = 30;
          setNextAnalysis(30);
          if (!analyzingRef.current && transcriptRef.current.trim()) runPeriodicAnalysis();
        }
      }, 1000);
      return () => clearInterval(t);
    }, [modeActive]);

    // Meld aan App of de solo-meeting actief is — zo blijft de module gemount (mic blijft
    // luisteren) wanneer je tijdens een meeting naar een andere module navigeert.
    useEffect(() => { if (onActiveChange) onActiveChange(modeActive); }, [modeActive]);
    useEffect(() => () => { if (onActiveChange) onActiveChange(false); }, []);

    const fmt = s => String(Math.floor(s / 60)).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");

    function startListening() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) { alert("Speech recognition niet beschikbaar. Gebruik Chrome of Edge, of typ het transcript handmatig."); return; }
      const recog = new SR();
      recog.lang = "nl-NL"; recog.continuous = true; recog.interimResults = true;
      recog.onresult = (e) => {
        let final = "", interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
          else interim += e.results[i][0].transcript;
        }
        if (final) { transcriptRef.current += final; setTranscript(transcriptRef.current); }
        setInterimText(interim);
      };
      recog.onend = () => { if (modeActiveRef.current) { try { recog.start(); } catch { } } else setIsListening(false); };
      recog.onerror = (e) => { if (e.error !== "no-speech") console.warn("Speech:", e.error); };
      recog.start(); recogRef.current = recog; setIsListening(true);
    }

    function stopListening() {
      modeActiveRef.current = false;
      if (recogRef.current) { recogRef.current.onend = null; try { recogRef.current.stop(); } catch { } recogRef.current = null; }
      setIsListening(false); setInterimText("");
    }

    function applyParsed(parsed) {
      setAnalysis({
        summary: parsed.summary || "",
        actions: Array.isArray(parsed.actions) ? parsed.actions : [],
        decisions: Array.isArray(parsed.decisions) ? parsed.decisions : [],
        risks: Array.isArray(parsed.risks) ? parsed.risks : [],
        followups: Array.isArray(parsed.followups) ? parsed.followups : [],
        open_questions: Array.isArray(parsed.open_questions) ? parsed.open_questions : [],
      });
    }

    async function runPeriodicAnalysis() {
      if (analyzingRef.current) return;
      analyzingRef.current = true; setAnalyzing(true);
      try {
        const { parsed } = await callGroq(MEETING_ANALYSIS_SYS, "Meeting: " + (activeMeetingRef.current?.title || "") + "\nTranscript:\n" + transcriptRef.current, { maxTokens: 1500, temperature: 0.3 });
        if (parsed) applyParsed(parsed);
      } catch (e) { console.warn("Analysis:", e); }
      analyzingRef.current = false; setAnalyzing(false);
    }

    async function runManualAnalysis() {
      if (!transcriptRef.current.trim() || analyzingRef.current) return;
      analyzingRef.current = true; setAnalyzing(true);
      try {
        const { parsed } = await callGroq(MEETING_ANALYSIS_SYS, "Meeting: " + (activeMeetingRef.current?.title || "") + "\nTranscript:\n" + transcriptRef.current, { maxTokens: 1500, temperature: 0.3 });
        if (parsed) applyParsed(parsed);
      } catch (e) { console.warn("Analysis:", e); }
      analyzingRef.current = false; setAnalyzing(false);
    }

    function startMeetingMode() {
      if (!nm.title.trim()) return;
      const meeting = { ...nm, id: Date.now(), date: dateStr(TODAY) };
      activeMeetingRef.current = meeting; setActiveMeeting(meeting);
      transcriptRef.current = ""; analyzingRef.current = false; modeActiveRef.current = true;
      setTranscript(""); setInterimText("");
      setAnalysis({ summary: "", actions: [], decisions: [], risks: [], followups: [], open_questions: [] });
      setElapsed(0); elapsedRef.current = 0; setNextAnalysis(30);
      setFinalMode(false); setFinalData(null); setSaving(false);
      setModeActive(true); setNewMod(false);
      setTimeout(startListening, 400);
    }

    async function stopMeetingMode() {
      modeActiveRef.current = false; setModeActive(false); stopListening();
      const text = transcriptRef.current.trim();
      if (!text) { setActiveMeeting(null); activeMeetingRef.current = null; return; }
      setSaving(true);
      try {
        const { parsed } = await callGroq(MEETING_FINAL_SYS, "Meeting: " + (activeMeetingRef.current?.title || "") + "\nVolledig transcript:\n" + text, { maxTokens: 2000, temperature: 0.3 });
        setFinalData({
          summary: parsed?.summary || text.slice(0, 400),
          actions: Array.isArray(parsed?.actions) ? parsed.actions : [],
          decisions: Array.isArray(parsed?.decisions) ? parsed.decisions : [],
          risks: Array.isArray(parsed?.risks) ? parsed.risks : [],
          followups: Array.isArray(parsed?.followups) ? parsed.followups : [],
          open_questions: Array.isArray(parsed?.open_questions) ? parsed.open_questions : [],
          transcript: text, duration: Math.round(elapsedRef.current / 60) || 1,
        });
      } catch {
        setFinalData({ summary: text.slice(0, 400), actions: [], decisions: [], risks: [], followups: [], open_questions: [], transcript: text, duration: Math.round(elapsedRef.current / 60) || 1 });
      }
      setSaving(false); setFinalMode(true);
    }

    async function saveMeetingAndTasks() {
      if (!finalData) return;
      setSaving(true);
      try {
        const structuredExtras = JSON.stringify({ risks: finalData.risks, open_questions: finalData.open_questions });
        const meetingRow = {
          title: activeMeeting.title, date: dateStr(TODAY), duration: finalData.duration,
          attendees: ["furgel"], status: "done", notes: finalData.summary,
          besluiten: finalData.decisions,
          actions: finalData.actions.map(a => ({ title: a.task, assignee: a.owner || "furgel", due: a.deadline || dateStr(addDays(TODAY, 7)) })),
          follow_up: finalData.followups,
          raw_notes: finalData.transcript + "\n\n__structured__\n" + structuredExtras,
        };
        const { data: mData, error: mErr } = await supabase.from("meetings").insert([meetingRow]).select();
        if (!mErr && mData) setMeetings(p => [mData[0], ...p]);
        if (finalData.actions.length > 0) {
          const taskRows = finalData.actions.map(a => ({
            title: a.task, owner: a.owner || "furgel", assignee: a.owner || "furgel",
            priority: "MED", status: "todo", due: a.deadline || dateStr(addDays(TODAY, 7)),
            dienst: "", notes: "Uit meeting: " + activeMeeting.title,
          }));
          const { data: tData, error: tErr } = await supabase.from("tasks").insert(taskRows).select();
          if (!tErr && tData && setTasks) setTasks(p => [...p, ...tData]);
        }
        const savedId = mData?.[0]?.id || null;
        setFinalMode(false); setFinalData(null); setActiveMeeting(null); activeMeetingRef.current = null;
        setSaving(false); setSel(savedId);
      } catch (e) { console.error("Save error:", e); setSaving(false); }
    }

    if (saving) return (
      <div className="fu" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 300, gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", border: "3px solid " + C.green, borderTopColor: "transparent", animation: "spin 1s linear infinite" }} />
        <p style={{ fontFamily: "monospace", color: C.green }}>{finalMode ? "Opslaan in Supabase..." : "Agent analyseert transcript..."}</p>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    );

    if (finalMode && finalData) return (
      <div className="fu">
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20, flexWrap: "wrap" }}>
          <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>Meeting rapport — {activeMeeting?.title}</h2>
          <Bdg label="AI Analyse Klaar" bg={C.greenDim} fg={C.green} />
          <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>{finalData.duration}min · {finalData.transcript.split(" ").length} woorden</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 13 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Samenvatting</p><p style={{ fontSize: 13, lineHeight: 1.7, color: C.muted }}>{finalData.summary || "—"}</p></div>
            {finalData.decisions?.length > 0 && (
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Besluiten ({finalData.decisions.length})</p>
                {finalData.decisions.map((b, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>✓</span><span style={{ fontSize: 13 }}>{b}</span></div>)}
              </div>
            )}
            {finalData.risks?.length > 0 && (
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Risico's ({finalData.risks.length})</p>
                {finalData.risks.map((r, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.red, flexShrink: 0 }}>⚠</span><span style={{ fontSize: 13, color: C.muted }}>{r}</span></div>)}
              </div>
            )}
            {finalData.open_questions?.length > 0 && (
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Openstaande Vragen ({finalData.open_questions.length})</p>
                {finalData.open_questions.map((q, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.yellow, flexShrink: 0 }}>?</span><span style={{ fontSize: 13, color: C.muted }}>{q}</span></div>)}
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="card">
              <p className="lbl" style={{ marginBottom: 8 }}>Actiepunten → Taken ({finalData.actions?.length || 0})</p>
              {finalData.actions?.length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Geen actiepunten gedetecteerd.</p>}
              {finalData.actions?.map((a, i) => {
                const u = USERS[a.owner] || USERS.furgel; return (
                  <div key={i} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{a.task}</p>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 9, background: u.color + "20", color: u.color, fontWeight: 700 }}>{u.name}</span>
                      {a.deadline && <span style={{ fontSize: 11, color: C.muted }}>Due: {a.deadline}</span>}
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop: 10, padding: 9, background: C.greenDim, borderRadius: 6, border: "1px solid " + C.green + "25" }}>
                <p style={{ fontSize: 11, color: C.green }}>Worden automatisch aangemaakt als taken in Supabase.</p>
              </div>
            </div>
            {finalData.followups?.length > 0 && (
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Follow-ups ({finalData.followups.length})</p>
                {finalData.followups.map((f, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.blue, flexShrink: 0 }}>→</span><span style={{ fontSize: 13, color: C.muted }}>{f}</span></div>)}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn bg" onClick={() => { setFinalMode(false); setFinalData(null); setActiveMeeting(null); activeMeetingRef.current = null; }}>Verwijderen</button>
          <button className="btn bp" style={{ padding: "9px 22px" }} onClick={saveMeetingAndTasks}>Opslaan & Taken aanmaken</button>
        </div>
      </div>
    );

    if (modeActive && activeMeeting) return (
      <div className="fu">
        <div style={{ background: C.redDim, border: "1px solid " + C.red + "40", borderRadius: 8, padding: "13px 18px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: C.red, animation: "pulse 1s infinite", flexShrink: 0 }} />
            <div><p style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: C.red }}>MEETING MODE ACTIEF</p><p style={{ fontSize: 12, color: C.muted }}>{activeMeeting.title}</p></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: C.red }}>{fmt(elapsed)}</span>
              <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginTop: 1 }}>Analyse over: {nextAnalysis}s</p>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Bdg label={isListening ? "MIC AAN" : "MIC UIT"} bg={isListening ? C.greenDim : C.border} fg={isListening ? C.green : C.muted} />
              {analyzing && <Bdg label="AI ANALYSEERT" bg={C.purpleDim} fg={C.purple} />}
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 13 }}>
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <p className="lbl">Live Transcript</p>
              <div style={{ display: "flex", gap: 6 }}>
                {isListening
                  ? <button className="btn bd" style={{ fontSize: 10, padding: "3px 8px" }} onClick={stopListening}>Mic Stoppen</button>
                  : <button className="btn bp" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => { modeActiveRef.current = true; startListening(); }}>Mic Starten</button>
                }
                <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={runManualAnalysis} disabled={analyzing}>Nu Analyseren</button>
              </div>
            </div>
            <div style={{ maxHeight: 260, overflowY: "auto", background: C.bg, borderRadius: 6, padding: "9px 11px", border: "1px solid " + C.border, fontFamily: "monospace", fontSize: 12, lineHeight: 1.9, color: C.text, marginBottom: 8, wordBreak: "break-word" }}>
              {transcript ? <>{transcript}<span style={{ color: C.muted, fontStyle: "italic" }}>{interimText}</span></> : <span style={{ color: C.dim }}>Transcript verschijnt automatisch. Zet de microfoon aan.</span>}
            </div>
            <textarea placeholder="Of typ/plak transcript handmatig..." value={transcript} onChange={e => { setTranscript(e.target.value); transcriptRef.current = e.target.value; }} rows={3} style={{ fontSize: 12, fontFamily: "monospace", resize: "vertical", borderColor: C.green + "30" }} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, overflowY: "auto", maxHeight: 420 }}>
            <div className="card" style={{ padding: 11 }}>
              <p className="lbl" style={{ marginBottom: 6 }}>Samenvatting (elke 30s bijgewerkt)</p>
              <p style={{ fontSize: 12, color: analysis.summary ? C.text : C.dim, lineHeight: 1.7 }}>{analysis.summary || "Wacht op eerste analyse..."}</p>
            </div>
            {analysis.actions?.length > 0 && (
              <div className="card" style={{ padding: 11 }}>
                <p className="lbl" style={{ marginBottom: 6 }}>Taken ({analysis.actions.length})</p>
                {analysis.actions.map((a, i) => {
                  const u = USERS[a.owner] || USERS.furgel; return (
                    <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid " + C.border, display: "flex", gap: 7, alignItems: "flex-start" }}>
                      <span style={{ color: C.yellow, flexShrink: 0 }}>→</span><span style={{ flex: 1, lineHeight: 1.5 }}>{a.task}</span>
                      <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 6, background: u.color + "20", color: u.color, fontWeight: 700, flexShrink: 0 }}>{u.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {analysis.decisions?.length > 0 && (
              <div className="card" style={{ padding: 11 }}>
                <p className="lbl" style={{ marginBottom: 6 }}>Besluiten ({analysis.decisions.length})</p>
                {analysis.decisions.map((d, i) => <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid " + C.border, display: "flex", gap: 7 }}><span style={{ color: C.green, flexShrink: 0 }}>✓</span><span style={{ lineHeight: 1.5 }}>{d}</span></div>)}
              </div>
            )}
            {analysis.risks?.length > 0 && (
              <div className="card" style={{ padding: 11 }}>
                <p className="lbl" style={{ marginBottom: 6 }}>Risico's ({analysis.risks.length})</p>
                {analysis.risks.map((r, i) => <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid " + C.border, display: "flex", gap: 7 }}><span style={{ color: C.red, flexShrink: 0 }}>⚠</span><span style={{ color: C.muted, lineHeight: 1.5 }}>{r}</span></div>)}
              </div>
            )}
            {analysis.open_questions?.length > 0 && (
              <div className="card" style={{ padding: 11 }}>
                <p className="lbl" style={{ marginBottom: 6 }}>Open Vragen ({analysis.open_questions.length})</p>
                {analysis.open_questions.map((q, i) => <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid " + C.border, display: "flex", gap: 7 }}><span style={{ color: C.yellow, flexShrink: 0 }}>?</span><span style={{ color: C.muted, lineHeight: 1.5 }}>{q}</span></div>)}
              </div>
            )}
            {analysis.followups?.length > 0 && (
              <div className="card" style={{ padding: 11 }}>
                <p className="lbl" style={{ marginBottom: 6 }}>Follow-ups ({analysis.followups.length})</p>
                {analysis.followups.map((f, i) => <div key={i} style={{ fontSize: 12, padding: "4px 0", borderBottom: "1px solid " + C.border, display: "flex", gap: 7 }}><span style={{ color: C.blue, flexShrink: 0 }}>→</span><span style={{ color: C.muted, lineHeight: 1.5 }}>{f}</span></div>)}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn bg" onClick={() => { modeActiveRef.current = false; setModeActive(false); stopListening(); setActiveMeeting(null); activeMeetingRef.current = null; }}>Annuleren</button>
          <button className="btn bd" style={{ padding: "9px 20px" }} onClick={stopMeetingMode}>Meeting Mode Stoppen & Samenvatten</button>
        </div>
      </div>
    );

    if (sel) {
      const m = meetings.find(x => x.id === sel);
      if (!m) { setSel(null); return null; }
      let extras = null;
      try { if (m.raw_notes?.includes("__structured__")) extras = JSON.parse(m.raw_notes.split("__structured__\n")[1] || "{}"); } catch { }
      return (
        <div className="fu">
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20, flexWrap: "wrap" }}>
            <button className="btn bg" onClick={() => setSel(null)}>← Terug</button>
            <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>{m.title}</h2>
            <span style={{ fontSize: 12, color: C.muted }}>{m.date} · {m.duration}min</span>
            {extras && <Bdg label="MEETING MODE" bg={C.purpleDim} fg={C.purple} />}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Samenvatting</p><p style={{ fontSize: 13, lineHeight: 1.7, color: C.muted }}>{m.notes || "—"}</p></div>
              {m.besluiten?.length > 0 && (
                <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Besluiten ({m.besluiten.length})</p>
                  {m.besluiten.map((b, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>✓</span><span style={{ fontSize: 13 }}>{b}</span></div>)}
                </div>
              )}
              {extras?.risks?.length > 0 && (
                <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Risico's</p>
                  {extras.risks.map((r, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.red, flexShrink: 0 }}>⚠</span><span style={{ fontSize: 13, color: C.muted }}>{r}</span></div>)}
                </div>
              )}
              {extras?.open_questions?.length > 0 && (
                <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Open Vragen</p>
                  {extras.open_questions.map((q, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.yellow, flexShrink: 0 }}>?</span><span style={{ fontSize: 13, color: C.muted }}>{q}</span></div>)}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Actiepunten ({(m.actions || []).length})</p>
                {(m.actions || []).map((a, i) => {
                  const u = USERS[a.assignee] || USERS.furgel; return (
                    <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{a.title}</p>
                      <div style={{ display: "flex", gap: 7 }}><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 9, background: u.color + "20", color: u.color, fontWeight: 700 }}>{u.name}</span>{a.due && <span style={{ fontSize: 11, color: C.muted }}>Due: {a.due}</span>}</div>
                    </div>
                  );
                })}
              </div>
              {m.follow_up?.length > 0 && (
                <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Follow-ups ({m.follow_up.length})</p>
                  {m.follow_up.map((f, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.blue, flexShrink: 0 }}>→</span><span style={{ fontSize: 13, color: C.muted }}>{f}</span></div>)}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fu">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ ...KOP_STIJL }}>Meetings</h2>
          <button className="btn bp" style={{ padding: "9px 18px", display: "flex", alignItems: "center", gap: 8 }} onClick={() => setNewMod(true)}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#000", animation: "pulse 1.2s infinite" }} />
            Meeting Mode
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 11, marginBottom: 18 }}>
          {[["Meetings", "" + meetings.length, "totaal", C.green], ["Actiepunten", "" + meetings.reduce((s, m) => s + (m.actions?.length || 0), 0), "gegenereerd", C.yellow], ["Besluiten", "" + meetings.reduce((s, m) => s + (m.besluiten?.length || 0), 0), "vastgelegd", C.blue]].map(([l, v, s, c]) => (
            <div className="st" key={l} style={{ "--ac": c }}><p className="lbl" style={{ marginBottom: 6 }}>{l}</p><p style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: c }}>{v}</p><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s}</p></div>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {meetings.length === 0 && <div style={{ padding: 22, textAlign: "center", color: C.muted, fontSize: 13 }}>Nog geen meetings. Klik op "Meeting Mode" om te starten.</div>}
          {meetings.map(m => (
            <div key={m.id} className="card" style={{ cursor: "pointer" }} onClick={() => setSel(m.id)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{m.title}</p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{m.date}</span><span style={{ fontSize: 12, color: C.muted }}>{m.duration}min</span>
                    <div style={{ display: "flex", gap: 4 }}>{(m.attendees || []).map(a => { const u = USERS[a]; return u ? <span key={a} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 9, background: u.color + "20", color: u.color, fontWeight: 700 }}>{u.name}</span> : null; })}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {m.raw_notes?.includes("__structured__") && <Bdg label="AI" bg={C.purpleDim} fg={C.purple} />}
                  <Bdg label={(m.actions?.length || 0) + " acties"} bg={C.yellowDim} fg={C.yellow} />
                </div>
              </div>
              <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{(m.notes || "").slice(0, 120)}{(m.notes || "").length > 120 ? "..." : ""}</p>
            </div>
          ))}
        </div>
        {newMod && (
          <Portal><div className="ovl" onClick={() => setNewMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 16, fontSize: 14 }}>MEETING MODE STARTEN</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Onderwerp *</p><input value={nm.title} onChange={e => setNm(p => ({ ...p, title: e.target.value }))} placeholder="bijv. Weekly Sync Q2" /></div>
                <div><p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Agenda (optioneel)</p><textarea rows={3} value={nm.agenda} onChange={e => setNm(p => ({ ...p, agenda: e.target.value }))} placeholder="Bespreekpunten..." /></div>
                <div style={{ background: C.surface, borderRadius: 7, padding: 11, border: "1px solid " + C.border }}>
                  <p className="lbl" style={{ marginBottom: 7 }}>Deelnemers</p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{["furgel"].map(u => { const usr = USERS[u]; return <span key={u} style={{ padding: "4px 12px", borderRadius: 12, fontSize: 12, background: usr.color + "20", color: usr.color, fontWeight: 700 }}>{usr.name} — {usr.email}</span>; })}</div>
                </div>
                <div style={{ background: C.greenDim, borderRadius: 6, padding: 11, border: "1px solid " + C.green + "30" }}>
                  <p style={{ fontSize: 12, color: C.green, lineHeight: 1.9 }}>
                    <strong>Meeting Mode luistert live mee (Chrome/Edge)</strong><br />
                    • Microfoon transcribeert automatisch (nl-NL)<br />
                    • Elke 30 seconden: AI detecteert taken, besluiten, risico's, open vragen<br />
                    • Bij stoppen: volledige eindanalyse + automatisch taken aanmaken in Supabase
                  </p>
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
                  <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                  <button className="btn bp" onClick={startMeetingMode}>Meeting Mode Starten</button>
                </div>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  // ─── VERGADER MODUS (gedeelde meeting met Jitsi video) ───────────────────────
  // Eigen Dif3r3nt-belvariant: WebRTC peer-to-peer audio + schermdelen, met
  // Supabase Realtime als signaling en de perfect-negotiation-aanpak tegen glare.
  function Dif3r3ntCall({ sessionId, meEmail, meName }) {
    const ICE = [
      { urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] },
      { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
      { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
      { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
    ];
    const [status, setStatus] = useState("verbinden");
    const [muted, setMuted] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [remoteSharing, setRemoteSharing] = useState(false);
    const [peerName, setPeerName] = useState("");
    const [meLevel, setMeLevel] = useState(0);
    const [peerLevel, setPeerLevel] = useState(0);
    const [err, setErr] = useState("");
    const [audioBlocked, setAudioBlocked] = useState(false);

    const pcRef = useRef(null), chRef = useRef(null), micRef = useRef(null);
    const screenRef = useRef(null), screenSenderRef = useRef(null);
    const remoteAudioRef = useRef(null), remoteScreenRef = useRef(null);
    const remoteStreamRef = useRef(null), resumeRef = useRef(null);
    const politeRef = useRef(false), makingOfferRef = useRef(false), ignoreOfferRef = useRef(false);
    const peerRef = useRef(""), ctxsRef = useRef([]), rafRef = useRef(null);
    const pendingIceRef = useRef([]);   // ICE-candidates die binnenkomen vóór de remote-description

    const send = (payload) => { try { chRef.current && chRef.current.send({ type: "broadcast", event: "sig", payload }); } catch (e) { /* stil */ } };
    async function negotiate() {
      const pc = pcRef.current; if (!pc) return;
      try { makingOfferRef.current = true; await pc.setLocalDescription(); send({ kind: "desc", from: meEmail, description: pc.localDescription }); }
      catch (e) { /* stil */ } finally { makingOfferRef.current = false; }
    }
    function monitor(stream, setLevel) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        ctxsRef.current.push(ctx);
        const an = ctx.createAnalyser(); an.fftSize = 256;
        ctx.createMediaStreamSource(stream).connect(an);
        const buf = new Uint8Array(an.frequencyBinCount);
        const tick = () => { an.getByteFrequencyData(buf); const avg = buf.reduce((a, b) => a + b, 0) / buf.length; setLevel(Math.min(1, avg / 90)); rafRef.current = requestAnimationFrame(tick); };
        tick();
      } catch (e) { /* stil */ }
    }
    // Speelt de remote-audio af. Browsers blokkeren autoplay vaak tot een user-gesture;
    // bij blokkade tonen we een "Geluid aanzetten"-knop en retryt elke klik/toets.
    function playRemote() {
      const el = remoteAudioRef.current, stream = remoteStreamRef.current;
      if (!el || !stream) return;
      try { if (el.srcObject !== stream) el.srcObject = stream; el.muted = false; el.volume = 1; } catch (e) { /* stil */ }
      const p = el.play && el.play();
      if (p && p.then) p.then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true));
      else setAudioBlocked(false);
    }

    useEffect(() => {
      let dead = false;
      async function start() {
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
          if (dead) { mic.getTracks().forEach(t => t.stop()); return; }
          micRef.current = mic;
          const pc = new RTCPeerConnection({ iceServers: ICE, bundlePolicy: "max-bundle" });
          pcRef.current = pc;
          mic.getAudioTracks().forEach(t => pc.addTrack(t, mic));
          pc.onicecandidate = ({ candidate }) => { if (candidate) send({ kind: "ice", from: meEmail, candidate }); };
          pc.onnegotiationneeded = async () => { if (peerRef.current) await negotiate(); };
          pc.onconnectionstatechange = () => {
            const s = pc.connectionState;
            if (s === "connected") setStatus("verbonden");
            else if (s === "failed") { setStatus("mislukt"); try { pc.restartIce(); } catch (e) { /* stil */ } }
            else if (s === "disconnected") setStatus("verbinden");
          };
          pc.ontrack = (ev) => {
            const [stream] = ev.streams;
            if (ev.track.kind === "audio") {
              remoteStreamRef.current = stream;
              playRemote();
              monitor(stream, setPeerLevel);
            } else if (ev.track.kind === "video") {
              if (remoteScreenRef.current) { remoteScreenRef.current.srcObject = stream; const p = remoteScreenRef.current.play && remoteScreenRef.current.play(); if (p && p.catch) p.catch(() => {}); }
              setRemoteSharing(true);
              ev.track.onended = () => setRemoteSharing(false);
              ev.track.onmute = () => setRemoteSharing(false);
              ev.track.onunmute = () => setRemoteSharing(true);
            }
          };
          const ch = supabase.channel("rtc_" + sessionId, { config: { broadcast: { self: false } } });
          chRef.current = ch;
          ch.on("broadcast", { event: "sig" }, async ({ payload }) => {
            if (!payload || payload.from === meEmail) return;
            try {
              if (payload.kind === "hello") {
                if (!peerRef.current) {
                  peerRef.current = payload.from; setPeerName(String(payload.name || payload.from).split("@")[0]);
                  politeRef.current = meEmail < payload.from;
                  send({ kind: "hello", from: meEmail, name: meName });
                  if (!politeRef.current) await negotiate();
                }
              } else if (payload.kind === "desc") {
                const desc = payload.description;
                const collision = desc.type === "offer" && (makingOfferRef.current || pc.signalingState !== "stable");
                ignoreOfferRef.current = !politeRef.current && collision;
                if (ignoreOfferRef.current) return;
                await pc.setRemoteDescription(desc);
                // Nu de remote-description er is: gebufferde ICE-candidates alsnog toevoegen.
                const queued = pendingIceRef.current.splice(0);
                for (const c of queued) { try { await pc.addIceCandidate(c); } catch (e) { /* stil */ } }
                if (desc.type === "offer") { await pc.setLocalDescription(); send({ kind: "desc", from: meEmail, description: pc.localDescription }); }
              } else if (payload.kind === "ice") {
                // Vóór de remote-description faalt addIceCandidate; buffer dan i.p.v. weggooien,
                // anders kan juist de beslissende TURN-relay-candidate verloren gaan (geen audio).
                if (pc.remoteDescription && pc.remoteDescription.type) {
                  try { await pc.addIceCandidate(payload.candidate); } catch (e) { /* stil bij ignore */ }
                } else {
                  pendingIceRef.current.push(payload.candidate);
                }
              } else if (payload.kind === "bye") {
                setRemoteSharing(false); setStatus("wachten"); peerRef.current = "";
              }
            } catch (e) { /* stil */ }
          });
          ch.subscribe((s) => { if (s === "SUBSCRIBED") { setStatus("wachten"); send({ kind: "hello", from: meEmail, name: meName }); } });
          monitor(mic, setMeLevel);
          // Elke gebruikersinteractie ontgrendelt geblokkeerde autoplay + resumt AudioContexts.
          const resume = () => { playRemote(); ctxsRef.current.forEach(c => { try { c.resume && c.resume(); } catch (e) { /* stil */ } }); };
          resumeRef.current = resume;
          document.addEventListener("pointerdown", resume);
          document.addEventListener("keydown", resume);
        } catch (e) {
          if (!dead) { setErr(e && e.name === "NotAllowedError" ? "Microfoon geweigerd - sta de mic toe en herlaad." : ((e && e.message) || "Kon de verbinding niet starten")); setStatus("mislukt"); }
        }
      }
      start();
      return () => {
        dead = true;
        try { send({ kind: "bye", from: meEmail }); } catch (e) { /* stil */ }
        if (resumeRef.current) { document.removeEventListener("pointerdown", resumeRef.current); document.removeEventListener("keydown", resumeRef.current); resumeRef.current = null; }
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        ctxsRef.current.forEach(c => { try { c.close(); } catch (e) { /* stil */ } });
        try { micRef.current && micRef.current.getTracks().forEach(t => t.stop()); } catch (e) { /* stil */ }
        try { screenRef.current && screenRef.current.getTracks().forEach(t => t.stop()); } catch (e) { /* stil */ }
        try { pcRef.current && pcRef.current.close(); } catch (e) { /* stil */ }
        try { if (chRef.current) supabase.removeChannel(chRef.current); } catch (e) { /* stil */ }
      };
      // eslint-disable-next-line
    }, [sessionId]);

    function toggleMute() { const m = !muted; setMuted(m); micRef.current && micRef.current.getAudioTracks().forEach(t => t.enabled = !m); }
    async function toggleShare() {
      if (sharing) {
        try { screenRef.current && screenRef.current.getTracks().forEach(t => t.stop()); } catch (e) { /* stil */ }
        if (screenSenderRef.current && pcRef.current) { try { pcRef.current.removeTrack(screenSenderRef.current); } catch (e) { /* stil */ } screenSenderRef.current = null; }
        screenRef.current = null; setSharing(false); if (peerRef.current) negotiate();
        return;
      }
      try {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        screenRef.current = screen; setSharing(true);
        const track = screen.getVideoTracks()[0];
        screenSenderRef.current = pcRef.current.addTrack(track, screen);
        track.onended = () => { setSharing(false); if (screenSenderRef.current && pcRef.current) { try { pcRef.current.removeTrack(screenSenderRef.current); } catch (e) { /* stil */ } screenSenderRef.current = null; } if (peerRef.current) negotiate(); };
      } catch (e) { /* gebruiker annuleerde de schermkeuze */ }
    }

    const statusColor = status === "verbonden" ? C.green : status === "mislukt" ? C.red : C.yellow;
    const people = [{ name: meName, level: muted ? 0 : meLevel, you: true }, { name: peerName, level: peerLevel, you: false }];
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: statusColor }} />
          <p className="lbl" style={{ margin: 0 }}>Dif3r3nt-gesprek · {status}{peerName ? " · met " + peerName : ""}</p>
          <div style={{ marginLeft: "auto", display: "flex", gap: 7 }}>
            <button className={"btn " + (muted ? "bd" : "bg")} style={{ fontSize: 12 }} onClick={toggleMute}>{muted ? "Mic uit" : "Mic aan"}</button>
            {audioBlocked && <button className="btn bp" style={{ fontSize: 12 }} onClick={playRemote} title="De browser blokkeerde het geluid - klik om aan te zetten">◉ Geluid aanzetten</button>}
            <button className={"btn " + (sharing ? "bp" : "bg")} style={{ fontSize: 12 }} onClick={toggleShare}>{sharing ? "Stop delen" : "Scherm delen"}</button>
          </div>
        </div>
        {err && <p style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>⚠ {err}</p>}
        <div style={{ display: "flex", gap: 14 }}>
          {people.map((p, i) => {
            const speaking = p.level > 0.12;
            const active = p.you || !!peerName;
            return (
              <div key={i} style={{ flex: 1, display: "flex", alignItems: "center", gap: 11, padding: "12px 14px", borderRadius: 10, border: "1px solid " + (speaking ? C.green : C.border), background: C.bg, transition: "border-color .12s", opacity: active ? 1 : .55 }}>
                <div style={{ position: "relative", width: 42, height: 42, flexShrink: 0 }}>
                  <div style={{ position: "absolute", inset: -3, borderRadius: "50%", border: "2px solid " + C.green, opacity: Math.min(1, p.level * 2.2), transform: "scale(" + (1 + p.level * 0.25) + ")", transition: "opacity .1s, transform .1s" }} />
                  <div style={{ width: 42, height: 42, borderRadius: "50%", background: C.surfaceHigh, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{(p.you ? meName : (peerName || "?")).slice(0, 1).toUpperCase()}</div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.you ? meName + " (jij)" : (peerName || "Wacht op deelnemer…")}</p>
                  <p style={{ fontSize: 11, color: speaking ? C.green : C.muted }}>{p.you ? (muted ? "gedempt" : (speaking ? "spreekt…" : "stil")) : (peerName ? (speaking ? "spreekt…" : "stil") : "nog niet ingehaakt")}</p>
                </div>
              </div>
            );
          })}
        </div>
        <video ref={remoteScreenRef} autoPlay playsInline muted style={{ width: "100%", borderRadius: 8, border: "1px solid " + C.border, marginTop: 12, display: remoteSharing ? "block" : "none", background: "#000" }} />
        {sharing && <p style={{ fontSize: 11, color: C.blue, marginTop: 10 }}>Je deelt je scherm{peerName ? " met " + peerName : ""}.</p>}
        <audio ref={remoteAudioRef} autoPlay />
      </div>
    );
  }

  function SharedMeeting({ sessionId, user, setView, setTasks, onExit }) {
    const meEmail = user?.email || "";
    const meName = user?.name || "Onbekend";
    const [session, setSession] = useState(null);
    const [transcripts, setTranscripts] = useState([]);
    const [isListening, setIsListening] = useState(false);
    const [interim, setInterim] = useState("");
    const [analysis, setAnalysis] = useState({ summary: "", actions: [], decisions: [], risks: [], followups: [], open_questions: [] });
    const [analyzing, setAnalyzing] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [nextAnalysis, setNextAnalysis] = useState(30);
    const [endingMeeting, setEndingMeeting] = useState(false);
    const [videoOn, setVideoOn] = useState(true);
    const [speakingPeers, setSpeakingPeers] = useState({}); // email -> naam van wie nu spreekt (excl. jezelf)
    const recRef = useRef(null);
    const listeningRef = useRef(false);
    const startTimeRef = useRef(Date.now());
    const analyzingRef = useRef(false);
    const lastInsertRef = useRef("");
    const chRef = useRef(null);

    const isHost = session && session.host_email === meEmail;
    const combinedTranscript = transcripts.map(t => `[${(t.sender_name || t.sender_email || "?").split("@")[0]}] ${t.text}`).join("\n");

    useEffect(() => {
      async function load() {
        const { data } = await supabase.from("meeting_sessions").select("*").eq("id", sessionId).maybeSingle();
        if (data) setSession(data);
        const { data: tr } = await supabase.from("meeting_transcripts").select("*").eq("session_id", sessionId).order("created_at", { ascending: true });
        if (tr) setTranscripts(tr);
      }
      load();
      const ch = supabase.channel("shared_meeting_" + sessionId)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "meeting_transcripts", filter: `session_id=eq.${sessionId}` }, (payload) => {
          setTranscripts(p => p.some(t => t.id === payload.new.id) ? p : [...p, payload.new]);
        })
        .on("broadcast", { event: "speaking" }, ({ payload }) => {
          if (!payload || payload.email === meEmail) return;
          setSpeakingPeers(p => { const n = { ...p }; if (payload.speaking) n[payload.email] = payload.name || payload.email; else delete n[payload.email]; return n; });
        })
        .on("postgres_changes", { event: "UPDATE", schema: "public", table: "meeting_sessions", filter: `id=eq.${sessionId}` }, (payload) => {
          setSession(payload.new);
          if (payload.new.live_summary && Object.keys(payload.new.live_summary).length > 0) {
            setAnalysis(prev => ({ ...prev, ...payload.new.live_summary }));
          }
          if (payload.new.status === "ended") {
            if (listeningRef.current) stopListening();
          }
        })
        .subscribe();
      chRef.current = ch;
      return () => { chRef.current = null; supabase.removeChannel(ch); };
    }, [sessionId]);

    // Reconciliatie: vang eventueel gemiste realtime-INSERTs op door periodiek het
    // transcript bij te trekken (dedup op id). Zo komt de andere deelnemer altijd door,
    // ook als een realtime-event wegvalt.
    useEffect(() => {
      if (!sessionId) return;
      const iv = setInterval(async () => {
        const { data } = await supabase.from("meeting_transcripts").select("*").eq("session_id", sessionId).order("created_at", { ascending: true });
        if (data) setTranscripts(prev => {
          const ids = new Set(prev.map(t => t.id));
          const add = data.filter(t => !ids.has(t.id));
          return add.length ? [...prev, ...add] : prev;
        });
      }, 4000);
      return () => clearInterval(iv);
    }, [sessionId]);

    // Tijd-tracker en periodieke analyse (alleen host)
    useEffect(() => {
      if (!session || session.status === "ended") return;
      let countdown = 30;
      const t = setInterval(() => {
        setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
        countdown -= 1;
        setNextAnalysis(countdown);
        if (countdown <= 0) {
          countdown = 30; setNextAnalysis(30);
          if (isHost && !analyzingRef.current && transcripts.length > 0) runPeriodicAnalysis();
        }
      }, 1000);
      return () => clearInterval(t);
    }, [session, transcripts.length, isHost]);

    // (Videobellen via Jitsi is vervangen door de eigen Dif3r3ntCall-component.)

    function startListening() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) { alert("Spraakherkenning niet beschikbaar in deze browser. Gebruik Chrome of Edge."); return; }
      const r = new SR();
      r.lang = "nl-NL"; r.continuous = true; r.interimResults = true;
      r.onresult = async (e) => {
        let final = "", inter = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
          else inter += e.results[i][0].transcript;
        }
        if (final.trim() && final.trim() !== lastInsertRef.current) {
          lastInsertRef.current = final.trim();
          await supabase.from("meeting_transcripts").insert([{
            session_id: sessionId, sender_email: meEmail, sender_name: meName, text: final.trim(), is_final: true,
          }]);
        }
        setInterim(inter);
      };
      r.onend = () => { if (listeningRef.current) { try { r.start(); } catch { } } else setIsListening(false); };
      r.onerror = (e) => { if (e.error !== "no-speech") console.warn("Speech:", e.error); };
      // Deel je spreekstatus met de andere deelnemer (broadcast, geen transcript-duplicatie).
      const sendSpeaking = (speaking) => { try { chRef.current && chRef.current.send({ type: "broadcast", event: "speaking", payload: { email: meEmail, name: meName, speaking } }); } catch { } };
      r.onspeechstart = () => sendSpeaking(true);
      r.onspeechend = () => sendSpeaking(false);
      try { r.start(); recRef.current = r; listeningRef.current = true; setIsListening(true); } catch (err) { console.warn(err); }
    }
    function stopListening() {
      listeningRef.current = false;
      if (recRef.current) { recRef.current.onend = null; try { recRef.current.stop(); } catch { } recRef.current = null; }
      try { chRef.current && chRef.current.send({ type: "broadcast", event: "speaking", payload: { email: meEmail, name: meName, speaking: false } }); } catch { }
      setIsListening(false); setInterim("");
    }

    async function runPeriodicAnalysis() {
      if (analyzingRef.current || !combinedTranscript.trim()) return;
      analyzingRef.current = true; setAnalyzing(true);
      try {
        const { parsed } = await callGroq(MEETING_ANALYSIS_SYS, "Meeting: " + (session?.title || "") + "\nGedeeld transcript (met sprekers):\n" + combinedTranscript, { maxTokens: 1500, temperature: 0.3 });
        if (parsed) {
          const next = { summary: parsed.summary || "", actions: Array.isArray(parsed.actions) ? parsed.actions : [], decisions: Array.isArray(parsed.decisions) ? parsed.decisions : [], risks: Array.isArray(parsed.risks) ? parsed.risks : [], followups: Array.isArray(parsed.followups) ? parsed.followups : [], open_questions: Array.isArray(parsed.open_questions) ? parsed.open_questions : [] };
          setAnalysis(next);
          await supabase.from("meeting_sessions").update({ live_summary: next }).eq("id", sessionId);
        }
      } catch (e) { console.warn("Analysis:", e); }
      analyzingRef.current = false; setAnalyzing(false);
    }

    async function endMeeting() {
      if (!isHost) {
        // Gast verlaat alleen lokaal
        stopListening();
        if (onExit) onExit();
        return;
      }
      if (!window.confirm("Vergadering afsluiten? Dit slaat de eindanalyse op en maakt taken aan.")) return;
      setEndingMeeting(true); stopListening();
      try {
        const fullText = combinedTranscript;
        const { parsed } = await callGroq(MEETING_FINAL_SYS, "Meeting: " + (session?.title || "") + "\nVolledig transcript:\n" + fullText, { maxTokens: 2000, temperature: 0.3 });
        const finalData = {
          summary: parsed?.summary || fullText.slice(0, 400),
          actions: Array.isArray(parsed?.actions) ? parsed.actions : [],
          decisions: Array.isArray(parsed?.decisions) ? parsed.decisions : [],
          risks: Array.isArray(parsed?.risks) ? parsed.risks : [],
          followups: Array.isArray(parsed?.followups) ? parsed.followups : [],
          open_questions: Array.isArray(parsed?.open_questions) ? parsed.open_questions : [],
        };
        await supabase.from("meeting_sessions").update({ status: "ended", ended_at: new Date().toISOString(), final_summary: finalData }).eq("id", sessionId);
        // Sla op in meetings tabel + maak taken
        const structuredExtras = JSON.stringify({ risks: finalData.risks, open_questions: finalData.open_questions });
        const meetingRow = {
          title: session?.title || "Vergadering", date: dateStr(TODAY), duration: Math.max(1, Math.round(elapsed / 60)),
          attendees: Array.isArray(session?.participants) ? session.participants : ["furgel"], status: "done", notes: finalData.summary,
          besluiten: finalData.decisions,
          actions: finalData.actions.map(a => ({ title: a.task, assignee: a.owner || "furgel", due: a.deadline || dateStr(addDays(TODAY, 7)) })),
          follow_up: finalData.followups,
          raw_notes: fullText + "\n\n__structured__\n" + structuredExtras,
        };
        await supabase.from("meetings").insert([meetingRow]);
        if (finalData.actions.length > 0 && setTasks) {
          const taskRows = finalData.actions.map(a => ({
            title: a.task, owner: a.owner || "furgel", assignee: a.owner || "furgel",
            priority: "MED", status: "todo", due: a.deadline || dateStr(addDays(TODAY, 7)),
            dienst: "", notes: "Uit gezamenlijke vergadering: " + (session?.title || ""),
          }));
          const { data } = await supabase.from("tasks").insert(taskRows).select();
          if (data) setTasks(p => [...p, ...data]);
        }
        // Reset agenda koppeling
        if (session?.event_id) {
          await supabase.from("events").update({ active_session_id: null }).eq("id", session.event_id);
        }
      } catch (e) { console.error("End meeting:", e); }
      setEndingMeeting(false);
      if (onExit) onExit();
    }

    if (!session) {
      return <div className="fu" style={{ padding: 30, textAlign: "center" }}><p style={{ color: C.muted }}>Sessie laden...</p></div>;
    }
    if (session.status === "ended") {
      return (
        <div className="fu">
          <div className="card" style={{ padding: 30, textAlign: "center" }}>
            <p style={{ fontSize: 30, marginBottom: 11 }}>✓</p>
            <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Vergadering afgesloten</p>
            <p style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>{session.title} — eindrapport is opgeslagen in Meetings.</p>
            <button className="btn bp" onClick={onExit}>← Terug</button>
          </div>
        </div>
      );
    }

    const fmt = s => String(Math.floor(s / 60)).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");
    const participantsList = [...new Set(transcripts.map(t => t.sender_name || t.sender_email).filter(Boolean))];

    return (
      <div className="fu">
        <div style={{ background: C.redDim, border: "1px solid " + C.red + "40", borderRadius: 8, padding: "13px 18px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0, flex: 1 }}>
            <div style={{ width: 9, height: 9, borderRadius: "50%", background: C.red, animation: "pulse 1s infinite", flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <p style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: C.red }}>VERGADER MODUS — GEDEELD {isHost ? "(host)" : "(gast)"}</p>
              <p style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis" }}>{session.title}</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: C.red }}>{fmt(elapsed)}</span>
              <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", marginTop: 1 }}>Analyse: {nextAnalysis}s</p>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Bdg label={isListening ? "TRANSCRIPT AAN" : "TRANSCRIPT UIT"} bg={isListening ? C.greenDim : C.border} fg={isListening ? C.green : C.muted} />
              {analyzing && <Bdg label="AI" bg={C.purpleDim} fg={C.purple} />}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 11, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.muted }}>Deelnemers:</span>
          {participantsList.length === 0 && <span style={{ fontSize: 11, color: C.dim, fontStyle: "italic" }}>Nog niemand heeft gesproken</span>}
          {participantsList.map((p, i) => (
            <span key={i} style={{ fontSize: 11, padding: "3px 9px", borderRadius: 11, background: C.greenDim, color: C.green, fontWeight: 600 }}>{p.split("@")[0]}</span>
          ))}
          {Object.keys(speakingPeers).length > 0 && (
            <span style={{ fontSize: 11, padding: "3px 10px", borderRadius: 11, background: C.greenMid, color: C.green, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, display: "inline-block" }} />
              {Object.values(speakingPeers).map(n => String(n).split("@")[0]).join(", ")} {Object.keys(speakingPeers).length > 1 ? "praten" : "is aan het woord"}
            </span>
          )}
          <span style={{ flex: 1 }} />
          <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setVideoOn(p => !p)} title={videoOn ? "Verberg het belvenster - het gesprek blijft gewoon doorlopen" : "Toon het belvenster"}>{videoOn ? "Verberg belvenster" : "Toon belvenster"}</button>
        </div>
        {/* Het gesprek blijft ALTIJD gemount. Eerder unmountte {videoOn && <Dif3r3ntCall/>}
            de hele call bij "Verberg gesprek", wat de peer-connection + microfoon sloot en
            de "bye" verstuurde -> beide kanten hoorden elkaar dan niet meer. videoOn regelt
            nu alleen nog de zichtbaarheid via CSS, zodat de audio blijft lopen. */}
        <div style={{ display: videoOn ? "block" : "none" }}>
          <Dif3r3ntCall sessionId={sessionId} meEmail={meEmail} meName={meName} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13, marginBottom: 14 }}>
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
              <p className="lbl">Gedeeld transcript ({transcripts.length} regels)</p>
              <div style={{ display: "flex", gap: 6 }}>
                {isListening ? <button className="btn bd" style={{ fontSize: 10, padding: "4px 9px" }} onClick={stopListening}>Mic stoppen</button> : <button className="btn bp" style={{ fontSize: 10, padding: "4px 9px" }} onClick={startListening}>Mic starten</button>}
              </div>
            </div>
            <div style={{ maxHeight: 360, overflow: "auto", padding: "7px 9px", background: C.bg, borderRadius: 6, fontSize: 12, lineHeight: 1.65 }}>
              {transcripts.length === 0 && <p style={{ color: C.dim, fontStyle: "italic" }}>Klik Mic starten om bij te dragen aan het transcript. Andere deelnemers zien jouw stem in realtime.</p>}
              {transcripts.map(t => {
                const isMe = t.sender_email === meEmail;
                const name = (t.sender_name || t.sender_email || "?").split("@")[0];
                return (
                  <div key={t.id} style={{ marginBottom: 6, padding: "5px 8px", borderRadius: 5, background: isMe ? C.greenDim + "55" : "transparent" }}>
                    <span style={{ fontSize: 10, color: isMe ? C.green : C.cyan, fontWeight: 700, fontFamily: "monospace", letterSpacing: ".05em" }}>{name.toUpperCase()}</span>
                    <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>{new Date(t.created_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</span>
                    <p style={{ marginTop: 2, color: C.text }}>{t.text}</p>
                  </div>
                );
              })}
              {interim && <p style={{ color: C.dim, fontStyle: "italic", marginTop: 5 }}>{interim}</p>}
            </div>
          </div>
          <div className="card">
            <p className="lbl" style={{ marginBottom: 9 }}>Live samenvatting</p>
            {!analysis.summary && <p style={{ fontSize: 12, color: C.dim }}>AI analyse loopt om de 30 seconden. Wacht tot er voldoende transcript is.</p>}
            {analysis.summary && <div style={{ marginBottom: 11 }}><p style={{ fontSize: 13, lineHeight: 1.7, color: C.muted }}>{analysis.summary}</p></div>}
            {analysis.actions?.length > 0 && (
              <div style={{ marginBottom: 9 }}>
                <p className="lbl" style={{ marginBottom: 5, fontSize: 9 }}>Acties tot nu toe</p>
                {analysis.actions.map((a, i) => <div key={i} style={{ padding: "4px 0", fontSize: 12 }}><span style={{ color: C.green, marginRight: 6 }}>→</span>{a.task}{a.owner && <span style={{ color: C.muted, marginLeft: 6, fontSize: 11 }}>· {a.owner}</span>}</div>)}
              </div>
            )}
            {analysis.decisions?.length > 0 && (
              <div style={{ marginBottom: 9 }}>
                <p className="lbl" style={{ marginBottom: 5, fontSize: 9 }}>Besluiten</p>
                {analysis.decisions.map((d, i) => <div key={i} style={{ padding: "4px 0", fontSize: 12, display: "flex", gap: 6 }}><span style={{ color: C.green }}>✓</span><span>{d}</span></div>)}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
          <button className="btn bg" onClick={() => { stopListening(); if (onExit) onExit(); }}>← Verlaat (zonder afsluiten)</button>
          <button className="btn bp" style={{ background: isHost ? C.red : C.muted, color: "#fff" }} onClick={endMeeting} disabled={endingMeeting}>
            {endingMeeting ? "Afsluiten..." : (isHost ? "Sluit vergadering af (host)" : "Verlaat vergadering")}
          </button>
        </div>
      </div>
    );
  }

  // ─── BRAINSTORM MODULE ───────────────────────────────────────────────────────
  const BRAINSTORM_AGENT_SYS = `Je bent de Brainstorm Agent voor Dif3r3nt VOF (Furgel=CTO, beoordeelt samen met Claude AI). Je krijgt een spraakfragment uit een brainstormsessie. Categoriseer ELKE uitspraak in idea (idee), action (concrete actie/taak), of question (vraag). Bij question: geef DIRECT een kort beknopt antwoord (max 3 zinnen, Nederlands). Wees pragmatisch, geen herhalingen, geen vulwoorden.
Reageer UITSLUITEND als JSON:
{"items":[{"type":"idea|action|question","text":"de uitspraak parafrasered (1 zin, kernachtig)","ai_response":"alleen bij type=question: jouw beknopte antwoord; anders leeg","suggested_owner":"alleen bij type=action: furgel (lowercase)","suggested_deadline":"alleen bij type=action: YYYY-MM-DD of leeg"}]}
Lege array {"items":[]} als het fragment alleen vulwoorden bevat.`;

  function Brainstorm({ user, setTasks, setView, onActiveChange }) {
    const meEmail = user?.email || "";
    const meKey = meEmail === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
    const meName = user?.name || "Onbekend";
    const [sessions, setSessions] = useState([]);
    const [active, setActive] = useState(null);
    const [items, setItems] = useState([]);
    const [isListening, setIsListening] = useState(false);
    const [interim, setInterim] = useState("");
    const [chunk, setChunk] = useState("");
    const [parsing, setParsing] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [nextParse, setNextParse] = useState(20);
    const recRef = useRef(null);
    const listeningRef = useRef(false);
    const chunkRef = useRef("");
    const fullTranscriptRef = useRef("");
    const startTimeRef = useRef(0);
    const parsingRef = useRef(false);
    const [newTitle, setNewTitle] = useState("");
    const [newMod, setNewMod] = useState(false);
    const [importOpen, setImportOpen] = useState(false);

    useEffect(() => {
      async function load() {
        const { data } = await supabase.from("brainstorm_sessions").select("*").order("created_at", { ascending: false });
        if (data) setSessions(data);
      }
      load();
    }, []);

    useEffect(() => {
      if (!active) { setItems([]); return; }
      async function loadItems() {
        const { data } = await supabase.from("brainstorm_items").select("*").eq("session_id", active.id).order("created_at", { ascending: false });
        if (data) setItems(data);
      }
      loadItems();
      const ch = supabase.channel("brainstorm_" + active.id)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "brainstorm_items", filter: `session_id=eq.${active.id}` }, (p) => {
          setItems(prev => [p.new, ...prev.filter(x => x.id !== p.new.id)]);
        })
        .subscribe();
      // Periodieke re-fetch als veiligheidsnet voor realtime hapering
      const refresh = setInterval(loadItems, 15000);
      return () => { supabase.removeChannel(ch); clearInterval(refresh); };
    }, [active?.id]);

    useEffect(() => {
      if (!active || active.status === "ended") return;
      let countdown = 20;
      const t = setInterval(() => {
        setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
        countdown -= 1; setNextParse(countdown);
        if (countdown <= 0) {
          countdown = 20; setNextParse(20);
          // Drempel verlaagd: zelfs 1 woord wordt verwerkt zodat korte sessies werken
          if (!parsingRef.current && chunkRef.current.trim().length > 3) parseChunk();
        }
      }, 1000);
      return () => clearInterval(t);
    }, [active]);

    // Meld aan App of er een actieve sessie loopt — zo blijft de module gemount
    // (en blijft de mic/transcriptie draaien) wanneer je naar een andere module switcht.
    useEffect(() => {
      if (onActiveChange) onActiveChange(!!active && active.status !== "ended");
    }, [active, active?.status]);
    useEffect(() => () => { if (onActiveChange) onActiveChange(false); }, []);

    // Optionele toast voor fouten of bevestiging
    const [toast, setToast] = useState(null);
    function showToast(msg, kind) { setToast({ msg, kind }); setTimeout(() => setToast(null), 3500); }

    // Notities uit bijvoorbeeld de iPhone: een afgesloten sessie met de gecontroleerde items, meteen te openen
    async function importNotities(items, titel) {
      const nu = new Date().toISOString();
      const tekst = items.map((i) => "- " + i.text).join("\n").slice(0, 20000);
      const { data: sessie, error } = await supabase.from("brainstorm_sessions").insert([{ title: titel, user_email: meEmail, status: "ended", ended_at: nu, full_transcript: tekst }]).select();
      if (error || !sessie) throw new Error("Sessie aanmaken mislukt: " + (error && error.message));
      const { error: e2 } = await supabase.from("brainstorm_items").insert(items.map((i) => naarBrainstormRij(i, sessie[0].id)));
      if (e2) { await supabase.from("brainstorm_sessions").delete().eq("id", sessie[0].id); throw new Error("Items opslaan mislukt: " + e2.message); }
      setSessions(p => [sessie[0], ...p]);
      setActive(sessie[0]);
    }

    async function startNewSession() {
      if (!newTitle.trim()) return;
      const { data, error } = await supabase.from("brainstorm_sessions").insert([{ title: newTitle, user_email: meEmail, status: "active" }]).select();
      if (!error && data) {
        setSessions(p => [data[0], ...p]);
        setActive(data[0]);
        startTimeRef.current = Date.now();
        setElapsed(0); setNextParse(20);
        chunkRef.current = ""; fullTranscriptRef.current = ""; setChunk("");
        setNewMod(false); setNewTitle("");
        setTimeout(() => startListening(), 300);
      }
    }

    function startListening() {
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SR) { showToast("Spraakherkenning niet beschikbaar — gebruik Chrome/Edge", "err"); return; }
      const r = new SR();
      r.lang = "nl-NL"; r.continuous = true; r.interimResults = true;
      r.onresult = (e) => {
        let final = "", inter = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
          else inter += e.results[i][0].transcript;
        }
        if (final) {
          chunkRef.current += final;
          fullTranscriptRef.current += final;
          setChunk(chunkRef.current);
        }
        setInterim(inter);
      };
      r.onend = () => {
        // Sommige browsers (Safari) stoppen na elke pauze. Restart als nog actief,
        // maar promote eerst eventuele lopende interim-tekst tot final zodat hij niet verloren gaat.
        setInterim(cur => {
          if (cur && cur.trim()) {
            chunkRef.current += cur + " ";
            fullTranscriptRef.current += cur + " ";
            setChunk(chunkRef.current);
          }
          return "";
        });
        if (listeningRef.current) { try { r.start(); } catch { setTimeout(() => { if (listeningRef.current) { try { r.start(); } catch { } } }, 250); } }
        else setIsListening(false);
      };
      r.onerror = (e) => { if (e.error !== "no-speech" && e.error !== "aborted") console.warn("Speech:", e.error); };
      try { r.start(); recRef.current = r; listeningRef.current = true; setIsListening(true); }
      catch (err) { showToast("Mic kon niet starten: " + (err?.message || "?"), "err"); }
    }
    function stopListening() {
      listeningRef.current = false;
      // Promote pending interim
      setInterim(cur => {
        if (cur && cur.trim()) {
          chunkRef.current += cur + " ";
          fullTranscriptRef.current += cur + " ";
          setChunk(chunkRef.current);
        }
        return "";
      });
      if (recRef.current) { recRef.current.onend = null; try { recRef.current.stop(); } catch { } recRef.current = null; }
      setIsListening(false);
    }

    async function parseChunk(manual) {
      if (!active || parsingRef.current) return;
      const fragment = chunkRef.current.trim();
      if (!fragment) {
        if (manual) showToast("Niets om te analyseren — spreek eerst iets in", "warn");
        return;
      }
      parsingRef.current = true; setParsing(true);
      chunkRef.current = ""; setChunk("");
      try {
        const { parsed, raw } = await callGroq(BRAINSTORM_AGENT_SYS, "Spraakfragment:\n" + fragment + "\n\nContext: brainstorm van " + meName + ", gebruik je rol-kennis (furgel=CTO) om suggested_owner te bepalen.", { maxTokens: 1200, temperature: 0.3 });
        const items = parsed && Array.isArray(parsed.items) ? parsed.items : [];
        if (items.length === 0) {
          // AI kon niets categoriseren — sla fragment alsnog op als 'note' zodat het niet verloren gaat
          await supabase.from("brainstorm_items").insert([{
            session_id: active.id, type: "note", text: fragment.slice(0, 500),
            ai_response: "AI kon dit fragment niet categoriseren — handmatig categoriseren of negeren.",
            status: "pending", source_chunk: fragment.slice(0, 500),
          }]);
          if (manual) showToast("AI kon geen acties detecteren — fragment opgeslagen als notitie", "warn");
        } else {
          const rows = items.map(it => {
            let typ = it.type === "action" || it.type === "idea" || it.type === "question" ? it.type : "idea";
            return {
              session_id: active.id,
              type: typ,
              text: it.text || "",
              ai_response: it.ai_response || "",
              status: typ === "question" && it.ai_response ? "answered" : "pending",
              source_chunk: fragment.slice(0, 500),
            };
          });
          const { error: insErr } = await supabase.from("brainstorm_items").insert(rows);
          if (insErr) showToast("Opslaan mislukt: " + insErr.message, "err");
          else if (manual) showToast(items.length + " item(s) toegevoegd", "ok");
        }
      } catch (e) {
        console.warn("Brainstorm parse:", e);
        showToast("Parse-fout: " + (e?.message || "AI niet bereikbaar"), "err");
        // Stop fragment niet verloren laten gaan: schrijf terug naar buffer zodat user opnieuw kan
        chunkRef.current = fragment; setChunk(fragment);
      }
      parsingRef.current = false; setParsing(false);
      // Defensive: force re-fetch zodat UI bijwerkt ook als realtime hapert
      const { data } = await supabase.from("brainstorm_items").select("*").eq("session_id", active.id).order("created_at", { ascending: false });
      if (data) setItems(data);
    }

    async function endSession() {
      if (!active) return;
      stopListening();
      // Force final parse als er nog iets in de buffer staat (ongeacht lengte)
      if (chunkRef.current.trim()) await parseChunk(false);
      await supabase.from("brainstorm_sessions").update({ status: "ended", ended_at: new Date().toISOString(), full_transcript: fullTranscriptRef.current || "" }).eq("id", active.id);
      setSessions(p => p.map(s => s.id === active.id ? { ...s, status: "ended" } : s));
      setActive(null);
    }

    async function deleteSession(s, e) {
      if (e) { e.stopPropagation(); }
      if (!s) return;
      if (!window.confirm(`Verwijder brainstormsessie "${s.title}"?\n\nDit verwijdert het transcript en alle items (ideeën/acties/vragen) permanent.`)) return;
      const { error } = await supabase.from("brainstorm_sessions").delete().eq("id", s.id);
      if (error) { showToast("Verwijderen mislukt: " + error.message, "err"); return; }
      setSessions(p => p.filter(x => x.id !== s.id));
      if (active && active.id === s.id) setActive(null);
    }

    async function convertToTask(it) {
      if (!setTasks || it.status === "converted") return;
      const due = dateStr(addDays(TODAY, 7));
      const taskRow = {
        title: it.text, owner: meKey, assignee: meKey, priority: "MED", status: "todo", due,
        dienst: "", notes: "Uit brainstorm: " + (active?.title || ""),
      };
      const { data } = await supabase.from("tasks").insert([taskRow]).select();
      if (data) {
        setTasks(p => [...p, data[0]]);
        await supabase.from("brainstorm_items").update({ status: "converted", task_id: data[0].id }).eq("id", it.id);
        setItems(p => p.map(x => x.id === it.id ? { ...x, status: "converted", task_id: data[0].id } : x));
      }
    }

    async function dismissItem(it) {
      await supabase.from("brainstorm_items").update({ status: "dismissed" }).eq("id", it.id);
      setItems(p => p.map(x => x.id === it.id ? { ...x, status: "dismissed" } : x));
    }

    const fmt = s => String(Math.floor(s / 60)).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");

    // ── Actieve sessie view ──
    if (active) {
      const ideas = items.filter(x => x.type === "idea" && x.status !== "dismissed");
      const actions = items.filter(x => x.type === "action" && x.status !== "dismissed");
      const questions = items.filter(x => x.type === "question" && x.status !== "dismissed");
      const notes = items.filter(x => x.type === "note" && x.status !== "dismissed");
      return (
        <div className="fu">
          <div style={{ background: active.status === "ended" ? C.border : C.blueDim, border: "1px solid " + (active.status === "ended" ? C.muted : C.blue + "55"), borderRadius: 8, padding: "13px 18px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
              {active.status !== "ended" && <div style={{ width: 9, height: 9, borderRadius: "50%", background: isListening ? C.green : C.blue, animation: isListening ? "pulse 1s infinite" : "none", flexShrink: 0 }} />}
              <div style={{ minWidth: 0 }}>
                <p style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: active.status === "ended" ? C.muted : C.blue }}>BRAINSTORM {active.status === "ended" ? "AFGESLOTEN" : "ACTIEF"}</p>
                <p style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis" }}>{active.title}</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {active.status !== "ended" && (
                <>
                  <div style={{ textAlign: "right" }}>
                    <span style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: C.blue }}>{fmt(elapsed)}</span>
                    <p style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>Parse: {nextParse}s</p>
                  </div>
                  {parsing && <Bdg label="AI" bg={C.purpleDim} fg={C.purple} />}
                  {isListening ? <button className="btn bd" style={{ fontSize: 11 }} onClick={stopListening}>Mic stoppen</button> : <button className="btn bp" style={{ fontSize: 11 }} onClick={startListening}>Mic starten</button>}
                </>
              )}
            </div>
          </div>
          {active.status !== "ended" && (chunk || interim || isListening) && (
            <div style={{ padding: "9px 12px", background: C.surface, border: "1px dashed " + (isListening ? C.green : C.border), borderRadius: 6, marginBottom: 11, fontSize: 12, lineHeight: 1.6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                <span style={{ color: isListening ? C.green : C.cyan, fontFamily: "monospace", fontSize: 10, fontWeight: 700, letterSpacing: ".05em" }}>{isListening ? "● LIVE BUFFER" : "BUFFER"}</span>
                <button className="btn bp" style={{ fontSize: 10, padding: "3px 9px" }} disabled={parsing || (!chunk && !interim)} onClick={() => parseChunk(true)} title="Analyseer huidige buffer direct">{parsing ? "⟳ Bezig" : "Analyseer nu"}</button>
              </div>
              <p style={{ color: C.text }}>{chunk || <span style={{ color: C.dim, fontStyle: "italic" }}>Wacht op spraak...</span>}{interim && <span style={{ color: C.dim, fontStyle: "italic" }}> {interim}</span>}</p>
            </div>
          )}
          {toast && (
            <div style={{ padding: "8px 12px", borderRadius: 6, marginBottom: 11, background: toast.kind === "err" ? C.redDim : toast.kind === "warn" ? C.yellowDim : C.greenDim, border: "1px solid " + (toast.kind === "err" ? C.red : toast.kind === "warn" ? C.yellow : C.green), color: toast.kind === "err" ? C.red : toast.kind === "warn" ? C.yellow : C.green, fontSize: 12 }}>
              {toast.msg}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 13, marginBottom: 14 }}>
            {/* Ideeën */}
            <div className="card">
              <p className="lbl" style={{ marginBottom: 9, color: C.yellow }}>Ideeën ({ideas.length})</p>
              {ideas.length === 0 && <p style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>Nog geen ideeën gevangen.</p>}
              {ideas.map(it => (
                <div key={it.id} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                  <p style={{ fontSize: 12, lineHeight: 1.5 }}>{it.text}</p>
                  <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
                    <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => convertToTask({ ...it, text: "Onderzoek idee: " + it.text })}>→ Taak</button>
                    <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => dismissItem(it)}>×</button>
                  </div>
                </div>
              ))}
            </div>
            {/* Acties */}
            <div className="card">
              <p className="lbl" style={{ marginBottom: 9, color: C.green }}>✓ Acties ({actions.length})</p>
              {actions.length === 0 && <p style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>Geen acties gedetecteerd.</p>}
              {actions.map(it => (
                <div key={it.id} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border, opacity: it.status === "converted" ? .55 : 1 }}>
                  <p style={{ fontSize: 12, lineHeight: 1.5, textDecoration: it.status === "converted" ? "line-through" : "none" }}>{it.text}</p>
                  <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
                    {it.status === "converted" ? <span style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>✓ TAAK AANGEMAAKT</span> : (
                      <>
                        <button className="btn bp" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => convertToTask(it)}>→ Maak taak</button>
                        <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => dismissItem(it)}>×</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Vragen */}
            <div className="card">
              <p className="lbl" style={{ marginBottom: 9, color: C.cyan }}>❓ Vragen ({questions.length})</p>
              {questions.length === 0 && <p style={{ fontSize: 12, color: C.dim, fontStyle: "italic" }}>Geen vragen.</p>}
              {questions.map(it => (
                <div key={it.id} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                  <p style={{ fontSize: 12, lineHeight: 1.5, fontWeight: 500 }}>{it.text}</p>
                  {it.ai_response && <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginTop: 5, padding: 7, background: C.cyanDim, borderLeft: "2px solid " + C.cyan, borderRadius: 3 }}><span style={{ color: C.cyan, fontWeight: 700, marginRight: 5 }}>AI:</span>{it.ai_response}</p>}
                  <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
                    <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => convertToTask({ ...it, text: "Beantwoord vraag: " + it.text })}>→ Maak taak</button>
                    <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => dismissItem(it)}>×</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {notes.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <p className="lbl" style={{ marginBottom: 9, color: C.muted }}>Niet-gecategoriseerde notities ({notes.length})</p>
              <p style={{ fontSize: 11, color: C.dim, marginBottom: 8, fontStyle: "italic" }}>AI kon deze fragmenten niet eenduidig in idee/actie/vraag plaatsen. Handmatig converteren of negeren.</p>
              {notes.map(it => (
                <div key={it.id} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                  <p style={{ fontSize: 12, lineHeight: 1.5, color: C.muted }}>{it.text}</p>
                  <div style={{ display: "flex", gap: 5, marginTop: 5, flexWrap: "wrap" }}>
                    <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => convertToTask(it)}>→ Maak taak</button>
                    <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => dismissItem(it)}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {active.status !== "ended" && (
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
              <button className="btn bg" onClick={() => { stopListening(); setActive(null); }}>← Pauzeer (sessie blijft actief)</button>
              <button className="btn bp" style={{ background: C.red, color: "#fff" }} onClick={endSession}>Sessie afsluiten</button>
            </div>
          )}
          {active.status === "ended" && (
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
              <button className="btn bg" onClick={() => setActive(null)}>← Terug naar overzicht</button>
              <button className="btn bd" onClick={() => deleteSession(active)}>Verwijder sessie</button>
            </div>
          )}
        </div>
      );
    }

    // ── List view ──
    return (
      <div className="fu">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 9 }}>
          <div>
            <h2 style={{ ...KOP_STIJL }}>Brainstorm</h2>
            <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Spreek je gedachten in — AI categoriseert in ideeën, acties en vragen. Acties worden taken.</p>
          </div>
          <span style={{ display: "inline-flex", gap: 8 }}><button className="btn bg" onClick={() => setImportOpen(true)}>Notities importeren</button><button className="btn bp" onClick={() => setNewMod(true)}>+ Nieuwe brainstorm</button></span>
        </div>
        {sessions.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: C.muted }}>
            <p style={{ fontSize: 34, marginBottom: 11, color: C.dim }}>◇</p>
            <p style={{ fontSize: 13 }}>Nog geen brainstormsessies.</p>
            <button className="btn bp" style={{ marginTop: 12 }} onClick={() => setNewMod(true)}>+ Start je eerste brainstorm</button>
          </div>
        )}
        {importOpen && <NotitiesImport soort="brainstorm" onImporteer={importNotities} onSluit={() => setImportOpen(false)} />}
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {sessions.map(s => (
            <div key={s.id} className="card" style={{ cursor: "pointer" }} onClick={() => { setActive(s); if (s.status !== "ended") startTimeRef.current = Date.now(); }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 7 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600 }}>{s.title}</p>
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{(s.user_email || "").split("@")[0]} · {(s.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <Bdg label={s.status === "ended" ? "AFGESLOTEN" : "ACTIEF"} bg={s.status === "ended" ? C.border : C.greenDim} fg={s.status === "ended" ? C.muted : C.green} />
                  {s.status === "ended" && <button className="btn bd" style={{ fontSize: 11, padding: "4px 8px" }} onClick={(e) => deleteSession(s, e)} title="Verwijder deze sessie permanent"></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
        {newMod && (
          <Portal><div className="ovl" onClick={() => setNewMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 14 }}>NIEUWE BRAINSTORM</h3>
              <Fld label="Titel / onderwerp">
                <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Bijv: Marketingstrategie 2026, Productroadmap, Klant X probleem" autoFocus />
              </Fld>
              <p style={{ fontSize: 11, color: C.muted, marginTop: 9, lineHeight: 1.6 }}>De microfoon start automatisch. Spreek je gedachten in — elke 20 seconden parseert de AI je laatste uitspraken naar ideeën / acties / vragen. Klik op "→ Maak taak" om acties direct aan Taken toe te voegen.</p>
              <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 14 }}>
                <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={startNewSession} disabled={!newTitle.trim()}>Start brainstorm</button>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  // ─── LAUNCH MODULE ────────────────────────────────────────────────────────────

  const LAUNCH_TABS = [
    { id: "studio", l: "Studio", icon: "▶" },
    { id: "testfase", l: "Testfase", icon: "◔" },
    { id: "positionering", l: "Positionering", icon: "◎" },
    { id: "seo", l: "SEO", icon: "◌" },
    { id: "landingspagina", l: "Landingspagina", icon: "⬡" },
    { id: "social", l: "Social Media", icon: "□" },
    { id: "advertenties", l: "Advertenties", icon: "◈" },
    { id: "sales", l: "Sales & Outreach", icon: "◆" },
    { id: "analytics", l: "Analytics", icon: "▣" },
    { id: "checklist", l: "Launch Checklist", icon: "✓" },
    { id: "status", l: "Launch Status", icon: "△" },
  ];

  const LAUNCH_AGENT_SYS = `Je bent de Launch Agent voor Dif3r3nt VOF (Furgel=CTO, beoordeelt samen met Claude AI). Je maakt een volledig launch-pakket in het Nederlands, gericht op de Nederlandse markt, voor ÉÉN specifieke dienst of product. De invoer bevat FEITEN over die dienst (naam, omschrijving van de eigenaar, publiek, tekst van de live pagina). Die feiten zijn je enige bron.

HARDE REGELS
1. Bouw uitsluitend op de aangeleverde feiten. Dif3r3nt zelf levert software aan het MKB, maar het MKB of zakelijk gebruik is NIET automatisch de doelgroep van elke dienst: neem dat alleen over als de feiten het zeggen.
2. Verzin niets. Geen functies, prijzen, aantallen, statistieken, testimonials, reviews, klantnamen, certificeringen, resultaten of beloftes die niet in de feiten staan. Staat iets er niet in, laat het weg of formuleer het neutraal. Voor "social_proof": alleen echte bewijzen uit de feiten; zijn die er niet, geef 2 concrete manieren om echt bewijs te verzamelen (bijvoorbeeld "vraag na een week 5 ouders om een korte reactie"), geen verzonnen citaten. Voor "pricing": alleen wat in de feiten staat, anders "nog te bepalen" met een korte vraag aan Furgel.
3. Pas publiek, toon, kanalen en call-to-actions aan op de dienst:
   - publiek "zakelijk": LinkedIn, demo-aanvraag en besluitvormers passen.
   - publiek "consumenten": Meta, TikTok en Google passen; CTA's als "Probeer gratis".
   - publiek "ouders_kinderen": spreek ouders, verzorgers en leerkrachten aan, NOOIT kinderen rechtstreeks. Geen schaarste- of drukmiddelen, geen beloftes over leerresultaten die niet in de feiten staan, wees helder over privacy en kosten, en houd je aan de Reclamecode voor kinderen en de AVG. Vermijd "Vraag demo aan" en besluitvormers-taal.
   - Is er geen publiek gekozen? Leid het af uit de feiten, en noem die keuze in "doelgroep".
4. Past een kanaal niet bij het publiek (bijvoorbeeld LinkedIn bij een kinderspel), laat dat veld dan leeg ([]) en neem dat platform niet op in "platform_ads", in plaats van een zinloze tekst te schrijven.
5. Behoud de eigen toon van de dienst (bijvoorbeeld speels en warm) als de feiten die laten zien.
6. Schrijf alles PLAATSKLAAR en volledig, behalve waar regel 2 zegt dat iets ontbreekt. Advertentieteksten zijn complete teksten (probleem, oplossing, wat de feiten zeggen als bewijs, concrete call-to-action), niet één regel.
Reageer UITSLUITEND als JSON:
{"doelgroep":"gedetailleerde doelgroepomschrijving","pijnpunten":"concrete pijnpunten van de doelgroep","value_proposition":"unieke waardepropositie in 1-2 zinnen","usp_lijst":["USP 1","USP 2","USP 3","USP 4"],"elevator_pitch":"overtuigende 60-seconden pitch","zoekwoorden":["keyword1","keyword2","keyword3","keyword4","keyword5"],"meta_title":"SEO-titel max 60 tekens","meta_description":"SEO-beschrijving max 160 tekens","seo_blog":"volledig SEO-blogartikel van minimaal 600 woorden","faq":[{"vraag":"Vraag 1?","antwoord":"Antwoord 1"},{"vraag":"Vraag 2?","antwoord":"Antwoord 2"},{"vraag":"Vraag 3?","antwoord":"Antwoord 3"}],"schema_markup":"{\"@context\":\"https://schema.org\",\"@type\":\"Service\"}","hero":"pakkende hero-sectie headline + subheadline","hook":"aandachttrekkende openingszin","voordelen":["Voordeel 1","Voordeel 2","Voordeel 3","Voordeel 4"],"features":[{"naam":"Feature 1","beschrijving":"beschrijving"},{"naam":"Feature 2","beschrijving":"beschrijving"}],"social_proof":["alleen een echt bewijs uit de feiten, of een concrete manier om echt bewijs te verzamelen"],"pricing":"prijsstructuur en tiers beschrijving","cta":"krachtige call-to-action tekst","trust_elements":["Vertrouwenselement 1","Vertrouwenselement 2"],"tiktok_scripts":["TikTok script 1 (30s)","TikTok script 2 (30s)"],"instagram_posts":["Instagram caption 1 met hashtags","Instagram caption 2 met hashtags"],"linkedin_posts":["alleen als LinkedIn bij het publiek past, anders []"],"facebook_posts":["Facebook post 1","Facebook post 2"],"youtube_shorts":["YouTube Shorts concept 1","YouTube Shorts concept 2"],"carrousels":[{"titel":"Carrousel titel","slides":["Slide 1 tekst","Slide 2 tekst","Slide 3 tekst"]}],"advertentie_hooks":["korte scroll-stopper hook 1","hook 2","hook 3","hook 4","hook 5"],"advertentie_teksten":["volledige plaatsklare advertentietekst 1 van 2-4 zinnen: probleem, oplossing, bewijs en duidelijke CTA","volledige advertentietekst 2","volledige advertentietekst 3","volledige advertentietekst 4"],"platform_ads":[{"platform":"Google Search","kop":"hoofdkop max 30 tekens","tekst":"twee beschrijvingen van max 90 tekens, plaatsklaar","cta":"concrete CTA"},{"platform":"Meta (Facebook & Instagram)","kop":"pakkende kop","tekst":"volledige primaire advertentietekst van 2-4 zinnen met probleem, oplossing en bewijs","cta":"Meer informatie"},{"platform":"LinkedIn (alleen bij zakelijk publiek, anders weglaten)","kop":"zakelijke kop","tekst":"professionele advertentietekst gericht op besluitvormers","cta":"passende CTA"},{"platform":"TikTok / Reels","kop":"scroll-stopper","tekst":"volledig kort videoscript met hook, kern en CTA, inclusief aanwijzingen voor beeld en tekst-overlay","cta":"Link in bio"}],"targeting":"gedetailleerde targetingomschrijving voor Meta/Google","budget_advies":"concreet budgetadvies per kanaal","ab_varianten":[{"naam":"Variant A","variant":"beschrijving"},{"naam":"Variant B","variant":"beschrijving"}],"dm_script":"persoonlijk, eerlijk en niet-opdringerig outreachscript passend bij het publiek","outreach_lijst":["plekken of groepen waar het publiek te bereiken is (bij zakelijk: bedrijfstypen; bij ouders: scholen, oudergroepen, kinderopvang)","...","..."],"follow_up_flow":"stapsgewijze follow-up sequentie beschrijving","email_funnel":["Email 1: Welkom","Email 2: Waarde","Email 3: CTA"],"lead_magnet":"concreet lead magnet idee met beschrijving","kpi_lijst":["KPI 1","KPI 2","KPI 3","KPI 4"],"tracking_scripts":"Google Analytics 4 + Meta Pixel implementatie instructies","utm_links":[{"naam":"Google Ads","url":"?utm_source=google&utm_medium=cpc&utm_campaign=launch"},{"naam":"Facebook","url":"?utm_source=facebook&utm_medium=social&utm_campaign=launch"}],"dashboard_koppeling":"aanbevolen dashboard tools en koppelingen","checklist_items":["Testlink verificatie","SEO meta tags live","Social media accounts klaar","Advertentiecampagnes opgezet","Analytics tracking actief","Email funnel getest","Team geïnformeerd","Klanten notificatie verstuurd"]}`;

  const LAUNCH_TEST_SYS = `Je bent de Test Agent voor Dif3r3nt VOF. Genereer een gedetailleerde testchecklist voor een dienst/product en evalueer het testresultaat op basis van de testfeedback. Reageer UITSLUITEND als JSON:
{"checklist":["Controleer of de dienst bereikbaar is via de testlink","Test alle kernfunctionaliteiten stap voor stap","Controleer responsiviteit op mobiel en desktop","Test foutmeldingen en edge cases","Controleer laadtijden en performance","Verifieer de UX/UI is intuïtief en foutloos","Test betalingsflow of aanmelding indien aanwezig","Controleer e-mailnotificaties","Verifieer data-integriteit","Documenteer alle bevindingen met screenshots"],"testResultaat":"uitgebreide analyse van het testresultaat","goedgekeurd":true,"redenAfkeuring":""}`;

  const LAUNCH_FINAL_SYS = `Je bent de Launch Agent voor Dif3r3nt VOF. Je krijgt LIVE diagnostiek van de testlink (HTTP-statuscode, eindURL, page title, gedetecteerde foutpatronen, body-snippet) en optionele handmatige testfeedback. Evalueer of de dienst klaar is voor launch.

PRIMAIRE BESLISREGEL — HTTP STATUS IS LEIDEND:
- HTTP 2xx en geen gedetecteerde foutpatronen → GOEDGEKEURD (ongeacht losse woorden in body).
- HTTP 3xx (redirect) is OK als de eind-URL en body kloppen.
- HTTP 4xx/5xx → AFGEKEURD met exacte statuscode.
- HTTP 0 / timeout → AFGEKEURD ("Server onbereikbaar").

BODY-PATRONEN — alleen vertrouwen bij EXPLICIETE bewijs:
- "404 / Pagina niet gevonden" pattern → alleen relevant als HTTP=404 OF de page title/h1 zelf ook "404"/"not found" toont. Als HTTP=200 én title=normaal product/dienst naam → NEGEER deze flag, hij is false positive.
- "Application error" / "deployment_not_found" / build-errors → AFKEUREN ongeacht HTTP (dit zijn echte signalen).
- "Lege root/app div" → AFKEUREN (SPA niet gehydrateerd) — bevestig met body-snippet of er content is.
- Een woord als "niet gevonden" of "404" dat ergens in een grote body voorkomt is GEEN bewijs van een error-page als HTTP=200 en title normaal is.

BELANGRIJKE REGEL TEGEN FALSE POSITIVES:
- Een false positive (afkeuren van een werkende site) is ERGER dan een false negative.
- Bij twijfel: bekijk de body-snippet. Als die normaal product-content toont (geen error-layout), keur GOED.
- Wijs handmatige feedback van Furgel zwaarder dan losse woordmatches.

VEREISTE OUTPUT:
1. Wees CONCREET en SPECIFIEK. Noem altijd de exacte HTTP-statuscode + welke patronen wel/niet relevant zijn.
2. redenAfkeuring MOET beginnen met categorie: [HTTP-fout]/[Body-fout]/[Timeout]/[Inhoud-ontbreekt]/[Manuele feedback]/[Build-error]/[Auth-vereist]. Daarna één zin met de exacte fout + welk bewijs (statuscode, body-snippet fragment).
3. aanbevelingen: 2-4 concrete actiestappen.
4. testResultaat: bij goedkeuring expliciet noemen wat de positieve signalen waren (HTTP, title, geen body-errors).

Reageer UITSLUITEND als JSON:
{"goedgekeurd":true,"testResultaat":"gedetailleerde analyse: HTTP-status, page title, welke patronen wel/niet relevant, conclusie","redenAfkeuring":"(leeg als goedgekeurd, anders: [Categorie] exacte specifieke reden met statuscode/foutmelding/element)","aanbevelingen":["Concrete actie 1","Concrete actie 2"]}`;

  const LAUNCH_STATUS_COL = {
    "In Testfase": { bg: C.yellowDim, fg: C.yellow },
    "Klaar voor Acceptatie": { bg: C.greenDim, fg: C.green },
    "Klaar voor Launch": { bg: C.cyanDim, fg: C.cyan },
    "Gelanceerd": { bg: C.greenDim, fg: C.green },
  };

  function LaunchFeedbackThread({ launchId, user, onCountChange }) {
    const meEmail = user?.email || "";
    const meName = user?.name || "Onbekend";
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyTo, setReplyTo] = useState(null);
    const [draft, setDraft] = useState("");
    const [filter, setFilter] = useState("all");
    const [posting, setPosting] = useState(false);

    useEffect(() => {
      if (!launchId) { setEntries([]); setLoading(false); return; }
      async function load() {
        const { data } = await supabase.from("launch_feedback").select("*").eq("launch_id", launchId).order("created_at", { ascending: true });
        if (data) {
          setEntries(data);
          if (onCountChange) onCountChange(data.filter(e => e.status === "open").length);
        }
        setLoading(false);
      }
      load();
      const ch = supabase.channel("launch_feedback_" + launchId)
        .on("postgres_changes", { event: "*", schema: "public", table: "launch_feedback", filter: `launch_id=eq.${launchId}` }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, [launchId]);

    async function postEntry() {
      if (!draft.trim() || posting || !launchId) return;
      setPosting(true);
      const row = {
        launch_id: launchId,
        author_email: meEmail, author_name: meName,
        type: replyTo ? "reply" : "user",
        message: draft.trim(),
        parent_id: replyTo,
        status: "open",
      };
      const { error } = await supabase.from("launch_feedback").insert([row]);
      if (error) alert("Kon niet opslaan: " + error.message);
      setDraft(""); setReplyTo(null); setPosting(false);
    }

    async function resolveEntry(id) {
      const now = new Date().toISOString();
      await supabase.from("launch_feedback").update({ status: "resolved", resolved_at: now, resolved_by: meEmail }).eq("id", id);
    }
    async function reopenEntry(id) {
      await supabase.from("launch_feedback").update({ status: "open", resolved_at: null, resolved_by: null }).eq("id", id);
    }
    async function deleteEntry(id) {
      if (!window.confirm("Verwijder dit bericht uit de feedback-thread?")) return;
      await supabase.from("launch_feedback").delete().eq("id", id);
    }

    const filtered = entries.filter(e => {
      if (filter === "open") return e.status === "open";
      if (filter === "resolved") return e.status === "resolved";
      if (filter === "ai") return e.type === "ai_analysis" || e.type === "ai_rejection";
      if (filter === "mine") return e.author_email === meEmail;
      return true;
    });

    function typeLabel(t) {
      if (t === "ai_analysis") return { l: "AI ANALYSE", bg: C.purpleDim, fg: C.purple };
      if (t === "ai_rejection") return { l: "✗ AI AFKEURING", bg: C.redDim, fg: C.red };
      if (t === "reply") return { l: "↳ REPLY", bg: C.blueDim, fg: C.blue };
      return { l: "FEEDBACK", bg: C.greenDim, fg: C.green };
    }

    return (
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 560, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 7 }}>
          <p className="lbl">Feedback thread ({entries.length})</p>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {[["all", "Alle"], ["open", "Open"], ["resolved", "Opgelost"], ["ai", "AI"], ["mine", "Mijn"]].map(([k, l]) => {
              const cnt = k === "all" ? entries.length : k === "open" ? entries.filter(e => e.status === "open").length : k === "resolved" ? entries.filter(e => e.status === "resolved").length : k === "ai" ? entries.filter(e => e.type === "ai_analysis" || e.type === "ai_rejection").length : entries.filter(e => e.author_email === meEmail).length;
              return <span key={k} className={"tab" + (filter === k ? " on" : "")} onClick={() => setFilter(k)} style={{ fontSize: 10, padding: "3px 8px" }}>{l} {cnt}</span>;
            })}
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 9, paddingRight: 4 }}>
          {loading && <p style={{ fontSize: 12, color: C.dim }}>Laden...</p>}
          {!loading && filtered.length === 0 && <p style={{ fontSize: 12, color: C.dim, fontStyle: "italic", padding: "15px 0", textAlign: "center" }}>Nog geen berichten in deze view.</p>}
          {filtered.map(e => {
            const tl = typeLabel(e.type);
            const isReply = !!e.parent_id;
            const isMine = e.author_email === meEmail;
            return (
              <div key={e.id} style={{ padding: "10px 12px", borderRadius: 6, background: e.status === "resolved" ? C.bg : C.surface, border: "1px solid " + (e.status === "resolved" ? C.border : tl.fg + "33"), marginLeft: isReply ? 20 : 0, opacity: e.status === "resolved" ? .7 : 1, position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 5 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <Bdg label={tl.l} bg={tl.bg} fg={tl.fg} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: isMine ? C.green : C.text }}>{e.author_name || "Onbekend"}{isMine ? " (jij)" : ""}</span>
                    <span style={{ fontSize: 10, color: C.dim, fontFamily: "monospace" }}>{new Date(e.created_at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                    {e.status === "resolved" && <Bdg label="✓ OPGELOST" bg={C.greenDim} fg={C.green} />}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {e.status === "open"
                      ? <button className="btn bg" style={{ fontSize: 9, padding: "2px 6px" }} onClick={() => resolveEntry(e.id)} title="Markeer als opgelost">✓</button>
                      : <button className="btn bg" style={{ fontSize: 9, padding: "2px 6px" }} onClick={() => reopenEntry(e.id)} title="Heropen">↺</button>}
                    <button className="btn bg" style={{ fontSize: 9, padding: "2px 6px" }} onClick={() => setReplyTo(e.id === replyTo ? null : e.id)} title="Reply">↳</button>
                    {(isMine || e.type !== "user") && <button className="btn bd" style={{ fontSize: 9, padding: "2px 6px" }} onClick={() => deleteEntry(e.id)} title="Verwijder">×</button>}
                  </div>
                </div>
                <p style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", color: C.muted }}>{e.message}</p>
                {e.resolved_by && <p style={{ fontSize: 10, color: C.dim, marginTop: 5, fontStyle: "italic" }}>Opgelost door {e.resolved_by.split("@")[0]} op {new Date(e.resolved_at).toLocaleDateString("nl-NL")}</p>}
              </div>
            );
          })}
        </div>
        <div style={{ borderTop: "1px solid " + C.border, paddingTop: 9 }}>
          {replyTo && (
            <div style={{ fontSize: 10, color: C.blue, marginBottom: 5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>↳ Reageert op bericht hierboven</span>
              <button className="btn bg" style={{ fontSize: 9, padding: "1px 6px" }} onClick={() => setReplyTo(null)}>× annuleer reply</button>
            </div>
          )}
          <textarea rows={3} value={draft} onChange={e => setDraft(e.target.value)} placeholder={replyTo ? "Typ je reply..." : "Nieuw feedback bericht..."} style={{ marginBottom: 6 }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 10, color: C.dim }}>Als {meName} · ⏎ submit</span>
            <button className="btn bp" style={{ fontSize: 11 }} onClick={postEntry} disabled={!draft.trim() || posting}>{posting ? "..." : replyTo ? "↳ Reageer" : "Plaats feedback"}</button>
          </div>
        </div>
      </div>
    );
  }

  function LaunchModule({ launches, setLaunches, pipeline, setPipeline, setTasks, setView, initialSel, user }) {
    const [sel, setSel] = useState(initialSel || null);
    const [tab, setTab] = useState("studio");
    const [approved, setApproved] = useState({});
    const [copiedId, setCopiedId] = useState("");
    const [generating, setGenerating] = useState(false);
    const [genErr, setGenErr] = useState("");
    const [omschrDraft, setOmschrDraft] = useState(null);
    const [feitenBezig, setFeitenBezig] = useState(false);
    const [genStep, setGenStep] = useState("");
    const [saving, setSaving] = useState(false);
    const [testLinkInput, setTestLinkInput] = useState("");
    const [storeUrl, setStoreUrl] = useState("");        // gedeelde Store OS test-link (app_settings)
    const [storeEdit, setStoreEdit] = useState(false);
    const storeCancelRef = useRef(false);                // Escape = annuleren, niet opslaan
    const [feedbackInput, setFeedbackInput] = useState("");
    const [localData, setLocalData] = useState(null);
    const [shotUrl, setShotUrl] = useState("");
    const [shotPages, setShotPages] = useState("");
    const [shotGallery, setShotGallery] = useState([]);
    const [shooting, setShooting] = useState(false);
    const [crawlBusy, setCrawlBusy] = useState(false);
    const [shotErr, setShotErr] = useState("");
    const [vidJobs, setVidJobs] = useState({});
    const [videoPromos, setVideoPromos] = useState({}); // per-video bewerkte promotekst (tussenstap voor de video-generatie)
    const [videoSecs, setVideoSecs] = useState({});     // per-video cliplengte (5 of 10 sec; het model kent alleen die twee)
    const [seoT, setSeoT] = useState("");        // bewerkbare meta-title
    const [seoD, setSeoD] = useState("");        // bewerkbare meta-description
    const [seoDirty, setSeoDirty] = useState(false);
    const [seoMsg, setSeoMsg] = useState("");
    const [openFaq, setOpenFaq] = useState(-1);
    const [kwBusy, setKwBusy] = useState(false);
    const [edits, setEdits] = useState({});      // inline-bewerkte velden (landing + positionering)
    const [editsBusy, setEditsBusy] = useState(false);
    const pendingRef = useRef({ id: null, edits: {}, seo: null }); // laatste niet-opgeslagen wijzigingen, voor auto-flush
    const [uspBusy, setUspBusy] = useState(false);
    const [linkStatus, setLinkStatus] = useState(null); // {ok, status, statusText, hint, elapsed_ms} of null
    const [linkChecking, setLinkChecking] = useState(false);
    const [vercelProjects, setVercelProjects] = useState([]);
    const [vercelDeployments, setVercelDeployments] = useState([]);
    const [vercelLoading, setVercelLoading] = useState(false);
    const [vercelError, setVercelError] = useState("");
    const [feedbackCounts, setFeedbackCounts] = useState({});

    async function checkLink(url) {
      if (!url || !/^https?:\/\//i.test(url)) { setLinkStatus(null); return; }
      setLinkChecking(true);
      try {
        const r = await apiFetch("/api/check-link?url=" + encodeURIComponent(url));
        const d = await r.json();
        setLinkStatus(d);
      } catch (e) {
        setLinkStatus({ ok: false, status: 0, statusText: "Check mislukt: " + (e?.message || "?"), hint: "Controleer je internetverbinding." });
      }
      setLinkChecking(false);
    }

    async function loadVercelProjects() {
      setVercelLoading(true); setVercelError("");
      try {
        const r = await apiFetch("/api/vercel-projects");
        const d = await r.json();
        if (d.error) { setVercelError(d.error); return; }
        setVercelProjects(d.projects || []);
      } catch (e) { setVercelError(e.message || "Onbekende fout"); }
      setVercelLoading(false);
    }

    async function loadVercelDeployments(projectId) {
      if (!projectId) return;
      setVercelLoading(true); setVercelError("");
      try {
        const r = await apiFetch("/api/vercel-deployments?projectId=" + encodeURIComponent(projectId));
        const d = await r.json();
        if (d.error) { setVercelError(d.error); return; }
        setVercelDeployments(d.deployments || []);
      } catch (e) { setVercelError(e.message || "Onbekende fout"); }
      setVercelLoading(false);
    }

    async function linkVercelProject(project) {
      if (!project) return;
      const patch = {
        vercel_project_id: project.id,
        vercel_project_name: project.name,
      };
      await savePatch(patch);
      setLocalData(p => ({ ...p, ...patch }));
      await loadVercelDeployments(project.id);
    }

    async function useVercelDeployment(dep) {
      if (!dep || !dep.url) return;
      const patch = {
        test_link: dep.url,
        test_status: "In test",
        vercel_deployment_id: dep.uid,
        vercel_deployment_target: dep.target || "preview",
      };
      await savePatch(patch);
      setLocalData(p => ({ ...p, ...patch }));
      setTestLinkInput("");
    }

    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("launch_modules").select("*").order("created_at", { ascending: false });
        if (!error && data) setLaunches(data);
      }
      async function loadFeedbackCounts() {
        const { data } = await supabase.from("launch_feedback").select("launch_id,status").eq("status", "open");
        if (data) {
          const counts = {};
          for (const r of data) counts[r.launch_id] = (counts[r.launch_id] || 0) + 1;
          setFeedbackCounts(counts);
        }
      }
      load(); loadFeedbackCounts();
      const ch = supabase.channel("launch_modules_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "launch_modules" }, () => load())
        .subscribe();
      const fb = supabase.channel("launch_feedback_counts")
        .on("postgres_changes", { event: "*", schema: "public", table: "launch_feedback" }, () => loadFeedbackCounts())
        .subscribe();
      return () => { supabase.removeChannel(ch); supabase.removeChannel(fb); };
    }, []);

    useEffect(() => {
      if (initialSel) setSel(initialSel);
    }, [initialSel]);

    const launch = launches.find(l => l.id === sel) || null;

    useEffect(() => {
      if (launch) {
        setLocalData({ ...launch });
        setTestLinkInput(launch.test_link || "");
        setFeedbackInput("");
      }
    }, [sel, launch?.id]);

    // Auto-check de testlink wanneer hij verandert (debounced) — gebruikt de live input
    useEffect(() => {
      const url = (testLinkInput || "").trim() || localData?.test_link || launch?.test_link || "";
      if (!url) { setLinkStatus(null); return; }
      const t = setTimeout(() => checkLink(url), 600);
      return () => clearTimeout(t);
    }, [testLinkInput, localData?.test_link, launch?.test_link]);

    // Laad Vercel projecten bij open van Testfase tab
    useEffect(() => {
      if (tab === "testfase" && launch && vercelProjects.length === 0 && !vercelLoading) {
        loadVercelProjects();
      }
    }, [tab, launch?.id]);

    // Laad deployments wanneer launch al een project gelinked heeft
    useEffect(() => {
      if (launch?.vercel_project_id && vercelDeployments.length === 0) {
        loadVercelDeployments(launch.vercel_project_id);
      }
    }, [launch?.vercel_project_id]);

    // Gedeelde Store OS test-link (app_settings). Beide zien dezelfde waarde.
    useEffect(() => {
      let alive = true;
      const load = async () => {
        const { data } = await supabase.from("app_settings").select("value").eq("key", "store_os_url").maybeSingle();
        if (alive) setStoreUrl((data && data.value) || "");
      };
      load();
      const ch = supabase.channel("app_settings_sync").on("postgres_changes", { event: "*", schema: "public", table: "app_settings" }, load).subscribe();
      return () => { alive = false; try { supabase.removeChannel(ch); } catch (e) { /* stil */ } };
    }, []);
    async function saveStoreUrl(v) {
      let val = (v || "").trim();
      setStoreEdit(false);
      // Wegklikken uit een onaangeraakt veld mag de gedeelde link niet wissen.
      // Alleen schrijven als de waarde echt veranderd is; bewust leegmaken
      // (er stond een link, die is weggehaald) komt hier dus wel doorheen.
      if (val === (storeUrl || "")) return;
      // Zonder protocol wordt de "Open Store OS"-link relatief en dus kapot.
      if (val && !/^https?:\/\//i.test(val)) val = "https://" + val;
      setStoreUrl(val);
      const mk = user?.email === "tristan@dif3r3nt.nl" ? "tristan" : "furgel";
      await supabase.from("app_settings").upsert({ key: "store_os_url", value: val || null, updated_by: mk, updated_at: new Date().toISOString() }, { onConflict: "key" });
    }

    async function savePatch(patch) {
      if (!sel) return false;
      setSaving(true);
      let body = { ...patch };
      let { data, error } = await supabase.from("launch_modules").update({ ...body, updated_at: new Date().toISOString() }).eq("id", sel).select();
      // Eén kolom die (nog) niet in de database bestaat laat PostgREST de HELE update weigeren,
      // waardoor ook SEO/landingspagina/positionering niet werden bewaard. Probeer opnieuw
      // zonder die kolom; de volledige inhoud staat ook in launch_package.
      for (let i = 0; i < 5 && error && error.code === "PGRST204"; i++) {
        const m = /'([^']+)' column/.exec(error.message || "");
        if (!m || !(m[1] in body)) break;
        console.warn("savePatch: kolom ontbreekt in launch_modules, overgeslagen:", m[1]);
        delete body[m[1]];
        ({ data, error } = await supabase.from("launch_modules").update({ ...body, updated_at: new Date().toISOString() }).eq("id", sel).select());
      }
      setSaving(false);
      // Een lege uitkomst (0 rijen bijgewerkt, bv. door RLS) is geen succes.
      if (error || !data || !data.length) {
        // Eén onbekende kolom laat PostgREST de hele update weigeren; niet stil falen.
        console.error("savePatch mislukt:", error?.message || "geen data terug", "| velden:", Object.keys(patch).join(", "));
        return false;
      }
      setLaunches(p => p.map(x => x.id === sel ? data[0] : x));
      setLocalData(p => p ? { ...p, ...patch } : data[0]);
      return true;
    }

    // Optimistische veld-update: zet localData direct (zodat de controlled select/input
    // meteen de nieuwe waarde toont) en persist daarna via savePatch. Voorkomt dat de
    // status terugspringt tijdens het async opslaan of door realtime-echo's van load().
    function patchField(patch) {
      setLocalData(p => p ? { ...p, ...patch } : p);
      savePatch(patch);
    }

    async function generateLaunchPackage() {
      if (!launch) return;
      // De agent bouwt alleen op feiten. Zonder omschrijving gokt hij (en valt hij terug op het MKB), dus dan niet starten.
      const omschr = (launch.dienst_omschrijving || "").trim();
      // Alleen echte feiten van de eigenaar tellen. Een eerder door de AI geschreven doelgroep is geen bron: dat is hoe verzonnen
      // pakketten ontstonden (een webwinkel werd "IT-infrastructuur", een leerspel een "cadeau-platform").
      if (!omschr) { setGenErr("Vul eerst bij \"Over deze dienst\" in wat de dienst is en voor wie (of haal de feiten op uit de testlink of de businesscase). Zonder feiten gaat de agent gokken."); return; }
      if (!launch.publiek) { setGenErr("Kies eerst bij \"Over deze dienst\" voor wie de dienst is (zakelijk, consumenten of kinderen)."); return; }
      const PUBLIEK_LABEL = { zakelijk: "zakelijk (bedrijven, B2B)", consumenten: "consumenten (B2C)", ouders_kinderen: "ouders, verzorgers en leerkrachten (het product zelf is voor kinderen)" };
      const feiten = [
        "Dienst: " + launch.title,
        "Publiek: " + (PUBLIEK_LABEL[launch.publiek] || "niet gekozen (leid het af uit de feiten)"),
        "Feiten over de dienst (van de eigenaar, leidend):\n" + omschr,
        launch.test_link ? "Testlink: " + launch.test_link : "",
      ].filter(Boolean).join("\n\n");
      setGenerating(true); setGenStep("Launch-pakket genereren..."); setGenErr("");
      setVideoPromos({}); setVideoSecs({});  // nieuwe scripts -> eerdere promo-aanpassingen en lengtekeuzes vervallen (ids zijn positioneel)
      try {
        // Rechtstreeks naar Groq (snel, ~10s, volledig pakket). Claude haalt de 60s
        // edge-limiet niet bij deze grote JSON-output. extractJSON redt een eventueel
        // afgekapte respons; lukt zelfs dat niet, dan tonen we een nette foutmelding.
        const { parsed } = await callGroq(LAUNCH_AGENT_SYS, feiten, { groqOnly: true, maxTokens: 6000, temperature: 0.4 });
        if (parsed && Object.keys(parsed).length) {
          setGenStep("Opslaan in Supabase...");
          const patch = {
            doelgroep: parsed.doelgroep || launch.doelgroep,
            pijnpunten: parsed.pijnpunten || launch.pijnpunten,
            value_proposition: parsed.value_proposition,
            usp_lijst: parsed.usp_lijst || [],
            elevator_pitch: parsed.elevator_pitch,
            zoekwoorden: parsed.zoekwoorden || [],
            meta_title: parsed.meta_title,
            meta_description: parsed.meta_description,
            seo_blog: parsed.seo_blog,
            faq: parsed.faq || [],
            schema_markup: parsed.schema_markup,
            hero: parsed.hero,
            hook: parsed.hook,
            voordelen: parsed.voordelen || [],
            features: parsed.features || [],
            social_proof: parsed.social_proof || [],
            pricing: parsed.pricing,
            cta: parsed.cta,
            trust_elements: parsed.trust_elements || [],
            tiktok_scripts: parsed.tiktok_scripts || [],
            instagram_posts: parsed.instagram_posts || [],
            linkedin_posts: parsed.linkedin_posts || [],
            facebook_posts: parsed.facebook_posts || [],
            youtube_shorts: parsed.youtube_shorts || [],
            carrousels: parsed.carrousels || [],
            advertentie_hooks: parsed.advertentie_hooks || [],
            advertentie_teksten: parsed.advertentie_teksten || [],
            platform_ads: parsed.platform_ads || [],
            targeting: parsed.targeting,
            budget_advies: parsed.budget_advies,
            ab_varianten: parsed.ab_varianten || [],
            dm_script: parsed.dm_script,
            outreach_lijst: parsed.outreach_lijst || [],
            follow_up_flow: parsed.follow_up_flow,
            email_funnel: parsed.email_funnel || [],
            lead_magnet: parsed.lead_magnet,
            kpi_lijst: parsed.kpi_lijst || [],
            tracking_scripts: parsed.tracking_scripts,
            utm_links: parsed.utm_links || [],
            dashboard_koppeling: parsed.dashboard_koppeling,
            checklist_items: parsed.checklist_items || launch.checklist_items || [],
            launch_package: parsed,
          };
          const ok = await savePatch(patch);
          setLocalData(p => ({ ...p, ...patch }));
          if (!ok) setGenErr("Het pakket is gegenereerd maar kon niet worden opgeslagen in de database. Probeer opnieuw; blijft het mislukken, meld dit dan.");
        } else {
          throw new Error("De AI gaf geen bruikbaar launch-pakket terug (waarschijnlijk te lang of afgekapt). Klik opnieuw op Maak mijn launch.");
        }
      } catch (e) { console.error("Launch package generatie:", e); setGenErr(e.message || "Generatie mislukt - probeer opnieuw."); }
      setGenerating(false); setGenStep("");
    }

    async function generateTestChecklist() {
      if (!launch) return;
      setGenerating(true); setGenStep("Testchecklist genereren...");
      try {
        const { parsed } = await callGroq(LAUNCH_TEST_SYS, `Dienst: ${launch.title}\nTestLink: ${launch.test_link || "(nog niet ingevuld)"}`, { maxTokens: 800, temperature: 0.4 });
        if (parsed && parsed.checklist) {
          const patch = { test_checklist: parsed.checklist, checklist_items: parsed.checklist };
          await savePatch(patch);
          setLocalData(p => ({ ...p, ...patch }));
        }
      } catch (e) { console.error("Checklist generatie:", e); }
      setGenerating(false); setGenStep("");
    }

    async function generateTestResultaat() {
      if (!launch) return;
      setGenerating(true); setGenStep("Live diagnostiek ophalen...");
      try {
        const feedback = feedbackInput || launch.test_feedback || "";
        const url = localData?.test_link || launch.test_link || "";
        // Verse live diagnostiek ophalen vóór AI-evaluatie
        let diag = null;
        if (url && /^https?:\/\//i.test(url)) {
          try {
            const r = await apiFetch("/api/check-link?url=" + encodeURIComponent(url));
            diag = await r.json();
            setLinkStatus(diag);
          } catch (e) {
            diag = { ok: false, status: 0, statusText: "Check-link API faalde: " + (e?.message || "?"), detected_errors: ["Kon /api/check-link niet aanroepen"] };
          }
        } else {
          diag = { ok: false, status: 0, statusText: "Geen geldige testlink", detected_errors: ["Geen http(s) testlink ingevuld"] };
        }
        setGenStep("Testresultaat analyseren (AI)...");
        const diagBlock = diag ? [
          `HTTP-status: ${diag.status} ${diag.statusText || ""}`,
          `Bereikbaar: ${diag.ok ? "ja" : "nee"}`,
          `Eind-URL: ${diag.final_url || url}${diag.redirected ? " (redirect)" : ""}`,
          `Content-Type: ${diag.content_type || "—"}`,
          `Page title: ${diag.title || "(geen <title>)"}`,
          `Latency: ${diag.elapsed_ms || "?"}ms`,
          `Gedetecteerde fouten: ${(diag.detected_errors || []).length ? diag.detected_errors.join(" | ") : "geen"}`,
          `Hint check-link: ${diag.hint || "—"}`,
          `Body-snippet (max 5KB):\n${diag.body_snippet || "(geen body)"}`
        ].join("\n") : "(geen diagnostiek)";
        const userMsg = `Dienst: ${launch.title}\nTestLink: ${url || "(leeg)"}\n\n=== LIVE DIAGNOSTIEK ===\n${diagBlock}\n\n=== HANDMATIGE FEEDBACK ===\n${feedback || "(geen handmatige feedback)"}`;
        const { parsed } = await callGroq(LAUNCH_FINAL_SYS, userMsg, { maxTokens: 1400, temperature: 0.2 });
        if (parsed) {
          const patch = {
            test_resultaat: parsed.testResultaat || "",
            test_feedback: feedback,
            test_status: parsed.goedgekeurd ? "Goedgekeurd" : "Afgekeurd",
            reden_afkeuring: parsed.goedgekeurd ? "" : (parsed.redenAfkeuring || ""),
            aanbevelingen: Array.isArray(parsed.aanbevelingen) ? parsed.aanbevelingen : [],
            test_diagnostiek: diag || {},
          };
          await savePatch(patch);
          setLocalData(p => ({ ...p, ...patch }));
          // Plaats AI-analyse als nieuwe entry in de feedback-thread zodat alles op één plek staat
          const aiMessage = parsed.goedgekeurd
            ? (parsed.testResultaat || "")
            : ((parsed.redenAfkeuring || "") + (Array.isArray(parsed.aanbevelingen) && parsed.aanbevelingen.length ? "\n\nAanbevelingen:\n- " + parsed.aanbevelingen.join("\n- ") : ""));
          if (aiMessage.trim()) {
            await supabase.from("launch_feedback").insert([{
              launch_id: launch.id,
              author_email: null, author_name: "AI Launch Agent",
              type: parsed.goedgekeurd ? "ai_analysis" : "ai_rejection",
              message: aiMessage, status: "open",
            }]);
          }
          if (!parsed.goedgekeurd && parsed.redenAfkeuring && launch.pipeline_item_id) {
            await supabase.from("pipeline").update({ col: "on_hold", updated: "Afgekeurd: " + parsed.redenAfkeuring.slice(0, 80) }).eq("id", launch.pipeline_item_id);
            if (setPipeline) setPipeline(p => p.map(x => x.id === launch.pipeline_item_id ? { ...x, col: "on_hold", updated: "Afgekeurd in testfase" } : x));
          }
          if (parsed.goedgekeurd) {
            const statusPatch = { status: "Klaar voor Acceptatie" };
            await savePatch(statusPatch);
            setLocalData(p => ({ ...p, ...statusPatch }));
            // Verschuif kaart naar Acceptatie
            if (launch.pipeline_item_id) {
              await supabase.from("pipeline").update({ col: "acceptatie", updated: "AI test goedgekeurd" }).eq("id", launch.pipeline_item_id);
              if (setPipeline) setPipeline(p => p.map(x => x.id === launch.pipeline_item_id ? { ...x, col: "acceptatie", updated: "AI test goedgekeurd" } : x));
            }
            // Maak acceptatie-taak voor Furgel
            if (setTasks) {
              const taskRow = {
                title: "Acceptatietest uitvoeren: " + launch.title,
                assignee: "furgel", owner: "furgel", priority: "HIGH", status: "todo",
                due: dateStr(addDays(TODAY, 2)),
                notes: "AI heeft de testfase goedgekeurd. Voer een manuele dubbele controle uit, en klik op 'Goedkeuren voor Launch' in de Launch Module zodra alles werkt. Testlink: " + (launch.test_link || "—"),
                dienst: launch.title,
              };
              const { data: tData } = await supabase.from("tasks").insert([taskRow]).select();
              if (tData) setTasks(p => [...p, ...tData]);
            }
          }
        }
      } catch (e) { console.error("Testresultaat generatie:", e); }
      setGenerating(false); setGenStep("");
    }

    async function createTasksVoorFurgel() {
      if (!launch) return;
      setSaving(true);
      const taskRows = [
        { title: "Test de dienst via de testlink: " + launch.title, assignee: "furgel", owner: "furgel", priority: "HIGH", status: "todo", due: dateStr(addDays(TODAY, 3)), notes: "Testlink: " + (launch.test_link || "(zie Launch Module)"), dienst: launch.title },
        { title: "Vul testfeedback in voor: " + launch.title, assignee: "furgel", owner: "furgel", priority: "HIGH", status: "todo", due: dateStr(addDays(TODAY, 4)), notes: "Documenteer bevindingen in de Launch Module → Testfase", dienst: launch.title },
        { title: "Upload screenshots van fouten/bevindingen: " + launch.title, assignee: "furgel", owner: "furgel", priority: "MED", status: "todo", due: dateStr(addDays(TODAY, 4)), notes: "Upload via Launch Module → Testfase → Screenshots", dienst: launch.title },
      ];
      const { data, error } = await supabase.from("tasks").insert(taskRows).select();
      if (!error && data && setTasks) setTasks(p => [...p, ...data]);
      setSaving(false);
    }

    async function acceptByFurgel() {
      if (!launch) return;
      const patch = { status: "Klaar voor Launch" };
      await savePatch(patch);
      setLocalData(p => ({ ...p, ...patch }));
      if (launch.pipeline_item_id && setPipeline) {
        await supabase.from("pipeline").update({ col: "ready_for_launch", updated: "Geaccepteerd door Furgel" }).eq("id", launch.pipeline_item_id);
        setPipeline(p => p.map(x => x.id === launch.pipeline_item_id ? { ...x, col: "ready_for_launch", updated: "Geaccepteerd door Furgel" } : x));
      }
    }

    async function publishLaunch() {
      if (!launch) return;
      const patch = { status: "Gelanceerd", launch_datum: dateStr(TODAY) };
      await savePatch(patch);
      setLocalData(p => ({ ...p, ...patch }));
      if (launch.pipeline_item_id && setPipeline) {
        await supabase.from("pipeline").update({ col: "live", priority: null, updated: "Gelanceerd op " + dateStr(TODAY) }).eq("id", launch.pipeline_item_id);
        setPipeline(p => p.map(x => x.id === launch.pipeline_item_id ? { ...x, col: "live", updated: "Gelanceerd op " + dateStr(TODAY) } : x));
      }
    }

    async function saveLinkAndStatus() {
      if (!launch) return;
      const patch = { test_link: testLinkInput || launch.test_link, test_status: "In test" };
      await savePatch(patch);
      setLocalData(p => ({ ...p, ...patch }));
    }

    function toggleChecklistItem(idx) {
      if (!launch) return;
      const current = [...(launch.checklist_status || [])];
      if (current[idx]) current[idx] = false; else current[idx] = true;
      const patch = { checklist_status: current };
      savePatch(patch);
      setLocalData(p => ({ ...p, checklist_status: current }));
    }

    const eff = localData || launch || {};

    // Houd de bewerkbare SEO-velden in sync met het gegenereerde pakket en reset bij
    // wisselen van dienst, maar overschrijf ze NIET terwijl de gebruiker typt (seoDirty):
    // anders springt de meta-title/description terug door een realtime-sync of een
    // localData-reset. Bij dienst-wissel (andere launch-id) wel altijd verversen.
    const seoSyncIdRef = useRef(null);
    useEffect(() => {
      const switched = seoSyncIdRef.current !== launch?.id;
      seoSyncIdRef.current = launch?.id;
      if (!switched && seoDirty) return;
      setSeoT(eff.meta_title || ""); setSeoD(eff.meta_description || ""); setSeoDirty(false);
      // eslint-disable-next-line
    }, [launch?.id, eff.meta_title, eff.meta_description, seoDirty]);

    // Laatste getypte waarden, zodat saveMeta na de (async) save kan zien of er
    // tijdens het opslaan verder getypt is.
    const seoLatestRef = useRef({ t: "", d: "" });
    seoLatestRef.current = { t: seoT, d: seoD };
    async function saveMeta() {
      const t = seoT, d = seoD;
      await savePatch({ meta_title: t, meta_description: d });
      setLocalData(p => ({ ...(p || launch || {}), meta_title: t, meta_description: d }));
      // Is er tijdens het opslaan doorgetypt? Dan blijft het veld dirty, anders zou de
      // sync-effect de nieuwere tekst (o.a. leestekens) terugzetten naar de opgeslagen waarde.
      if (seoLatestRef.current.t !== t || seoLatestRef.current.d !== d) return;
      setSeoDirty(false); setSeoMsg("✓ Opgeslagen"); setTimeout(() => setSeoMsg(m => m === "✓ Opgeslagen" ? "" : m), 1600);
    }
    async function genKeywords() {
      if (!launch || kwBusy) return;
      setKwBusy(true);
      try {
        const { parsed } = await callGroq("Je bent een Nederlandse SEO-specialist. Antwoord UITSLUITEND met JSON.", `Geef 8 extra long-tail Nederlandse zoekwoorden voor de dienst "${launch.title}". Vermijd duplicaten van: ${(eff.zoekwoorden || []).join(", ")}. JSON: {"zoekwoorden":["..."]}`, { maxTokens: 400, temperature: 0.5 });
        const nw = Array.isArray(parsed && parsed.zoekwoorden) ? parsed.zoekwoorden.filter(Boolean) : [];
        if (nw.length) {
          const merged = Array.from(new Set([...(eff.zoekwoorden || []), ...nw])).slice(0, 30);
          await savePatch({ zoekwoorden: merged });
          setLocalData(p => ({ ...(p || launch || {}), zoekwoorden: merged }));
        }
      } catch (e) { /* stil — AI is best-effort */ }
      setKwBusy(false);
    }

    // Generieke inline-bewerk-laag voor de Positionering- en Landingspagina-tabs.
    // Bij wisselen van dienst: flush eerst openstaande wijzigingen van de vorige
    // dienst (vangnet naast de debounced autosave), daarna pas leegmaken.
    useEffect(() => {
      setEdits({});
      return () => {
        const p = pendingRef.current;
        if (!p || !p.id) return;
        const patch = {};
        if (p.edits && Object.keys(p.edits).length) Object.assign(patch, p.edits);
        if (p.seo) Object.assign(patch, p.seo);
        if (Object.keys(patch).length) {
          supabase.from("launch_modules")
            .update({ ...patch, updated_at: new Date().toISOString() })
            .eq("id", p.id)
            .then(() => {}, () => {});
        }
      };
      // eslint-disable-next-line
    }, [launch?.id]);
    const editsDirty = Object.keys(edits).length > 0;
    const fval = (f) => (edits[f] !== undefined ? edits[f] : (eff[f] || ""));
    const setF = (f, v) => setEdits(p => ({ ...p, [f]: v }));
    async function saveEdits() {
      if (!editsDirty || editsBusy) return;
      setEditsBusy(true);
      await savePatch(edits);
      setLocalData(p => ({ ...(p || launch || {}), ...edits }));
      setEdits({}); setEditsBusy(false);
    }

    // ─── Auto-opslaan ──────────────────────────────────────────────────────────
    // Inline-bewerkingen en SEO-velden worden automatisch (debounced) bewaard, én
    // geflusht zodra je de module verlaat of uitlogt (beide unmounten deze component).
    // Zo gaat er nooit content verloren bij wisselen van module of uitloggen.
    useEffect(() => {
      pendingRef.current = {
        id: sel,
        edits: editsDirty ? edits : {},
        seo: seoDirty ? { meta_title: seoT, meta_description: seoD } : null,
      };
    });
    // Debounced autosave van inline-velden (Positionering + Landingspagina)
    useEffect(() => {
      if (!editsDirty || editsBusy) return;
      const t = setTimeout(() => { saveEdits(); }, 1000);
      return () => clearTimeout(t);
      // eslint-disable-next-line
    }, [edits, editsDirty]);
    // Debounced autosave van de SEO meta-velden
    useEffect(() => {
      if (!seoDirty) return;
      const t = setTimeout(() => { saveMeta(); }, 1000);
      return () => clearTimeout(t);
      // eslint-disable-next-line
    }, [seoT, seoD, seoDirty]);
    // Vangnet: flush openstaande wijzigingen direct naar de DB bij verlaten/uitloggen.
    useEffect(() => {
      return () => {
        const p = pendingRef.current;
        if (!p || !p.id) return;
        const patch = {};
        if (p.edits && Object.keys(p.edits).length) Object.assign(patch, p.edits);
        if (p.seo) Object.assign(patch, p.seo);
        if (Object.keys(patch).length) {
          supabase.from("launch_modules")
            .update({ ...patch, updated_at: new Date().toISOString() })
            .eq("id", p.id)
            .then(() => {}, () => {});
        }
      };
      // eslint-disable-next-line
    }, []);
    async function genUSP() {
      if (!launch || uspBusy) return;
      setUspBusy(true);
      try {
        const { parsed } = await callGroq("Je bent een positioneringsexpert. Antwoord UITSLUITEND met JSON.", `Geef 3 extra scherpe, concrete USP's voor de dienst "${launch.title}". Vermijd duplicaten van: ${(eff.usp_lijst || []).join(" | ")}. JSON: {"usps":["..."]}`, { maxTokens: 400, temperature: 0.6 });
        const nu = Array.isArray(parsed && parsed.usps) ? parsed.usps.filter(Boolean) : [];
        if (nu.length) {
          const merged = Array.from(new Set([...(eff.usp_lijst || []), ...nu])).slice(0, 12);
          await savePatch({ usp_lijst: merged });
          setLocalData(p => ({ ...(p || launch || {}), usp_lijst: merged }));
        }
      } catch (e) { /* stil */ }
      setUspBusy(false);
    }

    // Echte screenshots van de live dienst via Microlink (gratis, geen key).
    const shotBase = () => (shotUrl || eff.test_link || "").trim();
    async function shotGrab(url, mobile) {
      const q = new URLSearchParams({ url, screenshot: "true", meta: "false", "viewport.width": mobile ? "390" : "1366", "viewport.height": mobile ? "844" : "860" });
      if (mobile) { q.set("viewport.isMobile", "true"); q.set("viewport.deviceScaleFactor", "2"); }
      const r = await fetch("https://api.microlink.io/?" + q.toString());
      const d = await r.json();
      if (d.status !== "success") throw new Error(d.message || "mislukt");
      return d.data?.screenshot?.url || "";
    }
    // Vind automatisch de interne pagina's/modules van de dienst (server-side crawl).
    async function crawlPages() {
      const base = shotBase();
      if (!/^https?:\/\//.test(base)) { setShotErr("Vul eerst een geldige https-URL van je dienst in."); return; }
      setCrawlBusy(true); setShotErr("");
      try {
        const r = await apiFetch("/api/groq", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ crawl: base }) });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error?.message || "crawl mislukt");
        const urls = Array.isArray(d.urls) ? d.urls : [];
        setShotPages(urls.join("\n"));
        if (urls.length < 10) setShotErr("Slechts " + urls.length + " pagina('s) gevonden (mogelijk een app achter login of JS-routing). Vul hieronder zelf de module-URLs aan tot min. 10.");
      } catch (e) { setShotErr("✗ " + (e.message || "crawl mislukt")); }
      setCrawlBusy(false);
    }
    // Maak screenshots van álle opgegeven pagina's (paden t.o.v. de basis, of volledige URLs).
    async function makeShots() {
      const base = shotBase();
      if (!/^https?:\/\//.test(base)) { setShotErr("Vul een geldige https-URL van je dienst in (of een testlink)."); return; }
      let lines = shotPages.split("\n").map(s => s.trim()).filter(Boolean);
      if (lines.length === 0) lines = [base];
      const urls = []; const seen = new Set();
      for (const ln of lines) {
        let u; try { u = /^https?:\/\//i.test(ln) ? ln : new URL(ln, base).href; } catch (e) { continue; }
        if (!seen.has(u)) { seen.add(u); urls.push(u); }
        if (urls.length >= 12) break;
      }
      setShooting(true); setShotErr(""); setShotGallery(urls.map(u => ({ url: u, img: "", error: "" })));
      for (let i = 0; i < urls.length; i++) {
        try { const img = await shotGrab(urls[i], false); setShotGallery(g => g.map((x, j) => j === i ? { ...x, img } : x)); }
        catch (e) { setShotGallery(g => g.map((x, j) => j === i ? { ...x, error: e.message || "mislukt" } : x)); }
      }
      setShooting(false);
    }

    // Echte video uit een script via /api/video (Replicate); pollt tot de clip klaar is.
    async function makeVideo(item) {
      // Gebruik de (eventueel handmatig aangepaste) promotekst uit de tussenstap;
      // valt terug op het oorspronkelijke script als er niets is bewerkt.
      const promo = String((videoPromos[item.id] ?? item.text) || "").trim();
      if (!promo) { setVidJobs(p => ({ ...p, [item.id]: { error: "Promotekst is leeg - vul eerst iets in." } })); return; }
      const secs = Number(videoSecs[item.id]) || 5;   // 5 of 10 sec
      setVidJobs(p => ({ ...p, [item.id]: { status: "starting" } }));
      try {
        const idem = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
        const r = await apiFetch("/api/video", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt: promo.slice(0, 500), mode: "text_to_video", params: { durationSeconds: secs, aspectRatio: "9:16" }, idempotencyKey: idem, userEmail: (user && user.email) || "" }),
        });
        const d = await r.json();
        if (!r.ok) { setVidJobs(p => ({ ...p, [item.id]: { error: /VIDEO_API_KEY/.test(d.error || "") ? "Video-generatie nog niet geconfigureerd — Replicate-token (VIDEO_API_KEY) ontbreekt." : (d.error || "Aanmaken mislukt") } })); return; }
        const jid = d.id;
        setVidJobs(p => ({ ...p, [item.id]: { status: d.status || "running", jid } }));
        const poll = async () => {
          try {
            const pr = await apiFetch("/api/video?id=" + encodeURIComponent(jid));
            const pd = await pr.json();
            if (pd.asset && pd.asset.url) { setVidJobs(p => ({ ...p, [item.id]: { status: "succeeded", url: pd.asset.url } })); return; }
            if (pd.status === "failed" || pd.status === "cancelled") { setVidJobs(p => ({ ...p, [item.id]: { error: pd.error?.message || "Video mislukt" } })); return; }
            setVidJobs(p => ({ ...p, [item.id]: { status: pd.status || "running", jid } }));
            setTimeout(poll, 4000);
          } catch (e) { setTimeout(poll, 4000); }
        };
        setTimeout(poll, 4000);
      } catch (e) { setVidJobs(p => ({ ...p, [item.id]: { error: e.message || "Mislukt" } })); }
    }
    const statCol = LAUNCH_STATUS_COL[eff.status] || { bg: C.border, fg: C.muted };

    // ── LIST VIEW ──
    if (!sel) {
      return (
        <div className="fu">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <h2 style={{ ...KOP_STIJL }}>Launch Module</h2>
            <div style={{ display: "flex", gap: 8 }}>
              <Bdg label={launches.filter(l => l.status === "Gelanceerd").length + " gelanceerd"} bg={C.greenDim} fg={C.green} />
              <Bdg label={launches.filter(l => l.status === "In Testfase").length + " in test"} bg={C.yellowDim} fg={C.yellow} />
            </div>
          </div>
          <div className="card" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 11, flexWrap: "wrap", background: C.blueDim, border: "1px solid " + C.blue + "33" }}>
            <span style={{ fontSize: 17 }}>🛒</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 700 }}>Store OS - testomgeving</p>
              {!storeEdit
                ? <p style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{storeUrl || "Nog geen test-URL ingesteld. Plak de link zodra Store OS online staat, dan kan Furgel testen."}</p>
                : <input autoFocus defaultValue={storeUrl} onKeyDown={e => { if (e.key === "Enter") { storeCancelRef.current = false; saveStoreUrl(e.target.value); } if (e.key === "Escape") { storeCancelRef.current = true; setStoreEdit(false); } }} onBlur={e => { if (storeCancelRef.current) { storeCancelRef.current = false; return; } saveStoreUrl(e.target.value); }} placeholder="https://store-os...." style={{ width: "100%", fontSize: 12, marginTop: 4 }} />}
            </div>
            {!storeEdit && storeUrl && <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="btn bp" style={{ fontSize: 12, textDecoration: "none" }}>▶ Open Store OS</a>}
            {!storeEdit && <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setStoreEdit(true)}>{storeUrl ? "Wijzig" : "Link instellen"}</button>}
          </div>
          {launches.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>
              <p style={{ fontSize: 30, marginBottom: 14, color: C.dim }}>◇</p>
              <p style={{ marginBottom: 6 }}>Nog geen launches.</p>
              <p style={{ fontSize: 12 }}>Ga naar Bouwpipeline → Test Fase → Start Launch Module.</p>
              <button className="btn bg" style={{ marginTop: 14 }} onClick={() => setView("pipeline")}>Naar Bouwpipeline →</button>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {launches.map(l => {
              const sc = LAUNCH_STATUS_COL[l.status] || { bg: C.border, fg: C.muted };
              const pipeItem = pipeline.find(p => p.id === l.pipeline_item_id);
              return (
                <div key={l.id} className="card" style={{ cursor: "pointer" }} onClick={() => { setSel(l.id); setTab("testfase"); }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{l.title}</p>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <Bdg label={l.status} bg={sc.bg} fg={sc.fg} />
                        {l.test_status && <Bdg label={l.test_status} bg={l.test_status === "Goedgekeurd" ? C.greenDim : l.test_status === "Afgekeurd" ? C.redDim : l.test_status === "In test" ? C.yellowDim : C.border} fg={l.test_status === "Goedgekeurd" ? C.green : l.test_status === "Afgekeurd" ? C.red : l.test_status === "In test" ? C.yellow : C.muted} />}
                        {pipeItem && <span style={{ fontSize: 11, color: C.muted }}>Pipeline: {pipeItem.title}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontSize: 11, color: C.muted }}>{l.created_at?.slice(0, 10) || ""}</p>
                      <p style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>Verantw.: {USERS[l.verantwoordelijke]?.name || l.verantwoordelijke}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, fontSize: 11, color: C.dim, flexWrap: "wrap" }}>
                    {l.launch_package && Object.keys(l.launch_package).length > 0 && <span style={{ color: C.green }}>✓ Launch Package gegenereerd</span>}
                    {l.test_link && <span>{l.test_link.slice(0, 40)}{l.test_link.length > 40 ? "..." : ""}</span>}
                    {l.launch_datum && <span>{l.launch_datum}</span>}
                    {feedbackCounts[l.id] > 0 && <span style={{ color: C.yellow, fontWeight: 600 }}>{feedbackCounts[l.id]} open feedback</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // ── DETAIL VIEW ──
    if (!launch && !localData) { setSel(null); return null; }

    return (
      <div className="fu">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14, flexWrap: "wrap" }}>
          <button className="btn bg" onClick={() => setSel(null)}>← Terug</button>
          <h2 style={{ ...KOP_STIJL, fontSize: 16 }}>{eff.title}</h2>
          <Bdg label={eff.status || "In Testfase"} bg={statCol.bg} fg={statCol.fg} />
          {eff.test_status && <Bdg label={eff.test_status} bg={eff.test_status === "Goedgekeurd" ? C.greenDim : eff.test_status === "Afgekeurd" ? C.redDim : eff.test_status === "In test" ? C.yellowDim : C.border} fg={eff.test_status === "Goedgekeurd" ? C.green : eff.test_status === "Afgekeurd" ? C.red : eff.test_status === "In test" ? C.yellow : C.muted} />}
          {saving && <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>Opslaan...</span>}
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16, padding: "11px 14px", background: C.surface, borderRadius: 8, border: "1px solid " + C.border }}>
          <button className="btn bp" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }} onClick={() => { setTab("testfase"); saveLinkAndStatus(); createTasksVoorFurgel(); }} disabled={generating || saving}>
            Testfase starten
          </button>
          {eff.test_link && <button className="btn bg" style={{ fontSize: 11 }} onClick={() => window.open(eff.test_link, "_blank")}>Testlink openen</button>}
          <button className="btn bg" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }} onClick={generateTestResultaat} disabled={generating}>
            {generating && genStep.includes("Testresultaat") ? "⟳ Analyseren..." : "Testresultaat genereren (AI)"}
          </button>
          <button className="btn bg" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }} onClick={generateLaunchPackage} disabled={generating}>
            {generating ? "⟳ " + genStep : "Launch Package genereren"}
          </button>
          <button className="btn bg" style={{ fontSize: 11 }} onClick={createTasksVoorFurgel} disabled={saving}>Taken voor Furgel</button>
          {eff.status === "Klaar voor Acceptatie" && <button className="btn bp" style={{ fontSize: 11, background: C.green, color: "#000" }} onClick={acceptByFurgel}>✓ Goedkeuren voor Launch (Furgel)</button>}
          {eff.status === "Klaar voor Launch" && <button className="btn bp" style={{ fontSize: 11, background: C.cyan, color: "#000" }} onClick={publishLaunch}>Publiceer Launch</button>}
        </div>

        {/* Tab Navigation */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
          {LAUNCH_TABS.map(t => (
            <span key={t.id} className={"tab" + (tab === t.id ? " on" : "")} onClick={() => setTab(t.id)} style={{ fontSize: 10 }}>
              {t.icon} {t.l}
            </span>
          ))}
        </div>

        {/* ── Tab: Studio (kindvriendelijke goedkeur-feed) ── */}
        {tab === "studio" && (() => {
          const hasPkg = eff.launch_package && Object.keys(eff.launch_package).length > 0;
          const copy = (text, id) => { try { navigator.clipboard?.writeText(text); } catch (e) {} setCopiedId(id); setTimeout(() => setCopiedId(c => c === id ? "" : c), 1500); };
          const postToVisual = (text, badge) => {
            const clean = String(text || "").replace(/#[^\s#]+/g, "").trim();
            const head = (clean.split(/(?:\. |\n)/)[0] || clean).slice(0, 80);
            const rest = clean.slice(head.length).trim().split(/\n+/).map(l => l.trim()).filter(Boolean).slice(0, 3).join("\n").slice(0, 200);
            return { kind: "social_post", dim: { w: 1080, h: 1080 }, slides: [{ badge, headline: head, body: rest, cta: eff.cta || "Meer weten?" }] };
          };
          const visualItems = [];
          [["instagram_posts", "Instagram"], ["linkedin_posts", "LinkedIn"], ["facebook_posts", "Facebook"]].forEach(([key, label]) => {
            (eff[key] || []).forEach((txt, i) => { if (String(txt || "").trim()) visualItems.push({ id: key + i, label, text: String(txt), visual: postToVisual(txt, label) }); });
          });
          (eff.carrousels || []).forEach((c, ci) => {
            const ss = (c.slides || []).filter(Boolean);
            if (!ss.length) return;
            const slides = ss.map((s, si) => ({ headline: si === 0 ? (c.titel || String(s).slice(0, 40)) : "", body: String(s || "").slice(0, 200), cta: si === ss.length - 1 ? (eff.cta || "Neem contact op") : "" }));
            visualItems.push({ id: "carr" + ci, label: "Carrousel", text: (c.titel ? c.titel + "\n\n" : "") + ss.join("\n"), visual: { kind: "carousel", dim: { w: 1080, h: 1080 }, slides }, carousel: true });
          });
          const videoItems = [];
          [["tiktok_scripts", "TikTok"], ["youtube_shorts", "YouTube Shorts"]].forEach(([key, label]) => {
            (eff[key] || []).forEach((txt, i) => { if (String(txt || "").trim()) videoItems.push({ id: key + i, label, text: String(txt) }); });
          });
          const okCount = Object.values(approved).filter(Boolean).length;
          return (
            <div className="fu">
              <div className="card" style={{ marginBottom: 14, background: C.blueDim, border: "1px solid " + C.blue + "33" }}>
                <h3 style={{ fontFamily: "monospace", fontSize: 15, marginBottom: 6 }}>Launch Studio — {launch?.title || "kies een dienst"}</h3>
                <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>Eén knop maakt je complete launch. Daarna zie je per kanaal de échte post: een kant-en-klaar beeld + de tekst. Keur goed wat je mooi vindt, kopieer de tekst, download het beeld en zet het op je socials.</p>
              </div>

              <div className="card" style={{ marginBottom: 14 }}>
                <p className="lbl" style={{ marginBottom: 8 }}>Over deze dienst — de agent bouwt alleen hierop</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <p style={{ fontSize: 11, color: C.dim, marginBottom: 4 }}>Voor wie is het?</p>
                    <select value={launch?.publiek || ""} disabled={!launch} onChange={e => patchField({ publiek: e.target.value })}>
                      <option value="">Kies het publiek…</option>
                      <option value="zakelijk">Zakelijk (bedrijven, MKB)</option>
                      <option value="consumenten">Consumenten</option>
                      <option value="ouders_kinderen">Kinderen (de communicatie richt zich op ouders en leerkrachten)</option>
                    </select>
                  </div>
                  <div>
                    <p style={{ fontSize: 11, color: C.dim, marginBottom: 4 }}>Wat is de dienst? Alleen feiten: wat het is, voor wie, wat het kost, wat het wel en niet doet.</p>
                    <textarea rows={7} disabled={!launch} value={omschrDraft ?? (launch?.dienst_omschrijving || "")} onChange={e => setOmschrDraft(e.target.value)}
                      onBlur={() => { if (omschrDraft !== null && launch && omschrDraft !== (launch.dienst_omschrijving || "")) patchField({ dienst_omschrijving: omschrDraft }); setOmschrDraft(null); }}
                      placeholder="Bijvoorbeeld: een gratis te spelen leerspel voor kinderen van 3 tot 9 jaar. Geen account, geen reclame. Extra eilanden alleen via een abonnement dat ouders afsluiten…" style={{ fontSize: 12 }} />
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <button className="btn bg" style={{ fontSize: 11 }} disabled={!launch || feitenBezig} onClick={async () => {
                      const url = (window.prompt("Van welke pagina wil je de feiten ophalen? Een landingspagina met uitleg werkt beter dan de app zelf.", launch.test_link || "") || "").trim();
                      if (!url) return;
                      if (!/^https?:\/\//i.test(url)) { setGenErr("Vul een volledig adres in dat met https:// begint."); return; }
                      setFeitenBezig(true);
                      try {
                        const r = await apiFetch("/api/check-link?url=" + encodeURIComponent(url));
                        const d = await r.json();
                        if (!d.ok || !(d.zichtbare_tekst || d.meta_description)) { setGenErr("De testlink gaf geen leesbare pagina terug. Vul de omschrijving zelf in."); }
                        else {
                          const tekst = ["Pagina: " + (d.title || url), d.meta_description ? "Beschrijving: " + d.meta_description : "", "", "Tekst op de pagina:", d.zichtbare_tekst].filter(x => x !== "").join("\n");
                          setOmschrDraft(((launch.dienst_omschrijving || "").trim() ? launch.dienst_omschrijving.trim() + "\n\n" : "") + tekst);
                          setGenErr("");
                        }
                      } catch (e) { setGenErr("Feiten ophalen mislukt: " + (e?.message || "?")); }
                      setFeitenBezig(false);
                    }}>{feitenBezig ? "Ophalen…" : "↧ Haal feiten uit de testlink"}</button>
                    <span style={{ fontSize: 10, color: C.dim }}>Controleer en pas de tekst aan voordat je opslaat (klik buiten het veld). Haal weg wat niet klopt of nog niet waar is.</span>
                  </div>
                </div>
              </div>

              {!hasPkg ? (
                <div className="card" style={{ textAlign: "center", padding: "44px 20px" }}>
                  <p style={{ fontSize: 36, marginBottom: 12, color: C.dim }}>▶</p>
                  <p style={{ fontSize: 14, color: C.text, marginBottom: 6 }}>Nog geen content gemaakt.</p>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Klik op de knop — de OS schrijft én ontwerpt alles voor je (±halve minuut).</p>
                  <button className="btn bp" style={{ fontSize: 14, padding: "12px 24px" }} onClick={generateLaunchPackage} disabled={generating || !launch}>{generating ? "⟳ " + (genStep || "Bezig…") : "▶ Maak mijn launch"}</button>
                  {!launch && <p style={{ fontSize: 11, color: C.yellow, marginTop: 12 }}>Kies eerst een dienst bovenaan de Launch Module.</p>}
                  {genErr && <p style={{ fontSize: 12, color: C.red, marginTop: 14, lineHeight: 1.5 }}>{genErr}</p>}
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                    <span className="lbl" style={{ margin: 0 }}>{visualItems.length + videoItems.length} content-items · {okCount} goedgekeurd</span>
                    <button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} onClick={generateLaunchPackage} disabled={generating}>{generating ? "⟳ " + (genStep || "Bezig…") : "↻ Opnieuw maken"}</button>
                  </div>

                  {genErr && <p style={{ fontSize: 12, color: C.red, marginBottom: 12 }}>{genErr}</p>}

                  {visualItems.length === 0 && <p style={{ fontSize: 12, color: C.dim, marginBottom: 14 }}>Geen social-posts in dit pakket — maak opnieuw of vul de dienst-beschrijving aan.</p>}

                  {visualItems.map(it => {
                    const ok = !!approved[it.id];
                    return (
                      <div key={it.id} className="card" style={{ marginBottom: 14, borderColor: ok ? C.green + "66" : C.border }}>
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                          <div style={{ width: 230, flexShrink: 0, borderRadius: 8, overflow: "hidden", border: "1px solid " + C.border }} dangerouslySetInnerHTML={{ __html: growthSlideSVG(it.visual, 0, false) }} />
                          <div style={{ flex: 1, minWidth: 250 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <Bdg label={it.label} bg={C.blueDim} fg={C.blue} />
                              {ok && <span style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>✓ Klaar om te plaatsen</span>}
                            </div>
                            <p style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: 150, overflow: "auto", marginBottom: 11 }}>{it.text}</p>
                            <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                              <button className="btn bg" style={{ fontSize: 11 }} onClick={() => copy(it.text, it.id)}>{copiedId === it.id ? "✓ Gekopieerd" : "⎘ Kopieer tekst"}</button>
                              <button className="btn bg" style={{ fontSize: 11 }} onClick={() => downloadGrowthPNG(it.visual, 0, (launch?.title || "post") + "-" + it.id + ".png")}>↓ Download beeld</button>
                              {it.carousel && <button className="btn bg" style={{ fontSize: 11 }} onClick={async () => { for (let k = 0; k < it.visual.slides.length; k++) await downloadGrowthPNG(it.visual, k, (launch?.title || "carrousel") + "-" + (k + 1) + ".png"); }}>↓ Alle slides</button>}
                              <button className={"btn " + (ok ? "bg" : "bp")} style={{ fontSize: 11, marginLeft: "auto" }} onClick={() => setApproved(p => ({ ...p, [it.id]: !p[it.id] }))}>{ok ? "↺ Terug" : "✓ Goedkeuren"}</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {videoItems.length > 0 && (
                    <div className="card" style={{ marginBottom: 14 }}>
                      <p className="lbl" style={{ marginBottom: 4 }}>Tussenstap - verfijn de promo per video</p>
                      <p style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>Pas per video de promotekst aan om meer richting mee te geven. Precies wat hier staat wordt gebruikt voor de definitieve clip (±5s, 9:16). Klik daarna op "Maak video"; die verschijnt na ±25s met afspeler en download.</p>
                      {videoItems.map(it => {
                        const vj = vidJobs[it.id];
                        const busy = vj && (vj.status === "starting" || vj.status === "running" || vj.status === "queued");
                        const promo = videoPromos[it.id] ?? it.text;
                        const edited = videoPromos[it.id] !== undefined && videoPromos[it.id] !== it.text;
                        return (
                          <div key={it.id} style={{ borderTop: "1px solid " + C.border, padding: "10px 0" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                              <Bdg label={it.label} bg={C.redDim} fg={C.red} />
                              {edited && <span style={{ fontSize: 10, color: C.yellow, fontWeight: 700 }}>● aangepast</span>}
                              {edited && <button className="btn bg" style={{ fontSize: 10 }} onClick={() => setVideoPromos(p => { const n = { ...p }; delete n[it.id]; return n; })}>↺ Origineel</button>}
                              <button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => copy(promo, it.id)}>{copiedId === it.id ? "✓ Gekopieerd" : "⎘ Kopieer"}</button>
                              <select value={videoSecs[it.id] || 5} onChange={e => setVideoSecs(p => ({ ...p, [it.id]: Number(e.target.value) }))} title="Cliplengte" style={{ fontSize: 10, width: "auto", padding: "3px 6px" }}>
                                <option value={5}>5 sec &middot; €0,25</option>
                                <option value={10}>10 sec &middot; €0,50</option>
                              </select>
                              <button className="btn bp" style={{ fontSize: 10 }} disabled={busy} onClick={() => makeVideo(it)}>{busy ? "⟳ Video maken…" : (vj && vj.url ? "↻ Opnieuw" : "▶ Maak video")}</button>
                            </div>
                            <textarea value={promo} onChange={e => setVideoPromos(p => ({ ...p, [it.id]: e.target.value }))} rows={4} style={{ width: "100%", fontSize: 12, lineHeight: 1.55, resize: "vertical" }} placeholder="Promotekst voor deze video…" />
                            {busy && <p style={{ fontSize: 11, color: C.blue, marginTop: 6 }}>Bezig met genereren — dit kan ±1 minuut duren…</p>}
                            {vj && vj.error && <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>⚠ {vj.error}</p>}
                            {vj && vj.url && <div style={{ marginTop: 8 }}><video src={vj.url} controls style={{ width: 200, maxWidth: "100%", borderRadius: 8, border: "1px solid " + C.border, display: "block" }} /><a href={vj.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: C.blue }}>↓ Download video</a></div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* ── Tab: Testfase ── */}
        {tab === "testfase" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Testlink</p>
                <input value={testLinkInput} onChange={e => setTestLinkInput(e.target.value)} placeholder="https://test.dienst.nl" style={{ marginBottom: 8 }} />
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  <button className="btn bp" style={{ fontSize: 11, flex: 1, minWidth: 90 }} onClick={() => { const v = testLinkInput.trim(); savePatch({ test_link: v || null, test_status: v ? "In test" : "Nog niet getest" }); setLocalData(p => ({ ...p, test_link: v || null })); }}>{testLinkInput.trim() ? "Opslaan" : "Wissen"}</button>
                  {testLinkInput.trim() && <button className="btn bg" style={{ fontSize: 11 }} onClick={() => window.open(testLinkInput.trim(), "_blank")}>Openen →</button>}
                </div>
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Teststatus</p>
                <select value={eff.test_status || "Nog niet getest"} onChange={e => patchField({ test_status: e.target.value })} style={{ marginBottom: 0 }}>
                  {["Nog niet getest", "In test", "Goedgekeurd", "Afgekeurd"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <LaunchFeedbackThread launchId={launch?.id} user={user} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <p className="lbl">Testchecklist ({(eff.checklist_status || []).filter(Boolean).length}/{(eff.test_checklist || []).length})</p>
                  <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={generateTestChecklist} disabled={generating}>
                    {generating ? "⟳ Laden..." : "AI Genereren"}
                  </button>
                </div>
                {(eff.test_checklist || []).length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Klik op "AI Genereren" om de checklist aan te maken.</p>}
                {(eff.test_checklist || []).map((item, i) => (
                  <div key={i} onClick={() => toggleChecklistItem(i)} style={{ display: "flex", gap: 9, padding: "7px 0", borderBottom: "1px solid " + C.border, cursor: "pointer", alignItems: "flex-start" }}>
                    <span style={{ color: (eff.checklist_status || [])[i] ? C.green : C.dim, flexShrink: 0, marginTop: 1, fontSize: 14 }}>{(eff.checklist_status || [])[i] ? "☑" : "☐"}</span>
                    <span style={{ fontSize: 12, color: (eff.checklist_status || [])[i] ? C.text : C.muted, lineHeight: 1.5, textDecoration: (eff.checklist_status || [])[i] ? "line-through" : "none" }}>{item}</span>
                  </div>
                ))}
              </div>
              {eff.test_status === "Afgekeurd" && eff.reden_afkeuring && (
                <div className="card" style={{ borderColor: C.red, background: C.redDim }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
                    <span style={{ fontSize: 16 }}>✗</span>
                    <p className="lbl" style={{ color: C.red, margin: 0 }}>Reden van afkeuring</p>
                  </div>
                  <p style={{ fontSize: 13, lineHeight: 1.7, color: C.text, whiteSpace: "pre-wrap", fontWeight: 500 }}>{eff.reden_afkeuring}</p>
                  {Array.isArray(eff.aanbevelingen) && eff.aanbevelingen.length > 0 && (
                    <>
                      <p className="lbl" style={{ marginTop: 12, marginBottom: 7, color: C.red }}>Aanbevelingen om te herstellen</p>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7, color: C.muted }}>
                        {eff.aanbevelingen.map((a, i) => <li key={i} style={{ marginBottom: 4 }}>{a}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}
              {eff.test_diagnostiek && Object.keys(eff.test_diagnostiek || {}).length > 0 && (
                <div className="card">
                  <p className="lbl" style={{ marginBottom: 9 }}>Live diagnostiek (laatste check)</p>
                  <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 10px", fontSize: 11, fontFamily: "monospace", lineHeight: 1.6 }}>
                    <span style={{ color: C.dim }}>HTTP:</span>
                    <span style={{ color: eff.test_diagnostiek.ok ? C.green : C.red, fontWeight: 600 }}>{eff.test_diagnostiek.status || 0} {eff.test_diagnostiek.statusText || ""}</span>
                    <span style={{ color: C.dim }}>Eind-URL:</span>
                    <span style={{ color: C.muted, wordBreak: "break-all" }}>{eff.test_diagnostiek.final_url || "—"}{eff.test_diagnostiek.redirected ? " (redirect)" : ""}</span>
                    <span style={{ color: C.dim }}>Title:</span>
                    <span style={{ color: C.muted }}>{eff.test_diagnostiek.title || "—"}</span>
                    <span style={{ color: C.dim }}>Latency:</span>
                    <span style={{ color: C.muted }}>{eff.test_diagnostiek.elapsed_ms || "?"}ms</span>
                    <span style={{ color: C.dim }}>Type:</span>
                    <span style={{ color: C.muted }}>{eff.test_diagnostiek.content_type || "—"}</span>
                  </div>
                  {Array.isArray(eff.test_diagnostiek.detected_errors) && eff.test_diagnostiek.detected_errors.length > 0 && (
                    <div style={{ marginTop: 9, padding: "7px 9px", borderRadius: 6, background: C.redDim, border: "1px solid " + C.red }}>
                      <p style={{ fontSize: 11, color: C.red, fontWeight: 600, marginBottom: 4 }}>Gedetecteerde foutpatronen</p>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: C.text, lineHeight: 1.6 }}>
                        {eff.test_diagnostiek.detected_errors.map((e, i) => <li key={i}>{e}</li>)}
                      </ul>
                    </div>
                  )}
                  {eff.test_diagnostiek.hint && (
                    <p style={{ marginTop: 8, fontSize: 11, color: C.yellow, fontStyle: "italic" }}>{eff.test_diagnostiek.hint}</p>
                  )}
                </div>
              )}
              {eff.test_resultaat && (
                <div className="card">
                  <p className="lbl" style={{ marginBottom: 9 }}>Testresultaat (AI-analyse)</p>
                  <p style={{ fontSize: 12, lineHeight: 1.8, color: C.muted, whiteSpace: "pre-wrap" }}>{eff.test_resultaat}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Tab: Positionering ── */}
        {tab === "positionering" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {editsDirty && <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 12, color: C.cyan }}>{editsBusy ? "⟳ Automatisch opslaan…" : "● Wijzigingen worden automatisch opgeslagen"}</span><button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} disabled={editsBusy} onClick={saveEdits}>Nu opslaan</button></div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Doelgroep</p><textarea rows={3} value={fval("doelgroep")} onChange={e => setF("doelgroep", e.target.value)} placeholder="Voor wie is dit? (klik om te bewerken)" /></div>
                <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Pijnpunten</p><textarea rows={3} value={fval("pijnpunten")} onChange={e => setF("pijnpunten", e.target.value)} placeholder="Welk probleem los je op?" /></div>
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><p className="lbl" style={{ margin: 0 }}>Value Proposition</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("value_proposition"))} title="Kopieer">⎘</button></div>
                  <textarea rows={2} value={fval("value_proposition")} onChange={e => setF("value_proposition", e.target.value)} placeholder="Unieke waardepropositie in 1-2 zinnen" />
                </div>
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><p className="lbl" style={{ margin: 0 }}>Elevator Pitch</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("elevator_pitch"))} title="Kopieer">⎘</button></div>
                  <textarea rows={4} value={fval("elevator_pitch")} onChange={e => setF("elevator_pitch", e.target.value)} placeholder="Overtuigende 60-seconden pitch" style={{ fontStyle: "italic" }} />
                </div>
              </div>
              <div className="card" style={{ alignSelf: "flex-start" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                  <p className="lbl" style={{ margin: 0 }}>USP's ({(eff.usp_lijst || []).length}) — klik om te kopiëren</p>
                  <button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} disabled={uspBusy || !launch} onClick={genUSP}>{uspBusy ? "⟳…" : "+ Extra USP (AI)"}</button>
                </div>
                {(eff.usp_lijst || []).length === 0 && <p style={{ fontSize: 12, color: C.dim }}>—</p>}
                {(eff.usp_lijst || []).map((u, i) => (
                  <div key={i} onClick={() => navigator.clipboard?.writeText(u)} title="Klik om te kopiëren" style={{ display: "flex", gap: 9, padding: "9px 0", borderBottom: "1px solid " + C.border, cursor: "pointer" }}>
                    <span style={{ color: C.green, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                    <span style={{ fontSize: 13, lineHeight: 1.5 }}>{u}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: SEO ── */}
        {tab === "seo" && (() => {
          const kws = eff.zoekwoorden || [];
          const inText = (s) => kws.some(k => k && String(s || "").toLowerCase().includes(String(k).toLowerCase()));
          const checks = [
            { ok: seoT.length >= 30 && seoT.length <= 60, l: "Titel 30–60 tekens" },
            { ok: seoD.length >= 70 && seoD.length <= 160, l: "Beschrijving 70–160 tekens" },
            { ok: inText(seoT), l: "Zoekwoord in de titel" },
            { ok: inText(seoD), l: "Zoekwoord in de beschrijving" },
            { ok: kws.length >= 4, l: "Minstens 4 zoekwoorden" },
            { ok: (eff.faq || []).length >= 3, l: "Minstens 3 FAQ-items" },
            { ok: (eff.seo_blog || "").length >= 500, l: "SEO-blog ≥ 500 tekens" },
            { ok: !!eff.schema_markup, l: "Schema-markup aanwezig" },
          ];
          const score = Math.round(checks.filter(c => c.ok).length / checks.length * 100);
          const scoreColor = score >= 75 ? C.green : score >= 45 ? C.yellow : C.red;
          const displayUrl = (eff.test_link || "https://dif3r3nt.nl/" + String(launch?.title || "dienst").toLowerCase().replace(/[^a-z0-9]+/g, "-")).replace(/^https?:\/\//, "");
          const metaHtml = `<title>${seoT}</title>\n<meta name="description" content="${seoD}">\n<meta property="og:title" content="${seoT}">\n<meta property="og:description" content="${seoD}">`;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
              <div className="card">
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                  <p className="lbl" style={{ margin: 0 }}>Google-preview — live & bewerkbaar</p>
                  {seoMsg && <span style={{ fontSize: 11, color: C.green }}>{seoMsg}</span>}
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <button className="btn bg" style={{ fontSize: 11 }} onClick={() => { navigator.clipboard?.writeText(metaHtml); setSeoMsg("✓ Meta-tags gekopieerd"); setTimeout(() => setSeoMsg(""), 1600); }}>⎘ Meta-tags (HTML)</button>
                    <button className="btn bp" style={{ fontSize: 11 }} disabled={!seoDirty} onClick={saveMeta}>Opslaan</button>
                  </div>
                </div>
                <div style={{ background: "#fff", borderRadius: 8, padding: "13px 16px", marginBottom: 12 }}>
                  <p style={{ fontSize: 12, color: "#202124", marginBottom: 1 }}>{displayUrl}</p>
                  <p style={{ fontSize: 18, color: "#1a0dab", lineHeight: 1.3, margin: "1px 0 2px" }}>{(seoT || "Titel van je pagina").slice(0, 65)}</p>
                  <p style={{ fontSize: 13, color: "#4d5156", lineHeight: 1.45 }}>{(seoD || "De meta-beschrijving van je pagina verschijnt hier zodra je 'm invult.").slice(0, 165)}</p>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Meta title <span style={{ color: seoT.length > 60 ? C.red : seoT.length >= 30 ? C.green : C.yellow }}>· {seoT.length}/60</span></p>
                    <input value={seoT} onChange={e => { setSeoT(e.target.value); setSeoDirty(true); }} placeholder="Titel (30–60 tekens)" />
                  </div>
                  <div>
                    <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Meta description <span style={{ color: seoD.length > 160 ? C.red : seoD.length >= 70 ? C.green : C.yellow }}>· {seoD.length}/160</span></p>
                    <textarea rows={2} value={seoD} onChange={e => { setSeoD(e.target.value); setSeoDirty(true); }} placeholder="Beschrijving (70–160 tekens)" />
                  </div>
                </div>
              </div>

              <div className="card">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <p className="lbl" style={{ margin: 0 }}>SEO-score — live</p>
                  <span style={{ marginLeft: "auto", fontFamily: "monospace", fontWeight: 700, fontSize: 18, color: scoreColor }}>{score}/100</span>
                </div>
                <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: "hidden", marginBottom: 12 }}><div style={{ width: score + "%", height: "100%", background: scoreColor, transition: "width .3s" }} /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 14px" }}>
                  {checks.map((c, i) => <div key={i} style={{ display: "flex", gap: 7, fontSize: 12, color: c.ok ? C.text : C.muted }}><span style={{ color: c.ok ? C.green : C.dim }}>{c.ok ? "✓" : "○"}</span>{c.l}</div>)}
                </div>
              </div>

              <div className="card">
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                  <p className="lbl" style={{ margin: 0 }}>Zoekwoorden ({kws.length}) — klik om te kopiëren</p>
                  <button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} disabled={kwBusy || !launch} onClick={genKeywords}>{kwBusy ? "⟳ Genereren…" : "+ Extra zoekwoorden (AI)"}</button>
                </div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {kws.length === 0 && <span style={{ color: C.dim, fontSize: 12 }}>—</span>}
                  {kws.map((k, i) => <span key={i} onClick={() => navigator.clipboard?.writeText(k)} title="Klik om te kopiëren" style={{ padding: "3px 10px", borderRadius: 12, background: C.blueDim, color: C.blue, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{k}</span>)}
                </div>
              </div>

              {eff.seo_blog && (
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
                    <p className="lbl" style={{ margin: 0 }}>SEO Blog</p>
                    <button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(eff.seo_blog)}>⎘ Kopieer</button>
                  </div>
                  <div style={{ maxHeight: 350, overflowY: "auto", fontSize: 13, color: C.muted, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>{eff.seo_blog}</div>
                </div>
              )}

              {(eff.faq || []).length > 0 && (
                <div className="card">
                  <p className="lbl" style={{ marginBottom: 6 }}>FAQ ({eff.faq.length}) — klik om te openen</p>
                  {eff.faq.map((f, i) => (
                    <div key={i} style={{ borderBottom: "1px solid " + C.border }}>
                      <div onClick={() => setOpenFaq(openFaq === i ? -1 : i)} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "10px 0", cursor: "pointer" }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: openFaq === i ? C.blue : C.text }}>{f.vraag}</p>
                        <span style={{ color: C.muted, flexShrink: 0, fontWeight: 700 }}>{openFaq === i ? "−" : "+"}</span>
                      </div>
                      {openFaq === i && <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, padding: "0 0 11px" }}>{f.antwoord}</p>}
                    </div>
                  ))}
                </div>
              )}

              {eff.schema_markup && (
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <p className="lbl" style={{ margin: 0 }}>Schema Markup (JSON-LD)</p>
                    <button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(eff.schema_markup)}>⎘ Kopieer</button>
                  </div>
                  <pre style={{ fontSize: 11, color: C.green, background: C.bg, padding: 12, borderRadius: 6, overflowX: "auto", lineHeight: 1.7 }}>{eff.schema_markup}</pre>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Tab: Landingspagina ── */}
        {tab === "landingspagina" && (
          <div>
            <div className="card" style={{ marginBottom: 13 }}>
              <p className="lbl" style={{ marginBottom: 8 }}>Echte screenshots — meerdere modules van je dienst</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                <input value={shotUrl} onChange={e => setShotUrl(e.target.value)} placeholder={eff.test_link || "https://jouw-dienst.nl"} style={{ flex: 1, minWidth: 220 }} />
                <button className="btn bg" style={{ fontSize: 12 }} disabled={crawlBusy} onClick={crawlPages}>{crawlBusy ? "⟳ Zoeken…" : "Vind pagina's"}</button>
                <button className="btn bp" style={{ fontSize: 12 }} disabled={shooting} onClick={makeShots}>{shooting ? "⟳ Maken…" : "Maak screenshots"}</button>
              </div>
              <textarea value={shotPages} onChange={e => setShotPages(e.target.value)} rows={4} placeholder={"Eén pagina/pad per regel — bijv.\n/dashboard\n/instellingen\n/facturen\nhttps://jouw-dienst.nl/over-ons"} style={{ fontSize: 12, fontFamily: "monospace", marginBottom: 6 }} />
              <p style={{ fontSize: 10, color: C.dim, marginBottom: 10 }}>Klik "Vind pagina's" om de modules automatisch op te halen, of vul ze zelf in (min. 10 voor een volledige set). Modules achter login? Plak de directe URLs. Max 12 per keer.</p>
              {shotErr && <p style={{ fontSize: 11, color: C.yellow, marginBottom: 8 }}>{shotErr}</p>}
              {shotGallery.length > 0 && (
                <>
                  <p style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{shotGallery.filter(s => s.img).length} / {shotGallery.length} klaar</p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
                    {shotGallery.map((s, i) => (
                      <div key={i} style={{ border: "1px solid " + C.border, borderRadius: 8, overflow: "hidden", background: C.bg }}>
                        {s.img ? <a href={s.img} target="_blank" rel="noopener noreferrer"><img src={s.img} alt={s.url} style={{ width: "100%", display: "block" }} /></a>
                          : s.error ? <div style={{ padding: 14, fontSize: 10, color: C.yellow }}>⚠ {s.error}</div>
                            : <div style={{ padding: 14, fontSize: 11, color: C.muted }}>⟳ laden…</div>}
                        <p style={{ fontSize: 9, color: C.dim, padding: "5px 7px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace" }} title={s.url}>{(s.url || "").replace(/^https?:\/\/[^/]+/, "") || "/"}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="card" style={{ marginBottom: 13 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <p className="lbl" style={{ margin: 0 }}>Landingspagina — live preview & bewerken</p>
                {editsDirty && <button className="btn bp" style={{ fontSize: 11, marginLeft: "auto" }} disabled={editsBusy} onClick={saveEdits}>{editsBusy ? "Opslaan…" : "Opslaan"}</button>}
              </div>
              <div style={{ borderRadius: 10, border: "1px solid " + C.border, overflow: "hidden", marginBottom: 12 }}>
                <div style={{ background: "linear-gradient(180deg," + C.surfaceHigh + "," + C.bg + ")", padding: "26px 22px", textAlign: "center" }}>
                  <p style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1.25, marginBottom: 8 }}>{fval("hero") || "Je hero-headline"}</p>
                  <p style={{ fontSize: 13, color: C.yellow, fontStyle: "italic", marginBottom: 16 }}>{fval("hook") || "Je pakkende hook"}</p>
                  <span style={{ display: "inline-block", background: C.blue, color: "#fff", fontWeight: 700, fontSize: 13, padding: "10px 22px", borderRadius: 8 }}>{fval("cta") || "Call-to-action"}</span>
                  {(eff.voordelen || []).length > 0 && <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 18 }}>{(eff.voordelen || []).slice(0, 4).map((v, i) => <span key={i} style={{ fontSize: 11, color: C.muted, background: C.surface, border: "1px solid " + C.border, borderRadius: 16, padding: "4px 11px" }}>✓ {v}</span>)}</div>}
                </div>
                {(eff.social_proof || []).length > 0 && <div style={{ padding: "10px 16px", borderTop: "1px solid " + C.border, fontSize: 11, color: C.muted, textAlign: "center", fontStyle: "italic" }}>"{eff.social_proof[0]}"</div>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Hero-headline</p><input value={fval("hero")} onChange={e => setF("hero", e.target.value)} placeholder="Pakkende hoofdkop" /></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                  <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Hook</p><input value={fval("hook")} onChange={e => setF("hook", e.target.value)} placeholder="Aandachtstrekker" /></div>
                  <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>CTA</p><input value={fval("cta")} onChange={e => setF("cta", e.target.value)} placeholder="Call-to-action" /></div>
                </div>
              </div>
            </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Voordelen</p>
                {(eff.voordelen || []).map((v, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.green }}>✓</span><span style={{ fontSize: 13 }}>{v}</span></div>)}
              </div>
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Pricing</p><p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>{eff.pricing || <span style={{ color: C.dim }}>—</span>}</p></div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Features ({(eff.features || []).length})</p>
                {(eff.features || []).map((f, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>{f.naam}</p>
                    <p style={{ fontSize: 12, color: C.muted }}>{f.beschrijving}</p>
                  </div>
                ))}
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Social Proof</p>
                {(eff.social_proof || []).map((s, i) => <div key={i} style={{ fontSize: 12, color: C.muted, padding: "5px 0", borderBottom: "1px solid " + C.border, lineHeight: 1.6 }}>"{s}"</div>)}
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Trust Elements</p>
                {(eff.trust_elements || []).map((t, i) => <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", borderBottom: "1px solid " + C.border }}><span style={{ color: C.blue }}>◈</span><span style={{ fontSize: 13 }}>{t}</span></div>)}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* ── Tab: Social Media ── */}
        {tab === "social" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            {[
              { label: "TikTok Scripts", key: "tiktok_scripts", color: C.red, icon: "◇" },
              { label: "Instagram Posts", key: "instagram_posts", color: C.purple, icon: "○" },
              { label: "LinkedIn Posts", key: "linkedin_posts", color: C.blue, icon: "◆" },
              { label: "Facebook Posts", key: "facebook_posts", color: C.blue, icon: "▣" },
              { label: "YouTube Shorts", key: "youtube_shorts", color: C.red, icon: "▶" },
            ].map(({ label, key, color, icon }) => (
              <div key={key} className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>{icon} {label} ({(eff[key] || []).length})</p>
                {(eff[key] || []).length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Genereer het Launch Package voor content.</p>}
                {(eff[key] || []).map((p, i) => (
                  <div key={i} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, flex: 1, whiteSpace: "pre-wrap" }}>{p}</p>
                      <button onClick={() => navigator.clipboard?.writeText(p)} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 12, flexShrink: 0, padding: "2px 5px" }} title="Kopieer">⎘</button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {(eff.carrousels || []).length > 0 && (
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Carrousels</p>
                {eff.carrousels.map((c, i) => (
                  <div key={i} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                    <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>{c.titel}</p>
                    {(c.slides || []).map((s, j) => <p key={j} style={{ fontSize: 12, color: C.muted, padding: "3px 0" }}>Slide {j + 1}: {s}</p>)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Advertenties ── */}
        {tab === "advertenties" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Advertentie Hooks ({(eff.advertentie_hooks || []).length})</p>
                {(eff.advertentie_hooks || []).map((h, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                    <p style={{ fontSize: 13, color: C.yellow, fontWeight: 600, flex: 1 }}>{h}</p>
                    <button onClick={() => navigator.clipboard?.writeText(h)} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 12 }} title="Kopieer">⎘</button>
                  </div>
                ))}
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>Advertentieteksten</p>
                {(eff.advertentie_teksten || []).map((t, i) => (
                  <div key={i} style={{ padding: "9px 0", borderBottom: "1px solid " + C.border }}>
                    <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>{t}</p>
                  </div>
                ))}
              </div>
              {(eff.platform_ads || eff.launch_package?.platform_ads || []).length > 0 && (
                <div className="card">
                  <p className="lbl" style={{ marginBottom: 8 }}>Platform-advertenties (kant-en-klaar)</p>
                  {(eff.platform_ads || eff.launch_package?.platform_ads || []).map((a, i) => (
                    <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                        <Bdg label={a.platform || "Advertentie"} bg={C.blueDim} fg={C.blue} />
                        <button onClick={() => navigator.clipboard?.writeText([a.kop, a.tekst, a.cta && ("CTA: " + a.cta)].filter(Boolean).join("\n"))} style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 12, marginLeft: "auto" }} title="Kopieer">⎘</button>
                      </div>
                      {a.kop && <p style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 3 }}>{a.kop}</p>}
                      {a.tekst && <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{a.tekst}</p>}
                      {a.cta && <p style={{ fontSize: 11, color: C.green, marginTop: 4, fontWeight: 600 }}>→ {a.cta}</p>}
                    </div>
                  ))}
                </div>
              )}
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>A/B Varianten</p>
                {(eff.ab_varianten || []).map((v, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: C.blue, marginBottom: 3 }}>{v.naam}</p>
                    <p style={{ fontSize: 12, color: C.muted }}>{v.variant}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Targeting</p><p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>{eff.targeting || <span style={{ color: C.dim }}>—</span>}</p></div>
              <div className="card"><p className="lbl" style={{ marginBottom: 8 }}>Budgetadvies</p><p style={{ fontSize: 13, color: C.muted, lineHeight: 1.7 }}>{eff.budget_advies || <span style={{ color: C.dim }}>—</span>}</p></div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 6 }}>Waar plaats je deze advertenties?</p>
                <p style={{ fontSize: 11, color: C.dim, marginBottom: 10 }}>Directe links naar de advertentieplatforms — kies op basis van je doelgroep.</p>
                {[
                  ["Meta Ads (Facebook + Instagram)", "Breed bereik, B2C én B2B", "https://www.facebook.com/adsmanager"],
                  ["Google Ads (Search, Display, YouTube)", "Mensen die actief zoeken", "https://ads.google.com"],
                  ["LinkedIn Campaign Manager", "B2B, besluitvormers", "https://www.linkedin.com/campaignmanager"],
                  ["TikTok Ads Manager", "Jong publiek, korte video", "https://ads.tiktok.com"],
                  ["Microsoft Advertising (Bing)", "Zakelijk/ouder publiek, lagere CPC", "https://ads.microsoft.com"],
                  ["Marktplaats Admarkt", "NL consumenten met koopintentie", "https://www.marktplaats.nl/m/zakelijk-adverteren/"],
                  ["Google Bedrijfsprofiel (gratis)", "Lokaal gevonden worden", "https://business.google.com"],
                ].map(([naam, fit, url], i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{naam}</p>
                      <p style={{ fontSize: 11, color: C.muted }}>{fit}</p>
                    </div>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="btn bg" style={{ fontSize: 11, flexShrink: 0, textDecoration: "none" }}>Openen ↗</a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Sales & Outreach ── */}
        {tab === "sales" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {editsDirty && <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 12, color: C.cyan }}>{editsBusy ? "⟳ Automatisch opslaan…" : "● Wijzigingen worden automatisch opgeslagen"}</span><button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} disabled={editsBusy} onClick={saveEdits}>Nu opslaan</button></div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><p className="lbl" style={{ margin: 0 }}>DM Script</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("dm_script"))} title="Kopieer">⎘</button></div>
                  <textarea rows={6} value={fval("dm_script")} onChange={e => setF("dm_script", e.target.value)} placeholder="Persoonlijk DM-outreachscript" />
                </div>
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><p className="lbl" style={{ margin: 0 }}>Follow-up Flow</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("follow_up_flow"))} title="Kopieer">⎘</button></div>
                  <textarea rows={4} value={fval("follow_up_flow")} onChange={e => setF("follow_up_flow", e.target.value)} placeholder="Stapsgewijze follow-up sequentie" />
                </div>
                <div className="card">
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><p className="lbl" style={{ margin: 0 }}>Lead Magnet</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("lead_magnet"))} title="Kopieer">⎘</button></div>
                  <textarea rows={2} value={fval("lead_magnet")} onChange={e => setF("lead_magnet", e.target.value)} placeholder="Concreet lead magnet idee" />
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <div className="card">
                  <p className="lbl" style={{ marginBottom: 8 }}>Outreach Lijst ({(eff.outreach_lijst || []).length}) — klik om te kopiëren</p>
                  {(eff.outreach_lijst || []).map((o, i) => <div key={i} onClick={() => navigator.clipboard?.writeText(o)} title="Klik om te kopiëren" style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.border, cursor: "pointer" }}><span style={{ color: C.blue, flexShrink: 0 }}>→</span><span style={{ fontSize: 12 }}>{o}</span></div>)}
                </div>
                <div className="card">
                  <p className="lbl" style={{ marginBottom: 8 }}>Email Funnel ({(eff.email_funnel || []).length}) — klik om te kopiëren</p>
                  {(eff.email_funnel || []).map((e, i) => (
                    <div key={i} onClick={() => navigator.clipboard?.writeText(e)} title="Klik om te kopiëren" style={{ padding: "8px 0", borderBottom: "1px solid " + C.border, cursor: "pointer" }}>
                      <p style={{ fontSize: 11, color: C.muted, marginBottom: 3, fontFamily: "monospace" }}>Email {i + 1}</p>
                      <p style={{ fontSize: 12, color: C.text, lineHeight: 1.6 }}>{e}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Analytics ── */}
        {tab === "analytics" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {editsDirty && <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 12, color: C.cyan }}>{editsBusy ? "⟳ Automatisch opslaan…" : "● Wijzigingen worden automatisch opgeslagen"}</span><button className="btn bg" style={{ fontSize: 11, marginLeft: "auto" }} disabled={editsBusy} onClick={saveEdits}>Nu opslaan</button></div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>KPI's ({(eff.kpi_lijst || []).length}) — klik om te kopiëren</p>
                {(eff.kpi_lijst || []).map((k, i) => <div key={i} onClick={() => navigator.clipboard?.writeText(k)} title="Klik om te kopiëren" style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: "1px solid " + C.border, cursor: "pointer" }}><span style={{ color: C.green, flexShrink: 0, fontFamily: "monospace", fontSize: 11 }}>{String(i + 1).padStart(2, "0")}</span><span style={{ fontSize: 12 }}>{k}</span></div>)}
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 8 }}>UTM Links — klik om te kopiëren</p>
                {(eff.utm_links || []).map((u, i) => (
                  <div key={i} onClick={() => navigator.clipboard?.writeText(u.url || "")} title="Klik om te kopiëren" style={{ padding: "8px 0", borderBottom: "1px solid " + C.border, cursor: "pointer" }}>
                    <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{u.naam}</p>
                    <p style={{ fontSize: 11, color: C.muted, fontFamily: "monospace", wordBreak: "break-all" }}>{u.url}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}><p className="lbl" style={{ margin: 0 }}>Tracking Scripts</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("tracking_scripts"))} title="Kopieer">⎘</button></div>
              <textarea rows={5} value={fval("tracking_scripts")} onChange={e => setF("tracking_scripts", e.target.value)} placeholder="GA4 + Meta Pixel implementatie-instructies" style={{ fontFamily: "monospace", fontSize: 11 }} />
            </div>
            <div className="card">
              <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}><p className="lbl" style={{ margin: 0 }}>Dashboard Koppeling</p><button className="btn bg" style={{ fontSize: 10, marginLeft: "auto" }} onClick={() => navigator.clipboard?.writeText(fval("dashboard_koppeling"))} title="Kopieer">⎘</button></div>
              <textarea rows={2} value={fval("dashboard_koppeling")} onChange={e => setF("dashboard_koppeling", e.target.value)} placeholder="Aanbevolen dashboard-tools en koppelingen" />
            </div>
          </div>
        )}

        {/* ── Tab: Launch Checklist ── */}
        {tab === "checklist" && (
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <p className="lbl">Launch Checklist — {(eff.checklist_status || []).filter(Boolean).length}/{(eff.checklist_items || eff.test_checklist || []).length} voltooid</p>
              <div style={{ display: "flex", gap: 7 }}>
                <button className="btn bg" style={{ fontSize: 11 }} onClick={generateTestChecklist} disabled={generating}>AI Genereren</button>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: ((eff.checklist_status || []).filter(Boolean).length / Math.max(1, (eff.checklist_items || eff.test_checklist || []).length) * 100) + "%", height: "100%", background: C.green, borderRadius: 2, transition: "width .3s" }} />
              </div>
            </div>
            {(eff.checklist_items || eff.test_checklist || []).length === 0 && <p style={{ color: C.dim, fontSize: 12 }}>Genereer eerst de checklist via "AI Genereren".</p>}
            {(eff.checklist_items || eff.test_checklist || []).map((item, i) => (
              <div key={i} onClick={() => toggleChecklistItem(i)} style={{ display: "flex", gap: 11, padding: "11px 0", borderBottom: "1px solid " + C.border, cursor: "pointer", alignItems: "flex-start" }}>
                <span style={{ fontSize: 18, color: (eff.checklist_status || [])[i] ? C.green : C.dim, flexShrink: 0 }}>{(eff.checklist_status || [])[i] ? "☑" : "☐"}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, color: (eff.checklist_status || [])[i] ? C.muted : C.text, textDecoration: (eff.checklist_status || [])[i] ? "line-through" : "none" }}>{item}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Tab: Launch Status ── */}
        {tab === "status" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Launch Status</p>
                <select value={eff.status || "In Testfase"} onChange={e => {
                  // Een dienst is pas gelanceerd met een echte lanceerdatum; anders blijft de tabel in Live Diensten onbetrouwbaar.
                  if (e.target.value === "Gelanceerd" && !eff.launch_datum) { window.alert("Vul eerst de lanceerdatum in (hieronder bij Launch Datum). Een gelanceerde dienst heeft een echte datum nodig."); return; }
                  patchField({ status: e.target.value });
                }}>
                  {["In Testfase", "Klaar voor Acceptatie", "Klaar voor Launch", "Gelanceerd"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Launch Datum</p>
                <input type="date" value={eff.launch_datum || ""} onChange={e => patchField({ launch_datum: e.target.value })} />
                {eff.status === "Gelanceerd" && !eff.launch_datum && <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>⚠ Deze dienst staat op Gelanceerd maar heeft geen lanceerdatum. Vul de echte datum in.</p>}
              </div>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Verantwoordelijke</p>
                <select value={eff.verantwoordelijke || "furgel"} onChange={e => patchField({ verantwoordelijke: e.target.value })}>
                  <option value="furgel">Furgel (CTO)</option>
                </select>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              <div className="card">
                <p className="lbl" style={{ marginBottom: 10 }}>Status Overzicht</p>
                {[
                  ["Testfase", eff.test_status || "Nog niet getest", eff.test_status === "Goedgekeurd" ? C.green : eff.test_status === "In test" ? C.yellow : eff.test_status === "Afgekeurd" ? C.red : C.muted],
                  ["Launch Package", eff.launch_package && Object.keys(eff.launch_package).length > 0 ? "Gegenereerd" : "Nog niet gegenereerd", eff.launch_package && Object.keys(eff.launch_package).length > 0 ? C.green : C.muted],
                  ["Checklist", (eff.checklist_status || []).filter(Boolean).length + "/" + (eff.checklist_items || eff.test_checklist || []).length + " voltooid", (eff.checklist_status || []).filter(Boolean).length > 0 ? C.yellow : C.muted],
                  ["Launch Datum", eff.launch_datum || "Niet ingesteld", eff.launch_datum ? C.green : C.muted],
                ].map(([l, v, c]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                    <span style={{ fontSize: 12, color: C.muted }}>{l}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: c }}>{v}</span>
                  </div>
                ))}
              </div>
              {eff.status === "Klaar voor Acceptatie" && (
                <div style={{ background: C.greenDim, border: "1px solid " + C.green + "40", borderRadius: 8, padding: 16 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 8 }}>✓ Klaar voor Acceptatie</p>
                  <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>De AI-test is goedgekeurd. Furgel voert nu samen met Claude een manuele dubbele controle uit. Zodra alles werkt, klikt Furgel op "Goedkeuren voor Launch".</p>
                  <button className="btn bp" style={{ background: C.green, color: "#000", width: "100%" }} onClick={acceptByFurgel}>Goedkeuren voor Launch</button>
                </div>
              )}
              {eff.status === "Klaar voor Launch" && (
                <div style={{ background: C.cyanDim, border: "1px solid " + C.cyan + "40", borderRadius: 8, padding: 16 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: C.cyan, marginBottom: 8 }}>Klaar voor Launch!</p>
                  <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginBottom: 12 }}>Geaccepteerd door Furgel. Klik op "Publiceer Launch" om de dienst officieel te lanceren.</p>
                  <button className="btn bp" style={{ background: C.cyan, color: "#000", width: "100%" }} onClick={publishLaunch}>Publiceer Launch</button>
                </div>
              )}
              {eff.status === "Gelanceerd" && (
                <div style={{ background: C.greenDim, border: "1px solid " + C.green + "40", borderRadius: 8, padding: 16 }}>
                  <p style={{ fontSize: 15, fontWeight: 700, color: C.green, marginBottom: 6 }}>Gelanceerd!</p>
                  <p style={{ fontSize: 12, color: C.muted }}>Gelanceerd op {eff.launch_datum || "—"}.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── GROWTH OPERATOR MODULE ──────────────────────────────────────────────────
  const GROWTH_ENGINES = [
    { id: "content", l: "Content", icon: "◳", live: true, color: C.green },
    { id: "seo", l: "SEO", icon: "◌", live: true, color: C.blue },
    { id: "video", l: "AI Video", icon: "▸", live: true, color: C.purple },
    { id: "creative", l: "Creative Mastery", icon: "✺", live: true, color: C.purple },
    { id: "meta_ads", l: "Meta Ads Mastery", icon: "◎", live: true, color: C.red },
    { id: "klantenservice", l: "Klantenservice", icon: "☎", live: true, color: C.blue },
    { id: "social", l: "Social", icon: "□", live: false, color: C.cyan },
    { id: "outreach", l: "Outreach", icon: "↗", live: false, color: C.yellow },
    { id: "email", l: "E-mail/CRM", icon: "◫", live: false, color: C.muted },
    { id: "ads", l: "Paid Ads", icon: "◎", live: false, color: C.red },
    { id: "automation", l: "Automation", icon: "◆", live: false, color: C.cyan },
  ];

  const GROWTH_STATUS_COL = {
    pending: { bg: C.yellowDim, fg: C.yellow, l: "Wacht op review" },
    approved: { bg: C.greenDim, fg: C.green, l: "Goedgekeurd" },
    edited: { bg: C.blueDim, fg: C.blue, l: "Bewerkt + goedgekeurd" },
    rejected: { bg: C.redDim, fg: C.red, l: "Afgekeurd" },
    planned: { bg: C.cyanDim, fg: C.cyan, l: "Gepland" },
    executed: { bg: C.greenDim, fg: C.green, l: "Uitgevoerd" },
  };

  const CONTENT_TYPES = [
    { id: "blog", l: "Blogartikel", len: "600-1000 woorden, SEO-klaar" },
    { id: "landing", l: "Landingspagina sectie", len: "hero+features+CTA" },
    { id: "social_post", l: "Social media post", len: "LinkedIn/Instagram caption + hashtags" },
    { id: "video_script", l: "Video script", len: "30-60s gesproken script" },
    { id: "case_study", l: "Case study", len: "klantverhaal + resultaten" },
    { id: "whitepaper", l: "Whitepaper outline", len: "hoofdstukken + key points" },
    { id: "ad_copy", l: "Advertentietekst (Meta Ads)", len: "primaire tekst + kop + beschrijving, 2-3 varianten" },
    { id: "ticket_antwoord", l: "Klantenservice-conceptantwoord", len: "beantwoordt het ticket, geen toezeggingen die niet kloppen" },
  ];

  // Visuele content-types, gerenderd als merkgerichte SVG-assets (geen image-API)
  const VISUAL_TYPES = [
    { id: "social_square", l: "Social post (vierkant)", kind: "social_post", visual: true, dim: { w: 1080, h: 1080 }, len: "kop + body + CTA" },
    { id: "social_portrait", l: "Social post (portret)", kind: "social_post", visual: true, dim: { w: 1080, h: 1350 }, len: "kop + body + CTA" },
    { id: "banner", l: "Banner (liggend)", kind: "banner", visual: true, dim: { w: 1200, h: 628 }, len: "hero + CTA" },
    { id: "carousel", l: "Carrousel", kind: "carousel", visual: true, dim: { w: 1080, h: 1080 }, len: "3-6 slides" },
    { id: "story", l: "Story/Reel visual (Creative Mastery)", kind: "social_post", visual: true, dim: { w: 1080, h: 1920 }, len: "kop + body + CTA" },
    { id: "product_creative", l: "Productcreative (van productlink)", kind: "social_post", visual: true, dim: { w: 1080, h: 1080 }, len: "productfoto + kop + body + CTA, optioneel video" },
  ];
  const ALL_CONTENT_TYPES = [...CONTENT_TYPES, ...VISUAL_TYPES];

  // SEO-deliverables — gestructureerde generatie (fase 2), zelfde review-pijplijn
  const SEO_TYPES = [
    { id: "onpage_pakket", l: "On-page SEO-pakket", seo: true, len: "keyword + meta + structuur + FAQ + schema" },
    { id: "keyword_cluster", l: "Zoekwoord-onderzoek", seo: true, len: "hoofdterm + long-tail + intent" },
    { id: "meta_optim", l: "Meta-optimalisatie", seo: true, len: "title + description + slug" },
  ];
  const ALL_TYPES = [...CONTENT_TYPES, ...VISUAL_TYPES, ...SEO_TYPES];
  // Types die bij een engine horen (voor de engine-bewuste type-dropdown)
  function typesForEngine(engine) { return engine === "seo" ? SEO_TYPES : CONTENT_TYPES; }

  // Toegestane merk-accentkleuren (model kiest een naam, app mapt naar echte hex — blijft on-brand)
  const VIS_ACCENTS = { green: C.green, cyan: C.cyan, blue: C.blue, purple: C.purple, yellow: C.yellow, red: C.red };
  // Themapaletten voor de SVG-render
  function visTheme(theme, accentName) {
    const accent = VIS_ACCENTS[accentName] || C.green;
    if (theme === "light") return { bg: "#f4f4fa", bg2: "#ffffff", text: "#131418", sub: "#41415a", accent, onAccent: "#06121a", line: "#d4d4e4" };
    return { bg: "#0a0a0c", bg2: "#12121c", text: "#eef0fb", sub: "#9a9ab8", accent, onAccent: "#06121a", line: "#26263a" };
  }
  // XML-escape voor veilige tekst in SVG
  function esc(s) { return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
  // Woord-wrap → array regels, schatting op basis van tekens-per-regel
  function wrapText(text, maxChars, maxLines) {
    const words = String(text || "").trim().split(/\s+/).filter(Boolean);
    const lines = []; let cur = "";
    for (const w of words) {
      const next = cur ? cur + " " + w : w;
      if (next.length > maxChars && cur) { lines.push(cur); cur = w; } else { cur = next; }
      if (maxLines && lines.length >= maxLines) { break; }
    }
    if (cur && (!maxLines || lines.length < maxLines)) lines.push(cur);
    if (maxLines && lines.length > maxLines) lines.length = maxLines;
    // ellipsis als laatste regel afgekapt is
    if (maxLines && lines.length === maxLines) {
      const joined = lines.join(" ");
      if (joined.length < String(text || "").trim().length) {
        let last = lines[maxLines - 1];
        if (last.length > maxChars - 1) last = last.slice(0, maxChars - 1);
        lines[maxLines - 1] = last.replace(/[.,;:\s]+$/, "") + "…";
      }
    }
    return lines;
  }
  // Saneer een door de AI geleverde visual-spec: forceer geldige kind/theme/accent/dim + slide-aantal
  function normalizeVisual(v, ctype) {
    const kind = ctype?.kind || (["social_post", "banner", "carousel"].includes(v?.kind) ? v.kind : "social_post");
    const dim = ctype?.dim || { w: 1080, h: 1080 };
    const theme = v?.theme === "light" ? "light" : "dark";
    const accent = VIS_ACCENTS[v?.accent] ? v.accent : "green";
    let slides = Array.isArray(v?.slides) ? v.slides : [];
    slides = slides.map(s => ({
      badge: String(s?.badge || "").slice(0, 24),
      headline: String(s?.headline || "").slice(0, 120),
      sub: String(s?.sub || "").slice(0, 160),
      body: String(s?.body || "").slice(0, 400),
      cta: String(s?.cta || "").slice(0, 40),
    })).filter(s => s.headline || s.body || s.sub || s.cta);
    if (slides.length === 0) slides = [{ badge: "", headline: "ONVOLDOENDE CONTENT", sub: "De AI leverde geen bruikbare slide.", body: "", cta: "" }];
    if (kind === "carousel") { if (slides.length > 6) slides = slides.slice(0, 6); }
    else slides = slides.slice(0, 1);
    return { kind, theme, accent, dim, slides };
  }

  // Saneer een door de AI geleverde SEO-spec + bouw client-side een geldige FAQPage JSON-LD
  const SEO_INTENTS = ["informationeel", "commercieel", "transactioneel", "navigatie"];
  function normalizeSeo(s) {
    const str = (v, n) => String(v == null ? "" : v).trim().slice(0, n);
    const cluster = (Array.isArray(s?.keyword_cluster) ? s.keyword_cluster : []).slice(0, 15).map(k => ({
      keyword: str(k?.keyword, 90),
      type: ["hoofd", "long-tail", "vraag"].includes(k?.type) ? k.type : "long-tail",
      prioriteit: ["hoog", "midden", "laag"].includes(k?.prioriteit) ? k.prioriteit : "midden",
    })).filter(k => k.keyword);
    const headings = (Array.isArray(s?.headings) ? s.headings : []).slice(0, 20).map(h => ({
      level: ["H1", "H2", "H3"].includes(h?.level) ? h.level : "H2",
      text: str(h?.text, 120),
    })).filter(h => h.text);
    const faq = (Array.isArray(s?.faq) ? s.faq : []).slice(0, 8).map(f => ({
      q: str(f?.q, 160), a: str(f?.a, 400),
    })).filter(f => f.q && f.a);
    const internal_links = (Array.isArray(s?.internal_links) ? s.internal_links : []).slice(0, 10).map(l => ({
      anchor: str(l?.anchor, 80), naar: str(l?.naar, 120),
    })).filter(l => l.anchor);
    const out = {
      target_keyword: str(s?.target_keyword, 90),
      search_intent: SEO_INTENTS.includes(s?.search_intent) ? s.search_intent : "informationeel",
      slug: str(s?.slug, 90).replace(/^\/?/, "/"),
      meta_title: str(s?.meta_title, 120),       // waarde behouden; UI toont overschrijding van 60
      meta_description: str(s?.meta_description, 320), // UI toont overschrijding van 155
      keyword_cluster: cluster, headings, faq, internal_links,
    };
    // Geldige FAQPage JSON-LD client-side (niet door het model) → altijd parsebaar
    out.jsonld = faq.length > 0 ? JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faq.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    }, null, 2) : "";
    return out;
  }

  const CONTENT_AGENT_SYS = `Je bent de Content Engine van de Growth Operator binnen Dif3r3nt VOF.

=== WIE IS DIF3R3NT ===
Dif3r3nt VOF is een Nederlands SaaS / web-app / AI-tooling bedrijf van Furgel (CTO), die alles samen met Claude AI beoordeelt.
Wat ze maken: digitale tools, online services, AI-assistenten, automatiseringen, dashboards.
Wat ze NIET doen: fysieke producten, fysieke werken, gebouwen, infrastructuur.

Werkterrein: Nederlandse markt. B2B (ZZP'ers, kleine ondernemers, MKB) én B2C waar relevant.
Sector waarin Dif3r3nt zelf opereert = SOFTWARE / TECH. Niet een andere sector tenzij een specifieke DIENST een andere doelgroep heeft (en dat staat dan letterlijk in de DIENST CONTEXT).

=== ABSOLUUT VERBODEN — BOUW-GERELATEERDE TAAL ===
Onder GEEN ENKELE omstandigheid mag je in je antwoord termen gebruiken zoals:
"bouwsector", "bouwbranche", "bouwbedrijf", "bouwmethode", "duurzame bouw", "bouwprojecten",
"aannemer", "aannemerij", "construction", "civiele techniek", "infrastructuur" (in fysieke zin),
"bouw" als zelfstandig naamwoord, "constructie", "verbouwing", "renovatie", "vastgoed", "huizen bouwen".

Deze termen zijn alleen toegestaan als ze LETTERLIJK in DIENST CONTEXT of BRIEF VAN FURGEL staan.
"Reason3n", "Dif3r3nt OS" of andere productnamen hebben NIETS met de bouwsector te maken.
Als jouw associatie via "Reason" of "Reason3n" naar bouw gaat: dat is verkeerd. Reason3n is een digitale dienst.

=== KRITIEKE ANTI-HALLUCINATIE REGELS ===
1. ALLEEN feiten gebruiken die in de "DIENST CONTEXT" of "BRIEF" staan. Geen verzonnen sectoren, doelgroepen, prijzen, klanten of cijfers.
2. Als brief + context onvoldoende zijn → vul title="ONVOLDOENDE CONTEXT" en leg in "waarom" uit welke info ontbreekt. Genereer GEEN gokwerk-content.
3. Als DIENST CONTEXT vaag is ("digitale transformatie", "innovatie", "expertise"): schrijf in "waarom": "Positionering is te vaag — herzie eerst de Launch Module voor deze dienst" en stop. Geen fictieve content.
4. preview.body is publicatiegereed — geen placeholders, geen lorem ipsum, geen verzonnen klantnamen of cijfers.
5. Toon = nuchter Nederlands ondernemen. Geen marketing-buzzwords (synergie, ontketen, unlock, empower).

=== ZELF-CHECK VOOR VERZENDEN ===
Lees je eigen JSON terug. Voor elke "bouw"/"aannem"/"construct"/"vastgoed"-vorm: staat dit woord in DIENST CONTEXT of BRIEF? Zo nee → schrap of herschrijf. Liever leeg laten dan verzinnen.

Reageer UITSLUITEND als JSON:
{"title":"...","doel":"max 2 zinnen","waarom":"concreet signaal + aanleiding (max 3 zinnen)","preview":{"body":"volledige content","metadata":{"woordenaantal":0,"leestijd_min":0,"primaire_keywords":["kw1","kw2"],"tone":"...","kanaal":"..."}},"data_context":{"signalen":["signaal1","signaal2"],"doelgroep":"...","kanaal":"...","vorige_leerpunten":"samenvatting"},"risico":"specifiek risico (max 2 zinnen)","impact":"meetbare KPIs (max 2 zinnen)"}`;

  // Visuele variant — levert een gestructureerde spec die de app rendert als merkgerichte SVG-assets.
  const CONTENT_VISUAL_SYS = `Je bent de Visual Content Engine van de Growth Operator binnen Dif3r3nt VOF.

=== WIE IS DIF3R3NT ===
Dif3r3nt VOF is een Nederlands SaaS / web-app / AI-tooling bedrijf van Furgel (CTO), die alles samen met Claude AI beoordeelt.
Wat ze maken: digitale tools, online services, AI-assistenten, automatiseringen, dashboards.
Wat ze NIET doen: fysieke producten, fysieke werken, gebouwen, infrastructuur.
Werkterrein: Nederlandse markt. B2B (ZZP'ers, kleine ondernemers, MKB) én B2C waar relevant.
Sector waarin Dif3r3nt zelf opereert = SOFTWARE / TECH.

=== ABSOLUUT VERBODEN — BOUW-GERELATEERDE TAAL ===
Onder GEEN ENKELE omstandigheid termen als "bouwsector", "bouwbedrijf", "aannemer", "construction",
"civiele techniek", "infrastructuur" (fysiek), "bouw" (zelfst. nw.), "constructie", "verbouwing",
"renovatie", "vastgoed". Alleen toegestaan als ze LETTERLIJK in DIENST CONTEXT of BRIEF staan.

=== KRITIEKE ANTI-HALLUCINATIE REGELS ===
1. ALLEEN feiten uit "DIENST CONTEXT" of "BRIEF". Geen verzonnen sectoren, doelgroepen, prijzen of cijfers.
2. Onvoldoende context → title="ONVOLDOENDE CONTEXT" en leg in "waarom" uit welke info ontbreekt. Geen gokwerk.
3. Vage positionering → schrijf in "waarom": "Positionering te vaag — herzie eerst de Launch Module" en stop.
4. Alle tekst is publicatiegereed: geen placeholders, lorem ipsum, verzonnen klantnamen of cijfers.
5. Toon = nuchter Nederlands ondernemen. Geen buzzwords (synergie, ontketen, unlock, empower).

=== VISUELE OUTPUT-REGELS ===
Dit is een BEELD, geen artikel. Houd teksten kort en scanbaar:
- headline: kernboodschap, MAX ~8 woorden, geen punt aan het eind.
- sub: één korte ondersteunende zin (max ~14 woorden), mag leeg.
- body: 2 t/m 3 zeer korte regels/bullets (elk max ~8 woorden), gescheiden door \\n. Mag leeg.
- cta: korte call-to-action (max 5 woorden), bv. "Plan een demo" of "Probeer gratis".
- badge: kort label of leeg (bv. "NIEUW", "GIDS", "TIP").
"kind" wordt door het systeem bepaald en staat in de BRIEF. Houd je daaraan:
- "social_post" en "banner": PRECIES 1 slide.
- "carousel": 3 t/m 6 slides. Slide 1 = sterke hook; laatste slide = duidelijke CTA-slide.
"theme" = "dark" (standaard, donker merk) of "light". "accent" = EXACT één van:
green, cyan, blue, purple, yellow, red. Kies passend bij de boodschap.
"preview.body" = begeleidende caption + hashtags voor de post (los van het beeld, publicatieklaar).

=== ZELF-CHECK ===
Lees je JSON terug: staat er een bouw/aannem/construct/vastgoed-term die NIET in input staat? Schrap of herschrijf.
Klopt het aantal slides bij "kind"? Zijn alle teksten kort genoeg voor een beeld?

Reageer UITSLUITEND als JSON:
{"title":"...","doel":"max 2 zinnen","waarom":"concreet signaal + aanleiding (max 3 zinnen)","preview":{"body":"caption + hashtags","metadata":{"primaire_keywords":["kw1","kw2"],"tone":"...","kanaal":"..."},"visual":{"kind":"social_post|banner|carousel","theme":"dark|light","accent":"green|cyan|blue|purple|yellow|red","slides":[{"badge":"kort of leeg","headline":"max ~8 woorden","sub":"1 korte zin of leeg","body":"max 3 korte regels gescheiden door \\n, of leeg","cta":"korte CTA"}]}},"data_context":{"signalen":["signaal1","signaal2"],"doelgroep":"...","kanaal":"...","vorige_leerpunten":"samenvatting"},"risico":"specifiek risico (max 2 zinnen)","impact":"meetbare KPIs (max 2 zinnen)"}`;

  // Productcreative-engine - voor fysieke producten uit de webshops (Dif3r3nt Store, Casa Celeste
  // en andere eigen shops), op basis van echte, van de productpagina opgehaalde gegevens.
  // In tegenstelling tot CONTENT_VISUAL_SYS gaat dit WEL over fysieke producten.
  const CONTENT_PRODUCT_SYS = `Je bent de Productcreative Engine van de Growth Operator binnen Dif3r3nt VOF.

=== CONTEXT ===
Dif3r3nt VOF exploiteert ook eigen webshops (zoals Dif3r3nt Store en Casa Celeste) die fysieke producten
verkopen. Dit is een uitzondering op de regel dat Dif3r3nt alleen software maakt: voor DEZE opdracht
draait het om een echt, fysiek product uit zo'n webshop. Werkterrein: Nederlandse markt, consumenten (B2C).

=== KRITIEKE ANTI-HALLUCINATIE REGELS ===
1. ALLEEN feiten gebruiken uit "PRODUCT CONTEXT" (en eventueel "BRIEF VAN FURGEL" als extra instructie).
   Verzin GEEN materiaal, afmetingen, voorraad, reviews, sterren, garanties, certificeringen, kortingen
   of levertijden die niet letterlijk in PRODUCT CONTEXT staan.
2. Als de titel of beschrijving te summier is om een goede creative te maken: title="ONVOLDOENDE CONTEXT"
   en leg in "waarom" uit wat ontbreekt. Geen gokwerk.
3. Noem de prijs alleen als die in PRODUCT CONTEXT staat, en dan exact zo (geen afgeronde of verzonnen prijs).
4. Alle tekst is publicatiegereed: geen placeholders, lorem ipsum of verzonnen klantnamen/cijfers.
5. Toon = enthousiast maar geloofwaardig Nederlands e-commerce. Geen overdreven superlatieven
   ("beste ter wereld", "revolutionair") tenzij dat letterlijk in PRODUCT CONTEXT staat.

=== VISUELE OUTPUT-REGELS (statische creative) ===
Dit wordt een BEELD met een echte productfoto erin verwerkt, dus houd tekst kort en scanbaar:
- headline: kernboodschap over het product, MAX ~8 woorden, geen punt aan het eind.
- sub: een korte ondersteunende zin (max ~14 woorden), mag leeg.
- body: 2 t/m 3 zeer korte regels/bullets (elk max ~8 woorden) met concrete productfeiten, of leeg.
- cta: korte call-to-action (max 5 woorden), bv. "Bekijk in de winkel" of "Nu verkrijgbaar".
- badge: kort label of leeg (bv. "NIEUW", "IN VOORRAAD"). Alleen "IN VOORRAAD" als dat niet verzonnen is.
"kind" staat vast op "social_post" (1 slide) voor een productcreative - lever PRECIES 1 slide.
"theme" = "dark" of "light", "accent" = EXACT een van: green, cyan, blue, purple, yellow, red.

=== VIDEO-OUTPUT-REGELS (image-to-video, optioneel) ===
Als er een productfoto beschikbaar is, lever ook een "video"-object met een korte, Engelstalige
motion-prompt voor een image-to-video model (het model krijgt de echte productfoto als startbeeld):
- motion_prompt: Engels, 1-2 zinnen, beschrijft alleen CAMERABEWEGING en licht (bv. "slow gentle
  zoom in, soft studio lighting, subtle rotation"). Verzin GEEN nieuwe objecten of scene-elementen
  die niet op de foto staan, want het model ziet de foto en de prompt stuurt alleen de beweging.
- caption: korte Nederlandse begeleidende tekst voor bij de video (los van het beeld).
Als er GEEN productfoto is, laat "video" leeg/weg.

=== ZELF-CHECK ===
Lees je JSON terug: staat elk feit (prijs, materiaal, etc.) letterlijk in PRODUCT CONTEXT?
Is de motion_prompt puur over beweging/licht, zonder nieuwe objecten te verzinnen?

Reageer UITSLUITEND als JSON:
{"title":"...","doel":"max 2 zinnen","waarom":"concreet signaal + aanleiding (max 3 zinnen)","preview":{"body":"caption + hashtags","metadata":{"primaire_keywords":["kw1","kw2"],"tone":"...","kanaal":"social"},"visual":{"kind":"social_post","theme":"dark|light","accent":"green|cyan|blue|purple|yellow|red","slides":[{"badge":"kort of leeg","headline":"max ~8 woorden","sub":"1 korte zin of leeg","body":"max 3 korte regels gescheiden door \\n, of leeg","cta":"korte CTA"}]},"video":{"motion_prompt":"Engels, camera+licht alleen","caption":"korte Nederlandse tekst"}},"data_context":{"signalen":["signaal1"],"doelgroep":"...","kanaal":"social","vorige_leerpunten":"samenvatting"},"risico":"specifiek risico (max 2 zinnen)","impact":"meetbare KPIs (max 2 zinnen)"}`;

  // SEO-engine - gestructureerde on-page SEO-voorstellen voor review.
  const CONTENT_SEO_SYS = `Je bent de SEO Engine van de Growth Operator binnen Dif3r3nt VOF.

=== WIE IS DIF3R3NT ===
Dif3r3nt VOF is een Nederlands SaaS / web-app / AI-tooling bedrijf van Furgel (CTO), die alles samen met Claude AI beoordeelt.
Wat ze maken: digitale tools, online services, AI-assistenten, automatiseringen, dashboards.
Wat ze NIET doen: fysieke producten, fysieke werken, gebouwen, infrastructuur.
Werkterrein: Nederlandse markt. B2B (ZZP'ers, kleine ondernemers, MKB) én B2C waar relevant.
Sector waarin Dif3r3nt zelf opereert = SOFTWARE / TECH.

=== ABSOLUUT VERBODEN — BOUW-GERELATEERDE TAAL ===
Onder GEEN ENKELE omstandigheid termen als "bouwsector", "bouwbedrijf", "aannemer", "construction",
"civiele techniek", "infrastructuur" (fysiek), "bouw" (zelfst. nw.), "constructie", "verbouwing",
"renovatie", "vastgoed". Alleen toegestaan als ze LETTERLIJK in DIENST CONTEXT of BRIEF staan.

=== KRITIEKE ANTI-HALLUCINATIE REGELS ===
1. ALLEEN feiten uit "DIENST CONTEXT" of "BRIEF". Geen verzonnen sectoren, doelgroepen, klanten of cijfers.
2. **VERZIN GEEN ZOEKVOLUMES OF VERKEERSCIJFERS.** Je hebt geen toegang tot Search Console/keyword tools.
   Gebruik uitsluitend een redactionele "prioriteit": hoog | midden | laag. Nooit getallen als "1.200/mnd".
3. Onvoldoende context → title="ONVOLDOENDE CONTEXT" en leg in "waarom" uit welke info ontbreekt. Geen gokwerk.
4. Vage positionering → schrijf in "waarom": "Positionering te vaag — herzie eerst de Launch Module" en stop.
5. Alle tekst publicatiegereed, nuchter Nederlands ondernemen. Geen buzzwords (synergie, ontketen, unlock).

=== SEO-REGELS ===
- meta_title: pakkend, bevat het doelzoekwoord, MAX 60 tekens.
- meta_description: wervend + duidelijke meerwaarde, MAX 155 tekens.
- slug: kort, kleine letters, koppeltekens, begint met "/".
- search_intent: één van informationeel | commercieel | transactioneel | navigatie.
- keyword_cluster: 1 hoofdterm + relevante long-tail + vraag-zoekwoorden (type: hoofd|long-tail|vraag),
  elk met prioriteit hoog|midden|laag op basis van relevantie voor de dienst (geen verzonnen volume).
- headings: logische H1/H2/H3-structuur voor de pagina (H1 = doelzoekwoord-gericht).
- faq: 3–6 echte vragen + beknopte antwoorden (gebruik voor FAQ-schema).
- internal_links: anchor-tekst → naar welke pagina/dienst binnen Dif3r3nt.
Het deliverable-type staat in de BRIEF (On-page SEO-pakket = alles; Zoekwoord-onderzoek = vooral cluster;
Meta-optimalisatie = vooral meta_title/description/slug). Vul altijd minimaal target_keyword + meta in.

=== ZELF-CHECK ===
Lees je JSON terug: staan er bouw-termen die niet in input staan? Schrap. Staan er verzonnen volumes/getallen? Schrap.
Is meta_title ≤60 en meta_description ≤155 tekens?

Reageer UITSLUITEND als JSON:
{"title":"...","doel":"max 2 zinnen","waarom":"concreet signaal + aanleiding (max 3 zinnen)","preview":{"body":"korte content-brief / samenvatting (publicatieklaar)","metadata":{"primaire_keywords":["kw1","kw2"],"tone":"...","kanaal":"SEO"},"seo":{"target_keyword":"...","search_intent":"informationeel|commercieel|transactioneel|navigatie","slug":"/voorbeeld-url","meta_title":"max 60 tekens","meta_description":"max 155 tekens","keyword_cluster":[{"keyword":"...","type":"hoofd|long-tail|vraag","prioriteit":"hoog|midden|laag"}],"headings":[{"level":"H1|H2|H3","text":"..."}],"faq":[{"q":"...","a":"..."}],"internal_links":[{"anchor":"...","naar":"pagina of dienst"}]}},"data_context":{"signalen":["signaal1","signaal2"],"doelgroep":"...","kanaal":"SEO","vorige_leerpunten":"samenvatting"},"risico":"specifiek risico (max 2 zinnen)","impact":"meetbare KPIs (max 2 zinnen)"}`;

  // ─── VISUELE ASSET RENDER (SVG → PNG, geen externe API) ───────────────────────
  const VIS_MONO = "'Space Mono', ui-monospace, SFMono-Regular, monospace";
  const VIS_SANS = "'DM Sans', system-ui, -apple-system, sans-serif";

  // Bouwt één slide als volledige SVG-string. exportMode=true → vaste px-afmeting voor rasteren.
  function growthSlideSVG(visual, idx, exportMode) {
    const dim = visual?.dim || { w: 1080, h: 1080 };
    const W = dim.w, H = dim.h;
    const t = visTheme(visual?.theme, visual?.accent);
    const slides = Array.isArray(visual?.slides) ? visual.slides : [];
    const total = slides.length || 1;
    const s = slides[idx] || slides[0] || {};
    const kind = visual?.kind || "social_post";
    const sc = W / 1080;
    const px = v => Math.round(v * sc);
    const isFirst = idx === 0, isLast = idx === total - 1;
    const parts = [];
    // achtergrond + accent-randen
    parts.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="${t.bg}"/>`);
    parts.push(`<rect x="0" y="0" width="${W}" height="${px(10)}" fill="${t.accent}"/>`);

    // tekst-blok helper → geeft eind-y terug
    function block(txt, o) {
      const lines = wrapText(txt, o.maxChars, o.maxLines);
      if (lines.length === 0) return o.y;
      const lh = o.size * (o.lh || 1.2);
      let svg = `<text x="${o.x}" y="${o.y + o.size}" font-family="${o.font}" font-size="${o.size}" font-weight="${o.weight || 400}" fill="${o.fill}" letter-spacing="${o.ls || 0}"${o.anchor ? ` text-anchor="${o.anchor}"` : ""}>`;
      lines.forEach((ln, i) => { svg += `<tspan x="${o.x}" dy="${i === 0 ? 0 : lh}">${esc(ln)}</tspan>`; });
      svg += `</text>`;
      parts.push(svg);
      return o.y + o.size + (lines.length - 1) * lh + o.size * 0.3;
    }
    // wordmerk
    function wordmark(x, y, fill) {
      parts.push(`<text x="${x}" y="${y}" font-family="${VIS_MONO}" font-size="${px(26)}" font-weight="700" fill="${fill}" letter-spacing="2"><tspan fill="${t.accent}">◆ </tspan>DIF3R3NT</text>`);
    }
    // CTA-pill
    function ctaPill(x, y, label, anchorMid, maxW) {
      if (!label) return;
      const fs = px(28), padX = px(34), padY = px(18);
      const w = Math.min(maxW || (W - px(120)), label.length * fs * 0.62 + padX * 2);
      const rx = anchorMid ? x - w / 2 : x;
      parts.push(`<rect x="${rx}" y="${y}" width="${w}" height="${fs + padY * 2}" rx="${(fs + padY * 2) / 2}" fill="${t.accent}"/>`);
      parts.push(`<text x="${rx + w / 2}" y="${y + padY + fs * 0.78}" font-family="${VIS_SANS}" font-size="${fs}" font-weight="600" fill="${t.onAccent}" text-anchor="middle">${esc(label)}</text>`);
    }

    if (kind === "banner") {
      const bandX = Math.round(W * 0.72);
      parts.push(`<rect x="${bandX}" y="0" width="${W - bandX}" height="${H}" fill="${t.accent}"/>`);
      parts.push(`<text x="${bandX + (W - bandX) / 2}" y="${H * 0.66}" font-family="${VIS_MONO}" font-size="${px(360)}" fill="${t.onAccent}" opacity="0.18" text-anchor="middle">◆</text>`);
      const P = px(70);
      // tekst mag niet onder de accentband lopen → begrens kolombreedte + leid tekens-per-regel af
      const textW = Math.max(px(200), bandX - P - px(40));
      const hSize = px(56), sSize = px(26);
      const hMax = Math.max(10, Math.floor(textW / (hSize * 0.6)));   // mono ≈ 0.6·fontsize per teken
      const sMax = Math.max(14, Math.floor(textW / (sSize * 0.52)));  // sans ≈ 0.52·fontsize per teken
      wordmark(P, P + px(8), t.text);
      if (s.badge) parts.push(`<text x="${P}" y="${P + px(54)}" font-family="${VIS_MONO}" font-size="${px(22)}" font-weight="700" fill="${t.accent}" letter-spacing="3">${esc(String(s.badge).toUpperCase())}</text>`);
      let y = Math.round(H * 0.26);
      y = block(s.headline, { x: P, y, size: hSize, font: VIS_MONO, weight: 700, fill: t.text, maxChars: hMax, maxLines: 3, lh: 1.15 });
      if (s.sub) y = block(s.sub, { x: P, y: y + px(6), size: sSize, font: VIS_SANS, fill: t.sub, maxChars: sMax, maxLines: 2, lh: 1.3 });
      ctaPill(P, H - P - px(64), s.cta, false, textW);
    } else {
      // social_post & carousel
      const P = px(84);
      // Productfoto (eigen storage-URL, niet AI-gestuurd): beeldband bovenin + donkere scrim voor leesbaarheid.
      const hasImg = kind !== "carousel" && typeof visual?.productImage === "string" && /^(https:\/\/|data:image\/(png|jpe?g|webp);base64,)/.test(visual.productImage);
      let imgBandH = 0;
      if (hasImg) {
        imgBandH = Math.round(H * 0.56);
        const clipId = "pimg" + idx;
        parts.push(`<defs><clipPath id="${clipId}"><rect x="0" y="0" width="${W}" height="${imgBandH}"/></clipPath><linearGradient id="pgrad${idx}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${t.bg}" stop-opacity="0"/><stop offset="100%" stop-color="${t.bg}" stop-opacity="1"/></linearGradient></defs>`);
        parts.push(`<g clip-path="url(#${clipId})"><image href="${esc(visual.productImage)}" x="0" y="0" width="${W}" height="${imgBandH}" preserveAspectRatio="xMidYMid slice"/></g>`);
        parts.push(`<rect x="0" y="${Math.round(imgBandH * 0.55)}" width="${W}" height="${imgBandH * 0.45}" fill="url(#pgrad${idx})"/>`);
      }
      wordmark(P, P + px(10), hasImg ? "#ffffff" : t.text);
      if (kind === "carousel") parts.push(`<text x="${W - P}" y="${P + px(10)}" font-family="${VIS_MONO}" font-size="${px(24)}" font-weight="700" fill="${t.sub}" text-anchor="end">${idx + 1} / ${total}</text>`);

      if (kind === "carousel" && isLast) {
        // CTA-slide gecentreerd
        let y = Math.round(H * 0.30);
        y = block(s.headline, { x: W / 2, y, size: px(78), font: VIS_MONO, weight: 700, fill: t.text, maxChars: 18, maxLines: 3, lh: 1.12, anchor: "middle" });
        if (s.sub) y = block(s.sub, { x: W / 2, y: y + px(10), size: px(32), font: VIS_SANS, fill: t.sub, maxChars: 34, maxLines: 2, lh: 1.3, anchor: "middle" });
        ctaPill(W / 2, Math.round(H * 0.62), s.cta || "Neem contact op", true);
      } else {
        const badgeY = hasImg ? Math.round(imgBandH * 0.72) : Math.round(H * 0.20);
        if (s.badge) {
          parts.push(`<rect x="${P}" y="${badgeY}" width="${Math.min(W - 2 * P, String(s.badge).length * px(17) + px(44))}" height="${px(46)}" rx="${px(23)}" fill="${t.accent}" opacity="0.16"/>`);
          parts.push(`<text x="${P + px(22)}" y="${badgeY + px(31)}" font-family="${VIS_MONO}" font-size="${px(22)}" font-weight="700" fill="${t.accent}" letter-spacing="2">${esc(String(s.badge).toUpperCase())}</text>`);
        }
        let y = hasImg ? imgBandH + px(50) : Math.round(H * (s.badge ? 0.30 : 0.26));
        const hSize = hasImg ? px(62) : (kind === "carousel" && isFirst) ? px(94) : px(80);
        y = block(s.headline, { x: P, y, size: hSize, font: VIS_MONO, weight: 700, fill: t.text, maxChars: hasImg ? 20 : 16, maxLines: hasImg ? 3 : 4, lh: 1.12 });
        if (s.sub) y = block(s.sub, { x: P, y: y + px(8), size: px(28), font: VIS_SANS, fill: t.sub, maxChars: 40, maxLines: 2, lh: 1.3 });
        // body als bullets (regels gescheiden door \n)
        const bodyLines = String(s.body || "").split(/\n+/).map(l => l.trim()).filter(Boolean).slice(0, 3);
        let by = y + px(14);
        bodyLines.forEach(ln => {
          parts.push(`<text x="${P}" y="${by + px(24)}" font-family="${VIS_SANS}" font-size="${px(28)}" fill="${t.accent}">▸</text>`);
          by = block(ln, { x: P + px(44), y: by, size: px(28), font: VIS_SANS, fill: t.text, maxChars: 34, maxLines: 2, lh: 1.25 }) + px(8);
        });
        ctaPill(P, H - P - px(64), s.cta);
      }
      // dots voor carrousel
      if (kind === "carousel") {
        const dotR = px(7), gap = px(26), totW = (total - 1) * gap;
        const startX = W / 2 - totW / 2, dy = H - px(46);
        for (let k = 0; k < total; k++) {
          parts.push(`<circle cx="${startX + k * gap}" cy="${dy}" r="${dotR}" fill="${k === idx ? t.accent : t.line}"/>`);
        }
      }
    }
    const sizeAttr = exportMode ? `width="${W}" height="${H}"` : `width="100%" style="width:100%;height:auto;display:block"`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" ${sizeAttr}>${parts.join("")}</svg>`;
  }

  // Een SVG die als los bestand of via <img> wordt geladen haalt geen externe afbeeldingen op,
  // dus de productfoto gaat voor export als data-URL in de SVG.
  async function metIngebeddeFoto(visual) {
    if (!visual?.productImage || /^data:/.test(visual.productImage)) return visual;
    try {
      const r = await fetch(visual.productImage);
      const blob = await r.blob();
      const dataUrl = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(blob); });
      return { ...visual, productImage: dataUrl };
    } catch (e) { return visual; }
  }
  async function downloadGrowthSVG(visual, idx, filename) {
    const v = await metIngebeddeFoto(visual);
    const blob = new Blob([growthSlideSVG(v, idx, true)], { type: "image/svg+xml;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  }
  async function downloadGrowthPNG(visual, idx, filename) {
    try { await document.fonts.ready; } catch (e) { }
    const dim = visual?.dim || { w: 1080, h: 1080 };
    visual = await metIngebeddeFoto(visual);
    const url = URL.createObjectURL(new Blob([growthSlideSVG(visual, idx, true)], { type: "image/svg+xml;charset=utf-8" }));
    await new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => {
        const cv = document.createElement("canvas"); cv.width = dim.w; cv.height = dim.h;
        cv.getContext("2d").drawImage(img, 0, 0, dim.w, dim.h);
        cv.toBlob(b => {
          URL.revokeObjectURL(url);
          if (!b) { rej(new Error("toBlob faalde")); return; }
          const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = filename; a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 1500); res();
        }, "image/png");
      };
      img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("SVG kon niet geladen worden")); };
      img.src = url;
    });
  }

  function VisualAssetPreview({ visual }) {
    const [i, setI] = useState(0);
    const [busy, setBusy] = useState(false);
    if (!visual || !Array.isArray(visual.slides) || visual.slides.length === 0)
      return <p style={{ fontSize: 12, color: C.dim }}>Geen visuele data in dit voorstel.</p>;
    const total = visual.slides.length;
    const cur = Math.min(i, total - 1);
    const base = `dif3r3nt-${visual.kind || "asset"}`;
    const svg = growthSlideSVG(visual, cur, false);
    const dimLabel = `${visual.dim?.w || 1080}×${visual.dim?.h || 1080}px`;
    async function dl(type) {
      setBusy(true);
      try {
        if (type === "svg") downloadGrowthSVG(visual, cur, `${base}-${cur + 1}.svg`);
        else if (type === "png") await downloadGrowthPNG(visual, cur, `${base}-${cur + 1}.png`);
        else if (type === "all") { for (let k = 0; k < total; k++) await downloadGrowthPNG(visual, k, `${base}-${k + 1}.png`); }
      } catch (e) { alert("Export mislukt: " + (e?.message || "onbekend")); }
      setBusy(false);
    }
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 9 }}>
          {total > 1 && <button className="btn bg" onClick={() => setI(p => (p - 1 + total) % total)} style={{ padding: "4px 11px" }}>‹</button>}
          <div style={{ maxWidth: 360, width: "100%", borderRadius: 8, overflow: "hidden", border: "1px solid " + C.border, boxShadow: "0 6px 22px rgba(0,0,0,0.4)" }} dangerouslySetInnerHTML={{ __html: svg }} />
          {total > 1 && <button className="btn bg" onClick={() => setI(p => (p + 1) % total)} style={{ padding: "4px 11px" }}>›</button>}
        </div>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 7, fontSize: 10, color: C.muted, marginBottom: 9, flexWrap: "wrap" }}>
          <span>{dimLabel}</span>
          {total > 1 && <span>· slide {cur + 1}/{total}</span>}
          <span>· thema {visual.theme || "dark"}</span>
          <span>· accent {visual.accent || "green"}</span>
        </div>
        <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn bg" onClick={() => dl("png")} disabled={busy} title="Download deze slide als PNG">⬇ PNG</button>
          <button className="btn bg" onClick={() => dl("svg")} disabled={busy} title="Download deze slide als SVG (vector)">⬇ SVG</button>
          {total > 1 && <button className="btn bg" onClick={() => dl("all")} disabled={busy} title="Download alle slides als losse PNG's">{busy ? "⟳ Exporteren..." : "⬇ Alle slides (PNG)"}</button>}
        </div>
      </div>
    );
  }

  // Toont een SEO-voorstel: SERP-snippet preview + zoekwoord-cluster + koppen + FAQ + JSON-LD
  function SeoPreview({ seo }) {
    const [copied, setCopied] = useState(false);
    if (!seo) return <p style={{ fontSize: 12, color: C.dim }}>Geen SEO-data in dit voorstel.</p>;
    const TITLE_MAX = 60, DESC_MAX = 155;
    const tLen = (seo.meta_title || "").length, dLen = (seo.meta_description || "").length;
    const prioCol = { hoog: C.green, midden: C.yellow, laag: C.muted };
    const typeCol = { "hoofd": C.purple, "long-tail": C.blue, "vraag": C.cyan };
    const intentBadge = { informationeel: C.blue, commercieel: C.yellow, transactioneel: C.green, navigatie: C.muted }[seo.search_intent] || C.muted;
    async function copyLd() { try { await navigator.clipboard.writeText(seo.jsonld || ""); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { alert("Kopiëren mislukt"); } }
    const Counter = ({ n, max }) => (<span style={{ fontSize: 10, color: n > max ? C.red : C.muted, fontWeight: n > max ? 700 : 400 }}>{n}/{max}{n > max ? " ⚠" : ""}</span>);
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {/* Google SERP-snippet preview */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
            <p className="lbl">Google-preview</p>
            {seo.search_intent && <Bdg label={"intent: " + seo.search_intent} bg={intentBadge + "22"} fg={intentBadge} />}
          </div>
          <div style={{ background: "#fff", borderRadius: 8, padding: "12px 14px", border: "1px solid " + C.border }}>
            <div style={{ fontSize: 12, color: "#202124", fontFamily: "arial,sans-serif" }}>dif3r3nt.nl{seo.slug || ""}</div>
            <div style={{ fontSize: 18, color: "#1a0dab", fontFamily: "arial,sans-serif", lineHeight: 1.3, margin: "1px 0 2px" }}>{seo.meta_title || "(geen meta-title)"}</div>
            <div style={{ fontSize: 13, color: "#4d5156", fontFamily: "arial,sans-serif", lineHeight: 1.5 }}>{seo.meta_description || "(geen meta-description)"}</div>
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 5 }}>
            <span style={{ fontSize: 10, color: C.muted }}>title <Counter n={tLen} max={TITLE_MAX} /></span>
            <span style={{ fontSize: 10, color: C.muted }}>description <Counter n={dLen} max={DESC_MAX} /></span>
          </div>
        </div>
        {/* Zoekwoord-cluster */}
        {Array.isArray(seo.keyword_cluster) && seo.keyword_cluster.length > 0 && (
          <div className="card" style={{ padding: 12 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>Zoekwoord-cluster {seo.target_keyword && <span style={{ color: C.blue, fontWeight: 600 }}>· hoofd: {seo.target_keyword}</span>}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {seo.keyword_cluster.map((k, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span style={{ flex: 1, color: C.text }}>{k.keyword}</span>
                  <Bdg label={k.type} bg={(typeCol[k.type] || C.muted) + "22"} fg={typeCol[k.type] || C.muted} />
                  <span style={{ fontSize: 10, color: prioCol[k.prioriteit] || C.muted, fontWeight: 600, minWidth: 46, textAlign: "right" }}>{k.prioriteit}</span>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 10, color: C.dim, marginTop: 7 }}>Prioriteit = redactionele inschatting (geen zoekvolume — niet beschikbaar zonder keyword-tool).</p>
          </div>
        )}
        {/* Kop-structuur */}
        {Array.isArray(seo.headings) && seo.headings.length > 0 && (
          <div className="card" style={{ padding: 12 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>Kop-structuur</p>
            {seo.headings.map((h, i) => (
              <div key={i} style={{ fontSize: 12, padding: "2px 0", paddingLeft: h.level === "H3" ? 28 : h.level === "H2" ? 14 : 0, color: h.level === "H1" ? C.text : C.muted }}>
                <span style={{ fontFamily: "monospace", fontSize: 10, color: C.dim, marginRight: 6 }}>{h.level}</span>{h.text}
              </div>
            ))}
          </div>
        )}
        {/* FAQ */}
        {Array.isArray(seo.faq) && seo.faq.length > 0 && (
          <div className="card" style={{ padding: 12 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>❓ FAQ ({seo.faq.length})</p>
            {seo.faq.map((f, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{f.q}</p>
                <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{f.a}</p>
              </div>
            ))}
          </div>
        )}
        {/* Interne links */}
        {Array.isArray(seo.internal_links) && seo.internal_links.length > 0 && (
          <div className="card" style={{ padding: 12 }}>
            <p className="lbl" style={{ marginBottom: 8 }}>Interne links</p>
            {seo.internal_links.map((l, i) => (
              <div key={i} style={{ fontSize: 12, padding: "2px 0" }}><span style={{ color: C.blue }}>{l.anchor}</span><span style={{ color: C.dim }}> → {l.naar}</span></div>
            ))}
          </div>
        )}
        {/* JSON-LD schema */}
        {seo.jsonld && (
          <div className="card" style={{ padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
              <p className="lbl">{`{} `}FAQ-schema (JSON-LD)</p>
              <button className="btn bg" style={{ fontSize: 10, padding: "3px 9px" }} onClick={copyLd}>{copied ? "✓ Gekopieerd" : "Kopieer schema"}</button>
            </div>
            <pre style={{ margin: 0, padding: "9px 11px", background: C.bg, border: "1px solid " + C.border, borderRadius: 6, fontSize: 10.5, lineHeight: 1.5, color: C.muted, overflow: "auto", maxHeight: 220, fontFamily: "monospace" }}>{seo.jsonld}</pre>
          </div>
        )}
      </div>
    );
  }

  function GrowthReviewCard({ rv, canDecide, onBack, onApprove, onReject, onEdit, onPlan, onRegenerate, regenerating, auditLog, onSaveInline, onRefine }) {
    const sc = GROWTH_STATUS_COL[rv.status] || GROWTH_STATUS_COL.pending;
    const eng = GROWTH_ENGINES.find(e => e.id === rv.engine);
    const body = rv.preview?.body || "";
    const meta = rv.preview?.metadata || {};
    const ctx = rv.data_context || {};
    const vis = rv.preview?.visual;
    const seo = rv.preview?.seo;
    const typeLabel = (ALL_TYPES.find(t => t.id === rv.type) || {}).l || rv.type;
    // Inline bewerken + live preview + AI-verfijn (lokale state; opslaan via onSaveInline → decide).
    const [editing, setEditing] = useState(false);
    const [eBody, setEBody] = useState("");
    const [eSlides, setESlides] = useState(null);
    const [eSeo, setESeo] = useState(null);
    const [eSummary, setESummary] = useState("");
    const [refining, setRefining] = useState(false);
    const [saving, setSaving] = useState(false);
    function startEdit() {
      setEBody(typeof body === "string" ? body : JSON.stringify(body, null, 2));
      setESlides(vis && Array.isArray(vis.slides) ? vis.slides.map(s => ({ ...s })) : null);
      setESeo(seo ? { meta_title: seo.meta_title || "", meta_description: seo.meta_description || "", slug: seo.slug || "" } : null);
      setESummary(""); setEditing(true);
    }
    async function doRefine(instr) {
      if (!onRefine || refining || !eBody.trim()) return;
      setRefining(true);
      try { const out = await onRefine(eBody, instr); if (out) setEBody(out); } catch (e) { /* stil */ }
      setRefining(false);
    }
    async function doSave() {
      if (saving) return;
      setSaving(true);
      const newPreview = { ...rv.preview, body: eBody };
      if (vis && Array.isArray(eSlides)) newPreview.visual = { ...vis, slides: eSlides };
      if (seo && eSeo) newPreview.seo = { ...rv.preview.seo, ...eSeo };
      try { await onSaveInline(newPreview, eSummary); setEditing(false); } catch (e) { /* stil */ }
      setSaving(false);
    }
    const liveVis = editing && vis ? { ...vis, slides: eSlides || vis.slides } : vis;
    const liveSeo = editing && seo ? { ...rv.preview.seo, ...(eSeo || {}) } : seo;
    return (
      <div className="fu">
        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14, flexWrap: "wrap" }}>
          <button className="btn bg" onClick={onBack}>← Terug</button>
          <h2 style={{ ...KOP_STIJL, fontSize: 16, flex: 1 }}>{rv.title}</h2>
          {eng && <Bdg label={eng.icon + " " + eng.l} bg={eng.color + "20"} fg={eng.color} />}
          <Bdg label={typeLabel} bg={C.border} fg={C.muted} />
          <Bdg label={sc.l} bg={sc.bg} fg={sc.fg} />
        </div>
        {rv.status === "pending" && (
          <div style={{ display: "flex", gap: 7, padding: "11px 14px", background: canDecide ? C.greenDim : C.border, border: "1px solid " + (canDecide ? C.green : C.muted), borderRadius: 8, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, flex: 1, color: canDecide ? C.green : C.muted }}>{canDecide ? "Jij bent reviewer — keur dit voorstel goed, bewerk of wijs af." : "⏸ Wacht op review door Furgel."}</span>
            {canDecide && (
              <>
                <button className="btn bp" onClick={onApprove} style={{ background: C.green, color: "#000" }}>✓ Approve</button>
                <button className="btn bg" onClick={editing ? () => setEditing(false) : startEdit}>{editing ? "Sluit bewerken" : "✎ Edit"}</button>
                <button className="btn bg" onClick={onPlan}>Plan Later</button>
                <button className="btn bg" onClick={onReject} style={{ background: C.red + "20", color: C.red }}>✗ Reject</button>
                {onRegenerate && <button className="btn bg" onClick={onRegenerate} disabled={regenerating} title="Vervang content via huidige AI-prompt (na fix). Behoudt audit log.">{regenerating ? "⟳ Herzien..." : "Herzie met huidige AI"}</button>}
              </>
            )}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <div className="card"><p className="lbl" style={{ marginBottom: 7 }}>Doel</p><p style={{ fontSize: 13, lineHeight: 1.6 }}>{rv.doel || "—"}</p></div>
            <div className="card"><p className="lbl" style={{ marginBottom: 7 }}>Waarom NU</p><p style={{ fontSize: 13, lineHeight: 1.6, color: C.muted }}>{rv.waarom || "—"}</p></div>
            <div className="card">
              <p className="lbl" style={{ marginBottom: 7 }}>Data & signalen</p>
              {Array.isArray(ctx.signalen) && ctx.signalen.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7, color: C.muted }}>{ctx.signalen.map((s, i) => <li key={i}>{s}</li>)}</ul>
              ) : <p style={{ fontSize: 12, color: C.dim }}>—</p>}
              {ctx.doelgroep && <p style={{ fontSize: 11, color: C.muted, marginTop: 8 }}><span style={{ color: C.dim }}>Doelgroep:</span> {ctx.doelgroep}</p>}
              {ctx.kanaal && <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}><span style={{ color: C.dim }}>Kanaal:</span> {ctx.kanaal}</p>}
              {ctx.vorige_leerpunten && <p style={{ fontSize: 11, color: C.cyan, marginTop: 7, padding: 7, background: C.cyanDim, borderRadius: 5 }}>Geleerd uit eerdere reviews: {ctx.vorige_leerpunten}</p>}
            </div>
            <div className="card" style={{ borderColor: C.yellow + "40" }}><p className="lbl" style={{ marginBottom: 7, color: C.yellow }}>⚠ Risico's</p><p style={{ fontSize: 12, lineHeight: 1.6 }}>{rv.risico || "—"}</p></div>
            <div className="card" style={{ borderColor: C.green + "40" }}><p className="lbl" style={{ marginBottom: 7, color: C.green }}>Verwachte impact</p><p style={{ fontSize: 12, lineHeight: 1.6 }}>{rv.impact || "—"}</p></div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9, flexWrap: "wrap", gap: 5 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <p className="lbl" style={{ margin: 0 }}>{seo ? "SEO-voorstel" : vis ? "Visuele preview" : "Preview"}</p>
                  {body && <button onClick={() => navigator.clipboard?.writeText(typeof body === "string" ? body : JSON.stringify(body, null, 2))} style={{ background: "none", border: "1px solid " + C.border, borderRadius: 4, color: C.muted, cursor: "pointer", fontSize: 11, padding: "1px 7px" }} title="Kopieer content">⎘</button>}
                </div>
                {Object.keys(meta).length > 0 && (
                  <div style={{ display: "flex", gap: 5, fontSize: 10, color: C.muted, flexWrap: "wrap" }}>
                    {meta.woordenaantal > 0 && <span>{meta.woordenaantal} woorden</span>}
                    {meta.leestijd_min > 0 && <span>· {meta.leestijd_min}min lezen</span>}
                    {meta.tone && <span>· {meta.tone}</span>}
                    {meta.kanaal && <span>· {meta.kanaal}</span>}
                  </div>
                )}
              </div>
              {liveSeo && <SeoPreview seo={liveSeo} />}
              {liveVis && (
                <div style={{ marginBottom: 11, padding: "13px", background: C.bg, border: "1px solid " + C.border, borderRadius: 6 }}>
                  <VisualAssetPreview visual={liveVis} />
                </div>
              )}
              {rv.preview?.video && (
                <div style={{ marginBottom: 11, padding: "13px", background: C.bg, border: "1px solid " + C.border, borderRadius: 6 }}>
                  <p className="lbl" style={{ marginBottom: 8 }}>Video (image-to-video, eigen dagbudget)</p>
                  {rv.preview.video_url ? (
                    <video src={rv.preview.video_url} controls style={{ width: "100%", maxWidth: 320, borderRadius: 6, background: "#000", display: "block", margin: "0 auto" }} />
                  ) : rv.preview.video_error ? (
                    <p style={{ fontSize: 12, color: C.red }}>✗ {rv.preview.video_error}</p>
                  ) : rv.preview.video_job_id ? (
                    <p style={{ fontSize: 12, color: C.muted }}>⟳ Video wordt gegenereerd...</p>
                  ) : (
                    <p style={{ fontSize: 12, color: C.dim }}>Nog niet gestart.</p>
                  )}
                  {rv.preview.video.caption && <p style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{rv.preview.video.caption}</p>}
                </div>
              )}
              {editing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
                  {seo && (<>
                    <Fld label="Meta title"><input value={(eSeo && eSeo.meta_title) || ""} onChange={e => setESeo(p => ({ ...(p || {}), meta_title: e.target.value }))} /></Fld>
                    <Fld label="Meta description"><textarea rows={2} value={(eSeo && eSeo.meta_description) || ""} onChange={e => setESeo(p => ({ ...(p || {}), meta_description: e.target.value }))} /></Fld>
                  </>)}
                  {vis && Array.isArray(eSlides) && eSlides.map((s, i) => (
                    <div key={i} style={{ border: "1px solid " + C.border, borderRadius: 6, padding: 9 }}>
                      <p className="lbl" style={{ marginBottom: 6, color: C.cyan }}>Slide {i + 1}</p>
                      <Fld label="Kop"><input value={s.headline || ""} onChange={e => setESlides(prev => prev.map((x, j) => j === i ? { ...x, headline: e.target.value } : x))} /></Fld>
                      <Fld label="Subtekst"><input value={s.sub || ""} onChange={e => setESlides(prev => prev.map((x, j) => j === i ? { ...x, sub: e.target.value } : x))} /></Fld>
                      <Fld label="Body"><textarea rows={2} value={s.body || ""} onChange={e => setESlides(prev => prev.map((x, j) => j === i ? { ...x, body: e.target.value } : x))} /></Fld>
                      <Fld label="CTA"><input value={s.cta || ""} onChange={e => setESlides(prev => prev.map((x, j) => j === i ? { ...x, cta: e.target.value } : x))} /></Fld>
                    </div>
                  ))}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, flexWrap: "wrap" }}>
                      <p className="lbl" style={{ margin: 0 }}>{seo ? "Content-brief" : vis ? "Begeleidende tekst" : "Tekst"}</p>
                      <span style={{ fontSize: 10, color: C.dim }}>AI-verfijn:</span>
                      {["Korter", "Overtuigender", "Informeler", "Zakelijker"].map(instr => (
                        <button key={instr} className="btn bg" style={{ fontSize: 10, padding: "2px 8px" }} disabled={refining || !eBody.trim()} onClick={() => doRefine(instr.toLowerCase())}>{refining ? "⟳" : instr}</button>
                      ))}
                    </div>
                    <textarea rows={(vis || seo) ? 4 : 12} value={eBody} onChange={e => setEBody(e.target.value)} placeholder="Tekst…" />
                  </div>
                  <Fld label="Korte samenvatting van je wijziging (leert de AI)"><input value={eSummary} onChange={e => setESummary(e.target.value)} placeholder="bijv. 'informeler en korter'" /></Fld>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button className="btn bg" onClick={() => setEditing(false)}>Annuleer</button>
                    <button className="btn bp" disabled={saving} onClick={doSave}>{saving ? "Opslaan…" : "✓ Opslaan"}</button>
                  </div>
                </div>
              ) : (<>
                {(body || (!vis && !seo)) && (<>
                  {(vis || seo) && <p className="lbl" style={{ margin: "11px 0 6px" }}>{seo ? "Content-brief" : "✍ Begeleidende tekst"}</p>}
                  <div style={{ padding: "11px 13px", background: C.bg, border: "1px solid " + C.border, borderRadius: 6, maxHeight: (vis || seo) ? 260 : 520, overflow: "auto" }}>
                    <p style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap", color: C.text }}>{typeof body === "string" ? body : JSON.stringify(body, null, 2)}</p>
                  </div>
                </>)}
                {Array.isArray(meta.primaire_keywords) && meta.primaire_keywords.length > 0 && (
                  <div style={{ marginTop: 9, display: "flex", gap: 5, flexWrap: "wrap" }}>
                    {meta.primaire_keywords.map((k, i) => <span key={i} onClick={() => navigator.clipboard?.writeText(k)} title="Klik om te kopiëren" style={{ padding: "3px 9px", fontSize: 11, background: C.blueDim, color: C.blue, borderRadius: 11, cursor: "pointer" }}>{k}</span>)}
                  </div>
                )}
              </>)}
            </div>
            {rv.status === "rejected" && rv.reject_reason && (
              <div className="card" style={{ borderColor: C.red, background: C.redDim }}>
                <p className="lbl" style={{ color: C.red, marginBottom: 7 }}>✗ Reden afkeuring</p>
                <p style={{ fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{rv.reject_reason}</p>
              </div>
            )}
            {rv.status === "edited" && rv.edits_summary && (
              <div className="card" style={{ borderColor: C.blue + "60" }}>
                <p className="lbl" style={{ color: C.blue, marginBottom: 7 }}>✎ Bewerking door Furgel</p>
                <p style={{ fontSize: 12, lineHeight: 1.6 }}>{rv.edits_summary}</p>
              </div>
            )}
            {rv.status === "planned" && rv.scheduled_for && (
              <div className="card" style={{ borderColor: C.cyan + "60" }}>
                <p className="lbl" style={{ color: C.cyan, marginBottom: 7 }}>Gepland</p>
                <p style={{ fontSize: 12, lineHeight: 1.6 }}>Publicatiedatum: <strong>{rv.scheduled_for}</strong></p>
              </div>
            )}
            <div className="card">
              <p className="lbl" style={{ marginBottom: 9 }}>Audit log ({auditLog.length})</p>
              {auditLog.length === 0 && <p style={{ fontSize: 12, color: C.dim }}>Geen log entries.</p>}
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflow: "auto" }}>
                {auditLog.map(a => (
                  <div key={a.id} style={{ padding: "7px 9px", borderRadius: 5, background: C.bg, fontSize: 11, fontFamily: "monospace" }}>
                    <span style={{ color: C.cyan, fontWeight: 600 }}>{a.action}</span>
                    <span style={{ color: C.muted }}> · {(a.actor || "").split("@")[0]}</span>
                    <span style={{ color: C.dim }}> · {(a.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    {a.notes && <p style={{ color: C.muted, marginTop: 3, fontFamily: "sans-serif" }}>{a.notes}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── SDA — Scraping & Data Automation (MVP: OS-laag; VPS-worker via webhook) ───
  const SDA_BASIS = [
    { v: "b2b_leadgen", l: "B2B leadgeneratie", h: "Zakelijke contactgegevens van bedrijven verzamelen voor sales/outreach (geen consumenten)." },
    { v: "legitiem_belang", l: "Gerechtvaardigd belang", h: "Je hebt een aantoonbaar zakelijk belang dat zwaarder weegt dan de privacy-impact." },
    { v: "publieke_bron", l: "Publieke bron", h: "Data staat openbaar online en is bedoeld om publiek geraadpleegd te worden." },
  ];
  // Uitleg per template — wat haalt het op en waar landt het in de OS.
  const SDA_TEMPLATE_INFO = {
    kvk_signaal_monitor: { titel: "KvK-signaalmonitor", doel: "Nieuwe inschrijvingen / bedrijfswijzigingen volgen.", landt: "→ automatisch als lead in CRM", voorbeeld: "https://www.kvk.nl/zoeken/?q=..." },
    concurrent_prijzen: { titel: "Concurrentprijzen", doel: "Prijzen van concurrenten periodiek volgen.", landt: "→ marktdata voor Growth-content", voorbeeld: "https://concurrent.nl/prijzen" },
    vacature_trends: { titel: "Vacaturetrends", doel: "Vacatures per niche/sector volgen.", landt: "→ trendsignalen voor Growth-content", voorbeeld: "https://www.indeed.nl/vacatures?q=..." },
  };
  function SDAModule({ launches, user }) {
    const userKey = (user?.email || "").toLowerCase();
    const [templates, setTemplates] = useState([]);
    const [intents, setIntents] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [form, setForm] = useState({ template_id: "", target_url: "", legal_intent_id: "", parameters: "{}", dienst_id: "" });
    const [ni, setNi] = useState({ basis: "b2b_leadgen", beschrijving: "" });
    const [banner, setBanner] = useState("");
    const [busy, setBusy] = useState(false);
    const [showIntent, setShowIntent] = useState(false);

    async function sdaCall(path, body, method = "POST") {
      const base = process.env.REACT_APP_SUPABASE_URL, anon = process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!userKey) throw new Error("Niet ingelogd — kan SDA niet gebruiken.");
      // De functie leest wie je bent uit het inlogtoken, niet uit wat de browser opgeeft.
      const r = await fetch(`${base}/functions/v1/${path}`, {
        method,
        headers: await metSessie({ "Content-Type": "application/json", apikey: anon }),
        body: method === "GET" ? undefined : JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error?.message || ("HTTP " + r.status));
      return d;
    }

    async function loadAll() {
      const [{ data: tpls }, { data: ins }, { data: js }] = await Promise.all([
        supabase.from("template_registry").select("*").eq("actief", true).order("naam"),
        supabase.from("legal_intents").select("id,basis,beschrijving,created_at").order("created_at", { ascending: false }),
        supabase.from("scraping_jobs").select("*").order("created_at", { ascending: false }).limit(30),
      ]);
      if (tpls) setTemplates(tpls); if (ins) setIntents(ins); if (js) setJobs(js);
    }
    useEffect(() => {
      loadAll();
      const ch = supabase.channel("sda_jobs_live").on("postgres_changes", { event: "*", schema: "public", table: "scraping_jobs" }, () => loadAll()).subscribe();
      return () => { supabase.removeChannel(ch); };
      /* eslint-disable-next-line */
    }, []);

    async function createIntent() {
      if (ni.beschrijving.trim().length < 50) { setBanner("⚠ Toelichting moet minimaal 50 tekens zijn."); return; }
      setBusy(true); setBanner("");
      try { const d = await sdaCall("legal-intents", ni); await loadAll(); setForm(p => ({ ...p, legal_intent_id: d.legal_intent_id })); setNi({ basis: "b2b_leadgen", beschrijving: "" }); setShowIntent(false); setBanner("✓ Juridische grondslag vastgelegd."); }
      catch (e) { setBanner("✗ " + (e.message || "Fout")); }
      setBusy(false);
    }
    async function createJob() {
      if (!form.template_id || !form.target_url || !form.legal_intent_id) { setBanner("⚠ Kies template, grondslag én doel-URL."); return; }
      let params = {}; try { params = JSON.parse(form.parameters || "{}"); } catch { setBanner("⚠ Parameters zijn geen geldige JSON."); return; }
      setBusy(true); setBanner("");
      try {
        const d = await sdaCall("scraping-jobs", { template_id: form.template_id, target_url: form.target_url, legal_intent_id: form.legal_intent_id, parameters: params, dienst_id: form.dienst_id || undefined });
        setForm(p => ({ ...p, target_url: "" })); await loadAll();
        setBanner(d?.dispatch?.dispatched ? "✓ Opdracht gestart — de motor verzamelt nu. Status loopt live mee bij stap 3." : "✓ Opdracht aangemaakt — " + (d?.dispatch?.reason || "start automatisch") + ".");
      }
      catch (e) { setBanner("✗ " + (e.message || "Fout")); }
      setBusy(false);
    }

    const sc = { pending: C.yellow, running: C.blue, completed: C.green, failed: C.red, paused: C.muted };
    const scLabel = { pending: "in wachtrij", running: "bezig", completed: "klaar", failed: "mislukt", paused: "gepauzeerd" };
    const intentLabel = i => `${(SDA_BASIS.find(b => b.v === i.basis) || {}).l || i.basis} — ${i.beschrijving.slice(0, 40)}${i.beschrijving.length > 40 ? "…" : ""}`;
    const selTpl = templates.find(t => t.id === form.template_id);
    const tInfo = selTpl ? SDA_TEMPLATE_INFO[selTpl.naam] : null;
    const selBasisHelp = (SDA_BASIS.find(b => b.v === ni.basis) || {}).h;
    const Num = ({ n }) => (<span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: "50%", background: C.purple + "22", color: C.purple, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{n}</span>);

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 13, maxWidth: 760 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 3 }}>Scraping &amp; Data Automation</h2>
          <p style={{ fontSize: 12, color: C.muted }}>Verzamel automatisch openbare marktdata (bedrijven, prijzen, vacatures) en laat die in je CRM en Live Diensten landen — AVG-proof.</p>
        </div>

        {/* Zo werkt het */}
        <div className="card" style={{ background: C.purple + "0c", border: "1px solid " + C.purple + "33" }}>
          <p className="lbl" style={{ marginBottom: 8, color: C.purple }}>ZO WERKT HET</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}><Num n={1} /><p style={{ fontSize: 12, color: C.text }}><strong>Leg een grondslag vast</strong> — waaróm mag je deze data verzamelen (AVG). Dit hoeft maar één keer per type doel.</p></div>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}><Num n={2} /><p style={{ fontSize: 12, color: C.text }}><strong>Maak een opdracht</strong> — kies wát je wilt verzamelen (template), geef de website en start.</p></div>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}><Num n={3} /><p style={{ fontSize: 12, color: C.text }}><strong>Volg het resultaat</strong> — de status loopt live mee; klaar = de data staat in CRM / Live Diensten.</p></div>
          </div>
        </div>

        {/* Status verzamel-motor */}
        <div style={{
          padding: "9px 12px", borderRadius: 7, fontSize: 11.5, display: "flex", gap: 8, alignItems: "flex-start",
          background: C.greenDim, border: "1px solid " + C.green + "44", color: C.green
        }}>
          <span style={{ fontSize: 13 }}></span>
          <span><strong>Verzamel-motor actief (ingebouwd).</strong> Opdrachten worden direct verwerkt — de status loopt live mee bij stap 3. Geen aparte server nodig.</span>
        </div>

        {banner && <div style={{ padding: "10px 13px", borderRadius: 7, fontSize: 12, background: banner.startsWith("✗") ? C.redDim : banner.startsWith("⚠") ? C.yellowDim : C.greenDim, border: "1px solid " + (banner.startsWith("✗") ? C.red : banner.startsWith("⚠") ? C.yellow : C.green) + "55", color: banner.startsWith("✗") ? C.red : banner.startsWith("⚠") ? C.yellow : C.green }}>{banner}</div>}

        {/* Stap 1 — Grondslag */}
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 9, alignItems: "center" }}><Num n={1} /><p className="lbl" style={{ margin: 0 }}>Juridische grondslag (verplicht)</p></div>
          <p style={{ fontSize: 11, color: C.dim, margin: "-2px 0 0 29px" }}>Zonder grondslag weigert het systeem de opdracht. Kies een bestaande of leg een nieuwe vast.</p>
          <select value={form.legal_intent_id} onChange={e => setForm(p => ({ ...p, legal_intent_id: e.target.value }))} style={{ fontSize: 12 }}>
            <option value="">— Kies bestaande grondslag —</option>
            {intents.map(i => <option key={i.id} value={i.id}>{intentLabel(i)}</option>)}
          </select>
          {intents.length === 0 && !showIntent && <p style={{ fontSize: 11, color: C.yellow }}>Nog geen grondslag — leg er eerst één vast </p>}
          {!showIntent
            ? <button className="btn bg" style={{ fontSize: 11, alignSelf: "flex-start" }} onClick={() => setShowIntent(true)}>+ Nieuwe grondslag vastleggen</button>
            : (<div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid " + C.border, paddingTop: 10 }}>
              <select value={ni.basis} onChange={e => setNi(p => ({ ...p, basis: e.target.value }))} style={{ fontSize: 12 }}>
                {SDA_BASIS.map(b => <option key={b.v} value={b.v}>{b.l}</option>)}
              </select>
              {selBasisHelp && <p style={{ fontSize: 11, color: C.dim }}>ℹ {selBasisHelp}</p>}
              <textarea rows={3} value={ni.beschrijving} onChange={e => setNi(p => ({ ...p, beschrijving: e.target.value }))} placeholder="Bijv: 'B2B-leadgeneratie: openbare bedrijfsgegevens van MKB-bouwbedrijven in NL verzamelen voor onze sales-outreach naar zakelijke prospects.'" style={{ fontSize: 12 }} />
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button className="btn bp" style={{ fontSize: 11 }} disabled={busy} onClick={createIntent}>Vastleggen</button>
                <button className="btn bg" style={{ fontSize: 11 }} onClick={() => setShowIntent(false)}>Annuleer</button>
                <span style={{ fontSize: 10, color: ni.beschrijving.trim().length >= 50 ? C.green : C.dim, alignSelf: "center" }}>{ni.beschrijving.trim().length}/50 tekens</span>
              </div>
            </div>)}
        </div>

        {/* Stap 2 — Opdracht */}
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 9, alignItems: "center" }}><Num n={2} /><p className="lbl" style={{ margin: 0 }}>Opdracht aanmaken</p></div>
          <div>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Wat wil je verzamelen?</p>
            <select value={form.template_id} onChange={e => setForm(p => ({ ...p, template_id: e.target.value }))} style={{ fontSize: 12 }}>
              <option value="">— Kies een template —</option>
              {templates.map(t => { const inf = SDA_TEMPLATE_INFO[t.naam]; return <option key={t.id} value={t.id}>{inf ? inf.titel : t.naam}</option>; })}
            </select>
            {tInfo && <p style={{ fontSize: 11, color: C.dim, marginTop: 5 }}>ℹ {tInfo.doel} <span style={{ color: C.purple }}>{tInfo.landt}</span></p>}
          </div>
          <div>
            <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Doel-website (alleen openbare HTTPS-pagina)</p>
            <input value={form.target_url} onChange={e => setForm(p => ({ ...p, target_url: e.target.value }))} placeholder={tInfo ? tInfo.voorbeeld : "https://voorbeeld.nl/…"} style={{ fontSize: 12 }} />
            <p style={{ fontSize: 10.5, color: C.dim, marginTop: 4, lineHeight: 1.5 }}>Tip: grote retailers/platforms met botbescherming (Adidas, Bol, Marktplaats…) geven 403 en zijn níét te scrapen met de ingebouwde motor. Gebruik openbare, server-gerenderde lijst-/gidspagina's (branchegidsen, ledenlijsten, KvK-achtige overzichten).</p>
          </div>
          {Array.isArray(launches) && launches.length > 0 && (
            <div>
              <p style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Koppel aan dienst <span style={{ color: C.dim }}>(optioneel — dan kan Growth deze marktdata gebruiken bij content)</span></p>
              <select value={form.dienst_id} onChange={e => setForm(p => ({ ...p, dienst_id: e.target.value }))} style={{ fontSize: 12 }}>
                <option value="">— Geen koppeling —</option>
                {launches.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
              </select>
            </div>
          )}
          <details>
            <summary style={{ fontSize: 11, color: C.dim, cursor: "pointer" }}>Geavanceerd (optioneel)</summary>
            <textarea rows={2} value={form.parameters} onChange={e => setForm(p => ({ ...p, parameters: e.target.value }))} placeholder='Parameters (JSON), bijv. {"max_pages":20}' style={{ fontSize: 11, fontFamily: "monospace", marginTop: 6 }} />
          </details>
          <button className="btn bp" style={{ fontSize: 12, fontWeight: 700, alignSelf: "flex-start", opacity: busy ? .6 : 1 }} disabled={busy} onClick={createJob}>{busy ? "⟳ Bezig…" : "▶ Opdracht starten"}</button>
          <p style={{ fontSize: 10, color: C.dim }}>Limieten per run: max. 50 pagina's / 10 min. Persoonsgegevens worden vóór opslag verwijderd. Resultaten verlopen na 30 dagen.</p>
        </div>

        {/* Stap 3 — Volgen */}
        <div className="card">
          <div style={{ display: "flex", gap: 9, alignItems: "center", marginBottom: 8 }}><Num n={3} /><p className="lbl" style={{ margin: 0 }}>Opdrachten volgen</p></div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 9 }}>
            {Object.entries(scLabel).map(([k, v]) => (
              <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, color: C.dim }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc[k] }} />{v}
              </span>
            ))}
          </div>
          {jobs.length === 0
            ? <p style={{ fontSize: 12, color: C.dim }}>Nog geen opdrachten. Maak je eerste aan bij stap 2.</p>
            : jobs.map(j => (
              <div key={j.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid " + C.border }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc[j.status] || C.muted, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.target_url}</span>
                  </div>
                  <span style={{ fontSize: 10, color: C.dim, fontFamily: "monospace" }}>{scLabel[j.status] || j.status} · {j.records_extracted || 0} records</span>
                  {j.status === "failed" && j.error_message && <p style={{ fontSize: 11.5, color: C.yellow, lineHeight: 1.5, marginTop: 5, whiteSpace: "pre-wrap" }}>⚠ {j.error_message}</p>}
                </div>
                <span style={{ fontSize: 9, color: C.dim, fontFamily: "monospace", marginLeft: 8 }}>{new Date(j.created_at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
        </div>
      </div>
    );
  }

  // ─── AI VIDEO STUDIO (Growth fase 2 — Replicate, inert zonder VIDEO_API_KEY) ───
  // Reclame-studio (Fase 1): Gemini schrijft een scene-script, Nano Banana maakt
  // per scene een beeld. Animeren (Fase 2) + samenvoegen tot 1 video (Fase 3) volgt.
  function AdReclame({ user }) {
    const [brief, setBrief] = useState("");
    const [seconds, setSeconds] = useState(30);
    const [aspect, setAspect] = useState("9:16");
    const [brand, setBrand] = useState("Dif3r3nt - strak, modern, blauw accent, zakelijk vertrouwen");
    const [script, setScript] = useState(null);
    const [busy, setBusy] = useState(false);
    const [imgBusy, setImgBusy] = useState({});
    const [images, setImages] = useState({});
    const [banner, setBanner] = useState("");

    function setScene(i, patch) { setScript(s => s ? { ...s, scenes: s.scenes.map((x, k) => k === i ? { ...x, ...patch } : x) } : s); }

    async function genScript() {
      if (!brief.trim() || busy) return;
      setBusy(true); setBanner(""); setImages({});
      try {
        const r = await apiFetch("/api/video", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind: "script", brief, seconds, sceneSeconds: 5, aspectRatio: aspect, brand }) });
        const d = await r.json();
        if (!r.ok || !d.script) { setBanner("✗ " + (d.error || "Script genereren mislukt")); }
        else setScript(d.script);
      } catch (e) { setBanner("✗ Fout: " + (e.message || "?")); }
      setBusy(false);
    }
    async function genImage(i) {
      const sc = script?.scenes?.[i]; if (!sc) return;
      setImgBusy(p => ({ ...p, [i]: true })); setBanner("");
      try {
        const r = await apiFetch("/api/video", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ kind: "nano_image", prompt: sc.visual_prompt, aspectRatio: aspect }) });
        const d = await r.json();
        if (!r.ok) setBanner((d.code === "billing" ? "⚙ " : "✗ ") + (d.error || "Beeld mislukt"));
        else if (d.url || d.dataUrl) { setImages(p => ({ ...p, [i]: d.url || d.dataUrl })); if (d.warning) setBanner("⚠ " + d.warning); }
      } catch (e) { setBanner("✗ Fout: " + (e.message || "?")); }
      setImgBusy(p => ({ ...p, [i]: false }));
    }
    async function genAll() { for (let i = 0; i < (script?.scenes?.length || 0); i++) { if (!images[i]) await genImage(i); } }

    const pw = { "9:16": 130, "1:1": 190, "16:9": 230 }[aspect] || 130;
    const ph = { "9:16": 231, "1:1": 190, "16:9": 129 }[aspect] || 231;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {banner && <div style={{ padding: "10px 13px", borderRadius: 7, fontSize: 12, background: banner.startsWith("✗") ? C.redDim : C.yellowDim, border: "1px solid " + (banner.startsWith("✗") ? C.red : C.yellow) + "55", color: banner.startsWith("✗") ? C.red : C.yellow }}>{banner}</div>}
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p className="lbl">Reclame-studio</p>
            <Bdg label="FASE 1: SCRIPT + BEELD" bg={C.blueDim} fg={C.blue} />
          </div>
          <p style={{ fontSize: 11, color: C.muted }}>Beschrijf de dienst/boodschap. Het script wordt in scenes opgebouwd (TV-lengte), per scene genereer je een beeld met Nano Banana. Animeren + samenvoegen tot 1 video volgt in fase 2/3.</p>
          <Fld label="Brief / boodschap *">
            <textarea rows={4} value={brief} onChange={e => setBrief(e.target.value)} placeholder="Bijv: Reason3n schoont automatisch je sales-pipeline op en corrigeert deal-stages op basis van echte engagement-data. Voor sales-teams die tijd verliezen aan datavervuiling." />
          </Fld>
          <div className="frm-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 10 }}>
            <Fld label="Lengte"><select value={seconds} onChange={e => setSeconds(Number(e.target.value))}><option value={15}>15 sec (3 scenes)</option><option value={30}>30 sec (6 scenes)</option></select></Fld>
            <Fld label="Verhouding"><select value={aspect} onChange={e => setAspect(e.target.value)}><option value="9:16">9:16 (verticaal)</option><option value="1:1">1:1 (vierkant)</option><option value="16:9">16:9 (breed)</option></select></Fld>
            <Fld label="Huisstijl (optioneel)"><input value={brand} onChange={e => setBrand(e.target.value)} /></Fld>
          </div>
          <button className="btn bp" disabled={!brief.trim() || busy} onClick={genScript}>{busy ? "Script schrijven..." : (script ? "Nieuw script" : "Genereer script")}</button>
        </div>

        {script && (
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 600 }}>{script.title || "Reclame"}</p>
                {script.hook && <p style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>"{script.hook}"</p>}
              </div>
              <button className="btn bg" style={{ fontSize: 11 }} onClick={genAll}>Genereer alle beelden</button>
            </div>
            {script.scenes.map((sc, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "11px 0", borderTop: "1px solid " + C.border }}>
                <div style={{ flexShrink: 0, width: pw }}>
                  <div style={{ width: pw, height: ph, borderRadius: 6, border: "1px solid " + C.border, background: C.bg, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {images[i] ? <img src={images[i]} alt={"Scene " + (i + 1)} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 10, color: C.dim, textAlign: "center", padding: 6 }}>{imgBusy[i] ? "Genereren..." : "Geen beeld"}</span>}
                  </div>
                  <button className="btn bg" style={{ fontSize: 10, width: "100%", marginTop: 5 }} disabled={imgBusy[i]} onClick={() => genImage(i)}>{imgBusy[i] ? "..." : (images[i] ? "Opnieuw" : "Genereer beeld")}</button>
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 11, color: C.blue, fontFamily: "monospace" }}>SCENE {sc.n} · {sc.seconds}s</span>
                  <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 2 }}>Beeld-prompt (EN)</p><textarea rows={2} value={sc.visual_prompt || ""} onChange={e => setScene(i, { visual_prompt: e.target.value })} style={{ fontSize: 11, width: "100%" }} /></div>
                  <div className="frm-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 2 }}>Beeldtekst</p><input value={sc.on_screen_text || ""} onChange={e => setScene(i, { on_screen_text: e.target.value })} style={{ fontSize: 11 }} /></div>
                    <div><p style={{ fontSize: 10, color: C.dim, marginBottom: 2 }}>Voice-over</p><input value={sc.voiceover || ""} onChange={e => setScene(i, { voiceover: e.target.value })} style={{ fontSize: 11 }} /></div>
                  </div>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, color: C.muted, borderTop: "1px solid " + C.border, paddingTop: 9 }}>
              {script.cta && <span><strong style={{ color: C.dim }}>CTA:</strong> {script.cta}</span>}
              {script.music && <span><strong style={{ color: C.dim }}>Muziek:</strong> {script.music}</span>}
            </div>
            <p style={{ fontSize: 11, color: C.dim, background: C.bg, border: "1px dashed " + C.border, borderRadius: 6, padding: "8px 10px" }}>Volgende: <strong>Fase 2</strong> animeert elke scene tot een clip, <strong>Fase 3</strong> voegt ze samen met voice-over en muziek tot 1 doorlopende reclame.</p>
          </div>
        )}
      </div>
    );
  }

  function AIVideoStudio({ user }) {
    const meEmail = user?.email || "unknown";
    const [jobs, setJobs] = useState([]);
    const [assets, setAssets] = useState({});           // job_id → asset
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ prompt: "", mode: "text_to_video", aspectRatio: "9:16", durationSeconds: 5, seed: "", sourceUrl: "" });
    const [submitting, setSubmitting] = useState(false);
    const [uploadingSrc, setUploadingSrc] = useState(false);
    const [spent, setSpent] = useState(0);
    const [cap, setCap] = useState(5);
    const [banner, setBanner] = useState("");
    const jobsRef = useRef([]); jobsRef.current = jobs;

    async function loadAll() {
      const today = new Date().toISOString().slice(0, 10);
      const [{ data: js }, { data: as }, { data: led }] = await Promise.all([
        supabase.from("video_jobs").select("*").order("created_at", { ascending: false }).limit(30),
        supabase.from("video_assets").select("*").order("created_at", { ascending: false }).limit(60),
        supabase.from("video_spend_ledger").select("*").eq("user_email", meEmail).eq("day", today).maybeSingle(),
      ]);
      if (js) setJobs(js);
      if (as) { const m = {}; as.forEach(a => { if (!m[a.job_id]) m[a.job_id] = a; }); setAssets(m); }
      setSpent(led ? Number(led.spent_eur) : 0); setCap(led ? Number(led.cap_eur) || 5 : 5);
      setLoading(false);
    }
    useEffect(() => { loadAll();/* eslint-disable-next-line */ }, []);

    // Poll lopende jobs (één interval; leest laatste jobs via ref)
    useEffect(() => {
      const iv = setInterval(async () => {
        const running = jobsRef.current.filter(j => j.status === "running" || j.status === "queued");
        if (running.length === 0) return;
        for (const j of running) {
          try {
            const r = await apiFetch("/api/video?id=" + encodeURIComponent(j.id));
            const d = await r.json();
            if (d && d.id) {
              setJobs(p => p.map(x => x.id === d.id ? { ...x, status: d.status, error_message: d.error?.message || x.error_message } : x));
              if (d.asset && d.asset.url) setAssets(p => ({ ...p, [d.id]: { public_url: d.asset.url, mime_type: d.asset.mimeType } }));
              if (d.status === "failed" || d.status === "cancelled") { const t = new Date().toISOString().slice(0, 10); supabase.from("video_spend_ledger").select("*").eq("user_email", meEmail).eq("day", t).maybeSingle().then(({ data }) => { if (data) setSpent(Number(data.spent_eur)); }); }
            }
          } catch (e) {/* stil */ }
        }
      }, 4000);
      return () => clearInterval(iv);
    }, [meEmail]);

    async function uploadSource(file) {
      if (!file) return; setUploadingSrc(true);
      try { const { url } = await uploadToDocuments(file, "video-src"); setForm(p => ({ ...p, sourceUrl: url })); }
      catch (e) { setBanner("✗ Upload bronafbeelding mislukt: " + (e.message || "?")); }
      setUploadingSrc(false);
    }
    async function generate() {
      if (!form.prompt.trim() || submitting) return;
      if (form.mode === "image_to_video" && !form.sourceUrl) { setBanner("Upload eerst een bronafbeelding."); return; }
      setSubmitting(true); setBanner("");
      try {
        const idem = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
        const r = await apiFetch("/api/video", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prompt: form.prompt, mode: form.mode, sourceAssetUrl: form.sourceUrl || undefined,
            params: { durationSeconds: Number(form.durationSeconds), aspectRatio: form.aspectRatio, seed: form.seed || undefined },
            idempotencyKey: idem, userEmail: meEmail
          })
        });
        const d = await r.json();
        if (!r.ok) { setBanner((/VIDEO_API_KEY ontbreekt/.test(d.error || "") ? "⚙ " : "✗ ") + (d.error || "Onbekende fout")); setSubmitting(false); return; }
        setForm(p => ({ ...p, prompt: "", sourceUrl: "" }));
        await loadAll();
      } catch (e) { setBanner("✗ Fout: " + (e.message || "?")); }
      setSubmitting(false);
    }

    const pct = cap > 0 ? Math.min(100, Math.round(spent / cap * 100)) : 0;
    const capReached = spent >= cap;
    const succeeded = jobs.filter(j => j.status === "succeeded");
    const active = jobs.filter(j => j.status === "running" || j.status === "queued");
    const failed = jobs.filter(j => j.status === "failed" || j.status === "cancelled");
    const aspW = { "9:16": 180, "1:1": 260, "16:9": 320 }[form.aspectRatio] || 180;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {banner && <div style={{ padding: "10px 13px", borderRadius: 7, fontSize: 12, background: C.yellowDim, border: "1px solid " + C.yellow + "55", color: C.yellow }}>{banner}</div>}
        {/* Spend-meter */}
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
            <p className="lbl">AI Video — dagbudget</p>
            <span style={{ fontSize: 12, fontFamily: "monospace", color: capReached ? C.red : pct >= 80 ? C.yellow : C.muted }}>€{spent.toFixed(2)} / €{cap.toFixed(2)}</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}><div style={{ height: "100%", width: pct + "%", background: capReached ? C.red : pct >= 80 ? C.yellow : C.green, transition: "width .3s" }} /></div>
          {pct >= 80 && !capReached && <p style={{ fontSize: 11, color: C.yellow, marginTop: 6 }}>⚠ Je nadert het dagbudget.</p>}
          {capReached && <p style={{ fontSize: 11, color: C.red, marginTop: 6 }}>Dagbudget bereikt — genereren is geblokkeerd tot morgen.</p>}
        </div>
        {/* Generatieformulier */}
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          <Fld label="Prompt (beschrijf de clip) *">
            <textarea rows={3} value={form.prompt} onChange={e => setForm(p => ({ ...p, prompt: e.target.value }))} placeholder="Bijv: 'Strakke productshot van een laptop met Dif3r3nt-dashboard, zachte camerabeweging, moderne studio-belichting'" />
          </Fld>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {[["text_to_video", "→Tekst"], ["image_to_video", "→Beeld"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setForm(p => ({ ...p, mode: v }))} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid " + (form.mode === v ? C.purple : C.border), background: form.mode === v ? C.purpleDim : "transparent", color: form.mode === v ? C.purple : C.muted, fontSize: 12, cursor: "pointer" }}>{l}</button>
            ))}
          </div>
          {form.mode === "image_to_video" && (
            <Fld label="Bronafbeelding (geüpload — geen externe URL)">
              <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
                <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) uploadSource(f); e.target.value = ""; }} />
                {uploadingSrc && <span style={{ fontSize: 11, color: C.muted }}>⟳ uploaden...</span>}
                {form.sourceUrl && !uploadingSrc && <span style={{ fontSize: 11, color: C.green }}>✓ geladen</span>}
              </div>
            </Fld>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Fld label="Aspect ratio">
              <select value={form.aspectRatio} onChange={e => setForm(p => ({ ...p, aspectRatio: e.target.value }))}>
                <option value="9:16">9:16 (social/Reels)</option>
                <option value="1:1">1:1 (feed)</option>
                <option value="16:9">16:9 (landscape)</option>
              </select>
            </Fld>
            <Fld label={"Duur: " + form.durationSeconds + "s"}>
              <input type="range" min={1} max={10} value={form.durationSeconds} onChange={e => setForm(p => ({ ...p, durationSeconds: Number(e.target.value) }))} />
            </Fld>
            <Fld label="Seed (optioneel)">
              <input type="number" value={form.seed} onChange={e => setForm(p => ({ ...p, seed: e.target.value }))} placeholder="willekeurig" />
            </Fld>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn bp" onClick={generate} disabled={submitting || capReached || !form.prompt.trim()} style={{ background: C.purple, color: "#fff" }}>{submitting ? "⟳ Indienen..." : "Genereer clip"}</button>
          </div>
          <p style={{ fontSize: 10, color: C.dim }}>Geschatte kosten ≈ €{(form.durationSeconds * 0.05).toFixed(2)} · render duurt doorgaans tientallen seconden tot enkele minuten.</p>
        </div>
        {/* Actieve jobs */}
        {active.length > 0 && (
          <div className="card">
            <p className="lbl" style={{ marginBottom: 9 }}>⟳ Bezig ({active.length})</p>
            {active.map(j => (
              <div key={j.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0", fontSize: 12 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", border: "2px solid " + C.purple, borderTopColor: "transparent", animation: "spin 1s linear infinite" }} />
                <span style={{ flex: 1, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.prompt}</span>
                <Bdg label={j.status} bg={C.purpleDim} fg={C.purple} />
              </div>
            ))}
          </div>
        )}
        {/* Galerij */}
        <div>
          <p className="lbl" style={{ marginBottom: 9 }}>Galerij ({succeeded.length})</p>
          {loading ? <p style={{ fontSize: 12, color: C.muted }}>Laden...</p> : succeeded.length === 0 ? (
            <div style={{ padding: 26, textAlign: "center", color: C.muted, fontSize: 13, border: "1px dashed " + C.border, borderRadius: 8 }}>
              <p style={{ fontSize: 26, marginBottom: 8, color: C.dim }}>◇</p><p>Nog geen clips. Genereer je eerste video hierboven.</p>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12 }}>
              {succeeded.map(j => {
                const a = assets[j.id]; return (
                  <div key={j.id} className="card" style={{ padding: 10 }}>
                    {a && a.public_url ? (
                      <video src={a.public_url} controls style={{ width: "100%", maxWidth: aspW, borderRadius: 6, background: "#000", display: "block", margin: "0 auto" }} />
                    ) : <div style={{ padding: 18, textAlign: "center", color: C.dim, fontSize: 12 }}>geen asset</div>}
                    <p style={{ fontSize: 11, color: C.muted, marginTop: 7, lineHeight: 1.4, maxHeight: 34, overflow: "hidden" }}>{j.prompt}</p>
                    <div style={{ display: "flex", gap: 6, marginTop: 7, fontSize: 10, color: C.dim, alignItems: "center" }}>
                      <span>{j.params?.aspectRatio || "9:16"} · {j.params?.durationSeconds || "?"}s</span>
                      {a && a.public_url && <a href={a.public_url} target="_blank" rel="noopener noreferrer" download style={{ color: C.green, marginLeft: "auto" }}>⬇ download</a>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* Mislukte jobs */}
        {failed.length > 0 && (
          <div className="card" style={{ borderColor: C.red + "40" }}>
            <p className="lbl" style={{ color: C.red, marginBottom: 7 }}>Mislukt ({failed.length})</p>
            {failed.map(j => (
              <p key={j.id} style={{ fontSize: 11, color: C.muted, padding: "3px 0" }}>{j.prompt?.slice(0, 60)} — <span style={{ color: C.red }}>{j.error_message || j.status}</span></p>
            ))}
          </div>
        )}
      </div>
    );
  }

  function GrowthModule({ user, launches, services }) {
    const meEmail = user?.email || "";
    const canDecide = meEmail === "furgel@dif3r3nt.nl";
    const [tab, setTab] = useState("inbox");
    const [reviews, setReviews] = useState([]);
    const [learnings, setLearnings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sel, setSel] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [genStep, setGenStep] = useState("");
    const [newMod, setNewMod] = useState(false);
    const [nv, setNv] = useState({ engine: "content", type: "blog", brief: "", dienst: "" });
    const [productUrl, setProductUrl] = useState("");
    const [productData, setProductData] = useState(null);
    const [productBusy, setProductBusy] = useState(false);
    const [productErr, setProductErr] = useState("");
    const [wantVideo, setWantVideo] = useState(false);
    const [editMod, setEditMod] = useState(false);
    const [editBody, setEditBody] = useState("");
    const [editSummary, setEditSummary] = useState("");
    const [editSlides, setEditSlides] = useState(null);
    const [editSeo, setEditSeo] = useState(null);
    const [rejectMod, setRejectMod] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [planMod, setPlanMod] = useState(false);
    const [planDate, setPlanDate] = useState(dateStr(addDays(TODAY, 7)));
    const [auditLog, setAuditLog] = useState([]);
    const [regenerating, setRegenerating] = useState(false);

    useEffect(() => {
      async function load() {
        const [{ data: rv }, { data: ln }] = await Promise.all([
          supabase.from("growth_reviews").select("*").order("created_at", { ascending: false }),
          supabase.from("growth_learnings").select("*").order("created_at", { ascending: false }).limit(50),
        ]);
        if (rv) setReviews(rv);
        if (ln) setLearnings(ln);
        setLoading(false);
      }
      load();
      const ch = supabase.channel("growth_reviews_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "growth_reviews" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    useEffect(() => {
      async function loadAudit() {
        if (!sel) { setAuditLog([]); return; }
        const { data } = await supabase.from("growth_audit_log").select("*").eq("review_id", sel).order("created_at", { ascending: false });
        if (data) setAuditLog(data);
      }
      loadAudit();
    }, [sel]);

    async function logAudit(reviewId, action, before, after, notes) {
      await supabase.from("growth_audit_log").insert([{ review_id: reviewId, action, actor: meEmail || "unknown", before_state: before || null, after_state: after || null, notes: notes || null }]);
    }

    // Productgegevens ophalen van een productlink (eigen webshop-pagina's), via de SSRF-veilige edge function.
    async function haalProductOp() {
      const url = productUrl.trim();
      if (!url) return;
      setProductBusy(true); setProductErr(""); setProductData(null);
      try {
        const { data, error } = await supabase.functions.invoke("product-ophalen", { body: { action: "ophalen", url } });
        if (error) {
          let msg = error.message || "Ophalen mislukt";
          try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* standaardbericht */ }
          throw new Error(msg);
        }
        if (data && data.error) throw new Error(data.error);
        setProductData(data);
        if (!data.afbeelding_url) setWantVideo(false);
      } catch (e) { setProductErr(e.message || "Ophalen mislukt"); }
      setProductBusy(false);
    }

    // Start een creative-video (image-to-video) vanuit de zojuist opgehaalde productfoto, en koppel
    // het resultaat aan het growth_reviews-voorstel. Eigen dagbudget (origin "creative") bovenop de
    // algemene AI Video-cap; de server wijst het af met status 402 als het plafond bereikt is.
    async function startCreativeVideo(reviewId, motionPrompt, sourceAssetUrl, aspectRatio) {
      try {
        const idem = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : Date.now() + "-" + Math.random().toString(36).slice(2);
        const r = await apiFetch("/api/video", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prompt: motionPrompt, mode: "image_to_video", sourceAssetUrl,
            params: { durationSeconds: 5, aspectRatio: aspectRatio || "1:1" },
            idempotencyKey: idem, userEmail: meEmail, origin: "creative",
          }),
        });
        const d = await r.json();
        const { data: huidig } = await supabase.from("growth_reviews").select("preview").eq("id", reviewId).maybeSingle();
        const prevPreview = (huidig && huidig.preview) || {};
        if (!r.ok) {
          await supabase.from("growth_reviews").update({ preview: { ...prevPreview, video_error: d.error || "Videogeneratie mislukt" } }).eq("id", reviewId);
          return;
        }
        await supabase.from("growth_reviews").update({ preview: { ...prevPreview, video_job_id: d.id } }).eq("id", reviewId);
      } catch (e) {
        const { data: huidig } = await supabase.from("growth_reviews").select("preview").eq("id", reviewId).maybeSingle();
        await supabase.from("growth_reviews").update({ preview: { ...((huidig && huidig.preview) || {}), video_error: e.message || "Videogeneratie mislukt" } }).eq("id", reviewId);
      }
    }

    // Poll lopende creative-video-jobs die aan een growth_reviews-voorstel hangen, en schrijf het
    // resultaat (video_url) of de fout terug zodra de job klaar is.
    useEffect(() => {
      const lopend = reviews.filter(r => r.preview?.video_job_id && !r.preview?.video_url && !r.preview?.video_error);
      if (lopend.length === 0) return;
      const iv = setInterval(async () => {
        for (const r of lopend) {
          try {
            const res = await apiFetch("/api/video?id=" + encodeURIComponent(r.preview.video_job_id));
            const d = await res.json();
            if (!d || !d.status) continue;
            if (d.status === "succeeded" && d.asset && d.asset.url) {
              await supabase.from("growth_reviews").update({ preview: { ...r.preview, video_url: d.asset.url } }).eq("id", r.id);
            } else if (d.status === "failed" || d.status === "cancelled") {
              await supabase.from("growth_reviews").update({ preview: { ...r.preview, video_error: d.error?.message || "Videogeneratie mislukt" } }).eq("id", r.id);
            }
          } catch (e) { /* volgende tik opnieuw proberen */ }
        }
      }, 5000);
      return () => clearInterval(iv);
    }, [reviews]);

    async function saveLearning(reviewId, engine, type, context, value) {
      await supabase.from("growth_learnings").insert([{ engine, type, context, value: value || {}, source_review_id: reviewId }]);
    }

    async function generateProposal() {
      const isProduct = !!productData;
      if (!nv.brief.trim() && !isProduct) return;
      setGenerating(true); setGenStep("Eerdere leerpunten ophalen...");
      try {
        const relevantLearnings = learnings.filter(l => l.engine === nv.engine).slice(0, 10);
        const learningsBlock = relevantLearnings.length > 0
          ? relevantLearnings.map(l => `- [${l.type}] ${l.context || ""}`).join("\n")
          : "(nog geen leerpunten beschikbaar)";
        setGenStep("Dienst-context laden...");
        // Vind de gekoppelde launch op id of titel-match — pak ALLE bekende feiten uit Launch Module
        let dienstContext = "(geen dienst-context — voorstel mag alleen op basis van brief)";
        let dienstTitle = nv.dienst || "";
        let marktdataBlock = "(geen gekoppelde SDA-marktdata)";
        if (nv.dienst && Array.isArray(launches)) {
          const l = launches.find(x => x.id === nv.dienst) || launches.find(x => (x.title || "").toLowerCase() === (nv.dienst || "").toLowerCase());
          if (l) {
            dienstTitle = l.title;
            const lines = [`Naam: ${l.title}`];
            if (l.publiek) lines.push(`Publiek: ${l.publiek === "ouders_kinderen" ? "ouders, verzorgers en leerkrachten (het product zelf is voor kinderen; richt je nooit direct op kinderen)" : l.publiek === "zakelijk" ? "zakelijk (B2B)" : "consumenten (B2C)"}`);
            if (l.dienst_omschrijving) lines.push(`Feiten over de dienst (leidend, van de eigenaar):\n${l.dienst_omschrijving}`);
            if (l.doelgroep) lines.push(`Doelgroep: ${l.doelgroep}`);
            if (l.pijnpunten) lines.push(`Pijnpunten: ${l.pijnpunten}`);
            if (l.value_proposition) lines.push(`Value proposition: ${l.value_proposition}`);
            if (Array.isArray(l.usp_lijst) && l.usp_lijst.length > 0) lines.push(`USPs: ${l.usp_lijst.join(" | ")}`);
            if (l.elevator_pitch) lines.push(`Elevator pitch: ${l.elevator_pitch}`);
            if (Array.isArray(l.zoekwoorden) && l.zoekwoorden.length > 0) lines.push(`Keywords: ${l.zoekwoorden.join(", ")}`);
            if (l.pricing) lines.push(`Pricing: ${l.pricing}`);
            if (l.cta) lines.push(`Standaard CTA: ${l.cta}`);
            dienstContext = lines.join("\n");
            // Flow A: haal verzamelde SDA-marktdata voor deze dienst op
            try {
              const { data: mjobs } = await supabase.from("scraping_jobs").select("id").eq("dienst_id", l.id);
              const mids = (mjobs || []).map(j => j.id);
              if (mids.length) {
                const { data: mres } = await supabase.from("scraping_results").select("entity_type,data,source_url,created_at").in("job_id", mids).order("created_at", { ascending: false }).limit(10);
                if (mres && mres.length) marktdataBlock = mres.map(r => `- [${r.entity_type}] ${JSON.stringify(r.data).slice(0, 200)}${r.source_url ? " (bron: " + r.source_url + ")" : ""}`).join("\n");
              }
            } catch {/* marktdata is optioneel */ }
          } else {
            dienstContext = "Dienst-naam opgegeven door Furgel: \"" + nv.dienst + "\" — geen Launch Module match gevonden. WAARSCHUWING in waarom-veld dat context ontbreekt.";
          }
        }
        setGenStep("AI genereert voorstel...");
        const productContext = isProduct ? [
          `Productnaam: ${productData.titel}`,
          productData.prijs ? `Prijs: ${productData.prijs}${productData.valuta ? " " + productData.valuta : ""}` : null,
          productData.beschrijving ? `Beschrijving:\n${productData.beschrijving}` : null,
          `Winkel: ${productData.domein}`,
          `Productlink: ${productUrl.trim()}`,
          productData.afbeelding_url ? "Er is een productfoto beschikbaar (vul het video-object in)." : "Geen productfoto beschikbaar (laat 'video' weg).",
        ].filter(Boolean).join("\n") : "";
        const ctype = isProduct ? ALL_TYPES.find(c => c.id === "product_creative") : (ALL_TYPES.find(c => c.id === nv.type) || CONTENT_TYPES[0]);
        const isVisual = !isProduct && !!ctype.visual;
        const isSeo = !isProduct && !!ctype.seo;
        const visualBlock = isVisual ? `\n\n=== VISUELE OPDRACHT ===\nkind: ${ctype.kind}\nFormaat: ${ctype.dim.w}×${ctype.dim.h}px (${ctype.l}).\n${ctype.kind === "carousel" ? "Lever 3 t/m 6 slides: slide 1 = hook, laatste slide = CTA." : "Lever PRECIES 1 slide."}\nHoud alle tekst kort en scanbaar — dit wordt een beeld, geen artikel.\n=== EINDE VISUELE OPDRACHT ===` : "";
        const seoBlock = isSeo ? `\n\n=== SEO-OPDRACHT ===\nDeliverable: ${ctype.l} (${ctype.len}).\nVul minimaal target_keyword + meta_title + meta_description. VERZIN GEEN zoekvolumes — gebruik prioriteit hoog/midden/laag.\n=== EINDE SEO-OPDRACHT ===` : "";
        const sysPrompt = isProduct ? CONTENT_PRODUCT_SYS : (isSeo ? CONTENT_SEO_SYS : (isVisual ? CONTENT_VISUAL_SYS : CONTENT_AGENT_SYS));
        const userMsg = isProduct
          ? `Engine: ${nv.engine}\nContent type: Productcreative (social post, 1080x1080px, optioneel + video)\n\n=== PRODUCT CONTEXT (gebruik ALLEEN deze feiten) ===\n${productContext}\n=== EINDE PRODUCT CONTEXT ===\n\n=== BRIEF VAN FURGEL (extra instructie, optioneel) ===\n${nv.brief || "(geen extra instructie)"}\n=== EINDE BRIEF ===\n\nEerdere leerpunten van Furgel (verwerk deze):\n${learningsBlock}\n\nHerinnering: gebruik alleen feiten uit PRODUCT CONTEXT. Verzin geen materiaal, voorraad, garanties of kortingen.`
          : `Engine: ${nv.engine}\nContent type: ${ctype.l} (${ctype.len})${visualBlock}${seoBlock}\n\n=== DIENST CONTEXT (gebruik ALLEEN deze feiten) ===\n${dienstContext}\n=== EINDE DIENST CONTEXT ===\n\n=== BRIEF VAN FURGEL ===\n${nv.brief}\n=== EINDE BRIEF ===\n\nEerdere leerpunten van Furgel (verwerk deze):\n${learningsBlock}\n\n=== MARKTDATA (SDA, echte verzamelde data, gebruik als feiten) ===\n${marktdataBlock}\n=== EINDE MARKTDATA ===\n\nHerinnering: gebruik alleen feiten uit DIENST CONTEXT + BRIEF (+ MARKTDATA). Verzin geen sectoren of doelgroepen.`;
        // Helper: detecteer bouw-gerelateerde hallucinaties met regex-prefix (alleen relevant voor Dif3r3nt's eigen SaaS-diensten, niet voor fysieke producten)
        const inputLow = (dienstContext + " " + nv.brief).toLowerCase();
        function findHallucinations(parsedObj) {
          if (isProduct) return [];
          const text = JSON.stringify(parsedObj).toLowerCase();
          // Match elke "bouw"+(min. 1 letter), aannem*, construct*, vastgoed, civiele techniek, renovat*
          const re = /\b(bouw[a-z]+|aannem[a-z]*|construct[a-z]*|vastgoed[a-z]*|civiele\s+techniek|renovat[a-z]*|verbouw[a-z]*)\b/gi;
          const matches = new Set();
          let m; while ((m = re.exec(text)) !== null) {
            const w = m[1].toLowerCase();
            if (!inputLow.includes(w)) matches.add(w);
          }
          return Array.from(matches);
        }
        let { parsed } = await callGroq(sysPrompt, userMsg, { maxTokens: 2500, temperature: 0.2 });
        if (!parsed) { setGenerating(false); setGenStep(""); return; }
        let hallucinations = findHallucinations(parsed);
        // Auto-retry 1x als bouw-termen toch in output staan
        if (hallucinations.length > 0) {
          setGenStep("AI verzon " + hallucinations.join(", ") + " — herzien...");
          const retryMsg = userMsg + "\n\n=== VORIGE ANTWOORD AFGEKEURD ===\nJe gebruikte deze woorden die NIET in input staan: " + hallucinations.join(", ") + "\nDeze dienst heeft NIETS met bouw, aannemerij, vastgoed of construction te maken. Het is software/SaaS.\nGenereer opnieuw zonder die termen. Bij twijfel: laat het veld leeg of schrijf 'positionering te vaag, herzie Launch Module'.";
          const retry = await callGroq(sysPrompt, retryMsg, { maxTokens: 2500, temperature: 0.1 });
          if (retry.parsed) { parsed = retry.parsed; hallucinations = findHallucinations(parsed); }
        }
        let warning = "";
        if (hallucinations.length > 0) {
          warning = `⚠ AI bleef "${hallucinations.join(", ")}" gebruiken ondanks 2 pogingen. Mogelijk verzonnen — review extra kritisch.`;
          console.warn("Growth hallucination after retry:", hallucinations);
        }
        setGenStep("Opslaan in Supabase...");
        const preview = parsed.preview || {};
        if (isProduct) {
          preview.visual = normalizeVisual(parsed.preview?.visual, ctype);
          if (productData.afbeelding_url) preview.visual.productImage = productData.afbeelding_url;
          if (wantVideo && productData.afbeelding_url && parsed.preview?.video?.motion_prompt) {
            preview.video = { motion_prompt: String(parsed.preview.video.motion_prompt).slice(0, 600), caption: String(parsed.preview.video.caption || "").slice(0, 300) };
          }
        } else {
          if (isVisual) preview.visual = normalizeVisual(parsed.preview?.visual, ctype);
          if (isSeo) preview.seo = normalizeSeo(parsed.preview?.seo);
        }
        const row = {
          engine: nv.engine,
          type: isProduct ? "product_creative" : nv.type,
          title: parsed.title || "Onbenoemd voorstel",
          doel: parsed.doel || "",
          waarom: warning ? warning + "\n\n" + (parsed.waarom || "") : (parsed.waarom || ""),
          preview,
          data_context: parsed.data_context || {},
          risico: warning ? warning + "\n\n" + (parsed.risico || "") : (parsed.risico || ""),
          impact: parsed.impact || "",
          original_preview: preview,
          status: "pending",
        };
        const { data, error } = await supabase.from("growth_reviews").insert([row]).select();
        if (!error && data) {
          await logAudit(data[0].id, "created", null, row, "AI-gegenereerd voorstel, brief: " + nv.brief.slice(0, 100));
          if (isProduct && preview.video?.motion_prompt) {
            startCreativeVideo(data[0].id, preview.video.motion_prompt, productData.afbeelding_url, "1:1");
          }
          setNewMod(false);
          setNv({ engine: "content", type: "blog", brief: "", dienst: "" });
          setProductUrl(""); setProductData(null); setProductErr(""); setWantVideo(false);
          setSel(data[0].id);
        }
      } catch (e) { console.error("Growth proposal:", e); }
      setGenerating(false); setGenStep("");
    }

    async function decide(review, action, extra) {
      if (!canDecide) return;
      const now = new Date().toISOString();
      // 'notes' is GEEN kolom op growth_reviews (alleen op growth_audit_log). Meespreiden
      // in de kolom-patch liet de UPDATE stilletjes falen ("column notes does not exist"),
      // waardoor afwijzen/plannen wel klikte maar nooit werd doorgevoerd. Splits 'm af.
      const { notes, ...cols } = extra || {};
      const patch = { status: action, decided_by: meEmail, decided_at: now, updated_at: now, ...cols };
      const before = { status: review.status, decided_by: review.decided_by, decided_at: review.decided_at };
      const { data, error } = await supabase.from("growth_reviews").update(patch).eq("id", review.id).select();
      if (error) { console.error(error); alert("Doorvoeren mislukt: " + error.message); return; }
      await logAudit(review.id, action, before, patch, notes || null);
      if (action === "approved") {
        await saveLearning(review.id, review.engine, "approval", `Goedgekeurd: ${review.title}`, { type: review.type, tone: review.preview?.metadata?.tone });
      } else if (action === "rejected") {
        await saveLearning(review.id, review.engine, "rejection", `Afgekeurd (${extra?.reject_reason || "geen reden"}): ${review.title}`, { type: review.type });
      } else if (action === "edited") {
        await saveLearning(review.id, review.engine, "edit_pattern", `Bewerkt (${extra?.edits_summary || "zie diff"}): ${review.title}`, { type: review.type });
      }
      setReviews(p => p.map(r => r.id === review.id ? data[0] : r));
      setEditMod(false); setRejectMod(false); setPlanMod(false);
      setRejectReason(""); setEditBody(""); setEditSummary(""); setEditSlides(null); setEditSeo(null);
    }

    // Regenereer een bestaande review met huidige AI-prompt + post-check + retry (voor pre-fix voorstellen)
    async function regenerate(rv) {
      if (!rv || regenerating) return;
      if (!window.confirm("Vervang de huidige content van dit voorstel? De originele content wordt bewaard in original_preview voor audit.")) return;
      setRegenerating(true);
      try {
        // Reconstrueer brief uit bestaande data + dienst uit data_context.doelgroep waar mogelijk
        const origDienst = rv.data_context?.doelgroep || "";
        const matchedLaunch = Array.isArray(launches) ? launches.find(l => (l.title || "").toLowerCase() === (rv.title || "").toLowerCase() || (l.doelgroep || "").toLowerCase().includes(origDienst.toLowerCase().slice(0, 30))) : null;
        let dienstContext = "(geen Launch Module match — gebruik alleen brief)";
        if (matchedLaunch) {
          const lines = [`Naam: ${matchedLaunch.title}`];
          if (matchedLaunch.publiek) lines.push(`Publiek: ${matchedLaunch.publiek === "ouders_kinderen" ? "ouders, verzorgers en leerkrachten (het product zelf is voor kinderen; richt je nooit direct op kinderen)" : matchedLaunch.publiek === "zakelijk" ? "zakelijk (B2B)" : "consumenten (B2C)"}`);
          if (matchedLaunch.dienst_omschrijving) lines.push(`Feiten over de dienst (leidend, van de eigenaar):\n${matchedLaunch.dienst_omschrijving}`);
          if (matchedLaunch.doelgroep) lines.push(`Doelgroep: ${matchedLaunch.doelgroep}`);
          if (matchedLaunch.pijnpunten) lines.push(`Pijnpunten: ${matchedLaunch.pijnpunten}`);
          if (matchedLaunch.value_proposition) lines.push(`Value proposition: ${matchedLaunch.value_proposition}`);
          if (Array.isArray(matchedLaunch.usp_lijst) && matchedLaunch.usp_lijst.length > 0) lines.push(`USPs: ${matchedLaunch.usp_lijst.join(" | ")}`);
          if (matchedLaunch.elevator_pitch) lines.push(`Elevator pitch: ${matchedLaunch.elevator_pitch}`);
          dienstContext = lines.join("\n");
        }
        const ctype = ALL_TYPES.find(c => c.id === rv.type) || CONTENT_TYPES[0];
        const isVisual = !!ctype.visual;
        const isSeo = !!ctype.seo;
        const sysPrompt = isSeo ? CONTENT_SEO_SYS : (isVisual ? CONTENT_VISUAL_SYS : CONTENT_AGENT_SYS);
        const visualBlock = isVisual ? `\n\n=== VISUELE OPDRACHT ===\nkind: ${ctype.kind}\nFormaat: ${ctype.dim.w}×${ctype.dim.h}px (${ctype.l}).\n${ctype.kind === "carousel" ? "Lever 3 t/m 6 slides: slide 1 = hook, laatste slide = CTA." : "Lever PRECIES 1 slide."}\nHoud alle tekst kort en scanbaar.\n=== EINDE VISUELE OPDRACHT ===` : "";
        const seoBlock = isSeo ? `\n\n=== SEO-OPDRACHT ===\nDeliverable: ${ctype.l} (${ctype.len}). VERZIN GEEN zoekvolumes — gebruik prioriteit hoog/midden/laag.\n=== EINDE SEO-OPDRACHT ===` : "";
        const brief = `Originele titel: ${rv.title}\nOrigineel doel: ${rv.doel || "(leeg)"}\n\nDit voorstel werd eerder gegenereerd met een verouderde prompt. Behoud het ONDERWERP maar herzie de hele content met de huidige strikte regels.`;
        const userMsg = `Engine: ${rv.engine}\nContent type: ${ctype.l} (${ctype.len})${visualBlock}${seoBlock}\n\n=== DIENST CONTEXT (gebruik ALLEEN deze feiten) ===\n${dienstContext}\n=== EINDE DIENST CONTEXT ===\n\n=== BRIEF VAN FURGEL ===\n${brief}\n=== EINDE BRIEF ===\n\nHerinnering: gebruik alleen feiten uit DIENST CONTEXT + BRIEF. Verzin geen sectoren of doelgroepen. ABSOLUUT geen bouw/aannemerij/construction termen tenzij letterlijk in DIENST CONTEXT.`;
        const inputLow = (dienstContext + " " + brief).toLowerCase();
        function findHallucinations(o) {
          const t = JSON.stringify(o).toLowerCase();
          const re = /\b(bouw[a-z]+|aannem[a-z]*|construct[a-z]*|vastgoed[a-z]*|civiele\s+techniek|renovat[a-z]*|verbouw[a-z]*)\b/gi;
          const s = new Set(); let m; while ((m = re.exec(t)) !== null) { const w = m[1].toLowerCase(); if (!inputLow.includes(w)) s.add(w); }
          return Array.from(s);
        }
        let { parsed } = await callGroq(sysPrompt, userMsg, { maxTokens: 2500, temperature: 0.2 });
        if (!parsed) { alert("Regeneratie faalde — geen geldige AI response."); setRegenerating(false); return; }
        let halls = findHallucinations(parsed);
        if (halls.length > 0) {
          const retryMsg = userMsg + "\n\n=== VORIGE ANTWOORD AFGEKEURD ===\nWoorden die niet in input staan: " + halls.join(", ") + "\nGenereer opnieuw zonder die termen.";
          const retry = await callGroq(sysPrompt, retryMsg, { maxTokens: 2500, temperature: 0.1 });
          if (retry.parsed) { parsed = retry.parsed; halls = findHallucinations(parsed); }
        }
        const warning = halls.length > 0 ? `⚠ AI bleef "${halls.join(", ")}" gebruiken na retry. Mogelijk verzonnen.` : "";
        const newPreview = parsed.preview || {};
        if (isVisual) newPreview.visual = normalizeVisual(parsed.preview?.visual, ctype);
        if (isSeo) newPreview.seo = normalizeSeo(parsed.preview?.seo);
        const patch = {
          title: parsed.title || rv.title,
          doel: parsed.doel || "",
          waarom: warning ? warning + "\n\n" + (parsed.waarom || "") : (parsed.waarom || ""),
          preview: newPreview,
          data_context: parsed.data_context || {},
          risico: parsed.risico || "",
          impact: parsed.impact || "",
          // original_preview blijft zoals het was (audit)
          updated_at: new Date().toISOString(),
        };
        const { data, error } = await supabase.from("growth_reviews").update(patch).eq("id", rv.id).select();
        if (error) { alert("Opslaan mislukt: " + error.message); setRegenerating(false); return; }
        await logAudit(rv.id, "regenerated", { title: rv.title, doel: rv.doel }, patch, "Herzien met huidige AI-prompt + post-check" + (warning ? " — " + warning : ""));
        if (data) setReviews(p => p.map(r => r.id === rv.id ? data[0] : r));
      } catch (e) { console.error("Regenerate:", e); alert("Fout: " + (e?.message || "?")); }
      setRegenerating(false);
    }

    function openEdit(rv) {
      setEditBody(typeof rv.preview?.body === "string" ? rv.preview.body : JSON.stringify(rv.preview?.body || "", null, 2));
      setEditSummary("");
      const vis = rv.preview?.visual;
      setEditSlides(vis && Array.isArray(vis.slides) ? vis.slides.map(s => ({ ...s })) : null);
      const s = rv.preview?.seo;
      setEditSeo(s ? { meta_title: s.meta_title || "", meta_description: s.meta_description || "", slug: s.slug || "" } : null);
      setEditMod(true);
    }
    function updEditSlide(idx, field, val) {
      setEditSlides(prev => prev.map((s, i) => i === idx ? { ...s, [field]: val } : s));
    }

    async function submitEdit(rv) {
      const vis = rv.preview?.visual;
      const isVisual = !!vis;
      const isSeo = !!editSeo;
      if (!isVisual && !isSeo && !editBody.trim()) return;
      const newPreview = { ...rv.preview, body: editBody };
      if (isVisual && Array.isArray(editSlides)) newPreview.visual = { ...vis, slides: editSlides };
      if (isSeo) newPreview.seo = { ...rv.preview.seo, ...editSeo };
      await decide(rv, "edited", { preview: newPreview, edits_summary: editSummary || (isVisual ? "Visuele aanpassing door reviewer" : isSeo ? "SEO-aanpassing door reviewer" : "Tekstuele aanpassing door reviewer") });
    }

    function renderEditModal(rv) {
      if (!rv) return null;
      const isVisual = Array.isArray(editSlides);
      const isSeo = !!editSeo;
      return (
        <Portal><div className="ovl" onClick={() => setEditMod(false)}>
          <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 12 }}>✎ BEWERK + GOEDKEUREN</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 11, maxHeight: "70vh", overflow: "auto" }}>
              {isSeo ? (<>
                <Fld label={`Meta-title (${(editSeo.meta_title || "").length}/60${(editSeo.meta_title || "").length > 60 ? " ⚠" : ""})`}>
                  <input value={editSeo.meta_title} onChange={e => setEditSeo(p => ({ ...p, meta_title: e.target.value }))} />
                </Fld>
                <Fld label={`Meta-description (${(editSeo.meta_description || "").length}/155${(editSeo.meta_description || "").length > 155 ? " ⚠" : ""})`}>
                  <textarea rows={3} value={editSeo.meta_description} onChange={e => setEditSeo(p => ({ ...p, meta_description: e.target.value }))} />
                </Fld>
                <Fld label="Slug (URL-pad)">
                  <input value={editSeo.slug} onChange={e => setEditSeo(p => ({ ...p, slug: e.target.value }))} placeholder="/voorbeeld-url" />
                </Fld>
                <Fld label="Content-brief">
                  <textarea rows={6} value={editBody} onChange={e => setEditBody(e.target.value)} />
                </Fld>
                <p style={{ fontSize: 10, color: C.dim }}>Cluster, koppen en FAQ bewerk je via "Herzie met huidige AI" of een volgende generatie.</p>
              </>) : isVisual ? (<>
                {editSlides.map((s, i) => (
                  <div key={i} className="card" style={{ padding: 12, gap: 8, display: "flex", flexDirection: "column" }}>
                    <p className="lbl" style={{ color: C.cyan }}>Slide {i + 1} / {editSlides.length}</p>
                    <Fld label="Badge (kort label, mag leeg)"><input value={s.badge || ""} onChange={e => updEditSlide(i, "badge", e.target.value)} placeholder="bv. NIEUW" /></Fld>
                    <Fld label="Kop"><input value={s.headline || ""} onChange={e => updEditSlide(i, "headline", e.target.value)} /></Fld>
                    <Fld label="Subtekst"><input value={s.sub || ""} onChange={e => updEditSlide(i, "sub", e.target.value)} /></Fld>
                    <Fld label="Body (max 3 regels, gescheiden door enter)"><textarea rows={3} value={s.body || ""} onChange={e => updEditSlide(i, "body", e.target.value)} /></Fld>
                    <Fld label="CTA"><input value={s.cta || ""} onChange={e => updEditSlide(i, "cta", e.target.value)} /></Fld>
                  </div>
                ))}
                <Fld label="Begeleidende tekst / caption">
                  <textarea rows={4} value={editBody} onChange={e => setEditBody(e.target.value)} />
                </Fld>
              </>) : (
                <Fld label="Content body">
                  <textarea rows={14} value={editBody} onChange={e => setEditBody(e.target.value)} style={{ fontFamily: "monospace", fontSize: 12 }} />
                </Fld>
              )}
              <Fld label="Samenvatting van je wijziging (gaat naar audit log + AI-learning)">
                <input value={editSummary} onChange={e => setEditSummary(e.target.value)} placeholder="Bijv: 'Kop scherper, accent naar cyan, CTA korter'" />
              </Fld>
              <div style={{ display: "flex", gap: 7, justifyContent: "flex-end" }}>
                <button className="btn bg" onClick={() => setEditMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={() => submitEdit(rv)} disabled={!isVisual && !isSeo && !editBody.trim()}>✓ Opslaan + Goedkeuren</button>
              </div>
            </div>
          </div>
        </div></Portal>
      );
    }

    const filtered = reviews.filter(r => {
      if (tab === "inbox") return r.status === "pending";
      if (tab === "approved") return r.status === "approved" || r.status === "edited";
      if (tab === "rejected") return r.status === "rejected";
      if (tab === "planned") return r.status === "planned";
      if (tab === "all") return true;
      return r.engine === tab;
    }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    const selRv = reviews.find(r => r.id === sel) || null;
    const cnt = { pending: reviews.filter(r => r.status === "pending").length, approved: reviews.filter(r => r.status === "approved" || r.status === "edited").length, rejected: reviews.filter(r => r.status === "rejected").length, planned: reviews.filter(r => r.status === "planned").length };

    if (selRv) {
      return (
        <>
          <GrowthReviewCard rv={selRv} canDecide={canDecide} onBack={() => setSel(null)} onApprove={() => decide(selRv, "approved")} onReject={() => setRejectMod(true)} onEdit={() => openEdit(selRv)} onPlan={() => setPlanMod(true)} onRegenerate={() => regenerate(selRv)} regenerating={regenerating} auditLog={auditLog}
            onSaveInline={async (newPreview, summary) => { await decide(selRv, "edited", { preview: newPreview, edits_summary: summary || "Inline aanpassing door reviewer" }); }}
            onRefine={async (text, instruction) => {
              const { parsed } = await callGroq("Je bent een ervaren Nederlandse copywriter voor Dif3r3nt VOF. Herschrijf de gegeven tekst volgens de instructie; behoud de kernboodschap en de taal (Nederlands).", `Instructie: maak de tekst ${instruction}.\n\nTekst:\n${text}\n\nGeef JSON: {"tekst":"<de herschreven tekst>"}`, { maxTokens: 1500, temperature: 0.6 });
              return parsed && parsed.tekst ? String(parsed.tekst) : "";
            }} />
          {editMod && renderEditModal(selRv)}
          {rejectMod && (
            <Portal><div className="ovl" onClick={() => setRejectMod(false)}>
              <div className="mdl" onClick={e => e.stopPropagation()}>
                <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 12 }}>✗ AFKEUREN</h3>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 11, lineHeight: 1.5 }}>Geef de reden — de AI leert hiervan en doet dit voorstel niet opnieuw.</p>
                <Fld label="Reden afkeuring *">
                  <textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Bijv: 'Toon te formeel, niet onze doelgroep' of 'Onderwerp al behandeld vorige maand'" autoFocus />
                </Fld>
                <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 12 }}>
                  <button className="btn bg" onClick={() => setRejectMod(false)}>Annuleer</button>
                  <button className="btn bp" onClick={() => decide(selRv, "rejected", { reject_reason: rejectReason, notes: rejectReason })} disabled={!rejectReason.trim()} style={{ background: C.red, color: "#fff" }}>✗ Afkeuren</button>
                </div>
              </div>
            </div></Portal>
          )}
          {planMod && (
            <Portal><div className="ovl" onClick={() => setPlanMod(false)}>
              <div className="mdl" onClick={e => e.stopPropagation()}>
                <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 12 }}>PLAN VOOR LATER</h3>
                <Fld label="Publicatiedatum">
                  <input type="date" value={planDate} onChange={e => setPlanDate(e.target.value)} min={dateStr(TODAY)} />
                </Fld>
                <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 12 }}>
                  <button className="btn bg" onClick={() => setPlanMod(false)}>Annuleer</button>
                  <button className="btn bp" onClick={() => decide(selRv, "planned", { scheduled_for: planDate, notes: "Gepland voor " + planDate })}>Plan</button>
                </div>
              </div>
            </div></Portal>
          )}
        </>
      );
    }

    return (
      <div className="fu">
        <Kop titel="Growth" sub="Autonome groei-AI. Furgel reviewt elke actie samen met Claude. Actief: Content (tekst + visueel), SEO en AI Video."
          rechts={<>
            {cnt.pending > 0 && <Bdg label={cnt.pending + " wacht op review"} bg={C.yellowDim} fg={C.yellow} />}
            {canDecide ? <Bdg label={"Reviewer: " + (meEmail.split("@")[0] || "jij")} bg={C.greenDim} fg={C.green} /> : <Bdg label="Read-only (Furgel beslist)" bg={C.border} fg={C.muted} />}
            <button className="btn bp" onClick={() => setNewMod(true)}>+ Nieuw voorstel</button>
          </>} />
        <Tellers items={[{ label: "Pending", waarde: cnt.pending, kleur: C.yellow }, { label: "Goedgekeurd", waarde: cnt.approved, kleur: C.green }, { label: "Afgekeurd", waarde: cnt.rejected, kleur: C.red }, { label: "Gepland", waarde: cnt.planned, kleur: C.cyan }]} />
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 11, alignItems: "center" }}>
          {[["inbox", "Inbox"], ["all", "Alle"], ["approved", "Goedgekeurd"], ["rejected", "Afgekeurd"], ["planned", "Gepland"]].map(([v, l]) => (
            <span key={v} className={"tab" + (tab === v ? " on" : "")} onClick={() => setTab(v)} style={{ fontSize: 11 }}>{l}</span>
          ))}
          <span style={{ width: 1, height: 18, background: C.border, margin: "0 4px" }}></span>
          {GROWTH_ENGINES.map(e => (
            <span key={e.id} className={"tab" + (tab === e.id ? " on" : "")} onClick={() => e.live && setTab(e.id)} style={{ fontSize: 11, opacity: e.live ? 1 : .4, cursor: e.live ? "pointer" : "not-allowed" }} title={e.live ? "" : "Binnenkort — fase " + (GROWTH_ENGINES.indexOf(e) + 1)}>{e.icon} {e.l}{!e.live && " "}</span>
          ))}
          <span style={{ width: 1, height: 18, background: C.border, margin: "0 4px" }}></span>
          <span className={"tab" + (tab === "rankloop" ? " on" : "")} onClick={() => setTab("rankloop")} style={{ fontSize: 11 }} title="SEO content-levenscyclus">↺ Rank Loop</span>
          <span className={"tab" + (tab === "reclame" ? " on" : "")} onClick={() => setTab("reclame")} style={{ fontSize: 11 }} title="TV-reclame in scenes (script + Nano Banana-beelden)">▶ Reclame</span>
        </div>
        {tab === "video" && <AIVideoStudio user={user} />}
        {tab === "rankloop" && <RankLoop user={user} services={services} />}
        {tab === "reclame" && <AdReclame user={user} />}
        {tab !== "video" && tab !== "rankloop" && tab !== "reclame" && (() => {
          if (loading) return <Leeg tekst="Laden..." />;
          if (filtered.length === 0) return (
            <div className="card" style={{ textAlign: "center" }}>
              <Leeg tekst="Nog geen voorstellen in deze view." />
              <button className="btn bp" style={{ marginBottom: 10 }} onClick={() => setNewMod(true)}>+ Genereer eerste voorstel</button>
            </div>
          );
          const groepen = [["pending", "Wacht op review", C.yellow], ["planned", "Gepland", C.cyan], ["approved", "Goedgekeurd", C.green], ["rejected", "Afgekeurd", C.red]]
            .map(([k, label, kleur]) => ({ k, label, kleur, items: filtered.filter(r => (r.status === "edited" || r.status === "executed" ? "approved" : GROWTH_STATUS_COL[r.status] ? r.status : "pending") === k) }))
            .filter(g => g.items.length);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {groepen.map(g => (
                <Groep key={tab + g.k} label={g.label} kleur={g.kleur} aantal={g.items.length} standaardDicht={groepen.length > 1 && (g.k === "approved" || g.k === "rejected")}>
                  {g.items.map(r => {
                    const eng = GROWTH_ENGINES.find(e => e.id === r.engine);
                    return (
                      <Rij key={r.id} titel={r.title} voorbeeld={r.doel} onKlik={() => setSel(r.id)}
                        meta={<>
                          {eng && <Bdg label={eng.icon + " " + eng.l} bg={eng.color + "20"} fg={eng.color} />}
                          <Bdg label={(ALL_TYPES.find(t => t.id === r.type) || {}).l || r.type} bg={C.border} fg={C.muted} />
                          {(r.status === "edited" || r.status === "executed") && <Bdg label={GROWTH_STATUS_COL[r.status].l} bg={GROWTH_STATUS_COL[r.status].bg} fg={GROWTH_STATUS_COL[r.status].fg} />}
                          <span>{(r.created_at || "").slice(0, 10)}</span>
                          {r.decided_by && <span>{`beslist door ${r.decided_by.split("@")[0]}`}</span>}
                          {r.scheduled_for && <span>{`gepland: ${r.scheduled_for}`}</span>}
                        </>} />
                    );
                  })}
                </Groep>
              ))}
            </div>
          );
        })()}
        {newMod && (
          <Portal><div className="ovl" onClick={() => !generating && setNewMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 14 }}>+ NIEUW GROWTH VOORSTEL</h3>
              {generating ? (
                <div style={{ padding: 30, textAlign: "center" }}>
                  <p style={{ fontSize: 14, marginBottom: 8 }}>⟳ {genStep}</p>
                  <p style={{ fontSize: 11, color: C.muted }}>AI verwerkt eerdere leerpunten en genereert voorstel...</p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                  <Fld label="Engine">
                    <select value={nv.engine} onChange={e => { const eng = e.target.value; setNv(p => ({ ...p, engine: eng, type: typesForEngine(eng)[0].id })); }}>
                      {GROWTH_ENGINES.map(e => <option key={e.id} value={e.id} disabled={!e.live}>{e.icon} {e.l}{!e.live ? " — Binnenkort" : ""}</option>)}
                    </select>
                  </Fld>
                  {nv.engine === "creative" && (
                    <div className="card" style={{ padding: 11, background: C.bg }}>
                      <p className="lbl" style={{ marginBottom: 6 }}>Productlink (optioneel, voor webshop-producten)</p>
                      <div style={{ display: "flex", gap: 7 }}>
                        <input value={productUrl} onChange={e => setProductUrl(e.target.value)} placeholder="https://store.dif3r3nt.nl/product/..." disabled={productBusy} />
                        <button className="btn bg" style={{ fontSize: 11, whiteSpace: "nowrap" }} disabled={productBusy || !productUrl.trim()} onClick={haalProductOp}>{productBusy ? "⟳ Ophalen..." : "Haal gegevens op"}</button>
                      </div>
                      {productErr && <p style={{ fontSize: 11, color: C.red, marginTop: 6 }}>✗ {productErr}</p>}
                      {productData && (
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginTop: 9 }}>
                          {productData.afbeelding_url && <img src={productData.afbeelding_url} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: "1px solid " + C.border }} />}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12, fontWeight: 600 }}>{productData.titel}</p>
                            <p style={{ fontSize: 11, color: C.muted }}>{productData.prijs ? productData.prijs + " " + (productData.valuta || "") : "geen prijs gevonden"} · {productData.domein}</p>
                          </div>
                          <button className="btn bg" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => { setProductData(null); setProductUrl(""); setWantVideo(false); }}>✗</button>
                        </div>
                      )}
                      {productData && productData.afbeelding_url && (
                        <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 11, marginTop: 9, color: C.muted }}>
                          <input type="checkbox" checked={wantVideo} onChange={e => setWantVideo(e.target.checked)} style={{ width: "auto" }} />
                          Ook een video genereren (image-to-video, eigen dagbudget bovenop AI Video)
                        </label>
                      )}
                    </div>
                  )}
                  {!productData && (<>
                    <Fld label={nv.engine === "seo" ? "SEO-deliverable" : "Content type"}>
                      <select value={nv.type} onChange={e => setNv(p => ({ ...p, type: e.target.value }))}>
                        {nv.engine === "seo" ? (
                          SEO_TYPES.map(c => <option key={c.id} value={c.id}>{c.l} - {c.len}</option>)
                        ) : (<>
                          <optgroup label="Tekst">
                            {CONTENT_TYPES.map(c => <option key={c.id} value={c.id}>{c.l} - {c.len}</option>)}
                          </optgroup>
                          <optgroup label="Visueel (export naar PNG/SVG)">
                            {VISUAL_TYPES.filter(c => c.id !== "product_creative").map(c => <option key={c.id} value={c.id}>{c.l} - {c.dim.w}×{c.dim.h}</option>)}
                          </optgroup>
                        </>)}
                      </select>
                    </Fld>
                    <Fld label="Dienst (kies uit Launch Module voor volledige context)">
                      <select value={nv.dienst} onChange={e => setNv(p => ({ ...p, dienst: e.target.value }))}>
                        <option value="">- Algemeen (geen specifieke dienst) -</option>
                        {Array.isArray(launches) && launches.length > 0 && (
                          <optgroup label="Diensten met Launch Module">
                            {launches.map(l => <option key={l.id} value={l.id}>{l.title}{l.status === "Gelanceerd" ? " " : l.doelgroep ? " ✓" : " ⚠ geen positionering"}</option>)}
                          </optgroup>
                        )}
                      </select>
                    </Fld>
                    {nv.dienst && Array.isArray(launches) && (() => {
                      const l = launches.find(x => x.id === nv.dienst); if (!l) return null; const has = l.doelgroep || l.value_proposition || l.pijnpunten; return (
                        <div style={{ padding: "7px 11px", background: has ? C.greenDim : C.yellowDim, border: "1px solid " + (has ? C.green + "55" : C.yellow + "55"), borderRadius: 5, fontSize: 11, color: has ? C.green : C.yellow }}>
                          {has ? <>✓ Volledige context beschikbaar uit Launch Module, AI gebruikt de echte doelgroep, USP's en pijnpunten.</> : <>⚠ Deze dienst heeft nog geen positionering in Launch Module. Genereer eerst het Launch Package, anders moet de AI te veel gokken.</>}
                        </div>
                      );
                    })()}
                  </>)}
                  <Fld label={productData ? "Extra instructie voor AI (optioneel)" : "Brief voor AI *"}>
                    <textarea rows={productData ? 3 : 5} value={nv.brief} onChange={e => setNv(p => ({ ...p, brief: e.target.value }))} placeholder={productData ? "Bijv: 'Nadruk op de prijs' of 'Toon gericht op cadeautjes'" : "Wat moet de Content Engine genereren? Bijv: 'Blog over wettelijke verplichtingen rondom kilometeradministratie voor VOFs in 2026, gericht op kleine ondernemers'"} />
                  </Fld>
                  <div style={{ display: "flex", gap: 7, justifyContent: "flex-end" }}>
                    <button className="btn bg" onClick={() => setNewMod(false)}>Annuleer</button>
                    <button className="btn bp" onClick={generateProposal} disabled={!nv.brief.trim() && !productData}>Genereer voorstel</button>
                  </div>
                </div>
              )}
            </div>
          </div></Portal>
        )}
        {editMod && selRv && renderEditModal(selRv)}
        {rejectMod && selRv && (
          <Portal><div className="ovl" onClick={() => setRejectMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 12 }}>✗ AFKEUREN</h3>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 11, lineHeight: 1.5 }}>Geef de reden — de AI leert hiervan en doet dit voorstel niet opnieuw.</p>
              <Fld label="Reden afkeuring *">
                <textarea rows={4} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Bijv: 'Toon te formeel, niet onze doelgroep' of 'Onderwerp al behandeld vorige maand'" />
              </Fld>
              <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 12 }}>
                <button className="btn bg" onClick={() => setRejectMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={() => decide(selRv, "rejected", { reject_reason: rejectReason, notes: rejectReason })} disabled={!rejectReason.trim()} style={{ background: C.red, color: "#fff" }}>✗ Afkeuren</button>
              </div>
            </div>
          </div></Portal>
        )}
        {planMod && selRv && (
          <Portal><div className="ovl" onClick={() => setPlanMod(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 12 }}>PLAN VOOR LATER</h3>
              <Fld label="Publicatiedatum">
                <input type="date" value={planDate} onChange={e => setPlanDate(e.target.value)} min={dateStr(TODAY)} />
              </Fld>
              <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 12 }}>
                <button className="btn bg" onClick={() => setPlanMod(false)}>Annuleer</button>
                <button className="btn bp" onClick={() => decide(selRv, "planned", { scheduled_for: planDate, notes: "Gepland voor " + planDate })}>Plan</button>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  // Oproep naar de twee Meta Ads-edge functions. Zelfde patroon als verkoopCall (operatie-verkoop):
  // Supabase stuurt het inlogtoken van de teamlid mee, de functie controleert dat zelf.
  async function metaAdsSyncCall(actie, payload = {}) {
    const { data, error } = await supabase.functions.invoke("meta-ads-sync", { body: { actie, ...payload } });
    if (error) {
      let msg = error.message || "Aanroep mislukt";
      try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* standaardbericht */ }
      throw new Error(msg);
    }
    if (data && data.error) throw new Error(data.error);
    return data;
  }
  async function metaAdsActieCall(reviewId) {
    const { data, error } = await supabase.functions.invoke("meta-ads-actie", { body: { review_id: reviewId } });
    if (error) {
      let msg = error.message || "Aanroep mislukt";
      try { const j = await error.context.json(); if (j && j.error) msg = j.error; } catch (e) { /* standaardbericht */ }
      throw new Error(msg);
    }
    if (data && data.error) throw new Error(data.error);
    return data;
  }

  // ─── MARKETING (overkoepelend dashboard: Growth + Meta Ads Mastery + Klantenservice) ──────
  function MarketingDashboard({ services, setView }) {
    const [reviews, setReviews] = useState([]);
    const [inzichten, setInzichten] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      async function load() {
        const [{ data: rv }, { data: inz }] = await Promise.all([
          supabase.from("growth_reviews").select("id,engine,status,dienst_id,title,created_at").order("created_at", { ascending: false }),
          supabase.from("meta_ads_inzichten").select("spend,clicks,ctr,cpc,datum,campaign_id").order("datum", { ascending: false }).limit(50),
        ]);
        if (rv) setReviews(rv);
        if (inz) setInzichten(inz);
        setLoading(false);
      }
      load();
      const ch = supabase.channel("marketing_dashboard_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "growth_reviews" }, () => load())
        .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_inzichten" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    const MARKETING_ENGINES = ["content", "social", "ads", "meta_ads", "creative", "klantenservice"];
    const relevant = reviews.filter(r => MARKETING_ENGINES.includes(r.engine));
    const pending = relevant.filter(r => r.status === "pending");
    const perEngine = GROWTH_ENGINES.filter(e => MARKETING_ENGINES.includes(e.id)).map(e => ({
      ...e,
      pending: relevant.filter(r => r.engine === e.id && r.status === "pending").length,
      totaal: relevant.filter(r => r.engine === e.id).length,
    }));
    const spendTotaal = inzichten.reduce((s, i) => s + Number(i.spend || 0), 0);
    const clicksTotaal = inzichten.reduce((s, i) => s + Number(i.clicks || 0), 0);

    function Stat({ label, waarde, kleur, sub }) {
      return (
        <div className="card" style={{ padding: 14 }}>
          <p className="lbl" style={{ marginBottom: 6 }}>{label}</p>
          <p style={{ fontSize: 24, fontWeight: 700, color: kleur || C.text, fontFamily: "monospace" }}>{waarde}</p>
          {sub && <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</p>}
        </div>
      );
    }

    if (loading) return <div style={{ padding: 20 }}>Laden...</div>;
    return (
      <div style={{ padding: 20 }}>
        <h2 style={{ ...KOP_STIJL, marginBottom: 4 }}>Marketing</h2>
        <p style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
          Overzicht over Growth, Meta Ads Mastery, Creative Mastery en Klantenservice samen. Alle beoordeling gebeurt op 1 plek: de Growth-inbox.
        </p>
        <div className="row-grid" style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <Stat label="Wacht op beoordeling" waarde={pending.length} kleur={pending.length ? C.yellow : C.green} sub="over alle marketing-engines" />
          <Stat label="Meta Ads-spend (laatste dagen)" waarde={"€" + spendTotaal.toFixed(2)} kleur={C.red} sub={clicksTotaal + " clicks"} />
          <Stat label="Diensten met marketing-activiteit" waarde={new Set(relevant.filter(r => r.dienst_id).map(r => r.dienst_id)).size} kleur={C.blue} sub={(services || []).length + " diensten totaal"} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
          {perEngine.map(e => (
            <div key={e.id} className="card" style={{ padding: 14, cursor: "pointer" }} onClick={() => setView && setView(e.id === "meta_ads" ? "meta-ads" : e.id === "klantenservice" ? "klantenservice" : "growth")}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{e.icon}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{e.l}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{e.pending} wacht op review · {e.totaal} totaal</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── KLANTENSERVICE (intern ticketoverzicht + AI-conceptantwoord) ─────────────────────────
  function KlantenserviceModule({ user, services }) {
    const meEmail = user?.email || "";
    const [tickets, setTickets] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newT, setNewT] = useState(false);
    const [form, setForm] = useState({ klant_naam: "", klant_email: "", kanaal: "webshop", dienst_id: "", onderwerp: "", bericht: "" });
    const [generating, setGenerating] = useState(null);

    useEffect(() => {
      async function load() {
        const [{ data: t }, { data: rv }] = await Promise.all([
          supabase.from("klantenservice_tickets").select("*").order("created_at", { ascending: false }),
          supabase.from("growth_reviews").select("*").eq("engine", "klantenservice").order("created_at", { ascending: false }),
        ]);
        if (t) setTickets(t);
        if (rv) setReviews(rv);
        setLoading(false);
      }
      load();
      const ch = supabase.channel("klantenservice_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "klantenservice_tickets" }, () => load())
        .on("postgres_changes", { event: "*", schema: "public", table: "growth_reviews" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    async function addTicket() {
      if (!form.bericht.trim()) return;
      const row = { ...form, dienst_id: form.dienst_id || null, created_by: meEmail };
      const { data, error } = await supabase.from("klantenservice_tickets").insert([row]).select();
      if (!error && data) { setTickets(p => [data[0], ...p]); setNewT(false); setForm({ klant_naam: "", klant_email: "", kanaal: "webshop", dienst_id: "", onderwerp: "", bericht: "" }); }
    }

    async function genereerAntwoord(ticket) {
      setGenerating(ticket.id);
      try {
        const dienst = (services || []).find(s => s.id === ticket.dienst_id);
        const dienstBlok = dienst ? `Dienst: ${dienst.name}\n${dienst.description || ""}` : "(geen specifieke dienst gekoppeld)";
        const sys = "Je schrijft conceptantwoorden voor klantenservice van Dif3r3nt, een Nederlands MKB-bedrijf. Toon: vriendelijk, direct, geen overdreven excuses, geen toezeggingen die niet uit de input blijken (geen beloofde data, geen restitutie-bedragen verzinnen). Antwoord ALLEEN met geldige JSON: {\"antwoord\":\"string\"}.";
        const userMsg = `=== TICKET ===\nKanaal: ${ticket.kanaal}\nOnderwerp: ${ticket.onderwerp || "(geen onderwerp)"}\nBericht van klant:\n${ticket.bericht}\n=== EINDE TICKET ===\n\n=== DIENST-CONTEXT ===\n${dienstBlok}\n=== EINDE DIENST-CONTEXT ===\n\nSchrijf een kort, correct conceptantwoord in het Nederlands. Als er feiten ontbreken om de vraag goed te beantwoorden, zeg dat in het antwoord i.p.v. iets te verzinnen.`;
        const { parsed } = await callGroq(sys, userMsg, { maxTokens: 800, temperature: 0.3 });
        if (!parsed || !parsed.antwoord) { setGenerating(null); return; }
        const row = {
          engine: "klantenservice",
          type: "ticket_antwoord",
          title: "Conceptantwoord: " + (ticket.onderwerp || ticket.bericht.slice(0, 60)),
          doel: "Klaarzetten van een conceptantwoord voor dit ticket.",
          waarom: "AI-gegenereerd op basis van het ticket" + (dienst ? " en de gekoppelde dienst." : "."),
          preview: { antwoord: parsed.antwoord },
          data_context: { ticket_id: ticket.id },
          dienst_id: ticket.dienst_id || null,
          status: "pending",
        };
        const { data, error } = await supabase.from("growth_reviews").insert([row]).select();
        if (!error && data) {
          await supabase.from("growth_audit_log").insert([{ review_id: data[0].id, action: "created", actor: meEmail || "unknown", after_state: row, notes: "AI-conceptantwoord voor ticket " + ticket.id }]);
        }
      } catch (e) { console.error("Klantenservice conceptantwoord:", e); }
      setGenerating(null);
    }

    useEffect(() => {
      // Als een conceptantwoord goedgekeurd wordt, zet het ticket op "klaar om te versturen".
      // Geen auto-send: er is geen kanaalkoppeling, Furgel verstuurt het zelf.
      async function sync() {
        for (const r of reviews) {
          if (r.status === "approved" && r.data_context?.ticket_id) {
            const t = tickets.find(x => x.id === r.data_context.ticket_id);
            if (t && t.status === "open") {
              await supabase.from("klantenservice_tickets").update({ status: "concept_klaar", updated_at: new Date().toISOString() }).eq("id", t.id);
            }
          }
        }
      }
      if (reviews.length && tickets.length) sync();
    }, [reviews, tickets.length]);

    const TICKET_STATUS = { open: { bg: C.yellowDim, fg: C.yellow, l: "Open" }, concept_klaar: { bg: C.blueDim, fg: C.blue, l: "Concept klaar" }, beantwoord: { bg: C.greenDim, fg: C.green, l: "Beantwoord" }, gesloten: { bg: C.border, fg: C.muted, l: "Gesloten" } };

    if (loading) return <div style={{ padding: 20 }}>Laden...</div>;
    return (
      <div className="fu">
        <Kop titel="Klantenservice" sub={'Intern ticketoverzicht. Een conceptantwoord moet eerst goedgekeurd worden in de Growth-inbox voor het als "klaar" telt.'}
          rechts={<button className="btn bp" onClick={() => setNewT(true)}>+ Nieuw ticket</button>} />
        {tickets.length === 0 ? <div className="card"><Leeg tekst="Nog geen tickets." /></div> : (() => {
          const groepen = [["open", "Open", C.red], ["concept_klaar", "Concept klaar", C.yellow], ["beantwoord", "Beantwoord", C.green], ["gesloten", "Gesloten", C.muted]]
            .map(([k, label, kleur]) => ({ k, label, kleur, items: tickets.filter(t => (["concept_klaar", "beantwoord", "gesloten"].includes(t.status) ? t.status : "open") === k) }))
            .filter(g => g.items.length);
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {groepen.map(g => (
                <Groep key={g.k} label={g.label} kleur={g.kleur} aantal={g.items.length} standaardDicht={g.k === "gesloten" || g.k === "beantwoord"}>
                  {g.items.map(t => {
                    const st = TICKET_STATUS[t.status] || TICKET_STATUS.open;
                    const gekoppeld = reviews.filter(r => r.data_context?.ticket_id === t.id);
                    return (
                      <Rij key={t.id} titel={(t.onderwerp || "(geen onderwerp)") + (t.klant_naam ? " · " + t.klant_naam : "")} voorbeeld={t.bericht}
                        rechts={<span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: st.bg, color: st.fg, whiteSpace: "nowrap" }}>{st.l}</span>}
                        meta={<><span>{t.kanaal}</span><span>{new Date(t.created_at).toLocaleDateString("nl-NL")}</span></>}>
                        <p style={{ fontSize: 12, margin: "0 0 8px", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{t.bericht}</p>
                        {gekoppeld.map(r => (
                          <div key={r.id} style={{ marginTop: 8, padding: 10, borderRadius: 6, background: C.bg2, fontSize: 12 }}>
                            <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>Conceptantwoord ({r.status === "pending" ? "wacht op review in Growth" : r.status})</div>
                            {r.preview?.antwoord}
                          </div>
                        ))}
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                          {t.status === "open" && (
                            <button className="btn bg" disabled={generating === t.id} onClick={() => genereerAntwoord(t)}>
                              {generating === t.id ? "⟳ AI schrijft..." : "AI-conceptantwoord genereren"}
                            </button>
                          )}
                          {t.status !== "gesloten" && (
                            <button className="btn bg" style={{ fontSize: 11 }} onClick={() => supabase.from("klantenservice_tickets").update({ status: "gesloten", updated_at: new Date().toISOString() }).eq("id", t.id)}>Sluiten</button>
                          )}
                        </div>
                      </Rij>
                    );
                  })}
                </Groep>
              ))}
            </div>
          );
        })()}
        {newT && (
          <Portal><div className="ovl" onClick={() => setNewT(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 14 }}>+ NIEUW TICKET</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Fld label="Klant naam"><input value={form.klant_naam} onChange={e => setForm(p => ({ ...p, klant_naam: e.target.value }))} /></Fld>
                <Fld label="Klant e-mail"><input value={form.klant_email} onChange={e => setForm(p => ({ ...p, klant_email: e.target.value }))} /></Fld>
                <Fld label="Kanaal">
                  <select value={form.kanaal} onChange={e => setForm(p => ({ ...p, kanaal: e.target.value }))}>
                    <option value="webshop">Webshop</option>
                    <option value="email">E-mail</option>
                    <option value="telefoon">Telefoon</option>
                    <option value="overig">Overig</option>
                  </select>
                </Fld>
                <Fld label="Dienst (optioneel)">
                  <select value={form.dienst_id} onChange={e => setForm(p => ({ ...p, dienst_id: e.target.value }))}>
                    <option value="">Geen</option>
                    {(services || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Fld>
                <Fld label="Onderwerp"><input value={form.onderwerp} onChange={e => setForm(p => ({ ...p, onderwerp: e.target.value }))} /></Fld>
                <Fld label="Bericht van klant *"><textarea rows={4} value={form.bericht} onChange={e => setForm(p => ({ ...p, bericht: e.target.value }))} /></Fld>
              </div>
              <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 14 }}>
                <button className="btn bg" onClick={() => setNewT(false)}>Annuleer</button>
                <button className="btn bp" disabled={!form.bericht.trim()} onClick={addTicket}>Opslaan</button>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  // ─── AI VIDEO (los formulier: prompt in, clip uit via de RunComfy Model API) ──────────────
  const AI_VIDEO_PRESETS = [
    { id: "bytedance/seedance-v1.5-pro/text-to-video", label: "Seedance 1.5 Pro - snel en goedkoop (~$0,01/s @480p)", resolution: "480p" },
    { id: "bytedance/seedance-2.5/text-to-video/720p", label: "Seedance 2.5 - 720p, betere kwaliteit (~$0,31/s)" },
    { id: "bytedance/seedance-2.5/text-to-video/1080p", label: "Seedance 2.5 - 1080p, hoogste kwaliteit (~$0,57/s)" },
  ];
  function AIVideoModule() {
    const [preset, setPreset] = useState(AI_VIDEO_PRESETS[0].id);
    const [prompt, setPrompt] = useState("");
    const [duration, setDuration] = useState(5);
    const [aspect, setAspect] = useState("16:9");
    const [audio, setAudio] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | submitting | polling | done | error
    const [error, setError] = useState("");
    const [videoUrl, setVideoUrl] = useState("");

    async function genereer() {
      if (!prompt.trim()) return;
      setStatus("submitting"); setError(""); setVideoUrl("");
      const p = AI_VIDEO_PRESETS.find(x => x.id === preset);
      const input = { prompt: prompt.trim(), duration, aspect_ratio: aspect, generate_audio: audio };
      if (p.resolution) input.resolution = p.resolution;
      const { data: sub, error: e1 } = await supabase.functions.invoke("ai-video", { body: { action: "submit", model_id: p.id, input } });
      if (e1 || !sub?.request_id) { setStatus("error"); setError((sub && sub.error) || e1?.message || "versturen mislukt"); return; }
      setStatus("polling");
      const requestId = sub.request_id;
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const { data: st, error: e2 } = await supabase.functions.invoke("ai-video", { body: { action: "status", request_id: requestId } });
        if (e2) { setStatus("error"); setError(e2.message || "status ophalen mislukt"); return; }
        if (st?.status === "completed") {
          const { data: res, error: e3 } = await supabase.functions.invoke("ai-video", { body: { action: "result", request_id: requestId } });
          if (e3 || !res?.video) { setStatus("error"); setError((res && res.error) || e3?.message || "resultaat ophalen mislukt"); return; }
          setVideoUrl(res.video); setStatus("done"); return;
        }
        if (st?.status === "failed" || st?.status === "error") { setStatus("error"); setError(st?.error || "generatie mislukt"); return; }
      }
      setStatus("error"); setError("Duurde te lang (meer dan 4,5 minuut). Probeer het later nog eens.");
    }

    const bezig = status === "submitting" || status === "polling";
    return (
      <div style={{ padding: 20, maxWidth: 620 }}>
        <h2 style={{ ...KOP_STIJL, marginBottom: 4 }}>AI Video</h2>
        <p style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
          Genereer een losse videoclip via de RunComfy Model API. Elke generatie kost geld op het gedeelde RunComfy-saldo; bekijk de prijzen op runcomfy.com/models voor je een duur model kiest.
        </p>
        <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={{ fontSize: 12, color: C.muted }}>Model</label>
          <select value={preset} onChange={e => setPreset(e.target.value)} disabled={bezig}>
            {AI_VIDEO_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <label style={{ fontSize: 12, color: C.muted }}>Prompt</label>
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)} disabled={bezig} rows={4}
            placeholder="Beschrijf het shot: onderwerp, beweging, sfeer, camerawerk..." style={{ fontFamily: "inherit", fontSize: 13, padding: 8 }} />
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <label style={{ fontSize: 12, color: C.muted }}>Duur (s)
              <input type="number" min={3} max={15} value={duration} onChange={e => setDuration(Number(e.target.value))} disabled={bezig} style={{ marginLeft: 6, width: 60 }} />
            </label>
            <label style={{ fontSize: 12, color: C.muted }}>Beeldverhouding
              <select value={aspect} onChange={e => setAspect(e.target.value)} disabled={bezig} style={{ marginLeft: 6 }}>
                <option value="16:9">16:9</option><option value="9:16">9:16</option><option value="1:1">1:1</option>
              </select>
            </label>
            <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={audio} onChange={e => setAudio(e.target.checked)} disabled={bezig} /> Audio genereren
            </label>
          </div>
          <button className="btn bg" disabled={bezig || !prompt.trim()} onClick={genereer} style={{ alignSelf: "flex-start" }}>
            {status === "submitting" ? "Versturen..." : status === "polling" ? "Bezig met genereren..." : "Genereer clip"}
          </button>
          {status === "error" && <p style={{ color: C.red, fontSize: 13 }}>Mislukt: {error}</p>}
          {status === "done" && videoUrl && (
            <div style={{ marginTop: 6 }}>
              <video src={videoUrl} controls style={{ width: "100%", borderRadius: 10, background: "#000" }} />
              <p style={{ fontSize: 12, marginTop: 6 }}><a href={videoUrl} target="_blank" rel="noreferrer" style={{ color: C.blue }}>Open video in nieuw tabblad</a></p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── META ADS MASTERY (live Meta Ads-koppeling, acties lopen via de Growth-poort) ─────────
  function MetaAdsModule({ user, services }) {
    const meEmail = user?.email || "";
    const [accounts, setAccounts] = useState([]);
    const [campagnes, setCampagnes] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newA, setNewA] = useState(false);
    const [form, setForm] = useState({ label: "", ad_account_id: "", dienst_id: "", token: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [info, setInfo] = useState("");
    const herlaadRef = useRef(null);

    useEffect(() => {
      async function load() {
        const [{ data: acc }, { data: camp }, { data: rv }] = await Promise.all([
          supabase.from("meta_ads_accounts").select("id,dienst_id,label,ad_account_id,token_status,last_sync_at").order("created_at", { ascending: false }),
          supabase.from("meta_ads_campagnes").select("id,account_id,naam,status,dagbudget,laatst_gezien_at"),
          supabase.from("growth_reviews").select("*").eq("engine", "meta_ads").order("created_at", { ascending: false }),
        ]);
        if (acc) setAccounts(acc);
        if (camp) setCampagnes(camp);
        if (rv) setReviews(rv);
        setLoading(false);
      }
      herlaadRef.current = load;
      load();
      const ch = supabase.channel("meta_ads_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_accounts" }, () => load())
        .on("postgres_changes", { event: "*", schema: "public", table: "meta_ads_campagnes" }, () => load())
        .on("postgres_changes", { event: "*", schema: "public", table: "growth_reviews" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    async function koppelen() {
      if (!form.label.trim() || !form.ad_account_id.trim() || !form.token.trim()) return;
      setBusy(true); setErr("");
      try {
        await metaAdsSyncCall("koppelen", { label: form.label, ad_account_id: form.ad_account_id, dienst_id: form.dienst_id || null, token: form.token });
        setNewA(false);
        setForm({ label: "", ad_account_id: "", dienst_id: "", token: "" });
        setInfo("Account gekoppeld. Klik op Nu synchroniseren om de campagnes op te halen.");
        if (herlaadRef.current) await herlaadRef.current();
      } catch (e) { setErr(e.message); }
      setBusy(false);
    }

    async function syncNu() {
      setBusy(true); setErr(""); setInfo("");
      try {
        const res = await metaAdsSyncCall("tick");
        const lijst = (res && res.resultaten) || [];
        const fouten = lijst.filter(r => r.fout).map(r => r.fout);
        if (fouten.length) setErr("Synchronisatie mislukt: " + fouten.join(" | "));
        else {
          const aantal = lijst.reduce((s, r) => s + (r.campagnes || 0), 0);
          setInfo("Gesynchroniseerd: " + aantal + " campagne" + (aantal === 1 ? "" : "s") + " opgehaald.");
        }
        if (herlaadRef.current) await herlaadRef.current();
      } catch (e) { setErr(e.message); }
      setBusy(false);
    }

    async function decide(review, status) {
      const now = new Date().toISOString();
      const { data, error } = await supabase.from("growth_reviews").update({ status, decided_by: meEmail, decided_at: now, updated_at: now }).eq("id", review.id).select();
      if (!error && data) setReviews(p => p.map(r => r.id === review.id ? data[0] : r));
    }

    async function voerUit(review) {
      setBusy(true); setErr(""); setInfo("");
      try {
        await metaAdsActieCall(review.id);
        setInfo("Actie uitgevoerd op Meta.");
        if (herlaadRef.current) await herlaadRef.current();
      } catch (e) { setErr(e.message); }
      setBusy(false);
    }

    const TOKEN_STATUS = { ok: { bg: C.greenDim, fg: C.green, l: "OK" }, verlopen: { bg: C.redDim, fg: C.red, l: "Verlopen" }, ingetrokken: { bg: C.redDim, fg: C.red, l: "Ingetrokken" }, onbekend: { bg: C.border, fg: C.muted, l: "Onbekend" } };

    if (loading) return <div style={{ padding: 20 }}>Laden...</div>;
    return (
      <div className="fu">
        <Kop titel="Meta Ads Mastery" sub="Alleen lezen is automatisch (campagnes en resultaten). Elke schrijf-actie (pauzeren, budget aanpassen) komt eerst als voorstel in de Growth-inbox en wordt pas na goedkeuring uitgevoerd."
          rechts={<>
            <button className="btn bg" disabled={busy || accounts.length === 0} onClick={syncNu}>⟳ Nu synchroniseren</button>
            <button className="btn bp" onClick={() => setNewA(true)}>+ Account koppelen</button>
          </>} />
        {err && <p style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>{err}</p>}
        {info && !err && <p style={{ fontSize: 12, color: C.green, marginBottom: 10 }}>{info}</p>}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Groep label="Gekoppelde accounts" kleur={C.blue} aantal={accounts.length}>
            {accounts.length === 0 && <Leeg tekst="Nog geen Meta Ads-account gekoppeld." />}
            {accounts.map(a => {
              const st = TOKEN_STATUS[a.token_status] || TOKEN_STATUS.onbekend;
              const aantal = campagnes.filter(c => c.account_id === a.id).length;
              return (
                <Rij key={a.id} titel={a.label}
                  rechts={<span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: st.bg, color: st.fg }}>{st.l}</span>}
                  meta={<>
                    <span>{a.ad_account_id}</span>
                    <span>{aantal} campagne{aantal === 1 ? "" : "s"}</span>
                    <span>{a.last_sync_at ? "laatst gesynchroniseerd " + new Date(a.last_sync_at).toLocaleString("nl-NL") : "nog niet gesynchroniseerd"}</span>
                  </>} />
              );
            })}
          </Groep>

          {campagnes.length > 0 && (
            <Groep label="Campagnes" kleur={C.green} aantal={campagnes.length}>
              {[...campagnes].sort((x, y) => (x.status === "ACTIVE" ? 0 : 1) - (y.status === "ACTIVE" ? 0 : 1) || String(x.naam).localeCompare(String(y.naam))).map(c => (
                <Rij key={c.id} titel={c.naam} gedimd={c.status !== "ACTIVE"}
                  rechts={<Bdg label={c.status === "ACTIVE" ? "● Actief" : c.status === "PAUSED" ? "Gepauzeerd" : c.status} bg={c.status === "ACTIVE" ? C.greenDim : C.border} fg={c.status === "ACTIVE" ? C.green : C.muted} />}
                  meta={<>
                    {c.dagbudget != null && <span>{"€" + Number(c.dagbudget).toLocaleString("nl-NL") + " per dag"}</span>}
                    {c.laatst_gezien_at && <span>{"bijgewerkt " + new Date(c.laatst_gezien_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                  </>} />
              ))}
            </Groep>
          )}

          <Groep label="Voorstellen en signalen" kleur={C.yellow} aantal={reviews.length}>
            {reviews.length === 0 && <Leeg tekst="Nog niets te beoordelen." />}
            {reviews.map(r => (
              <Rij key={r.id} titel={r.title} gedimd={r.status === "rejected" || r.status === "executed"}
                meta={<><span>{r.type}</span><span>{r.status}</span></>}
                standaardOpen={r.status === "pending"}>
                {r.waarom && <p style={{ fontSize: 12, margin: "0 0 8px", lineHeight: 1.5 }}>{r.waarom}</p>}
                {r.status === "pending" && (
                  <div style={{ display: "flex", gap: 7 }}>
                    <button className="btn bg" onClick={() => decide(r, "rejected")}>Afkeuren</button>
                    <button className="btn bp" onClick={() => decide(r, "approved")}>Goedkeuren</button>
                  </div>
                )}
                {r.status === "approved" && r.type === "actie" && <button className="btn bp" disabled={busy} onClick={() => voerUit(r)}>Uitvoeren op Meta</button>}
              </Rij>
            ))}
          </Groep>
        </div>

        {newA && (
          <Portal><div className="ovl" onClick={() => !busy && setNewA(false)}>
            <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
              <h3 style={{ fontFamily: "monospace", fontSize: 14, marginBottom: 14 }}>+ META ADS-ACCOUNT KOPPELEN</h3>
              <p style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Het token wordt direct versleuteld opgeslagen en nooit meer teruggestuurd naar deze browser.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Fld label="Label *"><input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="Bijv. Casa Celeste" /></Fld>
                <Fld label="Ad account ID *"><input value={form.ad_account_id} onChange={e => setForm(p => ({ ...p, ad_account_id: e.target.value }))} placeholder="act_123456789" /></Fld>
                <Fld label="Dienst (optioneel)">
                  <select value={form.dienst_id} onChange={e => setForm(p => ({ ...p, dienst_id: e.target.value }))}>
                    <option value="">Geen</option>
                    {(services || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Fld>
                <Fld label="Access token *"><input type="password" value={form.token} onChange={e => setForm(p => ({ ...p, token: e.target.value }))} /></Fld>
              </div>
              {err && <p style={{ fontSize: 12, color: C.red, marginTop: 10 }}>{err}</p>}
              <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 14 }}>
                <button className="btn bg" onClick={() => setNewA(false)} disabled={busy}>Annuleer</button>
                <button className="btn bp" disabled={busy || !form.label.trim() || !form.ad_account_id.trim() || !form.token.trim()} onClick={koppelen}>{busy ? "⟳ Koppelen..." : "Koppelen"}</button>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  // ─── BUG TRACKER ──────────────────────────────────────────────────────────────
  const BUG_STATUS = { open: { bg: C.redDim, fg: C.red, label: "OPEN" }, in_progress: { bg: C.yellowDim, fg: C.yellow, label: "IN PROGRESS" }, resolved: { bg: C.greenDim, fg: C.green, label: "RESOLVED" }, wontfix: { bg: C.border, fg: C.muted, label: "WON'T FIX" } };

  // Mapping van interne view-ids naar leesbare module-namen (voor bug-rapportage)
  const MODULE_LABELS = {
    dashboard: "Dashboard", ecosystem: "Ecosysteem", validatie: "Validatie", pipeline: "Bouwpipeline",
    launch: "Launch Module", growth: "Growth Operator", live: "Live Diensten",
    marketing: "Marketing", "meta-ads": "Meta Ads Mastery", klantenservice: "Klantenservice",
    crm: "CRM", administratie: "Administratie", documenten: "Documenten",
    agenda: "Agenda", taken: "Taken", meeting: "Meetings", brainstorm: "Brainstorm",
    bugs: "Bug Tracker", legacy: "Onbekend (vóór tracking)",
    vandaag: "Vandaag", klanten: "Klanten", facturatie: "Offertes & Facturen", systeem: "Systeemstatus", control: "Control", nieuwsbrief: "Nieuwsbrief Store", shopguard: "ShopGuard",
  };

  function ReportBugButton({ user, view, setBugs, setView }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [screenshotFile, setScreenshotFile] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [form, setForm] = useState({ title: "", summary: "", priority: "MED" });
    const fileRef = useRef(null);
    const [listening, setListening] = useState(false);
    const [interim, setInterim] = useState("");
    const recRef = useRef(null);
    const summaryRef = useRef("");
    const canCapture = typeof navigator !== "undefined" && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === "function";
    const canSpeech = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

    function handleFileSelect(e) {
      const f = e.target.files?.[0];
      if (!f) return;
      setScreenshotFile(f);
      const reader = new FileReader();
      reader.onload = (ev) => setScreenshotPreview(ev.target.result);
      reader.readAsDataURL(f);
    }

    // DOM-capture via html2canvas: pakt de huidige viewport INCLUSIEF de bug-modal zelf,
    // zodat de reviewer ziet wat jij ziet (geen permission-prompt nodig).
    async function captureDOM() {
      if (capturing) return;
      setCapturing(true);
      try {
        if (!window.html2canvas) {
          await new Promise((res, rej) => {
            const existing = document.querySelector('script[data-h2c]');
            if (existing) { existing.addEventListener("load", res); existing.addEventListener("error", rej); return; }
            const s = document.createElement("script");
            s.dataset.h2c = "1";
            s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
            s.onload = res; s.onerror = rej;
            document.body.appendChild(s);
          });
        }
        // Snagit-stijl: de bug-modal (overlay met data-bug-overlay) wordt UITGESLOTEN
        // van de opname, zodat de daadwerkelijke fout op de pagina eronder zichtbaar is.
        const canvas = await window.html2canvas(document.body, {
          backgroundColor: "#0a0a0c",
          logging: false,
          useCORS: true,
          allowTaint: true,
          ignoreElements: (el) => !!(el && el.dataset && el.dataset.bugOverlay),
          scale: Math.min(2, window.devicePixelRatio || 1),
          windowWidth: document.documentElement.clientWidth,
          windowHeight: document.documentElement.clientHeight,
          x: window.scrollX,
          y: window.scrollY,
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight,
        });
        const blob = await new Promise(res => canvas.toBlob(res, "image/png", 0.92));
        if (blob) {
          const file = new File([blob], `bug_${Date.now()}.png`, { type: "image/png" });
          setScreenshotFile(file);
          const reader = new FileReader();
          reader.onload = (ev) => setScreenshotPreview(ev.target.result);
          reader.readAsDataURL(blob);
        }
      } catch (e) {
        console.warn("DOM capture:", e);
        alert("Schermknipsel mislukt: " + (e?.message || "onbekende fout") + "\n\nGebruik 'Uit bestand' om handmatig een screenshot te uploaden.");
      }
      setCapturing(false);
    }

    // Volledig scherm/venster capturen via getDisplayMedia (kan ook andere apps/tabs pakken).
    async function captureScreen() {
      if (!canCapture || capturing) return;
      setCapturing(true);
      let stream;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: "always" }, audio: false, preferCurrentTab: true });
        const track = stream.getVideoTracks()[0];
        await new Promise(r => setTimeout(r, 150));
        let blob;
        if (typeof window.ImageCapture === "function") {
          const cap = new window.ImageCapture(track);
          const bitmap = await cap.grabFrame();
          const canvas = document.createElement("canvas");
          canvas.width = bitmap.width; canvas.height = bitmap.height;
          canvas.getContext("2d").drawImage(bitmap, 0, 0);
          blob = await new Promise(res => canvas.toBlob(res, "image/png", 0.92));
        } else {
          const video = document.createElement("video");
          video.srcObject = stream; await video.play();
          await new Promise(r => setTimeout(r, 200));
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth || 1280; canvas.height = video.videoHeight || 720;
          canvas.getContext("2d").drawImage(video, 0, 0);
          blob = await new Promise(res => canvas.toBlob(res, "image/png", 0.92));
          video.srcObject = null;
        }
        track.stop(); stream.getTracks().forEach(t => t.stop());
        if (blob) {
          const file = new File([blob], `bug_${Date.now()}.png`, { type: "image/png" });
          setScreenshotFile(file);
          const reader = new FileReader();
          reader.onload = (ev) => setScreenshotPreview(ev.target.result);
          reader.readAsDataURL(blob);
        }
      } catch (e) {
        if (e?.name !== "NotAllowedError") console.warn("Screen capture:", e);
        try { stream && stream.getTracks().forEach(t => t.stop()); } catch { }
      }
      setCapturing(false);
    }

    function startSpeech() {
      if (!canSpeech || listening) return;
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      const r = new SR();
      r.lang = "nl-NL"; r.continuous = true; r.interimResults = true;
      summaryRef.current = form.summary || "";
      r.onresult = (e) => {
        let final = "", inter = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) final += e.results[i][0].transcript + " ";
          else inter += e.results[i][0].transcript;
        }
        if (final) {
          summaryRef.current = (summaryRef.current + " " + final).replace(/\s+/g, " ").trim();
          setForm(p => ({ ...p, summary: summaryRef.current }));
        }
        setInterim(inter);
      };
      r.onend = () => { setListening(false); setInterim(""); };
      r.onerror = (e) => { if (e.error !== "no-speech") console.warn("Speech:", e.error); };
      try { r.start(); recRef.current = r; setListening(true); } catch (e) { console.warn(e); }
    }

    function stopSpeech() {
      if (recRef.current) { try { recRef.current.stop(); } catch { } recRef.current = null; }
      setListening(false); setInterim("");
    }

    async function submit() {
      if (!form.title.trim()) return;
      stopSpeech();
      setSubmitting(true);
      try {
        let url = "";
        if (screenshotFile) {
          const ext = screenshotFile.name.split(".").pop() || "png";
          const path = `bugs/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
          const { error: upErr } = await supabase.storage.from("bug-screenshots").upload(path, screenshotFile, { cacheControl: "3600", upsert: false });
          if (!upErr) { const { data } = supabase.storage.from("bug-screenshots").getPublicUrl(path); url = data?.publicUrl || ""; }
        }
        const row = {
          title: form.title, summary: form.summary, priority: form.priority,
          status: "open", reported_by: user?.name || "furgel",
          module: view, screenshot_url: url,
        };
        const { data, error } = await supabase.from("bugs").insert([row]).select();
        if (!error && data && setBugs) { setBugs(p => [data[0], ...p]); logActivity("bug", row.title, "bugs"); }
        setOpen(false);
        setForm({ title: "", summary: "", priority: "MED" });
        summaryRef.current = "";
        setScreenshotFile(null); setScreenshotPreview(null);
        if (fileRef.current) fileRef.current.value = "";
      } catch (e) { alert("Bug-rapport mislukt: " + e.message); }
      setSubmitting(false);
    }

    function closeModal() {
      if (submitting) return;
      stopSpeech();
      setOpen(false);
    }

    const moduleLabel = MODULE_LABELS[view] || view;

    return (
      <>
        <button
          className="bug-fab"
          onClick={() => setOpen(true)}
          title="Rapporteer een bug op deze pagina"
          style={{ position: "fixed", right: 18, bottom: 18, zIndex: 998, width: 52, height: 52, borderRadius: "50%", border: "1px solid " + C.red + "55", background: C.surface, color: C.red, fontSize: 22, cursor: "pointer", boxShadow: "0 4px 14px rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" }}
          onMouseOver={e => { e.currentTarget.style.background = C.redDim; e.currentTarget.style.transform = "scale(1.08)"; }}
          onMouseOut={e => { e.currentTarget.style.background = C.surface; e.currentTarget.style.transform = "scale(1)"; }}>
          🐛
        </button>
        {open && (
          <Portal><div className="ovl" data-bug-overlay="1" onClick={closeModal}>
            <div className="mdl" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14 }}>
                <span style={{ fontSize: 24 }}>🐛</span>
                <div>
                  <h3 style={{ fontFamily: "monospace", fontSize: 14 }}>RAPPORTEER BUG</h3>
                  <p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Module: <span style={{ color: C.green, fontWeight: 600 }}>{moduleLabel}</span> (auto-detected)</p>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                <Fld label="Korte titel *">
                  <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Bijv: 'Filter werkt niet op de Taken-bak'" autoFocus />
                </Fld>
                <Fld label="Beschrijving (wat ging er fout?)">
                  <div style={{ position: "relative" }}>
                    <textarea rows={4} value={form.summary + (listening && interim ? " " + interim : "")} onChange={e => { setForm(p => ({ ...p, summary: e.target.value })); summaryRef.current = e.target.value; }} placeholder={listening ? "Luistert... spreek in Nederlands" : "Wat probeerde je te doen? Wat zag je? Wat verwachtte je?"} style={{ paddingRight: 46 }} />
                    {canSpeech && (
                      <button type="button" onClick={listening ? stopSpeech : startSpeech} title={listening ? "Stop opname" : "Start spraakopname (Nederlands)"} style={{ position: "absolute", right: 8, top: 8, width: 32, height: 32, borderRadius: "50%", border: "1px solid " + (listening ? C.red : C.border), background: listening ? C.redDim : "transparent", color: listening ? C.red : C.muted, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", animation: listening ? "pulse 1.4s infinite" : "none" }}></button>
                    )}
                  </div>
                  {listening && <p style={{ fontSize: 10, color: C.red, marginTop: 5, fontStyle: "italic" }}>● Luistert — klik nogmaals om te stoppen</p>}
                  {!canSpeech && <p style={{ fontSize: 10, color: C.dim, marginTop: 5 }}>Spraakopname werkt op Chrome/Edge/Safari (laatste versie).</p>}
                </Fld>
                <Fld label="Prioriteit">
                  <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                    <option value="HIGH">HIGH — Blokkeert werk</option>
                    <option value="MED">MED — Hinderlijk maar werkbaar</option>
                    <option value="LOW">LOW — Cosmetisch / nice-to-have</option>
                  </select>
                </Fld>
                <Fld label="Screenshot (optioneel)">
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 7 }}>
                    <button type="button" onClick={captureDOM} disabled={capturing} className="btn bp" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }} title="Pakt de huidige pagina; deze bug-modal wordt uit de opname weggelaten zodat de fout zichtbaar is">
                      {capturing ? "⟳ Bezig..." : "Snapshot deze pagina"}
                    </button>
                    {canCapture && (
                      <button type="button" onClick={captureScreen} disabled={capturing} className="btn bg" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }} title="Kies een ander venster, tab of het hele scherm">
                        Ander venster
                      </button>
                    )}
                    <label className="btn bg" style={{ fontSize: 11, cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                      Uit bestand
                      <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />
                    </label>
                  </div>
                  <p style={{ fontSize: 10, color: C.dim, marginTop: 3 }}>"Snapshot deze pagina" werkt zonder permission-prompt en laat deze bug-modal weg uit de opname, zodat je de fout eronder vastlegt. "Ander venster" pakt een andere tab/app of je hele scherm (alleen desktop).</p>
                  {screenshotPreview && (
                    <div style={{ marginTop: 8, position: "relative" }}>
                      <img src={screenshotPreview} alt="Screenshot preview" style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 5, border: "1px solid " + C.border, display: "block" }} />
                      <button className="btn bg" style={{ position: "absolute", top: 5, right: 5, fontSize: 10, padding: "3px 7px" }} onClick={() => { setScreenshotFile(null); setScreenshotPreview(null); if (fileRef.current) fileRef.current.value = ""; }}>×</button>
                    </div>
                  )}
                </Fld>
                <div style={{ display: "flex", gap: 7, justifyContent: "space-between", alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                  <button className="btn bg" onClick={() => { stopSpeech(); setOpen(false); if (setView) setView("bugs"); }} style={{ fontSize: 11 }} title="Naar Bug Tracker">Alle bugs</button>
                  <div style={{ display: "flex", gap: 7 }}>
                    <button className="btn bg" onClick={closeModal} disabled={submitting}>Annuleer</button>
                    <button className="btn bp" onClick={submit} disabled={!form.title.trim() || submitting}>{submitting ? "Versturen..." : "🐛 Rapporteer"}</button>
                  </div>
                </div>
              </div>
            </div>
          </div></Portal>
        )}
      </>
    );
  }

  function BugTracker({ bugs, setBugs, user }) {
    const [sel, setSel] = useState(null);
    const [newMod, setNewMod] = useState(false);
    const [copied, setCopied] = useState(false);
    const [filter, setFilter] = useState("all");
    const [moduleFilter, setModuleFilter] = useState("all");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [uploading, setUploading] = useState(false);
    const [screenshotFile, setScreenshotFile] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const fileRef = useRef(null);
    const [form, setForm] = useState({ title: "", summary: "", screenshot_url: "", likely_cause: "", impact: "", affected_files: "", requested_fix: "", codebase_context: "", priority: "MED" });

    function resetForm() { setForm({ title: "", summary: "", screenshot_url: "", likely_cause: "", impact: "", affected_files: "", requested_fix: "", codebase_context: "", priority: "MED" }); setScreenshotFile(null); setScreenshotPreview(null); }

    function handleFileSelect(e) {
      const f = e.target.files?.[0];
      if (!f) return;
      setScreenshotFile(f);
      const reader = new FileReader();
      reader.onload = (ev) => setScreenshotPreview(ev.target.result);
      reader.readAsDataURL(f);
    }

    function removeScreenshot() { setScreenshotFile(null); setScreenshotPreview(null); setForm(p => ({ ...p, screenshot_url: "" })); if (fileRef.current) fileRef.current.value = ""; }

    async function uploadScreenshot() {
      if (!screenshotFile) return form.screenshot_url || "";
      const ext = screenshotFile.name.split(".").pop() || "png";
      const path = `bugs/${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error } = await supabase.storage.from("bug-screenshots").upload(path, screenshotFile, { cacheControl: "3600", upsert: false });
      if (error) { console.error("Upload error:", error); alert("Screenshot upload mislukt: " + error.message); return ""; }
      const { data: urlData } = supabase.storage.from("bug-screenshots").getPublicUrl(path);
      return urlData?.publicUrl || "";
    }

    async function submitBug() {
      if (!form.title.trim()) return;
      setUploading(true);
      try {
        let url = form.screenshot_url;
        if (screenshotFile) { url = await uploadScreenshot(); }
        const row = { ...form, screenshot_url: url, status: "open", reported_by: user?.name || "furgel" };
        const { data, error } = await supabase.from("bugs").insert([row]).select();
        if (!error && data) { setBugs(p => [data[0], ...p]); logActivity("bug", row.title, "bugs"); }
        setNewMod(false); resetForm();
      } catch (e) { alert("Fout: " + e.message); }
      setUploading(false);
    }

    async function updateStatus(id, status) {
      const extra = status === "resolved" ? { resolved_at: new Date().toISOString() } : { resolved_at: null };
      const { data, error } = await supabase.from("bugs").update({ status, ...extra, updated_at: new Date().toISOString() }).eq("id", id).select();
      if (!error && data) { setBugs(p => p.map(b => b.id === id ? data[0] : b)); }
    }

    async function deleteBug(id) {
      if (!window.confirm("Weet je zeker dat je deze bug wilt verwijderen?")) return;
      const { error } = await supabase.from("bugs").delete().eq("id", id);
      if (!error) { setBugs(p => p.filter(b => b.id !== id)); setSel(null); }
    }

    function generatePrompt(b) {
      const screenshotSection = b.screenshot_url
        ? `Bekijk de screenshot op deze URL en analyseer visueel wat er fout is:\n${b.screenshot_url}`
        : "Geen screenshot bijgevoegd.";
      return `### BUG SAMENVATTING\n${b.title}${b.summary ? " — " + b.summary : ""}\n\n### SCREENSHOT ANALYSE\n${screenshotSection}\n\n### WAARSCHIJNLIJKE OORZAAK\n${b.likely_cause || "Niet opgegeven — analyseer de code."}\n\n### IMPACT\n${b.impact || "Niet opgegeven."}\n\n### BESTANDEN / MODULES DIE WAARSCHIJNLIJK AANGEPAST MOETEN WORDEN\n${b.affected_files || "Niet opgegeven — zoek in de codebase."}\n\n### GEVRAAGDE FIX\n${b.requested_fix || "Beschrijf stap-voor-stap hoe je dit oplost en implementeer de oplossing direct in code."}\n\n### CODEBASE CONTEXT\n${b.codebase_context || "Niet opgegeven."}\n\n### LEVERING\n- Geef de volledige aangepaste code.\n- Leg uit waarom dit de juiste fix is.\n- Controleer of de bug volledig is opgelost.`;
    }

    function copyPrompt(b) {
      navigator.clipboard.writeText(generatePrompt(b));
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }

    const filtered = bugs.filter(b => {
      if (filter !== "all" && b.status !== filter) return false;
      if (moduleFilter !== "all" && (b.module || "legacy") !== moduleFilter) return false;
      if (sourceFilter !== "all" && (b.source_app || "os") !== sourceFilter) return false;
      return true;
    });
    // Bron-opties voor het filter (welke source_app-waarden komen voor in de data)
    const sourceOrder = Array.from(new Set(bugs.map(b => b.source_app).filter(Boolean)));
    const openCount = bugs.filter(b => b.status === "open").length;
    const progressCount = bugs.filter(b => b.status === "in_progress").length;
    const resolvedCount = bugs.filter(b => b.status === "resolved").length;

    // Per-module stats
    const moduleStats = {};
    for (const b of bugs) {
      const m = b.module || "legacy";
      if (!moduleStats[m]) moduleStats[m] = { total: 0, open: 0, in_progress: 0, resolved: 0, wontfix: 0 };
      moduleStats[m].total++;
      moduleStats[m][b.status] = (moduleStats[m][b.status] || 0) + 1;
    }
    const moduleOrder = Object.keys(moduleStats).sort((a, b) => moduleStats[b].open + moduleStats[b].in_progress - moduleStats[a].open - moduleStats[a].in_progress || moduleStats[b].total - moduleStats[a].total);

    // Timeline laatste 30 dagen
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const created = bugs.filter(b => (b.created_at || "").slice(0, 10) === key).length;
      const resolved = bugs.filter(b => (b.resolved_at || "").slice(0, 10) === key).length;
      days.push({ key, d, created, resolved });
    }
    const maxBar = Math.max(1, ...days.map(x => Math.max(x.created, x.resolved)));

    // ── Detail view ──
    if (sel) {
      const b = bugs.find(x => x.id === sel);
      if (!b) { setSel(null); return null; }
      const st = BUG_STATUS[b.status] || BUG_STATUS.open;
      return (
        <div className="fu">
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 20, flexWrap: "wrap" }}>
            <button className="btn bg" onClick={() => { setSel(null); setCopied(false); }}>← Terug</button>
            <h2 style={{ ...KOP_STIJL, fontSize: 16, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.title}</h2>
            <Bdg label={st.label} bg={st.bg} fg={st.fg} />
            <Bdg label={b.priority} bg={(PCOL[b.priority] || C.muted) + "20"} fg={PCOL[b.priority] || C.muted} />
          </div>

          {/* AI Prompt kopieer sectie */}
          <div className="card" style={{ marginBottom: 13, borderColor: copied ? "rgba(52,189,130,.4)" : "rgba(91,142,255,.3)", background: copied ? C.greenDim : C.blueDim }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, color: copied ? C.green : C.blue }}>{copied ? "✓ AI Prompt gekopieerd!" : "AI Bug Fix Prompt"}</p>
                <p style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Kopieer en plak dit bij de AI om deze bug automatisch te laten fixen.</p>
              </div>
              <button className="btn bp" onClick={() => copyPrompt(b)} style={{ flexShrink: 0, fontSize: 12 }}>
                {copied ? "✓ Gekopieerd" : "Kopieer AI Prompt"}
              </button>
            </div>
          </div>

          {/* Meta info */}
          <div className="card" style={{ marginBottom: 13 }}>
            <p className="lbl" style={{ marginBottom: 11 }}>Bug Info</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 11 }}>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Module</p><p style={{ fontSize: 13, color: C.cyan, fontFamily: "monospace" }}>{MODULE_LABELS[b.module || "legacy"] || b.module || "-"}</p></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Bron</p><Bdg label={sourceAppLabel(b.source_app)} bg={C.purpleDim} fg={C.purple} /></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Gemeld door</p><p style={{ fontSize: 13, textTransform: "capitalize" }}>{b.reported_by || "-"}</p></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Gemeld op</p><p style={{ fontSize: 13 }}>{b.created_at ? new Date(b.created_at).toLocaleDateString("nl-NL") : "-"}</p></div>
              <div><p style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>Opgelost op</p><p style={{ fontSize: 13, color: b.resolved_at ? C.green : C.muted }}>{b.resolved_at ? new Date(b.resolved_at).toLocaleDateString("nl-NL") : "-"}</p></div>
            </div>
            {b.source_url && (
              <p style={{ fontSize: 12, marginTop: 11 }}>
                <a href={b.source_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue }}>Bekijk in bronproduct</a>
              </p>
            )}
          </div>

          {/* Screenshot */}
          {b.screenshot_url && (
            <div className="card" style={{ marginBottom: 8 }}>
              <p className="lbl" style={{ marginBottom: 7 }}>Screenshot</p>
              <a href={b.screenshot_url} target="_blank" rel="noopener noreferrer">
                <img src={b.screenshot_url} alt="Bug screenshot" style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 6, border: "1px solid " + C.border, cursor: "pointer" }} />
              </a>
            </div>
          )}

          {/* Alle velden */}
          {[["Samenvatting", b.summary], ["Waarschijnlijke Oorzaak", b.likely_cause], ["Impact", b.impact], ["Bestanden / Modules", b.affected_files], ["Gevraagde Fix", b.requested_fix], ["Codebase Context", b.codebase_context]].map(([label, val]) => (
            <div key={label} className="card" style={{ marginBottom: 8 }}>
              <p className="lbl" style={{ marginBottom: 7 }}>{label}</p>
              <p style={{ fontSize: 13, color: val ? C.text : C.dim, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{val || "Niet opgegeven"}</p>
            </div>
          ))}

          {/* Acties */}
          <div className="card" style={{ marginBottom: 13 }}>
            <p className="lbl" style={{ marginBottom: 11 }}>Status Wijzigen</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {b.status === "resolved" || b.status === "wontfix" ? (
                <>
                  <button className="btn bp" style={{ background: C.red, color: "#000", borderColor: C.red, fontWeight: 600 }} onClick={() => updateStatus(b.id, "open")}>
                    Bug Heropenen
                  </button>
                  <button className="btn bg" style={{ borderColor: C.yellowDim, color: C.yellow }} onClick={() => updateStatus(b.id, "in_progress")}>In Progress</button>
                </>
              ) : (
                <>
                  {b.status !== "open" && <button className="btn bg" style={{ borderColor: C.redDim, color: C.red }} onClick={() => updateStatus(b.id, "open")}>Open</button>}
                  {b.status !== "in_progress" && <button className="btn bg" style={{ borderColor: C.yellowDim, color: C.yellow }} onClick={() => updateStatus(b.id, "in_progress")}>In Progress</button>}
                  {b.status !== "resolved" && <button className="btn bp" onClick={() => updateStatus(b.id, "resolved")}>✓ Resolved</button>}
                  {b.status !== "wontfix" && <button className="btn bg" onClick={() => updateStatus(b.id, "wontfix")}>⚫ Won't Fix</button>}
                </>
              )}
              <button className="btn bd" style={{ marginLeft: "auto" }} onClick={() => deleteBug(b.id)}>Verwijderen</button>
            </div>
          </div>
        </div>
      );
    }

    // ── Lijst view ──
    return (
      <div className="fu">
        <Kop titel="Bug Tracker" rechts={<button className="btn bp" onClick={() => setNewMod(true)}>+ Bug melden</button>} />

        <Tellers items={[{ label: "Open", waarde: openCount, kleur: C.red }, { label: "In progress", waarde: progressCount, kleur: C.yellow }, { label: "Resolved", waarde: resolvedCount, kleur: C.green }, { label: "Totaal", waarde: bugs.length, kleur: C.blue }]} />

        {/* Dashboard: timeline + per-module stats */}
        {bugs.length > 0 && (
          <div style={{ marginBottom: 14 }}><Groep label="Overzicht" kleur={C.blue} standaardDicht><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11, padding: 12 }}>
            {/* Timeline */}
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 11 }}>
                <p className="lbl">Laatste 30 dagen</p>
                <p style={{ fontSize: 10, color: C.dim, fontFamily: "monospace" }}>
                  <span style={{ color: C.red }}>● gemeld</span> &nbsp;
                  <span style={{ color: C.green }}>● opgelost</span>
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 90, paddingBottom: 14, position: "relative" }}>
                {days.map((dd, i) => {
                  const isLast = i === days.length - 1;
                  return (
                    <div key={dd.key} title={`${dd.key}: ${dd.created} gemeld, ${dd.resolved} opgelost`} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 1, position: "relative" }}>
                      <div style={{ display: "flex", alignItems: "flex-end", height: 74, gap: 1 }}>
                        <div style={{ flex: 1, height: (dd.created / maxBar) * 100 + "%", minHeight: dd.created > 0 ? 2 : 0, background: C.red, borderRadius: "2px 2px 0 0", opacity: .85 }} />
                        <div style={{ flex: 1, height: (dd.resolved / maxBar) * 100 + "%", minHeight: dd.resolved > 0 ? 2 : 0, background: C.green, borderRadius: "2px 2px 0 0", opacity: .85 }} />
                      </div>
                      {(i === 0 || isLast || i === 15) && (
                        <span style={{ fontSize: 8, color: C.dim, fontFamily: "monospace", position: "absolute", bottom: -14, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>{dd.d.getDate()}/{dd.d.getMonth() + 1}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Per-module bars */}
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 11 }}>
                <p className="lbl">Per module</p>
                <p style={{ fontSize: 10, color: C.dim }}>Klik om te filteren</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, maxHeight: 175, overflowY: "auto" }}>
                {moduleOrder.map(m => {
                  const s = moduleStats[m];
                  const label = MODULE_LABELS[m] || m;
                  const active = moduleFilter === m;
                  return (
                    <div key={m} onClick={() => setModuleFilter(active ? "all" : m)} style={{ cursor: "pointer", padding: "5px 8px", borderRadius: 5, background: active ? C.greenDim : "transparent", border: "1px solid " + (active ? C.green + "55" : "transparent") }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                        <span style={{ fontSize: 11, fontWeight: active ? 700 : 500, color: active ? C.green : C.text }}>{label}</span>
                        <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{s.total} totaal</span>
                      </div>
                      <div style={{ display: "flex", height: 5, borderRadius: 2, overflow: "hidden", background: C.border }}>
                        {s.open > 0 && <div style={{ flex: s.open, background: C.red }} title={`${s.open} open`} />}
                        {s.in_progress > 0 && <div style={{ flex: s.in_progress, background: C.yellow }} title={`${s.in_progress} in progress`} />}
                        {s.resolved > 0 && <div style={{ flex: s.resolved, background: C.green }} title={`${s.resolved} resolved`} />}
                        {s.wontfix > 0 && <div style={{ flex: s.wontfix, background: C.muted }} title={`${s.wontfix} wontfix`} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div></Groep></div>
        )}

        {/* Filter tabs */}
        <div style={{ display: "flex", gap: 7, marginBottom: 13, flexWrap: "wrap", alignItems: "center" }}>
          {[["all", "Alles"], ["open", "Open"], ["in_progress", "In Progress"], ["resolved", "Resolved"], ["wontfix", "Won't Fix"]].map(([k, l]) => (
            <span key={k} className={"tab" + (filter === k ? " on" : "")} onClick={() => setFilter(k)}>{l} {k === "all" ? bugs.length : bugs.filter(b => b.status === k).length}</span>
          ))}
          <span style={{ width: 1, height: 18, background: C.border, margin: "0 4px" }} />
          <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={{ width: "auto", fontSize: 11, padding: "5px 8px" }}>
            <option value="all">Alle modules</option>
            {moduleOrder.map(m => <option key={m} value={m}>{MODULE_LABELS[m] || m} ({moduleStats[m].total})</option>)}
          </select>
          {sourceOrder.length > 0 && (
            <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={{ width: "auto", fontSize: 11, padding: "5px 8px" }}>
              <option value="all">Alle bronnen</option>
              <option value="os">Dif3r3nt OS</option>
              {sourceOrder.map(s => <option key={s} value={s}>{sourceAppLabel(s)}</option>)}
            </select>
          )}
          {(moduleFilter !== "all" || filter !== "all" || sourceFilter !== "all") && <button className="btn bg" style={{ fontSize: 10, padding: "3px 9px" }} onClick={() => { setFilter("all"); setModuleFilter("all"); setSourceFilter("all"); }}>× Wis filters</button>}
        </div>

        {/* Lijst: per status een groep, een regel per bug */}
        {filtered.length === 0 ? (
          <div className="card"><Leeg tekst="Geen bugs gevonden." /><div style={{ textAlign: "center", paddingBottom: 8 }}><button className="btn bp" onClick={() => setNewMod(true)}>+ Bug melden</button></div></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[["open", "Open", C.red], ["in_progress", "In progress", C.yellow], ["resolved", "Resolved", C.green, true], ["wontfix", "Won't fix", C.muted, true]].map(([k, label, kleur, dicht]) => {
              const items = filtered.filter(b => (["in_progress", "resolved", "wontfix"].includes(b.status) ? b.status : "open") === k);
              if (!items.length) return null;
              return (
                <Groep key={filter + k} label={label} kleur={kleur} aantal={items.length} standaardDicht={!!dicht && filter === "all"}>
                  {items.map(b => {
                    const m = b.module || "legacy";
                    return (
                      <Rij key={b.id} kleur={PCOL[b.priority] || C.muted} titel={b.title} onKlik={() => startTransition(() => setSel(b.id))}
                        gedimd={b.status === "resolved" || b.status === "wontfix"}
                        meta={<>
                          <span style={{ color: m === "legacy" ? C.dim : C.cyan }}>{MODULE_LABELS[m] || m}</span>
                          <span>{sourceAppLabel(b.source_app)}</span>
                          <span style={{ textTransform: "capitalize" }}>{b.reported_by || ""}</span>
                          <span>{b.created_at ? new Date(b.created_at).toLocaleDateString("nl-NL") : ""}</span>
                        </>} />
                    );
                  })}
                </Groep>
              );
            })}
          </div>
        )}

        {/* Nieuw Bug Modal */}
        {newMod && (
          <Portal><div className="ovl" onClick={() => { setNewMod(false); resetForm(); }}>
            <div className="mdl" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: "monospace", marginBottom: 6, fontSize: 15, color: C.red }}>🐛 BUG MELDEN</h3>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 17, lineHeight: 1.6 }}>Vul zoveel mogelijk in. Hoe meer detail, hoe sneller de AI de fix kan leveren.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Bug Titel *</p>
                  <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="bijv. Ritregistratie toont geen kaart op mobiel" />
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Samenvatting</p>
                  <textarea rows={3} value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} placeholder="Beschrijf wat er misgaat en wanneer het optreedt..." />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
                  <div>
                    <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Prioriteit</p>
                    <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                      <option value="HIGH">HIGH</option>
                      <option value="MED">MED</option>
                      <option value="LOW">LOW</option>
                    </select>
                  </div>
                  <div>
                    <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Screenshot URL (als je geen bestand uploadt)</p>
                    <input value={form.screenshot_url} onChange={e => setForm(p => ({ ...p, screenshot_url: e.target.value }))} placeholder="https://..." disabled={!!screenshotFile} />
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Screenshot Upload</p>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />
                  {!screenshotPreview ? (
                    <div onClick={() => fileRef.current?.click()} style={{ border: "2px dashed " + C.border, borderRadius: 8, padding: "20px", textAlign: "center", cursor: "pointer", transition: "all .15s", background: C.bg }} onMouseOver={e => e.currentTarget.style.borderColor = C.green} onMouseOut={e => e.currentTarget.style.borderColor = C.border} onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.green; }} onDragLeave={e => { e.currentTarget.style.borderColor = C.border; }} onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.border; const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith("image/")) { setScreenshotFile(f); const r = new FileReader(); r.onload = ev => setScreenshotPreview(ev.target.result); r.readAsDataURL(f); } }}>
                      <p style={{ fontSize: 24, marginBottom: 6, color: C.dim }}>◇</p>
                      <p style={{ fontSize: 12, color: C.muted }}>Klik om een screenshot te selecteren of sleep het hierin</p>
                      <p style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>PNG, JPG, WEBP — max 5MB</p>
                    </div>
                  ) : (
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <img src={screenshotPreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 180, borderRadius: 6, border: "1px solid " + C.border }} />
                      <button onClick={removeScreenshot} style={{ position: "absolute", top: -8, right: -8, width: 24, height: 24, borderRadius: "50%", border: "none", background: C.red, color: "#fff", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.4)" }}>✕</button>
                      <p style={{ fontSize: 11, color: C.green, marginTop: 4, fontFamily: "monospace" }}>✓ {screenshotFile?.name}</p>
                    </div>
                  )}
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Waarschijnlijke Oorzaak</p>
                  <textarea rows={2} value={form.likely_cause} onChange={e => setForm(p => ({ ...p, likely_cause: e.target.value }))} placeholder="Welk onderdeel van de code vermoedelijk fout is..." />
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Impact</p>
                  <textarea rows={2} value={form.impact} onChange={e => setForm(p => ({ ...p, impact: e.target.value }))} placeholder="Impact op functionaliteit, gebruikers, performance of veiligheid..." />
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Bestanden / Modules</p>
                  <textarea rows={2} value={form.affected_files} onChange={e => setForm(p => ({ ...p, affected_files: e.target.value }))} placeholder="bijv. src/App.jsx — BugTracker component, api/groq.js" />
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Gevraagde Fix</p>
                  <textarea rows={3} value={form.requested_fix} onChange={e => setForm(p => ({ ...p, requested_fix: e.target.value }))} placeholder="Beschrijf stap-voor-stap hoe de fix eruit zou moeten zien..." />
                </div>
                <div>
                  <p style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Codebase Context (optioneel)</p>
                  <textarea rows={3} value={form.codebase_context} onChange={e => setForm(p => ({ ...p, codebase_context: e.target.value }))} placeholder="Relevante codefragmenten of functies die verband houden met de bug..." />
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 5 }}>
                  <button className="btn bg" onClick={() => { setNewMod(false); resetForm(); }}>Annuleer</button>
                  <button className="btn bp" onClick={submitBug} style={{ opacity: form.title.trim() && !uploading ? 1 : .4 }} disabled={!form.title.trim() || uploading}>{uploading ? "Uploading..." : "🐛 Bug Registreren"}</button>
                </div>
              </div>
            </div>
          </div></Portal>
        )}
      </div>
    );
  }

  const NAV_GROUPS = [
    { label: null, items: [{ id: "vandaag", icon: "☀", l: "Vandaag" }, { id: "dashboard", icon: "⬡", l: "Dashboard" }] },
    { label: "Diensten", items: [
      { id: "validatie", icon: "◈", l: "Validatie" },
      { id: "pipeline", icon: "▣", l: "Bouwpipeline" },
      { id: "launch", icon: "△", l: "Launch Module" },
      { id: "live", icon: "◉", l: "Live Diensten" },
    ] },
    { label: "Sales & CRM", items: [
      { id: "crm", icon: "◎", l: "CRM" },
      { id: "klanten", icon: "◍", l: "Klanten" },
      { id: "facturatie", icon: "€", l: "Offertes & Facturen" },
      { id: "leadfinder", icon: "⊕", l: "Lead-finder" },
      { id: "sda", icon: "⊞", l: "Scraping (SDA)" },
    ] },
    { label: "Groei", items: [
      { id: "growth", icon: "↗", l: "Growth" },
      { id: "advisor", icon: "🧭", l: "Growth Advisor" },
      { id: "zichtbaarheid", icon: "⌕", l: "Zichtbaarheid" },
      { id: "gap", icon: "◬", l: "Gap Intelligence" },
      { id: "bezoekers", icon: "📈", l: "Bezoekers" },
    ] },
    { label: "Marketing", items: [
      { id: "marketing", icon: "⬟", l: "Marketing" },
      { id: "meta-ads", icon: "◎", l: "Meta Ads Mastery" },
      { id: "klantenservice", icon: "☎", l: "Klantenservice" },
      { id: "ai-video", icon: "🎬", l: "AI Video" },
      { id: "nieuwsbrief", icon: "✉", l: "Nieuwsbrief Store" },
      { id: "shopguard", icon: "⛨", l: "ShopGuard" },
    ] },
    { label: "Bedrijf", items: [
      { id: "administratie", icon: "◑", l: "Administratie" },
      { id: "documenten", icon: "◫", l: "Documenten" },
      { id: "agenda", icon: "◷", l: "Agenda" },
      { id: "taken", icon: "◻", l: "Taken" },
    ] },
    { label: "Team & AI", items: [
      { id: "control", icon: "◉", l: "Control" },
      { id: "goedkeuringen", icon: "✓", l: "Goedkeuringen" },
      { id: "operatie", icon: "◆", l: "Operatie" },
      { id: "meeting", icon: "⊙", l: "Meetings" },
      { id: "brainstorm", icon: "◇", l: "Brainstorm" },
      { id: "boardroom", icon: "⬢", l: "AI Boardroom" },
    ] },
    { label: "Systeem", items: [
      { id: "systeem", icon: "⚙", l: "Systeemstatus" },
      { id: "bugs", icon: "🐛", l: "Bug Tracker" },
      { id: "dev", icon: "⌗", l: "Dev Console" },
      { id: "wachtwoorden", icon: "🔑", l: "Wachtwoorden" },
    ] },
  ];
  const NAV = NAV_GROUPS.flatMap(g => g.items);
  // Schermen die een gast met alleen leesrechten niet kan gebruiken (de database of de edge function weigert hem):
  // die laten we voor hem helemaal weg in plaats van een lege pagina of foutmelding te tonen.
  const LEZER_WEG = new Set(["live", "zichtbaarheid", "systeem", "bugs", "dev", "wachtwoorden", "goedkeuringen", "operatie", "meta-ads"]);
  const magZien = (gebruiker, id) => !(gebruiker && gebruiker.leesModus && LEZER_WEG.has(id));


  // ─── AUTH ─────────────────────────────────────────────────────────────────────
  const ALLOWED = ["furgel@dif3r3nt.nl", ...LEZERS];

  // Zet de sessie om naar een gebruiker; gasten krijgen leesModus en de browser weigert dan schrijfverzoeken.
  function maakGebruiker(e) {
    const lezer = isLezer(e);
    zetLeesModus(lezer);
    if (lezer) return { email: e, name: "Tristan", role: "Lezer", leesModus: true };
    return { email: e, name: "Furgel", role: "CTO" };
  }

  // useAuth — echte Supabase Auth. De client beheert de JWT sessie automatisch.
  function useAuth() {
    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
      // Herstel bestaande sessie bij page refresh
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user && ALLOWED.includes(session.user.email)) {
          setUser(maakGebruiker(session.user.email));
        }
        setAuthReady(true);
      });

      // Luister naar auth state changes (login, logout, token refresh)
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session?.user && ALLOWED.includes(session.user.email)) {
          setUser(maakGebruiker(session.user.email));
        } else {
          zetLeesModus(false);
          setUser(null);
        }
      });

      return () => subscription.unsubscribe();
    }, []);

    async function login(email, password) {
      const e = email.toLowerCase().trim();
      if (!ALLOWED.includes(e)) return "Dit e-mailadres heeft geen toegang.";
      if (password.length < 6) return "Wachtwoord moet minimaal 6 tekens zijn.";

      const { error } = await supabase.auth.signInWithPassword({ email: e, password });
      if (error) {
        if (error.message.includes("Invalid login credentials")) return "Ongeldig wachtwoord.";
        if (error.message.includes("Email not confirmed")) return "Bevestig eerst de uitnodiging via je e-mail.";
        return error.message;
      }
      return null;
    }

    async function logout() {
      if (isLezer(user && user.email)) { logLezer("uitgelogd", ""); await leegLogboek(); nieuweSessieNaUitloggen(); }
      await supabase.auth.signOut();
      zetLeesModus(false);
      setUser(null);
    }

    return { user, login, logout, authReady };
  }

  function usePresence(currentUser) {
    const [online, setOnline] = useState({});
    useEffect(() => {
      if (!currentUser || currentUser.leesModus) return; // een lezer schrijft niets weg, ook geen aanwezigheid
      const key = currentUser.name.toLowerCase();
      const TIMEOUT_MS = 90000;
      async function heartbeat() {
        await supabase.from("user_presence").upsert(
          { user_key: key, user_name: currentUser.name, user_role: currentUser.role, last_seen: new Date().toISOString() },
          { onConflict: "user_key" }
        );
      }
      async function syncOnline() {
        const cutoff = new Date(Date.now() - TIMEOUT_MS).toISOString();
        const { data } = await supabase.from("user_presence").select("user_key").gte("last_seen", cutoff);
        const map = {};
        (data || []).forEach(r => { map[r.user_key] = true; });
        setOnline(map);
      }
      heartbeat();
      syncOnline();
      const beat = setInterval(() => { heartbeat(); syncOnline(); }, 30000);
      const ch = supabase.channel("presence_db_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "user_presence" }, syncOnline)
        .subscribe();
      return () => { clearInterval(beat); supabase.removeChannel(ch); };
    }, [currentUser?.name]);
    return online;
  }

  // ─── CHAT WINDOW ─────────────────────────────────────────────────────────────
  function ChatWindow({ currentUser, otherUserKey, onClose }) {
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [replyTo, setReplyTo] = useState(null); // bericht waarop wordt geantwoord (WhatsApp-stijl)
    const endRef = useRef(null);
    const inputRef = useRef(null);
    const otherUser = USERS[otherUserKey];
    const currentKey = currentUser.email.split('@')[0];
    const msgById = id => messages.find(x => x.id === id);
    const nameFor = key => key === currentKey ? "Jij" : (otherUser?.name || key);

    function startReply(m) { setReplyTo(m); setTimeout(() => inputRef.current?.focus(), 50); }

    // Scroll naar — en knipper kort — het aangehaalde origineel.
    function scrollToMsg(id) {
      const el = document.getElementById("chatmsg-" + id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const orig = el.style.background;
      el.style.transition = "background .3s";
      el.style.background = C.green + "22";
      setTimeout(() => { el.style.background = orig; }, 750);
    }

    useEffect(() => {
      const currentKey = currentUser.email.split('@')[0];
      async function load() {
        const { data } = await supabase.from("messages")
          .select("*")
          .or(`and(sender.eq.${currentKey},recipient.eq.${otherUserKey}),and(sender.eq.${otherUserKey},recipient.eq.${currentKey})`)
          .order("created_at", { ascending: true });
        if (data) setMessages(data);
        setTimeout(() => endRef.current?.scrollIntoView(), 100);
      }
      load();
      const ch = supabase.channel("chat_sync_" + otherUserKey)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, payload => {
          const m = payload.new;
          if ((m.sender === currentKey && m.recipient === otherUserKey) || (m.sender === otherUserKey && m.recipient === currentKey)) {
            setMessages(p => [...p, m]);
            setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
          }
        })
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, [currentUser, otherUserKey]);

    async function send(e) {
      e.preventDefault();
      if (!text.trim()) return;
      const msg = { sender: currentKey, recipient: otherUserKey, text: text.trim(), reply_to: replyTo?.id ?? null };
      setText("");
      setReplyTo(null);
      await supabase.from("messages").insert([msg]);
    }

    return (
      <div style={{ position: "fixed", bottom: 20, right: 20, width: 320, height: 420, background: C.surfaceHigh, border: "1px solid " + C.border, borderRadius: 12, display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,0.5)", zIndex: 9999, overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", background: C.surface, borderBottom: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: otherUser.color, boxShadow: "0 0 8px " + otherUser.color }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Chat met {otherUser.name}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {messages.length === 0 && <p style={{ textAlign: "center", color: C.muted, fontSize: 12, marginTop: 20 }}>Geen berichten. Stuur een bericht om de chat te starten!</p>}
          {messages.map(m => {
            const isMine = m.sender === currentKey;
            const quoted = m.reply_to ? msgById(m.reply_to) : null;
            return (
              <div key={m.id} id={"chatmsg-" + m.id} className="chat-msg" style={{ alignSelf: isMine ? "flex-end" : "flex-start", maxWidth: "85%", borderRadius: 8, padding: 2 }}>
                <div style={{ background: isMine ? C.greenDim : C.surface, border: "1px solid " + (isMine ? C.green + "40" : C.border), padding: "8px 12px", borderRadius: 14, borderBottomRightRadius: isMine ? 4 : 14, borderBottomLeftRadius: isMine ? 14 : 4, fontSize: 13, color: isMine ? C.green : C.text, lineHeight: 1.4 }}>
                  {m.reply_to && (
                    <div onClick={() => scrollToMsg(m.reply_to)} title="Ga naar bericht" style={{ borderLeft: "3px solid " + C.green, background: "rgba(0,0,0,0.22)", padding: "4px 8px", borderRadius: 6, marginBottom: 6, cursor: "pointer" }}>
                      <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: C.green }}>{quoted ? nameFor(quoted.sender) : "Bericht"}</span>
                      <span style={{ display: "block", fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{quoted ? quoted.text : "(verwijderd)"}</span>
                    </div>
                  )}
                  {m.text}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, justifyContent: isMine ? "flex-end" : "flex-start" }}>
                  <button onClick={() => startReply(m)} className="chat-reply-btn" style={{ background: "none", border: "none", color: C.dim, cursor: "pointer", fontSize: 10, padding: 0 }}>↩ Beantwoorden</button>
                  <span style={{ fontSize: 9, color: C.dim }}>
                    {new Date(m.created_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        {replyTo && (
          <div style={{ padding: "7px 12px", borderTop: "1px solid " + C.border, background: C.bg, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, borderLeft: "3px solid " + C.green, paddingLeft: 8, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 10, fontWeight: 600, color: C.green }}>Antwoord aan {nameFor(replyTo.sender)}</span>
              <span style={{ display: "block", fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{replyTo.text}</span>
            </div>
            <button onClick={() => setReplyTo(null)} aria-label="Annuleer antwoord" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        )}
        <form onSubmit={send} style={{ padding: 12, borderTop: "1px solid " + C.border, display: "flex", gap: 8, background: C.surface }}>
          <input ref={inputRef} value={text} onChange={e => setText(e.target.value)} placeholder={replyTo ? "Antwoord aan " + nameFor(replyTo.sender) + "..." : "Bericht aan " + otherUser.name + "..."} style={{ flex: 1, padding: "8px 14px", borderRadius: 20, border: "1px solid " + C.border, background: C.bg, fontSize: 13 }} />
          <button type="submit" disabled={!text.trim()} style={{ background: C.green, color: "#000", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", opacity: text.trim() ? 1 : 0.5 }}>
            ➤
          </button>
        </form>
      </div>
    );
  }

  function LoginScreen({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPw, setShowPw] = useState(false);

    async function handleLogin(e) {
      e.preventDefault();
      setLoading(true); setError("");
      const err = await onLogin(email, password);
      if (err) { setError(err); setLoading(false); }
    }

    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "DM Sans,sans-serif" }}>
        <style>{CSS}</style>
        <div style={{ width: 400, padding: 40, background: C.surface, border: "1px solid " + C.border, borderRadius: 16, boxShadow: "0 24px 64px rgba(0,0,0,.6)" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <img src={LOGO} alt="Dif3r3nt" style={{ width: 56, height: 56, objectFit: "contain", background: "#0B1528", borderRadius: 12, padding: 7, marginBottom: 16 }} />
            <p style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, letterSpacing: ".1em", color: C.green }}>DIF3R3NT</p>
          </div>

          <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>E-mailadres</p>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }}
                autoComplete="email" required
                style={{ fontSize: 14 }} />
            </div>
            <div>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Wachtwoord</p>
              <div style={{ position: "relative" }}>
                <input type={showPw ? "text" : "password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }}
                  autoComplete="current-password" required
                  style={{ fontSize: 14, paddingRight: 44 }} />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14 }}>
                  {showPw ? "" : ""}
                </button>
              </div>
            </div>

            {error && (
              <div style={{ background: C.redDim, border: "1px solid rgba(229,86,78,.3)", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: C.red }}>
                {error}
              </div>
            )}

            <button type="submit" className="btn bp" disabled={loading}
              style={{ padding: "12px", fontSize: 14, marginTop: 4, opacity: loading ? .7 : 1 }}>
              {loading ? "Inloggen..." : "Inloggen"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  function BezKpi({ label, value, color }) {
    return (
      <div className="card" style={{ minWidth: 170, flex: 1 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color }}>{Number(value || 0).toLocaleString("nl-NL")}</div>
        <div className="lbl" style={{ marginTop: 4 }}>{label}</div>
      </div>
    );
  }

  function Bezoekers() {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("loading"); // loading | ok | error
    const [days, setDays] = useState(30);

    useEffect(() => {
      let alive = true;
      setStatus("loading");
      supabase.rpc("website_daily_stats", { p_days: days }).then(({ data, error }) => {
        if (!alive) return;
        if (error) { console.error("[bezoekers]", error); setStatus("error"); return; }
        setRows(data || []);
        setStatus("ok");
      });
      return () => { alive = false; };
    }, [days]);

    const todayStr = new Date().toISOString().slice(0, 10);
    const today = rows.find(r => r.day === todayStr) || { pageviews: 0, visitors: 0 };
    const totalVisitors = rows.reduce((a, r) => a + Number(r.visitors || 0), 0);
    const totalViews = rows.reduce((a, r) => a + Number(r.pageviews || 0), 0);
    const maxV = Math.max(1, ...rows.map(r => Number(r.visitors || 0)));
    const chart = [...rows].sort((a, b) => String(a.day).localeCompare(String(b.day)));
    const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("nl-NL", { day: "2-digit", month: "short" });

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600 }}>Website-bezoekers</h2>
            <p style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Cookieloze, AVG-proof statistieken van dif3r3nt.nl</p>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[7, 30, 90].map(d => (
              <span key={d} className={"tab" + (days === d ? " on" : "")} onClick={() => setDays(d)}>{d}d</span>
            ))}
          </div>
        </div>

        {status === "loading" && <div className="card" style={{ color: C.muted, fontSize: 13 }}>Laden...</div>}
        {status === "error" && <div className="card" style={{ color: C.red, fontSize: 13 }}>Kon de statistieken niet laden. Controleer de Supabase-verbinding.</div>}
        {status === "ok" && (
          <div>
            <div style={{ display: "flex", gap: 11, flexWrap: "wrap", marginBottom: 14 }}>
              <BezKpi label="Bezoekers vandaag" value={today.visitors} color={C.green} />
              <BezKpi label={"Unieke bezoekers (" + days + "d)"} value={totalVisitors} color={C.blue} />
              <BezKpi label={"Pageviews (" + days + "d)"} value={totalViews} color={C.purple} />
              <BezKpi label="Pageviews vandaag" value={today.pageviews} color={C.cyan} />
            </div>

            <div className="card" style={{ marginBottom: 14 }}>
              <p className="lbl" style={{ marginBottom: 14 }}>Unieke bezoekers per dag</p>
              {chart.length === 0 ? (
                <p style={{ color: C.muted, fontSize: 13 }}>Nog geen bezoeken geregistreerd. Zodra er verkeer op dif3r3nt.nl komt, verschijnt het hier.</p>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 170, overflowX: "auto", paddingBottom: 2 }}>
                  {chart.map(r => {
                    const h = Math.round((Number(r.visitors || 0) / maxV) * 140);
                    return (
                      <div key={r.day} title={fmtDay(r.day) + ": " + r.visitors + " bezoekers, " + r.pageviews + " pageviews"} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, flex: "1 0 18px", minWidth: 18 }}>
                        <span style={{ fontSize: 10, color: C.muted }}>{r.visitors}</span>
                        <div style={{ width: "100%", maxWidth: 26, height: Math.max(h, 2), background: C.blue, borderRadius: "3px 3px 0 0", opacity: 0.85 }} />
                        <span style={{ fontSize: 9, color: C.dim, whiteSpace: "nowrap" }}>{fmtDay(r.day)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card">
              <p className="lbl" style={{ marginBottom: 6 }}>Per dag</p>
              <div style={{ display: "flex", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + C.border, fontSize: 11, color: C.muted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: ".06em" }}>
                <span style={{ flex: 1 }}>Dag</span>
                <span style={{ width: 100, textAlign: "right" }}>Bezoekers</span>
                <span style={{ width: 100, textAlign: "right" }}>Pageviews</span>
              </div>
              {rows.length === 0 && <div style={{ padding: "10px 0", color: C.muted, fontSize: 13 }}>Geen data in deze periode.</div>}
              {rows.map(r => (
                <div key={r.day} style={{ display: "flex", alignItems: "center", padding: "7px 0", borderBottom: "1px solid " + C.border, fontSize: 12 }}>
                  <span style={{ flex: 1 }}>{fmtDay(r.day)}</span>
                  <span style={{ width: 100, textAlign: "right", color: C.text, fontWeight: 600 }}>{r.visitors}</span>
                  <span style={{ width: 100, textAlign: "right", color: C.muted }}>{r.pageviews}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  function GrowthAdvisor({ user }) {
    const [stats, setStats] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [gen, setGen] = useState(false);
    const [genStep, setGenStep] = useState("");
    const meEmail = user?.email;

    async function load() {
      setLoading(true);
      const [sRes, rRes] = await Promise.all([
        supabase.rpc("growth_funnel_stats", { p_days: days }),
        supabase.from("growth_reviews").select("*").eq("engine", "advisor").order("created_at", { ascending: false }).limit(20),
      ]);
      setStats(sRes.data || null);
      setReviews(rRes.data || []);
      setLoading(false);
    }
    useEffect(() => { load(); }, [days]);

    function computeSignals(s) {
      if (!s) return [];
      const out = [];
      const L = s.leads || {}, W = s.website || {}, V = s.validation || {}, P = s.pipeline || {}, F = s.finance || {};
      const rev = Object.values(F.revenue_by_type_period || {}).reduce((a, b) => a + Number(b || 0), 0);
      const hours = Number(F.hours_in_period || 0);
      if ((L.total || 0) > 0 && (L.activities_total || 0) === 0)
        out.push({ sev: "high", text: (L.total) + " leads, 0 gelogde opvolgingen - niemand volgt leads op. Hier lekt omzet weg." });
      else if ((L.without_activity || 0) > 0)
        out.push({ sev: "med", text: (L.without_activity) + " leads zonder enige opvolging." });
      if ((W.visitors || 0) >= 20 && (L.new_in_period || 0) === 0)
        out.push({ sev: "high", text: (W.visitors) + " bezoekers, 0 nieuwe leads - de site converteert niet." });
      if (hours >= 20 && rev === 0)
        out.push({ sev: "high", text: hours + " uur gewerkt, geen geregistreerde omzet deze periode - check of werk omgezet wordt in betalende diensten." });
      if ((V.services_total || 0) > 0 && (P.total || 0) === 0)
        out.push({ sev: "med", text: (V.services_total) + " validaties maar niets in de bouwpipeline - validaties stranden." });
      if ((W.visitors || 0) === 0)
        out.push({ sev: "info", text: "Nog geen websitebezoek geregistreerd in deze periode." });
      return out;
    }
    const signals = computeSignals(stats);

    async function generate() {
      if (!stats) return;
      setGen(true); setGenStep("Funnel analyseren...");
      const sys = "Je bent de Growth Advisor van Dif3r3nt, een Nederlands software- en automatiseringsbedrijf met 1 founder (Furgel). Je analyseert de meegegeven funnel-data en signalen en levert een beknopt, eerlijk en concreet groeiadvies. Reken NIETS opnieuw uit - gebruik de cijfers als waarheid. Verzin geen data die er niet is. Geef maximaal 3 prioriteiten, gericht op de grootste bottleneck. Wees direct, geen marketingtaal.\n\nAntwoord UITSLUITEND als JSON:\n{\"title\":\"korte kop, max 8 woorden\",\"samenvatting\":\"2-3 zinnen stand van zaken op basis van de cijfers\",\"prioriteiten\":[{\"actie\":\"concrete actie deze week\",\"waarom\":\"onderbouwing met de cijfers\",\"impact\":\"hoog|midden|laag\",\"inspanning\":\"laag|midden|hoog\"}],\"risico\":\"1 zin belangrijkste risico of blinde vlek\"}";
      const userMsg = "Funnel-data (periode " + days + " dagen):\n" + JSON.stringify(stats) + "\n\nGedetecteerde signalen:\n" + (signals.length ? signals.map(x => "- [" + x.sev + "] " + x.text).join("\n") : "(geen)") + "\n\nGeef het groeiadvies als JSON.";
      let parsed = null;
      try { const r = await callGroq(sys, userMsg, { maxTokens: 1500, temperature: 0.3 }); parsed = r.parsed; } catch (e) { console.error("[advisor]", e); }
      if (!parsed) { setGen(false); setGenStep(""); return; }
      setGenStep("Opslaan...");
      const prioriteiten = Array.isArray(parsed.prioriteiten) ? parsed.prioriteiten : [];
      const row = {
        engine: "advisor", type: "funnel",
        title: parsed.title || "Growth Review",
        doel: parsed.samenvatting || "",
        waarom: prioriteiten.map((p, i) => (i + 1) + ". " + (p.actie || "") + "\n   Waarom: " + (p.waarom || "") + " (impact " + (p.impact || "?") + ", inspanning " + (p.inspanning || "?") + ")").join("\n\n"),
        preview: parsed,
        data_context: stats,
        risico: parsed.risico || "",
        impact: "",
        status: "pending",
      };
      const { data, error } = await supabase.from("growth_reviews").insert([row]).select();
      if (!error && data) {
        try { await supabase.from("growth_audit_log").insert([{ review_id: data[0].id, action: "created", actor: meEmail || "advisor", after_state: row, notes: "Funnel-review (" + days + "d)" }]); } catch (e) {/* audit optioneel */}
      }
      setGen(false); setGenStep("");
      load();
    }

    async function decide(rv, status) {
      const { error } = await supabase.from("growth_reviews").update({ status, decided_by: meEmail || "?", decided_at: new Date().toISOString() }).eq("id", rv.id);
      if (!error) {
        try { await supabase.from("growth_audit_log").insert([{ review_id: rv.id, action: status, actor: meEmail || "?", before_state: { status: rv.status }, after_state: { status } }]); } catch (e) {/* */}
        load();
      }
    }

    const W = stats?.website || {}, L = stats?.leads || {}, V = stats?.validation || {}, P = stats?.pipeline || {}, La = stats?.launches || {}, F = stats?.finance || {};
    const sevColor = (s) => s === "high" ? C.red : s === "med" ? C.yellow : C.blue;
    const stColor = (s) => s === "approved" ? C.green : s === "rejected" ? C.red : C.yellow;

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600 }}>Growth Advisor</h2>
            <p style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Datagedreven groeiadvies over je echte funnel. Cijfers uit Postgres, oordeel van de AI, jij beslist.</p>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {[7, 30, 90].map(d => <span key={d} className={"tab" + (days === d ? " on" : "")} onClick={() => setDays(d)}>{d}d</span>)}
          </div>
        </div>

        {loading && <div className="card" style={{ color: C.muted, fontSize: 13 }}>Funnel laden...</div>}
        {!loading && stats && (
          <div>
            <div style={{ display: "flex", gap: 11, flexWrap: "wrap", marginBottom: 14 }}>
              <BezKpi label={"Bezoekers (" + days + "d)"} value={W.visitors} color={C.cyan} />
              <BezKpi label="Leads totaal" value={L.total} color={C.blue} />
              <BezKpi label="Leads z. opvolging" value={L.without_activity} color={(L.without_activity || 0) > 0 ? C.red : C.green} />
              <BezKpi label="Validaties" value={V.services_total} color={C.purple} />
              <BezKpi label="In pipeline" value={P.total} color={C.purple} />
              <BezKpi label={"Uren (" + days + "d)"} value={F.hours_in_period} color={C.yellow} />
            </div>

            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
                <p className="lbl">Signalen ({signals.length})</p>
                <button className="btn bp" disabled={gen} onClick={generate}>{gen ? (genStep || "Bezig...") : "Genereer Growth Review"}</button>
              </div>
              {signals.length === 0 ? (
                <p style={{ color: C.muted, fontSize: 13 }}>Geen opvallende signalen in deze periode.</p>
              ) : signals.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "7px 0", borderBottom: i < signals.length - 1 ? "1px solid " + C.border : "none" }}>
                  <span style={{ flexShrink: 0, marginTop: 2, width: 7, height: 7, borderRadius: "50%", background: sevColor(s.sev) }} />
                  <span style={{ fontSize: 13 }}>{s.text}</span>
                </div>
              ))}
            </div>

            <p className="lbl" style={{ marginBottom: 8 }}>Growth Reviews</p>
            {reviews.length === 0 && <div className="card" style={{ color: C.muted, fontSize: 13 }}>Nog geen reviews. Klik op "Genereer Growth Review" voor een advies over je huidige funnel.</div>}
            {reviews.map(rv => {
              const pr = Array.isArray(rv.preview?.prioriteiten) ? rv.preview.prioriteiten : [];
              return (
                <div key={rv.id} className="card" style={{ marginBottom: 11 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div>
                      <h3 style={{ fontFamily: "monospace", fontSize: 14 }}>{rv.title}</h3>
                      <p style={{ fontSize: 12, color: C.dim, marginTop: 2 }}>{new Date(rv.created_at).toLocaleString("nl-NL")}</p>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: stColor(rv.status), background: stColor(rv.status) + "1f", padding: "3px 9px", borderRadius: 10 }}>{rv.status}</span>
                  </div>
                  {rv.doel && <p style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>{rv.doel}</p>}
                  {pr.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      {pr.map((p, i) => (
                        <div key={i} style={{ padding: "8px 0", borderTop: "1px solid " + C.border }}>
                          <p style={{ fontSize: 13, fontWeight: 600 }}>{(i + 1) + ". " + (p.actie || "")}</p>
                          <p style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{p.waarom}</p>
                          <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
                            <span style={{ fontSize: 10, color: C.green, border: "1px solid " + C.border, borderRadius: 5, padding: "1px 7px" }}>impact: {p.impact || "?"}</span>
                            <span style={{ fontSize: 10, color: C.yellow, border: "1px solid " + C.border, borderRadius: 5, padding: "1px 7px" }}>inspanning: {p.inspanning || "?"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {rv.risico && <p style={{ fontSize: 12, color: C.red, marginTop: 10 }}>Risico: {rv.risico}</p>}
                  {rv.status === "pending" && (
                    <div style={{ display: "flex", gap: 7, marginTop: 12 }}>
                      <button className="btn bg" onClick={() => decide(rv, "approved")} style={{ color: C.green }}>Goedkeuren</button>
                      <button className="btn bg" onClick={() => decide(rv, "rejected")} style={{ color: C.red }}>Archiveren</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  export default function App() {
    const { user, login, logout, authReady } = useAuth();
    const onlineUsers = usePresence(user);
    const params = new URLSearchParams(window.location.search);
    const deepEventId = params.get("event");
    // ?scherm=systeem: de klik op de systeemmelding opent direct dat scherm (alleen deze vaste lijst)
    const deepScherm = ["systeem"].includes(params.get("scherm")) ? params.get("scherm") : null;
    const [view, setView] = useState(deepEventId ? "agenda" : deepScherm || "vandaag");
    // Doorverwijzen tussen modules: een klant (lead) openen in Klanten, of een offerte starten voor die klant
    const [klantId, setKlantId] = useState(null);
    const [offerteLeadId, setOfferteLeadId] = useState(null);
    const openKlant = (id) => { setKlantId(String(id)); setView("klanten"); };
    const openFacturatie = (id) => { setOfferteLeadId(id ? String(id) : null); setView("facturatie"); };
    const [deepEvent] = useState(deepEventId);
    const [agentOpen, setAgentOpen] = useState(false);
    const [services, setServices] = useState(INIT_SERVICES);
    const [pipeline, setPipeline] = useState(INIT_PIPELINE);
    const [launches, setLaunches] = useState([]);
    // Live diensten = gelanceerde / klaar-voor-launch diensten uit de Launch Module.
    const liveServices = (launches || []).filter(l => l.status === "Gelanceerd" || l.status === "Klaar voor Launch");
    const [launchSel, setLaunchSel] = useState(null);
    const [sharedTasks, setSharedTasks] = useState(INIT_TASKS);
    const [openTasksForHealth, setOpenTasksForHealth] = useState([]); // echte open taken, voor Dashboard Service Health
    const [bugs, setBugs] = useState([]);
    const [chatWith, setChatWith] = useState(null);
    const [time, setTime] = useState(new Date());
    const [exportModal, setExportModal] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const [notifOpen, setNotifOpen] = useState(false);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [actieTellers, setActieTellers] = useState({}); // per module: wat wacht er op jou (zijbalk-bolletjes)
    const [globalToast, setGlobalToast] = useState(null);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    // Tabbalk onderin (telefoon): de vier modules die je zelf vastzet met het sterretje in "Meer".
    const [tabFavs, setTabFavs] = useState(() => {
      try {
        const v = JSON.parse(localStorage.getItem("os_tab_favs") || "null");
        if (Array.isArray(v) && v.length) return v.slice(0, 4);
      } catch (e) { /* standaardkeuze */ }
      return ["vandaag", "goedkeuringen", "taken", "crm"];
    });
    useEffect(() => { try { localStorage.setItem("os_tab_favs", JSON.stringify(tabFavs)); } catch (e) { /* geen opslag */ } }, [tabFavs]);
    // Een gast kan in een scherm terechtkomen dat hij niet mag zien (opgeslagen keuze, directe link): stuur hem naar Vandaag.
    useEffect(() => { if (user && !magZien(user, view)) setView("vandaag"); }, [user, view]);
    const togglePin = (id) => setTabFavs(p => p.includes(id) ? p.filter(x => x !== id) : [...p.slice(-3), id]);
    // Onthoud welke navigatie-categorieën zijn ingeklapt, ook na een herlaad.
    const [navCollapsed, setNavCollapsed] = useState(() => {
      try { return JSON.parse(localStorage.getItem("os_nav_collapsed") || "{}"); } catch { return {}; }
    });
    useEffect(() => { try { localStorage.setItem("os_nav_collapsed", JSON.stringify(navCollapsed)); } catch {} }, [navCollapsed]);
    const [navQuery, setNavQuery] = useState(""); // snel naar een module zoeken in de zijbalk
    const [meetingSessionId, setMeetingSessionId] = useState(null);
    const [brainstormActive, setBrainstormActive] = useState(false);
    const [soloMeetingActive, setSoloMeetingActive] = useState(false);
    const [incomingMeeting, setIncomingMeeting] = useState(null); // popup: de ander startte een vergadering
    const dismissedMeetingsRef = useRef(new Set());
    const [activityNote, setActivityNote] = useState(null); // popup: iemand voegde iets toe in een module

    useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);

    // Toont een popup zodra de ANDER een gedeelde vergadering start (agenda of meeting-
    // module) en jij die nog niet open hebt. Haakt op de meeting_sessions-tabel (data),
    // niet op de UI-knop, zodat het ongeacht de startplek werkt.
    useEffect(() => {
      const me = user?.email; if (!me) return;
      let alive = true;
      const consider = (s) => {
        if (!alive || !s || !s.id) return;
        if (s.ended_at || s.status === "ended") { setIncomingMeeting(cur => (cur && cur.id === s.id) ? null : cur); return; }
        if (s.host_email === me) return;                                  // ik ben de starter
        if (dismissedMeetingsRef.current.has(s.id)) return;               // weggeklikt
        // Bewust GEEN participants-filter meer: in dit 2-persoons team is elke vergadering
        // die de ANDER start relevant. De oude filter sloeg de ander over zodra de sessie
        // bv. alleen ["furgel"] als deelnemers had (uit de agenda-attendees), waardoor
        // Tristan nooit een popup kreeg. host_email !== me (hierboven) is voldoende.
        setIncomingMeeting(s);
      };
      const since = new Date(Date.now() - 4 * 3600 * 1000).toISOString();
      supabase.from("meeting_sessions").select("*").is("ended_at", null).gte("created_at", since).order("created_at", { ascending: false }).limit(5)
        .then(({ data }) => { (data || []).forEach(consider); });
      const ch = supabase.channel("live_meetings_watch")
        .on("postgres_changes", { event: "*", schema: "public", table: "meeting_sessions" }, (payload) => consider(payload.new || payload.old))
        .subscribe();
      return () => { alive = false; try { supabase.removeChannel(ch); } catch (e) { /* stil */ } };
      // eslint-disable-next-line
    }, [user?.email]);

    // Sandbox-heartbeat: houdt de 24/7-agents draaiend zolang de OS open is.
    // Tikt de server-side runner aan (die pakt hooguit 1 due taak per tik en
    // respecteert de kill-switch + dagcaps). De Vercel-cron doet daarnaast een
    // dagelijkse tik; samen zorgt dat voor continue werking.
    useEffect(() => {
      if (!user?.email) return;
      let alive = true;
      const tick = () => { if (!alive) return; apiFetch("/api/notify?job=agent-tick", { method: "POST" }).catch(() => {}); };
      const t0 = setTimeout(tick, 8000);
      const iv = setInterval(tick, 4 * 60000);
      return () => { alive = false; clearTimeout(t0); clearInterval(iv); };
    }, [user?.email]);

    // Activiteiten-popup: krijg een melding als de ander iets toevoegt in een module.
    useEffect(() => {
      const me = user?.email; if (!me) return;
      const ch = supabase.channel("activity_feed")
        .on("broadcast", { event: "activity" }, ({ payload }) => {
          if (!payload || payload.actor_email === me) return; // niet je eigen toevoeging
          setActivityNote(payload);
        })
        .subscribe((s) => { if (s === "SUBSCRIBED") _activityChannel = ch; });
      return () => { _activityChannel = null; try { supabase.removeChannel(ch); } catch (e) { /* stil */ } };
    }, [user?.email]);

    // Activiteiten-popup automatisch sluiten na ~9s (zoals de nieuw-bericht-melding).
    useEffect(() => { if (!activityNote) return; const t = setTimeout(() => setActivityNote(null), 9000); return () => clearTimeout(t); }, [activityNote]);

    // Terugkeer van een Mollie-betaling (?betaald=<docId>) → ga naar Administratie + herinner aan status-check
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.has("scherm")) {
        params.delete("scherm");
        const q = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? "?" + q : ""));
      }
      if (params.has("betaald")) {
        setView("administratie");
        setGlobalToast("Terug van de betaling — open de factuur en klik 'Controleer betaalstatus' om de status bij te werken.");
        setTimeout(() => setGlobalToast(null), 7000);
        params.delete("betaald");
        const q = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? "?" + q : ""));
      }
    }, []);

    // Sync Services
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("services").select("*").order("created_at", { ascending: false });
        if (!error && data) setServices(data);
      }
      load();
      const ch = supabase.channel("services_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "services" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    // Sync Pipeline
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("pipeline").select("*").order("created_at", { ascending: false });
        if (!error && data) setPipeline(data);
      }
      load();
      const ch = supabase.channel("pipeline_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "pipeline" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    // Sync Launch Modules
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("launch_modules").select("*").order("created_at", { ascending: false });
        if (!error && data) setLaunches(data);
      }
      load();
      const ch = supabase.channel("launch_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "launch_modules" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    // Sync Bugs
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("bugs").select("*").order("created_at", { ascending: false });
        if (!error && data) setBugs(data);
      }
      load();
      const ch = supabase.channel("bugs_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "bugs" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    // Sync open taken voor de Service Health-score op het Dashboard. Losstaand van
    // sharedTasks (die alleen lokaal gevuld wordt door modules die zelf een taak
    // aanmaken tijdens de sessie, dus geen betrouwbare telling van échte open taken).
    useEffect(() => {
      async function load() {
        const { data, error } = await supabase.from("tasks").select("id,title,dienst,status").neq("status", "done");
        if (!error && data) setOpenTasksForHealth(data);
      }
      load();
      const ch = supabase.channel("tasks_health_sync")
        .on("postgres_changes", { event: "*", schema: "public", table: "tasks" }, () => load())
        .subscribe();
      return () => supabase.removeChannel(ch);
    }, []);

    // Actie-tellers per module: verversen bij start, elke minuut, bij terugkeren naar het tabblad en na elke moduleswitch.
    useEffect(() => {
      if (!user) return;
      let leeft = true;
      const ververs = () => laadTellers(supabase).then(t => { if (leeft) setActieTellers(t); }).catch(() => {});
      ververs();
      const iv = setInterval(ververs, 60000);
      const zichtbaar = () => { if (document.visibilityState === "visible") ververs(); };
      document.addEventListener("visibilitychange", zichtbaar);
      return () => { leeft = false; clearInterval(iv); document.removeEventListener("visibilitychange", zichtbaar); };
    }, [user, view]);
    const actieTotaal = Object.values(actieTellers).reduce((a, x) => a + x.n, 0);
    useEffect(() => { document.title = (actieTotaal > 0 ? "(" + actieTotaal + ") " : "") + "Dif3r3nt OS"; }, [actieTotaal]);
    const tellerKleur = (x) => x.kleur === "rood" ? { bg: C.redDim, fg: C.red } : { bg: C.yellowDim, fg: C.yellow };

    // Global Unread Messages
    useEffect(() => {
      if (!user) return;
      const currentKey = user.email.split('@')[0];

      async function loadUnread() {
        const { data } = await supabase.from("messages").select("sender").eq("recipient", currentKey).eq("read", false);
        if (data) {
          const counts = {};
          data.forEach(m => { counts[m.sender] = (counts[m.sender] || 0) + 1; });
          setUnreadCounts(counts);
        }
      }
      loadUnread();

      const ch = supabase.channel("global_messages_sync")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages", filter: "recipient=eq." + currentKey }, payload => {
          const m = payload.new;
          setUnreadCounts(prev => {
            if (chatWith === m.sender) return prev; // Do not increment if currently chatting
            const next = { ...prev, [m.sender]: (prev[m.sender] || 0) + 1 };
            return next;
          });
          if (chatWith !== m.sender) {
            const senderName = m.sender === "furgel" ? "Furgel" : "Tristan";
            setGlobalToast(`Nieuw bericht van ${senderName}`);
            setTimeout(() => setGlobalToast(null), 5000);
          }
        })
        .subscribe();

      return () => supabase.removeChannel(ch);
    }, [user, chatWith]);

    // Mark as read when opening chat
    useEffect(() => {
      if (chatWith && user) {
        const currentKey = user.email.split('@')[0];
        supabase.from("messages").update({ read: true }).eq("recipient", currentKey).eq("sender", chatWith).then();
        setUnreadCounts(prev => ({ ...prev, [chatWith]: 0 }));
      }
    }, [chatWith, user]);

    // Notifications: upcoming events + overdue tasks
    useEffect(() => {
      async function loadNotifs() {
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];
        const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split("T")[0];
        const dayAfter = new Date(today); dayAfter.setDate(today.getDate() + 2);
        const dayAfterStr = dayAfter.toISOString().split("T")[0];
        const [{ data: events }, { data: overdue }, { data: todayDue }] = await Promise.all([
          supabase.from("events").select("id,title,date,time,attendees").gte("date", todayStr).lt("date", dayAfterStr).order("date").order("time"),
          supabase.from("tasks").select("id,title,due,assignee,priority").neq("status", "done").lt("due", todayStr).order("due"),
          supabase.from("tasks").select("id,title,due,assignee,priority").neq("status", "done").eq("due", todayStr),
        ]);
        const items = [];
        (events || []).forEach(e => {
          const isToday = e.date === todayStr;
          items.push({ id: "e" + e.id, type: "event", label: isToday ? "Vandaag" : "Morgen", title: e.title, sub: e.time || "", color: C.green });
        });
        (todayDue || []).forEach(t => {
          items.push({ id: "td" + t.id, type: "task", label: "Deadline vandaag", title: t.title, sub: t.assignee || "", color: C.yellow });
        });
        (overdue || []).forEach(t => {
          const days = Math.floor((today - new Date(t.due)) / 86400000);
          items.push({ id: "od" + t.id, type: "task", label: `${days}d te laat`, title: t.title, sub: t.assignee || "", color: C.red });
        });
        setNotifs(items);
      }
      loadNotifs();
    }, []);

    if (!authReady) return null; // wacht op sessie-herstel — voorkomt flash van loginscherm
    if (!user) return <LoginScreen onLogin={login} />;

    const pending = services.filter(s => s.state === "review_pending").length;
    const mrr = LIVE.reduce((s, x) => s + x.revenue, 0);
    const costs = LIVE.reduce((s, x) => s + x.costs, 0);
    const crmVal = DEALS.reduce((s, x) => s + x.value, 0);

    function buildKPIText() {
      const now = new Date().toLocaleDateString("nl-NL");
      const lines = [
        "DIF3R3NT OS — KPI RAPPORT",
        "Gegenereerd: " + now,
        "",
        "=== LIVE DIENSTEN ===",
        ...LIVE.map(s => "- " + s.title + ": MRR €" + s.revenue + " | Kosten €" + s.costs + " | Marge €" + (s.revenue - s.costs) + " | ROI " + s.roi + "% | PMF " + s.pmf),
        "",
        "TOTAAL MRR: €" + mrr,
        "TOTAAL KOSTEN: €" + costs,
        "NETTO MARGE: €" + (mrr - costs),
        "",
        "=== VALIDATIE PIPELINE ===",
        ...services.map(s => "- " + s.title + " | Status: " + s.state + " | Pain: " + s.pain + "/100 | Market: " + s.market + "/100 | Verdict: " + s.verdict),
        "",
        "=== BOUWPIPELINE ===",
        ...pipeline.map(p => "- " + p.title + " | Fase: " + p.col + " | Prioriteit: " + (p.priority || "-") + " | Dagen: " + p.days),
        "",
        "=== CRM PIPELINE ===",
        "Totale pipeline waarde: €" + crmVal,
        ...DEALS.map(d => "- " + d.name + " | Klant: " + d.client + " | Waarde: €" + d.value + " | Kans: " + d.prob + "%"),
      ];
      return lines.join("\n");
    }

    function exportTXT() {
      const txt = buildKPIText();
      const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Dif3R3Nt_KPI_Rapport_" + dateStr(new Date()) + ".txt";
      a.click(); URL.revokeObjectURL(url);
      setExportModal(false);
    }

    function exportCSV() {
      const now = dateStr(new Date());
      const rows = [
        ["Categorie", "Naam", "MRR", "Kosten", "Marge", "ROI%", "PMF", "Status", "Pain", "Market", "Verdict", "Waarde", "Kans%"],
        ...LIVE.map(s => ["Live", s.title, s.revenue, s.costs, s.revenue - s.costs, s.roi, s.pmf, "live", "", "", "", "", ""]),
        ...services.map(s => ["Validatie", s.title, "", "", "", "", "", s.state, s.pain, s.market, s.verdict, "", ""]),
        ...pipeline.map(p => ["Pipeline", p.title, "", "", "", "", "", p.col, "", "", "", "", ""]),
        ...DEALS.map(d => ["CRM", d.name, "", "", "", "", "", d.status, "", "", "", d.value, d.prob]),
      ];
      const csv = rows.map(r => r.map(v => {
        const s = String(v == null ? "" : v);
        const q = String.fromCharCode(34); return s.includes(",") ? q + s + q : s;
      }).join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Dif3R3Nt_KPIs_" + now + ".csv";
      a.click(); URL.revokeObjectURL(url);
      setExportModal(false);
    }

    function exportDOCX() {
      const now = new Date().toLocaleDateString("nl-NL");
      const liveLines = LIVE.map(s => s.title + ": MRR " + String.fromCharCode(8364) + s.revenue + " | Kosten " + s.costs + " | ROI " + s.roi + "% | PMF " + s.pmf + "\\par").join("\n");
      const valLines = services.map(s => s.title + " - " + s.state + " | Pain " + s.pain + " | Market " + s.market + " | " + s.verdict + "\\par").join("\n");
      const pipLines = pipeline.map(p => p.title + " - " + p.col + " (" + (p.priority || "-") + ")\\par").join("\n");
      const crmLines = DEALS.map(d => d.name + " | " + d.client + " | " + String.fromCharCode(8364) + d.value + " | " + d.prob + "%\\par").join("\n");
      const rtf = "{\\rtf1\\ansi\\deff0\n"
        + "{\\fonttbl{\\f0 Arial;}}\n"
        + "{\\b\\fs32 Dif3r3nt OS - KPI Rapport}\\par\n"
        + "{\\i Gegenereerd: " + now + "}\\par\\par\n"
        + "{\\b\\fs26 Live Diensten}\\par\n"
        + liveLines + "\n"
        + "{\\b Totaal MRR:} " + String.fromCharCode(8364) + mrr + "\\par\n"
        + "{\\b Netto Marge:} " + String.fromCharCode(8364) + (mrr - costs) + "\\par\\par\n"
        + "{\\b\\fs26 Validatie Pipeline}\\par\n"
        + valLines + "\n\\par"
        + "{\\b\\fs26 Bouwpipeline}\\par\n"
        + pipLines + "\n\\par"
        + "{\\b\\fs26 CRM Pipeline - " + String.fromCharCode(8364) + crmVal + "}\\par\n"
        + crmLines + "\n}";
      const blob = new Blob([rtf], { type: "application/rtf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Dif3R3Nt_KPI_" + dateStr(new Date()) + ".rtf";
      a.click(); URL.revokeObjectURL(url);
      setExportModal(false);
    }

    function exportPDF() {
      const now = new Date().toLocaleDateString("nl-NL");
      const liveRows = LIVE.map(s => "<tr><td><b>" + s.title + "</b></td><td style='color:#00aa55'>\u20AC" + s.revenue + "</td><td style='color:#cc2244'>\u20AC" + s.costs + "</td><td style='color:#00aa55'>\u20AC" + (s.revenue - s.costs) + "</td><td>" + s.roi + "%</td><td>" + s.pmf + "</td></tr>").join("");
      const valRows = services.map(s => "<tr><td><b>" + s.title + "</b></td><td>" + s.state.replace(/_/g, " ") + "</td><td>" + s.pain + "/100</td><td>" + s.market + "/100</td><td><b>" + s.verdict + "</b></td></tr>").join("");
      const pipRows = pipeline.map(p => "<tr><td><b>" + p.title + "</b></td><td>" + p.col.replace(/_/g, " ") + "</td><td>" + (p.priority || "-") + "</td><td>" + p.days + "d</td></tr>").join("");
      const crmRows = activeDeals.map(d => "<tr><td><b>" + d.name + "</b></td><td>" + d.client + "</td><td style='color:#00aa55'>\u20AC" + d.value.toLocaleString() + "</td><td>" + d.prob + "%</td><td>" + d.status + "</td></tr>").join("");
      const html = "<!DOCTYPE html><html><head><meta charset='utf-8'><title>Dif3R3Nt KPI Rapport</title>"
        + "<style>body{font-family:Arial,sans-serif;margin:40px;color:#111;font-size:13px}"
        + "h1{font-size:22px;color:#00aa55;border-bottom:2px solid #00aa55;padding-bottom:8px}"
        + "h2{font-size:16px;color:#333;margin-top:24px;margin-bottom:8px}"
        + "table{width:100%;border-collapse:collapse;margin-bottom:16px}"
        + "th{background:#f0f0f0;padding:7px 10px;text-align:left;font-size:11px;text-transform:uppercase}"
        + "td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}"
        + ".summary{display:flex;gap:20px;margin-bottom:20px}"
        + ".kpi{background:#f8f8f8;border:1px solid #ddd;border-radius:6px;padding:12px 16px}"
        + ".kv{font-size:20px;font-weight:700;color:#00aa55}.kl{font-size:10px;color:#888;text-transform:uppercase}"
        + "@media print{body{margin:20px}}</style></head><body>"
        + "<h1>Dif3r3nt OS \u2014 KPI Rapport</h1>"
        + "<p style='color:#888'>Gegenereerd: " + now + "</p>"
        + "<div class='summary'>"
        + "<div class='kpi'><div class='kv'>\u20AC" + mrr.toLocaleString() + "</div><div class='kl'>Totale MRR</div></div>"
        + "<div class='kpi'><div class='kv'>\u20AC" + (mrr - costs).toLocaleString() + "</div><div class='kl'>Netto Marge</div></div>"
        + "<div class='kpi'><div class='kv'>" + services.length + "</div><div class='kl'>Validaties</div></div>"
        + "<div class='kpi'><div class='kv'>\u20AC" + crmVal.toLocaleString() + "</div><div class='kl'>CRM Pipeline</div></div>"
        + "</div>"
        + "<h2>Live Diensten</h2><table><tr><th>Dienst</th><th>MRR</th><th>Kosten</th><th>Marge</th><th>ROI</th><th>PMF</th></tr>" + liveRows + "</table>"
        + "<h2>Validatie Pipeline</h2><table><tr><th>Dienst</th><th>Status</th><th>Pain</th><th>Market</th><th>Verdict</th></tr>" + valRows + "</table>"
        + "<h2>Bouwpipeline</h2><table><tr><th>Dienst</th><th>Fase</th><th>Prioriteit</th><th>Dagen</th></tr>" + pipRows + "</table>"
        + "<h2>CRM Pipeline \u2014 \u20AC" + crmVal.toLocaleString() + "</h2><table><tr><th>Deal</th><th>Klant</th><th>Waarde</th><th>Kans</th><th>Status</th></tr>" + crmRows + "</table>"
        + "</body></html>";
      const w = window.open("", "_blank", "width=900,height=700");
      if (!w) { alert("Pop-up geblokkeerd. Sta pop-ups toe voor deze site."); return; }
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 600);
      setExportModal(false);
    }


    return (
      <>
        <style>{CSS}</style>
        <div style={{ display: "flex", minHeight: "100vh", width: "100%", overflow: "hidden" }}>
          <div className={"sb-backdrop" + (mobileNavOpen ? " open" : "")} onClick={() => setMobileNavOpen(false)} />
          <div className={"sb" + (mobileNavOpen ? " open" : "")} style={{ width: 210, background: C.surface, borderRight: "1px solid " + C.border, display: "flex", flexDirection: "column", flexShrink: 0, position: "sticky", top: 0, height: "100vh" }}>
            <div className="sheet-handle" onClick={() => setMobileNavOpen(false)} />
            <div style={{ padding: "17px 13px", borderBottom: "1px solid " + C.border }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <img src={LOGO} alt="Dif3r3nt" style={{ width: 38, height: 38, objectFit: "contain", borderRadius: 8, background: "#0B1528", padding: 5 }} />
                <div>
                  <p style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 700, letterSpacing: ".1em" }}>DIF3R3NT</p>
                  <p style={{ fontFamily: "monospace", fontSize: 9, color: C.muted, letterSpacing: ".1em" }}>OS</p>
                </div>
              </div>
            </div>
            <div style={{ padding: "8px 8px 0" }}>
              <input
                value={navQuery}
                onChange={e => setNavQuery(e.target.value)}
                placeholder="Zoek een module..."
                style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "7px 9px", borderRadius: 6, border: "1px solid " + C.border, background: C.surfaceHigh, color: C.text }}
              />
            </div>
            <div style={{ padding: "8px 8px 10px", flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
              {(() => {
                const q = navQuery.trim().toLowerCase();
                const zoeken = q.length > 0;
                const groepen = NAV_GROUPS.map(g => ({
                  ...g,
                  items: g.items
                    .filter(n => n.id !== "dev" || user?.email === "furgel@dif3r3nt.nl")
                    .filter(n => magZien(user, n.id))
                    .filter(n => !zoeken || n.l.toLowerCase().includes(q)),
                })).filter(g => g.items.length);
                if (zoeken && groepen.length === 0) {
                  return <p style={{ fontSize: 12, color: C.dim, padding: "8px 6px" }}>Geen module gevonden voor "{navQuery}".</p>;
                }
                return groepen.map(g => {
                  const collapsed = !zoeken && g.label && navCollapsed[g.label];
                  return (
                    <div key={g.label || "_top"} style={{ marginBottom: 4 }}>
                      {g.label && (
                        <div onClick={() => setNavCollapsed(p => ({ ...p, [g.label]: !p[g.label] }))} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 6px 3px", cursor: "pointer", userSelect: "none" }}>
                          <span className="lbl" style={{ margin: 0 }}>{g.label}{collapsed && (() => { const tot = g.items.reduce((a, n) => a + (actieTellers[n.id] ? actieTellers[n.id].n : 0), 0); return tot > 0 ? <span style={{ marginLeft: 6, background: C.redDim, color: C.red, padding: "0 6px", borderRadius: 9, fontSize: 10 }}>{tot}</span> : null; })()}</span>
                          <span style={{ fontSize: 9, color: C.dim }}>{collapsed ? "▸" : "▾"}</span>
                        </div>
                      )}
                      {!collapsed && g.items.map(n => (
                        <div key={n.id} className={"nav" + (view === n.id ? " on" : "")} onClick={() => { setMobileNavOpen(false); setNavQuery(""); startTransition(() => setView(n.id)); }}>
                          <span style={{ fontSize: 13, opacity: .8 }}>{n.icon}</span>
                          <span>{n.l}</span>
                          <span className="nav-pin" role="button" aria-label={tabFavs.includes(n.id) ? "Losmaken uit de balk onderin" : "Vastzetten in de balk onderin"} onClick={e => { e.stopPropagation(); togglePin(n.id); }}>{tabFavs.includes(n.id) ? "★" : "☆"}</span>
                          {actieTellers[n.id] && n.id !== "bugs" && <span title={actieTellers[n.id].tip} style={{ marginLeft: "auto", background: tellerKleur(actieTellers[n.id]).bg, color: tellerKleur(actieTellers[n.id]).fg, padding: "1px 6px", borderRadius: 9, fontSize: 10, fontWeight: 700 }}>{actieTellers[n.id].n}</span>}
                          {n.id === "validatie" && pending > 0 && <span style={{ marginLeft: "auto", background: C.yellowDim, color: C.yellow, padding: "1px 6px", borderRadius: 9, fontSize: 10 }}>{pending}</span>}
                          {n.id === "bugs" && bugs.filter(b => b.status === "open").length > 0 && <span style={{ marginLeft: "auto", background: C.redDim, color: C.red, padding: "1px 6px", borderRadius: 9, fontSize: 10 }}>{bugs.filter(b => b.status === "open").length}</span>}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
            <div style={{ padding: "10px 8px", borderTop: "1px solid " + C.border }}>
              {/* Op een telefoon is de naam in de bovenbalk weggehaald; hier staat altijd met welk account je bent ingelogd. */}
              <div style={{ padding: "4px 10px 8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 10, color: C.dim, margin: 0 }}>Ingelogd als</p>
                  <p style={{ fontSize: 12, color: C.text, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user && user.email}</p>
                </div>
                <button className="btn bg" style={{ fontSize: 11, flexShrink: 0 }} onClick={() => logout()}>Uitloggen</button>
              </div>
              <div style={{ padding: "6px 10px", fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 7 }}>
                <Dot /><span style={{ fontFamily: "monospace" }}>SYS ONLINE</span>
              </div>
              <div style={{ padding: "2px 10px", fontSize: 10, color: C.dim, fontFamily: "monospace" }}>{time.toLocaleTimeString("nl-NL")}</div>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", marginRight: agentOpen ? 305 : 0, transition: "margin .25s ease" }}>
            <div className="topbar" style={{ height: 50, background: C.surface, borderBottom: "1px solid " + C.border, display: "flex", alignItems: "center", padding: "0 12px", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50, gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                <button className="hamburger" onClick={() => setMobileNavOpen(p => !p)} aria-label="Open menu">☰</button>
                <span style={{ color: C.muted, fontSize: 12 }} className="topbar-hide">OS</span>
                <span style={{ color: C.dim }} className="topbar-hide">/ </span>
                <span style={{ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{NAV.find(n => n.id === view)?.l}</span>
              </div>
              <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {Object.entries(USERS).filter(([key]) => key !== "tristan").map(([key, u]) => {
                    const isMe = user?.name === u.name;
                    const isOn = isMe || !!onlineUsers[key];
                    return (
                      <div key={key} className="user-badge" onClick={() => !isMe && setChatWith(key)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, background: C.surfaceHigh, border: "1px solid " + C.border, opacity: isOn ? 1 : 0.45, transition: "all .3s", cursor: isMe ? "default" : "pointer" }} onMouseOver={e => !isMe && (e.currentTarget.style.borderColor = u.color)} onMouseOut={e => !isMe && (e.currentTarget.style.borderColor = C.border)} title={isMe ? "" : "Klik om te chatten met " + u.name}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", display: "inline-block", background: isOn ? u.color : "#3a3a56", transition: "background .4s", boxShadow: isOn ? "0 0 6px " + u.color + "55" : "none" }} />
                        <span className="user-badge-name" style={{ fontSize: 12, fontWeight: 600, color: isOn ? C.text : C.muted, transition: "color .3s" }}>{u.name}</span>
                        <span className="user-badge-role" style={{ fontSize: 10, color: C.dim }}>{u.role}</span>
                        {unreadCounts[key] > 0 && <span style={{ marginLeft: 4, background: C.red, color: "#fff", fontSize: 9, fontWeight: 700, borderRadius: 10, padding: "1px 5px", boxShadow: "0 0 8px rgba(229,86,78,0.4)" }}>{unreadCounts[key]}</span>}
                        {isMe && <button onClick={e => { e.stopPropagation(); logout(); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 11, marginLeft: 2, padding: "1px 4px", borderRadius: 3 }} title="Uitloggen">⊗</button>}
                      </div>
                    );
                  })}
                </div>
                <MeldingenKnop user={user} />
                <div style={{ position: "relative" }}>
                  <button className="btn bg" style={{ fontSize: 13, padding: "6px 10px", letterSpacing: ".05em" }} onClick={() => setMoreOpen(p => !p)} title="Meer opties">⋯</button>
                  {moreOpen && (
                    <div onClick={() => setMoreOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
                  )}
                  {moreOpen && (
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: C.surface, border: "1px solid " + C.border, borderRadius: 8, padding: "6px", zIndex: 100, minWidth: 160, boxShadow: "0 8px 24px rgba(0,0,0,.4)" }}>
                      <a href="/01_Gebruikershandleiding.html" target="_blank" onClick={() => setMoreOpen(false)} style={{ display: "block", padding: "8px 12px", borderRadius: 5, fontSize: 12, color: C.muted, textDecoration: "none", transition: "all .1s" }} onMouseOver={e => e.currentTarget.style.background = C.surfaceHigh} onMouseOut={e => e.currentTarget.style.background = "transparent"}>Gebruikershandleiding</a>
                      <a href="/02_Functionaliteiten_Mogelijkheden.html" target="_blank" onClick={() => setMoreOpen(false)} style={{ display: "block", padding: "8px 12px", borderRadius: 5, fontSize: 12, color: C.muted, textDecoration: "none", transition: "all .1s" }} onMouseOver={e => e.currentTarget.style.background = C.surfaceHigh} onMouseOut={e => e.currentTarget.style.background = "transparent"}>⚙ Functionaliteiten</a>
                      <a href="/03_Onboarding_Nieuwe_Medewerkers.html" target="_blank" onClick={() => setMoreOpen(false)} style={{ display: "block", padding: "8px 12px", borderRadius: 5, fontSize: 12, color: C.muted, textDecoration: "none", transition: "all .1s" }} onMouseOver={e => e.currentTarget.style.background = C.surfaceHigh} onMouseOut={e => e.currentTarget.style.background = "transparent"}>Onboarding Gids</a>
                    </div>
                  )}
                </div>
                <button className="btn bg topbar-hide" style={{ fontSize: 11 }} onClick={() => setExportModal(true)}>Export KPIs</button>
                <div style={{ position: "relative" }}>
                  <button onClick={() => setNotifOpen(p => !p)} style={{ position: "relative", padding: "6px 10px", borderRadius: 6, border: "1px solid " + (notifOpen ? C.yellow + "55" : C.border), background: notifOpen ? C.yellowDim : "transparent", color: notifOpen ? C.yellow : C.muted, cursor: "pointer", fontSize: 14, lineHeight: 1 }} title="Meldingen">
                    
                    {notifs.length > 0 && <span style={{ position: "absolute", top: -4, right: -4, background: notifs.some(n => n.color === C.red) ? C.red : C.yellow, color: "#000", borderRadius: "50%", width: 16, height: 16, fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace" }}>{notifs.length}</span>}
                  </button>
                  {notifOpen && <div onClick={() => setNotifOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />}
                  {notifOpen && (
                    <div className="notif-pop" style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: C.surface, border: "1px solid " + C.border, borderRadius: 8, zIndex: 200, minWidth: 280, maxWidth: 340, boxShadow: "0 8px 28px rgba(0,0,0,.5)", overflow: "hidden" }}>
                      <div style={{ padding: "10px 14px", borderBottom: "1px solid " + C.border, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, letterSpacing: ".07em", color: C.muted }}>MELDINGEN</span>
                        {notifs.length > 0 && <span style={{ fontSize: 10, color: C.muted }}>{notifs.length} actief</span>}
                      </div>
                      {notifs.length === 0 ? (
                        <div style={{ padding: "18px 14px", textAlign: "center", color: C.dim, fontSize: 12 }}>
                          <p style={{ fontSize: 18, marginBottom: 6 }}>✓</p>
                          <p>Alles op schema. Geen openstaande meldingen.</p>
                        </div>
                      ) : (
                        <div style={{ maxHeight: 320, overflowY: "auto" }}>
                          {notifs.map(n => (
                            <div key={n.id} style={{ padding: "10px 14px", borderBottom: "1px solid " + C.border, display: "flex", gap: 10, alignItems: "flex-start" }}>
                              <div style={{ width: 3, borderRadius: 2, alignSelf: "stretch", background: n.color, flexShrink: 0 }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 10, fontFamily: "monospace", color: n.color, letterSpacing: ".05em" }}>{n.label}</span>
                                <p style={{ fontSize: 13, fontWeight: 500, color: C.text, margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.title}</p>
                                {n.sub && <p style={{ fontSize: 11, color: C.muted, margin: "1px 0 0" }}>{n.sub}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ padding: "8px 14px", borderTop: "1px solid " + C.border, display: "flex", gap: 6 }}>
                        <button className="btn bg" style={{ fontSize: 10, flex: 1 }} onClick={() => { setNotifOpen(false); setView("agenda"); }}>Agenda</button>
                        <button className="btn bg" style={{ fontSize: 10, flex: 1 }} onClick={() => { setNotifOpen(false); setView("taken"); }}>Taken</button>
                      </div>
                    </div>
                  )}
                </div>
                <button onClick={() => setAgentOpen(p => !p)} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + (agentOpen ? C.green + "55" : C.border), background: agentOpen ? C.greenDim : "transparent", color: agentOpen ? C.green : C.muted, cursor: "pointer", fontSize: 12, fontFamily: "'DM Sans',sans-serif", fontWeight: 500, display: user.leesModus ? "none" : "flex", alignItems: "center", gap: 6 }}>
                  <Dot /> Agent ({AGENT_INIT.length})
                  {((actieTellers.goedkeuringen ? actieTellers.goedkeuringen.n : 0) + (actieTellers.growth ? actieTellers.growth.n : 0)) > 0 && <span title="Wacht op jouw akkoord" style={{ background: C.yellowDim, color: C.yellow, padding: "0 6px", borderRadius: 9, fontSize: 10, fontWeight: 700 }}>{(actieTellers.goedkeuringen ? actieTellers.goedkeuringen.n : 0) + (actieTellers.growth ? actieTellers.growth.n : 0)}</span>}
                </button>
              </div>
            </div>

            <button
              className="scroll-fab"
              aria-label="Naar boven"
              onClick={() => { const el = document.querySelector('.main-content'); if (el) el.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >↑</button>

            {/* Tabbalk onderin (telefoon/tablet): jouw vier vaste modules en rechts "Meer" (alle modules). */}
            <nav className="tabbar" aria-label="Hoofdmenu">
              {tabFavs.map(id => NAV.find(n => n.id === id)).filter(Boolean).filter(n => magZien(user, n.id)).map(n => {
                const teller = actieTellers[n.id] && n.id !== "bugs" ? actieTellers[n.id].n : 0;
                return (
                  <button key={n.id} className={"tab-item" + (view === n.id && !mobileNavOpen ? " on" : "")} aria-current={view === n.id ? "page" : undefined} onClick={() => { setMobileNavOpen(false); setNavQuery(""); startTransition(() => setView(n.id)); }}>
                    <span className="tab-ico">{n.icon}{teller > 0 && <span className="tab-badge">{teller > 99 ? "99+" : teller}</span>}</span>
                    <span className="tab-lbl">{n.l}</span>
                  </button>
                );
              })}
              <button className={"tab-item" + (mobileNavOpen || !tabFavs.includes(view) ? " on" : "")} aria-label="Alle modules" aria-expanded={mobileNavOpen} onClick={() => setMobileNavOpen(p => !p)} style={{ gridColumn: 5 }}>
                <span className="tab-ico">☰</span>
                <span className="tab-lbl">Meer</span>
              </button>
            </nav>

            <div className="main-content" style={{ flex: 1, padding: 20, overflowY: "auto" }}>
             <ErrorBoundary resetKey={view}>
              {view === "dashboard" && <Ecosystem services={services} pipeline={pipeline} launches={launches} bugs={bugs} tasks={openTasksForHealth} setView={setView} />}
              {view === "validatie" && <Validatie services={services} setServices={setServices} pipeline={pipeline} setPipeline={setPipeline} />}
              {view === "pipeline" && <Pipeline pipeline={pipeline} setPipeline={setPipeline} launches={launches} setLaunches={setLaunches} setView={setView} setLaunchSel={setLaunchSel} services={services} />}
              {view === "launch" && <LaunchModule launches={launches} setLaunches={setLaunches} pipeline={pipeline} setPipeline={setPipeline} setTasks={setSharedTasks} setView={setView} initialSel={launchSel} user={user} />}
              {view === "growth" && <GrowthModule user={user} launches={launches} services={services} />}
              {view === "advisor" && <GrowthAdvisor user={user} />}
              {view === "marketing" && <MarketingDashboard services={services} setView={setView} />}
              {view === "meta-ads" && <MetaAdsModule user={user} services={services} />}
              {view === "klantenservice" && <KlantenserviceModule user={user} services={services} />}
              {view === "ai-video" && <AIVideoModule />}
              {view === "sda" && <SDAModule launches={launches} user={user} />}
              {view === "live" && <LiveServices liveServices={liveServices} user={user} />}
              {view === "bezoekers" && <Bezoekers />}
              {view === "wachtwoorden" && <WachtwoordBeheer />}
              {view === "crm" && <CRM openKlant={openKlant} openFacturatie={openFacturatie} />}
              {view === "vandaag" && <Vandaag user={user} setView={setView} openKlant={openKlant} />}
              {view === "klanten" && <Klanten user={user} setView={setView} startId={klantId} wisStartId={() => setKlantId(null)} openFacturatie={openFacturatie} />}
              {view === "facturatie" && <Facturatie user={user} openKlant={openKlant} startLeadId={offerteLeadId} wisStartLead={() => setOfferteLeadId(null)} />}
              {view === "systeem" && <Systeemstatus user={user} />}
              {view === "control" && <Control setView={setView} />}
              {view === "nieuwsbrief" && <Nieuwsbrief />}
              {view === "shopguard" && <ShopGuard />}
              {view === "leadfinder" && <LeadFinder />}
              {view === "gap" && <GapIntelligence user={user} />}
              {view === "administratie" && <Administratie user={user} />}
              {view === "documenten" && <Documenten services={services} />}
              {view === "agenda" && <Agenda initialEventId={deepEvent} setView={setView} user={user} setMeetingSessionId={setMeetingSessionId} />}
              {view === "taken" && <Taken user={user} setView={setView} openKlant={openKlant} />}
              {/* Meeting & Brainstorm blijven gemount zolang er een sessie loopt — zo stopt
                de vergadering/brainstorm niet wanneer je naar een andere module navigeert.
                Verbergen i.p.v. unmounten houdt Jitsi, mic-transcriptie en realtime levend. */}
              {(view === "meeting" || meetingSessionId || soloMeetingActive) && (
                <div style={{ display: view === "meeting" ? "block" : "none" }}>
                  <Meeting setView={setView} tasks={sharedTasks} setTasks={setSharedTasks} user={user} sharedSessionId={meetingSessionId} clearSharedSession={() => setMeetingSessionId(null)} onSoloActiveChange={setSoloMeetingActive} />
                </div>
              )}
              {(view === "brainstorm" || brainstormActive) && (
                <div style={{ display: view === "brainstorm" ? "block" : "none" }}>
                  <Brainstorm user={user} setTasks={setSharedTasks} setView={setView} onActiveChange={setBrainstormActive} />
                </div>
              )}
              {view === "zichtbaarheid" && <Zichtbaarheid user={user} setView={setView} />}
              {view === "goedkeuringen" && <Goedkeuringen user={user} setView={setView} />}
              {view === "operatie" && <Operatie user={user} services={services} pipeline={pipeline} setView={setView} />}
              {view === "boardroom" && <Boardroom user={user} services={services} setTasks={setSharedTasks} setView={setView} />}
              {view === "bugs" && <BugTracker bugs={bugs} setBugs={setBugs} user={user} />}
              {view === "dev" && user?.email === "furgel@dif3r3nt.nl" && <DevConsole user={user} />}
             </ErrorBoundary>
            </div>
          </div>

          {agentOpen && <div className="drw-backdrop" onClick={() => setAgentOpen(false)} aria-hidden="true" />}
          {agentOpen && <AgentDrawer onClose={() => setAgentOpen(false)} services={services} tasks={sharedTasks} user={user} liveServices={liveServices} />}

          {/* Popup: iemand (Furgel/Tristan) voegde iets toe in een module */}
          {activityNote && (
            <div onClick={() => { setView(activityNote.view); setActivityNote(null); }} style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 2900, background: C.surface, border: "1px solid " + C.blue + "66", borderRadius: 12, padding: "12px 16px", boxShadow: "0 10px 40px rgba(0,0,0,0.5)", maxWidth: 430, width: "92%", cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: C.blue, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700 }}>{activityNote.actor_name} voegde een {activityNote.label} toe</p>
                  <p style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activityNote.title ? activityNote.title + " · " : ""}tik om te bekijken</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setActivityNote(null); }} style={{ marginLeft: "auto", background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 15, flexShrink: 0 }}>✕</button>
              </div>
            </div>
          )}

          {/* Popup: de ander is een gedeelde vergadering gestart -> jij kunt toetreden */}
          {incomingMeeting && incomingMeeting.id !== meetingSessionId && (
            <div style={{ position: "fixed", top: 18, left: "50%", transform: "translateX(-50%)", zIndex: 3000, background: C.surface, border: "1px solid " + C.cyan + "66", borderRadius: 12, padding: "14px 18px", boxShadow: "0 12px 44px rgba(0,0,0,0.55)", maxWidth: 440, width: "92%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: C.green, flexShrink: 0, animation: "pulse 1.4s ease-in-out infinite" }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700 }}>{(incomingMeeting.host_email === "tristan@dif3r3nt.nl" ? "Tristan" : incomingMeeting.host_email === "furgel@dif3r3nt.nl" ? "Furgel" : "Iemand")} is een vergadering gestart</p>
                  <p style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{incomingMeeting.title || "Vergadering"}</p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn bp" style={{ flex: 1, fontWeight: 700 }} onClick={() => { const id = incomingMeeting.id; setIncomingMeeting(null); setMeetingSessionId(id); setView("meeting"); }}>▶ Deelnemen</button>
                <button className="btn bg" onClick={() => { dismissedMeetingsRef.current.add(incomingMeeting.id); setIncomingMeeting(null); }}>Later</button>
              </div>
            </div>
          )}
          {/* Drijvende indicator: een vergadering/brainstorm loopt op de achtergrond.
            Klik om terug te keren — zo is duidelijk dat de sessie niet gestopt is. */}
          {(meetingSessionId || soloMeetingActive) && view !== "meeting" && (
            <button onClick={() => setView("meeting")} style={{ position: "fixed", bottom: 78, left: 18, zIndex: 50, display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 24, border: "1px solid " + C.green + "66", background: C.surface, color: C.text, cursor: "pointer", fontSize: 12, fontWeight: 600, boxShadow: "0 6px 22px rgba(0,0,0,0.45)" }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: C.red, animation: "pulse 1.4s ease-in-out infinite" }} />
              Vergadering loopt — terug
            </button>
          )}
          {brainstormActive && view !== "brainstorm" && (
            <button onClick={() => setView("brainstorm")} style={{ position: "fixed", bottom: (meetingSessionId || soloMeetingActive) && view !== "meeting" ? 128 : 78, left: 18, zIndex: 50, display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 24, border: "1px solid " + C.cyan + "66", background: C.surface, color: C.text, cursor: "pointer", fontSize: 12, fontWeight: 600, boxShadow: "0 6px 22px rgba(0,0,0,0.45)" }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: C.cyan, animation: "pulse 1.4s ease-in-out infinite" }} />
              Brainstorm loopt — terug
            </button>
          )}
          <ReportBugButton user={user} view={view} setBugs={setBugs} setView={setView} />

          {exportModal && (
            <Portal><div className="ovl" onClick={() => setExportModal(false)}>
              <div className="mdl" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
                <h3 style={{ fontFamily: "monospace", marginBottom: 6, fontSize: 15 }}>Export KPIs</h3>
                <p style={{ fontSize: 12, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>Kies formaat. Bevat alle live diensten, validaties, pipeline en CRM data.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button className="btn bp" style={{ width: "100%", padding: "12px", fontSize: 13, display: "flex", alignItems: "center", gap: 12 }} onClick={exportPDF}>
                    <span style={{ fontSize: 22 }}>PDF</span>
                    <div style={{ textAlign: "left" }}><p style={{ fontWeight: 600 }}>PDF Rapport</p><p style={{ fontSize: 11, opacity: .7, marginTop: 2 }}>Opent printvenster — sla op als PDF</p></div>
                  </button>
                  <button className="btn bg" style={{ width: "100%", padding: "12px", fontSize: 13, display: "flex", alignItems: "center", gap: 12 }} onClick={exportDOCX}>
                    <span style={{ fontSize: 22 }}>DOC</span>
                    <div style={{ textAlign: "left" }}><p style={{ fontWeight: 600, color: C.text }}>Word / RTF</p><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Download .rtf — opent in Word, Pages, LibreOffice</p></div>
                  </button>
                  <button className="btn bg" style={{ width: "100%", padding: "12px", fontSize: 13, display: "flex", alignItems: "center", gap: 12 }} onClick={exportCSV}>
                    <span style={{ fontSize: 22 }}>CSV</span>
                    <div style={{ textAlign: "left" }}><p style={{ fontWeight: 600, color: C.text }}>CSV / Excel</p><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Download .csv — opent in Excel of Google Sheets</p></div>
                  </button>
                  <button className="btn bg" style={{ width: "100%", padding: "12px", fontSize: 13, display: "flex", alignItems: "center", gap: 12 }} onClick={exportTXT}>
                    <span style={{ fontSize: 22 }}>TXT</span>
                    <div style={{ textAlign: "left" }}><p style={{ fontWeight: 600, color: C.text }}>Platte tekst</p><p style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Download .txt — universeel leesbaar</p></div>
                  </button>
                </div>
                <button className="btn bg" style={{ width: "100%", marginTop: 13, fontSize: 12 }} onClick={() => setExportModal(false)}>Annuleer</button>
              </div>
            </div></Portal>
          )}

          {user.leesModus && <LeesModusLaag scherm={view} />}
          {chatWith && <ChatWindow currentUser={user} otherUserKey={chatWith} onClose={() => setChatWith(null)} />}

          {globalToast && (
            <div style={{ position: "fixed", top: 24, right: 24, background: C.greenDim, border: "1px solid " + C.green, borderRadius: 8, padding: "12px 20px", color: C.green, fontSize: 14, fontWeight: 600, boxShadow: "0 10px 30px rgba(0,0,0,0.5)", zIndex: 9999, animation: "fadeUp 0.3s ease", display: "flex", alignItems: "center", gap: 10 }}>
              {globalToast}
            </div>
          )}
        </div>
      </>
    );
  }
